/* Asset cache reset: R148 recovery from pre-release URL caching. */
(() => {
  "use strict";

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const motionBehavior = () => matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
  const coarsePointer = () => matchMedia("(pointer: coarse)").matches;
  // A short haptic tick on touch devices that support it (Android); iOS ignores vibrate. Never under reduced motion.
  const haptic = (pattern = 8) => {
    if (!coarsePointer() || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    try {
      navigator.vibrate?.(pattern);
    } catch (_error) {
      /* unsupported */
    }
  };
  const centerInScroller = (button, scroller) => {
    if (!button || !scroller) return;
    const buttonRect = button.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const left = scroller.scrollLeft + buttonRect.left - scrollerRect.left - (scroller.clientWidth - buttonRect.width) / 2;
    scroller.scrollTo({ left: Math.max(0, left), behavior: motionBehavior() });
  };
  const kindLabels = {
    music: "Music", video: "Video", photo: "Photo", art: "Art", design: "Design",
    writing: "Writing", podcast: "Podcast", fashion: "Fashion", food: "Food", travel: "Travel",
    comedy: "Comedy", gaming: "Gaming", tech: "Tech", learn: "Learn", event: "Event",
    collab: "Collab", ask: "Ask", product: "Product", business: "Business", profile: "Creators",
  };
  const WORLD_COUNTRY_CODES = new Set("ae af al am ao ar at au az ba bd be bf bg bi bj bn bo br bs bt bw by bz ca cd cf cg ch ci cl cm cn co cr cu cv cy cz de dj dk dm do dz ec ee eg eh er es et fi fj fk fr ga gb ge gh gl gm gn gq gr gt gw gy hn hr ht hu id ie il in iq ir is it jm jo jp ke kg kh km kp kr kw kz la lb lc lk lr ls lt lu lv ly ma md me mg mk ml mm mn mr mt mu mv mw mx my mz na nc ne ng ni nl no np nz om pa pe pg ph pk pl pr ps pt py qa ro rs ru rw sa sb sc sd se sg si sk sl sn so sr ss st sv sy sz td tf tg th tj tl tm tn tr tt tw tz ua ug us uy uz vc ve vn vu xk ye za zm zw".split(" "));
  const staffRoles = new Set(["moderator", "senior_moderator", "safety_specialist", "admin"]);
  const accountSections = new Set(["profile", "security", "privacy", "notifications", "information", "appearance", "help", "purchases", "creator", "settings"]);
  let businessInstance = null;
  let businessViewerId = null;
  const businessAdHosts = new Map();

  function businessFeatures() {
    if (!state.capabilities.commerce) return null;
    if (!businessInstance && window.LouderrBusiness) {
      businessInstance = window.LouderrBusiness.create({
        api, toast, runRecentAction, getViewer: () => state.viewer, requireVerified,
        navigateProvider, openAccount: () => openAccountDialog(),
        openCreator: () => openAccountDialog("creator"),
        openCreatorStudio: () => openAccountDialog("creator"),
        openPurchases: () => openAccountDialog("purchases"),
        onPaidContentChanged: invalidatePaidContent,
      });
    }
    return businessInstance;
  }

  async function mountBusinessSurface(view, container) {
    const operation = { route: state.routeRequestId, viewer: state.viewer?.id, social: state.messages };
    container.businessLoad = operation;
    const current = () => container.businessLoad === operation && state.view === view
      && state.routeRequestId === operation.route && state.viewer?.id === operation.viewer
      && state.messages === operation.social && state.capabilities.commerce;
    if (!current()) return;
    clear(container);
    const loading = create("p", "business-note", "Loading controls…");
    loading.setAttribute("role", "status"); container.append(loading);
    try {
      if (!businessFeatures()) await window.LouderrOptional?.load("business");
      if (!current()) return;
      const feature = businessFeatures();
      if (!feature) throw new Error("These controls could not be loaded. Try again.");
      clear(container);
      if (view === "advertise") await feature.mountAdvertiser(container);
      else await feature.mountPremium(container);
    } catch (error) {
      if (!current()) return;
      clear(container);
      const notice = create("p", "business-error", error.message);
      notice.setAttribute("role", "alert");
      const retry = create("button", "secondary-button", "Retry"); retry.type = "button";
      retry.addEventListener("click", () => { if (current()) void mountBusinessSurface(view, container); });
      container.append(notice, retry);
    }
  }

  function clearBusinessAds() {
    for (const { host } of businessAdHosts.values()) {
      businessInstance?.unmountAds(host);
      host.remove();
    }
    businessAdHosts.clear();
  }

  async function invalidatePaidContent(change = {}) {
    const viewerId = change.viewerId || state.viewer?.id;
    if (!viewerId || state.viewer?.id !== viewerId) return;
    state.paidContentVersion = (state.paidContentVersion || 0) + 1;
    clearBusinessAds();
    if (state.board?.is_sponsored) {
      state.board = null;
      renderBoard();
    }
    // Clear immediately; a failed refresh must not restore stale paid creative.
    await refreshRoom(true);
  }

  function syncBusinessAds(surface, container, count) {
    if (!state.capabilities.commerce) {
      clearBusinessAds();
      return;
    }
    const enabledSurface = (surface === "floor" && state.view === "all")
      || (surface === "flow" && state.view === "flow" && state.flow.view === "feed");
    const topics = new Set(["all", "music", "art", "writing", "film", "gaming", "technology", "other"]);
    const chosenKind = (state.filter || "all").split(",").find((kind) => kind && kind !== "first") || "all";
    const selected = surface === "floor" ? ({ video: "film" }[chosenKind] || chosenKind) : "all";
    const topic = topics.has(selected) ? selected : "other";
    let record = businessAdHosts.get(surface);
    if (record && (!enabledSurface || count < 6 || !state.viewer || record.topic !== topic)) {
      businessInstance?.unmountAds(record.host);
      record.host.remove();
      businessAdHosts.delete(surface);
      record = null;
    }
    if (!enabledSurface || count < 6 || !state.viewer) return;
    const feature = businessFeatures();
    if (!feature) {
      // Placements are optional. Do not retry failed downloads on every feed refresh.
      if (window.LouderrOptional && window.LouderrOptional.status("business") !== "error" && !container.businessAdLoad) {
        const operation = { viewer: state.viewer.id, social: state.messages, route: state.routeRequestId };
        container.businessAdLoad = operation;
        window.LouderrOptional.load("business").then(() => {
          if (state.viewer?.id !== operation.viewer || state.messages !== operation.social
              || state.routeRequestId !== operation.route || container.isConnected === false) return;
          syncBusinessAds(surface, container, surface === "floor" ? state.drops.length : state.flow.items.length);
        }).catch(() => { /* A failed optional placement must not interrupt the Floor. */ })
          .finally(() => { if (container.businessAdLoad === operation) delete container.businessAdLoad; });
      }
      return;
    }
    if (!record) {
      record = { host: create("div", `business-ad-host business-ad-host--${surface}`), topic };
      record.host.dataset.adSurface = surface;
      businessAdHosts.set(surface, record);
    }
    container.insertBefore(record.host, container.children[6] || null);
    feature.mountAds(record.host, {
      surface, topic, root: surface === "flow" ? container : null,
      onVisible: surface === "flow" ? () => {
        if (state.view !== "flow" || state.flow.view !== "feed") return;
        $$(".flow-video", el.flowFeed).forEach((video) => video.pause());
        state.flow.activeDropId = null;
      } : undefined,
    });
  }
  const assignableStaffRoles = ["user", "moderator", "senior_moderator", "safety_specialist"];
  const roleLabels = {
    user: "User",
    moderator: "Moderator",
    senior_moderator: "Senior moderator",
    safety_specialist: "Safety specialist",
    admin: "Administrator",
  };
  const chatEmotes = Object.freeze([
    { name: "hype", code: ":hype:", label: "Hype", glyph: "🔥" },
    { name: "love", code: ":love:", label: "Love", glyph: "🧡" },
    { name: "clap", code: ":clap:", label: "Clap", glyph: "👏" },
    { name: "lol", code: ":lol:", label: "Laugh", glyph: "😂" },
    { name: "hello", code: ":hello:", label: "Hello", glyph: "👋" },
    { name: "wow", code: ":wow:", label: "Wow", glyph: "😮" },
    { name: "gg", code: ":gg:", label: "Good game", glyph: "🏆" },
    { name: "go", code: ":go:", label: "Let's go", glyph: "🚀" },
  ]);
  const chatEmoteByCode = new Map(chatEmotes.map((emote) => [emote.code, emote]));
  const storedEmoteMotion = (() => {
    try {
      const value = localStorage.getItem("lobby.emotes.motion");
      return value === null ? !matchMedia("(prefers-reduced-motion: reduce)").matches : value === "on";
    } catch (_) { return !matchMedia("(prefers-reduced-motion: reduce)").matches; }
  })();
  const storedRecentEmotes = (() => {
    try {
      const value = JSON.parse(localStorage.getItem("lobby.emotes.recent") || "[]");
      // Native emoji are resolved when the optional catalog opens, after boot.
      return Array.isArray(value) ? value.filter((code) => typeof code === "string" && code.length > 0 && code.length <= 32).slice(0, 24) : [];
    } catch (_) { return []; }
  })();
  const state = {
    capabilities: Object.freeze({
      profile: "unavailable", beta_access: false, registration: false, email_delivery: false,
      password_recovery: false, email_change: false, social_login: false,
      commerce: false, uploads: false, media_publication: false, flow: false,
      translation: false, community_writes: false, staff_actions: false,
      studio_response_audio: false, studio_video_remix: false,
      message_files: false, calls: false, live: false,
    }),
    // Seats left, when the room has a capacity. Null means no cap is set.
    capacity: null,
    viewer: null,
    sessionRestricted: false,
    productName: "LOUDERR",
    csrf: null,
    drops: [],
    pending: [],
    nextCursor: null,
    feedLoadingMore: false,
    feedRequestId: 0,
    feedMutationVersion: 0,
    homeStoriesRequestId: 0,
    board: null,
    boardQueue: { items: [], eligible_count: 0, locked_count: 0 },
    presence: 0,
    stats: { drops: 0, reactions: 0, saves: 0, featured: 0 },
    filter: "all",
    // The server selects only a public, empty shell. Account/data reads still wait for startup.
    view: ["all", "discover", "chat", "creative", "flow", "trending"].includes(document.body?.dataset?.view)
      ? document.body.dataset.view : "all",
    startupPending: true,
    sort: "new",
    savedFolderId: null,
    query: "",
    authorId: null,
    paused: false,
    socket: null,
    reconnectAttempt: 0,
    reconnectTimer: null,
    heartbeatTimer: null,
    eventCursor: 0,
    realtime: { generation: 0, chain: Promise.resolve(), recovery: null, ownerUserId: null },
    boardTimer: null,
    menuDrop: null,
    menuAnchor: null,
    reportDrop: null,
    reportSurface: null,
    editingDrop: null,
    composerJourney: null,
    composerTitleBeforeOpen: "",
    studioTitleBeforeOpen: "",
    composerDraft: {
      ownerId: null, key: null, baseline: null, saved: new Map(),
      pending: null, busy: false, epoch: 0,
    },
    accountChangeReload: false,
    dropSubmissionActive: false,
    deferredCreatedDropIds: new Set(),
    dropRefreshes: new Map(),
    analyticsQueue: [],
    analyticsTimer: null,
    analyticsObserved: new Set(),
    ageAttestationRequired: false, ageRequirementNotMet: false, minimumAge: 16, pendingVerification: null,
    analyticsSession: sessionStorage.getItem("lobby.analytics.session") || crypto.randomUUID(),
    integritySession: sessionStorage.getItem("lobby.integrity.session") || crypto.randomUUID(),
    integrityObserved: new Set(),
    integrityPending: new Map(),
    observed: new Set(),
    dwellStarts: new Map(),
    trending: { scope: "drops", period: "day", category: "all", data: null, requestId: 0, historyRequestId: 0, previewRequestId: 0 },
    trendingTimer: null,
    flow: {
      view: "feed", items: [], now: null, stories: [], history: [], historyCursor: null, chatItems: [], storyLens: "now",
      loading: false, observer: null, activeDropId: null, activeVideo: null, countdownTimer: null,
      renderedView: null, resizeFrame: null,
      previewObserver: null, previewQueue: [], previewActive: null,
      refreshTimer: null, audienceChannel: null, eventRefreshTimer: null,
      nowRetryAt: 0, nowFailures: 0,
      storySnapshot: null, storyExpiryTimer: null, storyOpenObserver: null,
      storyRetryAt: 0, storyFailures: 0,
      storyPages: [], storyNextCursor: null, storyHasUpdates: false, storyResetRequired: false,
      storyResetScroll: false,
      nextCursor: null, pageCursors: [], snapshot: null, requestId: 0, totalPaged: 0,
      controller: null, resetRequired: false, hasUpdates: false, lastAppend: false,
      feedOwner: null, lastRevalidate: false,
      chatDraft: "", chatDraftOwner: null,
    },
    chat: {
      items: [], nextCursor: null, lens: "all", replyTo: null, reportMessage: null,
      reportSurface: "chat",
      unreadCount: 0, newWhileAway: 0, loaded: false, lastTrackedRead: null,
      typingUsers: new Map(), typingHeartbeatTimer: null, typingExpiryTimer: null,
      typingLastSentAt: 0, typingRequestActive: false,
    },
    comments: {
      drop: null, items: [], nextCursor: null, totalCount: 0, pinned: null,
      loading: false, requestId: 0, dialogVersion: 0, submission: null,
    },
    emotes: {
      target: null, trigger: null, autocomplete: false,
      motionEnabled: storedEmoteMotion, recent: storedRecentEmotes,
    },
    search: {
      type: "all", category: "all", people: [], drops: [],
      peopleCursor: null, peoplePage: 0, peoplePageCursors: [null], dropsCursor: null,
      dropPages: 0, dropCursors: [], retry: null, requestId: 0, controller: null, loaded: false,
    },
    discovery: { mode: "search", items: [], data: null, requestId: 0 },
    world: {
      readStatus: {}, readAttempts: {}, lensSelection: null,
      view: "pulse", pulse: null, map: null, mic: null, moment: null, lens: null, lensRequestId: 0,
      micEntry: null, micHistory: [], moments: [],
      daily: null, questions: [], questionScope: "all", polls: [], collaborations: [],
      collaborationRole: "", collaborationLocation: "", roomNowLoaded: false,
      roomNowRequest: null, roomNowRefreshTimer: null, mapSvgPromise: null,
      pendingFocus: null,
    },
    records: { view: "scouts", period: "month", scope: "people", data: null, mine: null, pendingSlotId: null },
    events: { when: "upcoming", items: [], loaded: false, requestId: 0, focusId: null },
    exposure: { ownerId: null, observer: null },
    gallery: { items: [], dirty: false, locked: false, previewUrls: new Set() },
    utilityRefreshTimer: null,
    notifications: { items: [], nextCursor: null, unreadCount: 0, loaded: false },
    journey: { rootId: null, data: null, items: [], nextCursor: null, loading: false, requestId: 0, subscribing: false },
    journeyUpdates: { items: [], nextCursor: null, loading: false, requestId: 0, loaded: false },
    messages: {
      conversations: [], unreadCount: 0, loaded: false, selectedId: null, folder: "inbox",
      people: newMessageSearchState(), startingRecipients: new Set(),
      conversationCursor: null, conversationsLoading: false,
      conversationRequestId: 0, threadRequestId: 0, selectionVersion: 0,
      drafts: new Map(), sending: new Set(), deciding: new Set(),
      deletionVersion: 0, deletedMessages: new Map(),
      items: [], nextCursor: null, conversation: null, participant: null, reportMessage: null,
    },
    profile: { data: null, drops: [], offers: [], status: null, handle: null, nextCursor: null, requestId: 0, loading: false },
    translation: {
      enabled: false, private_content_enabled: false, default_language: "en",
      languages: [{ code: "en", label: "English" }],
    },
    relationships: { items: [], loaded: false, loading: false },
    accountSection: "profile",
    federatedProviders: [],
    passwordSignInExpanded: false,
    security: { sessions: [], notifications: [], loaded: false },
    purchases: { items: [], loaded: false },
    creator: { status: null, dashboard: null, loaded: false },
    privateEvents: { cursor: 0, initialized: false, timer: null },
    enforcement: { items: [], loaded: false, timer: null },
    activeSince: document.visibilityState === "visible" ? performance.now() : null,
  };
  sessionStorage.setItem("lobby.analytics.session", state.analyticsSession);
  sessionStorage.setItem("lobby.integrity.session", state.integritySession);
  document.documentElement.classList.toggle("emote-motion-off", !state.emotes.motionEnabled);

  const el = {
    board: $("#board-content"), countdown: $("#board-countdown"), progress: $("#board-progress"),
    boardQueue: $("#board-queue-list"), boardEligibleCount: $("#board-eligible-count"),
    boardCapacityNote: $("#board-capacity-note"),
    boardReason: $("#board-reason"), feed: $("#feed"), loading: $("#feed-loading"),
    empty: $("#feed-empty"), feedError: $("#feed-error"), loadMore: $("#load-more"),
    title: $("#feed-title"),
    subtitle: $("#feed-subtitle"), status: $("#feed-status"), connection: $("#connection-label"),
    presence: $("#presence-count"), presenceNote: $("#presence-note"),
    trendingPreview: $("#trending-preview"), trendingSection: $("#trending"),
    trendingList: $("#trending-leaderboard"), trendingLoading: $("#trending-loading"),
    trendingError: $("#trending-error"), trendingEmpty: $("#trending-empty"),
    trendingFocus: $("#trending-focus"), trendingCountdown: $("#trending-countdown"),
    trendingHistory: $("#trending-history"),
    trendingExplanation: $("#trending-explanation"), trendingCategory: $("#trending-category"),
    flowSection: $("#flow"), flowFeed: $("#flow-feed"), flowNow: $("#flow-now"),
    flowStories: $("#flow-stories"), flowHistory: $("#flow-history"),
    flowLoading: $("#flow-loading"), flowError: $("#flow-error"),
    flowPagination: $("#flow-pagination"), flowMore: $("#flow-load-more"),
    flowRefresh: $("#flow-refresh"), flowPageStatus: $("#flow-page-status"),
    flowEmpty: $("#flow-empty"), storyLenses: $("#story-lenses"),
    feedOrder: $("#feed-order"), topicFollow: $("#topic-follow"),
    saveFolders: $("#save-folders"),
    storyPagination: $("#story-pagination"), storyMore: $("#story-load-more"),
    storyRefresh: $("#story-refresh"), storyPageStatus: $("#story-page-status"),
    categoryRail: $(".category-rail"), categoryStrip: $(".category-strip"),
    categoryPrevious: $("#category-scroll-previous"), categoryNext: $("#category-scroll-next"),
    mobileSearch: $(".mobile-search-wrap"),
    boardSection: $("#board"), feedSection: $("#floor"),
    profileRank: $("#profile-rank-banner"), profileAchievements: $("#profile-achievements"),
    pageContext: $("#page-context"), pageTitle: $("#page-title"), pageDescription: $("#page-description"),
    chatShell: $("#chat-shell"), chatRoomList: $("#chat-room-list"), chatRoomSearch: $("#chat-room-search"),
    chatRoomTitle: $("#chat-room-title"), chatRoomDescription: $("#chat-room-description"),
    chatRoomHost: $("#chat-room-host"), chatHostToggle: $("#chat-host-toggle"),
    chatRoomStatus: $("#chat-room-status"), chatRoomError: $("#chat-room-error"), chatRoomEmpty: $("#chat-room-empty"),
    chatRoomsOpen: $("#chat-rooms-open"), chatComposerCount: $("#chat-composer-count"),
    emoteCategories: $("#emote-categories"),
    chatSection: $("#chat"), chatMessages: $("#chat-messages"), chatLoading: $("#chat-loading"),
    chatError: $("#chat-error"), chatEmpty: $("#chat-empty"), chatOlder: $("#chat-load-older"),
    chatNew: $("#chat-new-messages"), chatForm: $("#chat-form"), chatBody: $("#chat-body"),
    chatSend: $("#chat-send"), chatEmote: $("#chat-emote-button"), chatNote: $("#chat-composer-note"),
    chatTyping: $("#chat-typing"),
    chatReply: $("#chat-reply-context"), chatReplyAuthor: $("#chat-reply-author"),
    chatReplyCopy: $("#chat-reply-copy"), chatMarkUnread: $("#chat-mark-unread"),
    comments: $("#comments-dialog"), commentsTitle: $("#comments-title"),
    commentsSummary: $("#comments-summary"), commentsList: $("#comments-list"),
    commentsLoading: $("#comments-loading"), commentsError: $("#comments-error"),
    commentsEmpty: $("#comments-empty"), commentsMore: $("#comments-more"),
    commentsForm: $("#comments-form"), commentsBody: $("#comments-body"),
    commentsSend: $("#comments-send"), commentsFormError: $("#comments-form-error"),
    discoverSection: $("#discover"), discoverQuery: $("#discover-query"),
    discoverCategory: $("#discover-category"), discoverLoading: $("#discover-loading"),
    discoverError: $("#discover-error"), discoverResults: $("#discover-results"),
    discoverEmpty: $("#discover-empty"), discoverPeople: $("#discover-people-list"),
    discoverDrops: $("#discover-drop-list"), discoverMorePeople: $("#discover-more-people"),
    discoverPreviousPeople: $("#discover-previous-people"), discoverPeoplePaging: $("#discover-people-paging"),
    discoverMoreDrops: $("#discover-more-drops"), discoverPeopleNote: $("#discover-people-note"),
    discoverDropsNote: $("#discover-drops-note"),
    discoverEmptyTitle: $("#discover-empty h3"), discoverEmptyCopy: $("#discover-empty p"),
    discoverSearchTools: $("#discover-search-tools"), discoverSurfaceNote: $("#discover-surface-note"),
    discoverSearchField: $("#discover-search-field"), discoverSearchTypeControl: $("#discover-search-type-control"),
    discoverCategoryField: $("#discover-category-field"),
    worldSurface: $("#world-surface"), worldContent: $("#world-content"),
    recordsSurface: $("#records-surface"), recordsContent: $("#records-content"),
    eventsSurface: $("#events-surface"), eventsList: $("#events-list"),
    eventsEmpty: $("#events-empty"), eventsNew: $("#events-new"), eventDialog: $("#event-dialog"),
    recordsPeriod: $("#records-period"), recordsPeriodField: $("#records-period-field"), recordsScope: $("#records-scope"),
    recordsScopeField: $("#records-scope-field"),
    notificationsSection: $("#notifications"), notificationList: $("#notification-list"),
    notificationsLoading: $("#notifications-loading"), notificationsError: $("#notifications-error"),
    notificationsEmpty: $("#notifications-empty"), notificationsMore: $("#notifications-more"),
    notificationsReadAll: $("#notifications-read-all"),
    messagesSection: $("#messages"), messagesLoading: $("#messages-loading"),
    messagesError: $("#messages-error"), messagesShell: $("#messages-shell"),
    conversationList: $("#conversation-list"), conversationsEmpty: $("#conversations-empty"),
    messagesInbox: $("#messages-inbox"), messagesNew: $("#messages-new"), messagesEmptyNew: $("#messages-empty-new"),
    messagesSearch: $("#messages-search"), messagesSearchClear: $("#messages-search-clear"),
    messagesSearchPanel: $("#messages-search-panel"), messagesSearchStatus: $("#messages-search-status"),
    messagesSearchError: $("#messages-search-error"), messagesSearchResults: $("#messages-search-results"),
    messagesSearchMore: $("#messages-search-more"), messagesSearchRetry: $("#messages-search-retry"),
    conversationsMore: $("#conversations-more"),
    messageThreadEmpty: $("#message-thread-empty"), messageThreadContent: $("#message-thread-content"),
    messageParticipant: $("#message-participant"), messageParticipantAvatar: $("#message-participant-avatar"),
    messageParticipantName: $("#message-participant-name"), messageParticipantHandle: $("#message-participant-handle"),
    messageRequest: $("#message-request"), messageRequestTitle: $("#message-request-title"),
    messageRequestDescription: $("#message-request-description"), messageRequestActions: $("#message-request-actions"),
    messageRequestAccept: $("#message-request-accept"), messageRequestDecline: $("#message-request-decline"),
    messageRequestError: $("#message-request-error"),
    messageList: $("#message-list"), messagesOlder: $("#messages-older"),
    messageThreadHeader: $("#message-thread-content > .message-thread-header"), messageNoticeSlot: $("#message-notice-slot"),
    messageHistoryNavigation: $("#message-history-navigation"), messageHistoryStatus: $("#message-history-status"),
    messageHistoryRetry: $("#message-history-retry"), messageHistoryLatest: $("#messages-latest"),
    messageForm: $("#message-form"), messageBody: $("#message-body"), messageActions: $("#message-actions-dialog"),
    messageMediaInput: $("#message-media-input"), messageMediaButton: $("#message-media-button"),
    messageMediaDraft: $("#message-media-draft"), messageMediaPreview: $("#message-media-preview"),
    messageMediaStatus: $("#message-media-status"), messageMediaRemove: $("#message-media-remove"),
    messageFileInput: $("#message-file-input"), messageFileButton: $("#message-file-button"),
    messageFileCheck: $("#message-file-check"),
    sendDropDialog: $("#send-drop-dialog"), sendDropSubject: $("#send-drop-subject"),
    sendDropNote: $("#send-drop-note"), sendDropList: $("#send-drop-list"),
    sendDropEmpty: $("#send-drop-empty"), sendDropError: $("#send-drop-error"),
    sendDropStatus: $("#send-drop-status"), sendDropMore: $("#send-drop-more"),
    sendDropRetry: $("#send-drop-retry"), sendDropSignIn: $("#send-drop-sign-in"),
    sendDropSearch: $("#send-drop-search"), shareCopyLink: $("#share-copy-link"), shareNative: $("#share-native"), shareStory: $("#share-story"),
    commentsReplyDraft: $("#comments-reply-draft"), commentsReplyName: $("#comments-reply-name"),
    commentsReplyCancel: $("#comments-reply-cancel"),
    messageSend: $("#message-send"), messageEmote: $("#message-emote-button"), messageFormError: $("#message-form-error"),
    emotePicker: $("#emote-picker"), emoteSearch: $("#emote-search"),
    emoteGrid: $("#emote-grid"), emoteMotion: $("#emote-motion-toggle"),
    profileSection: $("#profile-view"), profileLoading: $("#profile-view-loading"),
    profileError: $("#profile-view-error"), profileContent: $("#profile-view-content"),
    profileViewAvatar: $("#profile-view-avatar"), profileViewName: $("#profile-view-name"),
    profileViewHandle: $("#profile-view-handle"), profileViewBio: $("#profile-view-bio"),
    profileViewLocation: $("#profile-view-location"), profileViewWebsite: $("#profile-view-website"),
    profileViewJoined: $("#profile-view-joined"), profileFollow: $("#profile-follow"),
    profileMessage: $("#profile-message"), profileSafetyMenu: $("#profile-safety-menu"), profileMute: $("#profile-mute"),
    profileRestrict: $("#profile-restrict"),
    profileBlock: $("#profile-block"), profileReport: $("#profile-report"),
    profileEdit: $("#profile-edit"),
    profileFollowers: $("#profile-followers"), profileFollowing: $("#profile-following"),
    profileDropCount: $("#profile-drop-count"), profileApplauseCount: $("#profile-applause-count"),
    profileDrops: $("#profile-drop-list"), profileDropGrid: $("#profile-drop-grid"), profileDropsEmpty: $("#profile-drops-empty"),
    profileDropsLoading: $("#profile-drops-loading"), profileDropsError: $("#profile-drops-error"),
    profileDropsMore: $("#profile-drops-more"),
    profileStatus: $("#profile-status-panel"), profileOffers: $("#profile-offers"),
    profileOfferList: $("#profile-offer-list"),
    connections: $("#connections-dialog"), connectionsTitle: $("#connections-title"),
    connectionsCopy: $("#connections-copy"), connectionsList: $("#connections-list"),
    connectionsEmpty: $("#connections-empty"), connectionsMore: $("#connections-more"), connectionsStatus: $("#connections-status"),
    pendingBanner: $("#pending-banner"), pendingCount: $("#pending-count"), live: $("#live-toggle"),
    composer: $("#composer"), composerTitle: $("#composer-title"), dropForm: $("#drop-form"),
    projectComposer: $("#composer-project"), projectStartLabel: $("#project-start-label"),
    projectStart: $("#project-start"), projectStartOptions: $("#project-start-options"),
    projectPrompt: $("#project-prompt"), projectAllowResponses: $("#project-allow-responses"),
    projectComposerContext: $("#project-composer-context"), projectComposerLabel: $("#project-composer-label"),
    projectComposerTitle: $("#project-composer-title"), projectEditNote: $("#project-edit-note"),
    project: $("#project-dialog"), projectTitle: $("#project-dialog-title"), projectLoading: $("#project-loading"),
    projectError: $("#project-error"), projectErrorCopy: $("#project-error-copy"),
    projectContent: $("#project-content"), projectRoot: $("#project-root"), projectTimeline: $("#project-timeline"),
    projectInvitation: $("#project-invitation"), projectSubscribe: $("#project-subscribe"),
    projectUpdate: $("#project-update"), projectRespond: $("#project-respond"),
    projectFollowNote: $("#project-follow-note"), projectActionError: $("#project-action-error"),
    projectResponseSetting: $("#project-response-setting"), projectResponseSettingLabel: $("#project-response-setting-label"),
    projectEmpty: $("#project-empty"), projectMore: $("#project-more"),
    projectUpdatesList: $("#project-updates-list"), projectUpdatesLoading: $("#project-updates-loading"),
    projectUpdatesError: $("#project-updates-error"), projectUpdatesErrorCopy: $("#project-updates-error-copy"),
    projectUpdatesEmpty: $("#project-updates-empty"), projectUpdatesMore: $("#project-updates-more"),
    projectUpdatesSection: $("#project-updates"),
    projectUpdatesRead: $("#project-updates-read"),
    submitDrop: $("#submit-drop"), dropKind: $("#drop-kind"),
    dropTitle: $("#drop-title"), dropBody: $("#drop-body"), dropLink: $("#drop-link"),
    dropHereFirst: $("#drop-here-first"), dropHereFirstDays: $("#drop-here-first-days"), dropHereFirstWindow: $("#drop-here-first-window"),
    dropMedia: $("#drop-media"), dropGallery: $("#drop-gallery"),
    uploadField: $("#upload-field"), uploadLabel: $("#upload-label"), uploadAction: $("#upload-action"),
    uploadProgress: $("#upload-progress"), editMediaNote: $("#edit-media-note"),
    formError: $("#form-error"), account: $("#sign-in-dialog"), guestAccount: $("#guest-account"), studioStart: $("#studio-start-dialog"),
    // Settings is a screen, not a popup over the page: the bottom bar stays reachable from inside it.
    settings: $("#account-dialog"), settingsError: $("#settings-error"),
    memberAccount: $("#member-account"), accountError: $("#account-error"),
    federatedAuth: $("#federated-auth"), federatedProviders: $("#federated-provider-list"),
    verificationPanel: $("#verification-panel"), verificationToken: $("#verification-token"), verificationError: $("#verification-error"),
    agePanel: $("#age-panel"), ageBirthDate: $("#age-birth-date"), ageError: $("#age-error"), confirmAge: $("#confirm-age"),
    profileForm: $("#profile-form"), profileSaveError: $("#profile-save-error"), profileAvatar: $("#profile-avatar"),
    profileLanguage: $("#profile-language"), profileAutoTranslate: $("#profile-auto-translate"),
    profileTranslatePrivate: $("#profile-translate-private"),
    translationPreferenceNote: $("#translation-preference-note"),
    profileAvatarInput: $("#profile-avatar-file"), profileAvatarField: $("#avatar-upload-field"),
    profileAvatarLabel: $("#avatar-upload-label"), profileAvatarAction: $("#avatar-upload-action"),
    wordFilter: $("#word-filter"), wordFilterInput: $("#word-filter-input"),
    wordFilterStatus: $("#word-filter-status"), wordFilterError: $("#word-filter-error"),
    relationshipControls: $("#relationship-controls"), relationshipsList: $("#relationships-list"),
    relationshipsLoading: $("#relationships-loading"), relationshipsEmpty: $("#relationships-empty"),
    relationshipsRefresh: $("#relationships-refresh"),
    accountProfilePanel: $("#account-profile-panel"), accountSecurityPanel: $("#account-security-panel"),
    accountPurchasesPanel: $("#account-purchases-panel"), accountCreatorPanel: $("#account-creator-panel"),
    accountSettingsPanel: $("#account-settings-panel"),
    securityLoading: $("#security-loading"), securityError: $("#security-error"),
    securitySessions: $("#security-session-list"), securityNotifications: $("#security-notification-list"),
    securityIdentities: $("#security-identity-list"),
    securityRevokeOthers: $("#security-revoke-others"), purchasesContent: $("#purchases-content"),
    creatorContent: $("#creator-content"),
    enforcementPanel: $("#enforcement-panel"), enforcementList: $("#enforcement-list"),
    report: $("#report-dialog"), reportForm: $("#report-form"), reportError: $("#report-error"),
    menu: $("#action-menu"), toast: $("#toast"), admin: $("#admin-dialog"),
    adminContent: $("#admin-content"), adminNav: $("#admin-nav"),
    adminSections: $("#admin-sections"), adminRefresh: $("#admin-refresh"), adminStatus: $("#admin-status"),
    adminAccount: $("#account-open-moderation"),
  };

  function create(tag, className = "", text = "") {
    const item = document.createElement(tag);
    if (className) item.className = className;
    if (text) item.textContent = text;
    return item;
  }

  function accountBadge(user) {
    const badge = user?.identity_badge;
    if (!badge || typeof badge !== "object") return null;
    const labels = {
      top_creator: ["blue", ["Top Creator"]],
      staff: ["yellow", ["Moderator", "Senior Moderator", "Safety Specialist", "Administrator"]],
      owner_ceo: ["orange", ["Owner & CEO"]],
    };
    const allowed = Object.prototype.hasOwnProperty.call(labels, badge.kind) ? labels[badge.kind] : null;
    if (!allowed || badge.color !== allowed[0] || !allowed[1].includes(badge.label)) return null;
    return { kind: badge.kind, color: badge.color, label: badge.label };
  }

  function accountAccessibleName(user, fallback = "Unavailable creator") {
    const name = typeof user?.display_name === "string" && user.display_name ? user.display_name : fallback;
    const badge = accountBadge(user);
    return badge ? `${name}, ${badge.label}` : name;
  }

  function renderAccountName(node, user, fallback = "Unavailable creator") {
    // Identity is server-authoritative; email verification and roles are not badge inputs.
    const name = typeof user?.display_name === "string" && user.display_name ? user.display_name : fallback;
    const badge = accountBadge(user);
    if (!badge) {
      node.textContent = name;
      node.className = (node.className || "").split(/\s+/).filter((value) => value !== "identity-name").join(" ");
      return node;
    }
    node.textContent = "";
    node.classList.add("identity-name");
    node.append(create("span", "identity-name-text", name));
    if (badge) {
      const mark = create("span", `identity-badge identity-badge--${badge.color}`);
      mark.setAttribute("role", "img");
      mark.setAttribute("aria-label", badge.label);
      mark.setAttribute("title", badge.label);
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("viewBox", "0 0 24 24");
      svg.setAttribute("width", "16");
      svg.setAttribute("height", "16");
      svg.setAttribute("aria-hidden", "true");
      svg.setAttribute("focusable", "false");
      const rosette = document.createElementNS("http://www.w3.org/2000/svg", "path");
      rosette.setAttribute("d", "M12 1.5 15 3.3 18.5 3.5 20.1 6.6 22.5 9 21.7 12.4 22 15.9 19.1 17.9 17.3 21 13.8 21.3 10.5 22.5 7.8 20.2 4.5 19 3.7 15.6 1.5 12.8 2.9 9.6 3.3 6.1 6.5 4.6 8.9 2Z");
      rosette.setAttribute("fill", "currentColor");
      const check = document.createElementNS("http://www.w3.org/2000/svg", "path");
      check.setAttribute("d", "m7.5 12 3 3 6-6");
      check.setAttribute("class", "identity-badge-check");
      check.setAttribute("fill", "none");
      check.setAttribute("stroke-width", "2.4");
      check.setAttribute("stroke-linecap", "round");
      check.setAttribute("stroke-linejoin", "round");
      svg.append(rosette, check);
      mark.append(svg);
      node.append(mark);
    }
    return node;
  }

  function accountNameNode(user, tag = "strong", className = "", fallback = "Unavailable creator") {
    return renderAccountName(create(tag, className), user, fallback);
  }

  function engagementSurface(node) {
    if (!node) return null;
    if (node.closest("#board-content")) return "board";
    if (node.closest("#feed")) return "floor";
    const integritySurface = node.closest("[data-integrity-surface]")?.dataset.integritySurface;
    if (["flow", "now", "story"].includes(integritySurface)) return integritySurface;
    return null;
  }

  function surfaceMetadata(node) {
    const surface = engagementSurface(node);
    return surface ? { surface } : {};
  }

  function icon(name) {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
    use.setAttribute("href", `#icon-${name}`);
    svg.append(use);
    return svg;
  }

  function clear(node) {
    while (node.firstChild) {
      node.firstChild.releaseProfilePreview?.();
      node.firstChild.remove();
    }
  }

  function translationControl(translation, renderedFields, applyFields) {
    const metadata = translation?.fields;
    if (!metadata || typeof metadata !== "object") return null;
    const keys = Object.keys(renderedFields).filter((key) => (
      metadata[key]?.translated && typeof metadata[key]?.original === "string"
    ));
    if (!keys.length) return null;
    const translated = { ...renderedFields };
    const original = { ...renderedFields };
    keys.forEach((key) => { original[key] = metadata[key].original; });
    const sourceLabels = [...new Set(keys.map((key) => metadata[key].source_language_label).filter(Boolean))];
    const control = create("div", "translation-control");
    const label = create("span", "translation-label", `Translated from ${sourceLabels.join(" + ") || "another language"}`);
    const toggle = create("button", "translation-toggle", "Show original");
    toggle.type = "button";
    toggle.setAttribute("aria-pressed", "false");
    let showingOriginal = false;
    toggle.addEventListener("click", () => {
      showingOriginal = !showingOriginal;
      applyFields(showingOriginal ? original : translated);
      toggle.textContent = showingOriginal ? "Show translation" : "Show original";
      toggle.setAttribute("aria-pressed", String(showingOriginal));
      label.textContent = showingOriginal
        ? `Original · ${sourceLabels.join(" + ") || "source language"}`
        : `Translated from ${sourceLabels.join(" + ") || "another language"}`;
    });
    control.append(label, toggle);
    return control;
  }

  function renderTranslationPreferenceAvailability() {
    if (!el.profileLanguage) return;
    const available = Boolean(state.translation?.enabled);
    const preferences = el.profileLanguage.closest(".translation-preferences");
    if (preferences) preferences.hidden = !available;
    const editable = available && state.viewer?.status === "active";
    el.profileLanguage.disabled = !editable;
    el.profileAutoTranslate.disabled = !editable;
    el.profileTranslatePrivate.disabled = !editable || !state.translation?.private_content_enabled;
    el.translationPreferenceNote.textContent = available
      ? state.translation?.private_content_enabled
        ? "English is the shared discovery language. Originals are preserved. Private messages leave the application only when both people opt in and the operator enables its reviewed provider."
        : "English is the shared discovery language. Originals are preserved. Private-message translation is disabled by this operator."
      : "Automatic translation is not configured on this server. Original content remains available.";
  }

  function renderTranslationPreferences() {
    renderTranslationPreferenceAvailability();
    // Background capability refreshes may change permissions, never an unsaved draft.
    if (!el.profileLanguage || profileDraftDirty) return;
    const languages = Array.isArray(state.translation?.languages) && state.translation.languages.length
      ? state.translation.languages : [{ code: "en", label: "English" }];
    const selected = state.viewer?.preferred_language || state.translation?.default_language || "en";
    clear(el.profileLanguage);
    for (const language of languages) {
      if (!language?.code || !language?.label) continue;
      const option = create("option", "", language.label);
      option.value = language.code;
      option.selected = language.code === selected;
      el.profileLanguage.append(option);
    }
    if (![...el.profileLanguage.options].some((option) => option.value === selected)) {
      const fallback = create("option", "", selected.toUpperCase());
      fallback.value = selected; fallback.selected = true; el.profileLanguage.append(fallback);
    }
    el.profileAutoTranslate.checked = Boolean(state.viewer?.auto_translate_public);
    el.profileTranslatePrivate.checked = Boolean(state.viewer?.translate_private_messages);
  }

  function friendlyValidationMessage(message) {
    // Server-side validation speaks in field terms ("value is not a valid email address: ...");
    // people read the reason after a plain instruction.
    return String(message ?? "").replace(/^value is not a valid email address:?\s*/i, "Enter a valid email address. ");
  }

  async function sessionExpired() {
    // Background pollers used to be the only thing that noticed; every button just failed. A wrong password on
    // a credential check answers 401 as well while the session is still valid, so refreshAuth asks the server
    // and signs the tab out only if the session really ended. Nothing here rejects: api() does not wait for it.
    const viewerId = state.viewer?.id || null;
    try {
      const result = await refreshAuth();
      // Another tab signed in as someone else: reload into that account, as the 409 path does.
      if (viewerId && result?.user && result.user.id !== viewerId) reloadAfterAccountChange();
    } catch (_) { /* Unanswered: the next request asks again. */ }
  }

  function detailMessage(payload, statusCode) {
    const detail = payload?.detail;
    if (Array.isArray(detail)) return detail.map((item) => friendlyValidationMessage(item.msg)).join(" ");
    if (typeof detail === "string") return detail;
    if (detail && typeof detail.message === "string") return detail.message;
    return `Request failed (${statusCode}).`;
  }

  function stampMessageRead(payload, started) {
    const wall = Date.now(), mono = globalThis.performance?.now?.() ?? null;
    const elapsed = Math.max(0, wall - started.wall,
      Number.isFinite(mono) && Number.isFinite(started.mono) ? mono - started.mono : 0);
    const seen = new WeakSet(), key = Symbol.for("louderr.message.read-timing");
    function visit(value, server = null) {
      if (!value || typeof value !== "object" || seen.has(value)) return;
      seen.add(value);
      if (Array.isArray(value)) { for (const item of value) visit(item, server); return; }
      if (typeof value.server_time === "string") server = value.server_time;
      if (value.expires_at != null || value.reply_to?.expires_at != null) {
        // Symbols survive internal object spreads but stay out of JSON bodies,
        // cache keys and rendered content comparisons.
        Object.defineProperty(value, key, {value: {wall, mono, elapsed, server}, enumerable: true, configurable: true});
      }
      for (const field of ["items", "messages", "conversations", "conversation", "last_message", "current", "reply_to"])
        visit(value[field], server);
    }
    visit(payload);
  }

  async function api(path, options = {}) {
    const readStarted = {wall: Date.now(), mono: globalThis.performance?.now?.() ?? null};
    const updating = !["GET", "HEAD", "OPTIONS"].includes(String(options.method || "GET").toUpperCase());
    if (updating) {
      state.updatePendingWrites = (state.updatePendingWrites || 0) + 1;
      // Only boot receipt reuse observes this revision; normal reads and writes keep their ownership.
      state.startupWriteRevision = (state.startupWriteRevision || 0) + 1;
    }
    try {
      const requestViewerId = state.viewer?.id || null;
      const requestCsrf = state.csrf;
      const requestSocialState = state.messages;
      const headers = new Headers(options.headers || {});
      const init = { method: options.method || "GET", credentials: "same-origin", headers };
      if (options.signal) init.signal = options.signal;
      if (options.json !== undefined) {
        headers.set("Content-Type", "application/json");
        init.body = JSON.stringify(options.json);
      } else if (options.body) {
        init.body = options.body;
      }
      if (options.csrf) {
        if (!state.csrf) throw new Error("Your session needs to be refreshed.");
        headers.set("X-CSRF-Token", state.csrf);
      }
      if (state.viewer?.id && path !== "/api/auth/me") headers.set("X-Account-ID", state.viewer.id);
      const response = await window.LobbyChallenges.fetch(path, init);
      const type = response.headers.get("content-type") || "";
      const payload = type.includes("application/json") ? await response.json() : await response.text();
      if (!response.ok) {
        const error = new Error(detailMessage(payload, response.status));
        error.status = response.status;
        error.retryAfter = response.headers.get("Retry-After");
        error.code = typeof payload?.detail === "object" ? payload.detail.code : null;
        error.reauthenticationRequired = response.headers.get("X-Reauthentication-Required") === "true";
        error.reauthenticationMethod = response.headers.get("X-Reauthentication-Method");
        error.accountChanged = response.status === 409
          && typeof payload?.detail === "string"
          && payload.detail.includes("account changed in another tab");
        error.staffMfaRequired = response.headers.get("X-Staff-MFA-Required") === "true";
        if (response.status === 401 && state.viewer?.id === requestViewerId && requestViewerId
            && state.csrf === requestCsrf && state.messages === requestSocialState
            && !path.startsWith("/api/auth/")) sessionExpired();
        // The session now belongs to a different account, changed in another tab.
        // Showing the previous account over it is worse than reloading into the
        // account the server actually has.
        if (response.status === 409 && error.accountChanged && state.viewer?.id === requestViewerId
            && requestViewerId && state.csrf === requestCsrf
            && state.messages === requestSocialState) reloadAfterAccountChange();
        throw error;
      }
      // This is the ONLY thing that invalidates the hub when a relationship changes. applyEvent
      // lists "relationship.changed" among the types it invalidates on, which reads as though the
      // event covers it -- but no such event exists: that string appears nowhere else in the
      // repository and nothing publishes it. Removing this line would silently stop blocking and
      // muting from clearing the private Home cache.
      if (init.method !== "GET" && (path.startsWith("/api/relationships")
        || /^\/api\/journeys\/[^/]+\/subscription$/.test(path))) globalThis.LouderrHome?.invalidate();
      if (path.startsWith("/api/messages/") || /^\/api\/state(?:\?|$)/.test(path)) stampMessageRead(payload, readStarted);
      return payload;
    } finally {
      if (updating) state.updatePendingWrites -= 1;
      state.appUpdate?.present();
    }
  }

  const capabilityKeys = Object.freeze([
    "beta_access", "registration", "email_delivery", "password_recovery", "email_change",
    "social_login", "commerce", "uploads", "media_publication", "flow", "translation",
    "community_writes", "staff_actions",
    // The server has always sent this; leaving it out of the allowlist meant
    // state.capabilities.studio_response_audio was undefined no matter what the server
    // said, so the two guards reading it were permanently off rather than gated.
    "studio_response_audio", "studio_video_remix", "message_files",
    // Adults-only calls and live video on LOUDERR's own media server.
    "calls", "live",
  ]);

  function applyCapabilities(payload) {
    const valid = payload && ["full", "no-cost-closed-beta-v1"].includes(payload.profile)
      && capabilityKeys.every((key) => typeof payload[key] === "boolean"
        || (["message_files", "calls", "live"].includes(key) && payload[key] === undefined));
    const next = valid
      ? Object.freeze(Object.fromEntries([["profile", payload.profile], ...capabilityKeys.map((key) => [key, payload[key] === true])]))
      : Object.freeze(Object.fromEntries([["profile", "unavailable"], ...capabilityKeys.map((key) => [key, false])]));
    state.capabilities = next;
    state.federatedRegistration = valid && next.social_login && payload.federated_registration === true;
    document.documentElement.dataset.capabilitiesLoaded = "true";
    document.documentElement.dataset.capabilityProfile = next.profile;
    $$('[data-requires-capability]').forEach((node) => {
      node.classList.toggle("capability-withheld", !next[node.dataset.requiresCapability]);
    });
    if (!next.commerce) {
      clearBusinessAds();
      if (!state.startupPending && ["advertise", "premium"].includes(state.view)) setView("all");
      if (["purchases", "creator"].includes(state.accountSection)) setAccountSection("profile", false);
    }
    if (!state.startupPending && !next.flow && state.view === "flow") setView("all");
    if (typeof document.querySelectorAll === "function") {
      // The closed beta serves the FLOW queue only; Now, Stories and 15 Seconds answer 404 there.
      const flowLimited = next.profile === "no-cost-closed-beta-v1";
      document.querySelectorAll("[data-flow-view]").forEach((tab) => { if (tab.dataset.flowView !== "feed") tab.hidden = flowLimited; });
      if (flowLimited && state.flow?.view && state.flow.view !== "feed") state.flow.view = "feed";
    }
    if (!next.social_login) state.federatedProviders = [];
    if (!next.translation) {
      state.translation = {
        enabled: false, private_content_enabled: false, default_language: "en",
        languages: [{ code: "en", label: "English" }],
      };
    }
    applyCapacity(payload && payload.capacity);
    renderFederatedAuth();
    renderTranslationPreferences();
  }

  // Seats left in the room. The server sends this only while a capacity is set, so its absence
  // means "no cap", not "full".
  function applyCapacity(value) {
    const numbers = value && ["limit", "taken", "remaining"].every((key) => Number.isFinite(Number(value[key])));
    state.capacity = numbers
      ? Object.freeze({
        limit: Number(value.limit),
        taken: Number(value.taken),
        remaining: Number(value.remaining),
        open: value.open !== false,
        registration_open: value.open !== false && value.registration_open !== false,
      })
      : null;
    renderCapacity();
  }

  // Whether the door would accept a new registration, as opposed to whether the room still has
  // places: seats held for outstanding invite codes are places, and a code still redeems into one.
  function seatsAcceptRegistration(seats) {
    if (!seats) return true;
    return seats.registration_open === undefined ? Boolean(seats.open) : Boolean(seats.registration_open);
  }

  function renderCapacity() {
    const note = $("#capacity-note");
    if (!note) return;
    const seats = state.capacity;
    if (!seats) { note.hidden = true; note.textContent = ""; return; }
    const canRegister = seatsAcceptRegistration(seats);
    // Keep the form focused on joining; capacity only needs attention when joining is unavailable.
    note.hidden = canRegister;
    note.classList?.toggle?.("is-full", !canRegister);
    note.textContent = !seats.open
      ? "LOUDERR is full right now. More places open as the room grows."
      : canRegister ? ""
        : "The last places are held for members who have been sent a code. Ask a member for one.";
    // A full room must say so before the form is filled in, not after it is submitted. The fields
    // stay visible so the person can see what joining involves; only the button is held back.
    const submit = $("#register-form button[type='submit']");
    if (submit && !state.authSubmissionActive) {
      submit.disabled = !canRegister;
      submit.textContent = canRegister ? "Create account" : "Full right now";
    }
    // Capabilities land after the first paint, so the closed-beta note is redrawn with the seats.
    const intro = $("#home-intro");
    if (intro && !intro.hidden) renderGuestIntroCopy(intro);
  }

  async function loadCapabilities(attempt = 0) {
    if (state.capabilityRequest) return state.capabilityRequest;
    if (state.capabilityRetryTimer != null) clearTimeout(state.capabilityRetryTimer);
    state.capabilityRetryTimer = null;
    state.capabilityRequest = (async () => {
      try { applyCapabilities(await api("/api/capabilities")); }
      catch (_) { applyCapabilities(null); }
      if (attempt > 0 && state.capabilities.community_writes) syncCapabilityRecovery();
      // Keep server denials in force. A longer outage must not strand this tab.
      const temporarilyClosed = state.capabilities.profile === "no-cost-closed-beta-v1"
        && !state.capabilities.community_writes;
      if (!temporarilyClosed && state.capabilities.profile !== "unavailable") return;
      const delays = temporarilyClosed ? [250, 1000, 3000] : [2000, 5000, 15000];
      if (typeof setTimeout !== "function") return;
      state.capabilityRetryTimer = setTimeout(() => {
        state.capabilityRetryTimer = null;
        if (typeof document !== "undefined" && document.hidden) return;
        if (typeof navigator !== "undefined" && navigator.onLine === false) return;
        void loadCapabilities(Math.min(attempt + 1, 3));
      }, attempt < 3 ? delays[attempt] : 30000);
    })().finally(() => { state.capabilityRequest = null; });
    return state.capabilityRequest;
  }

  function resumeCapabilityRecovery() {
    if (document.hidden || navigator.onLine === false) return;
    const pending = state.capabilities.profile === "unavailable"
      || (state.capabilities.profile === "no-cost-closed-beta-v1" && !state.capabilities.community_writes);
    if (pending) return loadCapabilities(3);
  }

  function syncCapabilityRecovery() {
    // Re-enable existing controls without reloading content, replacing media or navigating.
    renderAccount();
    updateChatComposer();
    if (state.view === "profile") syncProfileWriteControls();
    if (state.view === "messages") renderMessageThread();
    if (!el.empty.hidden) renderFeedEmptyState();
  }

  // The sign-in form and Settings are two surfaces now, so an error goes to the one being looked
  // at: Settings into its own slot, everything else into the dialog it belongs to. Resolving it
  // here rather than at twenty-four call sites is what keeps account deletion and data export
  // from reporting a failure into a closed dialog nobody can see.
  function accountErrorSlot() {
    return state.view === "settings" ? el.settingsError : el.accountError;
  }

  function writesPausedNotice() {
    toast(state.capabilities.profile === "unavailable"
      ? "Still connecting to LOUDERR. Try again in a moment."
      : "Posting, reactions and messages are paused right now.", true);
  }

  // A toast may carry one action, the way a snackbar offers Undo: it then stays a little longer
  // and accepts a tap. Without an action it is the same quiet line as before.
  function toast(message, error = false, options = {}) {
    const keepAtBottom = state.view === "messages"
      && (state.messages.participant || state.messages.conversation?.kind === "group")
      && el.messageList.scrollHeight - el.messageList.scrollTop - el.messageList.clientHeight < 48;
    placeToast();
    const action = options && options.action && typeof options.action.run === "function" ? options.action : null;
    if (action) {
      const button = create("button", "toast-action", action.label || "Undo");
      button.type = "button";
      button.addEventListener("click", () => {
        clearTimeout(el.toast._timer);
        el.toast.classList.remove("is-visible");
        void action.run();
      });
      el.toast.replaceChildren(create("span", "toast-text", message), button);
    } else el.toast.textContent = message;
    el.toast.classList.toggle("is-error", error);
    el.toast.classList.toggle("has-action", Boolean(action));
    el.toast.classList.add("is-visible");
    clearTimeout(el.toast._timer);
    el.toast._timer = setTimeout(() => el.toast.classList.remove("is-visible"), action ? 6500 : 3200);
    if (keepAtBottom) scrollMessagesBottom();
  }

  function placeToast() {
    // A toast must share the active modal's top layer to remain visible.
    const openDialogElement = document.activeElement?.closest?.("dialog[open]")
      || document.querySelector("dialog[open]");
    const target = openDialogElement
      || (state.view === "messages" && (state.messages.participant || state.messages.conversation?.kind === "group")
        ? document.getElementById("message-notice-slot") : document.body);
    if (el.toast.parentElement !== target) target.append(el.toast);
  }

  function syncMessageThreadClass() {
    const open = state.view === "messages" && Boolean(state.viewer)
      && Boolean(state.messages.participant || state.messages.conversation?.kind === "group");
    document.body.classList.toggle("message-thread-open", open);
  }

  function showError(node, message) {
    node.alertRevision = (node.alertRevision || 0) + 1;
    delete node.mediaAlert;
    node.textContent = message;
    node.hidden = false;
    // Account form feedback must remain readable after the error adds height.
    if (["profile-save-error", "age-error", "verification-error", "settings-error"].includes(node.id)
        && node.closest?.("dialog[open], .settings-view:not([hidden])") && node.getClientRects().length) {
      node.scrollIntoView({ block: "nearest", behavior: "instant" });
    }
  }

  function hideError(node) {
    node.alertRevision = (node.alertRevision || 0) + 1;
    delete node.mediaAlert;
    node.hidden = true;
    node.textContent = "";
  }

  function messageDeliveryError(error) {
    const unconfirmed = "We couldn't confirm delivery. Check the conversation before trying again.";
    if (error?.name === "TypeError"
      && ["Failed to fetch", "Load failed", "NetworkError when attempting to fetch resource."].includes(error.message)) {
      return unconfirmed;
    }
    return error?.message || unconfirmed;
  }

  // The raw open. Only openDialogWithHistory reaches it, so nothing puts a dialog on screen
  // without an entry behind it.
  function showDialog(dialog) {
    if (!dialog.open) dialog.showModal();
  }

  // Every dialog is somewhere you can leave again, so opening one always adds a history entry:
  // Back, the phone's back gesture and the frame's back control all close it instead of leaving
  // the app. A path and a return route pass straight through for the dialogs that have a URL.
  function openDialog(dialog, path = null, options = {}) {
    openDialogWithHistory(dialog, path, options);
  }

  // LOUDERR's own confirmation in place of the browser's box. The question becomes the title,
  // the consequence the body, and the verb sits on the button, the way the feed apps people
  // already use ask before something irreversible. It shadows window.confirm on purpose so every
  // call site and every test hook keeps the same name; the difference is that it must be awaited.
  // Resolves true only when the verb was tapped; Cancel, Escape, Back and the backdrop resolve false.
  async function confirm(message, options = {}) {
    const signal = options.signal;
    if (signal?.aborted) return false;
    const dialog = $("#confirm-dialog");
    if (!dialog || typeof dialog.showModal !== "function") return globalThis.confirm(message);
    const text = String(message || "").trim();
    const sentences = text.split(/(?<=[.?!])\s+/).filter(Boolean);
    const question = sentences.find(sentence => sentence.endsWith("?")) || sentences[0] || text;
    const title = options.title || question;
    const body = options.body ?? sentences.filter(sentence => sentence !== question).join(" ");
    const verbs = { "Log out": "Log out", Delete: "Delete", Block: "Block", Remove: "Remove", Leave: "Leave",
      Discard: "Discard", Revoke: "Revoke", Ban: "Ban", Suspend: "Suspend", Warn: "Warn", Mute: "Mute",
      Restrict: "Restrict", Transfer: "Transfer ownership", Switch: "Switch accounts", Restore: "Restore access" };
    const verb = Object.keys(verbs).find(word => title.startsWith(word));
    const destructive = options.destructive
      ?? ["Log out", "Delete", "Block", "Remove", "Leave", "Discard", "Revoke", "Ban", "Suspend", "Transfer"].includes(verb);
    const accept = $("#confirm-accept"), cancel = $("#confirm-cancel");
    const titleNode = $("#confirm-title"), bodyNode = $("#confirm-body");
    const previous = dialog.confirmOperation;
    // Updating an open prompt keeps its history entry. A closed prompt must drain its
    // queued close event (including history settlement) before this dialog is reused.
    const ready = previous && !dialog.open ? previous.closed : null;
    previous?.retire({ keepOpen: dialog.open });
    return new Promise((resolve) => {
      let settled = false, displayed = false, releaseClosed = null;
      const operation = { closed: ready || Promise.resolve(), retire: finish };
      dialog.confirmOperation = operation;
      const owns = () => dialog.confirmOperation === operation;
      function release() {
        dialog.removeEventListener("close", onClose);
        releaseClosed?.(); releaseClosed = null;
      }
      function finish({ accepted = false, keepOpen = false } = {}) {
        if (settled) return;
        settled = true;
        signal?.removeEventListener("abort", abort);
        if (displayed && owns()) {
          if (accept.onclick === acceptClick) accept.onclick = null;
          if (cancel.onclick === cancelClick) cancel.onclick = null;
          titleNode.textContent = ""; bodyNode.textContent = ""; bodyNode.hidden = true;
          if (keepOpen) release();
          else if (dialog.open) dialog.close("cancel");
        }
        resolve(accepted);
      }
      function onClose() {
        if (dialog.open) return;
        release();
        if (owns()) finish({ accepted: dialog.returnValue === "confirm" });
      }
      function abort() { if (owns()) finish(); }
      function acceptClick() { if (owns() && !settled) dialog.close("confirm"); }
      function cancelClick() { if (owns() && !settled) dialog.close("cancel"); }
      function start() {
        if (settled || !owns()) return;
        if (signal?.aborted) { finish(); return; }
        displayed = true;
        operation.closed = new Promise(done => { releaseClosed = done; });
        titleNode.textContent = title; bodyNode.textContent = body; bodyNode.hidden = !body;
        accept.textContent = options.confirmLabel || (verb ? verbs[verb] : "Continue");
        cancel.textContent = options.cancelLabel || "Cancel";
        accept.classList.toggle("danger-button", destructive);
        accept.classList.toggle("publish-button", !destructive);
        dialog.returnValue = "";
        dialog.addEventListener("close", onClose);
        accept.onclick = acceptClick; cancel.onclick = cancelClick;
        try { openDialog(dialog); cancel.focus(); }
        catch (_) { release(); finish(); }
      }
      signal?.addEventListener("abort", abort, { once: true });
      if (ready) void ready.then(start);
      else start();
    });
  }

  // Dialogs behave like pages: opening one adds a history entry, so the phone's Back
  // closes it instead of leaving the app, and a path such as /settings/privacy or
  // /create addresses the same screen. A dialog opened on top of another reuses that
  // entry, so Back always returns to the page beneath rather than to a closed layer.
  function openDialogWithHistory(dialog, path = null, options = {}) {
    if (!dialog || dialog.open) return;
    attachDialogHistory(dialog, path, options);
    showDialog(dialog);
  }

  // An entry has to name the dialog it belongs to, and the modules that build their own dialogs
  // mostly do not give them an id. An anonymous one gets a key of its own the first time it opens;
  // the id stays whatever its author chose, including nothing.
  let anonymousDialogs = 0;

  function dialogName(dialog) {
    if (dialog.id) return dialog.id;
    if (!dialog.dataset.historyKey) dialog.dataset.historyKey = `dialog-${++anonymousDialogs}`;
    return dialog.dataset.historyKey;
  }

  function attachDialogHistory(dialog, path = null, options = {}) {
    // A real <dialog> always carries a dataset. Anything without one cannot hold the marker this
    // relies on, so there is nothing here to attach.
    if (!dialog?.dataset || dialog.dataset.historyEntry || dialog.dataset.historyPending) return;
    try {
      // back() is asynchronous. A quick API response can open the next dialog before
      // that traversal finishes; defer its entry so the pending Back cannot dismiss it.
      if (settleDialogHistory.pendingBack) {
        dialog.dataset.historyPending = "true";
        settleDialogHistory.pendingBack.dialogs.push({ dialog, path, options });
        return;
      }
      const here = `${location.pathname}${location.search}`;
      const target = path || here;
      const stored = history.state && typeof history.state === "object" ? history.state : {};
      // History state survives reloads, tab restores and Forward, so a dialog marker in it
      // only means "chained" while that parent dialog is actually open right now.
      const previousDialog = stored.louderrDialog && $$("dialog").find(node =>
        (node.id || node.dataset?.historyKey) === stored.louderrDialog && node.dataset?.historyEntry);
      const parentOpen = Boolean(previousDialog?.open);
      const replacingDialog = previousDialog && !previousDialog.open;
      const { louderrDialog: staleDialog, louderrReturn: staleReturn, louderrParent: staleParent, ...cleaned } = stored;
      void staleDialog; void staleReturn; void staleParent;
      const previous = parentOpen || replacingDialog ? stored : cleaned;
      const chained = parentOpen;
      // A deep link (the URL already names this dialog) is replaced in place and returns to
      // the page beneath it, so Back or X can never land on a URL that reopens the dialog.
      const deepLink = !chained && Boolean(path) && target === here;
      const entryState = { ...previous, louderrDialog: dialogName(dialog) };
      let mode;
      if (replacingDialog) {
        // close() queues its event. An action opening the next dialog in the same turn
        // must take over that entry before the old close handler settles it.
        mode = previousDialog.dataset.historyEntry;
        entryState.louderrReturn = previous.louderrReturn || here;
        history.replaceState(entryState, "", target);
      } else if (chained) {
        mode = "chained";
        entryState.louderrParent = { state: previous, url: here };
        entryState.louderrReturn = previous.louderrReturn || "/";
        history.replaceState(entryState, "", target);
      } else if (deepLink) {
        mode = "replace";
        entryState.louderrReturn = options.returnTo || "/";
        history.replaceState(entryState, "", target);
      } else {
        mode = "push";
        entryState.louderrReturn = here;
        history.pushState(entryState, "", target);
      }
      dialog.dataset.historyEntry = mode;
      // Dialogs wired at startup settle their entry on close. Any other dialog settles here, and
      // its close counts as the person's own, so Back never lands on a dead entry afterwards.
      if (dialog.dataset.historyWired !== "true") {
        dialog.addEventListener("close", () => {
          if (dialog.dataset.historyEntry) dialog.dataset.userClose = "true";
          settleDialogHistory(dialog);
        }, { once: true });
      }
    } catch (_) { /* history unavailable: the dialog still opens */ }
  }

  // On close: a person's own close (X, Escape, Back) of a pushed entry steps back to the page
  // beneath; a chained dialog restores its parent's entry; any other close restores the URL.
  function settleDialogHistory(dialog) {
    const entry = dialog.dataset.historyEntry;
    const userClose = dialog.dataset.userClose === "true";
    delete dialog.dataset.historyEntry;
    delete dialog.dataset.userClose;
    if (!entry || history.state?.louderrDialog !== dialogName(dialog)) return;
    const { louderrDialog, louderrReturn, louderrParent, ...rest } = history.state;
    void louderrDialog;
    if (entry === "chained") {
      history.replaceState(louderrParent?.state || rest, "", louderrParent?.url || louderrReturn || "/");
      updateBackControl();
      return;
    }
    if (entry === "push" && userClose) {
      settleDialogHistory.pendingBack = {
        returnTo: louderrReturn || "/", from: `${location.pathname}${location.search}`,
        routeRequestId: state.routeRequestId, identity: state.messages, dialogs: [],
      };
      history.back();
    }
    // A deep-linked dialog closed by code (for example sign-in completing inside it) keeps
    // its URL so the route can finish what the link asked for; only the marker is cleared.
    else if (entry === "replace" && !userClose) {
      history.replaceState(rest, "", `${location.pathname}${location.search}`);
      updateBackControl();
    } else {
      history.replaceState(rest, "", louderrReturn || "/");
      updateBackControl();
    }
  }

  function finishDialogHistoryBack() {
    const pending = settleDialogHistory.pendingBack;
    delete settleDialogHistory.pendingBack;
    if (!pending) return false;
    const here = `${location.pathname}${location.search}`;
    for (const { dialog, path, options } of pending.dialogs) {
      delete dialog.dataset.historyPending;
      if (!dialog.open) continue;
      if (here === pending.returnTo) attachDialogHistory(dialog, path, options);
      else dialog.close();
    }
    // Dismissing a same-page dialog is not a new profile/settings navigation.
    // Re-rendering that route can close controls the person has just opened.
    return here === pending.returnTo && here === pending.from
      && here === `${location.pathname}${location.search}`
      && state.routeRequestId === pending.routeRequestId && state.messages === pending.identity;
  }

  // A dialog reached by its own URL (for example /d/<id>?comments=1) opens in deep-link mode
  // and returns to the page beneath the query when closed.
  function dialogDeepLink(flag) {
    const query = new URLSearchParams(location.search);
    if (!location.pathname.startsWith("/d/") || query.get(flag) !== "1") return null;
    return { path: `${location.pathname}${location.search}`, returnTo: location.pathname };
  }

  // --- One way back -----------------------------------------------------------------------
  // The five places the bottom bar goes are roots: you are never inside Home, Discover, Video,
  // Messages or your own profile, so none of them shows a back control. Everything else was
  // entered from somewhere, and says so. The parent is only the fallback for a cold link or a
  // reload deep in the product; where we pushed an entry, Back returns to where the person was.
  const ROUTE_PARENTS = {
    d: "/", u: "/discover", t: "/discover", saved: "/", mine: "/", following: "/", topics: "/",
    unseen: "/", notifications: "/", creative: "/", advertise: "/", premium: "/", settings: "/",
    studio: "/", chat: "/", trending: "/discover", messages: "/messages",
  };
  // Counted in one place instead of at the forty call sites that push a route, so a call site
  // cannot forget. Forward counts as a step back here, which at worst sends the control to the
  // parent rather than to the previous page.
  const nav = { entries: 0 };

  // The post at the reading position: the first one whose top is on screen, else the one covering it.
  // Back holds this post in place, because a bare offset drifts when content above it changed height
  // while the reader was away (an own post's Fair start panel loaded, a chip row re-rendered).
  function readingAnchor() {
    if (typeof document === "undefined") return null;
    for (const list of ["feed", "discover-drop-list", "profile-drop-list", "profile-drop-grid", "flow-feed"]) {
      const container = document.getElementById(list);
      if (!container?.getClientRects().length) continue;
      let covering = null;
      for (const node of container.children) {
        // Profile grid tiles carry their Drop as a property, every other list as data-drop-id.
        const id = node.dataset?.dropId || node.profileDropId;
        if (!id) continue;
        const { top, bottom } = node.getBoundingClientRect();
        if (top >= innerHeight) break;
        if (top >= 0) return { list, id, top };
        if (bottom > 0) covering = { list, id, top };
      }
      if (covering) return covering;
    }
    return null;
  }

  function countRoutePushes() {
    const push = history.pushState.bind(history);
    history.pushState = (data, title, url) => {
      // Save before a destination's shorter loading view clamps the document.
      // Native same-document restoration does not retain this position in every engine.
      history.replaceState({ ...history.state, louderrScroll: { left: window.scrollX, top: window.scrollY, anchor: readingAnchor() } }, "");
      nav.entries += 1; push(data, title, url); updateBackControl();
    };
  }

  function restoreRouteScroll(operation, position) {
    if (!position || !Number.isFinite(position.left) || !Number.isFinite(position.top)
      || position.left < 0 || position.top < 0) return;
    const requestId = state.routeRequestId;
    const path = `${location.pathname}${location.search}`;
    let interacted = false;
    const stop = () => { interacted = true; };
    const events = ["wheel", "touchmove", "pointerdown", "keydown"];
    for (const event of events) addEventListener(event, stop, { passive: true });
    const anchor = position.anchor;
    const anchorNode = () => {
      const list = anchor && document.getElementById(anchor.list);
      const node = list && [...list.children].find(child => (child.dataset?.dropId || child.profileDropId) === anchor.id);
      return node?.getClientRects().length ? node : null;
    };
    // Video scrolls inside #flow-feed on larger screens, where re-showing the view lets the browser
    // re-snap to another card; every other list, and Video on short screens, scrolls the page.
    const ownScroller = node => {
      const list = node.parentElement;
      if (!list || !(list.scrollHeight > list.clientHeight + 1) || typeof getComputedStyle !== "function") return null;
      return /auto|scroll/.test(getComputedStyle(list).overflowY) ? list : null;
    };
    const restore = () => {
      if (interacted || state.routeRequestId !== requestId || path !== `${location.pathname}${location.search}`) return;
      const node = anchorNode();
      const inner = node && ownScroller(node);
      if (inner) {
        scrollTo({ left: position.left, top: position.top, behavior: "instant" });
        inner.scrollTop += node.getBoundingClientRect().top - anchor.top;
        return;
      }
      const top = node ? window.scrollY + node.getBoundingClientRect().top - anchor.top : position.top;
      scrollTo({ left: position.left, top, behavior: "instant" });
    };
    // Until the route settles, a late layout change or a scroll nobody asked for (scroll anchoring on a
    // chip row that re-rendered, a panel that loaded) must not move the post being read.
    const hold = () => {
      const node = anchorNode();
      if (node && Math.abs(node.getBoundingClientRect().top - anchor.top) > 1) restore();
    };
    const settle = anchor && typeof ResizeObserver === "function" ? new ResizeObserver(hold) : null;
    if (settle) {
      settle.observe(document.body);
      // Captured, so a list's own scrolling (which does not bubble) is held as well as the page's.
      addEventListener("scroll", hold, { passive: true, capture: true });
    }
    const clean = () => {
      for (const event of events) removeEventListener(event, stop);
      if (settle) { settle.disconnect(); removeEventListener("scroll", hold, true); }
    };
    // Reused content has its height immediately. A cold destination may need its read first.
    requestAnimationFrame(restore);
    Promise.resolve(operation).then(() => requestAnimationFrame(() => { restore(); clean(); }), clean);
  }

  function backTarget() {
    const parts = location.pathname.split("/").filter(Boolean);
    if (!parts.length) return null;
    const [root] = parts;
    if (parts.length === 1 && ["discover", "flow", "messages", "chat"].includes(root)) return null;
    if (root === "u" && parts.length === 2 && state.viewer?.handle
      && decodeURIComponent(parts[1]).toLowerCase() === state.viewer.handle.toLowerCase()) return null;
    return ROUTE_PARENTS[root] || "/";
  }

  function dialogsWithHistory() {
    return $$("dialog[open]").filter((dialog) => dialog.dataset.historyEntry);
  }

  // Back closes what is on top: the newest dialog first, then the page.
  // Escape is how a dialog is asked to close, and several of ours do real work in that handler: the
  // Story overlays stop playback, drop their leave guard, take themselves out of the document and hand
  // focus back. Closing one behind its back would skip all of it, so ask the way Escape asks and close
  // it ourselves only when nothing takes over.
  function requestDialogClose(dialog) {
    dialog.dataset.userClose = "true";
    if (!dialog.dispatchEvent(new Event("cancel", { cancelable: true }))) return;
    dialog.close();
  }

  function goBack() {
    const dialog = dialogsWithHistory().pop();
    if (dialog) {
      requestDialogClose(dialog);
      return;
    }
    // Settings steps back inside itself first. A section opened from the list carries a
    // history entry and one opened from a link does not, and only backFromAccountSection knows
    // which - the frame would otherwise leave Settings altogether from a linked section. The
    // profile editor reached directly is excluded because that branch calls goBack itself.
    if (state.view === "settings" && state.accountSection && state.accountSection !== "settings"
      && !(state.accountSection === "profile" && accountEditorDirect)) {
      backFromAccountSection();
      return;
    }
    if (nav.entries > 0) { history.back(); return; }
    const parent = backTarget();
    if (!parent) return;
    // Going up replaces rather than pushes, so this entry does not pile up and Back from the parent
    // leaves the way it would have from a cold link. replaceState is not the counted one, so the
    // control has to be told the route changed.
    history.replaceState({}, "", parent);
    void routeLocation();
    updateBackControl();
  }

  function updateBackControl() {
    const control = $("#back-control");
    if (!control) return;
    // Screen headers reserve space for navigation, including while the body scrolls.
    // Keep the same history control there, then return it to the page frame on other routes.
    const parent = location.pathname.startsWith("/messages/")
      ? $("#messages .message-thread-header")
      : /^\/settings(?:\/|$)/.test(location.pathname)
        ? $("#account-dialog .dialog-header") : $("#main-content");
    if (parent && control.parentElement !== parent) parent.prepend(control);
    // A dialog carries its own way out; the frame's control belongs to the page beneath it.
    control.hidden = Boolean(dialogsWithHistory().length) || !backTarget();
  }

  // Eighteen module files build their own dialogs and open them straight through showModal, so
  // a convention would have to be remembered nineteen times. Watching the open attribute gives
  // every modal dialog the same way out, whoever opened it and whenever it was written.
  function watchDialogs() {
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        const dialog = record.target;
        if (dialog?.tagName !== "DIALOG" || !dialog.open) continue;
        if (dialog.dataset.historyEntry || dialog.dataset.noHistory === "true") continue;
        let modal = false;
        try { modal = dialog.matches(":modal"); } catch (_) { modal = false; }
        if (modal) attachDialogHistory(dialog);
      }
      updateBackControl();
    });
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ["open"] });
    return observer;
  }

  // A sheet is a choice you can decline, so the dark area behind it closes it, the way every
  // sheet on a phone does. A screen holds work and does not close by a mistap.
  function closeSheetOnBackdrop(event) {
    const dialog = event.target;
    if (dialog?.tagName !== "DIALOG" || !dialog.open || dialog.dataset.screen === "true") return;
    if (!event.isTrusted) return;
    const box = dialog.getBoundingClientRect?.();
    if (!box) return;
    const inside = event.clientX >= box.left && event.clientX <= box.right
      && event.clientY >= box.top && event.clientY <= box.bottom;
    if (inside) return;
    requestDialogClose(dialog);
  }

  function closeNestedDialogOnEscape(event) {
    if (event.key !== "Escape" || event.defaultPrevented || event.isComposing || event.keyCode === 229) return;
    const dialogs = document.querySelectorAll("dialog[open]:modal");
    const dialog = document.activeElement?.closest?.("dialog[open]:modal");
    if (dialogs.length < 2 || !dialog) return;
    // Native close-watcher groups can dismiss multiple dialogs with one Escape.
    // Request only the focused layer, preserving its cancel/busy handlers.
    event.preventDefault();
    if (typeof dialog.requestClose === "function") dialog.requestClose();
    else if (dialog.dispatchEvent(new Event("cancel", { cancelable: true }))) dialog.close();
  }

  function isGlobalSearchShortcut(event) {
    if (event.key !== "/" || event.defaultPrevented || event.isComposing || event.keyCode === 229
      || event.ctrlKey || event.metaKey || event.altKey || document.querySelector("dialog[open]")) return false;
    const target = event.target || document.activeElement;
    return !target?.isContentEditable
      && !target?.closest?.('input, textarea, select, [role="textbox"], [role="combobox"]');
  }

  function initials(name) {
    const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
    return parts.length > 1 ? `${parts[0][0]}${parts.at(-1)[0]}`.toUpperCase() : parts[0]?.slice(0, 2).toUpperCase() || "?";
  }

  function avatarHue(value) {
    let hash = 0;
    for (const char of String(value || "lobby")) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
    return hash % 360;
  }

  function previewImageUrl(url, width) {
    return typeof url === "string" && /^\/api\/media\/[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}\/thumbnail$/.test(url)
      && [160, 480, 960].includes(width) ? `${url}?width=${width}` : url;
  }

  function styleAvatar(node, user) {
    if (!node) return;
    const name = user?.display_name || "Guest";
    node.textContent = initials(name);
    node.style.background = `hsl(${avatarHue(user?.id || name)} 38% 34%)`;
    if (user?.avatar_url) {
      node.textContent = "";
      node.style.backgroundImage = `url(${encodeURI(previewImageUrl(user.avatar_url, 160))})`;
      node.style.backgroundSize = "cover";
    } else {
      node.style.backgroundImage = "none";
    }
  }

  function applyProductName(value) {
    const name = String(value || "LOUDERR").trim().slice(0, 40) || "LOUDERR";
    state.productName = name;
    $$('[data-product-name]').forEach((node) => { node.textContent = name; });
    $$(".brand").forEach((node) => {
      const brandName = $(".brand-wordmark", node)?.textContent?.trim() || name;
      node.setAttribute("aria-label", `${brandName} home`);
    });
    $$('[data-search-input]').forEach((node) => { node.placeholder = `Search ${name}`; });
  }

  function readableFileSize(bytes) {
    if (!Number.isFinite(bytes) || bytes <= 0) return "";
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
  }

  function renderFileControl(field, input, label, action, emptyLabel) {
    const file = input?.files?.[0] || null;
    field?.classList.toggle("has-file", Boolean(file));
    if (label) label.textContent = file ? `${file.name} · ${readableFileSize(file.size)}` : emptyLabel;
    if (action) action.textContent = file ? "Replace" : "Choose";
  }

  function clearGalleryPreviews() {
    stopServerSoundPreviews();
    for (const url of state.gallery.previewUrls) URL.revokeObjectURL(url);
    state.gallery.previewUrls.clear();
  }

  function galleryItemsFromDrop(drop) {
    if (!drop) return [];
    const returnedMedia = Array.isArray(drop.media) ? drop.media : null;
    const source = returnedMedia ?? (
      drop.media_asset_id && drop.media_url && drop.media_type
        ? [{
          position: 0,
          media_asset_id: drop.media_asset_id,
          media_url: drop.media_url,
          thumbnail_url: drop.thumbnail_url,
          media_type: drop.media_type,
          media_status: drop.media_status,
          scanner_status: drop.scanner_status,
          media_alt_text: drop.media_alt_text,
          captions_url: drop.captions_url,
          transcript_url: drop.transcript_url,
        }]
        : []
    );
    return source.map((item, index) => ({
      key: `asset:${item.media_asset_id}:${item.position ?? index}`,
      id: item.media_asset_id,
      file: null,
      mediaType: item.media_type,
      url: item.media_url,
      thumbnailUrl: item.thumbnail_url,
      alt: item.media_alt_text || "",
      originalAlt: item.media_alt_text || "",
      captions: "", originalCaptions: "",
      transcript: "", originalTranscript: "",
      captionsPresent: Boolean(item.captions_url), transcriptPresent: Boolean(item.transcript_url),
      remixText: drop.video_binding?.clean_asset_id === item.media_asset_id && drop.video_text ? {
        captions: drop.video_text.captions_vtt || "", originalCaptions: drop.video_text.captions_vtt || "",
        transcript: drop.video_text.transcript_text || "", originalTranscript: drop.video_text.transcript_text || "",
        captionsPresent: Boolean(drop.video_text.captions_vtt), transcriptPresent: Boolean(drop.video_text.transcript_text),
        durationMs: drop.video_text.duration_ms,
      } : null,
      remixTextUnavailable: drop.video_binding?.clean_asset_id === item.media_asset_id && !drop.video_text,
      position: Number.isInteger(item.position) ? item.position : index,
      existing: true,
    }));
  }

  function dropLabel(drop) {
    return drop.title || drop.body?.slice(0, 80) || `${kindLabels[drop.kind] || "Post"} by ${drop.author?.display_name || "creator"}`;
  }

  function gallerySelectionIsValid(items) {
    if (items.length > 35) return "Add no more than 35 photos or videos.";
    const mediaTypes = items.map((item) => item.mediaType || item.file?.type || "");
    const visualOnly = mediaTypes.every((type) => /^(image|video)\//.test(type));
    if (items.length > 1 && !visualOnly) return "Audio must be attached on its own.";
    const newFiles = items.map((item) => item.file).filter(Boolean);
    if (newFiles.some((file) => file.size > 25 * 1024 * 1024)) return "Each file must be 25 MB or smaller.";
    if (newFiles.reduce((total, file) => total + file.size, 0) > 50 * 1024 * 1024) {
      return "The selected media must total 50 MB or less.";
    }
    return null;
  }

  function initializeComposerGallery(drop = null) {
    clearGalleryPreviews();
    delete state.gallery.videoTextAttempt;
    const items = galleryItemsFromDrop(drop);
    const positions = items.map((item) => item.position);
    state.gallery.items = items;
    state.gallery.dirty = false;
    state.gallery.locked = Boolean(items.length && positions.some((position, index) => position !== index));
    el.dropMedia.value = "";
    renderDropMediaControl();
  }

  function moveGalleryItem(index, direction) {
    if (!guardSoundSelectionEdit()) return;
    const next = index + direction;
    if (state.gallery.locked || next < 0 || next >= state.gallery.items.length) return;
    [state.gallery.items[index], state.gallery.items[next]] = [state.gallery.items[next], state.gallery.items[index]];
    state.gallery.dirty = true;
    renderDropMediaControl();
    requestAnimationFrame(() => $(`[data-gallery-index="${next}"] .gallery-move-${direction < 0 ? "up" : "down"}`, el.dropGallery)?.focus());
  }

  async function removeGalleryItem(index) {
    if (!guardSoundSelectionEdit()) return;
    if (state.gallery.locked) return;
    const gallery = state.gallery;
    const removed = gallery.items[index];
    if (!removed) return;
    const context = galleryUploadContext(removed.upload);
    if (removed.upload && !["selected", "succeeded", "rejected"].includes(removed.upload.state)) {
      try { if (!await mediaUploads().cancel(removed.upload)) return; }
      catch (error) { showUploadError(el.formError, error, context, false); return; }
    }
    if (!uploadContextCurrent(context) || !gallery.items.includes(removed)) return;
    reconcileMediaAlert(el.formError, removed.upload);
    if (removed.upload) mediaUploads().detach(removed.upload);
    gallery.items.splice(gallery.items.indexOf(removed), 1);
    if (removed?.previewUrl) {
      URL.revokeObjectURL(removed.previewUrl);
      state.gallery.previewUrls.delete(removed.previewUrl);
    }
    state.gallery.dirty = true;
    renderDropMediaControl();
  }

  function galleryPreview(item) {
    const source = item.previewUrl || item.thumbnailUrl || item.url;
    const type = item.mediaType || item.file?.type || "";
    if (!source) return create("span", "gallery-preview-fallback", "Media");
    if (type.startsWith("image/")) {
      const image = create("img"); image.src = source; image.alt = item.alt || ""; return image;
    }
    if (type.startsWith("video/")) {
      const video = create("video"); video.src = item.url || source; video.preload = "metadata"; video.controls = true; video.playsInline = true; return video;
    }
    return create("span", "gallery-preview-fallback", "Audio");
  }

  function normalizeCaptionImport(text, durationMs = null) {
    const normalized = text.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n").trim();
    if (normalized.length > 20000) throw new Error("Captions must be 20,000 characters or fewer.");
    if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(normalized)) throw new Error("The caption file contains unsupported characters.");
    const blocks = normalized.split(/\n[ \t]*\n/), header = blocks[0].split("\n", 1)[0];
    if (!/^WEBVTT(?:[ \t].*)?$/.test(header) || header.includes("-->") || blocks.length < 2) {
      throw new Error("Captions must start with WEBVTT followed by a blank line.");
    }
    const timing = /^((?:\d{2,}:)?[0-5]\d:[0-5]\d\.\d{3})[ \t]+-->[ \t]+((?:\d{2,}:)?[0-5]\d:[0-5]\d\.\d{3})(?:[ \t]+.*)?$/;
    let count = 0, previous = -1;
    for (const block of blocks.slice(1)) {
      if (!block.trim()) continue;
      const lines = block.split("\n");
      if (/^NOTE(?:[ \t]|$)/.test(lines[0]) || ["STYLE", "REGION"].includes(lines[0])) continue;
      const match = timing.exec(lines[lines[0].includes("-->") ? 0 : 1] || "");
      if (!match) throw new Error("Each caption must have a valid start and end time.");
      const [start, end] = match.slice(1, 3).map(value => value.split(":").reduce((seconds, part) => seconds * 60 + Number(part), 0));
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) throw new Error("Each caption must end after it starts.");
      if (start < previous) throw new Error("Captions must be ordered by their start time.");
      if (durationMs !== null && (!Number.isSafeInteger(durationMs) || durationMs < 1 || Math.round(end * 1000) > durationMs))
        throw new Error("Captions must fit within the finished video timeline.");
      const timingIndex = lines[0].includes("-->") ? 0 : 1;
      if (durationMs !== null && !lines.slice(timingIndex + 1).some(line => line.trim()))
        throw new Error("Each timed caption must include text.");
      previous = start; count += 1;
    }
    if (!count) throw new Error("Add at least one timed caption.");
    return normalized;
  }

  async function importGalleryCaptions(item, file, fieldCurrent = () => true, parent = item) {
    if (!file) return false;
    const context = galleryUploadContext(), key = state.composerDraft.key;
    const revision = item.captionEditRevision || 0;
    const token = {};
    item.captionImportToken = token;
    try {
      if (!/\.vtt$/i.test(file.name)) throw new Error("Choose a .vtt caption file.");
      if (!file.size || file.size > 80000) throw new Error("Choose a non-empty caption file no larger than 80 KB.");
      let buffer;
      try { buffer = await file.arrayBuffer(); }
      catch (_) { throw new Error("The caption file could not be read. Choose it again."); }
      if (buffer.byteLength > 80000) throw new Error("The caption file is larger than 80 KB.");
      let text;
      try { text = new TextDecoder("utf-8", { fatal: true }).decode(buffer); }
      catch (_) { throw new Error("Save the caption file as UTF-8 and try again."); }
      const captions = normalizeCaptionImport(text, item.durationMs ?? null);
      if (!uploadContextCurrent(context) || state.composerDraft.key !== key || !el.composer.open
          || state.composerDraft.busy || !state.gallery.items.includes(parent) || !fieldCurrent()
          || (parent !== item && parent.remixText !== item)
          || item.captionImportToken !== token || (item.captionEditRevision || 0) !== revision) return false;
      item.captions = captions;
      item.captionsRemoved = false;
      item.captionEditRevision = revision + 1;
      return true;
    } finally {
      if (item.captionImportToken === token) delete item.captionImportToken;
    }
  }

  function renderGalleryAccessibility(item, name, parent = item) {
    const details = create("details", "gallery-accessibility");
    details.open = Boolean(item.accessibilityOpen);
    details.addEventListener("toggle", () => { item.accessibilityOpen = details.open; });
    details.append(create("summary", "", parent === item ? "Captions and transcript" : "Finished remix captions and transcript"));
    if (parent !== item) details.append(create("p", "metric-note",
      `Times start at 00:00 of the finished ${(item.durationMs / 1000).toFixed(3)}-second remix.`));
    for (const [key, title, accessibleName, placeholder] of [
      ["captions", "Captions", `WebVTT captions for ${name}`, "Paste timed captions from a .vtt file"],
      ["transcript", "Transcript", `Transcript for ${name}`, "Write out what is said in this recording"],
    ]) {
      const section = create("div", "gallery-accessibility-field");
      const label = create("label");
      const input = create("textarea");
      input.rows = 3; input.maxLength = 20000; input.value = item[key] || "";
      input.placeholder = placeholder;
      input.setAttribute("aria-label", accessibleName);
      label.append(create("span", "", title), input);
      const status = create("p", "metric-note"); status.setAttribute("role", "status");
      const remove = create("button", "text-button"); remove.type = "button";
      let importing = false, importMessage = "", importError = false, importSequence = 0;
      const importButton = key === "captions" ? create("button", "text-button", "Import .vtt") : null;
      if (importButton) {
        importButton.type = "button";
        importButton.setAttribute("aria-label", `Import captions for ${name}`);
      }
      const update = () => {
        const removed = Boolean(item[`${key}Removed`]);
        input.disabled = removed || Boolean(state.composerDraft.busy);
        if (importButton) importButton.disabled = importing || Boolean(state.composerDraft.busy);
        remove.textContent = removed ? `Keep ${title.toLowerCase()}` : `Remove ${title.toLowerCase()}`;
        remove.hidden = !removed && !item[`${key}Present`] && !String(item[key] || "").trim();
        status.setAttribute("role", importError ? "alert" : "status");
        status.classList.toggle("is-error", importError);
        status.textContent = importing ? "Reading captions…" : importMessage || (removed ? "Will be removed when you save."
          : item[`${key}Present`] ? String(item[key] || "").trim()
            ? "Replacement entered. Checked when you save." : "Attached. Leave this field empty to keep it."
          : "");
        status.hidden = !status.textContent;
      };
      input.addEventListener("input", () => {
        item[key] = input.value;
        if (key === "captions") item.captionEditRevision = (item.captionEditRevision || 0) + 1;
        importMessage = ""; importError = false; update();
      });
      remove.addEventListener("click", () => {
        if (state.composerDraft.busy) return;
        item[`${key}Removed`] = !item[`${key}Removed`];
        if (key === "captions") item.captionEditRevision = (item.captionEditRevision || 0) + 1;
        importMessage = ""; importError = false;
        update();
      });
      if (importButton) {
        const fileInput = create("input"); fileInput.type = "file";
        fileInput.accept = ".vtt,text/vtt"; fileInput.hidden = true;
        fileInput.setAttribute("aria-label", `Caption file for ${name}`);
        importButton.addEventListener("click", () => { fileInput.value = ""; fileInput.click(); });
        fileInput.addEventListener("change", async () => {
          const file = fileInput.files?.[0];
          if (!file || state.composerDraft.busy) return;
          const context = galleryUploadContext();
          const sequence = ++importSequence;
          importing = true; importMessage = ""; importError = false; update();
          try {
            const applied = await importGalleryCaptions(item, file, () => input.isConnected, parent);
            if (sequence !== importSequence) return;
            if (applied) { input.value = item.captions; importMessage = "File added to your draft. Checked when you save."; update(); input.focus(); }
            else importMessage = "Import cancelled. Your draft is unchanged.";
          } catch (error) { if (sequence === importSequence) { importMessage = error.message; importError = true; } }
          finally {
            if (sequence === importSequence) {
              importing = false; fileInput.value = "";
              if (uploadContextCurrent(context) && input.isConnected) update();
            }
          }
        });
        section.append(fileInput);
      }
      update();
      const actions = create("div", "gallery-accessibility-actions");
      if (importButton) actions.append(importButton);
      actions.append(remove);
      section.append(label, status, actions); details.append(section);
    }
    return details;
  }

  function renderDropMediaControl() {
    stopServerSoundPreviews();
    clear(el.dropGallery);
    const count = state.gallery.items.length;
    el.uploadField.classList.toggle("has-file", count > 0);
    renderGalleryUploadLabel();
    el.uploadAction.textContent = count ? "Add more" : "Select from device"; el.dropMedia.setAttribute?.("aria-label", el.uploadAction.textContent);
    el.dropMedia.disabled = state.gallery.locked;
    el.editMediaNote.hidden = !state.gallery.locked;
    if (state.gallery.locked) {
      el.editMediaNote.textContent = "Some media is unavailable. Its order is preserved unless you replace the whole set.";
      const replace = create("button", "text-button", "Replace all media"); replace.type = "button";
      replace.addEventListener("click", () => {
        state.gallery.items = [];
        state.gallery.locked = false;
        state.gallery.dirty = true;
        renderDropMediaControl();
        el.dropMedia.focus();
      });
      el.editMediaNote.append(" ", replace);
    }
    state.gallery.items.forEach((item, index) => {
      const row = create("article", "composer-gallery-item");
      row.dataset.galleryIndex = String(index);
      const preview = create("div", "composer-gallery-preview"); preview.append(galleryPreview(item));
      const copy = create("div", "composer-gallery-copy");
      const name = item.file?.name || item.name || `${item.mediaType?.startsWith("image/") ? "Photo" : item.mediaType?.startsWith("video/") ? "Video" : "Audio"} ${index + 1}`;
      copy.append(create("strong", "", name));
      const alt = create("textarea");
      alt.rows = 1; alt.maxLength = 300; alt.value = item.alt;
      alt.placeholder = item.mediaType?.startsWith("image/") ? "Describe this photo" : "Describe this media";
      alt.setAttribute("aria-label", `Accessibility description for ${name}`);
      alt.addEventListener("input", () => { item.alt = alt.value; });
      copy.append(alt);
      if (item.upload) {
        const status = create("p", "metric-note upload-job-status", uploadStatusText(item.upload));
        status.setAttribute("role", "status"); status.setAttribute("aria-live", "polite");
        const check = create("button", "secondary-button upload-job-check", "Check status"); check.type = "button";
        updateUploadButton(check, item.upload, "check", "Check status");
        check.hidden = ["selected", "succeeded", "rejected"].includes(item.upload.state);
        check.addEventListener("click", async () => {
          const context = galleryUploadContext(item.upload);
          try { await mediaUploads().check(item.upload); }
          catch (error) { showUploadError(el.formError, error, context); }
        });
        const retry = create("button", "secondary-button upload-job-retry", "Retry upload"); retry.type = "button";
        updateUploadButton(retry, item.upload, "retry", "Retry upload");
        retry.hidden = !["response_lost", "failed", "cancelled", "expired"].includes(item.upload.state);
        retry.addEventListener("click", async () => {
          const context = galleryUploadContext(item.upload);
          try { await mediaUploads().retry(item.upload); }
          catch (error) { showUploadError(el.formError, error, context); }
        });
        const discard = create("button", "secondary-button upload-job-discard", "Discard selection locally"); discard.type = "button";
        discard.hidden = !item.upload.error;
        discard.addEventListener("click", async () => {
          const gallery = state.gallery, ownerId = state.viewer?.id;
          if (item.upload.ownerId !== ownerId || !gallery.items.includes(item)) return;
          if (!(await confirm("Server cancellation is unconfirmed. Private processing may continue. This only discards the selection in this tab and does not publish it. Discard locally?", { confirmLabel: "Discard" }))) return;
          if (state.viewer?.id !== ownerId || state.gallery !== gallery || !gallery.items.includes(item)) return;
          mediaUploads().detach(item.upload);
          gallery.items.splice(gallery.items.indexOf(item), 1);
          if (item.previewUrl) { URL.revokeObjectURL(item.previewUrl); gallery.previewUrls.delete(item.previewUrl); }
          gallery.dirty = true;
          renderDropMediaControl();
        });
        copy.append(status, check, retry, discard);
      }
      const controls = create("div", "composer-gallery-actions");
      const up = create("button", "gallery-move-up"); up.type = "button"; up.setAttribute("aria-label", `Move ${name} earlier`); up.append(icon("chevron"));
      up.disabled = state.gallery.locked || index === 0; up.addEventListener("click", () => moveGalleryItem(index, -1));
      const down = create("button", "gallery-move-down"); down.type = "button"; down.setAttribute("aria-label", `Move ${name} later`); down.append(icon("chevron"));
      up.hidden = down.hidden = count < 2;
      down.disabled = state.gallery.locked || index === count - 1; down.addEventListener("click", () => moveGalleryItem(index, 1));
      const remove = create("button", "gallery-remove"); remove.type = "button"; remove.setAttribute("aria-label", `Remove ${name}`); remove.append(icon("trash"));
      remove.disabled = state.gallery.locked; remove.addEventListener("click", () => removeGalleryItem(index));
      const edit = create("button", "gallery-studio", "Studio"); edit.type = "button";
      edit.hidden = !/^(image|video)\//.test(item.mediaType || "");
      edit.disabled = Boolean(item.upload && !["selected", "succeeded"].includes(item.upload.state));
      edit.addEventListener("click", () => openGalleryStudio(item));
      controls.append(edit, up, down, remove);
      renderGallerySoundControls(item, copy);
      renderGalleryVideoControls(item, copy);
      row.append(preview, copy, controls); el.dropGallery.append(row);
      if (item.remixText) {
        const details = renderGalleryAccessibility(item.remixText, "finished remix", item);
        const play = create("button", "secondary-button", "Preview captions"); play.type = "button";
        play.addEventListener("click", () => {
          try {
            const player = item.videoHandoff ? row.querySelector("[data-server-video-preview]") : row.querySelector("video");
            if (!player) throw new Error("Prepare the finished video before previewing its captions.");
            previewRemixCaptions(item, player); player.focus();
          } catch (error) { showError(el.formError, error.message); }
        });
        details.append(play); row.append(details);
      }
      else if (item.videoHandoff || item.remixTextUnavailable) row.append(create("p", "metric-note", "Final remix captions are available when the finished video is ready."));
      else if (item.mediaType?.startsWith("video/") || item.mediaType?.startsWith("audio/")) { row.append(renderGalleryAccessibility(item, name)); }
    });
    renderComposerStep();
  }

  function bindComposerMediaPicker(input, onSelection) {
    let pending = null;
    const begin = event => {
      pending = null;
      if (!el.composer.open || state.composerDraft.busy || !guardSoundSelectionEdit()) {
        event.preventDefault();
        return;
      }
      pending = {
        context: galleryUploadContext(), identity: state.messages,
        sequence: state.composerCaptureSequence = (state.composerCaptureSequence || 0) + 1,
      };
    };
    input.addEventListener("click", begin);
    input.addEventListener("drop", begin);
    input.addEventListener("cancel", () => { pending = null; input.value = ""; });
    input.addEventListener("change", event => {
      const selection = pending;
      pending = null;
      if (!selection || selection.sequence !== state.composerCaptureSequence
          || selection.identity !== state.messages || !uploadContextCurrent(selection.context)
          || !el.composer.open || state.composerDraft.busy) {
        input.value = "";
        return;
      }
      try { onSelection(event); }
      finally { input.value = ""; }
    });
  }

  function addSelectedMediaFiles(event) {
    if (!guardSoundSelectionEdit()) return;
    const input = event?.currentTarget || el.dropMedia;
    const types = {jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", gif: "image/gif", mp4: "video/mp4", mov: "video/quicktime", webm: "video/webm", weba: "audio/webm", m4a: "audio/mp4", mp3: "audio/mpeg", wav: "audio/wav", ogg: "audio/ogg"};
    const files = [...(input.files || [])].map(file => {
      const type = types[file.name.split(".").at(-1).toLowerCase()];
      return (!file.type || file.type === "application/octet-stream") && type ? new File([file], file.name, {type, lastModified: file.lastModified}) : file;
    });
    input.value = "";
    // iPhone videos (.mov) upload as they are: the server transcodes them. HEIC photos, M4V files and
    // anything over the upload limit go through Studio, which converts and compresses them in the browser.
    if (files.some(file => file.size > 25 * 1024 * 1024 || /^image\/hei[cf]/.test(file.type) || file.type === "video/x-m4v" || /\.(heic|heif|m4v)$/i.test(file.name))) {
      openStudioFiles(files); return;
    }
    appendComposerFiles(files);
  }

  function appendComposerFiles(files) {
    if (!guardVideoSelectionEdit()) return;
    if (!files.length || state.gallery.locked) return;
    const additions = files.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      state.gallery.previewUrls.add(previewUrl);
      return {
        key: `file:${crypto.randomUUID()}`,
        name: file.name,
        upload: window.LobbyUploads ? mediaUploads().select(file) : null,
        id: null,
        file,
        studioFile: file,
        mediaType: file.type,
        previewUrl,
        url: null,
        thumbnailUrl: null,
        alt: "",
        originalAlt: "",
        captions: "", originalCaptions: "", transcript: "", originalTranscript: "",
        captionsPresent: false, transcriptPresent: false,
        position: null,
        existing: false,
      };
    });
    const next = [...state.gallery.items, ...additions];
    const error = gallerySelectionIsValid(next);
    if (error) {
      for (const item of additions) {
        if (item.upload) mediaUploads().detach(item.upload);
        URL.revokeObjectURL(item.previewUrl);
        state.gallery.previewUrls.delete(item.previewUrl);
      }
      showError(el.formError, error);
      renderDropMediaControl();
      return;
    }
    hideError(el.formError);
    const firstPick = !state.editingDrop && !state.gallery.items.length;
    if (!state.editingDrop && !state.gallery.items.length && !el.dropKind.value) {
      if (additions[0]?.mediaType.startsWith("video/")) el.dropKind.value = "video";
      else if (additions[0]?.mediaType.startsWith("image/")) el.dropKind.value = "photo";
    }
    state.gallery.items = next;
    state.gallery.dirty = true;
    renderDropMediaControl();
    // Selection remains local and reviewable even if the optional download fails.
    window.LouderrOptional?.load("uploads").catch(() => {});
    renderComposerStep();
    // The first pick goes straight to the caption and starts uploading, like Instagram's Next;
    // Edit media is one tap away there.
    if (firstPick && state.gallery.step !== "details" && typeof nextComposerStep === "function") void nextComposerStep();
  }

  // Original-sound publication state stays with the actual Studio export.
  function soundJson(value) {
    if (Array.isArray(value)) return `[${value.map(soundJson).join(",")}]`;
    if (value && typeof value === "object") return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${soundJson(value[key])}`).join(",")}}`;
    return JSON.stringify(value);
  }

  async function soundDigest(bytes) {
    return [...new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))].map(byte => byte.toString(16).padStart(2, "0")).join("");
  }

  function soundScope(item = null) {
    const context = galleryUploadContext(), handoff = item?.soundHandoff;
    return () => uploadContextCurrent(context) && el.composer.open
      && (!item || state.gallery.items.includes(item) && item.soundHandoff === handoff);
  }

  function soundAssert(current) {
    if (!current()) throw new DOMException("The edit or account changed. Your previous draft is kept.", "AbortError");
  }

  async function soundFile(url, name, current, identity = null) {
    const path = new URL(url, location.origin);
    if (path.origin !== location.origin || !/^\/api\/(media|sounds)\//.test(path.pathname)) throw new Error("This sound source cannot be opened.");
    soundAssert(current);
    const response = await fetch(path.href, { credentials: "same-origin", headers: { "X-Account-ID": state.viewer.id } });
    soundAssert(current);
    if (response.status !== 200) throw new Error("This media is unavailable. Your edit is kept.");
    const wireType = response.headers.get("content-type")?.split(";")[0], wireLength = response.headers.get("content-length");
    const wireHash = /^"([a-f0-9]{64})"$/.exec(response.headers.get("etag") || "")?.[1];
    if (!/^[1-9][0-9]*$/.test(wireLength || "") || !wireHash || !wireType) throw new Error("The media response has no verifiable byte identity.");
    const expected = { size_bytes: Number(wireLength), sha256: wireHash, content_type: wireType, ...identity };
    const reader = response.body?.getReader(), chunks = []; let size = 0;
    if (!reader) throw new Error("This browser cannot read the selected media.");
    try {
      while (true) {
        const { done, value } = await reader.read(); soundAssert(current);
        if (done) break;
        size += value.byteLength;
        if (size > 25 * 1024 * 1024) throw new Error("This Studio source exceeds 25 MB.");
        chunks.push(value);
      }
    } catch (error) { await reader.cancel().catch(() => {}); throw error; }
    finally { reader.releaseLock(); }
    const file = new File(chunks, name, { type: response.headers.get("content-type")?.split(";")[0] || "application/octet-stream" });
    if (size !== expected.size_bytes || size !== Number(wireLength) || file.type !== expected.content_type
      || await soundDigest(await file.arrayBuffer()) !== expected.sha256 || expected.sha256 !== wireHash)
      throw new Error("The media changed. Reload its current sound before editing.");
    soundAssert(current); return file;
  }

  function soundResultFile(result) {
    const clean = Object.hasOwn(result || {}, "baseFile");
    if (clean && Object.hasOwn(result, "file")) throw new Error("Studio returned an ambiguous output.");
    const file = clean ? result.baseFile : result?.file;
    if (!(file instanceof File) || !file.size) throw new Error("Studio did not return a finished file. Your project is kept.");
    if (clean && (!result.soundRecipe || result.project?.outputMode !== "clean-base")) throw new Error("The saved sound recipe is missing.");
    if (!clean && result.project?.soundtrackOrigin === "platform") throw new Error("Reopen this edit with an original sound from the sound library before publishing.");
    return file;
  }

  async function adoptStudioSound(result, source = null) {
    soundResultFile(result);
    if (!result.baseFile) return result;
    const project = result.project, identity = project.soundMix?.source || source || project.credit?.soundSource;
    if (!identity?.sound_id || !/^[a-f0-9]{64}$/.test(identity.source_identity_sha256 || "")) throw new Error("Choose an original sound from the library before publishing this edit.");
    const canonical = window.LobbyStudio.canonicalSoundRecipe(result.soundRecipe);
    const digest = await soundDigest(new TextEncoder().encode(canonical));
    const capsule = project.soundMix || { version: 1, recipe: result.soundRecipe, recipe_sha256: digest, source: identity, status: "saved", authoring: null, basis: null };
    await window.LobbyStudio.importSoundMix(capsule);
    if (capsule.status !== "saved" || capsule.recipe_sha256 !== digest
        || window.LobbyStudio.canonicalSoundRecipe(capsule.recipe) !== canonical) throw new Error("The finished file and saved recipe do not match. Your project is kept.");
    return { ...result, project: { ...project, soundMix: capsule } };
  }

  function soundHandoffFromResult(result, previous = null) {
    if (!result.baseFile) return null;
    if (!state.viewer?.id) throw new Error("Sign in before preparing an original-sound publication. Your Studio project is kept.");
    return { version: 1, draftId: previous?.draftId || crypto.randomUUID(), journalRevision: previous?.journalRevision ?? null,
      ownerId: state.viewer.id, baseFile: result.baseFile, recipe: result.soundRecipe,
      recipeSha256: result.project.soundMix.recipe_sha256, source: { ...result.project.soundMix.source },
      prepare: null, render: null, job: null, action: null, previewed: null, publication: null };
  }

  async function appendStudioResult(result) {
    if (!guardVideoSelectionEdit()) return;
    const file = soundResultFile(result), handoff = soundHandoffFromResult(result);
    const count = state.gallery.items.length;
    appendComposerFiles([file]);
    if (state.gallery.items.length !== count + 1) return;
    const item = state.gallery.items.at(-1);
    item.studioProject = result.project; item.soundHandoff = handoff;
    renderDropMediaControl();
    if (handoff) await keepSoundDraft(item, soundScope(item));
  }

  async function keepSoundDraft(item, current) {
    soundAssert(current);
    const handoff = item.soundHandoff;
    if (!handoff || handoff.ownerId !== state.viewer?.id) throw new Error("The sound draft belongs to another account.");
    // Use a separate key in the established Studio store; never overwrite its edit/Undo draft.
    const value = structuredClone({ version: 1, revision: crypto.randomUUID(), project: item.studioProject, savedAt: Date.now(),
      soundPublication: { handoff, item: { id: item.id, alt: item.alt, captions: item.captions, transcript: item.transcript,
        originalAlt: item.originalAlt, originalCaptions: item.originalCaptions, originalTranscript: item.originalTranscript,
        captionsPresent: item.captionsPresent, transcriptPresent: item.transcriptPresent,
        captionsRemoved: item.captionsRemoved, transcriptRemoved: item.transcriptRemoved },
        fields: composerFields(), editingDrop: state.editingDrop, ownerId: state.viewer.id } });
    // Serialize this owner's writes, including clear, so a late older write cannot
    // restore a draft after successful publication or explicit discard.
    await queueSoundDraft(handoff.ownerId, async () => {
      const previous = await window.LobbyStudio.drafts.loadStrict(`sound-publication:${handoff.ownerId}`);
      const pending = previous?.soundPublication?.handoff;
      if (pending && pending.draftId !== handoff.draftId && (pending.render || pending.publication))
        throw new Error("Resume or explicitly discard the previous saved sound publication before replacing its device copy.");
      if ((!previous || pending?.draftId === handoff.draftId) && (previous?.revision ?? null) !== (handoff.journalRevision ?? null))
        throw new Error("This saved sound draft changed in another tab. Reload its current receipt before saving this older copy.");
      value.soundPublication.handoff.journalRevision = value.revision;
      await window.LobbyStudio.drafts.compareAndSave(`sound-publication:${handoff.ownerId}`, previous?.revision ?? null, value);
      handoff.journalRevision = value.revision;
    });
    soundAssert(current);
  }

  const soundDraftWrites = new Map();
  function queueSoundDraft(owner, action) {
    const next = (soundDraftWrites.get(owner) || Promise.resolve()).catch(() => {}).then(action);
    soundDraftWrites.set(owner, next);
    return next.finally(() => { if (soundDraftWrites.get(owner) === next) soundDraftWrites.delete(owner); });
  }

  function clearSoundDraft(owner, draftId, observedRevision) {
    if (owner && draftId) return queueSoundDraft(owner, async () => {
      const previous = await window.LobbyStudio?.drafts.loadStrict(`sound-publication:${owner}`);
      if (previous?.soundPublication?.handoff?.draftId === draftId) {
        if ((previous.revision ?? null) !== (observedRevision ?? null)) throw new Error("A newer device draft was preserved. Reload it before discarding it.");
        await window.LobbyStudio.drafts.compareAndClear(`sound-publication:${owner}`, observedRevision ?? null);
      }
    });
  }

  function saveCurrentSoundDraft(allowClosed = false) {
    const item = state.gallery.items.find(entry => entry.soundHandoff);
    if (!item || (!el.composer.open && allowClosed !== true)) return;
    const context = galleryUploadContext(), handoff = item.soundHandoff;
    const current = () => uploadContextCurrent(context) && state.gallery.items.includes(item) && item.soundHandoff === handoff;
    void keepSoundDraft(item, current).catch(error => { if (current()) showError(el.formError, `Sound draft could not be saved on this device: ${error.message}`); });
  }

  function renderSoundDraftResume() {
    const anchor = $("#composer-resume-studio"); if (!anchor) return;
    let button = $("#composer-resume-sound-publication");
    if (!button) {
      button = create("button", "secondary-button", "Resume saved sound publication"); button.id = "composer-resume-sound-publication"; button.type = "button";
      anchor.after(button);
      button.addEventListener("click", () => { const current = soundScope();
        void resumeSoundPublication().catch(error => { if (current()) showError(el.formError, error.message); }); });
    }
    button.hidden = true;
    const owner = state.viewer?.id, context = galleryUploadContext(); if (!owner) return;
    void window.LobbyStudio?.drafts.loadStrict(`sound-publication:${owner}`).then(value => {
      if (uploadContextCurrent(context)) button.hidden = Boolean(!value?.soundPublication || state.gallery.items.length);
    }).catch(error => { if (uploadContextCurrent(context)) showError(el.formError, `Saved sound draft unavailable: ${error.message}`); });
  }

  function restoreSoundDetachReceipt() {
    const owner = state.viewer?.id, drop = state.editingDrop;
    if (!owner || !drop || state.gallery.soundDetach) return;
    try {
      const value = JSON.parse(sessionStorage.getItem(soundDetachKey(owner, drop.id)) || "null");
      if (value?.ownerId === owner && value.dropId === drop.id && Number.isSafeInteger(value.expectedVersion)) state.gallery.soundDetach = value;
    } catch (_) { /* An unreadable receipt never becomes a new request. */ }
  }

  function restoreSoundFields(fields) {
    el.dropKind.value = fields.kind; el.dropTitle.value = fields.title;
    el.dropBody.value = fields.body; el.dropLink.value = fields.link;
    restoreComposerJourney(fields.journey || null);
    if (el.dropHereFirst) el.dropHereFirst.checked = fields.hereFirst;
    if (el.dropHereFirstDays) el.dropHereFirstDays.value = fields.hereFirstDays || "";
  }

  function stopServerSoundPreviews() {
    for (const player of el.dropGallery.querySelectorAll("[data-server-sound-preview], [data-server-video-preview]")) {
      player.pause(); player.removeAttribute("src"); player.load();
    }
  }

  function soundHasPendingPublication() {
    return Boolean(state.gallery.soundDetach?.publication || state.gallery.items.some(item => item.soundHandoff?.publication || item.videoHandoff?.publication));
  }

  function guardSoundSelectionEdit() {
    if (state.gallery.items.some(item => item.videoHandoff)) return guardVideoSelectionEdit();
    if (!soundHasPendingPublication()) return true;
    showError(el.formError, "Resolve the submitted sound draft before changing its media, or explicitly discard the local draft.");
    return false;
  }

  async function resumeSoundPublication() {
    const current = soundScope(), owner = state.viewer?.id;
    if (!owner) return;
    await soundDraftWrites.get(owner); soundAssert(current);
    const value = await window.LobbyStudio?.drafts.loadStrict(`sound-publication:${owner}`); soundAssert(current);
    const saved = value?.soundPublication;
    if (!saved || saved.ownerId !== owner || saved.handoff.ownerId !== owner) throw new Error("No sound publication draft is saved for this account.");
    if (state.gallery.items.length) throw new Error("Keep or discard the current selection before resuming this sound draft.");
    // An existing publication is replayed before current editor/version gates.
    // Its lost PATCH reply may already have advanced the binding or edit deadline.
    if (saved.editingDrop && !saved.handoff.publication) {
      const descriptor = await api(`/api/drops/${encodeURIComponent(saved.editingDrop.id)}/sound-edit`); soundAssert(current);
      if (descriptor.sound_binding_version !== saved.editingDrop.sound_binding_version) throw new Error("The post changed. Reopen its current edit before using this saved draft.");
    }
    openComposer(saved.editingDrop || null, saved.fields.journey || null, { reconcileOnly: Boolean(saved.handoff.publication) });
    if (state.viewer?.id !== owner || !el.composer.open) return;
    clearGalleryPreviews(); state.gallery.items = [];
    const item = { ...saved.item, key: `file:${crypto.randomUUID()}`, file: saved.item.id ? null : saved.handoff.baseFile,
      studioFile: saved.handoff.baseFile, studioProject: value.project, soundHandoff: { ...saved.handoff, previewed: null },
      mediaType: saved.handoff.baseFile.type, name: saved.handoff.baseFile.name, existing: false,
      previewUrl: URL.createObjectURL(saved.handoff.baseFile) };
    state.gallery.items.push(item); state.gallery.previewUrls.add(item.previewUrl); state.gallery.dirty = true;
    state.editingDrop = saved.editingDrop;
    restoreSoundFields(saved.fields);
    renderDropMediaControl(); updateComposerCopy();
  }

  function soundAudioPreparationState(value) {
    if (!value || !["missing", "queued", "processing", "ready", "failed", "retired"].includes(value.state) ||
        !Number.isSafeInteger(value.generation) || value.generation < 0 || value.generation > 2147483647 ||
        !(value.error_code === null || typeof value.error_code === "string" && /^[a-z0-9_]{1,64}$/.test(value.error_code)))
      throw new Error("Audio preparation status is unavailable. Refresh the sound.");
    return { state: value.state, generation: value.generation, error_code: value.error_code };
  }
  function soundAudioPreparationKey(owner, sound) { return "louderr-audio-preparation-v1:" + owner + ":" + sound; }
  function readSoundAudioPreparation(owner, sound) {
    const raw = sessionStorage.getItem(soundAudioPreparationKey(owner, sound));
    if (raw === null) return null;
    let value; try { value = JSON.parse(raw); } catch (_) { throw new Error("This tab's audio preparation request is unreadable and has been kept."); }
    if (value?.version !== 1 || value.owner !== owner || value.sound !== sound ||
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value.payload?.action_token || "") ||
        !Number.isSafeInteger(value.payload?.expected_generation) || value.payload.expected_generation < 0 ||
        value.payload.expected_generation > 2147483647 || Object.keys(value.payload).sort().join(",") !== "action_token,expected_generation")
      throw new Error("This tab's audio preparation request is unreadable and has been kept.");
    return value;
  }
  function createSoundAudioPreparation(initial, hooks) {
    const owner = state.viewer?.id, id = initial.id;
    let value = initial, operation = 0, working = false, disposed = false, timer = null, wake = null;
    const live = () => !disposed && hooks.current() && state.viewer?.id === owner;
    const notify = () => { if (live()) hooks.changed(value, working); };
    const pending = () => readSoundAudioPreparation(owner, id);
    function forget(request) {
      const stored = pending();
      if (stored?.payload.action_token === request.payload.action_token &&
          stored.payload.expected_generation === request.payload.expected_generation)
        sessionStorage.removeItem(soundAudioPreparationKey(owner, id));
    }
    function stop() {
      operation++; working = false;
      clearTimeout(timer); timer = null; const done = wake; wake = null; done?.(false); notify();
    }
    function dispose() { disposed = true; stop(); }
    function wait(current) {
      return new Promise(resolve => {
        if (!current()) { resolve(false); return; }
        wake = resolve;
        timer = setTimeout(() => { timer = null; wake = null; resolve(current()); }, 3000);
      });
    }
    function accept(next) {
      if (!next || next.id !== id) throw new Error("The sound changed while its audio was checked.");
      const nextState = soundAudioPreparationState(next.audio_preparation);
      const previousState = soundAudioPreparationState(value.audio_preparation);
      if (nextState.generation < previousState.generation) throw new Error("An older audio status arrived. Check the sound again.");
      value = next; notify(); return nextState;
    }
    async function run(prepare) {
      if (!live() || working) return;
      const attempt = ++operation, current = () => live() && operation === attempt;
      working = true; notify();
      try {
        let request = prepare ? pending() : null, observed = null;
        if (!request) {
          const data = await api("/api/sounds/" + encodeURIComponent(id));
          if (!current()) return;
          observed = accept(data.sound);
          if (prepare && (!["queued", "processing", "ready"].includes(observed.state)
              || observed.state === "ready" && !value.can_use)) {
            if (value.creator?.id !== owner || value.state !== "offered" || !value.allowed_actions?.includes("prepare_audio"))
              throw new Error("New audio preparation is unavailable for this sound.");
            request = { version: 1, owner, sound: id,
              payload: { action_token: crypto.randomUUID(), expected_generation: observed.generation } };
            // Store before submission. A lost reply must reuse this exact request, even after reload.
            sessionStorage.setItem(soundAudioPreparationKey(owner, id), JSON.stringify(request));
          }
        }
        if (request) {
          let reply;
          try {
            reply = await api("/api/sounds/" + encodeURIComponent(id) + "/audio-prepare",
              { method: "POST", csrf: true, json: request.payload });
          } catch (error) {
            if (!current()) return;
            // Only a definitive rejection permits a new intent on a later click.
            // A transport failure, pause or server failure keeps the exact unknown request.
            if ([409, 422].includes(error.status)) {
              try { forget(request); }
              catch (_) { hooks.warning("The request was rejected, but this tab could not clear its retry record."); }
            }
            throw error;
          }
          if (!current()) return;
          observed = soundAudioPreparationState(reply.audio_preparation);
          if (typeof reply.replayed !== "boolean" || observed.generation < request.payload.expected_generation)
            throw new Error("The audio preparation reply is incomplete. Retry the same request.");
          // A confirmed server response is retained even if tab storage cannot be cleared.
          value = { ...value, can_use: false, audio_preparation: observed }; notify();
          try {
            forget(request);
          } catch (_) { if (current()) hooks.warning("Audio preparation was accepted, but this tab could not clear its retry record."); }
          observed = null; // The POST has status only; obtain current descriptors before enabling Use.
        }
        const deadline = Date.now() + 60000;
        for (let poll = 0; current() && poll < 21; poll++) {
          if (!observed) {
            const data = await api("/api/sounds/" + encodeURIComponent(id));
            if (!current()) return;
            observed = accept(data.sound);
          }
          if (!["queued", "processing"].includes(observed.state) || Date.now() >= deadline || poll === 20) return;
          if (!await wait(current)) return;
          observed = null;
        }
      } finally { if (operation === attempt) { working = false; notify(); } }
    }
    return { prepare: () => run(true), check: () => run(false), stop, dispose, pending,
      get value() { return value; }, get working() { return working; } };
  }

  async function chooseGallerySound(item) {
    if (!state.capabilities.studio_response_audio || state.composerDraft.busy || mediaStudioOpening) return;
    if (!guardSoundSelectionEdit()) return;
    const current = soundScope(item), dialog = create("dialog", "small-dialog"), content = create("div");
    const title = create("h2", "", "Choose an original sound"), query = create("input"), results = create("div");
    query.type = "search"; query.maxLength = 100; query.setAttribute("aria-label", "Search original sounds");
    const search = create("button", "secondary-button", "Search"), close = create("button", "secondary-button", "Close");
    search.type = close.type = "button";
    const status = create("p", "metric-note"); status.setAttribute("role", "status");
    dialog.setAttribute("aria-label", "Choose an original sound");
    content.append(title, query, search, status, results, close); dialog.append(content); document.body.append(dialog);
    let sequence = 0, busy = false, activePreparation = null;
    const preparations = [];
    const live = () => current() && dialog.open;
    const stopPreparations = () => { preparations.splice(0).forEach(value => value.dispose()); activePreparation = null; };
    const finish = () => { sequence++; stopPreparations(); for (const media of results.querySelectorAll("audio")) { media.pause(); media.removeAttribute("src"); media.load(); } dialog.remove(); };
    dialog.addEventListener("close", finish, { once: true }); close.addEventListener("click", () => dialog.close());
    const load = async () => {
      const request = ++sequence; stopPreparations();
      try {
        const data = await api("/api/sounds?q=" + encodeURIComponent(query.value) + "&limit=30");
        if (!live() || sequence !== request) return;
        for (const media of results.querySelectorAll("audio")) { media.pause(); media.removeAttribute("src"); media.load(); }
        clear(results); status.textContent = data.items.length ? "Preview a sound, then choose it." : "No original sounds found.";
        for (let sound of data.items) {
          const row = create("section"), name = create("p", "", sound.title + " · " + (sound.creator?.display_name || "Creator"));
          const rowCurrent = () => live() && sequence === request && row.isConnected;
          const preview = create("audio"); preview.controls = true; preview.preload = "none"; preview.src = sound.preview_url; preview.style.maxWidth = "100%";
          const use = create("button", "secondary-button", "Use this sound"); use.type = "button"; use.disabled = !sound.can_use;
          const preparationStatus = create("p", "metric-note"); preparationStatus.setAttribute("role", "status");
          const preparationWarning = create("p", "metric-note"); preparationWarning.hidden = true;
          const prepareAudio = create("button", "secondary-button", "Prepare audio"); prepareAudio.type = "button";
          const checkAudio = create("button", "secondary-button", "Check audio preparation"); checkAudio.type = "button";
          let preparePayload = { expected_version: sound.version, action_token: crypto.randomUUID() };
          const warning = message => { if (rowCurrent()) { preparationWarning.textContent = message; preparationWarning.hidden = !message; } };
          let preparation;
          function paintPreparation(next, working) {
            if (next.version !== sound.version) preparePayload = { expected_version: next.version, action_token: crypto.randomUUID() };
            sound = next; use.disabled = busy || working || !sound.can_use;
            name.textContent = sound.title + " · " + (sound.creator?.display_name || "Creator");
            if (preview.getAttribute("src") !== sound.preview_url) {
              preview.pause();
              if (sound.preview_url) preview.src = sound.preview_url; else preview.removeAttribute("src");
              preview.load();
            }
            checkAudio.disabled = busy || working;
            const owned = sound.creator?.id === state.viewer?.id && sound.state === "offered";
            let observed, retry = false;
            try { observed = soundAudioPreparationState(sound.audio_preparation); retry = owned && Boolean(preparation.pending()); }
            catch (error) { preparationStatus.textContent = error.message; prepareAudio.hidden = true; return; }
            const labels = { missing: "Audio has not been prepared yet.", queued: "Audio preparation is queued.",
              processing: "Preparing audio…", ready: sound.can_use ? "Audio is ready to use." : "Audio is prepared but cannot currently be used.",
              failed: "Audio preparation failed" + (observed.error_code ? " (" + observed.error_code + ")." : "."),
              retired: "This audio preparation is no longer available." };
            preparationStatus.textContent = labels[observed.state];
            const staleReady = observed.state === "ready" && !sound.can_use && sound.allowed_actions?.includes("prepare_audio");
            prepareAudio.hidden = !owned || !retry && !["missing", "failed"].includes(observed.state) && !staleReady;
            prepareAudio.disabled = busy || working || !retry && !sound.allowed_actions?.includes("prepare_audio");
            prepareAudio.textContent = retry ? "Retry preparation request" : observed.state === "failed" ? "Retry audio preparation" : "Prepare audio";
            checkAudio.textContent = working ? "Checking audio preparation…" : "Check audio preparation";
          }
          preparation = createSoundAudioPreparation(sound, { current: rowCurrent, changed: paintPreparation, warning });
          preparations.push(preparation);
          function startPreparation(prepare) {
            if (!rowCurrent() || busy || preparation.working) return;
            activePreparation?.stop(); activePreparation = preparation; warning("");
            void (prepare ? preparation.prepare() : preparation.check()).catch(error => { if (rowCurrent()) warning(error.message); });
          }
          prepareAudio.addEventListener("click", () => startPreparation(true));
          checkAudio.addEventListener("click", () => startPreparation(false));
          use.addEventListener("click", async () => {
            if (busy || !rowCurrent() || !sound.can_use) return;
            activePreparation?.stop(); busy = true; use.disabled = true;
            try {
              const grant = await api("/api/sounds/" + sound.id + "/prepare", { method: "POST", csrf: true, json: preparePayload });
              soundAssert(rowCurrent);
              if (!grant.can_use || !grant.source) throw new Error("This sound is no longer available for a new edit.");
              const soundAudio = window.LobbyStudio.copySoundAudio({ interval: grant.source.audio_interval, preview: grant.source.audio_preview });
              if (soundAudio.interval.source_identity_sha256 !== grant.source_identity_sha256)
                throw new Error("The original sound changed while its audio was prepared.");
              const file = await soundFile(soundAudio.preview.url, sound.title + ".wav", rowCurrent, soundAudio.preview);
              const checked = await api("/api/sounds/" + sound.id + "/prepare", { method: "POST", csrf: true, json: preparePayload });
              soundAssert(rowCurrent);
              if (!checked.can_use || checked.source_identity_sha256 !== grant.source_identity_sha256 ||
                  window.LobbyStudio.soundJson(window.LobbyStudio.copySoundAudio({ interval: checked.source?.audio_interval, preview: checked.source?.audio_preview })) !== window.LobbyStudio.soundJson(soundAudio))
                throw new Error("The original sound changed while it opened. Choose it again.");
              const selection = { start_ms: soundAudio.interval.start_ms, end_ms: soundAudio.interval.end_ms, loop: true };
              const selected = { soundtrack: file, soundtrackOrigin: "platform", outputMode: "clean-base", soundSelection: selection, soundAudio, requireSoundAudio: true,
                credit: { dropId: sound.source?.drop_id, handle: sound.creator?.handle,
                  soundSource: { sound_id: sound.id, source_identity_sha256: grant.source_identity_sha256 } } };
              dialog.close(); await openGalleryStudio(item, selected);
            } catch (error) { if (rowCurrent()) status.textContent = error.message; }
            finally { busy = false; if (rowCurrent()) paintPreparation(sound, preparation.working); }
          });
          row.append(name, preview, preparationStatus, preparationWarning, prepareAudio, checkAudio, use); results.append(row);
          paintPreparation(sound, false);
        }
      } catch (error) { if (live()) status.textContent = error.message; }
    };
    search.addEventListener("click", load); query.addEventListener("keydown", event => { if (event.key === "Enter") { event.preventDefault(); void load(); } });
    openDialog(dialog); query.focus(); await load();
  }

  async function coldSoundInput(item, current, replacingSound = false) {
    const drop = state.editingDrop;
    const descriptor = await api(`/api/drops/${encodeURIComponent(drop.id)}/sound-edit`); soundAssert(current);
    if (descriptor.version !== 1 || descriptor.drop_id !== drop.id || descriptor.author_id !== state.viewer.id) throw new Error("The post edit changed.");
    const clean = descriptor.clean_media.find(media => media.media_asset_id === item.id);
    if (!clean || !descriptor.allowed_operations.includes("edit_clean")) throw new Error("The clean source is unavailable for editing.");
    const file = await soundFile(clean.media_url, "clean-original", current, clean);
    state.editingDrop = { ...drop, sound_binding: descriptor.sound_binding, sound_binding_version: descriptor.sound_binding_version };
    if (replacingSound || state.gallery.soundDetach || !descriptor.sound_binding || descriptor.sound_binding.clean_asset_id !== item.id) return { clips: [{ file }] };
    if (!descriptor.allowed_operations.includes("open_saved_mix") || !descriptor.sound_mix || !descriptor.sound?.audio_preview?.url)
      throw new Error("This saved sound is unavailable. Keep the current post or explicitly remove its sound; your edit is kept.");
    const soundAudio = window.LobbyStudio.copySoundAudio({ interval: descriptor.sound.audio_interval, preview: descriptor.sound.audio_preview });
    if (soundAudio.interval.source_identity_sha256 !== descriptor.sound_mix.source.source_identity_sha256)
      throw new Error("The current sound audio no longer matches this saved edit.");
    const soundtrack = await soundFile(soundAudio.preview.url, "original-sound.wav", current, soundAudio.preview);
    return { clips: [{ file }], soundtrack, soundtrackOrigin: "platform", outputMode: "clean-base",
      soundMix: descriptor.sound_mix, soundSelection: descriptor.sound_mix.recipe, soundPreviewRequired: false, soundAudio, requireSoundAudio: true,
      credit: { dropId: descriptor.sound.source?.drop_id, handle: descriptor.sound.creator?.handle } };
  }

  async function prepareServerSound(item) {
    const scope = soundScope(item), handoff = item.soundHandoff;
    if (!handoff || item.soundBusy) return;
    const operation = {}; item.soundOperation = operation;
    const current = () => scope() && item.soundOperation === operation;
    item.soundBusy = true;
    try {
      if (!handoff.render) await prepareGallerySelection(); soundAssert(current);
      if (state.gallery.items.length !== 1 || !item.id) throw new Error("An original sound requires one prepared video.");
      if (!handoff.prepare) {
        const data = await api(`/api/sounds/${encodeURIComponent(handoff.source.sound_id)}`); soundAssert(current);
        if (!data.sound.can_use) throw new Error("This original sound cannot be used in a new mix.");
        handoff.prepare = { expected_version: data.sound.version, action_token: crypto.randomUUID() };
        await keepSoundDraft(item, current);
      }
      if (!handoff.render) {
        const grant = await api(`/api/sounds/${encodeURIComponent(handoff.source.sound_id)}/prepare`,
          { method: "POST", csrf: true, json: handoff.prepare }); soundAssert(current);
        if (!grant.can_use || grant.source_identity_sha256 !== handoff.source.source_identity_sha256) throw new Error("The original sound changed. Choose its current version in Studio.");
        handoff.render = { grant_id: grant.grant_id, clean_asset_id: item.id, recipe: handoff.recipe, action_token: crypto.randomUUID() };
        await keepSoundDraft(item, current);
      }
      if (handoff.render.clean_asset_id !== item.id) throw new Error("The clean upload changed. Reopen Studio before mixing.");
      let data;
      try { data = await api(`/api/sound-renders/by-token/${handoff.render.action_token}`); }
      catch (error) { soundAssert(current); if (error.status !== 404) throw error;
        data = await api("/api/sound-renders", { method: "POST", csrf: true, json: handoff.render }); }
      soundAssert(current);
      if (handoff.action) {
        data = await api(`/api/sound-renders/${handoff.action.jobId}/${handoff.action.kind}`,
          { method: "POST", csrf: true, json: handoff.action.payload }); soundAssert(current);
        handoff.action = null;
      }
      const deadline = Date.now() + 60000;
      while (true) {
        const job = data.job;
        if (job.clean_asset_id !== item.id || job.recipe_sha256 !== handoff.recipeSha256) throw new Error("The server mix no longer matches this edit.");
        const paint = !handoff.job || handoff.job.state !== job.state;
        if (handoff.job?.version !== job.version) handoff.previewed = null;
        handoff.job = job; await keepSoundDraft(item, current);
        if (paint && job.state !== "ready") renderDropMediaControl();
        if (!["queued", "processing", "retry_wait"].includes(job.state) || Date.now() >= deadline) break;
        await new Promise(resolve => setTimeout(resolve, 1500)); soundAssert(current);
        data = await api(`/api/sound-renders/${job.id}`); soundAssert(current);
      }
      if (handoff.job.state !== "ready") throw new Error(`Server mix: ${handoff.job.state}. Your draft is kept; check it again later.`);
    } finally { if (item.soundOperation === operation) { item.soundBusy = false; if (current()) renderDropMediaControl(); } }
  }

  async function changeServerSound(item, kind) {
    const current = soundScope(item), handoff = item.soundHandoff;
    if (!handoff?.job || handoff.publication || state.composerDraft.busy) return;
    if (handoff.action && handoff.action.kind !== kind) throw new Error("Check the earlier processing action before choosing another.");
    // Fence a status wait immediately; its late reply cannot overwrite this action.
    item.soundOperation = {}; item.soundBusy = false;
    handoff.action ||= { jobId: handoff.job.id, kind, payload: {
      expected_generation: handoff.job.generation, action_token: crypto.randomUUID() } };
    handoff.previewed = null;
    await keepSoundDraft(item, current);
    await prepareServerSound(item);
  }

  function renderSoundReceiptControls(container, attempt, current, resume) {
    if (!attempt) return;
    const pending = attempt.response?.status === "pending_review";
    const approved = pending && attempt.method === "POST" && attempt.response.publication?.review_status === "approved";
    container.append(create("p", "metric-note", pending
      ? `Moderation: ${attempt.response.publication?.review_status || "pending"}. Approval does not publish the post.`
      : "This submission is kept with its original request until its result is confirmed."));
    const check = create("button", "secondary-button", approved ? "Publish reviewed post" : "Restore submitted draft and check"); check.type = "button";
    check.addEventListener("click", () => {
      if (!current() || state.composerDraft.busy) return;
      restoreSoundFields(attempt.fields);
      for (const item of state.gallery.items) {
        const metadata = attempt.metadata.find(entry => entry.id === item.id);
        if (metadata) Object.assign(item, metadata);
      }
      resume(approved); renderDropMediaControl(); updateComposerCopy(); el.dropForm.requestSubmit();
    });
    container.append(check);
  }

  function soundAttemptMetadata() {
    const textDraft = value => value ? Object.fromEntries([
      "captions", "transcript", "originalCaptions", "originalTranscript", "captionsPresent", "transcriptPresent",
      "captionsRemoved", "transcriptRemoved", "durationMs", "accessibilityOpen",
    ].map(key => [key, value[key]])) : null;
    return state.gallery.items.map(({ id, alt, captions, transcript, originalAlt, originalCaptions, originalTranscript,
      captionsPresent, transcriptPresent, captionsRemoved, transcriptRemoved, remixText, previousRemixText }) => ({ id, alt, captions, transcript,
      originalAlt, originalCaptions, originalTranscript, captionsPresent, transcriptPresent, captionsRemoved, transcriptRemoved,
      ...(remixText ? { remixText: textDraft(remixText), previousRemixText: textDraft(previousRemixText) } : {}) }));
  }

  function soundDetachKey(owner, dropId) { return `louderr.sound-detach:${owner}:${dropId}`; }
  function keepSoundDetach(detach) {
    // No files are stored here. This preserves the exact small PATCH receipt across
    // a reload of the same tab; it does not change ordinary composer draft storage.
    sessionStorage.setItem(soundDetachKey(detach.ownerId, detach.dropId), JSON.stringify(detach));
  }

  async function detachGallerySound(item) {
    if (!guardSoundSelectionEdit() || state.composerDraft.busy) return;
    const current = soundScope(item), drop = state.editingDrop;
    const descriptor = await api(`/api/drops/${drop.id}/sound-edit`); soundAssert(current);
    if (descriptor.author_id !== state.viewer.id || descriptor.drop_id !== drop.id
      || !descriptor.allowed_operations.includes("detach_sound")) throw new Error("This post has no current sound to remove.");
    state.gallery.soundDetach = { ownerId: state.viewer.id, dropId: drop.id,
      expectedVersion: descriptor.sound_binding_version, publication: null };
    state.editingDrop = { ...drop, sound_binding_version: descriptor.sound_binding_version };
    // A pending clean export is still clean. Removing the managed selection must
    // not mix the platform track locally or restore a historical recipe on reopen.
    item.soundOperation = {}; item.soundHandoff = null;
    if (item.studioProject) item.studioProject = { ...item.studioProject, soundtrack: null, soundtrackOrigin: "local",
      credit: null, outputMode: "mixed", soundSelection: null, soundMix: null, soundPreviewRequired: false };
    keepSoundDetach(state.gallery.soundDetach); renderDropMediaControl();
  }

  function renderGallerySoundControls(item, container) {
    if (!item.mediaType?.startsWith("video/")) return;
    const add = create("button", "secondary-button", "Choose original sound"); add.type = "button";
    add.hidden = !state.capabilities.studio_response_audio;
    add.disabled = state.composerDraft.busy || Boolean(item.soundHandoff?.publication)
      || Boolean(item.upload && !["selected", "succeeded"].includes(item.upload.state));
    add.addEventListener("click", () => { void chooseGallerySound(item).catch(error => showError(el.formError, error.message)); });
    container.append(add);
    if (state.editingDrop && item.id) {
      const remove = create("button", "text-button", state.gallery.soundDetach ? "Original sound will be removed when you save" : "Remove original sound"); remove.type = "button";
      remove.disabled = Boolean(state.gallery.soundDetach) || state.composerDraft.busy;
      const context = galleryUploadContext();
      remove.addEventListener("click", () => { void detachGallerySound(item).catch(error => { if (uploadContextCurrent(context)) showError(el.formError, error.message); }); });
      container.append(remove);
    }
    renderServerSound(item, container);
    if (state.gallery.soundDetach) renderSoundReceiptControls(container, state.gallery.soundDetach.publication, soundScope(item), () => {});
  }

  function renderServerSound(item, container) {
    const handoff = item.soundHandoff;
    if (!handoff) return;
    const current = soundScope(item), status = create("p", "metric-note", handoff.job ? `Server mix: ${handoff.job.state}` : "Prepare and play the server mix before publishing.");
    status.setAttribute("role", "status");
    const prepare = create("button", "secondary-button", item.soundBusy ? "Preparing server mix…" : "Prepare / check server mix"); prepare.type = "button"; prepare.disabled = Boolean(item.soundBusy);
    prepare.disabled ||= Boolean(handoff.publication);
    prepare.addEventListener("click", () => { void prepareServerSound(item).catch(error => { if (current()) showError(el.formError, error.message); }); });
    container.append(status, prepare);
    for (const [kind, visible] of [["retry", handoff.job?.state === "failed" || handoff.action?.kind === "retry"],
      ["cancel", handoff.job && !["cancelled", "expired", "revoked", "rejected"].includes(handoff.job.state)]]) {
      if (!visible || handoff.publication) continue;
      const action = create("button", "secondary-button", `${kind === "retry" ? "Retry" : "Cancel"} server mix`); action.type = "button";
      action.addEventListener("click", () => { void changeServerSound(item, kind).catch(error => { if (current()) showError(el.formError, error.message); }); });
      container.append(action);
    }
    if (handoff.job?.state === "ready") {
      const job = handoff.job, player = create("video"); player.controls = true; player.playsInline = true; player.preload = "metadata";
      player.src = `/api/sound-renders/${encodeURIComponent(job.id)}/media`; player.style.maxWidth = "100%";
      player.dataset.serverSoundPreview = "true";
      player.setAttribute("aria-label", "Server mixed preview");
      let previous = null, progressed = 0;
      player.addEventListener("timeupdate", () => {
        if (!current() || !player.isConnected || handoff.job !== job || player.paused || player.seeking || player.muted || !player.volume) { previous = null; return; }
        const now = player.currentTime;
        if (previous !== null && now > previous && now - previous < 0.75) progressed += now - previous;
        previous = now;
        if (progressed >= 0.1) { handoff.previewed = { renderId: job.id, version: job.version }; status.textContent = "Server mix played. Ready to publish."; }
      });
      player.addEventListener("seeking", () => { previous = null; });
      player.addEventListener("error", () => { if (current() && player.isConnected && handoff.job === job) {
        handoff.previewed = null; status.textContent = "This mix is unavailable. Check the sound before publishing.";
      } });
      container.append(player);
    }
    renderSoundReceiptControls(container, handoff.publication, current, approved => { handoff.resubmitRequested = approved; });
  }

  async function submitSoundPublication(path, method, payload) {
    if (state.gallery.items.some(item => item.videoHandoff)) return submitVideoPublication(path, method, payload);
    if (galleryVideoTextUpdate() || state.gallery.videoTextAttempt) return submitExistingVideoText(path, method, payload);
    const item = state.gallery.items.find(entry => entry.soundHandoff);
    if (!item && !state.gallery.soundDetach) return api(path, { method, csrf: true, json: payload });
    if (!item) {
      const current = soundScope(), detach = state.gallery.soundDetach;
      if (method !== "PATCH" || detach.ownerId !== state.viewer.id || path !== `/api/drops/${detach.dropId}`)
        throw new Error("Reopen this post before removing its sound.");
      let attempt = detach.publication;
      if (!attempt) {
        attempt = { path, method, original: structuredClone(payload), fields: composerFields(), metadata: soundAttemptMetadata(),
          payload: { ...payload, sound_binding: null, expected_sound_binding_version: detach.expectedVersion,
            publication_token: crypto.randomUUID() }, response: null };
        detach.publication = attempt; keepSoundDetach(detach);
      } else if (soundJson(attempt.original) !== soundJson(payload)) throw new Error("Restore the submitted draft to check its previous result before editing it again.");
      const result = await api(path, { method, csrf: true, json: attempt.payload }); soundAssert(current);
      attempt.response = result;
      try { keepSoundDetach(detach); } catch (_) { toast("The server replied, but its receipt could not be updated on this device. The original retry token is kept.", true); }
      return result;
    }
    const current = soundScope(item), handoff = item.soundHandoff;
    if (handoff.ownerId !== state.viewer.id) throw new Error("This sound draft belongs to another account.");
    let attempt = handoff.publication;
    if (!attempt) {
      const preview = handoff.previewed, job = handoff.job;
      if (!job || job.state !== "ready" || preview?.renderId !== job.id || preview.version !== job.version) throw new Error("Prepare and play the server mix before publishing.");
      const latest = (await api(`/api/sound-renders/${job.id}`)).job; soundAssert(current);
      if (latest.state !== "ready" || latest.version !== job.version || latest.clean_asset_id !== item.id || latest.recipe_sha256 !== handoff.recipeSha256) {
        handoff.previewed = null; throw new Error("The server mix changed. Check and play it again.");
      }
      attempt = { path, method, original: structuredClone(payload), fields: composerFields(), metadata: soundAttemptMetadata(), payload: { ...payload,
        sound_binding: { render_id: job.id, render_version: job.version }, publication_token: crypto.randomUUID(),
        expected_sound_binding_version: method === "PATCH" ? state.editingDrop.sound_binding_version : 0 }, response: null };
      if (method === "PATCH" && !Number.isSafeInteger(attempt.payload.expected_sound_binding_version)) throw new Error("Reload this post's sound edit before publishing changes.");
      handoff.publication = attempt; await keepSoundDraft(item, current);
    } else if (attempt.path !== path || attempt.method !== method || soundJson(attempt.original) !== soundJson(payload)) {
      throw new Error("The earlier sound publication must be reconciled with its unchanged draft before submitting another version.");
    }
    if (handoff.resubmitRequested) {
      if (attempt.method !== "POST" || attempt.response?.publication?.review_status !== "approved") throw new Error("This request has no approved create-publication resubmission.");
      attempt = { ...attempt, payload: { ...attempt.payload, publication_token: crypto.randomUUID(),
        resubmission_of: attempt.payload.publication_token }, response: null };
      handoff.publication = attempt; handoff.resubmitRequested = false; await keepSoundDraft(item, current);
    }
    const response = await api(attempt.path, { method: attempt.method, csrf: true, json: attempt.payload });
    soundAssert(current); attempt.response = response;
    try { await keepSoundDraft(item, current); }
    catch (error) { if (current()) toast(`The server replied, but its receipt could not be updated on this device: ${error.message}`, true); }
    return response;
  }

  const videoDraftWrites = new Map();
  function videoScope(item = null) {
    const context = galleryUploadContext(), handoff = item?.videoHandoff;
    return () => uploadContextCurrent(context) && el.composer.open
      && (!item || state.gallery.items.includes(item) && item.videoHandoff === handoff);
  }
  function queueVideoDraft(owner, action) {
    const next = (videoDraftWrites.get(owner) || Promise.resolve()).catch(() => {}).then(action);
    videoDraftWrites.set(owner, next);
    return next.finally(() => { if (videoDraftWrites.get(owner) === next) videoDraftWrites.delete(owner); });
  }
  async function keepVideoDraft(item, current) {
    soundAssert(current);
    const handoff = item.videoHandoff;
    if (!handoff || handoff.ownerId !== state.viewer?.id) throw new Error("This video draft belongs to another account.");
    const value = structuredClone({ version: 1, revision: crypto.randomUUID(), project: handoff.project, savedAt: Date.now(),
      videoPublication: { ownerId: handoff.ownerId, handoff, fields: composerFields(), editingDrop: state.editingDrop,
        item: soundAttemptMetadata().find(entry => entry.id === item.id) || {} } });
    await queueVideoDraft(handoff.ownerId, async () => {
      const key = `video-publication:${handoff.ownerId}`, previous = await window.LobbyStudio.drafts.loadStrict(key);
      const saved = previous?.videoPublication?.handoff;
      if (previous && (!saved || saved.ownerId !== handoff.ownerId)) throw new Error("The saved video draft is unreadable and was preserved.");
      if (saved && saved.draftId !== handoff.draftId) throw new Error("Resume or discard the previous saved video draft before replacing its device copy.");
      if ((previous?.revision ?? null) !== (handoff.journalRevision ?? null)) throw new Error("This video draft changed in another tab. Resume its current copy before saving.");
      value.videoPublication.handoff.journalRevision = value.revision;
      await window.LobbyStudio.drafts.compareAndSave(key, previous?.revision ?? null, value);
      handoff.journalRevision = value.revision;
    });
    soundAssert(current);
  }
  function clearVideoDraft(owner, draftId, revision) {
    if (!owner || !draftId) return;
    return queueVideoDraft(owner, async () => {
      const key = `video-publication:${owner}`, saved = await window.LobbyStudio.drafts.loadStrict(key);
      if (saved?.videoPublication?.handoff?.draftId !== draftId) return;
      if ((saved.revision ?? null) !== (revision ?? null)) throw new Error("A newer device video draft was preserved. Resume it before discarding it.");
      await window.LobbyStudio.drafts.compareAndClear(key, revision ?? null);
    });
  }
  function saveCurrentVideoDraft(allowClosed = false) {
    const item = state.gallery.items.find(value => value.videoHandoff);
    if (!item || !el.composer.open && allowClosed !== true) return;
    const context = galleryUploadContext(), handoff = item.videoHandoff;
    const current = () => uploadContextCurrent(context) && state.gallery.items.includes(item) && item.videoHandoff === handoff;
    void keepVideoDraft(item, current).catch(error => { if (current()) showError(el.formError, `Video draft could not be saved on this device: ${error.message}`); });
  }
  function videoController(item) {
    if (!item.videoHandoff) throw new Error("Choose a video to remix first.");
    if (item.videoController?.draft === item.videoHandoff) return item.videoController;
    const current = videoScope(item);
    item.videoController = globalThis.LouderrVideoRemixClient.create(item.videoHandoff, {
      current, owner: () => state.viewer?.id, api, save: () => keepVideoDraft(item, current),
      changed: () => { if (current() && !state.composerDraft.busy) renderDropMediaControl(); }, warning: message => toast(message, true),
    });
    return item.videoController;
  }
  function guardVideoSelectionEdit() {
    if (!state.gallery.items.some(item => item.videoHandoff)) return true;
    showError(el.formError, "Remove the video remix selection before changing its source clips. Check any submitted post first.");
    return false;
  }
  async function chooseGalleryVideo(item) {
    if (!state.capabilities.studio_video_remix || state.composerDraft.busy || mediaStudioOpening) return;
    if (!guardSoundSelectionEdit()) return;
    if (state.gallery.items.length !== 1 || !item.mediaType?.startsWith("video/")) throw new Error("Choose one video, or combine your own clips in Studio first.");
    if (item.soundHandoff || state.gallery.soundDetach || state.editingDrop?.sound_binding)
      throw new Error("Remove the original sound selection before choosing a video remix.");
    if (!item.studioProject) await openGalleryStudio(item);
    if (!item.studioProject || !state.gallery.items.includes(item) || !el.composer.open) return;
    if (item.studioProject.soundtrackOrigin === "platform" || item.studioProject.outputMode === "clean-base")
      throw new Error("Use your own audio and clips in Studio before choosing a video remix.");
    const before = videoScope(item), selectedFile = item.file;
    await prepareGallerySelection(); soundAssert(before);
    const base = await api(`/api/uploads/${encodeURIComponent(item.id)}`); soundAssert(before);
    if (base.id !== item.id || base.state !== "succeeded" || base.ready !== true || !/^video\//.test(base.content_type || "")
        || !Number.isSafeInteger(base.duration_ms) || base.duration_ms < 1 || base.duration_ms > 120000)
      throw new Error("Your finished Studio video is not ready for remixing yet.");
    const file = selectedFile || await soundFile(`/api/media/${item.id}`, "my-video.mp4", before);
    soundAssert(before);
    await loadDeferred("video-remix");
    const selection = await globalThis.LouderrVideoRemixStudio.choose({ current: before, api, origin: location.origin,
      baseDurationMs: base.duration_ms, postId: state.gallery.videoSourcePostId,
      posts: [...state.drops, ...(state.profile?.drops || [])].filter(post => globalThis.LouderrVideoRemixControls.sourceCandidates(post).length),
      openDialog: dialog => openDialogWithHistory(dialog) });
    soundAssert(before); if (!selection) return;
    const handoff = await globalThis.LouderrVideoRemixClient.newDraft({ ownerId: state.viewer.id, source: selection.source,
      selection: selection.selection, cleanAssetId: item.id, baseFile: file, project: item.studioProject });
    soundAssert(before); item.videoHandoff = handoff; item.videoController = null;
    item.previousRemixText = item.remixText || null;
    item.remixText = { captions: "", transcript: "", originalCaptions: "", originalTranscript: "",
      durationMs: handoff.recipe.base_end_ms - handoff.recipe.base_start_ms
        + (handoff.recipe.mode === "excerpt" ? handoff.recipe.source_end_ms - handoff.recipe.source_start_ms : 0) };
    state.gallery.dirty = true;
    try { await keepVideoDraft(item, videoScope(item)); }
    finally { renderDropMediaControl(); updateComposerCopy(); }
  }
  async function checkGalleryVideo(item) {
    const controller = videoController(item), current = videoScope(item), operation = {};
    item.videoOperation = operation;
    const deadline = Date.now() + 60000;
    for (let count = 0; count < 40; count++) {
      const job = await controller.check(); soundAssert(current);
      if (item.videoOperation !== operation || !["queued", "processing"].includes(job.state) || Date.now() >= deadline) return;
      await new Promise(resolve => setTimeout(resolve, 1500));
      if (!current() || item.videoOperation !== operation) return;
    }
  }
  async function removeGalleryVideo(item) {
    const current = videoScope(item), handoff = item.videoHandoff;
    if (!handoff || state.composerDraft.busy || videoController(item).busy) return;
    if (handoff.publication || handoff.action || handoff.render && !["cancelled", "failed", "rejected", "revoked", "expired"].includes(handoff.job?.state))
      throw new Error("Cancel or reconcile the video remix before removing its selection.");
    await clearVideoDraft(handoff.ownerId, handoff.draftId, handoff.journalRevision); soundAssert(current);
    item.videoOperation = {}; item.videoController = null; item.videoHandoff = null;
    item.remixText = item.previousRemixText || null; delete item.previousRemixText;
    renderDropMediaControl(); updateComposerCopy();
  }
  function renderGalleryVideoControls(item, host) {
    if (!item.mediaType?.startsWith("video/")) return;
    if (!item.videoHandoff) {
      const button = create("button", "secondary-button", "Remix a video"); button.type = "button";
      button.hidden = !state.capabilities.studio_video_remix;
      button.disabled = state.composerDraft.busy || Boolean(item.soundHandoff) || state.gallery.items.length !== 1
        || Boolean(item.upload && !["selected", "succeeded"].includes(item.upload.state));
      const context = galleryUploadContext();
      button.addEventListener("click", () => { void chooseGalleryVideo(item).catch(error => {
        if (uploadContextCurrent(context) && state.gallery.items.includes(item)) showError(el.formError, error.message);
      }); });
      host.append(button); return;
    }
    const current = videoScope(item), controller = videoController(item);
    globalThis.LouderrVideoRemixStudio.renderResult({ host, draft: item.videoHandoff, controller, current,
      prepare: () => checkGalleryVideo(item), action: kind => { item.videoOperation = {}; return controller.action(kind); },
      remove: () => removeGalleryVideo(item), error: error => { if (current()) showError(el.formError, error.message); },
      receipt: renderSoundReceiptControls });
  }
  async function submitVideoPublication(path, method, payload) {
    const item = state.gallery.items.find(value => value.videoHandoff);
    if (!item) return submitSoundPublication(path, method, payload);
    if (state.gallery.items.length !== 1 || item.soundHandoff || state.gallery.soundDetach)
      throw new Error("Publish a video remix with one source clip and its saved video selection.");
    const text = galleryVideoTextUpdate();
    return videoController(item).publish(path, method, { ...payload, ...(text ? { video_text: text } : {}) }, { fields: composerFields(), metadata: soundAttemptMetadata(),
      expectedVersion: method === "POST" ? 0 : state.editingDrop?.video_binding_version });
  }
  function renderVideoDraftResume() {
    const anchor = $("#composer-resume-studio"); if (!anchor) return;
    let button = $("#composer-resume-video-publication");
    if (!button) {
      button = create("button", "secondary-button", "Resume saved video remix"); button.id = "composer-resume-video-publication"; button.type = "button";
      anchor.after(button);
      button.addEventListener("click", () => { void resumeVideoPublication().catch(error => showError(el.formError, error.message)); });
    }
    button.hidden = true;
    const owner = state.viewer?.id, context = galleryUploadContext(); if (!owner) return;
    void window.LobbyStudio?.drafts.loadStrict(`video-publication:${owner}`).then(value => {
      if (uploadContextCurrent(context)) button.hidden = !value?.videoPublication || Boolean(state.gallery.items.length);
    }).catch(error => { if (uploadContextCurrent(context)) showError(el.formError, `Saved video draft unavailable: ${error.message}`); });
  }
  async function resumeVideoPublication() {
    const current = videoScope(), owner = state.viewer?.id;
    if (!owner) return;
    // The draft store and the remix client both live outside the first load.
    await Promise.all([loadStudio(), loadDeferred("video-remix")]); soundAssert(current);
    if (state.gallery.items.length) throw new Error("Keep or discard the current media before resuming a saved video remix.");
    await videoDraftWrites.get(owner); soundAssert(current);
    const value = await window.LobbyStudio.drafts.loadStrict(`video-publication:${owner}`); soundAssert(current);
    const saved = value?.videoPublication;
    if (!saved || saved.ownerId !== owner) throw new Error("No video remix is saved for this account.");
    await globalThis.LouderrVideoRemixClient.validateDraft(saved.handoff, owner); soundAssert(current);
    if (saved.editingDrop && !saved.handoff.publication) {
      const post = await api(`/api/drops/${saved.editingDrop.id}`); soundAssert(current);
      if (post.author_id !== owner || post.video_binding_version !== saved.editingDrop.video_binding_version)
        throw new Error("The post changed. Open its current edit before using this saved remix.");
    }
    openComposer(saved.editingDrop || null, saved.fields.journey || null, { reconcileOnly: Boolean(saved.handoff.publication) });
    if (state.viewer?.id !== owner || !el.composer.open) return;
    clearGalleryPreviews();
    const handoff = { ...saved.handoff, previewed: null }, previewUrl = URL.createObjectURL(handoff.baseFile);
    const item = { ...saved.item, key: `asset:${handoff.cleanAssetId}`, id: handoff.cleanAssetId, file: null,
      studioFile: handoff.baseFile, studioProject: handoff.project, videoHandoff: handoff,
      mediaType: handoff.baseFile.type, name: handoff.baseFile.name || "My video", existing: false, previewUrl };
    state.gallery.items = [item]; state.gallery.previewUrls.add(previewUrl); state.gallery.dirty = true;
    state.editingDrop = saved.editingDrop; restoreSoundFields(saved.fields);
    renderDropMediaControl(); updateComposerCopy();
  }
  async function remixPostVideo(drop) {
    if (!state.capabilities.studio_video_remix || !state.capabilities.community_writes) return;
    if (state.gallery.items.some(item => item.videoHandoff || item.soundHandoff)) { toast("Resume or discard the current remix before starting another.", true); return; }
    openComposer(); if (!el.composer.open) return;
    state.gallery.videoSourcePostId = drop.id;
    if (state.gallery.items.length === 1 && state.gallery.items[0].mediaType?.startsWith("video/")) {
      try { await chooseGalleryVideo(state.gallery.items[0]); } catch (error) { showError(el.formError, error.message); }
    } else showError(el.formError, "Choose your own video, then select Remix a video to use this post.");
  }

  // Surfaces most visits never open do not belong in the first load. Each group names its files in
  // <meta name="louderr-deferred-script" data-group="..."> tags, in the order the script tags used, and
  // this fetches them once on demand. Injected scripts are async by default, so async = false is what
  // keeps their execution in that order while the browser still fetches them in parallel.
  const deferredGroups = new Map();
  const deferredScripts = new Map();

  function loadDeferredScript(source) {
    if (deferredScripts.has(source)) return deferredScripts.get(source);
    let resolve, reject;
    const request = new Promise((yes, no) => { resolve = yes; reject = no; });
    deferredScripts.set(source, request);
    let element, finished = false;
    const finish = (error) => {
      if (finished) return;
      finished = true;
      if (element) { element.onload = null; element.onerror = null; }
      if (error) {
        if (deferredScripts.get(source) === request) deferredScripts.delete(source);
        element?.remove();
        reject(new Error("Part of LOUDERR could not be loaded. Check your connection and try again."));
      } else resolve(source);
    };
    try {
      element = document.createElement("script");
      element.src = source;
      element.async = false;
      element.onload = () => finish(false);
      element.onerror = () => finish(true);
      document.head.append(element);
    } catch (_) { finish(true); }
    return request;
  }

  function loadDeferred(group) {
    const pending = deferredGroups.get(group);
    if (pending) return pending;
    const sources = $$(`meta[name="louderr-deferred-script"][data-group="${group}"]`).map((tag) => tag.content);
    if (!sources.length) return Promise.reject(new Error("That part of LOUDERR is not in this build."));
    // Keep successful and still-loading files when another file fails. Reexecuting
    // the entire group would duplicate module-level listeners and reset exports.
    const loading = Promise.all(sources.map(loadDeferredScript));
    let timer;
    const deadline = new Promise((_, reject) => {
      timer = setTimeout(() => {
        const error = new Error("Loading took too long. Reload the page and try again.");
        error.code = "DEFERRED_LOAD_TIMEOUT";
        reject(error);
      }, 20000);
    });
    // Only the failed group promise is forgotten; each file owns its own retry.
    // A deadline ends the wait, not script execution. Keep pending file ownership
    // so a later caller cannot inject a second copy with duplicate listeners.
    const settled = Promise.race([loading, deadline]).catch((error) => {
      deferredGroups.delete(group); throw error;
    }).finally(() => clearTimeout(timer));
    settled.catch(() => {});
    deferredGroups.set(group, settled);
    return settled;
  }

  function loadStudio() {
    if (window.LobbyStudio) return Promise.resolve(window.LobbyStudio);
    return loadDeferred("studio").then(() => {
      if (!window.LobbyStudio) throw new Error("Studio did not load. Reload the page and try again.");
      return window.LobbyStudio;
    });
  }

  function studio() {
    if (!window.LobbyStudio) throw new Error("Studio did not load. Reload the page and try again.");
    // Guests may edit locally; nothing reaches the server until Publish, which still needs an account.
    if (!mediaStudio) mediaStudio = window.LobbyStudio.createStudio($("#studio-dialog"), { owner: () => state.viewer?.id || null, allowGuest: true });
    return mediaStudio;
  }

  // A draft is looked up under the account first; a guest's draft is offered to whoever signs in on this device.
  function studioDraftKey() {
    return state.viewer?.id || "guest";
  }

  async function studioDraftLookup() {
    const store = window.LobbyStudio?.drafts;
    if (!store) return null;
    const owner = state.viewer?.id || null, key = owner || "guest";
    const own = await store.load(key);
    if ((state.viewer?.id || null) !== owner) return null;
    if (own) return { key, value: own };
    if (owner) {
      const guest = await store.load("guest");
      if ((state.viewer?.id || null) !== owner) return null;
      if (guest) return { key: "guest", value: guest };
    }
    return null;
  }

  function draftToStudioInput(value) {
    const project = value.project;
    return { clips: project.clips.map(clip => ({ file: clip.file, edit: clip.edit })), overlays: (project.overlays || []).map(layer => ({ ...layer })), format: project.format,
      soundtrack: project.soundtrack || null, musicVolume: project.musicVolume, soundtrackOrigin: project.soundtrackOrigin || "local",
      credit: project.credit || null, music: project.music || null, outputMode: project.outputMode || "mixed",
      soundSelection: project.soundSelection || null, soundMix: project.soundMix || null, soundAudio: project.soundAudio || null,
      requireSoundAudio: project.outputMode === "clean-base" && project.soundtrackOrigin === "platform",
      soundPreviewRequired: project.soundPreviewRequired ?? false, selected: value.selected, playhead: value.playhead };
  }

  async function openStudioFiles(files) {
    if (mediaStudioOpening || state.composerDraft.busy || !guardSoundSelectionEdit()) return;
    const context = galleryUploadContext();
    mediaStudioOpening = true;
    try {
      await loadStudio();
      if (!uploadContextCurrent(context) || !el.composer.open) return;
      const output = await studio().open({ clips: files.map(file => ({ file })) });
      if (!output || !uploadContextCurrent(context) || !el.composer.open) return;
      const result = await adoptStudioSound(output);
      if (!uploadContextCurrent(context) || !el.composer.open) return;
      await appendStudioResult(result);
    } catch (error) { if (uploadContextCurrent(context)) showError(el.formError, error.message); }
    finally { mediaStudioOpening = false; }
  }

  async function resumeStudioDraftIntoComposer() {
    if (mediaStudioOpening || state.composerDraft.busy || !guardSoundSelectionEdit()) return;
    const context = galleryUploadContext();
    mediaStudioOpening = true;
    try {
      await loadStudio();
      if (!uploadContextCurrent(context) || !el.composer.open) return;
      const found = await studioDraftLookup();
      if (!uploadContextCurrent(context) || !el.composer.open) return;
      if (!found) { syncComposerStudioDraft(); return; }
      const output = await studio().open(draftToStudioInput(found.value));
      if (!output || !uploadContextCurrent(context) || !el.composer.open) return;
      const result = await adoptStudioSound(output);
      if (!uploadContextCurrent(context) || !el.composer.open) return;
      await appendStudioResult(result);
      if (found.key === "guest" && uploadContextCurrent(context) && state.viewer?.id) await window.LobbyStudio?.drafts?.clear("guest");
    } catch (error) { if (uploadContextCurrent(context)) showError(el.formError, error.message); }
    finally { mediaStudioOpening = false; syncComposerStudioDraft(); }
  }

  function syncComposerStudioDraft() {
    const button = $("#composer-resume-studio");
    if (!button) return;
    button.hidden = true;
    const viewerId = state.viewer?.id || null;
    studioDraftLookup().then(found => {
      if ((state.viewer?.id || null) !== viewerId) return;
      const summary = found ? window.LobbyStudio?.draftSummary?.(found.value) : null;
      button.hidden = !summary || state.gallery.items.length > 0;
      if (summary) button.textContent = "Resume your Studio draft · " + summary.label;
    }).catch(() => { button.hidden = true; });
  }

  // Standalone Studio at /studio: edit without an account, save to the phone, or publish after signing in.
  async function openStudioStart() {
    if (!el.studioStart) return;
    const opening = { viewer: state.viewer?.id || null, social: state.messages };
    state.studioStartLoad = opening;
    const current = () => state.studioStartLoad === opening && el.studioStart.open
      && (state.viewer?.id || null) === opening.viewer && state.messages === opening.social;
    renderStudioStart();
    if (typeof openDialogWithHistory === "function") openDialogWithHistory(el.studioStart, "/studio");
    else openDialog(el.studioStart);
    if (typeof document !== "undefined") {
      if (!state.studioTitleBeforeOpen) state.studioTitleBeforeOpen = document.title;
      document.title = `Studio · ${state.productName}`;
    }
    // The sheet is up on the tap. The editor and the draft store it carries arrive behind it, and the
    // resume row fills in when they do, so nothing waits on a 180 KB fetch to become visible.
    try { await loadStudio(); }
    catch (error) { if (current()) showError($("#studio-start-error"), error.message); return; }
    if (current()) renderStudioStart();
  }

  function renderStudioStart() {
    if (!el.studioStart) return;
    const result = state.studioStandalone;
    const resultPanel = $("#studio-start-result");
    if (resultPanel) {
      resultPanel.hidden = !result;
      if (result) $("#studio-start-result-name").textContent = soundResultFile(result).name;
      $("#studio-start-signin-note").hidden = !result || Boolean(state.viewer?.email_verified);
      $("#studio-start-publish").textContent = state.viewer?.email_verified ? "Publish on LOUDERR" : "Sign in to publish";
    }
    hideError($("#studio-start-error"));
    const draftPanel = $("#studio-start-draft");
    if (!draftPanel) return;
    draftPanel.hidden = true;
    const viewerId = state.viewer?.id || null;
    studioDraftLookup().then(found => {
      if ((state.viewer?.id || null) !== viewerId || !el.studioStart.open) return;
      const summary = found ? window.LobbyStudio?.draftSummary?.(found.value) : null;
      draftPanel.hidden = !summary;
      if (summary) $("#studio-start-draft-summary").textContent = "· " + summary.label;
    }).catch(() => { draftPanel.hidden = true; });
  }

  async function openStandaloneStudio(input, draftKey = null) {
    if (mediaStudioOpening || state.composerDraft.busy) return;
    mediaStudioOpening = true;
    const viewerId = state.viewer?.id || null;
    try {
      hideError($("#studio-start-error"));
      await loadStudio();
      const output = await studio().open(input);
      if (!output || (state.viewer?.id || null) !== viewerId) return;
      const result = await adoptStudioSound(output);
      if ((state.viewer?.id || null) !== viewerId) return;
      if (draftKey === "guest" && viewerId) await window.LobbyStudio?.drafts?.clear("guest");
      if ((state.viewer?.id || null) !== viewerId) return;
      state.studioStandalone = { ...result, ownerId: viewerId };
      renderStudioStart();
      if (!el.studioStart.open) void openStudioStart();
    } catch (error) { if (el.studioStart?.open) showError($("#studio-start-error"), error.message); else toast(error.message, true); }
    finally { mediaStudioOpening = false; }
  }

  async function publishStandaloneStudio() {
    const result = state.studioStandalone;
    if (!result) return;
    if (!state.viewer?.email_verified) {
      // The file waits in this tab; the account dialog explains the intent.
      state.composerDraft.pending = { drop: null, journey: null, ownerId: state.viewer?.id || null };
      renderComposerIntent();
      el.studioStart.close();
      requireVerified({ announce: false });
      return;
    }
    if (result.ownerId && result.ownerId !== state.viewer.id) { toast("Reopen this Studio edit in its original account.", true); return; }
    try {
      soundResultFile(result);
      openComposer();
      if (!el.composer.open) return;
      await appendStudioResult(result);
      if (state.studioStandalone === result) state.studioStandalone = null;
      el.studioStart.close();
    } catch (error) { showError(el.formError, error.message); }
  }

  async function openGalleryStudio(item, selectedSound = null) {
    if (mediaStudioOpening || state.composerDraft.busy || !state.gallery.items.includes(item) || !guardSoundSelectionEdit()) return;
    if (item.upload && !["selected", "succeeded"].includes(item.upload.state)) return;
    const current = soundScope(item), context = galleryUploadContext(), previousHandoff = item.soundHandoff;
    mediaStudioOpening = true;
    try {
      await loadStudio();
      if (!uploadContextCurrent(context) || !el.composer.open) return;
      soundAssert(current);
      let file = item.studioFile || item.file, input;
      if (item.studioProject) input = draftToStudioInput({ project: item.studioProject });
      else if (state.editingDrop && item.id && (state.editingDrop.sound_binding_version > 0 || selectedSound)) {
        input = await coldSoundInput(item, current, Boolean(selectedSound)); file = input.clips[0].file;
      } else {
        if (!file && item.id) file = await soundFile(`/api/media/${encodeURIComponent(item.id)}`, "original", current);
        if (!file) throw new Error("Choose the original file to edit it in Studio.");
        input = { clips: [{ file }] };
      }
      soundAssert(current);
      if (selectedSound) input = { ...input, ...selectedSound, soundMix: null, soundPreviewRequired: false };
      const output = await studio().open(input);
      if (!output || !current()) return;
      const result = await adoptStudioSound(output); soundAssert(current);
      const resultFile = soundResultFile(result), handoff = soundHandoffFromResult(result, previousHandoff);
      await window.LouderrOptional?.load("uploads"); soundAssert(current);
      if (item.upload) mediaUploads().detach(item.upload);
      if (item.previewUrl) { URL.revokeObjectURL(item.previewUrl); state.gallery.previewUrls.delete(item.previewUrl); }
      const previewUrl = URL.createObjectURL(resultFile); state.gallery.previewUrls.add(previewUrl);
      Object.assign(item, { key: "file:" + crypto.randomUUID(), file: resultFile, studioFile: file,
        studioProject: result.project, soundHandoff: handoff, name: resultFile.name, mediaType: resultFile.type, id: null,
        upload: mediaUploads().select(resultFile), previewUrl, url: null, thumbnailUrl: null, existing: false,
        captions: "", originalCaptions: "", transcript: "", originalTranscript: "", captionsPresent: false, transcriptPresent: false });
      if (handoff) delete state.gallery.soundDetach;
      state.gallery.dirty = true; renderDropMediaControl(); renderComposerStep();
      if (handoff) await keepSoundDraft(item, soundScope(item));
    } catch (error) { if (uploadContextCurrent(context)) showError(el.formError, error.message); }
    finally { mediaStudioOpening = false; }
  }

  function setComposerDocumentTitle() {
    document.title = `${state.editingDrop ? "Edit Drop" : "Create"} · ${state.productName}`;
  }

  function openComposerCamera(mode = "photo") {
    if (!el.composer.open || state.composerDraft.busy || !guardSoundSelectionEdit()) return;
    const context = galleryUploadContext(), identity = state.messages;
    const sequence = state.composerCaptureSequence = (state.composerCaptureSequence || 0) + 1;
    let consumed = false;
    const consume = () => {
      if (consumed || sequence !== state.composerCaptureSequence || state.messages !== identity
          || !uploadContextCurrent(context) || !el.composer.open || state.composerDraft.busy) return false;
      consumed = true;
      return true;
    };
    const captureAvailable = Boolean(globalThis.navigator?.mediaDevices?.getUserMedia
      && globalThis.LouderrCamera && (mode !== "video" || globalThis.MediaRecorder));
    if (!captureAvailable) {
      $(mode === "video" ? "#drop-camera-video" : "#drop-camera-photo")?.click();
      return;
    }
    globalThis.LouderrCamera.open({
      title: "Camera", mode, maxVideoSeconds: 90, useLabel: "Use",
      onCapture: file => {
        if (!consume()) return;
        if (file.size > 25 * 1024 * 1024) openStudioFiles([file]);
        else appendComposerFiles([file]);
      },
      onFallback: () => { if (consume()) el.dropMedia?.click(); },
    });
  }

  // The line under the selection. A failed upload says what the server said (a rate limit, a size or
  // type refusal) instead of sending the person into Edit media to find out.
  function composerMediaStatus(unfinished, attention, inReview) {
    if (attention) {
      const reasons = [...new Set(unfinished.map(item => item.upload?.error).filter(reason => typeof reason === "string" && reason.trim()))];
      return reasons.length === 1 ? `${reasons[0].trim()} Open Edit media to retry.` : "An upload needs attention. Open Edit media to check or retry.";
    }
    if (inReview) return "A photo or video is waiting for a safety review. You can publish once it is approved.";
    return unfinished.length ? "Your media is still uploading. You can publish now; the post goes up as soon as it is ready." : "Media ready to publish.";
  }

  function renderComposerStep() {
    const next = $("#composer-next"); if (!next) return;
    const details = state.gallery.step === "details";
    el.dropBody.required = !state.gallery.items.length;
    const bodyLabel = $("#drop-body-label");
    if (bodyLabel) {
      bodyLabel.textContent = state.gallery.items.length ? "Caption" : "Post";
      el.dropBody.placeholder = state.gallery.items.length ? "Write a caption…" : "What would you like to share?";
    }
    $("#composer-media-step").hidden = details;
    $("#composer-details-step").hidden = !details;
    next.hidden = details || !state.gallery.items.length; next.disabled = state.composerDraft.busy || !state.gallery.items.length; el.composer?.classList?.toggle?.("composer-blank", !details && !state.editingDrop && !state.gallery.items.length && !el.dropBody.value && !el.dropTitle?.value && !el.dropLink?.value);
    $("#composer-back").hidden = !details;
    // Post | Story | Live is a choice for something new, not while editing a Drop or adding to a project.
    const mode = $(".composer-mode");
    if (mode) mode.hidden = details || Boolean(state.editingDrop || state.composerJourney);
    el.submitDrop.hidden = !details;
    const unfinished = state.gallery.items.filter(item => item.upload ? item.upload.state !== "succeeded" || item.upload.error : !item.id);
    const attention = unfinished.some(item => item.upload?.error || ["failed", "rejected", "response_lost", "cancelled", "expired", "verification_lost"].includes(item.upload?.state));
    const inReview = !attention && unfinished.some(item => item.upload?.state === "awaiting_review");
    // Publishing waits for media that is still on its way (prepareGallerySelection), as Instagram's Share
    // does, so only an upload that needs attention or a person's review holds the button.
    el.submitDrop.disabled = state.composerDraft.busy || attention || inReview;
    $("#composer-media-status").hidden = !details || !state.gallery.items.length;
    $("#composer-media-status").textContent = composerMediaStatus(unfinished, attention, inReview);
    $("#composer-media-review").hidden = !state.gallery.items.length;
    $("#composer-selection-summary").textContent = state.gallery.items.length + (state.gallery.items.length === 1 ? " item" : " items");
  }

  async function nextComposerStep() {
    if (state.composerDraft.busy || !state.gallery.items.length) return;
    const context = galleryUploadContext();
    state.gallery.step = "details"; renderComposerStep(); hideError(el.formError);
    try { await prepareGallerySelection(); }
    catch (error) {
      if (uploadContextCurrent(context) && error.uploadAlert?.kind !== "pending") showUploadError(el.formError, error, context);
    } finally { if (uploadContextCurrent(context)) renderComposerStep(); }
  }

  function startTextPost() {
    if (state.composerDraft.busy) return;
    if (!state.editingDrop && !el.dropKind.value) {
      const pristine = !composerHasChanges();
      el.dropKind.value = "writing";
      if (pristine) state.composerDraft.baseline = composerFingerprint();
    }
    state.gallery.step = "details";
    renderComposerStep();
    el.dropBody.focus();
  }

  let profileAvatarPreviewUrl = null;
  let profileAvatarUpload = null;
  let profileSaveBusy = false;
  let profileDraftDirty = false;
  let profileDraftOwner = null;
  let profilePhotoFile = null;
  let profilePhotoEdit = null;
  function setProfilePhotoFile(file) {
    const transfer = new DataTransfer();
    if (file) transfer.items.add(file);
    el.profileAvatarInput.files = transfer.files;
  }
  async function adjustProfilePhoto(source) {
    if (!source || !state.capabilities.uploads || !state.capabilities.community_writes) return;
    const operation = { ownerId: state.viewer?.id };
    profilePhotoEdit = operation;
    const file = await window.LouderrProfilePhoto.open(source);
    if (profilePhotoEdit !== operation || state.viewer?.id !== operation.ownerId) return;
    profilePhotoEdit = null;
    if (!file || state.view !== "settings" || !state.capabilities.uploads || !state.capabilities.community_writes) return;
    setProfilePhotoFile(file);
    profileDraftDirty = true;
    renderProfileAvatarControl();
  }
  function renderProfileAvatarControl() {
    renderFileControl(
      el.profileAvatarField,
      el.profileAvatarInput,
      el.profileAvatarLabel,
      el.profileAvatarAction,
      "JPG, PNG, WebP or GIF",
    );
    if (profileAvatarPreviewUrl) {
      URL.revokeObjectURL(profileAvatarPreviewUrl);
      profileAvatarPreviewUrl = null;
    }
    const file = el.profileAvatarInput?.files?.[0];
    profilePhotoFile = file || null;
    const adjust = $("#profile-photo-adjust");
    if (adjust) adjust.hidden = !(file || state.viewer?.avatar_url) || !state.capabilities.uploads;
    if (file) {
      window.LouderrOptional?.load("uploads").catch(() => {});
      profileAvatarPreviewUrl = URL.createObjectURL(file);
      el.profileAvatar.textContent = "";
      el.profileAvatar.style.backgroundImage = `url(${profileAvatarPreviewUrl})`;
      el.profileAvatar.style.backgroundSize = "cover";
      el.profileAvatar.style.backgroundPosition = "center";
    } else if (state.viewer) styleAvatar(el.profileAvatar, state.viewer);
  }

  const accountSectionLabels = { profile: "Edit profile", security: "Security and sign-in", privacy: "Privacy",
    notifications: "Notifications", information: "Your information", appearance: "Appearance",
    help: "Help and support", purchases: "Purchases", creator: "Creator", settings: "Settings and privacy" };
  let accountEditorDirect = false;
  state.privacySettings = window.LouderrPrivacy.create({
    root: $("#privacy-preferences"), api,
    getIdentity: () => ({viewerId: state.viewer?.id || null, csrf: state.csrf, social: state.messages}),
    onChange: () => state.messageTyping?.sync(),
  });
  state.profilePlaces = window.LouderrProfilePlaces.create({
    root: $("#profile-places"), api,
    getIdentity: () => ({viewerId: state.viewer?.id || null, csrf: state.csrf, social: state.messages}),
    getSavedLocation: () => state.viewer?.location || "",
    onChange: () => { profileDraftDirty = true; },
  });

  function setAccountSection(section, load = true) {
    if (!accountSections.has(section)) section = "settings";
    if (!state.capabilities.commerce && ["purchases", "creator"].includes(section)) section = "settings";
    if (!state.viewer) section = "profile";
    const changed = state.accountSection !== section;
    const cameFromList = state.accountSection === "settings";
    state.accountSection = section;
    if (section !== "profile") state.profilePlaces?.dismiss();
    el.settings.dataset.section = section;
    for (const name of accountSections) {
      const panel = $("#account-" + name + "-panel");
      if (panel) panel.hidden = name !== section;
    }
    const title = $("#account-page-title");
    title.hidden = !state.viewer;
    title.textContent = section === "profile" && (state.ageAttestationRequired || state.ageRequirementNotMet)
      ? "Your account" : accountSectionLabels[section];
    if (state.view === "settings") {
      document.title = `${accountSectionLabels[section] || "Settings and privacy"} · ${state.productName}`;
    }
    if (changed) {
      $(".profile-body", el.settings).scrollTop = 0;
      // Settings scrolls with the page now; reset the real scroller when changing sections.
      if (state.view === "settings" && document.scrollingElement) document.scrollingElement.scrollTop = 0;
    }
    if (!load || !state.viewer) return;
    if (location.pathname.split("/")[1] === "settings" && !new URLSearchParams(location.search).has("moderation_action")) {
      const path = section === "settings" ? "/settings" : "/settings/" + section;
      // Opening a section from the list is a step you can take back, so it gets an entry and the
      // system Back returns to the list rather than leaving Settings. Every other move replaces,
      // so sections cannot stack up on one another.
      if (location.pathname !== path) {
        const step = changed && cameFromList && section !== "settings";
        if (step) history.pushState(history.state, "", path);
        else history.replaceState(history.state, "", path);
        if (step) el.settings.dataset.sectionStep = "true";
        else delete el.settings.dataset.sectionStep;
      }
    }
    if (section === "security") refreshSecurity();
    if (section === "privacy") { refreshRelationships(); refreshWordFilter(); state.privacySettings?.refresh(); }
    if (section === "purchases") refreshPurchases();
    if (section === "creator") refreshCreator();
    if (section === "notifications") refreshPushControls().catch(() => {});
    if (state.view === "settings") title.focus({ preventScroll: true });
  }

  function backFromAccountSection() {
    if (state.accountSection === "profile" && accountEditorDirect) { goBack(); return; }
    // The button and the system Back are the same step, so they must not undo each other.
    if (el.settings.dataset.sectionStep === "true") { history.back(); return; }
    const previous = state.accountSection;
    setAccountSection("settings");
    $("#account-tab-" + previous)?.focus({ preventScroll: true });
  }

  function syncThemeControls() {
    const preference = window.lobbyTheme?.preference?.() || "system";
    const resolved = window.lobbyTheme?.resolved?.() || document.documentElement.dataset.theme || "dark";
    $$('[data-theme-choice]').forEach((input) => {
      input.checked = input.value === preference;
    });
    const status = preference === "system"
      ? `Following this device · currently ${resolved}`
      : `Using ${resolved} mode on this device`;
    $$('[data-theme-status]').forEach((node) => { node.textContent = status; });
  }

  function openAccountDialog(section = null, authView = "login") {
    if (["settings", "appearance", "notifications"].includes(section) && !state.viewer) {
      const settingsPath = location.pathname.split("/")[1] === "settings"
        ? `${location.pathname}${location.search}` : null;
      syncThemeControls();
      openDialogWithHistory($("#appearance-dialog"), settingsPath, { returnTo: "/" });
      return;
    }
    renderAccount();
    accountEditorDirect = section === "profile" && location.pathname.split("/")[1] !== "settings";
    if (state.viewer) {
      const needsSetup = !state.viewer.email_verified || state.ageAttestationRequired || state.ageRequirementNotMet;
      setAccountSection(section || (needsSetup ? "profile" : "settings"));
    }
    if (state.viewer) {
      // A member goes to the Settings screen. The bottom bar stays where it is, which is the point:
      // Home, Discover, Video and Profile were unreachable from inside the old popup.
      // Settle the sign-in entry before moving to the account screen. Its asynchronous close
      // event must not restore the old route over the newly opened age/verification step.
      for (const dialog of [el.account, el.comments, el.project]) {
        if (!dialog?.open) continue;
        if (dialog === el.account) dialog.keepComposerIntent = true;
        settleDialogHistory(dialog);
        dialog.close();
      }
      const current = state.accountSection;
      const onSettings = location.pathname.split("/")[1] === "settings";
      const path = onSettings
        ? `${location.pathname}${location.search}`
        : (current && current !== "settings" ? `/settings/${current}` : "/settings");
      if (!onSettings) history.pushState({}, "", path);
      setView("settings");
      syncThemeControls();
      $("#account-page-title").focus({ preventScroll: true });
      return;
    }
    // Each guest entry selects its advertised task without clearing either form draft.
    authTab(authView);
    if (location.pathname.split("/")[1] === "settings") {
      openDialogWithHistory(el.account, `${location.pathname}${location.search}`);
    } else openDialogWithHistory(el.account);
    syncThemeControls();
    if (state.viewer) $("#account-page-title").focus({ preventScroll: true });
  }

  const federatedProviderLabels = new Map([["google", "Google"], ["apple", "Apple"]]);

  function configuredFederatedProviders() {
    if (!state.capabilities.social_login) return new Set();
    const configured = new Set();
    for (const provider of state.federatedProviders || []) {
      if (provider && federatedProviderLabels.has(provider.id)) configured.add(provider.id);
    }
    return configured;
  }

  function renderFederatedAuth() {
    clear(el.federatedProviders);
    const providers = configuredFederatedProviders();
    const inviteTab = $("#auth-tab-invite");
    if (inviteTab) {
      inviteTab.hidden = Boolean(state.federatedRegistration || state.capabilities.registration);
      if (inviteTab.hidden && !$("#invite-panel").hidden) {
        authTab("login");
        return;
      }
    }
    for (const provider of ["google", "apple"]) {
      if (!providers.has(provider)) continue;
      const link = create("a", "federated-provider-button", `Continue with ${federatedProviderLabels.get(provider)}`);
      link.href = `/api/auth/oauth/${provider}/start`;
      link.rel = "nofollow";
      link.addEventListener("click", event => {
        rememberComposerAuthReturn();
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        // A provider callback changes the session cookie before the old page unloads.
        // Do not send its queued guest telemetry as the newly restricted member.
        state.analyticsRedirecting = true;
        clearTimeout(state.analyticsTimer);
        state.analyticsQueue.splice(0);
      });
      el.federatedProviders.append(link);
    }
    const registrationActive = !$("#register-form").hidden;
    const resetActive = !$("#reset-form").hidden;
    el.federatedAuth.hidden = !state.capabilities.social_login
      || providers.size === 0 || resetActive
      || (registrationActive && !state.federatedRegistration);
    $("#guest-account-title").textContent = state.viewer ? "Account"
      : resetActive ? "Reset password" : registrationActive ? "Create account"
      : !$("#invite-panel").hidden ? "Join with a code" : "Sign in";
    $(".auth-divider span", el.federatedAuth).textContent = registrationActive
      ? "or use your email" : "or sign in with a password";
    const hint = $("#federated-signup-note");
    if (hint) {
      const registrationFull = state.capacity && !seatsAcceptRegistration(state.capacity);
      hint.hidden = el.federatedAuth.hidden || !state.federatedRegistration
        || (state.capabilities.registration && !registrationFull);
      hint.textContent = registrationFull
        ? "This round is full. Existing members can still sign in."
        : state.capabilities.registration
          ? "Choose Google or create an account with your email address. No invitation code needed."
          : "No invitation code needed. Your Google account is all you need to get started.";
    }
    renderPasswordSignIn(providers);
  }

  function renderPasswordSignIn(providers) {
    const toggle = $("#password-sign-in-toggle");
    if (!toggle) return;
    const login = $("#login-form");
    const resetActive = !$("#reset-form").hidden;
    const singleLogin = !state.capabilities.registration
      && (!state.capabilities.beta_access || state.federatedRegistration);
    const providerFirst = singleLogin && !resetActive
      && state.capabilities.social_login && providers.size > 0;
    // A late provider response must not hide credentials already being entered.
    if (providerFirst && !toggle.dataset.initialized) {
      if (login.contains(document.activeElement)
        || $$('input', login).some((input) => input.value)) state.passwordSignInExpanded = true;
      toggle.dataset.initialized = "true";
    }
    toggle.hidden = !providerFirst;
    toggle.setAttribute("aria-expanded", String(Boolean(state.passwordSignInExpanded)));
    toggle.textContent = state.passwordSignInExpanded ? "Hide password sign-in" : "Sign in with a password";
    $(".auth-tabs", el.guestAccount).hidden = resetActive || singleLogin;
    $(".auth-divider", el.federatedAuth).hidden = providerFirst;
    if (singleLogin && !resetActive) login.hidden = providerFirst && !state.passwordSignInExpanded;
    login.setAttribute("role", singleLogin ? "form" : "tabpanel");
    if (singleLogin) {
      login.removeAttribute("aria-labelledby");
      login.setAttribute("aria-label", "Sign in with a password");
    } else {
      login.removeAttribute("aria-label");
      login.setAttribute("aria-labelledby", "auth-tab-login");
    }
  }

  function providerAuthorizationUrl(provider, value) {
    const expectedOrigins = {
      google: "https://accounts.google.com",
      apple: "https://appleid.apple.com",
    };
    if (!expectedOrigins[provider] || typeof value !== "string") return null;
    try {
      const url = new URL(value);
      return url.origin === expectedOrigins[provider] ? url.href : null;
    } catch (_) { return null; }
  }

  async function connectFederatedProvider(provider, button) {
    button.disabled = true;
    try {
      const result = await runRecentAction(() => api(`/api/account/identities/${provider}/start`, {
        method: "POST", csrf: true,
      }));
      const destination = providerAuthorizationUrl(provider, result.authorization_url);
      if (!destination) throw new Error("The sign-in provider returned an unsafe destination.");
      location.assign(destination);
    } catch (error) {
      toast(error.message, true);
      button.disabled = false;
    }
  }

  async function disconnectFederatedProvider(provider, button) {
    const label = federatedProviderLabels.get(provider) || provider;
    button.disabled = true;
    try {
      await runRecentAction(() => api(`/api/account/identities/${provider}`, {
        method: "DELETE", csrf: true,
      }));
      state.security.loaded = false;
      await refreshSecurity(true);
      toast(`${label} was disconnected.`);
    } catch (error) {
      toast(error.message, true);
      button.disabled = false;
    }
  }

  function renderManagedAccounts() {
    const viewerId = state.viewer?.id || null;
    const identity = JSON.stringify([viewerId, state.viewer?.email_verified, state.viewer?.status, state.viewer?.display_name]);
    if (state.managedAccounts?.identity !== identity) {
      const changedViewer = state.managedAccounts?.viewerId !== viewerId;
      state.managedAccounts = { identity, viewerId, items: [], loaded: false, loading: false, busy: false, error: null, panel: "list" };
      if (changedViewer) {
        $("#managed-account-form").reset();
        $("#account-switcher-dialog").close();
      }
    }
    const accounts = state.managedAccounts;
    const creating = accounts.panel === "create";
    const recovering = Boolean(accounts.recovery);
    const trigger = $("#account-switcher-trigger");
    trigger.hidden = !viewerId;
    trigger.setAttribute("aria-label", `${state.viewer?.handle || "Account"}, switch accounts`);
    trigger.setAttribute("aria-expanded", String($("#account-switcher-dialog").open));
    $("#active-account-handle").textContent = state.viewer?.handle || "";
    $("#guest-account-title").hidden = Boolean(viewerId);
    $("#managed-accounts-title").textContent = creating ? "Add account" : "Switch account";
    $("#managed-account-back").hidden = !creating;
    $("#managed-account-back").disabled = accounts.busy;
    $("#managed-account-close").disabled = accounts.busy;
    $("#managed-accounts").hidden = creating;
    $("#managed-account-form").hidden = !creating;
    $("#managed-account-add").disabled = !accounts.loaded || !accounts.canCreate || accounts.busy || recovering;
    $("#managed-account-limit").hidden = !accounts.loaded || accounts.canCreate;
    $("#managed-account-limit").textContent = accounts.items.length >= 3
      ? "3 of 3 accounts"
      : state.viewer?.email_verified
        ? "Account creation is currently unavailable."
        : "Verify your email to add an account.";
    $("#managed-account-error").hidden = !accounts.error;
    $("#managed-account-error").textContent = accounts.error || "";
    $("#managed-account-retry").hidden = !accounts.error || (creating && !recovering);
    $("#managed-account-retry").disabled = accounts.loading || accounts.busy;
    $("#managed-account-progress").hidden = creating || !(accounts.loading || accounts.busy);
    $("#managed-account-progress").textContent = recovering ? "Checking account…" : accounts.busy ? "Switching account…" : "Loading accounts…";
    const list = $("#managed-account-list");
    clear(list);
    for (const account of accounts.items) {
      const button = create("button", "managed-account-row");
      button.type = "button";
      button.disabled = accounts.busy || recovering;
      if (account.current) button.setAttribute("aria-current", "true");
      const avatar = create("span", "avatar");
      avatar.setAttribute("aria-hidden", "true");
      styleAvatar(avatar, account);
      const copy = create("span", "managed-account-copy");
      copy.append(create("strong", "", account.handle), create("span", "", account.display_name));
      button.append(avatar, copy);
      if (account.current) {
        const check = create("span", "managed-account-check", "✓");
        check.setAttribute("aria-label", "Current account");
        button.append(check);
      }
      button.addEventListener("click", () => account.current ? closeManagedAccounts() : switchManagedAccount(account.id));
      list.append(button);
    }
    for (const control of $("#managed-account-form").elements) control.disabled = accounts.busy || recovering;
    $("#managed-account-submit").textContent = accounts.busy && creating ? "Creating account…" : "Create account";
  }

  function openManagedAccounts() {
    if (!state.viewer) return;
    renderManagedAccounts();
    state.managedAccounts.panel = "list";
    state.managedAccounts.error = null;
    openDialog($("#account-switcher-dialog"));
    renderManagedAccounts();
    $("#managed-accounts-title").focus();
    refreshManagedAccounts();
  }

  function closeManagedAccounts() {
    if (state.managedAccounts?.busy) return;
    $("#account-switcher-dialog").close();
  }

  function setManagedAccountPanel(panel) {
    const accounts = state.managedAccounts;
    if (!accounts || accounts.busy || (panel === "create" && !accounts.canCreate)) return;
    accounts.panel = panel;
    accounts.error = null;
    renderManagedAccounts();
    (panel === "create" ? $("#managed-accounts-title") : $("#managed-account-add")).focus();
  }

  function cancelManagedAccounts(event) {
    if (state.managedAccounts?.busy) event.preventDefault();
    else if (state.managedAccounts?.panel === "create") {
      event.preventDefault();
      setManagedAccountPanel("list");
    }
  }

  async function refreshManagedAccounts() {
    const accounts = state.managedAccounts;
    if (!state.viewer || !accounts || accounts.loading || accounts.busy) return;
    accounts.loading = true;
    accounts.error = null;
    renderManagedAccounts();
    try {
      if (accounts.recovery && !await recoverManagedAccountChange(accounts)) return;
      const result = await api("/api/account/accounts");
      if (state.managedAccounts !== accounts || state.viewer?.id !== accounts.viewerId) return;
      accounts.items = result.items;
      accounts.canCreate = result.can_create;
      accounts.loaded = true;
      accounts.error = null;
    } catch (error) {
      if (state.managedAccounts === accounts) accounts.error = error.message;
    } finally {
      accounts.loading = false;
      if (state.managedAccounts === accounts) renderManagedAccounts();
    }
  }

  async function recoverManagedAccountChange(accounts) {
    const current = () => state.managedAccounts === accounts && state.viewer?.id === accounts.viewerId;
    const recovery = accounts.recovery;
    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), 8000);
    try {
      // This read intentionally has no X-Account-ID: the cookie may already have
      // changed even though the mutation's response could not be read.
      const auth = await api("/api/auth/me", { signal: controller.signal });
      if (!current() || accounts.recovery !== recovery) return false;
      if (!auth || !Object.prototype.hasOwnProperty.call(auth, "user")
        || (auth.user !== null && (typeof auth.user?.id !== "string" || !auth.user.id))) throw new Error("Invalid account response");
      if ((auth.user?.id || null) !== accounts.viewerId) {
        try { localStorage.setItem("louderr-account-change", crypto.randomUUID()); } catch (_) {}
        reloadAfterAccountChange(`/?account=${recovery.section}`);
        return false;
      }
      if (typeof auth.csrf_token !== "string" || !auth.csrf_token) throw new Error("Missing session token");
      state.csrf = auth.csrf_token;
      accounts.recovery = null;
      accounts.error = recovery.error;
      return true;
    } catch (_) {
      if (current()) accounts.error = "We couldn't confirm your active account. Check your connection and try again.";
      return false;
    } finally { clearTimeout(deadline); }
  }

  async function changeManagedAccount(path, payload, section = "profile") {
    const accounts = state.managedAccounts;
    if (!state.viewer || !accounts || accounts.busy) return;
    if (accounts.recovery) { await refreshManagedAccounts(); return; }
    if (state.composerDraft.busy || profileSaveBusy) {
      toast("Wait for your changes to finish saving before switching accounts.");
      return;
    }
    if ((profileDraftDirty || hasUnsavedComposerDrafts())
      && !(await confirm("Switch accounts and discard your unsaved changes?"))) return;
    if (!$("#account-switcher-dialog").open) {
      accounts.panel = "list";
      openDialog($("#account-switcher-dialog"));
    }
    accounts.busy = true;
    accounts.error = null;
    renderManagedAccounts();
    try {
      await api(path, { method: "POST", csrf: true, json: payload });
      if (state.managedAccounts !== accounts || state.viewer?.id !== accounts.viewerId) return;
      // Rebuild every private surface and connection with the newly rotated cookie.
      try { localStorage.setItem("louderr-account-change", crypto.randomUUID()); } catch (_) {}
      reloadAfterAccountChange(`/?account=${section}`);
    } catch (error) {
      if (state.managedAccounts !== accounts || state.viewer?.id !== accounts.viewerId) return;
      accounts.recovery = { section, error: error.message };
      renderManagedAccounts();
      await recoverManagedAccountChange(accounts);
      accounts.busy = false;
      if (state.managedAccounts === accounts) renderManagedAccounts();
    }
  }

  function reloadAfterAccountChange(destination = null) {
    globalThis.LouderrCalls?.reset();
    globalThis.LouderrVoicePlayback?.reset();
    // The shared session has already changed. Keeping an old private screen is
    // no longer a valid cancellation; drafts were confirmed before the request.
    state.accountChangeReload = true;
    if (state.socket) state.socket.close();
    if (destination) location.assign(destination);
    else location.reload();
  }

  async function switchManagedAccount(accountId, section = "profile") {
    return changeManagedAccount("/api/account/accounts/switch", { account_id: accountId }, section);
  }

  async function createManagedAccount(event) {
    event.preventDefault();
    const form = event.currentTarget.elements;
    return changeManagedAccount("/api/account/accounts", {
      display_name: form.display_name.value.trim(), handle: form.handle.value.trim() || null,
    });
  }

  function renderAccount() {
    if (state.messageAttachment?.current?.() === false) clearMessageAttachment();
    state.messageSearch?.sync();
    state.messageShared?.sync();
    state.messageAlbums?.sync();
    state.messagePolls?.sync();
    globalThis.louderrSoundPlayback?.syncScope();
    state.savedFolderControls?.syncIdentity();
    state.sharedCollections?.syncIdentity();
    state.postArchive?.syncIdentity();
    state.coauthors?.syncIdentity();
    state.originalSounds?.syncIdentity();
    state.videoRemixControls?.syncIdentity();
    state.videoRemixRemoval?.syncIdentity();
    state.storiesV2?.syncIdentity();
    state.live?.syncIdentity();
    state.socialExtras?.syncIdentity();
    globalThis.LouderrVideoRemixStudio?.sync();
    globalThis.LouderrCalls?.sync();
    globalThis.LouderrVoicePlayback?.sync();
    globalThis.LouderrMessageTools?.sync();
    state.messages.scheduled?.sync();
    globalThis.LouderrMessageHide?.sync();
    state.groupInvites?.sync();
    globalThis.LouderrMessageGroups?.sync();
    state.followState?.syncIdentity();
    state.followRequests?.syncIdentity();
    state.followRequests?.syncCapabilities();
    state.connectionsPanel?.syncIdentity();
    state.connectionsPanel?.syncCapabilities();
    if (state.privacySettings?.syncIdentity()) void state.privacySettings.refresh();
    state.messageTyping?.sync();
    state.profilePlaces?.syncIdentity();
    syncAdminAccess();
    if (typeof updateSurfaceChrome === "function") updateSurfaceChrome();
    const memberHomeIdentity = state.viewer ? `${state.viewer.id}:${Boolean(state.viewer.email_verified)}:${state.viewer.status}:${Boolean(state.sessionRestricted)}` : null;
    if (memberHomeIdentity !== state.memberHomeFor) {
      state.memberHomeFor = memberHomeIdentity;
      if (!state.sessionRestricted) globalThis.LouderrHome?.refresh();
      if (typeof refreshPushControls === "function") refreshPushControls().catch(() => {});
    }
    if (state.exposure) syncExposureIdentity();
    const viewerId = state.viewer?.id || null;
    if (el.ageError && el.confirmAge.ageOperation && el.confirmAge.ageOperation.identity !== state.messages) {
      hideError(el.ageError); el.ageBirthDate.value = ""; el.ageBirthDate.disabled = false;
      el.confirmAge.disabled = false; el.confirmAge.textContent = "Confirm"; el.confirmAge.ageOperation = null;
    }
    if (profileDraftOwner !== viewerId) {
      globalThis.LouderrProfilePhoto?.cancel();
      profilePhotoEdit = null;
      profilePhotoFile = null;
      profileDraftOwner = viewerId;
      profileDraftDirty = false;
      profileAvatarUpload = null;
      hideError(el.profileSaveError);
      if (el.ageError) {
        hideError(el.ageError); el.ageBirthDate.value = "";
        el.ageBirthDate.disabled = false; el.confirmAge.disabled = false;
        el.confirmAge.textContent = "Confirm"; el.confirmAge.ageOperation = null;
      }
      el.profileForm.elements.avatar.value = "";
      mediaUploadManager?.detachAll();
      const previousUploadStatus = $(".avatar-upload-status", el.profileAvatarField);
      if (previousUploadStatus) clear(previousUploadStatus);
    }
    if (businessViewerId !== viewerId) {
      businessViewerId = viewerId;
      state.paidContentVersion = (state.paidContentVersion || 0) + 1;
      clearBusinessAds();
      businessInstance?.reset();
      // Sign-in happens in a dialog without changing the current route.
      // Rebuild mounted business panels for the new identity after clearing old data.
      if (["advertise", "premium"].includes(state.view)) businessInstance?.refresh();
    }
    syncComposerIdentity();
    updateSurfaceChrome();
    const user = state.viewer;
    renderManagedAccounts();
    const accountActive = user?.status === "active";
    const restricted = Boolean(user && state.sessionRestricted);
    $$('[data-current-name]').forEach((node) => renderAccountName(node, user, "Sign in"));
    $$('[data-current-avatar]').forEach((node) => styleAvatar(node, user));
    $("#account-status").textContent = user
      ? (accountActive
        ? (user.email_verified ? `@${user.handle}` : `Verify your email · @${user.handle}`)
        : `${user.status} · @${user.handle}`)
      : "Guest · read only";
    el.guestAccount.hidden = Boolean(user);
    el.memberAccount.hidden = !user;
    $("#guest-open-settings").hidden = true;
    const logoutButton = $("#logout-account");
    if (logoutButton) logoutButton.hidden = !user;
    if (typeof document !== "undefined" && document.body?.dataset) document.body.dataset.viewer = user ? "member" : "guest";
    const mobileProfileLabel = $(".mobile-bottom-nav [data-open-self-profile] span");
    if (mobileProfileLabel) mobileProfileLabel.textContent = user ? "Profile" : "Sign in";
    renderFederatedAuth();
    setAccountSection(user ? state.accountSection : "profile", false);
    el.verificationPanel.hidden = !user || user.email_verified || !accountActive;
    el.agePanel.hidden = !user || !state.ageAttestationRequired || !accountActive;
    const ageLimited = Boolean(user && (state.ageAttestationRequired || state.ageRequirementNotMet));
    $("#age-access-notice").hidden = !user || !state.ageRequirementNotMet;
    $("#age-access-title").textContent = `LOUDERR is for ages ${state.minimumAge} and up`;
    $(".account-shortcuts", el.memberAccount).hidden = ageLimited;
    $("#account-tab-profile span").textContent = ageLimited ? "Your account" : "Edit profile";
    $("[data-account-section='creator']").hidden = ageLimited || !state.capabilities.commerce;
    el.profileForm.hidden = ageLimited;
    $("#age-review-messages").hidden = !ageLimited;
    el.messagesNew.disabled = ageLimited;
    el.messagesEmptyNew.disabled = ageLimited;
    if (restricted) {
      state.messages.receipts?.destroy();
      state.messages.receipts = null;
    }
    el.adminNav.hidden = !user || !accountActive || restricted || !staffRoles.has(user.role);
    el.adminAccount.hidden = el.adminNav.hidden;
    $("#account-staff-verification").hidden = el.adminNav.hidden;
    $("#account-dialog-copy").hidden = Boolean(user && !restricted);
    $("#account-dialog-copy").textContent = ageLimited
      ? "Your account tools remain available in Settings and privacy."
      : restricted
      ? "You can review moderation actions, appeal, manage existing billing in Settings and Purchases, export or delete your account, and log out."
      : user
        ? "Manage your public profile, safety controls, and session."
        : "Sign in to publish and participate.";
    $("#export-account").disabled = !user;
    if ($("#export-followers")) $("#export-followers").disabled = !user;
    $("#logout-all-account").disabled = !accountActive;
    $("#delete-account").disabled = !user;
    updateChatComposer();
    if (el.chatBody.readOnly && state.emotes.target === el.chatBody) closeEmotePicker();
    $("#security-password-username").value = user?.email || "";
    $("#security-email-username").value = user?.email || "";
    const passwordEnabled = user?.password_login_enabled !== false;
    const managedAccount = Boolean(user?.primary_account_id && user.primary_account_id !== user.id);
    $("#managed-signin-note").hidden = !managedAccount;
    $("#security-password-form").closest("details").hidden = managedAccount;
    $("#security-email-form").closest("details").hidden = managedAccount;
    $("#security-identity-list").closest("section").hidden = managedAccount;
    const passwordCurrentField = $("#security-password-current-field");
    const emailCurrentField = $("#security-email-current-field");
    const passwordCurrent = $("input[name='current_password']", $("#security-password-form"));
    const emailCurrent = $("input[name='current_password']", $("#security-email-form"));
    passwordCurrentField.hidden = !passwordEnabled;
    emailCurrentField.hidden = !passwordEnabled;
    passwordCurrent.required = passwordEnabled;
    emailCurrent.required = passwordEnabled;
    passwordCurrent.disabled = !passwordEnabled;
    emailCurrent.disabled = !passwordEnabled;
    $("#security-password-copy").hidden = passwordEnabled;
    $("#security-password-summary").textContent = passwordEnabled ? "Change password" : "Add a password";
    $("button[type='submit']", $("#security-password-form")).textContent = passwordEnabled ? "Change password" : "Add password";
    if (user) {
      const form = el.profileForm.elements;
      if (!profileDraftDirty) {
        if (form.handle) form.handle.value = user.handle || "";
        form.display_name.value = user.display_name || "";
        form.bio.value = user.bio || "";
        form.location.value = user.location || "";
        state.profilePlaces?.load(user.location || "");
        form.website.value = user.website || "";
        renderTranslationPreferences();
      }
      styleAvatar(el.profileAvatar, user);
      renderProfileAvatarControl();
      [...form].forEach((control) => { control.disabled = !accountActive || !state.capabilities.community_writes; });
      if (form.location.disabled) state.profilePlaces?.dismiss();
      renderTranslationPreferenceAvailability();
    }
    if (!user) {
      el.profileAvatarInput.value = "";
      renderProfileAvatarControl();
      state.notifications.unreadCount = 0;
      state.messages.unreadCount = 0;
    }
    updateSocialBadges();
    renderEnforcement();
    const canManageRelationships = Boolean(user?.email_verified && accountActive && !ageLimited);
    el.relationshipControls.hidden = !canManageRelationships;
    $("#privacy-access-note").hidden = canManageRelationships;
    if (canManageRelationships && !state.relationships.loaded && !state.relationships.loading) {
      refreshRelationships();
    }
    syncThemeControls();
  }

  function renderRelationships() {
    clear(el.relationshipsList);
    el.relationshipsLoading.hidden = !state.relationships.loading;
    el.relationshipsEmpty.hidden = state.relationships.loading || state.relationships.items.length > 0;
    for (const item of state.relationships.items) {
      if (!item.user) continue;
      const row = create("article", "relationship-row");
      const person = create("div", "relationship-person");
      const avatar = create("span", "avatar avatar-small");
      styleAvatar(avatar, item.user);
      const identity = create("span");
      identity.append(
        accountNameNode(item.user),
        create("small", "", `@${item.user.handle} · ${{ block: "Blocked", restrict: "Restricted" }[item.kind] || "Muted"}`),
      );
      person.append(avatar, identity);
      const remove = create("button", "secondary-button", item.kind === "block" ? "Unblock" : "Unmute");
      remove.type = "button";
      remove.addEventListener("click", async () => {
        const viewerId = state.viewer?.id;
        const relationships = state.relationships;
        const current = () => state.viewer?.id === viewerId && state.relationships === relationships;
        remove.disabled = true;
        try {
          await api(`/api/relationships/${item.kind}/${encodeURIComponent(item.user.id)}`, {
            method: "DELETE", csrf: true,
          });
          if (!current()) return;
          clearBusinessAds();
          state.feedWindows?.invalidate({ erase: true });
          state.relationships.loaded = false;
          await refreshRelationships();
          if (current()) toast(`${item.kind === "block" ? "Unblocked" : "Unmuted"} ${item.user.display_name}`);
        } catch (error) {
          if (current()) { toast(error.message, true); remove.disabled = false; }
        }
      });
      row.append(person, remove);
      el.relationshipsList.append(row);
    }
  }

  // The filter is a list; the field is a comma-separated line, because typing one is
  // faster than managing chips for something people set once and rarely revisit.
  function wordFilterWords(value) {
    return value.split(",").map((word) => word.trim()).filter(Boolean);
  }

  async function refreshWordFilter() {
    if (!el.wordFilter) return;
    el.wordFilter.hidden = !state.viewer?.email_verified;
    if (el.wordFilter.hidden) return;
    try {
      const result = await api("/api/account/privacy");
      const words = Array.isArray(result?.muted_words) ? result.muted_words : [];
      el.wordFilterInput.value = words.join(", ");
      el.wordFilterStatus.textContent = words.length
        ? `${words.length} ${words.length === 1 ? "word" : "words"} filtered.`
        : "No words filtered.";
      state.wordFilterPolicy = result?.message_policy || "everyone";
    } catch (_) {
      el.wordFilter.hidden = true;
    }
  }

  async function saveWordFilter(event) {
    event.preventDefault();
    if (!state.viewer) return;
    hideError(el.wordFilterError);
    const words = wordFilterWords(el.wordFilterInput.value);
    try {
      // message_policy is required by the endpoint, so send back the one already set
      // rather than silently changing who may message this account.
      const current = state.wordFilterPolicy
        || (await api("/api/account/privacy")).message_policy || "everyone";
      await api("/api/account/privacy", {
        method: "PATCH", csrf: true, json: { message_policy: current, muted_words: words },
      });
      await refreshWordFilter();
      toast(words.length ? "Word filter saved" : "Word filter cleared");
    } catch (error) { showError(el.wordFilterError, error.message); }
  }

  async function refreshRelationships() {
    if (!state.viewer?.email_verified || state.viewer.status !== "active" || state.relationships.loading) return;
    const relationships = state.relationships;
    const social = state.messages;
    const viewerId = state.viewer.id;
    const current = () => state.relationships === relationships && state.messages === social
      && state.viewer?.id === viewerId;
    state.relationships.loading = true;
    renderRelationships();
    try {
      const result = await api("/api/relationships", { csrf: true });
      if (!current()) return;
      state.relationships.items = result.items || [];
      state.relationships.loaded = true;
    } catch (error) { if (current()) toast(error.message, true); }
    finally {
      if (current()) { state.relationships.loading = false; renderRelationships(); }
    }
  }

  function renderSecurity() {
    const trustSummary = $("#account-trust-summary");
    if (trustSummary) {
      const trust = state.security.trust;
      trustSummary.textContent = !trust ? "Loading account access…"
        : trust.trust_state === "trusted" ? "Higher limits are active. A staff-approved checkmark grants trust automatically; normal community rules still apply."
        : trust.trust_state === "normal" ? `You can post, reply and send message requests. Higher limits are earned through regular use: ${Math.min(Math.floor(trust.account_age_seconds / 86400), Math.ceil(trust.trusted_account_age_required_seconds / 86400))} of ${Math.ceil(trust.trusted_account_age_required_seconds / 86400)} days and ${trust.successful_actions} of ${trust.trusted_successful_actions_required} successful interactions, subject to safety checks. Repeated actions and rejected content do not add progress.`
        : !trust.email_verified ? "Verify your email to post, reply and send message requests."
        : "Some limits apply to this account. Review your account notices for any actions taken.";
    }
    clear(el.securitySessions);
    clear(el.securityNotifications);
    clear(el.securityIdentities);
    const configured = configuredFederatedProviders();
    const linked = new Map((state.security.identities || []).map((item) => [item.provider, item]));
    for (const provider of ["google", "apple"]) {
      if (!configured.has(provider) && !linked.has(provider)) continue;
      const row = create("article", "security-row");
      const copy = create("div", "security-row-copy");
      const identity = linked.get(provider);
      copy.append(
        create("strong", "", federatedProviderLabels.get(provider)),
        create(
          "span",
          "",
          identity
            ? `Connected · last used ${relativeTime(identity.last_used_at)}`
            : "Not connected",
        ),
      );
      if (identity) {
        if (identity.can_disconnect === false) {
          const needsRecovery = identity.disconnect_blocked_reason === "password_recovery_unavailable";
          if (needsRecovery) copy.append(create("span", "", "Keep this connected until password recovery is available."));
          row.append(copy, create("span", "identity-connected", needsRecovery ? "Keep connected" : "Only sign-in"));
        } else {
          const disconnect = create("button", "secondary-button", "Disconnect");
          disconnect.type = "button";
          disconnect.addEventListener("click", () => disconnectFederatedProvider(provider, disconnect));
          row.append(copy, disconnect);
        }
      } else {
        const connect = create("button", "secondary-button", "Connect");
        connect.type = "button";
        connect.addEventListener("click", () => connectFederatedProvider(provider, connect));
        row.append(copy, connect);
      }
      el.securityIdentities.append(row);
    }
    if (!el.securityIdentities.childElementCount) {
      el.securityIdentities.append(create("p", "utility-empty", "No provider sign-ins are configured."));
    }
    const sessions = state.security.sessions || [];
    for (const session of sessions) {
      const row = create("article", "security-row");
      const copy = create("div", "security-row-copy");
      copy.append(
        create("strong", "", session.current ? "Current session" : "Active session"),
        create("span", "", `Last used ${relativeTime(session.last_seen_at)} · expires ${new Date(session.expires_at).toLocaleDateString()}`),
      );
      const revoke = create("button", session.current ? "danger-button" : "secondary-button", session.current ? "Log out" : "Remove");
      revoke.type = "button";
      revoke.addEventListener("click", async () => {
        const viewerId = state.viewer?.id || null;
        const socialState = state.messages;
        const current = () => state.messages === socialState && (state.viewer?.id || null) === viewerId;
        revoke.disabled = true;
        try {
          if (session.current) state.endingSession = (state.endingSession || 0) + 1;
          try { await api(`/api/security/sessions/${encodeURIComponent(session.id)}`, { method: "DELETE", csrf: true }); }
          finally { if (session.current) state.endingSession -= 1; }
          if (!current()) return;
          if (session.current) {
            finishLocalLogout();
            toast("Logged out");
          } else {
            state.security.loaded = false;
            await refreshSecurity(true);
            if (!current()) return;
            toast("Session removed");
          }
        } catch (error) {
          if (!current()) return;
          toast(error.message, true); revoke.disabled = false;
        }
      });
      row.append(copy, revoke);
      el.securitySessions.append(row);
    }
    if (!sessions.length) el.securitySessions.append(create("p", "utility-empty", "No active sessions were returned."));
    el.securityRevokeOthers.hidden = sessions.filter((item) => !item.current).length === 0;

    const notices = state.security.notifications || [];
    for (const notice of notices) {
      const row = create("button", `security-notice${notice.read_at ? "" : " is-unread"}`);
      row.type = "button";
      const copy = create("span", "security-row-copy");
      copy.append(create("strong", "", notice.title), create("span", "", notice.body), create("time", "", relativeTime(notice.created_at)));
      row.append(copy);
      row.disabled = Boolean(notice.read_at);
      row.addEventListener("click", async () => {
        row.disabled = true;
        try {
          await api(`/api/security/notifications/${encodeURIComponent(notice.id)}/read`, { method: "PUT", csrf: true });
          notice.read_at = new Date().toISOString();
          renderSecurity();
        } catch (error) { toast(error.message, true); row.disabled = false; }
      });
      el.securityNotifications.append(row);
    }
    if (!notices.length) el.securityNotifications.append(create("p", "utility-empty", "No security notices yet."));
  }

  async function refreshSecurity(force = false) {
    if (!state.viewer || state.security.loading || (state.security.loaded && !force)) {
      if (state.security.loaded) renderSecurity();
      return;
    }
    const securityState = state.security;
    const socialState = state.messages;
    const viewerId = state.viewer.id;
    const current = () => state.security === securityState && state.messages === socialState
      && state.viewer?.id === viewerId;
    state.security.loading = true;
    el.securityLoading.hidden = false;
    hideError(el.securityError);
    try {
      const [sessions, notifications, identities, trust] = await Promise.all([
        api("/api/security/sessions"), api("/api/security/notifications?limit=50"),
        api("/api/account/identities"),
        api("/api/security/trust"),
      ]);
      if (!current()) return;
      state.security.sessions = sessions.items || [];
      state.security.notifications = notifications.items || [];
      state.security.identities = identities.linked || [];
      state.security.trust = trust;
      state.federatedProviders = identities.providers || state.federatedProviders;
      state.security.loaded = true;
      renderSecurity();
    } catch (error) { if (current()) showError(el.securityError, error.message); }
    finally {
      if (current()) { state.security.loading = false; el.securityLoading.hidden = true; }
    }
  }

  async function reauthenticate(password) {
    return api("/api/security/reauthenticate", {
      method: "POST", csrf: true, json: { password },
    });
  }

  async function submitPasswordChange(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const errorNode = $(".form-error", form);
    const button = $("button[type='submit']", form);
    const values = new FormData(form);
    hideError(errorNode); button.disabled = true;
    try {
      const passwordEnabled = state.viewer?.password_login_enabled !== false;
      const currentPassword = passwordEnabled ? values.get("current_password") : null;
      if (passwordEnabled) await reauthenticate(currentPassword);
      const result = await api("/api/security/password", {
        method: "POST", csrf: true,
        json: { current_password: currentPassword, new_password: values.get("new_password") },
      });
      state.csrf = result.csrf_token;
      form.reset();
      state.security.loaded = false;
      await refreshAuth();
      await refreshSecurity(true);
      toast("Password changed. Other sessions were removed.");
    } catch (error) { showError(errorNode, error.message); }
    finally { button.disabled = false; }
  }

  async function submitEmailChange(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const errorNode = $(".form-error", form);
    const button = $("button[type='submit']", form);
    const values = new FormData(form);
    hideError(errorNode); button.disabled = true;
    try {
      const passwordEnabled = state.viewer?.password_login_enabled !== false;
      const currentPassword = passwordEnabled ? values.get("current_password") : null;
      if (passwordEnabled) await reauthenticate(currentPassword);
      const result = await api("/api/security/email", {
        method: "POST", csrf: true,
        json: { current_password: currentPassword, new_email: values.get("new_email") },
      });
      state.csrf = result.csrf_token;
      if (result.development_verification_token) el.verificationToken.value = result.development_verification_token;
      form.reset();
      state.security.loaded = false;
      await refreshAuth();
      setAccountSection("profile");
      toast("Email changed. Verify the new address.");
    } catch (error) { showError(errorNode, error.message); }
    finally { button.disabled = false; }
  }

  async function revokeOtherSessions() {
    const sessions = state.security.sessions.filter((item) => !item.current);
    if (!sessions.length) return;
    el.securityRevokeOthers.disabled = true;
    try {
      for (const session of sessions) {
        await api(`/api/security/sessions/${encodeURIComponent(session.id)}`, { method: "DELETE", csrf: true });
      }
      state.security.loaded = false;
      await refreshSecurity(true);
      toast(`${sessions.length} other ${sessions.length === 1 ? "session" : "sessions"} removed`);
    } catch (error) { toast(error.message, true); }
    finally { el.securityRevokeOthers.disabled = false; }
  }

  function purchaseState(item) {
    const label = utilityLabel(item.status || "pending");
    if (item.status === "canceled" && item.entitlement_ends_at) {
      return item.entitlement_active
        ? `Canceled · access until ${new Date(item.entitlement_ends_at).toLocaleDateString()}`
        : `Canceled · access ended ${new Date(item.entitlement_ends_at).toLocaleDateString()}`;
    }
    if (item.entitlement_ends_at && item.entitlement_active) {
      return `${label} · access until ${new Date(item.entitlement_ends_at).toLocaleDateString()}`;
    }
    return label;
  }

  function renderPurchases() {
    clear(el.purchasesContent);
    const header = create("header", "account-panel-header");
    header.append(create("div"));
    $("div", header).append(create("h3", "", "Purchases"), create("p", "", "Orders, access and recurring billing."));
    const refresh = create("button", "text-button", "Refresh"); refresh.type = "button";
    refresh.addEventListener("click", () => refreshPurchases(true)); header.append(refresh);
    el.purchasesContent.append(header);
    if (state.purchases.loading) {
      el.purchasesContent.append(create("p", "utility-empty", "Loading purchases…"));
      return;
    }
    const items = state.purchases.items || [];
    const list = create("div", "purchase-list");
    for (const item of items) {
      const row = create("article", "purchase-row");
      const copy = create("div", "purchase-copy");
      copy.append(
        create("strong", "", item.offer.title),
        create("span", "", `${formatMoney(item.amount_minor, item.currency)} · ${purchaseState(item)}`),
        create("time", "", new Date(item.created_at).toLocaleDateString()),
      );
      const actions = create("div", "purchase-actions");
      if (item.fulfillment_url) {
        const accessUrl = providerUrl(item.fulfillment_url);
        if (accessUrl) {
          const access = create("a", "publish-button", item.entitlement_active ? "Open" : "View");
          access.href = accessUrl; access.target = "_blank"; access.rel = "noopener noreferrer nofollow";
          actions.append(access);
        }
      }
      if (item.billing_portal_available) {
        const billing = create("button", "secondary-button", "Manage billing"); billing.type = "button";
        billing.addEventListener("click", async () => {
          billing.disabled = true;
          try {
            await openCommerceProvider(`/api/purchases/${encodeURIComponent(item.id)}/billing-portal`);
          } catch (error) { toast(error.message, true); billing.disabled = false; }
        });
        actions.append(billing);
      }
      row.append(copy, actions);
      list.append(row);
    }
    if (!items.length) list.append(create("p", "utility-empty", "No purchases yet."));
    el.purchasesContent.append(list);
  }

  async function refreshPurchases(force = false) {
    if (!state.viewer || state.purchases.loading || (state.purchases.loaded && !force)) {
      renderPurchases();
      return;
    }
    state.purchases.loading = true;
    const purchases = state.purchases;
    const viewerId = state.viewer.id;
    const current = () => state.purchases === purchases && state.viewer?.id === viewerId;
    renderPurchases();
    try {
      const result = await api("/api/purchases");
      if (!current()) return;
      state.purchases.items = result.items || [];
      state.purchases.loaded = true;
    } catch (error) { if (current()) toast(error.message, true); }
    finally { if (current()) { purchases.loading = false; renderPurchases(); } }
  }

  async function manageAccountBilling(action, button) {
    const routes = {
      "premium-portal": "/api/premium/billing-portal",
      "premium-cancel": "/api/premium/cancel",
      "creator-portal": "/api/creator/studio/billing-portal",
    };
    if (!state.viewer || !routes[action] || button.disabled) return;
    if (action === "premium-cancel" && !(await confirm("Cancel your existing Premium subscription?", { confirmLabel: "Cancel subscription", cancelLabel: "Keep Premium", destructive: true }))) return;
    const viewerId = state.viewer.id;
    const identity = state.messages;
    const current = () => state.viewer?.id === viewerId && state.messages === identity;
    button.disabled = true;
    hideError(accountErrorSlot());
    try {
      const result = await api(routes[action], { method: "POST", csrf: true });
      if (!current()) return;
      if (action === "premium-cancel") {
        if (result.status !== "canceled") throw new Error("Cancellation was not confirmed. Check your billing status before retrying.");
        toast("Premium cancellation confirmed");
      }
      else navigateProvider(result.url || result.portal_url);
    } catch (error) { if (current()) showError(accountErrorSlot(), error.message); }
    finally { button.disabled = false; }
  }

  async function openCommerceProvider(path, field = "url") {
    const viewerId = state.viewer?.id;
    const social = state.messages;
    if (!viewerId) return;
    const result = await api(path, { method: "POST", csrf: true });
    if (state.viewer?.id !== viewerId || state.messages !== social) return;
    navigateProvider(result[field]);
  }

  function creatorSummary(summary) {
    const section = create("section", "creator-summary");
    section.append(create("h4", "", "Initial checkouts"));
    if (!summary?.by_currency?.length) {
      section.append(create("p", "utility-empty", "No completed initial checkouts yet."));
      return section;
    }
    const rows = create("div", "creator-summary-rows");
    for (const item of summary.by_currency) {
      const row = create("div", "creator-summary-row");
      row.append(
        create("strong", "", item.currency.toUpperCase()),
        create("span", "", `${formatCount(item.orders)} orders`),
        create("span", "", `${formatMoney(item.gross_initial_checkout_minor, item.currency)} gross`),
        create("span", "", `${formatMoney(item.refunded_initial_checkout_minor, item.currency)} refunded`),
        create("span", "", `${formatMoney(item.net_initial_checkout_minor, item.currency)} net`),
        create("span", "", `${formatMoney(item.recorded_initial_platform_fee_minor, item.currency)} recorded fee`),
      );
      rows.append(row);
    }
    section.append(rows, create("p", "metric-note", "Initial Checkout figures only; they are not provider payout settlement or recurring lifetime revenue."));
    return section;
  }

  function creatorOfferRow(offer) {
    const row = create("article", "creator-item-row");
    const copy = create("div", "creator-item-copy");
    const inventory = offer.inventory_remaining === null ? "No inventory limit" : `${formatCount(offer.inventory_remaining)} left`;
    copy.append(
      create("strong", "", offer.title),
      create("span", "", `${formatMoney(offer.price_minor, offer.currency)}${offer.billing_interval === "one_time" ? "" : ` / ${offer.billing_interval}`} · ${offer.status} · ${inventory}`),
    );
    const actions = create("div", "creator-item-actions");
    const edit = create("button", "secondary-button", "Edit"); edit.type = "button";
    edit.addEventListener("click", () => openOfferDialog(offer));
    const remove = create("button", "text-button", "Delete"); remove.type = "button";
    remove.addEventListener("click", async () => {
      if (!(await confirm(`Delete “${offer.title}”?`))) return;
      remove.disabled = true;
      try {
        await api(`/api/creator/offers/${encodeURIComponent(offer.id)}`, { method: "DELETE", csrf: true });
        state.creator.loaded = false; await refreshCreator(true); toast("Offer deleted");
      } catch (error) { toast(error.message, true); remove.disabled = false; }
    });
    actions.append(edit, remove); row.append(copy, actions); return row;
  }

  function creatorCampaignRow(campaign) {
    const row = create("article", "creator-item-row");
    const copy = create("div", "creator-item-copy");
    const status = campaign.status === "pending_review" ? "Pending review" : campaign.status.replaceAll("_", " ");
    copy.append(
      create("strong", "", campaign.name),
      create("span", "", `${status} · ${formatCount(campaign.slots_used)} / ${formatCount(campaign.slot_budget)} slots`),
    );
    if (campaign.review_note) copy.append(create("small", "", campaign.review_note));
    const actions = create("div", "creator-item-actions");
    if (campaign.status === "approved") {
      const pay = create("button", "publish-button", "Pay"); pay.type = "button";
      pay.addEventListener("click", async () => {
        pay.disabled = true;
        try { await openCommerceProvider(`/api/sponsors/campaigns/${encodeURIComponent(campaign.id)}/checkout`, "checkout_url"); }
        catch (error) { toast(error.message, true); pay.disabled = false; }
      });
      actions.append(pay);
    }
    row.append(copy, actions); return row;
  }

  function renderCreator() {
    clear(el.creatorContent);
    const header = create("header", "account-panel-header");
    const title = create("div"); title.append(create("h3", "", "Creator"), create("p", "", "Offers, payouts and promotion."));
    const refresh = create("button", "text-button", "Refresh"); refresh.type = "button";
    refresh.addEventListener("click", () => refreshCreator(true));
    header.append(title, refresh); el.creatorContent.append(header);
    if (state.creator.loading) {
      el.creatorContent.append(create("p", "utility-empty", "Loading creator tools…"));
      return;
    }
    const status = state.creator.status;
    const dashboard = state.creator.dashboard;
    if (!status || !dashboard) {
      el.creatorContent.append(create("p", "utility-empty", "Creator tools are unavailable right now."));
      return;
    }
    const account = dashboard.account;
    const setup = create("section", "creator-setup");
    const studioRow = create("div", "creator-setup-row");
    const studioCopy = create("div", "creator-item-copy");
    const studioActive = ["active", "trialing"].includes(account.plan_status);
    const studioInterval = status.creator_studio.interval || "month";
    studioCopy.append(create("strong", "", "Creator Pro"), create("span", "", studioActive ? `Active${account.current_period_end ? ` · renews ${new Date(account.current_period_end).toLocaleDateString()}` : ""}` : `${formatMoney(status.creator_studio.display_price_minor, status.creator_studio.currency)} / ${studioInterval}`));
    const studioAction = create("button", "secondary-button", studioActive ? "Manage" : "Start"); studioAction.type = "button";
    studioAction.disabled = studioActive ? false : !status.creator_studio.available;
    studioAction.addEventListener("click", async () => {
      studioAction.disabled = true;
      try { await openCommerceProvider(studioActive ? "/api/creator/studio/billing-portal" : "/api/creator/studio/checkout", studioActive ? "url" : "checkout_url"); }
      catch (error) { toast(error.message, true); studioAction.disabled = false; }
    });
    studioRow.append(studioCopy, studioAction);
    const payoutRow = create("div", "creator-setup-row");
    const payoutCopy = create("div", "creator-item-copy");
    payoutCopy.append(create("strong", "", "Payouts"), create("span", "", account.connect.payouts_enabled ? "Ready" : account.connect.details_submitted ? "Submitted" : "Setup required"));
    const payoutAction = create("button", "secondary-button", account.connect.details_submitted ? "Dashboard" : "Set up"); payoutAction.type = "button";
    payoutAction.disabled = !status.creator_sales.available;
    payoutAction.addEventListener("click", async () => {
      payoutAction.disabled = true;
      try { await openCommerceProvider(account.connect.details_submitted ? "/api/creator/connect/dashboard-link" : "/api/creator/connect/onboarding-link"); }
      catch (error) { toast(error.message, true); payoutAction.disabled = false; }
    });
    payoutRow.append(payoutCopy, payoutAction); setup.append(studioRow, payoutRow); el.creatorContent.append(setup);
    const feeBps = Number(status.creator_sales?.platform_fee_bps);
    if (Number.isFinite(feeBps) && feeBps >= 0) {
      const percent = (value) => (value % 100 ? (value / 100).toFixed(1) : String(value / 100));
      const schedule = status.creator_sales?.payout_schedule?.interval || "monthly";
      el.creatorContent.append(create("p", "metric-note creator-keep-note", `You keep ${percent(10000 - feeBps)}% of every sale and membership; LOUDERR keeps ${percent(feeBps)}%. Payouts are ${schedule}. Your followers list is yours to export at any time from Account.`));
    }
    el.creatorContent.append(creatorSummary(dashboard.checkout_summary));

    const offers = create("section", "creator-collection");
    const offersHeader = create("header"); offersHeader.append(create("h4", "", "Offers"));
    const addOffer = create("button", "publish-button", "New offer"); addOffer.type = "button";
    addOffer.addEventListener("click", () => openOfferDialog()); offersHeader.append(addOffer); offers.append(offersHeader);
    for (const offer of dashboard.offers || []) offers.append(creatorOfferRow(offer));
    if (!dashboard.offers?.length) offers.append(create("p", "utility-empty", "No offers yet."));
    el.creatorContent.append(offers);

    const campaigns = create("section", "creator-collection");
    const campaignsHeader = create("header"); campaignsHeader.append(create("h4", "", "Promotion"));
    const promote = create("button", "secondary-button", "Promote"); promote.type = "button";
    promote.disabled = !status.sponsorship.available;
    promote.addEventListener("click", openSponsorDialog); campaignsHeader.append(promote); campaigns.append(campaignsHeader);
    for (const campaign of dashboard.campaigns || []) campaigns.append(creatorCampaignRow(campaign));
    if (!dashboard.campaigns?.length) campaigns.append(create("p", "utility-empty", "No campaigns yet."));
    el.creatorContent.append(campaigns);
  }

  async function refreshCreator(force = false) {
    if (!state.viewer || state.creator.loading || (state.creator.loaded && !force)) {
      renderCreator();
      return;
    }
    const creator = state.creator;
    const social = state.messages;
    const viewerId = state.viewer.id;
    const current = () => state.creator === creator && state.messages === social
      && state.viewer?.id === viewerId;
    state.creator.loading = true; renderCreator();
    try {
      const [status, dashboard] = await Promise.all([api("/api/commerce/status"), api("/api/creator/dashboard")]);
      if (!current()) return;
      state.creator.status = status; state.creator.dashboard = dashboard; state.creator.loaded = true;
    } catch (error) { if (current()) toast(error.message, true); }
    finally { if (current()) { state.creator.loading = false; renderCreator(); } }
  }

  function dialogField(label, control, hint = "") {
    const wrapper = create("label", "field");
    wrapper.append(create("span", "", label), control);
    if (hint) wrapper.append(create("small", "", hint));
    return wrapper;
  }

  async function openOfferDialog(existing = null) {
    if (!requireVerified()) return;
    const dialog = buildUtilityDialog(existing ? "Edit offer" : "New offer");
    const form = create("form", "utility-form offer-form");
    const title = create("input"); title.required = true; title.maxLength = 100; title.value = existing?.title || "";
    const description = create("textarea"); description.maxLength = 280; description.rows = 3; description.value = existing?.description || "";
    const type = create("select");
    [["support", "Support"], ["digital", "Digital"], ["membership", "Membership"], ["ticket", "Ticket"], ["commission", "Commission"], ["access", "Access"]].forEach(([value, label]) => { const option = create("option", "", label); option.value = value; option.selected = existing?.type === value; type.append(option); });
    type.disabled = Boolean(existing);
    const price = create("input"); price.type = "number"; price.required = true; price.min = "1"; price.step = "0.01"; price.value = existing ? String(existing.price_minor / 100) : "5.00";
    const currency = create("select");
    ["eur", "usd", "nok", "gbp", "sek", "dkk"].forEach((value) => { const option = create("option", "", value.toUpperCase()); option.value = value; option.selected = (existing?.currency || "eur") === value; currency.append(option); });
    const billing = create("select");
    [["one_time", "One time"], ["month", "Monthly"], ["year", "Yearly"]].forEach(([value, label]) => { const option = create("option", "", label); option.value = value; option.selected = (existing?.billing_interval || "one_time") === value; billing.append(option); });
    const status = create("select");
    [["draft", "Draft"], ["paused", "Paused"], ["active", "Published"]].forEach(([value, label]) => { const option = create("option", "", label); option.value = value; option.selected = (existing?.status || "draft") === value; option.disabled = value === "active" && !state.creator.dashboard?.account?.can_sell && existing?.status !== "active"; status.append(option); });
    const inventory = create("input"); inventory.type = "number"; inventory.min = "1"; inventory.max = "1000000"; inventory.placeholder = "No limit"; inventory.value = existing?.inventory_total || "";
    const fulfillment = create("input"); fulfillment.type = "url"; fulfillment.placeholder = "https://"; fulfillment.value = "";
    const dropSelect = create("select");
    const noDrop = create("option", "", "No linked Drop"); noDrop.value = ""; dropSelect.append(noDrop);
    dropSelect.disabled = Boolean(existing);
    const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
    const submit = create("button", "publish-button", existing ? "Save" : "Create"); submit.type = "submit";
    form.append(
      dialogField("Title", title), dialogField("Description", description), dialogField("Type", type),
      dialogField("Price", price), dialogField("Currency", currency), dialogField("Billing", billing),
      dialogField("Status", status), dialogField("Inventory · optional", inventory),
      dialogField("Fulfillment link · required for delivered access", fulfillment), dialogField("Linked Drop · optional", dropSelect),
      error, submit,
    );
    dialog.append(form); openDialog(dialog);
    try {
      const drops = await api(`/api/drops?author_id=${encodeURIComponent(state.viewer.id)}`);
      for (const drop of drops.items || []) {
        if (drop.structured_type) continue;
        const option = create("option", "", dropLabel(drop)); option.value = drop.id; option.selected = existing?.drop_id === drop.id; dropSelect.append(option);
      }
    } catch (_) { /* linking remains optional */ }
    const syncOfferFields = () => {
      const membership = type.value === "membership";
      if (membership && billing.value === "one_time") billing.value = "month";
      if (!membership) billing.value = "one_time";
      $$('option', billing).forEach((option) => { option.disabled = membership ? option.value === "one_time" : option.value !== "one_time"; });
      const needsFulfillment = ["digital", "membership", "ticket", "access"].includes(type.value)
        && status.value === "active" && !existing?.fulfillment_configured;
      fulfillment.required = needsFulfillment;
    };
    type.addEventListener("change", syncOfferFields); status.addEventListener("change", syncOfferFields); syncOfferFields();
    form.addEventListener("submit", async (event) => {
      event.preventDefault(); hideError(error); submit.disabled = true;
      const fulfillmentUrl = fulfillment.value.trim();
      if (fulfillmentUrl && !providerUrl(fulfillmentUrl)) {
        showError(error, "Fulfillment links must use HTTPS."); submit.disabled = false; return;
      }
      const common = {
        title: title.value.trim(), description: description.value.trim(),
        price_minor: Math.round(Number(price.value) * 100), currency: currency.value,
        billing_interval: billing.value, status: status.value,
        inventory_total: inventory.value ? Number(inventory.value) : null,
        fulfillment_url: fulfillmentUrl ? providerUrl(fulfillmentUrl) : null,
      };
      try {
        const payload = existing ? { ...common } : { ...common, offer_type: type.value, drop_id: dropSelect.value || null };
        if (existing?.fulfillment_configured && !fulfillment.value.trim()) delete payload.fulfillment_url;
        await api(existing ? `/api/creator/offers/${encodeURIComponent(existing.id)}` : "/api/creator/offers", {
          method: existing ? "PATCH" : "POST", csrf: true,
          json: payload,
        });
        dialog.close(); state.creator.loaded = false; await refreshCreator(true); toast(existing ? "Offer updated" : "Offer created");
      } catch (requestError) { showError(error, requestError.message); submit.disabled = false; }
    });
    requestAnimationFrame(() => title.focus());
  }

  async function openSponsorDialog() {
    if (!requireVerified()) return;
    const dialog = buildUtilityDialog("Promote a Drop");
    const form = create("form", "utility-form sponsor-form");
    const drop = create("select"); drop.required = true;
    const name = create("input"); name.required = true; name.maxLength = 100;
    const ctaLabel = create("input"); ctaLabel.required = true; ctaLabel.maxLength = 40; ctaLabel.value = "Learn more";
    const ctaUrl = create("input"); ctaUrl.type = "url"; ctaUrl.required = true; ctaUrl.placeholder = "https://";
    const starts = create("input"); starts.type = "datetime-local"; starts.required = true;
    const ends = create("input"); ends.type = "datetime-local"; ends.required = true;
    const slots = create("input"); slots.type = "number"; slots.required = true; slots.min = "1"; slots.max = "100"; slots.value = "1";
    const toLocalInput = (date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    starts.value = toLocalInput(new Date(Date.now() + 60 * 60 * 1000));
    ends.value = toLocalInput(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
    const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
    const submit = create("button", "publish-button", "Submit for review"); submit.type = "submit";
    const sponsorStatus = state.creator.status?.sponsorship;
    const sponsorTerms = sponsorStatus
      ? `Sponsored · ${formatMoney(sponsorStatus.price_per_slot_minor, sponsorStatus.currency)} per Board slot · at most ${String(sponsorStatus.maximum_frequency).replace("/", " in ")} turns. Kept outside organic ranking.`
      : "Sponsored placement is reviewed and kept outside organic ranking.";
    form.append(
      create("p", "utility-copy", sponsorTerms),
      dialogField("Drop", drop), dialogField("Campaign name", name), dialogField("Button label", ctaLabel),
      dialogField("Destination", ctaUrl), dialogField("Starts", starts), dialogField("Ends", ends),
      dialogField("Board slots", slots), error, submit,
    );
    dialog.append(form); openDialog(dialog);
    try {
      const drops = await api(`/api/drops?author_id=${encodeURIComponent(state.viewer.id)}`);
      for (const item of drops.items || []) {
        if (item.structured_type) continue;
        const option = create("option", "", item.title); option.value = item.id; drop.append(option);
      }
      if (!drop.childElementCount) {
        submit.disabled = true;
        showError(error, "No eligible owned Drop is available for promotion.");
      }
    } catch (requestError) { submit.disabled = true; showError(error, requestError.message); }
    form.addEventListener("submit", async (event) => {
      event.preventDefault(); hideError(error); submit.disabled = true;
      const destination = providerUrl(ctaUrl.value.trim());
      if (!destination) { showError(error, "Sponsor destinations must use HTTPS."); submit.disabled = false; return; }
      try {
        await api("/api/sponsors/campaigns", {
          method: "POST", csrf: true,
          json: {
            creative_drop_id: drop.value, name: name.value.trim(), cta_label: ctaLabel.value.trim(),
            cta_url: destination, starts_at: new Date(starts.value).toISOString(),
            ends_at: new Date(ends.value).toISOString(), slot_budget: Number(slots.value),
          },
        });
        dialog.close(); state.creator.loaded = false; await refreshCreator(true); toast("Campaign submitted for review");
      } catch (requestError) { showError(error, requestError.message); submit.disabled = false; }
    });
  }

  async function buyOffer(offer, button) {
    if (!state.viewer) {
      sessionStorage.setItem("lobby.pending.offer", offer.id);
      renderAccount(); openDialog(el.account); toast("Sign in, then select Buy again to continue");
      return;
    }
    if (!requireVerified()) return;
    button.disabled = true;
    try {
      await openCommerceProvider(`/api/offers/${encodeURIComponent(offer.id)}/checkout`, "checkout_url");
    } catch (error) { toast(error.message, true); button.disabled = false; }
  }

  function renderProfileCommerce() {
    clear(el.profileOfferList);
    const offers = state.profile.offers || [];
    el.profileOffers.hidden = offers.length === 0;
    for (const offer of offers) {
      const row = create("article", "profile-offer-row");
      const copy = create("div", "profile-offer-copy");
      copy.append(
        create("strong", "", offer.title),
        create("span", "", offer.description || offer.type.replaceAll("_", " ")),
        create("b", "", `${formatMoney(offer.price_minor, offer.currency)}${offer.billing_interval === "one_time" ? "" : ` / ${offer.billing_interval}`}`),
      );
      const action = offer.type === "support" ? "Support" : offer.type === "membership" ? "Join" : "Buy";
      const buy = create("button", "publish-button", offer.available ? action : "Sold out"); buy.type = "button";
      const own = state.viewer?.id === offer.creator_id;
      if (own) { buy.textContent = "Your offer"; buy.disabled = true; }
      else if (!offer.available) buy.disabled = true;
      else buy.addEventListener("click", () => buyOffer(offer, buy));
      row.append(copy, buy); el.profileOfferList.append(row);
    }
    const feeBps = state.profile.offersFeeBps;
    if (offers.length && Number.isFinite(feeBps)) {
      const percent = (value) => (value % 100 ? (value / 100).toFixed(1) : String(value / 100));
      el.profileOfferList.append(create("p", "metric-note", `${percent(10000 - feeBps)}% of what you pay goes to the creator. LOUDERR keeps ${percent(feeBps)}%.`));
    }
  }

  function renderEnforcement() {
    const enforcement = state.enforcement;
    const viewerId = state.viewer?.id;
    const items = enforcement.items;
    el.enforcementPanel.hidden = !state.viewer || items.length === 0;
    if (!viewerId) { clear(el.enforcementList); return; }
    const cards = enforcement.cards ||= new Map();
    const desired = [];
    for (const action of items) {
      const fingerprint = JSON.stringify(action);
      const existing = cards.get(action.action_id);
      if (existing?.fingerprint === fingerprint) { desired.push(existing.card); continue; }
      const card = create("article", "enforcement-card");
      card.dataset.moderationAction = action.action_id;
      card.tabIndex = -1;
      const actionLabels = {
        warn: "Account warning", suspend: "Account suspended", ban: "Account banned",
        remove_drop: "Drop removed", remove_chat_message: "Chat message or comment removed",
        remove_direct_message: "Private message removed", restore_drop: "Drop restored",
        restore_chat_message: "Chat message or comment restored", restore_direct_message: "Private message restored",
      };
      const title = create("strong", "", actionLabels[action.action] || "Moderation decision");
      const reference = create("details", "enforcement-reference");
      reference.append(create("summary", "", "Decision reference"), create("code", "", action.action_id));
      const reason = create("p", "", action.reason);
      const timing = create("small", "", `${new Date(action.starts_at).toLocaleString()}${action.ends_at ? ` · ends ${new Date(action.ends_at).toLocaleString()}` : ""}`);
      card.append(title, timing, reason);
      if (action.reverted_at) card.append(create("p", "enforcement-state", `Decision reversed · ${new Date(action.reverted_at).toLocaleString()}`));
      if (action.appeal) {
        const appeal = action.appeal;
        const appealLabels = { pending: "Appeal awaiting review", upheld: "Appeal reviewed: decision unchanged", overturned: "Appeal reviewed: decision reversed" };
        card.append(create("p", "enforcement-state", `${appealLabels[appeal.status] || "Appeal status unavailable"}${appeal.resolution ? ` · ${appeal.resolution}` : ""}`));
      } else if (action.appealable) {
        const form = create("form", "appeal-form");
        const label = create("label", "field");
        label.append(create("span", "", "Appeal statement"));
        const statement = create("textarea");
        statement.required = true; statement.minLength = 10; statement.maxLength = 1000; statement.rows = 3;
        statement.placeholder = "Explain what should be reconsidered.";
        label.append(statement);
        const submit = create("button", "secondary-button", "Submit appeal"); submit.type = "submit";
        form.append(label, submit);
        const accountCurrent = () => state.viewer?.id === viewerId && state.enforcement === enforcement;
        const current = () => accountCurrent() && cards.get(action.action_id)?.card === card && card.isConnected;
        form.addEventListener("submit", async (event) => {
          event.preventDefault();
          if (!current() || submit.disabled) return;
          submit.disabled = true; statement.readOnly = true;
          try {
            const result = await api("/api/appeals", {
              method: "POST", csrf: true,
              json: { moderation_action_id: action.action_id, statement: statement.value },
            });
            if (!current()) return;
            if (result?.status !== "pending" || typeof result.id !== "string" || !result.id) {
              throw new Error("Could not confirm the appeal. Refresh your account before trying again.");
            }
            const refreshed = await refreshEnforcement();
            if (accountCurrent()) toast(refreshed ? "Appeal submitted" : "Appeal submitted. Reopen your account to refresh its status.");
          } catch (error) {
            if (current()) { toast(error.message, true); submit.disabled = false; statement.readOnly = false; }
          }
        });
        card.append(form);
      }
      card.append(reference);
      cards.set(action.action_id, { fingerprint, card });
      desired.push(card);
    }
    const desiredCards = new Set(desired);
    for (const child of Array.from(el.enforcementList.children)) {
      if (!desiredCards.has(child)) child.remove();
    }
    for (const [id, entry] of cards) {
      if (!desiredCards.has(entry.card)) cards.delete(id);
    }
    let anchor = el.enforcementList.firstElementChild;
    for (const card of desired) {
      if (card === anchor) anchor = anchor.nextElementSibling;
      else el.enforcementList.insertBefore(card, anchor);
    }
  }

  async function refreshEnforcement(quiet = true) {
    if (!state.viewer) {
      clearTimeout(state.enforcement.timer);
      state.enforcement = { items: [], loaded: false, timer: null };
      renderEnforcement();
      return;
    }
    const viewerId = state.viewer.id;
    const enforcement = state.enforcement;
    const requestId = enforcement.requestId = (enforcement.requestId || 0) + 1;
    const current = () => state.viewer?.id === viewerId && state.enforcement === enforcement
      && enforcement.requestId === requestId;
    try {
      const result = await api("/api/appeals/actions");
      if (!current()) return;
      state.enforcement.items = result.items;
      state.enforcement.loaded = true;
      renderEnforcement();
      return true;
    } catch (error) {
      if (current() && !quiet) toast(error.message, true);
    }
  }

  async function refreshAuth({ syncRealtime = true, signal } = {}) {
    const previousViewer = state.viewer;
    const wasRestricted = state.sessionRestricted;
    const previousViewerId = previousViewer?.id || null;
    const socialState = state.messages;
    const result = await api("/api/auth/me", { signal });
    if (state.messages !== socialState || state.viewer !== previousViewer
        || (state.viewer?.id || null) !== previousViewerId) return;
    if (previousViewerId && !result.user) {
      // The server no longer has this tab's session (it expired, was revoked, or another tab signed out).
      // Sign the tab out as a logout does, once, whichever request noticed first. A sign-out this tab asked
      // for (state.endingSession) does that itself, with its own message, when its reply arrives.
      if (state.endingSession) return result;
      state.federatedProviders = result.federated_providers || [];
      finishLocalLogout();
      toast("Your session expired. Sign in again.", true);
      if (el.account && !el.account.open) el.account.showModal();
      return result;
    }
    // Keep the age step and its status retry visible until the server has
    // supplied all admission flags. Missing fields are not an approval.
    if (state.ageAttestationRequired && result.user
        && ["age_attestation_required", "age_requirement_not_met", "session_restricted"]
          .some(key => typeof result[key] !== "boolean")) {
      throw new Error("Account status was not confirmed.");
    }
    state.viewer = result.user;
    state.csrf = result.csrf_token;
    state.sessionRestricted = Boolean(result.session_restricted);
    state.ageAttestationRequired = Boolean(result.age_attestation_required);
    state.ageRequirementNotMet = Boolean(result.age_requirement_not_met);
    state.minimumAge = Number(result.minimum_age) || 16;
    state.federatedProviders = result.federated_providers || [];
    if (previousViewerId !== (state.viewer?.id || null)) resetSocialState();
    renderAccount();
    // Signing in on Home: the story tray is members-only, so fetch it now instead of at the next navigation.
    if (state.view === "all" && typeof refreshHomeStories === "function") refreshHomeStories();
    if (syncRealtime) syncRealtimeWithAuth();
    if (wasRestricted && !state.sessionRestricted) schedulePrivatePolling(true);
    return result;
  }

  function applyAuthResponse(result) {
    const previousViewerId = state.viewer?.id || null;
    state.viewer = result.user;
    state.csrf = result.csrf_token;
    state.sessionRestricted = Boolean(result.session_restricted);
    state.ageAttestationRequired = Boolean(result.age_attestation_required);
    state.ageRequirementNotMet = Boolean(result.age_requirement_not_met);
    state.minimumAge = Number(result.minimum_age) || 16;
    if (result.federated_providers) state.federatedProviders = result.federated_providers;
    state.relationships = { items: [], loaded: false, loading: false };
    state.world.micEntry = null;
    state.security = { sessions: [], notifications: [], loaded: false };
    state.purchases = { items: [], loaded: false };
    state.creator = { status: null, dashboard: null, loaded: false };
    state.accountSection = "profile";
    if (previousViewerId !== (state.viewer?.id || null)) resetSocialState();
    renderAccount();
    // Signing in on Home: the story tray is members-only, so fetch it now instead of at the next navigation.
    if (state.view === "all" && typeof refreshHomeStories === "function") refreshHomeStories();
    if (result.development_verification_token) {
      el.verificationToken.value = result.development_verification_token;
    }
  }

  function setConnection(mode, label) {
    el.status.classList.toggle("is-live", mode === "live");
    el.status.classList.toggle("is-offline", mode === "offline");
    el.connection.textContent = label;
  }

  function relativeTime(value, now = Date.now()) {
    const seconds = Math.round((new Date(value).getTime() - now) / 1000);
    const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    if (Math.abs(seconds) < 60) return formatter.format(seconds, "second");
    const minutes = Math.round(seconds / 60);
    if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
    const hours = Math.round(minutes / 60);
    if (Math.abs(hours) < 24) return formatter.format(hours, "hour");
    return formatter.format(Math.round(hours / 24), "day");
  }

  function compactTime(value) {
    const then = new Date(value).getTime();
    if (!Number.isFinite(then)) return "";
    const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (seconds < 60) return "now";
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days}d`;
    return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(value));
  }

  function formatCount(value) {
    return new Intl.NumberFormat(undefined, { notation: value >= 10000 ? "compact" : "standard" }).format(value || 0);
  }

  function formatMoney(minor, currency) {
    if (!Number.isFinite(Number(minor)) || !currency) return "Not available";
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: String(currency).toUpperCase(),
    }).format(Number(minor) / 100);
  }

  function appendBounded(items, item, limit) {
    const existing = items.findIndex((candidate) => candidate.id === item.id);
    if (existing >= 0) items[existing] = item;
    else items.push(item);
    if (items.length > limit) items.splice(0, items.length - limit);
  }

  function appendFlowChat(message) {
    appendBounded(state.flow.chatItems, message, 60);
  }

  function providerUrl(value) {
    try {
      const url = new URL(value);
      return url.protocol === "https:" ? url.href : null;
    } catch (_) { return null; }
  }

  function navigateProvider(value) {
    const url = providerUrl(value);
    if (!url) throw new Error("The payment provider returned an unsafe destination.");
    location.assign(url);
  }

  function upsertDrop(drop, prepend = true, insert = true) {
    state.feedWindows?.patch(drop.id, drop);
    const index = state.drops.findIndex((item) => item.id === drop.id);
    if (index >= 0) state.drops[index] = drop;
    else if (insert && state.view !== "drop") {
      if (prepend) state.drops.unshift(drop);
      else state.drops.push(drop);
    }
    const flowIndex = state.flow.items.findIndex((item) => item.id === drop.id);
    if (flowIndex >= 0) state.flow.items[flowIndex] = { ...state.flow.items[flowIndex], ...drop };
    if (state.flow.now?.current?.drop?.id === drop.id) {
      state.flow.now.current.drop = { ...state.flow.now.current.drop, ...drop };
    }
    state.flow.stories = state.flow.stories.map((story) => story.drop?.id === drop.id
      ? { ...story, drop: { ...story.drop, ...drop } }
      : story);
  }

  function paintFollowControl(button, author, fallbackFollowing = false, mode = "text") {
    if (!button || !author?.id) return;
    // The profile page reuses this button while navigating between people.
    // An operation for the old author must not reclaim it after retargeting.
    if (button._followAuthor?.id !== author.id) delete button._followOperation;
    button._followAuthor = author;
    button._followFallback = fallbackFollowing;
    button._followMode = mode;
    button.dataset.followAuthor = author.id;
    const value = state.followState.get(author, fallbackFollowing);
    const label = value.uncertain ? "Check status" : value.requested ? "Requested"
      : value.following ? "Following" : "Follow";
    const action = value.uncertain ? "Check follow status for" : value.requested ? "Cancel follow request to"
      : value.following ? "Unfollow" : "Follow";
    button.title = value.requested ? "Requested · Cancel request" : label;
    button.setAttribute("aria-label", `${action} ${author.display_name || author.handle || "this account"}`);
    if (mode === "profile") {
      button.className = value.following || value.requested ? "secondary-button" : "publish-button";
    }
    button.classList.toggle("is-following", Boolean(value.following));
    button.classList.toggle("is-requested", Boolean(value.requested));
    button.disabled = false;
    if (value.busy) {
      button.setAttribute("aria-busy", "true");
      button.setAttribute("aria-disabled", "true");
    } else {
      button.removeAttribute("aria-busy");
      button.removeAttribute("aria-disabled");
    }
    if (mode === "icon") {
      button.hidden = value.following && !value.busy && !value.uncertain;
      let caption = button.querySelector(".follow-request-label");
      if (!caption) { caption = create("span", "follow-request-label"); button.append(caption); }
      caption.textContent = label;
      caption.hidden = !value.requested && !value.uncertain;
    } else button.textContent = label;
  }

  function paintProfileFollowState() {
    const profile = state.profile.data;
    if (!state.followState || !profile?.user) return;
    const value = state.followState.get(profile.user, Boolean(profile.relationship?.is_following));
    paintFollowControl(el.profileFollow, profile.user, Boolean(profile.relationship?.is_following), "profile");
    if (profile.relationship?.blocking || profile.relationship?.blocked || profile.relationship?.is_self
        || profile.user.id === state.viewer?.id || state.profile.drops.length
        || state.profile.postsPending || state.profile.postsError) return;
    if (value.uncertain || !value.visibility || !value.access) {
      // Knowing the relationship does not confirm access or prove that no posts exist.
      $("h3", el.profileDropsEmpty).textContent = "No posts to show";
      $("p", el.profileDropsEmpty).textContent = value.uncertain
        ? "Follow status could not be confirmed. Use Check status to try again."
        : "No posts are visible to you here right now.";
    } else if (value.visibility === "private" && ["closed", "requested"].includes(value.access)) {
      $("h3", el.profileDropsEmpty).textContent = "This account is private";
      $("p", el.profileDropsEmpty).textContent = value.requested
        ? "Your follow request is waiting for their approval. You can cancel it with Requested."
        : "Follow this account to request access to their posts.";
    } else {
      // A later access reply may retire a previously private/unknown explanation.
      $("h3", el.profileDropsEmpty).textContent = "No posts to show";
      $("p", el.profileDropsEmpty).textContent = "There are no posts available here right now.";
    }
  }

  function receiveFollowState(author, value, reason) {
    if (typeof value.following === "boolean") setAuthorFollowState(author.id, value.following);
    for (const button of $$("[data-follow-author]")) {
      if (button.dataset.followAuthor === author.id && button._followAuthor) {
        paintFollowControl(button, button._followAuthor, button._followFallback, button._followMode);
      }
    }
    if (["current", "reconciled"].includes(reason) && value.known && !value.busy && !value.uncertain) {
      // Private access can end on unfollow; never restore a parked old-access copy.
      if (!value.following && value.visibility !== "public") state.feedWindows?.invalidate({ erase: true });
      else state.feedWindows?.invalidate({ view: "following", erase: !value.following });
    }
    state.connectionsPanel?.repaintFollow(author, reason);
    if (state.view === "profile" && state.profile.data?.user?.id === author.id) {
      paintProfileFollowState();
      if (["current", "reconciled"].includes(reason)) {
        // A fresh owned read retires older profile/post reads and refreshes access/counts.
        void refreshProfile(state.profile.data.user.handle);
      }
    }
  }

  function openFollowRequests() {
    if (!requireVerified()) return;
    const target = "/notifications?follow_requests=1";
    if (`${location.pathname}${location.search}` !== target) {
      history.pushState({ louderrFollowRequests: true }, "", target);
    }
    void routeLocation();
  }

  function dismissFollowRequests(reason) {
    if (reason !== "dismiss" || location.pathname !== "/notifications"
        || new URLSearchParams(location.search).get("follow_requests") !== "1") return;
    if (history.state?.louderrFollowRequests) history.back();
    else {
      history.replaceState({}, "", "/notifications");
      void routeLocation();
    }
  }

  function setAuthorFollowState(authorId, following) {
    const update = (drop) => drop?.author_id === authorId
      ? { ...drop, viewer_follows_author: following }
      : drop;
    const cached = [state.feedWindows?.active?.items || [], state.feedWindows?.parked?.items || []];
    for (const id of new Set(cached.flat().filter(drop => drop.author_id === authorId).map(drop => drop.id))) {
      state.feedWindows.patch(id, { viewer_follows_author: following });
    }
    for (const key of ["drops"]) state[key] = state[key].map(update);
    state.search.drops = state.search.drops.map(update);
    state.discovery.items = state.discovery.items.map(update);
    state.profile.drops = state.profile.drops.map(update);
    state.flow.items = state.flow.items.map(update);
    if (state.flow.now?.current?.drop) state.flow.now.current.drop = update(state.flow.now.current.drop);
    state.flow.stories = state.flow.stories.map((story) => ({ ...story, drop: update(story.drop) }));
    if (state.board?.drop) state.board.drop = update(state.board.drop);
    if (state.comments.drop) state.comments.drop = update(state.comments.drop);
  }

  async function toggleDropAuthorFollow(drop, source, context = {}) {
    if (context.current && !context.current()) return;
    if (source.getAttribute("aria-busy") === "true") return;
    if (!state.capabilities.community_writes || !requireVerified() || drop.author_id === state.viewer?.id) return;
    if (!state.followState) { toast("Follow controls could not load. Reload the page.", true); return; }
    const author = { ...drop.author, id: drop.author_id };
    const viewerId = state.viewer.id, session = state.messages, csrf = state.csrf;
    const route = state.routeRequestId, path = `${location.pathname}${location.search}`;
    const current = () => state.viewer?.id === viewerId && state.messages === session && state.csrf === csrf;
    const visible = () => current() && (!context.current || context.current())
      && route === state.routeRequestId && path === `${location.pathname}${location.search}`;
    const before = state.followState.get(author, Boolean(drop.viewer_follows_author));
    paintFollowControl(source, author, Boolean(drop.viewer_follows_author), source._followMode || "text");
    const operation = source._followOperation = {};
    source.setAttribute("aria-busy", "true");
    source.setAttribute("aria-disabled", "true");
    try {
      const result = await state.followState.act(author, Boolean(drop.viewer_follows_author));
      if (!current() || !result || result.outcome === "ignored") return;
      if (result.outcome === "current" && !before.following && result.following) {
        track("follow", null, { target_user_id: author.id, surface: state.view });
      }
      if (visible() && result.message) toast(result.message, Boolean(result.error));
    } catch (error) {
      if (visible()) toast(error.message, true);
    } finally {
      if (source._followOperation === operation) {
        delete source._followOperation;
        source.removeAttribute("aria-busy");
        source.removeAttribute("aria-disabled");
        if (current() && source._followAuthor?.id === author.id) paintFollowControl(source, author, Boolean(drop.viewer_follows_author), source._followMode || "text");
      }
    }
  }

  function removeDrop(dropId) {
    state.feedWindows?.invalidate({ removeId: dropId });
    state.coauthors?.withdraw(dropId);
    if (state.world) {
      // Public withdrawal also retires reads started outside the World page.
      state.world.roomNowRequest = null;
      state.world.lensRequestId = (state.world.lensRequestId || 0) + 1;
      let roomChanged = false;
      if (state.world.mic?.current?.drop?.id === dropId) {
        state.world.mic = { ...state.world.mic, current: null };
        roomChanged = true;
      }
      if (state.world.moment?.current?.target?.id === dropId) {
        state.world.moment = { ...state.world.moment, current: null };
        roomChanged = true;
      }
      if (state.world.lens?.items) {
        state.world.lens = { ...state.world.lens, items: state.world.lens.items.filter((drop) => drop.id !== dropId) };
      }
      const withdrawnSlots = new Map();
      state.world.micHistory = (state.world.micHistory || []).map((slot) => {
        if (slot.drop?.id !== dropId) return slot;
        const unavailable = { ...slot, drop: null, holder: null };
        withdrawnSlots.set(slot.id, unavailable);
        return unavailable;
      });
      for (const card of $$("[data-world-mic-slot]")) {
        const unavailable = withdrawnSlots.get(card.dataset.worldMicSlot);
        if (unavailable) card.replaceWith(micHistoryCard(unavailable));
      }
      state.world.moments = (state.world.moments || []).filter((moment) => moment.target?.id !== dropId);
      for (const card of $$("[data-world-moment]")) {
        if (card.dataset.dropId !== dropId) continue;
        const list = card.parentElement;
        card.remove();
        if (!list.childElementCount) list.append(create("p", "utility-empty", "No completed Moments yet."));
      }
      for (const dialog of $$(".utility-dialog[data-mic-entry]")) {
        if (dialog.dataset.micEntry !== "loading" && !$$("option", dialog).some((option) => option.value === dropId)) continue;
        dialog.replaceChildren();
        dialog.close();
      }
      if (roomChanged) renderRoomAffordances();
      // Retire reads immediately: the scheduled refresh has not started yet.
      if (state.discovery.mode === "world") state.discovery.requestId += 1;
      for (const key of ["questions", "polls", "collaborations"]) {
        state.world[key] = state.world[key].filter((item) => item.drop?.id !== dropId);
      }
      if (state.world.daily?.drop?.id === dropId) state.world.daily = null;
      if (state.view === "discover" && state.discovery.mode === "world") renderWorld();
    }
    for (const dialog of $$(".utility-dialog[data-drop-id]")) {
      if (dialog.dataset.dropId !== dropId) continue;
      // Remove private copy and actions as well as the visible modal.
      dialog.replaceChildren();
      dialog.close();
    }
    state.drops = state.drops.filter((item) => item.id !== dropId);
    state.search.drops = state.search.drops.filter((item) => item.id !== dropId);
    state.discovery.items = state.discovery.items.filter((item) => item.id !== dropId);
    state.profile.drops = state.profile.drops.filter((item) => item.id !== dropId);
    state.pending = state.pending.filter((id) => id !== dropId);
    state.flow.items = state.flow.items.filter((item) => item.id !== dropId);
    state.flow.stories = state.flow.stories.filter((item) => item.drop?.id !== dropId);
    state.flow.storyPages = (state.flow.storyPages || []).map((page) => ({
      ...page, items: page.items.filter((item) => item.drop?.id !== dropId),
    }));
    if (state.flow.view === "stories") {
      // A response already read before this event may still succeed after abort.
      // Retire its request identity before permitting another page or refresh.
      state.flow.requestId += 1;
      state.flow.controller?.abort();
      state.flow.controller = null;
      state.flow.loading = false;
      el.flowLoading.hidden = true;
      renderStoryPaging();
    }
    if (state.flow.now?.current?.drop?.id === dropId) state.flow.now.current.drop = null;
    for (const tile of $$("#discover-drop-list > .profile-tile")) {
      if (tile.dataset.dropId === dropId) tile.remove();
    }
    for (const card of $$(".drop-card, .flow-card")) {
      if (card.dataset.dropId !== dropId) continue;
      for (const media of $$("video, audio", card)) {
        media.pause(); media.removeAttribute("src"); media.load();
      }
      // Story footers retain reply/owner actions, so retire the entire wrapper.
      (card.closest?.(".story-card") || card).remove();
    }
    renderFeedEmptyState();
    el.loadMore.hidden = !state.nextCursor;
  }

  async function refreshDropInPlace(dropId) {
    const token = Symbol(dropId);
    state.dropRefreshes.set(dropId, token);
    const context = {
      viewerId: state.viewer?.id || null, view: state.view, filter: state.filter,
      session: state.messages, csrf: state.csrf, path: location.pathname, query: feedQuery(),
      cache: state.feedWindows, window: state.feedWindows?.active, revision: state.feedWindows?.active?.revision,
    };
    const current = () => state.dropRefreshes.get(dropId) === token
      && context.viewerId === (state.viewer?.id || null)
      && context.view === state.view && context.filter === state.filter
      && context.session === state.messages && context.csrf === state.csrf && context.path === location.pathname
      && context.query === feedQuery() && context.cache === state.feedWindows
      && context.window === state.feedWindows?.active && context.revision === state.feedWindows?.active?.revision;
    try {
      // Current borrowed-media reads reconcile native tracks and card keys first.
      // A racing ordinary response would otherwise replace the paused player.
      if (globalThis.louderrSoundPlayback?.revalidateDrop) {
        await globalThis.louderrSoundPlayback.revalidateDrop(dropId);
        if (!current()) return;
      }
      const drop = await api(`/api/drops/${encodeURIComponent(dropId)}`);
      if (!current() || drop?.id !== dropId) return;
      // Detail readability does not prove Saved/folder/Following/topic membership.
      // Update present records across surfaces without inserting a new feed member.
      upsertDrop(drop, false, false);
      if (state.board?.drop?.id === drop.id) state.board.drop = { ...state.board.drop, coauthors: drop.coauthors };
      globalThis.LouderrCoauthors?.refreshBylines(drop, { nameNode: accountNameNode, openProfile: openCreator });
      for (const card of $$(".drop-card", el.feed)) {
        if (card.dataset.dropId !== dropId) continue;
        const updated = updateFeedCard(card, drop, card.feedOptions || {});
        if (updated !== card) card.replaceWith(updated);
        break;
      }
      observeCards();
      observeExposurePanels();
    } catch (error) {
      if (!current() || error.name === "AbortError") return;
      if ([403, 404].includes(error.status)) removeDrop(dropId);
    } finally {
      if (state.dropRefreshes.get(dropId) === token) state.dropRefreshes.delete(dropId);
    }
  }

  function appendMediaCaptions(video, asset, flow = false) {
    if (!asset.captions_url) return;
    const captions = create("track");
    captions.kind = "captions";
    captions.label = "Captions";
    // The author has not supplied a language; do not mislabel their text as English.
    captions.srclang = "und";
    captions.src = asset.captions_url;
    captions.default = true;
    if (flow) captions.addEventListener("load", () => {
      for (const cue of captions.track.cues || []) {
        // Native bottom captions sit behind Flow's author/caption overlay.
        // Position automatic cues in the open video area, clear of the action rail.
        if (cue.line !== "auto") continue;
        cue.snapToLines = false;
        cue.line = 45;
        cue.position = 40;
        cue.size = 72;
      }
    });
    video.append(captions);
  }

  function appendMediaTranscript(holder, asset, className = "media-transcript") {
    if (!asset.transcript_url) return;
    const transcript = create("a", className, "Transcript");
    transcript.href = asset.transcript_url;
    transcript.target = "_blank";
    transcript.rel = "noopener";
    transcript.setAttribute("aria-label", "Read transcript (opens in a new tab)");
    holder.append(transcript);
  }

  function syncPlaybackMediaText(media, asset) {
    const holder = media.closest("figure, .flow-visual, .post-archive-media");
    if (!holder) return;
    const key = JSON.stringify([asset?.captions_url || null, asset?.transcript_url || null,
      asset?.media_text_revision || null]);
    if (media.playbackTextKey === key) return;
    const previousMode = [...(media.textTracks || [])].find(track => ["captions", "subtitles"].includes(track.kind))?.mode;
    for (const track of [...media.querySelectorAll("track[kind='captions'], track[kind='subtitles']")]) {
      track.track.mode = "disabled";
      track.remove();
    }
    for (const link of holder.querySelectorAll(".media-transcript, .flow-transcript")) link.remove();
    if (asset) {
      const flow = media.classList.contains("flow-video");
      if (media.tagName === "VIDEO") {
        appendMediaCaptions(media, asset, flow);
        const track = media.querySelector("track[kind='captions']");
        if (track && ["hidden", "showing"].includes(previousMode)) track.track.mode = previousMode;
      }
      appendMediaTranscript(holder, asset, flow ? "flow-transcript" : "media-transcript");
    }
    media.playbackTextKey = key;
  }

  function galleryCarousel(holder) {
    const slides = [...holder.children], total = slides.length;
    const wrap = create("div", "drop-media-carousel");
    const counter = create("span", "drop-media-count");
    counter.setAttribute("aria-live", "polite");
    const controls = create("div", "drop-media-navigation");
    const previous = create("button", "secondary-button", "Previous");
    const next = create("button", "secondary-button", "Next");
    previous.type = next.type = "button";
    previous.setAttribute("aria-label", "Previous media item");
    next.setAttribute("aria-label", "Next media item");
    holder.tabIndex = 0;
    holder.setAttribute("role", "region");
    holder.setAttribute("aria-roledescription", "carousel");
    holder.setAttribute("aria-label", `Post media, ${total} items`);
    let selected = -1, frame = 0;
    const activate = index => {
      index = Math.max(0, Math.min(total - 1, index));
      if (selected === index) return;
      selected = index;
      slides.forEach((slide, position) => {
        const active = position === selected;
        slide.dataset.galleryActive = String(active);
        slide.inert = !active;
        slide.setAttribute("aria-hidden", String(!active));
        slide.setAttribute("role", "group");
        slide.setAttribute("aria-label", `Media ${position + 1} of ${total}`);
        if (!active) for (const media of slide.querySelectorAll("video, audio")) media.pause();
      });
      counter.textContent = `${selected + 1}/${total}`;
      previous.disabled = selected === 0;
      next.disabled = selected === total - 1;
      // The player invalidates outstanding play promises and releases hidden sources.
      holder.dispatchEvent(new CustomEvent("louderr:carouselchange", { bubbles: true }));
    };
    const move = index => {
      activate(index);
      holder.scrollTo({ left: selected * Math.max(1, holder.clientWidth), behavior: "auto" });
    };
    previous.addEventListener("click", () => move(selected - 1));
    next.addEventListener("click", () => move(selected + 1));
    holder.addEventListener("keydown", event => {
      // Do not steal seek-slider, native media, or button keyboard input.
      if (event.target !== holder || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = { ArrowLeft: selected - 1, ArrowRight: selected + 1, Home: 0, End: total - 1 }[event.key];
      if (target === undefined) return;
      event.preventDefault(); move(target);
    });
    holder.addEventListener("scroll", () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (holder.isConnected) activate(Math.round(holder.scrollLeft / Math.max(1, holder.clientWidth)));
      });
    }, { passive: true });
    // Native fallback remains safe even if the optional enhanced player never loads.
    for (const event of ["play", "playing"]) holder.addEventListener(event, event => {
      const media = event.target;
      if (media instanceof HTMLMediaElement && media.closest("[data-gallery-active='false']")) media.pause();
    }, true);
    activate(0);
    controls.append(previous, next); wrap.append(holder, counter, controls);
    return wrap;
  }

  function bindImageRecovery(image, figure) {
    let panel, message, retry, deadline = null;
    const settle = failed => {
      clearTimeout(deadline); deadline = null;
      if (!image.isConnected) return;
      figure.classList.toggle("has-image-error", failed);
      if (!panel && failed) {
        panel = create("div", "image-recovery");
        message = create("span"); message.setAttribute("role", "status");
        retry = create("button", "secondary-button", "Try again"); retry.type = "button";
        retry.setAttribute("aria-label", "Try loading image again");
        retry.addEventListener("click", () => {
          if (!image.isConnected || retry.disabled) return;
          retry.disabled = true; message.textContent = "Loading image…";
          const src = image.getAttribute("src"), srcset = image.getAttribute("srcset");
          image.removeAttribute("srcset"); image.removeAttribute("src");
          // Reuse the authorized responsive sources, without switching to a
          // larger original or adding a cache-busting URL on a metered link.
          if (srcset) image.setAttribute("srcset", srcset);
          if (src) image.setAttribute("src", src);
          deadline = setTimeout(() => settle(true), 15000);
        });
        panel.append(message, retry); figure.append(panel);
      }
      if (panel) {
        if (!failed && document.activeElement === retry) {
          image.setAttribute("tabindex", "-1");
          image.focus({ preventScroll: true });
        }
        panel.hidden = !failed;
        retry.disabled = false;
        message.textContent = failed ? "Image could not be loaded." : "";
      }
    };
    image.addEventListener("error", () => settle(true));
    image.addEventListener("load", () => settle(false));
  }

  function mediaElement(drop, board = false, options = {}) {
    const holder = create("div", board ? "board-visual drop-media-gallery" : "drop-media-gallery");
    const items = Array.isArray(drop.media)
      ? drop.media
      : drop.media_url && drop.media_type
        ? [{
          position: 0,
          media_asset_id: drop.media_asset_id,
          media_url: drop.media_url,
          thumbnail_url: drop.thumbnail_url,
          media_type: drop.media_type,
          media_alt_text: drop.media_alt_text,
          captions_url: drop.captions_url,
          transcript_url: drop.transcript_url,
        }]
        : [];
    if (!items.length) {
      if (board) holder.append(create("div", "board-placeholder", initials(drop.author.display_name)));
      return holder;
    }
    holder.classList.toggle("is-multiple", items.length > 1);
    holder.classList.toggle("is-nonprimary-only", Number(items[0].position) !== 0);
    for (const item of items) {
      if (!item?.media_url || !item?.media_type) continue;
      const figure = create("figure", "drop-media-item");
      if (Number.isInteger(item.position)) figure.dataset.mediaPosition = String(item.position);
      let media;
      if (item.media_type.startsWith("image/")) {
        media = create("img");
        const preview = previewImageUrl(item.thumbnail_url, 960);
        media.src = preview || item.media_url;
        // Descriptors must not promise more pixels than the sanitized thumbnail
        // can contain. Smaller/unknown originals keep their best available size.
        const sourceWidth = Number(item.width), sourceHeight = Number(item.height);
        const thumbnailWidth = Math.min(sourceWidth, 1280, 1280 * sourceWidth / sourceHeight);
        if (!board && preview !== item.thumbnail_url && sourceWidth > 0 && sourceHeight > 0 && thumbnailWidth >= 960) {
          media.srcset = `${previewImageUrl(item.thumbnail_url, 480)} 480w, ${preview} 960w`;
          media.sizes = "(max-width: 820px) 100vw, 620px";
        }
        media.alt = item.media_alt_text || "";
        media.loading = board && holder.childElementCount === 0 ? "eager" : "lazy";
        media.decoding = "async";
      } else if (item.media_type.startsWith("audio/")) {
        media = create("audio");
        media.controls = true;
        media.preload = "metadata";
        media.src = item.media_url;
      } else if (item.media_type.startsWith("video/")) {
        media = create("video");
        media.controls = true;
        media.playsInline = true;
        media.preload = board && items.length === 1 ? "metadata" : "none";
        media.src = item.media_url;
        if (item.thumbnail_url) media.poster = item.thumbnail_url;
        appendMediaCaptions(media, item);
      }
      if (!media) continue;
      // Reserve the intrinsic box before a lazy image or video poster arrives so feed
      // cards do not jump vertically while the first paint is settling.
      const mediaWidth = Number(item.width), mediaHeight = Number(item.height);
      if ((media.tagName === "IMG" || media.tagName === "VIDEO")
        && Number.isFinite(mediaWidth) && mediaWidth > 0
        && Number.isFinite(mediaHeight) && mediaHeight > 0) {
        media.width = mediaWidth;
        media.height = mediaHeight;
      }
      if (media instanceof HTMLMediaElement) {
        media.setAttribute("aria-label", item.media_alt_text || dropLabel(drop));
        if (!options.privateArchive) bindMediaAnalytics(media, drop.id);
        globalThis.louderrSoundPlayback?.register(media, drop, item, options.soundContext || null);
      }
      figure.append(media);
      if (item.media_type.startsWith("image/")) bindImageRecovery(media, figure);
      if (media instanceof HTMLMediaElement) appendMediaTranscript(figure, item);
      if (item.media_alt_text && !item.media_type.startsWith("image/")) {
        figure.append(create("figcaption", "media-description", item.media_alt_text));
      }
      holder.append(figure);
    }
    if (holder.childElementCount > 1) return galleryCarousel(holder);
    return holder;
  }

  function bindMediaAnalytics(media, dropId) {
    let started = false;
    let lastBucket = -1;
    media.addEventListener("play", () => {
      if (!started) { started = true; track("media_start", dropId); }
    });
    media.addEventListener("timeupdate", () => {
      if (!Number.isFinite(media.duration) || media.duration <= 0) return;
      const bucket = Math.min(100, Math.floor((media.currentTime / media.duration) * 4) * 25);
      if (bucket > lastBucket && bucket > 0) {
        lastBucket = bucket;
        track("media_progress", dropId, { completion_percent: bucket });
      }
    });
  }

  function makeSignalButton(drop, kind, iconName, label) {
    const button = create("button");
    button.type = "button";
    button.dataset.signal = kind;
    button.dataset.requiresCapability = "community_writes";
    button.className = kind === "applaud" ? "reaction-heart" : "";
    button.classList.toggle("capability-withheld", !state.capabilities.community_writes);
    const active = Boolean(drop.viewer?.[kind === "applaud" ? "applauded" : kind === "save" ? "saved" : "nominated"]);
    button.classList.toggle("is-reacted", active);
    button.setAttribute("aria-pressed", String(active));
    button.append(icon(iconName), create("span", "", `${label} ${formatCount(drop.reactions?.[kind] || 0)}`));
    button.addEventListener("click", (event) => toggleSignal(drop.id, kind, event.currentTarget));
    return button;
  }

  let boardImpressionObserver;
  const boardPromise = "The Board shows one Drop at a time. Not every Drop gets a turn.";
  function boardReasonForPeople(board) {
    if (board?.is_sponsored) return board.reason || "Paid placement.";
    const reasons = {
      emerging_first_exposure: "Picked from newer creators.",
      rising_acceleration: "Picked because it is rising.",
      fair_random: "Picked from the creators seen least.",
      trending_momentum: "Picked because it is trending.",
      new_account_discovery: "Picked from new accounts.",
      event_mic_moment: "Picked for an event or a special moment.",
    };
    return reasons[board?.reason_code] || "Picked by the Board's rotation.";
  }

  function renderBoardQueue() {
    clear(el.boardQueue);
    const queue = state.boardQueue || {};
    const eligible = Math.max(0, Number(queue.eligible_count) || 0);
    const capacity = queue.capacity && typeof queue.capacity === "object" ? queue.capacity : {};
    const capacityLabel = capacity.state === "saturated" ? " · at capacity"
      : capacity.state === "constrained" ? " · constrained" : "";
    el.boardEligibleCount.textContent = `${formatCount(eligible)} eligible${capacityLabel}`;
    if (el.boardCapacityNote) {
      const dailySupply = Math.max(0, Number(capacity.maximum_organic_slots_per_day_at_ceiling) || 0);
      const paused = capacity.intake_gate?.enforced ? " New posts are paused right now." : "";
      el.boardCapacityNote.textContent = dailySupply
        ? `Up to ${formatCount(dailySupply)} Drops get a turn each day.${paused}`
        : paused.trim();
    }
    const items = Array.isArray(queue.items) ? queue.items.slice(0, 5) : [];
    if (!items.length) {
      el.boardQueue.append(create("p", "", "Nothing in line yet."));
      return;
    }
    for (const item of items) {
      const candidate = create(item.creator ? "button" : "div", "board-queue-candidate");
      if (item.creator) {
        candidate.type = "button";
        candidate.append(
          create("strong", "", `@${item.creator.handle}`),
          create("small", "", `${kindLabels[item.kind] || item.kind || "Drop"} · ${item.duration_seconds}s`),
        );
        candidate.setAttribute("aria-label", `Coming up ${item.position}: ${item.creator.display_name}`);
        candidate.addEventListener("click", () => openCreator(item.creator));
      } else {
        candidate.append(create("strong", "", "Hidden candidate"), create("small", "", `Position ${item.position}`));
      }
      el.boardQueue.append(candidate);
    }
  }

  function renderBoard() {
    clear(el.board);
    renderBoardQueue();
    el.boardSection.classList.toggle("is-empty", !state.board?.drop);
    const boardContext = $("#board-context");
    if (boardContext) boardContext.textContent = state.board?.drop
      ? "Featured across the room" : "One featured Drop, shared by everyone";
    boardImpressionObserver?.disconnect();
    clearInterval(state.boardTimer);
    const queuedCandidates = Array.isArray(state.boardQueue?.items) ? state.boardQueue.items.length : 0;
    el.boardSection.classList.toggle("is-quiet", !state.board?.drop && !state.board?.hidden && queuedCandidates === 0);
    if (!state.board?.drop) {
      const emptyCopy = state.board?.hidden
        ? "This Board Drop is hidden by your controls."
        : queuedCandidates
          ? "Between features. The next Drop is coming up."
          : "Nothing featured yet. Drops that reach their discovery target can appear here.";
      el.board.append(create("div", "board-empty", emptyCopy));
      el.countdown.textContent = "--:--";
      el.progress.style.width = "0%";
      el.boardReason.textContent = boardPromise;
      return;
    }
    const drop = state.board.drop;
    const isSponsored = Boolean(state.board.is_sponsored);
    const sponsorship = isSponsored ? state.board.sponsorship : null;
    const card = create("article", "board-card");
    card.classList.toggle("is-sponsored", isSponsored);
    if (!isSponsored) {
      card.dataset.dropId = drop.id;
      card.dataset.integritySurface = "board";
    }
    card.append(mediaElement(drop, true));
    const info = create("div", "board-info");
    const author = create("button", "board-author");
    author.type = "button";
    const avatar = create("span", "avatar avatar-small");
    styleAvatar(avatar, drop.author);
    const meta = create("span", "board-author-meta");
    meta.append(accountNameNode(drop.author), create("small", "", `@${drop.author.handle} · ${kindLabels[drop.kind] || drop.kind}`));
    author.append(avatar, meta);
    author.addEventListener("click", () => openCreator(drop.author));
    info.append(author);
    globalThis.LouderrCoauthors?.appendByline(info, drop, { nameNode: accountNameNode, openProfile: openCreator });
    if (isSponsored) {
      const disclosure = sponsorship?.disclosure === "Sponsored" ? sponsorship.disclosure : "Sponsored";
      info.append(create("span", "board-sponsor-label", disclosure));
    }
    const boardTitle = create("h2", "board-title", drop.title);
    const boardCaption = create("p", "board-caption", drop.body);
    info.append(boardTitle, boardCaption);
    const boardTranslation = translationControl(
      drop.translation,
      { title: drop.title, body: drop.body },
      (fields) => { boardTitle.textContent = fields.title; boardCaption.textContent = fields.body; },
    );
    if (boardTranslation) info.append(boardTranslation);
    const actions = create("div", "board-actions");
    actions.append(
      makeSignalButton(drop, "applaud", "heart", "Applaud"),
      makeSignalButton(drop, "save", "bookmark", "Save"),
    );
    if (!isSponsored) actions.append(makeSignalButton(drop, "nominate", "broadcast", "Nominate"));
    const campaignCta = sponsorship?.cta;
    const linkUrl = isSponsored ? providerUrl(campaignCta?.url) : drop.link_url;
    if (linkUrl) {
      const link = create("a", "", isSponsored ? (campaignCta?.label || "Learn more") : "Visit");
      link.href = linkUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer nofollow ugc";
      link.prepend(icon("external"));
      link.addEventListener("click", () => track("outbound_click", drop.id, { surface: "board" }));
      actions.append(link);
    }
    info.append(actions);
    card.append(info);
    el.board.append(card);
    el.boardReason.textContent = `${boardReasonForPeople(state.board)} ${boardPromise}`;
    boardImpressionObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
      const key = `board:${state.board.slot_id}`;
      if (!state.observed.has(key)) {
        state.observed.add(key);
        track("board_impression", drop.id, { slot_id: state.board.slot_id });
      }
      boardImpressionObserver.disconnect();
    }, { threshold: [0.5] });
    boardImpressionObserver.observe(card);
    observeIntegrityCards();
    const update = () => {
      const start = new Date(state.board.started_at).getTime();
      const end = new Date(state.board.ends_at).getTime();
      const remaining = Math.max(0, end - Date.now());
      const seconds = Math.ceil(remaining / 1000);
      el.countdown.textContent = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
      el.progress.style.width = `${Math.max(0, Math.min(100, (remaining / Math.max(1, end - start)) * 100))}%`;
    };
    update();
    state.boardTimer = setInterval(update, 200);
  }

  function feedContentKey(drop, options = {}) {
    // Match translationControl: completed language detection without a translation
    // changes no visible content or controls and must not restart the media either.
    const translation = {};
    for (const key of ["title", "body"]) {
      const field = drop.translation?.fields?.[key];
      if (field?.translated && typeof field.original === "string") {
        translation[key] = [field.original, field.source_language_label || null];
      }
    }
    // applyCapabilities updates write controls in place; access recovery changes no content.
    return JSON.stringify([{ ...drop, translation }, options, state.viewer?.id || null,
      { ...state.capabilities, community_writes: undefined }]);
  }

  function feedMediaKey(drop, options = {}) {
    return JSON.stringify([drop.id, drop.author_id, drop.media, drop.media_asset_id, drop.media_url,
      drop.media_type, drop.thumbnail_url, drop.media_alt_text, drop.captions_url, drop.transcript_url,
      options, state.viewer?.id || null, { ...state.capabilities, community_writes: undefined }]);
  }

  // A guest's first card as the server drew it (guest_home_document in app/shell.py). Its image is
  // kept when it is the one mediaElement would draw, so the image that painted first stays on screen.
  function serverDrawnImage(card, drop) {
    if (card?.dataset.serverDrawn !== "true" || card.dataset.dropId !== drop.id) return false;
    const items = Array.isArray(drop.media) ? drop.media : [drop];
    const item = items[0];
    if (items.length !== 1 || !String(item?.media_type || "").startsWith("image/")) return false;
    const drawn = card.querySelector(".drop-media img");
    return Boolean(drawn && drawn.getAttribute("src") === (previewImageUrl(item.thumbnail_url, 960) || item.media_url));
  }

  // From here on it is an ordinary card, and the drawn image gets the same failure recovery. It began
  // loading with the HTML, so it may already have failed before anything listened for that.
  function adoptServerDrawnCard(card, media) {
    delete card.dataset.serverDrawn;
    for (const image of $$(".drop-media-item > img", media)) {
      bindImageRecovery(image, image.parentElement);
      if (image.complete && !image.naturalWidth) image.dispatchEvent(new Event("error"));
    }
  }

  function updateFeedCard(card, drop, options = {}) {
    if (card?.feedContentKey === feedContentKey(drop, options)) return card;
    const retain = card && (card.feedMediaKey === feedMediaKey(drop, options)
      && !card.dataset.personalFilterHidden && card.querySelector(".drop-media video, .drop-media audio")
      || serverDrawnImage(card, drop));
    return makeDropCard(drop, options, retain ? card : null);
  }

  function makeDropCard(drop, options = {}, retained = null) {
    const template = $("#feed-card-template").content.firstElementChild.cloneNode(true);
    const card = retained || template;
    const retainedMedia = retained ? $(".drop-media", retained) : null;
    if (retainedMedia) {
      // Keep the native media and its ancestors connected. Moving a video into
      // a replacement card would itself interrupt decoding and pending Play.
      for (const child of [...card.childNodes]) if (child !== retainedMedia) child.remove();
      let afterMedia = false;
      for (const child of [...template.childNodes]) {
        if (child.nodeType === 1 && child.classList.contains("drop-media")) { afterMedia = true; continue; }
        if (afterMedia) card.append(child); else card.insertBefore(child, retainedMedia);
      }
    }
    card.feedContentKey = feedContentKey(drop, options);
    card.feedMediaKey = feedMediaKey(drop, options);
    card.soundPlaybackDrop = JSON.parse(JSON.stringify(drop));
    card.feedOptions = { ...options };
    card.dataset.dropId = drop.id;
    card.dataset.authorId = drop.author_id;
    if (options.surface) card.dataset.integritySurface = options.surface;
    if (options.allocationId) card.dataset.allocationId = options.allocationId;
    if (options.analyticsSurface) card.dataset.analyticsSurface = options.analyticsSurface;
    const interactionEvents = {
      undiscovered: "undiscovered_interaction",
      right_now: "right_now_click",
      rising: "rising_interaction",
      mic: "mic_interaction",
    };
    const trackInteraction = (action) => {
      const eventType = interactionEvents[options.analyticsSurface];
      if (eventType) track(eventType, drop.id, { surface: options.analyticsSurface, action });
    };
    const avatar = $(".drop-avatar", card);
    styleAvatar(avatar, drop.author);
    renderAccountName($(".drop-author-name", card), drop.author);
    globalThis.LouderrCoauthors?.appendByline($(".drop-header", card), drop, { nameNode: accountNameNode, openProfile: openCreator });
    $(".drop-kind", card).textContent = kindLabels[drop.kind] || drop.kind;
    $(".drop-time", card).textContent = `${(typeof compactTime === "function" ? compactTime : relativeTime)(drop.created_at)}${drop.edited_at ? " · edited" : ""}`;
    $(".drop-time", card).title = new Date(drop.created_at).toLocaleString();
    const title = $(".drop-title", card);
    const titleLink = create("a", "drop-title-link", drop.title);
    titleLink.href = `/d/${encodeURIComponent(drop.id)}`;
    titleLink.addEventListener("click", (event) => {
      trackInteraction("open_drop");
      // Stay inside the app: a full reload would drop the live socket, any parked draft and the scroll position.
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      history.pushState({}, "", titleLink.getAttribute("href"));
      routeLocation();
    });
    title.append(titleLink); title.hidden = !drop.title;
    const description = $(".drop-description", card);
    description.textContent = drop.body; description.hidden = !drop.body;
    if (Array.isArray(drop.tags) && drop.tags.length && typeof openTopic === "function") {
      const tags = create("div", "drop-tags");
      for (const tag of drop.tags.slice(0, 5)) {
        const chip = create("a", "drop-tag", `#${tag}`);
        chip.href = `/t/${encodeURIComponent(tag)}`;
        chip.addEventListener("click", (event) => { event.preventDefault(); openTopic(tag); });
        tags.append(chip);
      }
      $(".drop-copy", card).append(tags);
    }
    const translated = translationControl(
      drop.translation,
      { title: drop.title, body: drop.body },
      (fields) => { titleLink.textContent = fields.title; title.hidden = !fields.title; description.textContent = fields.body; description.hidden = !fields.body; },
    );
    if (translated) $(".drop-copy", card).append(translated);
    globalThis.LouderrOriginalSounds?.appendCredit($(".drop-copy", card), drop, {
      openSource: id => { history.pushState({}, "", `/d/${encodeURIComponent(id)}`); routeLocation(); }, openProfile: openCreator,
    });
    if (drop.journey?.root_drop_id) {
      const project = create("button", "project-badge", journeyModeLabel(drop.journey.mode));
      project.type = "button";
      project.setAttribute("aria-haspopup", "dialog");
      project.setAttribute("aria-label", `View project: ${dropLabel(drop)}`);
      project.append(create("span", "", "View project →"));
      project.addEventListener("click", () => openJourney(drop.journey.root_drop_id));
      $(".drop-copy", card).prepend(project);
    }
    if (drop.first_on_louderr) {
      const until = drop.first_on_louderr_until ? new Date(drop.first_on_louderr_until) : null;
      const stillExclusive = until && Number.isFinite(until.getTime()) && until > new Date();
      const badge = create("span", "here-first-badge", stillExclusive ? `Here first · only here until ${until.toLocaleDateString()}` : "Here first");
      badge.title = "The creator says this appeared on LOUDERR before anywhere else.";
      $(".drop-copy", card).prepend(badge);
    }
    if (options.discoveryMode) appendDiscoveryMetadata($(".drop-copy", card), drop, options.discoveryMode);
    $("[data-open-author]", card).addEventListener("click", () => { trackInteraction("open_author"); openCreator(drop.author); });
    // Follow from the card itself, like a suggested post: shown only until you follow.
    if (state.viewer && drop.author_id !== state.viewer.id && !options.hideFollow) {
      const follow = create("button", "drop-follow");
      follow.type = "button";
      follow.dataset.requiresCapability = "community_writes";
      follow.classList.toggle("capability-withheld", !state.capabilities.community_writes);
      const followIcon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      followIcon.setAttribute("aria-hidden", "true");
      const followUse = document.createElementNS("http://www.w3.org/2000/svg", "use");
      followUse.setAttribute("href", "#icon-user-plus");
      followIcon.append(followUse);
      follow.append(followIcon);
      follow.title = "Follow";
      follow.setAttribute("aria-label", `Follow ${drop.author?.display_name || drop.author?.handle || "creator"}`);
      if (state.followState) paintFollowControl(follow, { ...drop.author, id: drop.author_id }, Boolean(drop.viewer_follows_author), "icon");
      else follow.hidden = Boolean(drop.viewer_follows_author);
      follow.addEventListener("click", () => toggleDropAuthorFollow(drop, follow));
      $(".more-button", card).before(follow);
    }
    const media = $(".drop-media", card);
    const builtMedia = retainedMedia?.firstElementChild || mediaElement(drop);
    if (!retainedMedia && builtMedia.childElementCount) media.append(builtMedia);
    if (retained?.dataset.serverDrawn) adoptServerDrawnCard(card, media);
    if (retainedMedia) {
      for (const figure of $$(".drop-media-item", media)) {
        const asset = Array.isArray(drop.media)
          ? drop.media.find(item => Number(item.position || 0) === Number(figure.dataset.mediaPosition || 0)) : drop;
        const label = asset?.media_alt_text || dropLabel(drop);
        for (const node of $$("video, audio, .video-player", figure)) node.setAttribute("aria-label", label);
      }
    }
    card.dataset.kind = drop.kind || "";
    card.classList.toggle("is-text-only", !builtMedia.childElementCount);
    // Double-tap (or double-click) the post body to applaud, with a heart burst.
    const applaudByDoubleTap = () => {
      if (!state.capabilities.community_writes || !state.viewer) return;
      const burst = create("span", "heart-burst");
      burst.setAttribute("aria-hidden", "true");
      burst.append(icon("heart"));
      card.append(burst);
      burst.addEventListener("animationend", () => burst.remove(), { once: true });
      setTimeout(() => burst.remove(), 900);
      if (!drop.viewer?.applauded) toggleSignal(drop.id, "applaud", card);
    };
    $(".drop-copy", card).addEventListener("dblclick", applaudByDoubleTap);
    media.ondblclick = applaudByDoubleTap;
    const counts = drop.reactions || {};
    for (const kind of ["applaud", "nominate", "save"]) {
      const button = $(`[data-signal="${kind}"]`, card);
      if (!button) continue;
      const activeKey = kind === "applaud" ? "applauded" : kind === "save" ? "saved" : "nominated";
      button.classList.toggle("is-reacted", Boolean(drop.viewer?.[activeKey]));
      const count = Number(counts[kind] || 0);
      $("b", button).textContent = count ? formatCount(count) : "";
      button.classList.toggle("is-zero", !count);
      button.addEventListener("click", (event) => { trackInteraction(kind); toggleSignal(drop.id, kind, event.currentTarget); });
    }
    const repost = state.socialExtras?.makeRepostButton?.(drop);
    if (repost) $(".primary-actions", card).insertBefore(repost, $("[data-share]", card));
    const comments = $("[data-comments]", card);
    const commentTotal = Number(drop.comment_count || 0);
    $("b", comments).textContent = commentTotal ? formatCount(commentTotal) : "";
    comments.classList.toggle("is-zero", !commentTotal);
    comments.setAttribute("aria-label", `View ${formatCount(drop.comment_count || 0)} comments`);
    comments.addEventListener("click", () => { trackInteraction("comments"); openComments(drop, { compose: commentTotal === 0 }); });
    $("[data-share]", card).addEventListener("click", (event) => { trackInteraction("share"); shareDrop(drop, event.currentTarget); });
    $(".more-button", card).addEventListener("click", (event) => openActionMenu(drop, event.currentTarget));
    const commentCount = Number(drop.comment_count || 0);
    const engagementLine = $(".engagement-line", card);
    clear(engagementLine);
    // The counts already stand beside the heart and the bookmark. Instagram adds a line only when it
    // names someone the reader knows; a bare repeat of the same number is noise, so this stays empty.
    engagementLine.hidden = true;
    // The conversation line sits under the caption, like a post page: open the comments or start them.
    const commentsLine = create("button", `drop-comments-line${commentCount ? "" : " is-empty"}`);
    commentsLine.type = "button";
    commentsLine.textContent = commentCount
      ? `View ${commentCount === 1 ? "1 comment" : `all ${formatCount(commentCount)} comments`}`
      : "Add a comment…";
    if (!commentCount) {
      commentsLine.dataset.requiresCapability = "community_writes";
      commentsLine.classList.toggle("capability-withheld", !state.capabilities.community_writes);
    }
    commentsLine.addEventListener("click", () => { trackInteraction("comments"); openComments(drop, { compose: commentCount === 0 }); });
    // Keep keyboard and screen-reader order identical to the visible photo-feed order.
    if (builtMedia.childElementCount) card.append($(".drop-copy", card));
    card.append(commentsLine);
    const link = $(".drop-link", card);
    if (drop.link_url) {
      link.href = drop.link_url;
      link.addEventListener("click", (event) => { trackInteraction("outbound"); track("outbound_click", drop.id, surfaceMetadata(event.currentTarget)); });
    } else link.hidden = true;
    if (options.ownerExposure && state.viewer?.id === drop.author_id) {
      $(".drop-copy", card).append(exposurePanel(drop));
    }
    // Last, so the card is complete before the viewer's own filter decides
    // whether to fold it away. The filter runs entirely in this browser, never
    // reorders or removes anything, and clears data-integrity-surface on a
    // folded card so a Drop nobody looked at cannot spend its author's Fair
    // start. See app/static/personal-filter.js.
    window.LouderrPersonalFilter?.applyToCard(card, drop);
    return card;
  }

  function discoveryMovementLabel(value) {
    if (value && typeof value === "object") return movementLabel(value);
    const movement = Number(value);
    if (!Number.isFinite(movement)) return "New";
    if (movement > 0) return `↑${movement}`;
    if (movement < 0) return `↓${Math.abs(movement)}`;
    return "Steady";
  }

  function discoveryAgeLabel(seconds) {
    const value = Math.max(0, Number(seconds) || 0);
    if (value < 60) return "under 1 min old";
    if (value < 3600) return `${Math.floor(value / 60)} min old`;
    if (value < 86400) return `${Math.floor(value / 3600)} hr old`;
    return `${Math.floor(value / 86400)} d old`;
  }

  function appendDiscoveryMetadata(container, drop, mode) {
    const components = drop.score_components || {};
    const meta = create("div", `discovery-rank-meta is-${mode}`);
    const pieces = [`#${formatCount(drop.rank)}`];
    if (mode === "right-now" && Number.isFinite(Number(components.age_seconds))) {
      pieces.push(discoveryAgeLabel(components.age_seconds));
    }
    pieces.push(discoveryMovementLabel(drop.movement));
    if (mode === "rising" && Number.isFinite(Number(components.acceleration))) {
      pieces.push(`+${formatCount(components.acceleration)} acceleration`);
    }
    if (Number.isFinite(Number(drop.score_points))) pieces.push(`${formatCount(drop.score_points)} momentum`);
    if (mode === "rising") meta.append(create("strong", "", "Rising"));
    meta.append(create("span", "", pieces.join(" · ")));
    container.append(meta);
  }

  function appendBoardPath(panel, board) {
    if (!board) return;
    const section = create("section", "board-path");
    const labels = {
      on_board: "ON THE BOARD",
      locked: "LOCKED FOR BOARD",
      eligible: "✓ BOARD ELIGIBLE",
      cooldown: "BOARD COOLDOWN",
      building_exposure: "PATH TO BOARD",
      not_eligible: "NOT BOARD ELIGIBLE",
    };
    section.append(
      create("strong", board.board_eligible ? "is-board-eligible" : "", labels[board.status] || "PATH TO BOARD"),
      create("p", "", board.explanation || "Board eligibility is unavailable."),
    );
    const facts = create("div", "board-path-facts");
    if (Number.isFinite(Number(board.eligible_pool_size))) facts.append(create("span", "", `${formatCount(board.eligible_pool_size)} currently eligible`));
    if (board.next_eligible_at) facts.append(create("span", "", `Eligible again ${relativeTime(board.next_eligible_at)}`));
    if (board.signals) facts.append(create("span", "", `${formatCount(board.signals.applause || 0)} applause · ${formatCount(board.signals.saves || 0)} saves · ${formatCount(board.signals.nominations || 0)} nominations`));
    if (facts.childElementCount) section.append(facts);
    if (board.appearances > 0) {
      section.append(create("small", "", `${formatCount(board.appearances)} Board ${board.appearances === 1 ? "appearance" : "appearances"}. Selection odds are never published.`));
    } else {
      section.append(create("small", "", "A completed discovery target can unlock Board eligibility. Eligibility does not promise a turn."));
    }
    panel.append(section);
  }

  // One heading row and one status line in every state (loading, loaded, refused), so an own
  // post does not grow under the reader when its read lands: measured on /mine, the loaded panel
  // pushed the next post 181 px down. The meter, the rules and the Board path open under Details.
  function renderExposurePanel(panel, payload = null, error = null) {
    clear(panel);
    const heading = create("div", "exposure-heading");
    heading.append(create("strong", "", "Fair start"));
    const controls = create("span", "exposure-actions");
    const toggle = create("button", "text-button", "Details"); toggle.type = "button";
    const refresh = create("button", "text-button", "Refresh"); refresh.type = "button";
    refresh.addEventListener("click", () => loadExposure(panel.dataset.dropId, panel, true).catch(() => {}));
    controls.append(toggle, refresh); heading.append(controls); panel.append(heading);
    const more = create("div", "exposure-more");
    more.id = `exposure-more-${panel.dataset.dropId}`;
    const line = (className, text) => panel.append(create("p", `exposure-line ${className}`, text));
    const paint = (open) => {
      more.hidden = !open;
      toggle.setAttribute("aria-expanded", String(open));
      panel.classList.toggle("is-open", open);
    };
    toggle.setAttribute("aria-controls", more.id);
    toggle.addEventListener("click", () => {
      const open = more.hidden;
      panel.dataset.detailsOpen = String(open);
      paint(open);
    });
    const finish = () => {
      // Kept in place, never removed, so the heading row is the same width and height while loading.
      toggle.disabled = !more.childElementCount;
      panel.append(more);
      paint(!toggle.disabled && panel.dataset.detailsOpen === "true");
    };
    if (error) {
      line("is-error", error);
      return finish();
    }
    if (!payload) {
      line("is-loading", "Loading progress…");
      return finish();
    }
    if (!payload.eligible_for_guaranteed_exposure || !payload.guaranteed_exposure) {
      const pending = payload.distribution_status === "pending_trust";
      line(
        `exposure-status ${pending ? "is-pending" : "is-ineligible"}`,
        payload.eligibility_explanation || "This Drop is not eligible for a fair start.",
      );
      const trust = pending ? payload.trust_progress : null;
      if (trust) {
        const requiredDays = Math.max(1, Math.round(Number(trust.account_age_required_seconds || 0) / 86400));
        const ageDays = Math.min(requiredDays, Math.floor(Number(trust.account_age_seconds || 0) / 86400));
        const requiredActions = Number(trust.successful_actions_required || 0);
        const actions = Math.min(requiredActions, Number(trust.successful_actions || 0));
        more.append(create(
          "small",
          "exposure-trust-progress",
          `Account age ${ageDays} of ${requiredDays} days · ${actions} of ${requiredActions} successful actions.`,
        ));
      }
      appendBoardPath(more, payload.board);
      return finish();
    }
    const progress = payload.guaranteed_exposure;
    if (closedBetaTelemetryDisabled()) {
      // Impressions are not collected in this profile, so the meter could never move. Say what is true.
      line("exposure-status", "Fair start pacing is paused during the closed beta: every eligible Drop is shown to everyone here.");
      appendBoardPath(more, payload.board);
      return finish();
    }
    const current = Math.max(0, Number(progress.qualified_unique_accounts ?? progress.qualified_unique_people) || 0);
    const target = Math.max(1, Number(progress.target) || 1);
    line(`exposure-status${progress.complete ? " is-complete" : ""}`, progress.complete ? `Fair start done: ${formatCount(target)} qualified accounts reached` : `${formatCount(current)} of ${formatCount(target)} qualified accounts so far`);
    const meter = create("progress"); meter.max = target; meter.value = Math.min(current, target);
    meter.setAttribute("aria-label", `Fair start: ${current} of ${target} qualified accounts`);
    more.append(meter);
    if (progress.excluded_or_repeat_events > 0) {
      more.append(create("small", "", `${formatCount(progress.excluded_or_repeat_events)} repeat or excluded ${progress.excluded_or_repeat_events === 1 ? "view" : "views"} did not count`));
    }
    const definition = create("details", "discovery-explainer");
    definition.append(create("summary", "", "What counts?"), create("p", "", "New Drops are first shown to this many different accounts. A view counts when the Drop is on someone's screen. There is no promise of views, sales, timing or a Board turn."));
    more.append(definition);
    appendBoardPath(more, payload.board);
    finish();
  }

  function syncExposureIdentity(forceReset = false) {
    const ownerId = state.viewer?.id || null;
    if (!forceReset && state.exposure.ownerId === ownerId) return;
    state.exposure.observer?.disconnect();
    // The replacement object is an identity epoch, including A -> B -> A.
    state.exposure = { ownerId, observer: null };
    for (const panel of $$(".exposure-panel")) {
      delete panel.exposureRequest;
      delete panel.exposureState;
      clear(panel);
      panel.hidden = true;
    }
  }

  async function loadExposure(dropId, panel, force = false) {
    syncExposureIdentity();
    const exposure = state.exposure;
    const ownerId = state.viewer?.id;
    const available = () => Boolean(dropId && ownerId && panel?.isConnected
      && state.exposure === exposure && state.viewer?.id === ownerId
      && panel.exposureState === exposure && panel.dataset.ownerId === ownerId
      && panel.dataset.dropId === dropId);
    if (!available()) return;
    if (!force && panel.exposureRequest) return panel.exposureRequest.promise;
    const operation = {};
    panel.exposureRequest = operation;
    const current = () => available() && panel.exposureRequest === operation;
    panel.hidden = false;
    renderExposurePanel(panel);
    // Keep only the in-flight request, never fulfilled privileged response JSON.
    operation.promise = (async () => {
      try {
        const data = await api(`/api/discovery/exposure/${encodeURIComponent(dropId)}`);
        if (current()) renderExposurePanel(panel, data);
      } catch (requestError) {
        if (!current()) return;
        if (requestError.status === 404) panel.hidden = true;
        else renderExposurePanel(panel, null, requestError.message);
        throw requestError;
      } finally {
        if (panel.exposureRequest === operation) delete panel.exposureRequest;
      }
    })();
    return operation.promise;
  }

  function exposurePanel(drop) {
    syncExposureIdentity();
    const panel = create("section", "exposure-panel");
    panel.dataset.dropId = drop.id;
    panel.dataset.ownerId = drop.author_id;
    panel.exposureState = state.exposure;
    panel.setAttribute("aria-label", `Discovery progress for ${dropLabel(drop)}`);
    renderExposurePanel(panel);
    return panel;
  }

  function observeExposurePanels() {
    const exposure = state.exposure;
    exposure.observer?.disconnect();
    if (state.view !== "mine" || !("IntersectionObserver" in window)) return;
    exposure.observer = new IntersectionObserver((entries) => {
      if (state.exposure !== exposure) return;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        exposure.observer.unobserve(entry.target);
        loadExposure(entry.target.dataset.dropId, entry.target).catch(() => {});
      }
    }, { rootMargin: "160px 0px" });
    $$(".exposure-panel", el.feed).forEach((panel) => exposure.observer.observe(panel));
  }

  let impressionObserver;
  let utilityAnalyticsObserver;
  function observeUtilityAnalytics() {
    utilityAnalyticsObserver?.disconnect();
    utilityAnalyticsObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
        const node = entry.target;
        const eventType = node.dataset.analyticsEvent;
        const key = node.dataset.analyticsKey || `${eventType}:${node.dataset.dropId || "surface"}`;
        trackOnce(key, eventType, node.dataset.dropId || null);
        utilityAnalyticsObserver.unobserve(node);
      }
    }, { threshold: [0.5] });
    $$('[data-analytics-event]').forEach((node) => utilityAnalyticsObserver.observe(node));
  }

  function observeCards() {
    impressionObserver?.disconnect();
    impressionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const dropId = entry.target.dataset.dropId;
        const analyticsSurface = entry.target.dataset.analyticsSurface;
        const eventType = {
          floor: "floor_impression",
          undiscovered: "undiscovered_open",
          right_now: "right_now_open",
          rising: "rising_open",
          mic: "mic_impression",
        }[analyticsSurface];
        if (!eventType) continue;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (!state.observed.has(`${analyticsSurface}:${dropId}`)) {
            state.observed.add(`${analyticsSurface}:${dropId}`);
            track(eventType, dropId, analyticsSurface === "rising" ? { surface: "rising" } : {});
          }
          state.dwellStarts.set(`${analyticsSurface}:${dropId}`, performance.now());
        } else if (state.dwellStarts.has(`${analyticsSurface}:${dropId}`)) {
          const duration = Math.round(performance.now() - state.dwellStarts.get(`${analyticsSurface}:${dropId}`));
          state.dwellStarts.delete(`${analyticsSurface}:${dropId}`);
          if (duration >= 1000) track("dwell", dropId, { duration_ms: duration });
        }
      }
    }, { threshold: [0, 0.5] });
    $$(".drop-card", el.feed)
      .concat($$(".drop-card", el.discoverDrops), $$(".drop-card", el.profileDrops), $$(".drop-card", el.worldContent))
      .forEach((card) => impressionObserver.observe(card));
    observeIntegrityCards();
  }

  let integrityObserver;
  function cancelIntegrityExposure(card) {
    const pending = state.integrityPending.get(card);
    if (!pending) return;
    clearTimeout(pending.timer);
    state.integrityPending.delete(card);
  }

  function closedBetaTelemetryDisabled() {
    return state.capabilities.profile === "no-cost-closed-beta-v1";
  }

  async function submitIntegrityImpression(card, pending) {
    if (closedBetaTelemetryDisabled()) return;
    const surface = card.dataset.integritySurface;
    const dropId = card.dataset.dropId;
    if (!surface || !dropId || !state.viewer?.email_verified || !state.csrf) return;
    const allocationId = card.dataset.allocationId || null;
    const key = `${surface}:${dropId}:${allocationId || "session"}`;
    if (state.integrityObserved.has(key)) return;
    const visibleMs = Math.max(1000, Math.round(performance.now() - pending.startedAt));
    const ratio = Math.max(0.5, Math.min(1, pending.maxRatio));
    state.integrityObserved.add(key);
    try {
      const result = await api("/api/integrity/impressions", {
        method: "POST",
        csrf: true,
        json: {
          event_key: crypto.randomUUID(),
          drop_id: dropId,
          surface,
          session_key: state.integritySession,
          visible_ms: visibleMs,
          visible_ratio_milli: Math.round(ratio * 1000),
          foreground: true,
          occurred_at: new Date().toISOString(),
          allocation_id: allocationId,
        },
      });
      card.dataset.integrityState = result.integrity_state || "recorded";
      if (allocationId && result.exposure_complete) card.dataset.exposureComplete = "true";
      dispatchEvent(new CustomEvent("lobby:integrity-impression", {
        detail: { dropId, surface, allocationId, result },
      }));
    } catch (error) {
      card.dataset.integrityState = "failed";
      setTimeout(() => state.integrityObserved.delete(key), 30000);
    }
  }

  function scheduleIntegrityExposure(card, ratio) {
    if (closedBetaTelemetryDisabled()) return;
    if (document.visibilityState !== "visible" || !state.viewer?.email_verified || !state.csrf) return;
    const surface = card.dataset.integritySurface;
    const allocationId = card.dataset.allocationId || null;
    const key = `${surface}:${card.dataset.dropId}:${allocationId || "session"}`;
    if (state.integrityObserved.has(key)) return;
    const existing = state.integrityPending.get(card);
    if (existing) {
      existing.maxRatio = Math.max(existing.maxRatio, ratio);
      return;
    }
    const pending = { startedAt: performance.now(), maxRatio: ratio, timer: null };
    pending.timer = setTimeout(() => {
      state.integrityPending.delete(card);
      if (
        document.visibilityState !== "visible"
        || !card.isConnected
        || Number(card.dataset.visibleRatio || 0) < 0.5
      ) return;
      submitIntegrityImpression(card, pending);
    }, 1000);
    state.integrityPending.set(card, pending);
  }

  function observeIntegrityCards() {
    integrityObserver?.disconnect();
    for (const card of state.integrityPending.keys()) cancelIntegrityExposure(card);
    if (closedBetaTelemetryDisabled()) return;
    if (!state.viewer?.email_verified || !state.csrf || !("IntersectionObserver" in window)) return;
    integrityObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        entry.target.dataset.visibleRatio = String(entry.intersectionRatio);
        if (
          entry.isIntersecting
          && entry.intersectionRatio >= 0.5
          && document.visibilityState === "visible"
        ) scheduleIntegrityExposure(entry.target, entry.intersectionRatio);
        else cancelIntegrityExposure(entry.target);
      }
    }, { threshold: [0, 0.5, 0.75, 1] });
    $$('[data-integrity-surface][data-drop-id]').forEach((card) => integrityObserver.observe(card));
  }

  let feedPaginationObserver;
  function observeFeedPagination() {
    feedPaginationObserver?.disconnect();
    if (!state.nextCursor || el.loadMore.hidden) return;
    feedPaginationObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting) || state.feedBusy || state.feedWindows?.token) return;
      refreshFeed(true);
    }, { rootMargin: "240px 0px" });
    feedPaginationObserver.observe(el.loadMore);
  }

  function renderFeedEmptyState() {
    const title = $("#feed-empty-title");
    const copy = $("#feed-empty-copy");
    const action = $("#feed-empty-action");
    if (!title || !copy || !action) return;
    const personal = ["saved", "mine", "following"].includes(state.view);
    let content;
    if (state.view === "drop") content = ["This Drop is unavailable", "It may have been removed or may not be visible to you.", "Explore the room", "home"];
    else if (personal && !state.viewer) content = ["Your space starts here", "Sign in to see your saved Drops, follows, and posts.", "Sign in", "signin"];
    else if (state.filter === "first") content = ["Nothing here first yet", "Creators mark Drops that appear on LOUDERR before anywhere else. They show up here.", "Show all categories", "clear-filter"];
    else if (state.filter !== "all") { const chosen = state.filter.split(",").filter((kind) => kind && kind !== "first"); const where = chosen.length > 1 ? "these categories" : kindLabels[chosen[0]] || chosen[0]; content = [state.filter.split(",").includes("first") ? `Nothing here first in ${where} yet` : chosen.length > 1 ? "Nothing in these categories yet" : `No ${where} Drops here yet`, "Try all categories to see more of this view.", "Show all categories", "clear-filter"]; }
    else if (state.view === "unseen") content = ["Nothing is waiting for first viewers", "When a Drop is waiting for its first qualified accounts, it shows up here. Right now nothing is waiting for you.", "See the latest Drops", "home"];
    else if (!state.capabilities.community_writes && state.view === "saved") content = ["No saved Drops here", "Previously saved public Drops will appear here when available.", "Browse the room", "home"];
    else if (!state.capabilities.community_writes && state.view === "mine") content = ["No public Drops here", "Your existing public Drops will appear here when available.", "Browse the room", "home"];
    else if (!state.capabilities.community_writes && state.view === "following") content = ["No followed creators here", "Existing follows will appear here when they have public Drops.", "Discover people", "discover"];
    else if (!state.capabilities.community_writes) content = ["The room is quiet", "No public Drops are available yet.", "Explore World", "discover"];
    else if (state.view === "saved") content = ["Keep something worth returning to", "Save a Drop with the bookmark button. You will find it here.", "Find a Drop", "home"];
    else if (state.view === "mine") content = ["Your first Drop starts here", "Share something you made, found, or want to talk about.", "Create a Drop", "create"];
    else if (state.view === "following") content = ["Find your people", "Follow a creator to see their Drops here. The shared room is always open to everyone.", "Discover people", "discover"];
    else if (!state.viewer && state.view === "all") {
      const canJoin = Boolean(state.capabilities.registration || state.federatedRegistration)
        && (!state.capacity || seatsAcceptRegistration(state.capacity));
      content = canJoin
        ? ["Share what you're making", "Create an account to post your work, get feedback, and follow other creators.", "Create account", "join"]
        : ["The room is quiet", "Already a member? Sign in to share your work and follow other creators.", "Sign in", "signin"];
    }
    else if (state.view === "topics") content = ["Follow a topic to fill this lens", "Drops under the topics you follow gather here.", "Find topics", "discover"]; else content = ["Share something you made", "A photo, a video, a track, or a few words. It shows up here for the whole room.", "Create a Drop", "create"];
    [title.textContent, copy.textContent, action.textContent, action.dataset.emptyAction] = content;
    action.hidden = false;
  }

  function renderFeed() {
    // Suggestions belong to the Drop being read; re-rendering that same Drop keeps them.
    if (state.view !== "drop" || !state.drops.some((drop) => drop.id === state.relatedDropId)) resetRelatedDrops();
    const visible = state.drops.filter((drop) => !state.hiddenDropIds?.has?.(drop.id));
    // Reconnection revalidates the feed even when its contents have not changed.
    // Keep those cards connected so a pending Play, playback and focused controls survive.
    const retainedIds = new Set(visible.map((drop) => drop.id));
    const existing = new Map($$(".drop-card", el.feed).map((card) => [card.dataset.dropId, card]));
    for (const card of existing.values()) if (!retainedIds.has(card.dataset.dropId)) card.remove();
    // The unseen lens is the Fair start allocation: its cards carry their allocation so qualified
    // exposure is reported exactly as it is from Discover, and never counted as a Floor impression.
    const unseen = state.view === "unseen";
    for (const [index, drop] of visible.entries()) {
      const options = unseen
        ? { surface: "undiscovered", analyticsSurface: "undiscovered", allocationId: drop.allocation_id || null, discoveryMode: "undiscovered" }
        : { surface: "floor", analyticsSurface: "floor", ownerExposure: state.view === "mine" };
      const previous = existing.get(drop.id);
      const card = updateFeedCard(previous, drop, options);
      if (previous && card !== previous) previous.remove();
      const current = $$(".drop-card", el.feed)[index];
      if (current !== card) el.feed.insertBefore(card, current || null);
    }
    syncBusinessAds("floor", el.feed, visible.length);
    const pending = state.feedWindows?.current(state.feedWindows.active)
      && (state.feedWindows.token || (state.feedBusy && !state.feedWindows.active.loaded));
    el.loading.hidden = !pending || visible.length > 0;
    el.empty.hidden = Boolean(pending) || visible.length > 0;
    renderFeedEmptyState();
    el.loadMore.hidden = !state.nextCursor;
    if (typeof renderRailPeople === "function") renderRailPeople();
    observeCards();
    observeExposurePanels();
    observeFeedPagination();
  }

  function resetRelatedDrops() {
    state.relatedRequestId = (state.relatedRequestId || 0) + 1;
    state.relatedDropId = null;
    document.getElementById("related-drops")?.remove();
  }

  async function loadRelatedDrops(dropId) {
    resetRelatedDrops();
    if (!dropId) return;
    state.relatedDropId = dropId;
    const path = location.pathname;
    const requestId = state.relatedRequestId;
    const routeRequestId = state.routeRequestId;
    const socialState = state.messages;
    const viewerId = state.viewer?.id || null;
    try {
      const payload = await api(`/api/drops/${encodeURIComponent(dropId)}/related`);
      // Returning to the same path or account must not revive an earlier audience.
      if (location.pathname !== path || state.view !== "drop"
        || state.relatedRequestId !== requestId || state.routeRequestId !== routeRequestId
        || state.messages !== socialState || (state.viewer?.id || null) !== viewerId) return;
      renderRelatedDrops(payload);
    } catch (_) {
      // A suggestion list is not worth an error message. The Drop is readable
      // without it, and `related_results_enabled` off returns an empty list
      // rather than an error, so this path is only for a real failure.
    }
  }

  /**
   * Drops whose wording resembles the one being read.
   *
   * Content only. No behaviour, no history, no engagement figure, and no
   * authority over the Floor, the Board or Trending -- see app/related.py. It
   * is lexical, so the copy says wording rather than implying understanding.
   *
   * A viewer's personal filter is applied here too, and a matching entry is left
   * out rather than folded. This is a suggestion list with no promise of order
   * or completeness, unlike the shared Floor, so there is nothing for a strip to
   * preserve; if everything is filtered out the section does not appear.
   */
  function renderRelatedDrops(payload) {
    document.getElementById("related-drops")?.remove();
    const items = Array.isArray(payload?.items) ? payload.items : [];
    if (!items.length || state.view !== "drop") return;
    const filter = window.LouderrPersonalFilter;
    const visible = filter
      ? items.filter((item) => !filter.match(item))
      : items;
    if (!visible.length) return;

    const section = create("section", "related-drops");
    section.id = "related-drops";
    section.setAttribute("aria-labelledby", "related-drops-title");
    const heading = create("h2", "related-drops-title", "Similar wording");
    heading.id = "related-drops-title";
    section.append(heading);
    const list = create("ul", "related-drops-list");
    for (const item of visible) {
      const row = create("li", "related-drops-row");
      const link = create("a", "related-drops-link");
      link.href = `/d/${encodeURIComponent(item.id)}`;
      const caption = String(item.body || "").trim().split(/\r?\n/)[0].slice(0, 90);
      link.append(create("span", "related-drops-heading", item.title || caption || "Drop"));
      link.append(create("span", "related-drops-author", item.author_name || ""));
      link.addEventListener("click", (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey
          || event.shiftKey || event.altKey) return;
        event.preventDefault();
        history.pushState({}, "", link.getAttribute("href"));
        routeLocation();
      });
      row.append(link);
      list.append(row);
    }
    section.append(list);
    // The conversation belongs to this post; suggestions follow it in reading
    // and keyboard order on both stacked and side-by-side layouts.
    const conversation = document.getElementById("drop-comments-inline");
    (conversation || el.feed).insertAdjacentElement("afterend", section);
  }

  function flowStageLabel(stage) {
    return {
      guaranteed_exposure: "Fair start",
      rising: "Rising",
      trending: "Trending",
      global: "Global now",
      new: "New",
    }[stage] || "Public Flow";
  }

  function primaryVideo(drop) {
    const items = Array.isArray(drop?.media) ? drop.media : [];
    return items.find((item) => item?.media_type?.startsWith("video/") && item.media_url)
      || (drop?.media_type?.startsWith("video/") && drop.media_url
        ? { media_url: drop.media_url, thumbnail_url: drop.thumbnail_url, media_alt_text: drop.media_alt_text,
          captions_url: drop.captions_url, transcript_url: drop.transcript_url }
        : null);
  }

  function flowMediaRoot() {
    if (state.view !== "flow") return null;
    return { feed: el.flowFeed, now: el.flowNow, stories: el.flowStories }[state.flow.view] || null;
  }

  function flowPreviewVisible(video) {
    const root = flowMediaRoot();
    if (!video.isConnected || !root?.contains(video)) return false;
    const bounds = root.getBoundingClientRect(), rect = video.getBoundingClientRect();
    return rect.bottom > Math.max(0, bounds.top) - 100
      && rect.top < Math.min(innerHeight, bounds.bottom) + 100;
  }

  function attachFlowVideoSource(video) {
    if (globalThis.louderrSoundPlayback?.canLoad(video) === false) return false;
    video.flowMediaSource = globalThis.louderrSoundPlayback?.source(video) || video.flowMediaSource;
    if (video.flowNeedsRetry) return false;
    if (!video.getAttribute("src") && video.flowMediaSource) {
      video.preload = "metadata";
      video.src = video.flowMediaSource;
    }
    return Boolean(video.getAttribute("src"));
  }

  function drainFlowPreviews() {
    if (state.flow.previewActive) return;
    let video;
    while ((video = state.flow.previewQueue.shift())) {
      video.flowPreviewQueued = false;
      if (flowPreviewVisible(video) && !video.flowPreviewAttempted && !video.flowNeedsRetry && !video.error && video.readyState < 2) break;
      video = null;
    }
    if (!video) return;
    video.flowPreviewAttempted = true;
    let timer, finished = false;
    const finish = (reason) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      video.removeEventListener("loadeddata", loaded);
      video.removeEventListener("error", failed);
      if (reason === "cancel" || reason === "timeout") {
        video.flowPreviewAttempted = reason === "timeout";
        if (video.readyState < 2) {
          video.pause();
          video.preload = "none";
          video.removeAttribute("src");
          video.load();
        }
        if (reason === "timeout") video.flowLoadFailed?.();
      }
      if (state.flow.previewActive?.video === video) state.flow.previewActive = null;
      drainFlowPreviews();
    };
    const loaded = () => finish("loaded"), failed = () => finish("error");
    state.flow.previewActive = { video, cancel: () => finish("cancel") };
    video.addEventListener("loadeddata", loaded);
    video.addEventListener("error", failed);
    timer = setTimeout(() => finish("timeout"), 15000);
    // Assigning src can start native loading even with preload="none" (WebKit).
    // Bind it only when needed, without resetting an existing native play request.
    attachFlowVideoSource(video);
    if (video.readyState >= 2) loaded();
  }

  function queueFlowPreview(video) {
    if (video.flowPreviewQueued || video.flowPreviewAttempted || video.flowNeedsRetry || video.error || video.readyState >= 2) return;
    video.flowPreviewQueued = true;
    state.flow.previewQueue.push(video);
    drainFlowPreviews();
  }

  function observeFlowPreviews() {
    state.flow.previewObserver?.disconnect();
    const root = flowMediaRoot();
    if (!root || state.flow.view === "now") return;
    if (window.louderrVideo && !window.louderrVideo.canAutoplay()) return;
    if (!("IntersectionObserver" in window)) {
      const first = $("video", root);
      if (first) queueFlowPreview(first);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (flowMediaRoot() !== root || state.flow.previewObserver !== observer) return;
      for (const entry of entries) if (entry.isIntersecting) queueFlowPreview(entry.target);
    }, { root: null, rootMargin: "100px 0px", threshold: 0 });
    state.flow.previewObserver = observer;
    $$("video", root).forEach((video) => observer.observe(video));
  }

  function bindFlowVideoRecovery(video, visual, title) {
    const failure = create("div", "flow-media-error"); failure.hidden = true;
    const message = create("p"); message.setAttribute("role", "status");
    const retry = create("button", "text-button flow-media-retry", "Retry video"); retry.type = "button";
    retry.setAttribute("aria-label", `Retry video ${title}`);
    failure.append(message, retry); visual.append(failure);
    let busy = false, retryAt = 0, retryTimer = null, loadTimer = null;
    const updateRetry = () => {
      clearTimeout(retryTimer);
      const remaining = Math.max(0, retryAt - Date.now());
      retry.setAttribute("aria-disabled", String(busy || remaining > 0));
      retry.setAttribute("aria-busy", String(busy));
      retry.textContent = busy ? "Loading video" : remaining ? `Retry in ${Math.ceil(remaining / 1000)}s` : "Retry video";
      if (remaining && video.isConnected) retryTimer = setTimeout(updateRetry, Math.min(1000, remaining));
    };
    const failed = () => {
      clearTimeout(loadTimer);
      busy = false; video.flowNeedsRetry = true;
      retryAt = Date.now() + 5000;
      message.textContent = "Video couldn't load.";
      failure.hidden = false;
      updateRetry();
    };
    video.flowLoadFailed = failed;
    video.addEventListener("error", failed);
    video.addEventListener("loadeddata", () => {
      clearTimeout(loadTimer); clearTimeout(retryTimer);
      busy = false; retryAt = 0; video.flowNeedsRetry = false;
      if (document.activeElement === retry && flowMediaRoot()?.contains(video)) video.focus({ preventScroll: true });
      failure.hidden = true;
      updateRetry();
    });
    const retryVideo = async () => {
      if (busy || Date.now() < retryAt || !video.isConnected || !flowMediaRoot()?.contains(video)) return;
      if (globalThis.louderrSoundPlayback && !await globalThis.louderrSoundPlayback.beforePlay(video, true)) return;
      busy = true; message.textContent = "Loading video…"; failure.hidden = false;
      updateRetry();
      video.flowNeedsRetry = false;
      const hadSource = Boolean(video.getAttribute("src"));
      attachFlowVideoSource(video);
      if (hadSource) video.load();
      loadTimer = setTimeout(() => {
        busy = false; updateRetry();
        if (video.isConnected && flowMediaRoot()?.contains(video) && video.readyState < 2) {
          video.pause(); video.preload = "none"; video.removeAttribute("src"); video.load(); failed();
        }
      }, 15000);
      try { await video.play(); }
      catch (_) {
        if (video.isConnected && flowMediaRoot()?.contains(video)) failed();
        else { clearTimeout(loadTimer); busy = false; updateRetry(); }
      }
    };
    retry.addEventListener("click", retryVideo);
    return retryVideo;
  }

  function setFlowActionCount(button, label, count) {
    const value = formatCount(count || 0);
    button.setAttribute("aria-label", `${label} ${value}`);
    button.title = label;
    $("span", button).textContent = value;
  }

  function makeFlowCard(drop, options = {}) {
    const card = create("article", `flow-card${options.now ? " is-now" : ""}`);
    card.flowContentKey = flowContentKey(drop);
    card.soundPlaybackDrop = JSON.parse(JSON.stringify(drop));
    card.dataset.dropId = drop.id;
    card.dataset.authorId = drop.author_id;
    card.dataset.integritySurface = options.now ? "now" : options.story ? "story" : "flow";
    if (drop.allocation_id) card.dataset.allocationId = drop.allocation_id;
    const visual = create("div", "flow-visual");
    const asset = primaryVideo(drop);
    let video = null;
    let textOnly = false;
    if (asset) {
      video = create("video", "flow-video");
      video.preload = options.now ? "metadata" : "none";
      video.flowMediaSource = asset.media_url;
      globalThis.louderrSoundPlayback?.register(video, drop, asset);
      if (options.now) attachFlowVideoSource(video);
      video.addEventListener("pointerdown", () => attachFlowVideoSource(video));
      video.addEventListener("focus", () => attachFlowVideoSource(video));
      video.playsInline = true;
      video.controls = true;
      video.tabIndex = 0;
      video.muted = !options.now;
      video.loop = !options.now && !options.story;
      if (asset.thumbnail_url) video.poster = asset.thumbnail_url;
      video.setAttribute("aria-label", asset.media_alt_text || dropLabel(drop));
      appendMediaCaptions(video, asset, true);
      visual.append(video);
      const retryVideo = bindFlowVideoRecovery(video, visual, drop.title);
      const playback = create("button", "flow-playback");
      playback.type = "button";
      const syncPlayback = () => {
        const action = options.now ? (video.paused && !video.flowSoundPlayPending ? "Play" : "Pause") : (video.muted ? "Sound on" : "Mute");
        playback.textContent = action;
        playback.setAttribute("aria-label", `${action === "Sound on" ? "Turn sound on for" : action} ${dropLabel(drop)}`);
      };
      for (const event of ["play", "pause", "ended", "volumechange"]) video.addEventListener(event, syncPlayback);
      syncPlayback();
      playback.addEventListener("click", async () => {
        if (!video) return;
        if (video.error || video.flowNeedsRetry) { await retryVideo(); syncPlayback(); return; }
        if (options.now && (!video.paused || video.flowSoundPlayPending)) {
          globalThis.louderrSoundPlayback?.cancelPlay(video);
          video.flowSoundPlayAttempt = null; video.flowSoundPlayPending = false;
          video.pause(); syncPlayback(); return;
        }
        if (!options.now) video.muted = !video.muted;
        syncPlayback();
        if (globalThis.louderrSoundPlayback) {
          const attempt = Symbol(); video.flowSoundPlayAttempt = attempt; video.flowSoundPlayPending = true; syncPlayback();
          const allowed = await globalThis.louderrSoundPlayback.beforePlay(video, true);
          if (video.flowSoundPlayAttempt !== attempt) return;
          video.flowSoundPlayPending = false; syncPlayback();
          if (!allowed) return;
        }
        attachFlowVideoSource(video);
        try { await video.play(); } catch (_) { /* Native controls remain available to retry. */ }
        syncPlayback();
      });
      let completed = false;
      video.addEventListener("timeupdate", () => {
        if (completed || !Number.isFinite(video.duration) || video.duration <= 0) return;
        const ratio = video.currentTime / video.duration;
        if (ratio < 0.95) return;
        completed = true;
        track(options.now ? "now_complete" : "flow_completion", drop.id, { completion_percent: 100 });
      });
      visual.append(playback);
      appendMediaTranscript(visual, asset, "flow-transcript");
    } else {
      const mediaItems = Array.isArray(drop.media) ? drop.media : [];
      const fallback = mediaItems.find((item) => item?.media_url)
        || (drop.media_url ? {
          media_url: drop.media_url, media_type: drop.media_type,
          media_alt_text: drop.media_alt_text,
          transcript_url: drop.transcript_url,
        } : null);
      if (fallback?.media_type?.startsWith("image/")) {
        const image = create("img", "flow-image");
        image.src = fallback.media_url;
        image.alt = fallback.media_alt_text || dropLabel(drop);
        visual.append(image);
      } else if (fallback?.media_type?.startsWith("audio/")) {
        const audio = create("audio", "flow-audio");
        audio.src = fallback.media_url;
        audio.controls = true;
        audio.preload = "metadata";
        audio.setAttribute("aria-label", fallback.media_alt_text || dropLabel(drop));
        bindMediaAnalytics(audio, drop.id);
        visual.append(create("div", "flow-audio-art", initials(drop.author.display_name)), audio);
        appendMediaTranscript(visual, fallback, "flow-transcript");
      } else {
        textOnly = true;
        card.classList.add("is-text");
        visual.hidden = true;
      }
    }
    const overlay = create("div", "flow-overlay");
    const context = create("div", "flow-context");
    if (!options.story) {
      const stage = create("span", "flow-stage", options.now
        ? (options.reasonCode === "trending" ? "TRENDING NOW" : "15 SECONDS OF FAME")
        : flowStageLabel(drop.flow_stage));
      context.append(stage);
      if (!options.now && Number.isFinite(Number(drop.global_position))) {
        context.append(create("span", "flow-position", `Public position ${formatCount(drop.global_position)}`));
      }
      if (drop.flow_stage === "rising" && Number.isFinite(Number(drop.stage_rank))) {
        context.append(create("span", "flow-rank", `Rising #${formatCount(drop.stage_rank)} · ${discoveryMovementLabel(drop.movement)}`));
      }
    }
    const author = create("button", "flow-author"); author.type = "button";
    const avatar = create("span", "avatar avatar-small"); styleAvatar(avatar, drop.author);
    const identity = create("span");
    identity.append(accountNameNode(drop.author), create("small", "", `@${drop.author.handle}`));
    author.append(avatar, identity);
    author.addEventListener("click", () => openCreator(drop.author));
    const authorLine = create("div", "flow-author-line");
    authorLine.append(author);
    if (state.viewer && state.viewer.id !== drop.author_id) {
      const follow = create(
        "button",
        `flow-follow${drop.viewer_follows_author ? " is-following" : ""}`,
        drop.viewer_follows_author ? "Following" : "Follow",
      );
      follow.type = "button";
      if (state.followState) paintFollowControl(follow, { ...drop.author, id: drop.author_id }, Boolean(drop.viewer_follows_author));
      follow.addEventListener("click", () => toggleDropAuthorFollow({
        ...drop, viewer_follows_author: follow.classList.contains("is-following"),
      }, follow));
      authorLine.append(follow);
    }
    globalThis.LouderrCoauthors?.appendByline(authorLine, drop, { nameNode: accountNameNode, openProfile: openCreator });
    const title = create("h2");
    title.dir = "auto";
    const titleLink = create(textOnly ? "span" : "a", textOnly ? "flow-text-title" : "flow-drop-link", drop.title);
    const dropUrl = `/d/${encodeURIComponent(drop.id)}`;
    const openPost = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      return openDrop(drop.id);
    };
    if (!textOnly) { titleLink.href = dropUrl; titleLink.addEventListener("click", openPost); }
    title.append(titleLink); title.hidden = !drop.title;
    const body = create("p", "", drop.body);
    body.dir = "auto"; body.hidden = !drop.body;
    const textOpen = textOnly ? create("a", "flow-text-open", "Read full Drop") : null;
    if (textOpen) {
      textOpen.href = dropUrl;
      textOpen.addEventListener("click", openPost);
      textOpen.setAttribute("aria-label", `Read ${dropLabel(drop)}`);
    }
    const translation = translationControl(
      drop.translation,
      { title: drop.title, body: drop.body },
      (fields) => {
        titleLink.textContent = fields.title;
        body.textContent = fields.body; title.hidden = !fields.title; body.hidden = !fields.body;
        if (textOpen) textOpen.setAttribute("aria-label", `Read ${fields.title}`);
      },
    );
    const actions = create("div", "flow-actions");
    actions.setAttribute("role", "group");
    actions.setAttribute("aria-label", "Drop actions");
    const applaud = makeSignalButton(drop, "applaud", "heart", "Applaud");
    const save = makeSignalButton(drop, "save", "bookmark", "Save");
    setFlowActionCount(applaud, "Applaud", drop.reactions?.applaud);
    setFlowActionCount(save, "Save", drop.reactions?.save);
    const share = create("button"); share.type = "button"; share.append(icon("share"), create("span", "", "Share"));
    share.addEventListener("click", (event) => shareDrop(drop, event.currentTarget));
    const comments = create("button"); comments.type = "button";
    comments.dataset.flowComments = "true";
    comments.append(icon("chat"), create("span"));
    setFlowActionCount(comments, "Comments", drop.comment_count);
    comments.addEventListener("click", () => openComments(drop));
    const chat = create("button"); chat.type = "button";
    chat.setAttribute("aria-label", "Live Chat");
    chat.title = "Live Chat";
    chat.append(icon("broadcast"), create("span", "", "Chat"));
    chat.addEventListener("click", () => {
      if (options.now) {
        const panel = $("[data-flow-live-chat]", el.flowNow);
        panel?.scrollIntoView({ block: "nearest", behavior: motionBehavior() });
        $("textarea", panel)?.focus({ preventScroll: true });
        return;
      }
      history.pushState({}, "", "/chat"); setView("chat");
    });
    actions.append(applaud, comments, save);
    const repost = state.socialExtras?.makeRepostButton?.(drop, { flow: true });
    if (repost) actions.append(repost);
    actions.append(share);
    if (options.now) actions.append(chat);
    overlay.append(context, authorLine, title, body);
    if (translation) overlay.append(translation);
    globalThis.LouderrOriginalSounds?.appendCredit(overlay, drop, {
      openSource: id => { history.pushState({}, "", `/d/${encodeURIComponent(id)}`); routeLocation(); }, openProfile: openCreator,
    });
    if (textOpen) overlay.append(textOpen);
    card.append(visual, overlay, actions);
    return card;
  }

  function updateMobileChromeSize() {
    for (const [selector, property] of [
      [".mobile-header", "--mobile-header-height"],
      [".mobile-bottom-nav", "--mobile-nav-height"],
    ]) {
      const height = $(selector)?.getBoundingClientRect().height || 0;
      const value = `${Math.ceil(height)}px`;
      if (document.documentElement.style.getPropertyValue(property) !== value) {
        document.documentElement.style.setProperty(property, value);
      }
    }
  }

  function flowContentKey(drop) {
    return JSON.stringify({
      title: drop.title, body: drop.body, kind: drop.kind,
      author: drop.author, translation: drop.translation, sound: drop.sound, video_remix: drop.video_remix,
      media: drop.media, media_url: drop.media_url, media_type: drop.media_type,
      thumbnail_url: drop.thumbnail_url, media_alt_text: drop.media_alt_text,
      captions_url: drop.captions_url, transcript_url: drop.transcript_url,
      flow_stage: drop.flow_stage, global_position: drop.global_position,
      stage_rank: drop.stage_rank, movement: drop.movement,
    });
  }

  function refreshFlowControls() {
    const view = state.flow.view || "feed";
    const items = view === "now" ? [state.flow.now?.current?.drop]
      : view === "stories" ? state.flow.stories.map((story) => story.drop) : state.flow.items;
    const drops = new Map(items.filter(Boolean).map((drop) => [drop.id, drop]));
    const root = { feed: el.flowFeed, now: el.flowNow, stories: el.flowStories, history: el.flowHistory }[view] || el.flowSection;
    const cards = $$(".flow-card", root);
    // A changed caption, author or media still requires the normal content refresh.
    // In particular, never retain an old media element after media was removed.
    if (cards.some((card) => drops.has(card.dataset.dropId)
      && card.flowContentKey !== flowContentKey(drops.get(card.dataset.dropId)))) {
      renderFlow();
      return;
    }
    for (const card of cards) {
      const drop = drops.get(card.dataset.dropId);
      if (!drop) continue;
      globalThis.LouderrCoauthors?.appendByline($(".flow-author-line", card), drop, { nameNode: accountNameNode, openProfile: openCreator });
      if (drop.allocation_id) card.dataset.allocationId = drop.allocation_id;
      else delete card.dataset.allocationId;
      for (const button of $$("[data-signal]", card)) {
        const kind = button.dataset.signal;
        const key = kind === "applaud" ? "applauded" : kind === "save" ? "saved" : "nominated";
        const active = Boolean(drop.viewer?.[key]);
        button.classList.toggle("is-reacted", active);
        button.setAttribute("aria-pressed", String(active));
        const label = { applaud: "Applaud", save: "Save", nominate: "Nominate" }[kind];
        setFlowActionCount(button, label, drop.reactions?.[kind]);
      }
      const follow = $(".flow-follow", card);
      if (follow) {
        if (state.followState) paintFollowControl(follow, { ...drop.author, id: drop.author_id }, Boolean(drop.viewer_follows_author));
        else {
          follow.classList.toggle("is-following", Boolean(drop.viewer_follows_author));
          follow.textContent = drop.viewer_follows_author ? "Following" : "Follow";
          follow.disabled = false;
        }
      }
      const comments = $("[data-flow-comments]", card);
      if (comments) setFlowActionCount(comments, "Comments", drop.comment_count);
      state.socialExtras?.refreshRepostButton?.($("[data-repost]", card), drop);
    }
  }

  function stopFlowTimers({ keepAudience = false } = {}) {
    clearInterval(state.flow.countdownTimer);
    if (!keepAudience) state.flow.audienceChannel?.stop();
    clearTimeout(state.flow.refreshTimer);
    clearTimeout(state.flow.eventRefreshTimer);
    clearTimeout(state.flow.storyExpiryTimer);
    state.flow.storyExpiryTimer = null;
    state.flow.storyOpenObserver?.disconnect();
    state.flow.storyOpenObserver = null;
    state.flow.countdownTimer = null;
    state.flow.refreshTimer = null;
    state.flow.eventRefreshTimer = null;
  }

  function observeFlowCards() {
    state.flow.observer?.disconnect();
    if (state.view !== "flow" || state.flow.view !== "feed" || !("IntersectionObserver" in window)) return;
    const scrollsPage = matchMedia("(max-height: 700px)").matches;
    const nav = $(".mobile-bottom-nav");
    const bottomInset = scrollsPage && nav && getComputedStyle(nav).position === "fixed"
      ? Math.max(0, innerHeight - nav.getBoundingClientRect().top) : 0;
    const observed = new Map();
    const observer = new IntersectionObserver((entries) => {
      if (state.view !== "flow" || state.flow.view !== "feed" || state.flow.observer !== observer) return;
      for (const entry of entries) observed.set(entry.target, entry);
      const visible = [...observed.values()]
        .filter((entry) => entry.isIntersecting && entry.intersectionRect.height >= 0.65
          * Math.min(entry.boundingClientRect.height, entry.rootBounds?.height || innerHeight))
        .sort((a, b) => b.intersectionRect.height - a.intersectionRect.height)[0];
      if (!visible) {
        $$(".flow-video", el.flowFeed).forEach((video) => video.pause());
        state.flow.activeDropId = null;
        state.flow.activeVideo = null;
        return;
      }
      const dropId = visible.target.dataset.dropId;
      const selectedVideo = $("video", visible.target);
      const activate = state.flow.activeDropId !== dropId || state.flow.activeVideo !== selectedVideo;
      if (state.flow.activeDropId && state.flow.activeDropId !== dropId) {
        track("flow_swipe", state.flow.activeDropId, { to_drop_id: dropId });
      }
      state.flow.activeDropId = dropId;
      state.flow.activeVideo = selectedVideo;
      $$(".flow-video", el.flowFeed).forEach((item) => {
        if (item === selectedVideo) {
          const autoplay = !window.louderrVideo || window.louderrVideo.canAutoplay();
          if (activate && autoplay) {
            const selected = item;
            const play = () => {
              if (state.flow.activeVideo === selected && selected.isConnected && attachFlowVideoSource(selected)) selected.play().catch(() => {});
            };
            if (globalThis.louderrSoundPlayback) void globalThis.louderrSoundPlayback.beforePlay(selected).then(allowed => { if (allowed) play(); });
            else play();
          }
        }
        else item.pause();
      });
      trackOnce(`flow-open:${dropId}`, "flow_open", dropId, { surface: "flow" });
    }, { root: scrollsPage ? null : el.flowFeed,
      rootMargin: `0px 0px -${bottomInset}px 0px`, threshold: [0, 0.1, 0.25, 0.4, 0.5, 0.65, 0.9, 1] });
    state.flow.observer = observer;
    $$(".flow-card", el.flowFeed).forEach((card) => state.flow.observer.observe(card));
    observeIntegrityCards();
  }

  function renderFlowFeed(append = false) {
    const previousScroll = el.flowFeed.scrollTop;
    const retainedIds = new Set(state.flow.items.map((item) => item.id));
    const existing = new Map($$(".flow-card", el.flowFeed).map((card) => [card.dataset.dropId, card]));
    let removedHeight = 0;
    for (const card of existing.values()) {
      if (retainedIds.has(card.dataset.dropId)) continue;
      if (append && card.offsetTop < previousScroll) removedHeight += card.offsetHeight;
      card.remove();
    }
    for (const [index, item] of state.flow.items.entries()) {
      let card = existing.get(item.id);
      if (!card || card.flowContentKey !== flowContentKey(item)) {
        card?.remove();
        card = makeFlowCard(item);
      }
      const current = $$(".flow-card", el.flowFeed)[index];
      if (current !== card) el.flowFeed.insertBefore(card, current || null);
    }
    refreshFlowControls();
    syncBusinessAds("flow", el.flowFeed, state.flow.items.length);
    el.flowFeed.scrollTop = Math.max(0, previousScroll - removedHeight);
    observeFlowCards();
  }

  function renderFlowPaging() {
    const historyMore = el.flowHistory && $("[data-flow-history-more]", el.flowHistory);
    if (historyMore) {
      historyMore.disabled = state.flow.loading;
      historyMore.setAttribute("aria-busy", String(state.flow.loading));
      historyMore.textContent = state.flow.loading ? "Loading…" : "Load older outcomes";
    }
    el.flowMore.hidden = !state.flow.nextCursor || state.flow.resetRequired;
    // The pull gesture exists only on touch screens, so Refresh stays at the end of a list of videos
    // for pointer and keyboard users (tapping Video again also refreshes). An empty feed shows no strip
    // unless it has something to say: new videos arrived, or the list has to be reloaded.
    el.flowRefresh.hidden = !state.flow.items.length && !state.flow.hasUpdates && !state.flow.resetRequired;
    el.flowPagination.hidden = state.flow.view !== "feed" || (el.flowMore.hidden && el.flowRefresh.hidden);
    el.flowMore.disabled = state.flow.loading;
    el.flowMore.setAttribute("aria-busy", String(state.flow.loading));
    el.flowMore.textContent = state.flow.loading && state.flow.lastAppend ? "Loading more…" : "Load more";
    el.flowRefresh.disabled = state.flow.loading;
    el.flowRefresh.textContent = state.flow.hasUpdates ? "New Drops · refresh" : "Refresh";
    el.flowPageStatus.classList.toggle("is-empty", !state.flow.items.length && !state.flow.loading && !state.flow.resetRequired);
    el.flowPageStatus.textContent = state.flow.resetRequired
      ? "Refresh to load the latest Drops."
      : state.flow.loading && !state.flow.items.length ? "Loading Flow…"
        : state.flow.items.length ? `${formatCount(state.flow.totalPaged || state.flow.items.length)} Drops${state.flow.nextCursor ? "" : " · All caught up"}` : "";
  }

  function acceptFlowPage(result, append = false) {
    const renderWindow = 144;
    const cursorWindow = 6;
    const items = Array.isArray(result.items) ? result.items : [];
    const known = new Set(append ? state.flow.items.map((item) => item.id) : []);
    const merged = append
      ? [...new Map([...state.flow.items, ...items].map((item) => [item.id, item])).values()]
      : items;
    state.flow.totalPaged = append
      ? (state.flow.totalPaged || state.flow.items.length) + items.filter((item) => !known.has(item.id)).length
      : merged.length;
    state.flow.items = merged.slice(-renderWindow);
    state.flow.nextCursor = result.next_cursor || null;
    state.flow.snapshot = result.snapshot || null;
    state.flow.feedOwner = { viewerId: state.viewer?.id || null, messages: state.messages, csrf: state.csrf,
      capabilityProfile: state.capabilities?.profile, mediaPublication: state.capabilities?.media_publication };
    state.flow.pageCursors = append
      ? [...new Set([...state.flow.pageCursors, result.page_cursor].filter(Boolean))]
      : result.page_cursors || [result.page_cursor].filter(Boolean);
    state.flow.pageCursors = state.flow.pageCursors.slice(-cursorWindow);
    state.flow.resetRequired = Boolean(result.reset_required);
    if (!append) state.flow.hasUpdates = false;
  }

  function flowPageUrl(cursor = null) {
    const params = new URLSearchParams({ limit: "24" });
    if (cursor) params.set("cursor", cursor);
    return `/api/flow?${params}`;
  }

  async function revalidateFlowPages(signal) {
    const cursors = [...state.flow.pageCursors];
    if (!cursors.length) return api(flowPageUrl(), { signal });
    const pages = [];
    try {
      // Keep the same snapshot start, but follow its current contiguous page chain.
      // Restored or removed items can shift page boundaries since the previous read.
      let cursor = cursors[0];
      for (let index = 0; index < cursors.length; index += 1) {
        const page = await api(flowPageUrl(cursor), { signal });
        pages.push(page);
        if (!page.next_cursor) break;
        cursor = page.next_cursor;
      }
    } catch (error) {
      if (error.status !== 410 && error.code !== "invalid_flow_cursor") throw error;
      return { items: [], next_cursor: null, page_cursors: [], reset_required: true };
    }
    return {
      ...pages.at(-1),
      items: [...new Map(pages.flatMap((page) => page.items || []).map((item) => [item.id, item])).values()],
      page_cursors: pages.map((page) => page.page_cursor).filter(Boolean),
    };
  }

  function flowRetryDelay(error, fallback = 5000) {
    const raw = String(error?.retryAfter || "").trim();
    const httpDate = /^[A-Z][a-z]{2}, \d{2} [A-Z][a-z]{2} \d{4} \d{2}:\d{2}:\d{2} GMT$/.test(raw);
    const obsolete = raw.match(/^[A-Z][a-z]+, (\d{2})-([A-Z][a-z]{2})-(\d{2}) (\d{2}:\d{2}:\d{2} GMT)$/);
    const asctime = /^[A-Z][a-z]{2} [A-Z][a-z]{2} [ \d]\d \d{2}:\d{2}:\d{2} \d{4}$/.test(raw);
    let date = httpDate ? Date.parse(raw) : asctime ? Date.parse(`${raw} GMT`) : NaN;
    if (obsolete) {
      const currentYear = new Date().getUTCFullYear();
      let year = Math.floor(currentYear / 100) * 100 + Number(obsolete[3]);
      if (year > currentYear + 50) year -= 100;
      date = Date.parse(`${obsolete[1]} ${obsolete[2]} ${year} ${obsolete[4]}`);
    }
    const delay = /^\d+$/.test(raw) ? Number(raw) * 1000 : date - Date.now();
    return Number.isFinite(delay) && delay >= 0
      ? Math.max(1000, Math.min(Number.MAX_SAFE_INTEGER - Date.now(), delay + 250)) : fallback;
  }

  function renderFlowRetry() {
    const button = $("#retry-flow");
    if (!button) return;
    const waiting = (state.flow.view === "now" && Date.now() < (state.flow.nowRetryAt || 0))
      || (state.flow.view === "stories" && performance.now() < (state.flow.storyRetryAt || 0));
    button.disabled = waiting || state.flow.loading;
    button.textContent = waiting ? "Waiting to retry" : "Retry";
  }

  function scheduleNowRefresh(delay = 5000) {
    clearTimeout(state.flow.refreshTimer);
    if (state.view !== "flow" || state.flow.view !== "now") return;
    renderFlowRetry();
    const wait = Math.max(250, delay, (state.flow.nowRetryAt || 0) - Date.now());
    state.flow.refreshTimer = setTimeout(() => {
      state.flow.refreshTimer = null;
      if (state.view !== "flow" || state.flow.view !== "now") return;
      if (document.visibilityState !== "visible" || navigator.onLine === false || state.flow.loading) {
        scheduleNowRefresh(5000); return;
      }
      refreshFlow();
    }, Math.min(2147483647, wait));
  }

  function scheduleNowHeartbeat() {
    if (!state.flow.audienceChannel && globalThis.LouderrNowAudience) {
      const url = new URL("/ws", location.href);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      state.flow.audienceChannel = globalThis.LouderrNowAudience.create({
        url: url.toString(),
        getContext: () => {
          const current = state.flow.now?.current;
          const card = $(".flow-card", el.flowNow);
          const slotKey = current && JSON.stringify([current.slot_id, current.starts_at, current.ends_at, current.reason_code]);
          const eligible = current?.drop && card?.isConnected && card.nowSlotKey === slotKey;
          return {
            identity: state.messages, csrf: state.csrf, viewerId: state.viewer?.id || null,
            active: state.view === "flow" && state.flow.view === "now"
              && document.visibilityState === "visible" && navigator.onLine !== false,
            card: eligible ? card : null, slotId: eligible ? current.slot_id : null,
            browserKey: state.analyticsSession,
          };
        },
        onCount: (count, context) => {
          const node = $("[data-now-audience]", context.card);
          if (node) node.textContent = `~${formatCount(count)} watching`;
        },
        onUnavailable: (context) => {
          const node = $("[data-now-audience]", context.card);
          if (node && node.textContent !== "Audience unavailable") node.textContent = "Audience unavailable";
        },
      });
    }
    state.flow.audienceChannel?.start();
  }

  function flowChatMessageNode(message) {
    const row = create("article", "flow-chat-message");
    row.dataset.chatMessageId = message.id;
    const author = accountNameNode(message.author, "button", "flow-chat-author");
    author.type = "button";
    author.addEventListener("click", () => openCreator(message.author));
    const time = create("time", "", chatTime(message.created_at));
    time.dateTime = message.created_at;
    const meta = create("header"); meta.append(author, time);
    const body = create("p");
    if (message.deleted) body.textContent = message.removed_by_moderation ? "Removed by moderation" : "Message deleted";
    else appendChatText(body, message.body);
    row.append(meta, body);
    return row;
  }

  async function submitFlowChat(event) {
    event.preventDefault();
    if (!requireVerified()) return;
    const context = chatContext();
    const form = event.currentTarget;
    const textarea = $("textarea", form);
    const button = $("button[type='submit']", form);
    const errorNode = $("[data-flow-chat-error]", form);
    const original = textarea.value;
    const body = original.trim();
    if (!body || button.disabled || state.sessionRestricted || state.viewer.status !== "active") return;
    errorNode.hidden = true;
    button.disabled = true;
    try {
      const message = await api("/api/chat/messages?room=global", {
        method: "POST", csrf: true, json: { body, reply_to_id: null },
      });
      if (!context.current()) return;
      if (message?.accepted === true && message.status === "pending_review"
        && /^[0-9a-f-]{36}$/i.test(message.quarantine_id || "") && /^[0-9a-f-]{36}$/i.test(message.decision_id || "")) {
        if (textarea.value === original) {
          textarea.value = "";
          if (state.flow.chatDraft === original) state.flow.chatDraft = "";
        }
        toast("Message submitted for review. It is not visible in Global yet.");
        return;
      }
      if (typeof message?.id !== "string" || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(message.id)
        || message.author?.id !== context.viewerId || typeof message.body !== "string"
        || !Number.isFinite(Date.parse(message.created_at)) || (message.room || "global") !== "global") {
        throw new Error("Delivery was not confirmed. Your draft is kept; check Global before retrying.");
      }
      appendFlowChat(message);
      if (chatWorkspace().room === "global" && state.chat.lens === "all") appendBounded(state.chat.items, message, 500);
      if (textarea.value === original) {
        textarea.value = "";
        if (state.flow.chatDraft === original) state.flow.chatDraft = "";
      }
      if (!textarea.value) renderFlowLiveChat();
      track("chat_message", null, { message_id: message.id, surface: "now" });
    } catch (error) {
      if (!context.current()) return;
      errorNode.textContent = error.message;
      errorNode.hidden = false;
    } finally { if (context.current()) button.disabled = false; }
  }

  function renderFlowLiveChat() {
    const panel = $("[data-flow-live-chat]", el.flowNow);
    if (!panel) return;
    const readStatus = $("[data-flow-chat-status]", panel);
    if (readStatus) {
      readStatus.hidden = !["loading", "unavailable"].includes(state.flow.chatReadStatus);
      readStatus.textContent = state.flow.chatReadStatus === "unavailable"
        ? "Chat could not be refreshed. Open full Chat to try again." : "Loading chat…";
    }
    const list = $("[data-flow-chat-list]", panel);
    const atBottom = !list.dataset.loaded || list.scrollHeight - list.clientHeight - list.scrollTop < 48;
    const scrollTop = list.scrollTop;
    const anchor = [...list.children].find((node) => node.offsetTop + node.offsetHeight > scrollTop);
    const anchorOffset = anchor ? scrollTop - anchor.offsetTop : 0;
    clear(list);
    for (const message of state.flow.chatItems.slice(-40)) list.append(flowChatMessageNode(message));
    const restored = anchor && [...list.children].find((node) => node.dataset.chatMessageId === anchor.dataset.chatMessageId);
    list.scrollTop = atBottom ? list.scrollHeight : restored ? restored.offsetTop + anchorOffset : scrollTop;
    list.dataset.loaded = "true";
    const composer = $("[data-flow-chat-composer]", panel);
    const owner = state.viewer?.id || null;
    if (state.flow.chatDraftOwner !== owner) {
      state.flow.chatDraft = "";
      state.flow.chatDraftOwner = owner;
    }
    // Keep the live form, selection, focus, pending submission and error intact.
    if (state.viewer?.email_verified && composer.dataset.owner === owner && $("textarea", composer)) return;
    clear(composer);
    composer.dataset.owner = owner || "";
    if (!state.viewer?.email_verified) {
      const join = create("button", "secondary-button", state.viewer ? "Verify to join" : "Sign in to join");
      join.type = "button";
      join.addEventListener("click", () => openDialog(el.account));
      composer.append(join);
      return;
    }
    const form = create("form", "flow-chat-form");
    form.dataset.flowChatComposer = "true";
    const textarea = create("textarea");
    textarea.rows = 1;
    textarea.maxLength = 700;
    textarea.value = state.flow.chatDraft || "";
    textarea.placeholder = "Message everyone watching";
    textarea.setAttribute("aria-label", "Live Chat message");
    const send = create("button"); send.type = "submit"; send.setAttribute("aria-label", "Send to Live Chat"); send.append(icon("send"));
    const errorNode = create("div", "form-error"); errorNode.dataset.flowChatError = "true"; errorNode.hidden = true;
    form.append(textarea, send, errorNode);
    form.addEventListener("submit", submitFlowChat);
    textarea.addEventListener("input", () => {
      if (state.flow.chatDraftOwner === owner) state.flow.chatDraft = textarea.value;
    });
    textarea.addEventListener("keydown", (event) => {
      if (event.isComposing || event.keyCode === 229) return;
      if (event.key === "Enter" && !event.shiftKey && (event.ctrlKey || event.metaKey)) {
        event.preventDefault(); form.requestSubmit();
      }
    });
    composer.append(form);
  }

  function renderFlowNow(entering = false) {
    const payload = state.flow.now;
    const current = payload?.current;
    const audienceText = payload?.watching_available !== false && Number.isSafeInteger(payload?.watching) && payload.watching >= 0
      ? `~${formatCount(payload.watching)} watching` : "Audience unavailable";
    const retainedChat = $("[data-flow-live-chat]", el.flowNow);
    const previousCard = $(".flow-card", el.flowNow);
    const slotKey = current && JSON.stringify([current.slot_id, current.starts_at, current.ends_at, current.reason_code]);
    const retainCard = !entering && current?.drop && previousCard?.nowSlotKey === slotKey
      && previousCard.flowContentKey === flowContentKey(current.drop);
    const focused = document.activeElement;
    const restoreFocus = retainedChat?.contains(focused);
    if (!retainCard) {
      $$("video, audio", el.flowNow).forEach((media) => media.pause());
      clear(el.flowNow);
    }
    stopFlowTimers({ keepAudience: true });
    const serverNow = new Date(payload?.server_time).getTime();
    const endsAt = new Date(current?.ends_at).getTime();
    const remaining = endsAt - serverNow;
    scheduleNowRefresh(Number.isFinite(remaining) && remaining > 0 ? remaining + 250 : 5000);
    if (!current) {
      const empty = create("div", "flow-now-empty");
      empty.append(create("strong", "", "Between moments"), create("p", "", "The next eligible creator will appear here for everyone at the same time."));
      el.flowNow.append(empty);
      scheduleNowHeartbeat();
      return;
    }
    if (!current.drop) {
      const hidden = create("div", "flow-now-empty");
      hidden.append(create("strong", "", "This moment is hidden by your controls"), create("p", "", "The global slot continues unchanged for everyone else."));
      el.flowNow.append(hidden);
      scheduleNowHeartbeat();
      return;
    }
    let shell = $(".flow-now-stage", el.flowNow);
    let card = previousCard;
    if (!retainCard) {
      shell = create("section", "flow-now-stage");
      const live = create("div", "flow-now-live");
      live.append(
        create("span", "flow-live-label", "NOW · LIVE"),
        create("strong", "flow-now-countdown", "00:15"),
        create("span", "flow-now-audience", audienceText),
      );
      live.lastChild.dataset.nowAudience = "true";
      card = makeFlowCard(current.drop, { now: true, reasonCode: current.reason_code });
      card.nowSlotKey = slotKey;
      const chatPanel = create("aside", "flow-live-chat");
      chatPanel.dataset.flowLiveChat = "true";
      const chatHeader = create("header");
      const chatCopy = create("div"); chatCopy.append(create("strong", "", "Live Chat"), create("small", "", "The public room, synchronized with NOW."));
      const openChat = create("button", "text-button", "Open full Chat"); openChat.type = "button";
      openChat.addEventListener("click", () => { history.pushState({}, "", "/chat"); setView("chat"); });
      chatHeader.append(chatCopy, openChat);
      const chatStatus = create("p", "metric-note"); chatStatus.dataset.flowChatStatus = "true";
      chatStatus.setAttribute("role", "status"); chatStatus.hidden = true;
      const chatList = create("div", "flow-chat-list"); chatList.dataset.flowChatList = "true"; chatList.setAttribute("role", "log"); chatList.setAttribute("aria-live", "polite");
      const chatComposer = create("div", "flow-chat-composer"); chatComposer.dataset.flowChatComposer = "true";
      chatPanel.append(chatHeader, chatStatus, chatList, chatComposer);
      card.append(live);
      shell.append(card, retainedChat || chatPanel);
      el.flowNow.append(shell);
    } else {
      $("[data-now-audience]", card).textContent = audienceText;
      refreshFlowControls();
    }
    renderFlowLiveChat();
    if (restoreFocus && focused.isConnected) focused.focus({ preventScroll: true });
    const video = $("video", card);
    const startsAt = new Date(current.starts_at).getTime();
    const clockOffset = Date.now() - serverNow;
    const synchronize = () => {
      if (state.view !== "flow" || state.flow.view !== "now" || document.visibilityState !== "visible" || !video?.isConnected
        || state.flow.now?.current?.slot_id !== current.slot_id) return;
      if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;
      const elapsed = Math.max(0, (Date.now() - clockOffset - startsAt) / 1000);
      video.currentTime = Math.min(Math.max(0, video.duration - 0.05), elapsed);
      if (globalThis.louderrSoundPlayback) {
        void globalThis.louderrSoundPlayback.beforePlay(video).then(allowed => {
          if (allowed && video.isConnected && state.view === "flow" && state.flow.view === "now"
              && state.flow.now?.current?.slot_id === current.slot_id) video.play().catch(() => {});
        });
      } else video.play().catch(() => {});
    };
    if (video && !retainCard) {
      video.addEventListener("loadedmetadata", synchronize);
      if (video.readyState >= 1) synchronize();
    }
    const update = () => {
      const remaining = Math.max(0, endsAt - (Date.now() - clockOffset));
      $(".flow-now-countdown", shell).textContent = `00:${String(Math.ceil(remaining / 1000)).padStart(2, "0")}`;
    };
    update();
    state.flow.countdownTimer = setInterval(update, 200);
    scheduleNowHeartbeat();
    trackOnce(`now-open:${current.slot_id}`, "now_open", current.drop.id, { slot_id: current.slot_id });
    observeIntegrityCards();
  }

  // Telling the author somebody watched is a side effect of watching, so a failure
  // here is silent: it must never interrupt the person looking at the story.
  function markStorySeen(story) {
    if (!state.viewer || !story || story.viewer_can_delete) return;
    state.flow.seenStories = state.flow.seenStories || new Set();
    // Per account: another profile opening the same Story in this tab is another viewer.
    const key = `${state.viewer.id}:${story.id}`;
    if (state.flow.seenStories.has(key)) return;
    state.flow.seenStories.add(key);
    void api(`/api/flow/stories/${encodeURIComponent(story.id)}/seen`, { method: "POST", csrf: true })
      .catch(() => { state.flow.seenStories.delete(key); });
  }

  async function openStoryViewers(story) {
    const dialog = create("dialog", "small-dialog story-viewers-dialog");
    const heading = create("h2", "", "Who has seen this");
    heading.id = "story-viewers-title";
    dialog.setAttribute("aria-labelledby", heading.id);
    const list = create("div", "story-viewers-list");
    const status = create("p", "", "Loading\u2026");
    status.setAttribute("role", "status");
    const close = create("button", "secondary-button", "Close");
    close.type = "button";
    close.addEventListener("click", () => dialog.close());
    dialog.append(heading, status, list, close);
    dialog.addEventListener("close", () => dialog.remove());
    document.body.append(dialog);
    openDialog(dialog);
    try {
      const result = await api(`/api/flow/stories/${encodeURIComponent(story.id)}/viewers`);
      if (!dialog.isConnected) return;
      const total = Number(result.total) || 0;
      status.textContent = total
        ? `${formatCount(total)} ${total === 1 ? "person has" : "people have"} seen this`
        : "Nobody has opened this yet.";
      for (const item of result.items || []) {
        const row = create("div", "story-viewer-row");
        const avatar = create("span", "avatar avatar-small");
        styleAvatar(avatar, item.viewer);
        row.append(avatar, accountNameNode(item.viewer),
          create("span", "story-viewer-when", relativeTime(item.first_seen_at, storyServerTime())));
        list.append(row);
      }
      // The count can exceed the names: somebody blocked is counted, never named.
      if (total > (result.items || []).length) {
        list.append(create("p", "story-viewers-note",
          result.has_more ? "Only the most recent are listed." : "Some viewers are not shown."));
      }
    } catch (error) {
      if (dialog.isConnected) status.textContent = error.message || "That could not be loaded.";
    }
  }

  async function removeStoryFromFlow(storyId) {
    try {
      await api(`/api/flow/stories/${encodeURIComponent(storyId)}`, { method: "DELETE", csrf: true });
      state.flow.stories = state.flow.stories.filter((item) => item.id !== storyId);
      state.flow.storyPages = (state.flow.storyPages || []).map((page) => ({
        ...page, items: page.items.filter((item) => item.id !== storyId),
      }));
      if (state.view === "flow" && state.flow.view === "stories") renderFlow();
      toast("Story removed");
    } catch (error) { toast(error.message, true); }
  }

  function currentStorySnapshot() {
    const snapshot = state.flow.storySnapshot;
    return snapshot && snapshot.lens === state.flow.storyLens
      && snapshot.viewerId === (state.viewer?.id || null) && snapshot.socialState === state.messages
      ? snapshot : null;
  }

  function storyServerTime() {
    const snapshot = currentStorySnapshot();
    return snapshot ? snapshot.generatedAt + Math.max(0, performance.now() - snapshot.requestStartedAt) : NaN;
  }

  function acceptStorySnapshot(result, requestStartedAt, append = false) {
    const generatedAt = Date.parse(result?.generated_at);
    if (result?.lens !== state.flow.storyLens || !Array.isArray(result?.items) || !Number.isFinite(generatedAt)
      || !Number.isFinite(requestStartedAt) || requestStartedAt > performance.now()) {
      throw new Error("Stories could not be refreshed. Try again.");
    }
    const previous = currentStorySnapshot() ? state.flow.storyPages || [] : [];
    const incoming = result.pages || [result];
    const paged = Boolean(result.snapshot || result.page_cursor || result.next_cursor || result.pages);
    const cursorValid = (cursor) => typeof cursor === "string" && cursor.length > 0 && cursor.length <= 768;
    if (!Array.isArray(incoming) || !incoming.length || incoming.length > 5
      || (append && incoming.length !== 1)
      || incoming.some((page, index) => page?.lens !== result.lens || !Array.isArray(page.items)
        || page.items.length > 50 || !Number.isFinite(Date.parse(page.generated_at))
        || page.snapshot?.id !== result.snapshot?.id || (page.next_cursor && page.next_cursor === page.page_cursor)
        || (paged && (!cursorValid(page.page_cursor) || !/^[a-f0-9]{64}$/.test(page.snapshot?.id || "")
          || (page.next_cursor !== null && !cursorValid(page.next_cursor))
          || (index > 0 && incoming[index - 1].next_cursor !== page.page_cursor)
          || incoming.slice(0, index).some((prior) => prior.page_cursor === page.page_cursor)
          || incoming.slice(0, index).some((prior) => prior.page_cursor === page.next_cursor))))) {
      throw new Error("Stories could not be refreshed. Try again.");
    }
    if (append && (!previous.length || previous.at(-1).next_cursor !== result.page_cursor
      || !result.page_cursor || previous.at(-1).snapshot?.id !== result.snapshot?.id
      || previous.some((page) => page.page_cursor === result.page_cursor))) {
      throw new Error("Refresh Stories before loading more.");
    }
    const pages = (append ? [...previous, ...incoming] : incoming).slice(-5);
    const elapsed = Math.max(0, performance.now() - requestStartedAt);
    const previousTime = storyServerTime();
    state.flow.storySnapshot = {
      lens: result.lens, generatedAt: Number.isFinite(previousTime)
        ? Math.max(generatedAt, previousTime - elapsed) : generatedAt, requestStartedAt,
      viewerId: state.viewer?.id || null, socialState: state.messages,
    };
    // Charge the full request interval so a delayed response cannot prolong known expiry.
    // This may hide a Story early by request latency; it never proves server eligibility.
    state.flow.storyPages = pages;
    state.flow.storyNextCursor = pages.at(-1).next_cursor || null;
    state.flow.storyHasUpdates = pages.some((page) => page.has_updates);
    state.flow.storyResetRequired = false;
    state.flow.stories = [...new Map(pages.flatMap((page) => page.items).map((story) => [story.id, story])).values()];
    state.flow.storyRetryAt = 0;
    state.flow.storyFailures = 0;
    pruneExpiredStories();
  }

  function pruneExpiredStories() {
    const now = storyServerTime();
    state.flow.stories = state.flow.stories.filter((story) => Date.parse(story.expires_at) > now);
  }

  function storyPageUrl(cursor = null, lens = state.flow.storyLens) {
    const params = new URLSearchParams({ lens, limit: "30" });
    if (cursor) params.set("cursor", cursor);
    return `/api/flow/stories?${params}`;
  }

  async function revalidateStoryPages(signal) {
    if (state.flow.storyResetRequired || performance.now() < (state.flow.storyRetryAt || 0)
      || document.visibilityState !== "visible" || navigator.onLine === false) return null;
    const lens = state.flow.storyLens, viewerId = state.viewer?.id || null;
    const socialState = state.messages, requestId = state.flow.requestId;
    const current = () => state.view === "flow" && state.flow.view === "stories"
      && state.flow.storyLens === lens && (state.viewer?.id || null) === viewerId
      && state.messages === socialState && state.flow.requestId === requestId
      && document.visibilityState === "visible" && navigator.onLine !== false;
    const loaded = currentStorySnapshot() ? [...state.flow.storyPages] : [];
    if (!loaded.length || !state.flow.stories.length || !loaded[0].page_cursor) return api(storyPageUrl(null, lens), { signal });
    const pages = [];
    let cursor = loaded[0].page_cursor;
    for (let index = 0; index < loaded.length; index += 1) {
      const page = await api(storyPageUrl(cursor, lens), { signal });
      if (!current()) return null;
      if (page.page_cursor !== cursor || pages.some((previous) => previous.page_cursor === page.page_cursor)) {
        throw new Error("Stories could not be refreshed. Try again.");
      }
      pages.push(page);
      cursor = page.next_cursor;
      if (!cursor) break;
    }
    return { ...pages.at(-1), generated_at: pages[0].generated_at, pages,
      items: pages.flatMap((page) => page.items) };
  }

  function recordStoryReadFailure(error) {
    el.flowError.hidden = false;
    $("p", el.flowError).textContent = error.message;
    el.flowEmpty.hidden = true;
    if (error.code === "story_snapshot_expired" || error.code === "invalid_story_cursor") {
      state.flow.storyResetRequired = true;
      state.flow.storyNextCursor = null;
      state.flow.storyRetryAt = 0;
    } else {
      state.flow.storyFailures = Math.min(3, (state.flow.storyFailures || 0) + 1);
      state.flow.storyRetryAt = performance.now() + Math.max(30000 * 2 ** (state.flow.storyFailures - 1),
        flowRetryDelay(error, 30000));
    }
  }

  function renderStoryPaging() {
    const loaded = Boolean(currentStorySnapshot());
    const waiting = performance.now() < (state.flow.storyRetryAt || 0);
    const moreFocused = document.activeElement === el.storyMore;
    el.storyPagination.hidden = state.flow.view !== "stories"
      || (!state.flow.stories.length && !el.flowError.hidden);
    el.storyMore.hidden = !loaded || !state.flow.storyNextCursor || state.flow.storyResetRequired;
    el.storyMore.disabled = state.flow.loading || waiting;
    el.storyMore.setAttribute("aria-busy", String(state.flow.loading && state.flow.lastAppend));
    el.storyMore.textContent = state.flow.loading && state.flow.lastAppend ? "Loading more…" : "Load more";
    el.storyRefresh.disabled = state.flow.loading || waiting;
    el.storyRefresh.textContent = loaded && state.flow.storyHasUpdates ? "New Stories · refresh" : "Refresh";
    if (moreFocused && el.storyMore.hidden) el.storyRefresh.focus({ preventScroll: true });
    el.storyPageStatus.textContent = state.flow.storyResetRequired ? "Refresh to continue browsing."
      : waiting ? "Waiting to retry" : !loaded || state.flow.loading ? "Loading Stories…"
        : state.flow.storyHasUpdates ? "New Stories available."
          : state.flow.stories.length ? `${formatCount(state.flow.stories.length)} Stories${state.flow.storyNextCursor ? "" : " · All caught up"}` : "";
  }

  function scheduleStoryExpiry() {
    clearTimeout(state.flow.storyExpiryTimer);
    state.flow.storyExpiryTimer = null;
    const snapshot = currentStorySnapshot();
    if (state.view !== "flow" || state.flow.view !== "stories" || !snapshot || !state.flow.stories.length) return;
    const expiresAt = Math.min(...state.flow.stories.map((story) => Date.parse(story.expires_at)));
    const timer = setTimeout(() => {
      if (state.flow.storyExpiryTimer !== timer) return;
      state.flow.storyExpiryTimer = null;
      if (state.view !== "flow" || state.flow.view !== "stories" || currentStorySnapshot() !== snapshot) return;
      renderFlow();
    }, Math.max(20, Math.min(2147483647, expiresAt - storyServerTime())));
    state.flow.storyExpiryTimer = timer;
  }

  function scheduleStoriesRefresh(delay = 30000) {
    clearTimeout(state.flow.refreshTimer);
    state.flow.refreshTimer = null;
    if (state.view !== "flow" || state.flow.view !== "stories"
      || document.visibilityState !== "visible" || navigator.onLine === false || state.flow.storyResetRequired) return;
    const lens = state.flow.storyLens, viewerId = state.viewer?.id || null, socialState = state.messages;
    renderFlowRetry();
    const wait = Math.max(250, delay, (state.flow.storyRetryAt || 0) - performance.now());
    const timer = setTimeout(() => {
      if (state.flow.refreshTimer !== timer) return;
      state.flow.refreshTimer = null;
      if (state.view !== "flow" || state.flow.view !== "stories" || state.flow.storyLens !== lens
        || (state.viewer?.id || null) !== viewerId || state.messages !== socialState) return;
      if (document.visibilityState !== "visible" || navigator.onLine === false) return;
      if (state.flow.loading) { scheduleStoriesRefresh(); return; }
      refreshFlow(false, true);
    }, Math.min(2147483647, wait));
    state.flow.refreshTimer = timer;
  }

  function observeStoryOpens() {
    state.flow.storyOpenObserver?.disconnect();
    state.flow.storyOpenObserver = null;
    const snapshot = currentStorySnapshot();
    if (state.view !== "flow" || state.flow.view !== "stories" || !snapshot
      || document.visibilityState !== "visible" || !("IntersectionObserver" in window)) return;
    const nav = $(".mobile-bottom-nav"), bounds = nav?.getBoundingClientRect();
    const inset = bounds?.height > 0 && getComputedStyle(nav).position === "fixed"
      ? Math.max(0, innerHeight - bounds.top) : 0;
    const publications = new Map(state.flow.stories.map((story) => [story.id, story]));
    const observer = new IntersectionObserver((entries) => {
      if (state.flow.storyOpenObserver !== observer || currentStorySnapshot() !== snapshot
        || state.view !== "flow" || state.flow.view !== "stories" || document.visibilityState !== "visible") return;
      for (const entry of entries) {
        const story = publications.get(entry.target.dataset.storyId);
        if (!entry.target.isConnected || !entry.isIntersecting || entry.intersectionRatio < 0.5
          || !story || Date.parse(story.expires_at) <= storyServerTime()) continue;
        trackOnce(`story-open:${snapshot.viewerId || "guest"}:${story.id}:${story.created_at}`, "story_open", story.drop.id, { lens: snapshot.lens });
        markStorySeen(story);
        observer.unobserve(entry.target);
      }
    }, { root: null, rootMargin: `0px 0px -${inset}px 0px`, threshold: [0, 0.5] });
    state.flow.storyOpenObserver = observer;
    $$(".story-card", el.flowStories).forEach((card) => observer.observe(card));
  }

  function observeFlowStories() {
    if (state.view !== "flow" || state.flow.view !== "stories" || !("IntersectionObserver" in window)) return;
    state.flow.observer?.disconnect();
    const nav = $(".mobile-bottom-nav");
    const navBounds = nav?.getBoundingClientRect();
    const bottomInset = navBounds?.height > 0 && getComputedStyle(nav).position === "fixed"
      ? Math.max(0, innerHeight - navBounds.top) : 0;
    const observer = new IntersectionObserver((entries) => {
      if (state.view !== "flow" || state.flow.view !== "stories" || state.flow.observer !== observer) return;
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRect.height <= 0 || entry.intersectionRect.width <= 0) {
          entry.target.pause();
        }
      }
    }, { root: null, rootMargin: `0px 0px -${bottomInset}px 0px`, threshold: 0 });
    state.flow.observer = observer;
    $$("video, audio", el.flowStories).forEach((media) => observer.observe(media));
  }

  function renderFlowStories() {
    const existing = new Map($$(".story-card", el.flowStories).map((wrap) => [wrap.dataset.storyId, wrap]));
    const retainedIds = new Set(state.flow.stories.map((story) => story.id));
    const anchor = [...existing.values()].find((wrap) => retainedIds.has(wrap.dataset.storyId)
      && wrap.getBoundingClientRect().bottom > Math.max(0, el.flowStories.getBoundingClientRect().top));
    const anchorTop = anchor?.getBoundingClientRect().top;
    if (el.storyPagination.parentNode !== el.flowStories) el.flowStories.append(el.storyPagination);
    for (const wrap of existing.values()) {
      if (retainedIds.has(wrap.dataset.storyId)) continue;
      $$("video, audio", wrap).forEach((media) => media.pause());
      wrap.remove();
    }
    for (const [index, story] of state.flow.stories.entries()) {
      const contentKey = JSON.stringify([flowContentKey(story.drop), story.expires_at, Boolean(story.viewer_can_delete)]);
      let wrap = existing.get(story.id);
      if (!wrap || wrap.storyContentKey !== contentKey) {
        if (wrap) {
          $$("video, audio", wrap).forEach((media) => media.pause());
          wrap.remove();
        }
        wrap = create("article", "story-card");
        wrap.dataset.storyId = story.id;
        wrap.storyContentKey = contentKey;
        wrap.storyPlaybackMetadata = [story.expires_at, Boolean(story.viewer_can_delete)];
        wrap.append(makeFlowCard(story.drop, { story: true }));
        const expiry = create("footer", "story-footer");
        const expires = create("span"); expires.dataset.storyExpiry = "true";
        expiry.append(expires);
        if (story.viewer_can_delete) {
          const seen = create("button", "text-button", "Seen by"); seen.type = "button";
          seen.addEventListener("click", () => openStoryViewers(story));
          expiry.append(seen);
          const remove = create("button", "text-button", "Remove"); remove.type = "button";
          remove.addEventListener("click", () => removeStoryFromFlow(story.id));
          expiry.append(remove);
        } else if (state.viewer && story.drop?.author_id !== state.viewer.id) {
          // Replying to a story is sending its Drop into a conversation, which the
          // send-a-Drop path already does, audience rules and all.
          const reply = create("button", "text-button", "Reply"); reply.type = "button";
          reply.addEventListener("click", () => openSendDrop(story.drop));
          expiry.append(reply);
        }
        wrap.append(expiry);
      }
      const current = $$(".story-card", el.flowStories)[index];
      if (current !== wrap) el.flowStories.insertBefore(wrap, current || el.storyPagination);
      $("[data-story-expiry]", wrap).textContent = `Expires ${relativeTime(story.expires_at, storyServerTime())}`;
    }
    if (state.flow.storyResetScroll) {
      el.flowStories.scrollTop = 0;
      if (el.flowStories.getBoundingClientRect().top < 0) el.flowStories.scrollIntoView({ block: "start" });
      state.flow.storyResetScroll = false;
    } else if (anchor?.isConnected) {
      const delta = anchor.getBoundingClientRect().top - anchorTop;
      if (Math.abs(delta) > 1) {
        const scrollable = ["auto", "scroll"].includes(getComputedStyle(el.flowStories).overflowY)
          && el.flowStories.scrollHeight > el.flowStories.clientHeight;
        if (scrollable) el.flowStories.scrollTop += delta;
        else window.scrollBy(0, delta);
      }
    }
    if (state.flow.view === "stories") refreshFlowControls();
    renderStoryPaging();
    observeFlowStories();
    observeStoryOpens();
    scheduleStoryExpiry();
    scheduleStoriesRefresh();
    observeIntegrityCards();
  }

  function renderFlowHistory() {
    clear(el.flowHistory);
    if (!state.viewer) {
      const gate = create("div", "flow-history-gate");
      gate.append(create("h2", "", "Your 15 seconds"), create("p", "", "Sign in to see your past moments in Now."));
      const signIn = create("button", "publish-button", "Sign in"); signIn.type = "button";
      signIn.addEventListener("click", () => openDialog(el.account));
      gate.append(signIn); el.flowHistory.append(gate); return;
    }
    for (const item of state.flow.history) {
      const metrics = item.metrics || {};
      const row = create("article", "flow-history-row");
      row.append(
        create("div", "flow-history-copy", ""),
        create("strong", "", `${formatCount(metrics.verified_unique_viewers || 0)} verified viewers`),
      );
      const copy = row.firstChild;
      copy.append(create("h3", "", item.title), create("p", "", `${new Date(item.starts_at).toLocaleString()} · ${item.reason_code === "trending" ? "Trending lane" : "15 Seconds of Fame"}`));
      const facts = create("p", "flow-history-facts", `${formatCount(metrics.valid_applause || 0)} applause · ${formatCount(metrics.valid_saves || 0)} saves`);
      row.append(facts); el.flowHistory.append(row);
    }
    if (state.flow.historyCursor) {
      const more = create("button", "secondary-button", state.flow.loading ? "Loading…" : "Load older outcomes");
      more.dataset.flowHistoryMore = "true";
      more.type = "button";
      more.disabled = state.flow.loading;
      more.addEventListener("click", () => refreshFlow(true));
      el.flowHistory.append(more);
    }
  }

  function renderFlowTabs() {
    const view = state.flow.view;
    $$('[data-flow-view]').forEach((button) => {
      const active = button.dataset.flowView === view;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-selected", String(active));
      button.tabIndex = active ? 0 : -1;
    });
    for (const [surface, root] of Object.entries({ feed: el.flowFeed, now: el.flowNow,
      stories: el.flowStories, history: el.flowHistory })) {
      root.hidden = surface !== view;
      if (root.hidden) $$("video, audio", root).forEach((media) => media.pause());
    }
    el.storyLenses.hidden = view !== "stories";
    el.flowEmpty.hidden = true;
  }

  function renderFlow(append = false) {
    const view = state.flow.view;
    if (view === "stories") pruneExpiredStories();
    const entering = state.flow.renderedView !== view;
    if (entering) {
      stopFlowTimers();
      state.flow.previewObserver?.disconnect();
      for (const video of state.flow.previewQueue) video.flowPreviewQueued = false;
      state.flow.previewQueue = [];
      state.flow.previewActive?.cancel();
    }
    state.flow.renderedView = view;
    if (view !== "feed") {
      state.flow.observer?.disconnect();
      state.flow.activeDropId = null;
      state.flow.activeVideo = null;
    }
    renderFlowTabs();
    $$('[data-story-lens]').forEach((button) => {
      const active = button.dataset.storyLens === state.flow.storyLens;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-selected", String(active));
      button.tabIndex = active ? 0 : -1;
    });
    el.flowEmpty.hidden = view === "feed" ? state.flow.items.length > 0
      : view === "stories" ? state.flow.stories.length > 0
        : view === "history" ? state.flow.history.length > 0 || !state.viewer : true;
    if (view === "stories" && !el.flowError.hidden) el.flowEmpty.hidden = true;
    $("h3", el.flowEmpty).textContent = view === "stories" ? "No Stories yet"
      : view === "history" ? "No moments yet" : "No videos yet";
    $("p", el.flowEmpty).textContent = view === "stories" ? "Stories from this view will appear here. Try another view or check back soon."
      : view === "history" ? "Your moments in Live will appear here."
        : "";
    // "No videos yet" and Share a video say it; a sentence promising that videos will appear adds nothing.
    $("p", el.flowEmpty).hidden = !$("p", el.flowEmpty).textContent;
    renderFlowPaging();
    if (view === "feed") renderFlowFeed(append);
    else if (view === "now") renderFlowNow(entering);
    else if (view === "stories") renderFlowStories();
    else renderFlowHistory();
    observeFlowPreviews();
  }

  function resumeFlow() {
    if (state.flow.view !== "feed") return refreshFlow();
    const owner = state.flow.feedOwner;
    const owned = owner && owner.viewerId === (state.viewer?.id || null) && owner.messages === state.messages
      && owner.csrf === state.csrf && owner.capabilityProfile === state.capabilities?.profile
      && owner.mediaPublication === state.capabilities?.media_publication;
    if (!owned) {
      // Same-view auth/capability recovery can retire a queue without a route departure.
      stopFlowTimers();
      state.flow.observer?.disconnect();
      state.flow.observer = null;
      state.flow.activeDropId = null;
      state.flow.activeVideo = null;
      state.flow.renderedView = null;
      state.flow.previewObserver?.disconnect();
      state.flow.previewObserver = null;
      for (const video of state.flow.previewQueue || []) video.flowPreviewQueued = false;
      state.flow.previewQueue = [];
      state.flow.previewActive?.cancel();
      cancelAnimationFrame(state.flow.resizeFrame);
      state.flow.resizeFrame = null;
      $$("video, audio", el.flowFeed).forEach((media) => media.pause());
      state.flow.items = [];
      state.flow.pageCursors = [];
      state.flow.nextCursor = null;
      state.flow.snapshot = null;
      state.flow.totalPaged = 0;
      state.flow.resetRequired = false;
      state.flow.feedOwner = null;
      clear(el.flowFeed);
      el.flowFeed.scrollTop = 0;
    }
    return refreshFlow(false, Boolean(owned && !state.flow.resetRequired && state.flow.pageCursors.length));
  }

  async function refreshFlow(append = false, revalidate = false) {
    const view = state.flow.view;
    if (view === "now" && Date.now() < (state.flow.nowRetryAt || 0)) { scheduleNowRefresh(0); return; }
    if (view === "stories" && performance.now() < (state.flow.storyRetryAt || 0)) { scheduleStoriesRefresh(0); return; }
    const canAppendFeed = view === "feed" && state.flow.nextCursor && !state.flow.resetRequired;
    const canAppendHistory = view === "history" && state.flow.historyCursor;
    const canAppendStories = view === "stories" && currentStorySnapshot() && state.flow.storyNextCursor && !state.flow.storyResetRequired;
    if (append && (state.flow.loading || (!canAppendFeed && !canAppendHistory && !canAppendStories))) return;
    state.flow.controller?.abort();
    const controller = new AbortController();
    state.flow.controller = controller;
    const requestId = ++state.flow.requestId;
    const flowState = state.flow;
    const viewerId = state.viewer?.id || null;
    const socialState = state.messages;
    const csrf = state.csrf;
    const capabilityProfile = state.capabilities?.profile;
    const mediaPublication = state.capabilities?.media_publication;
    const storyLens = state.flow.storyLens;
    const storyReadStartedAt = view === "stories" ? performance.now() : null;
    const storyFocusedControl = view === "stories" && [el.storyMore, el.storyRefresh].includes(document.activeElement)
      ? document.activeElement : null;
    const ownsRead = () => state.flow === flowState && requestId === state.flow.requestId && state.flow.controller === controller;
    const current = () => ownsRead()
      && viewerId === (state.viewer?.id || null) && socialState === state.messages && csrf === state.csrf
      && (view !== "feed" || (capabilityProfile === state.capabilities?.profile && mediaPublication === state.capabilities?.media_publication))
      && state.flow.view === view && (view !== "stories" || state.flow.storyLens === storyLens);
    state.flow.loading = true;
    state.flow.lastAppend = append;
    state.flow.lastRevalidate = revalidate;
    el.flowLoading.hidden = false;
    el.flowError.hidden = true;
    renderFlowPaging();
    if (view === "stories") renderStoryPaging();
    try {
      if (view === "feed") {
        const result = revalidate ? await revalidateFlowPages(controller.signal)
          : await api(flowPageUrl(append ? state.flow.nextCursor : null), { signal: controller.signal });
        if (!current()) return;
        acceptFlowPage(result, append);
        if (!append && !revalidate) el.flowFeed.scrollTop = 0;
      } else if (view === "now") {
        const nowRead = api("/api/flow/now", { signal: controller.signal });
        if (Date.now() >= (state.flow.chatRetryAt || 0)) {
          state.flow.chatReadStatus = "loading";
          const mutationVersion = state.chat?.mutationVersion || 0;
          void api("/api/chat/messages?limit=40", { signal: controller.signal }).then(chat => {
            if (!current() || controller.signal.aborted || state.view !== "flow") return;
            const merged = new Map((chat.items || []).map(item => [item.id, item]));
            for (const { message, version } of state.chat?.mutations?.values() || []) {
              if (version > mutationVersion && (message.room || "global") === "global") merged.set(message.id, message);
            }
            state.flow.chatItems = [...merged.values()].sort((a, b) => new Date(a.created_at) - new Date(b.created_at) || a.id.localeCompare(b.id)).slice(-60);
            state.flow.chatReadStatus = "ready";
            state.flow.chatRetryAt = 0;
            renderFlowLiveChat();
          }).catch(error => {
            if (!current() || controller.signal.aborted || state.view !== "flow" || error.name === "AbortError") return;
            state.flow.chatReadStatus = "unavailable";
            state.flow.chatRetryAt = Date.now() + flowRetryDelay(error, 30000);
            renderFlowLiveChat();
          });
        }
        const now = await nowRead;
        if (!current()) return;
        state.flow.nowFailures = 0; state.flow.nowRetryAt = 0;
        state.flow.now = now;
      }
      else if (view === "stories") {
        const result = revalidate ? await revalidateStoryPages(controller.signal)
          : await api(storyPageUrl(append ? state.flow.storyNextCursor : null), { signal: controller.signal });
        if (!current()) return;
        if (!result) return;
        acceptStorySnapshot(result, storyReadStartedAt, append);
        state.flow.storyResetScroll = !append && !revalidate;
      } else if (state.viewer) {
        const params = new URLSearchParams({ limit: "50" });
        if (append && state.flow.historyCursor) params.set("cursor", state.flow.historyCursor);
        const result = await api(`/api/flow/history/me?${params}`, { signal: controller.signal });
        if (!current()) return;
        state.flow.history = append
          ? [...new Map([...state.flow.history, ...(result.items || [])].map((item) => [item.slot_id, item])).values()]
          : result.items || [];
        state.flow.historyCursor = result.next_cursor || null;
      } else { state.flow.history = []; state.flow.historyCursor = null; }
      if (current() && state.view === "flow") renderFlow(append);
    } catch (error) {
      if (!current() || error.name === "AbortError") return;
      if (view === "now") controller.abort();
      if (view === "feed" && (error.status === 410 || error.code === "invalid_flow_cursor")) {
        state.flow.resetRequired = true;
        state.flow.nextCursor = null;
        state.flow.items = [];
        state.flow.totalPaged = 0;
        state.flow.pageCursors = [];
        renderFlow();
      }
      el.flowError.hidden = false;
      $("p", el.flowError).textContent = state.flow.resetRequired
        ? "This queue is no longer available. Use Refresh to start again." : error.message;
      if (view === "now" && (!error.status || error.status === 429 || error.status >= 500)) {
        state.flow.nowFailures = Math.min(5, (state.flow.nowFailures || 0) + 1);
        state.flow.nowRetryAt = Date.now() + flowRetryDelay(error, Math.min(60000, 5000 * 2 ** (state.flow.nowFailures - 1)));
        scheduleNowRefresh(0);
      }
      if (view === "stories") {
        recordStoryReadFailure(error);
      }
    } finally {
      // A retired account/capability result cannot paint, but its own busy slot must settle.
      if (ownsRead()) {
        state.flow.loading = false;
        el.flowLoading.hidden = true;
        renderFlowPaging();
        renderFlowRetry();
        if (view === "stories" && current()) {
          renderStoryPaging();
          if (storyFocusedControl && (document.activeElement === document.body || document.activeElement === storyFocusedControl)) {
            const target = storyFocusedControl === el.storyMore && el.storyMore.hidden ? el.storyRefresh : storyFocusedControl;
            target.focus({ preventScroll: true });
          }
          scheduleStoriesRefresh();
        }
      }
    }
  }

  function renderStats() {
    el.presence.textContent = formatCount(state.presence);
    $$("[data-rail-presence]").forEach((node) => {
      node.textContent = state.presence ? `${formatCount(state.presence)} online` : "";
    });
    $("#stat-drops").textContent = formatCount(state.stats.drops);
    $("#stat-reactions").textContent = formatCount(state.stats.reactions);
    $("#stat-saves").textContent = formatCount(state.stats.saves);
    $("#stat-featured").textContent = formatCount(state.stats.featured);
  }

  function movementLabel(movement) {
    if (!movement) return "Not available";
    if (movement.direction === "steady") return "No change";
    if (movement.direction === "new") return "NEW";
    return `${movement.direction === "up" ? "↑" : "↓"}${movement.places}`;
  }

  function renderTrendingPreview(data) {
    clear(el.trendingPreview);
    if (data?.unavailable) {
      el.trendingPreview.append(create("p", "metric-note", "Trending is temporarily unavailable."));
      return;
    }
    if (!data?.items?.length) {
      el.trendingPreview.append(create("p", "metric-note", "Nothing is trending yet."));
      return;
    }
    for (const item of data.items) {
      const button = create("button", "trending-preview-item");
      button.type = "button";
      const copy = create("span", "trending-preview-copy");
      copy.append(
        create("strong", "", item.drop.title),
        create("small", "", `@${item.drop.author.handle} · ${movementLabel(item.movement)}`),
      );
      button.append(
        create("b", "trending-preview-rank", `#${item.rank}`),
        copy,
        create("span", "trending-preview-score", formatCount(item.points)),
      );
      button.addEventListener("click", () => openDrop(item.drop.id));
      el.trendingPreview.append(button);
    }
  }

  function openDiscoverMode(mode, worldView = null) {
    state.discovery.mode = mode;
    if (worldView) state.world.view = worldView;
    const url = new URL("/discover", location.origin);
    if (mode !== "search") url.searchParams.set("mode", mode);
    if (worldView) url.searchParams.set("view", worldView);
    history.pushState({}, "", `${url.pathname}${url.search}`);
    setView("discover");
    scrollTo({ top: 0, behavior: "instant" });
  }

  // The order the reader picks. The unseen lens is an allocation, not a cursor feed,
  // so it takes no order; every other lens on Home does, and so does a topic page.
  // Folders name some of what this account already saved. The strip only exists on the
  // saved lens, because that is the only place a folder means anything.
  function paintSaveFolders() {
    state.savedFolderControls?.paint();
  }

  async function refreshSaveFolders() {
    await state.savedFolderControls?.refresh();
  }

  function paintFeedOrder() {
    const strip = el.feedOrder;
    if (!strip) return;
    strip.hidden = !["all", "following", "topics", "topic"].includes(state.view);
    for (const button of strip.querySelectorAll("[data-sort]")) {
      const active = button.dataset.sort === state.sort;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
    }
  }

  function setFeedOrder(sort) {
    if (!["new", "top", "discussed"].includes(sort) || sort === state.sort) return;
    state.sort = sort;
    // A cursor belongs to the order that produced it, and the server refuses one
    // from the other order. Drop it rather than send it back.
    state.nextCursor = null;
    paintFeedOrder();
    refreshFeed();
    scrollTo({ top: 0, behavior: typeof motionBehavior === "function" ? motionBehavior() : "auto" });
  }

  // Home lenses each own a route, like Following; opening one is a navigation, not a filter.
  function openLens(view) {
    if (view !== "saved") state.savedFolderId = null;
    else if (state.viewer) refreshSaveFolders();
    const path = view === "all" ? "/" : `/${view}`;
    if (location.pathname !== path) history.pushState({}, "", path);
    setView(view);
    scrollTo({ top: 0, behavior: "instant" });
  }

  // Signed-in Home opens on the exchange: what to do first, what is waiting for
  // first viewers, and today's question. Guests never see these cards.
  // Web push. The service worker is registered only when a member turns notifications
  // on, and it has no fetch handler, so it never caches or intercepts pages.
  const PUSH_COPY = {
    off: "Get notifications for private messages, replies, applause and new followers, even when LOUDERR is closed.",
    on: "Notifications are on for private messages, replies, applause and new followers on this device.",
    blocked: "Notifications are blocked for this site in your browser settings.",
  };
  function pushSupported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  }
  function pushKeyBytes(base64url) {
    const padded = base64url.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (base64url.length % 4)) % 4);
    return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
  }
  async function pushSubscription() {
    const registration = await waitForPushSetup(navigator.serviceWorker.getRegistration("/"));
    return registration ? waitForPushSetup(registration.pushManager.getSubscription()) : null;
  }
  async function waitForPushSetup(promise) {
    let timer;
    try {
      return await Promise.race([promise, new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error("Notification setup is taking too long. Check your connection and try again.")), 20000);
      })]);
    } finally { clearTimeout(timer); }
  }
  function pushSetupError(error) {
    const message = error?.message || "Could not change notifications. Try again.";
    if (!/push service error/i.test(message)) return message;
    return navigator.brave
      ? "Brave could not set up notifications. In Brave Settings → Privacy and security, enable ‘Use Google Services for Push Messaging’. Reload LOUDERR and try again."
      : "Your browser could not connect to its notification service. Check your connection and browser notification settings, then try again.";
  }
  async function pushSubscriptionActive(subscription) {
    if (!subscription) return false;
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(subscription.endpoint));
    const hex = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
    const result = await api(`/api/push/subscriptions?endpoint_digest=${hex}`);
    if (typeof result?.subscribed !== "boolean") throw new Error("Notification status is unavailable.");
    return result.subscribed;
  }
  async function refreshPushControls() {
    const section = $("#push-settings");
    const toggle = $("#push-toggle");
    const status = $("#push-status");
    if (!section || !toggle || !status) return;
    if (!state.viewer) { section.hidden = true; state.pushKey = null; return; }
    if (!pushSupported()) {
      section.hidden = false; toggle.hidden = true; state.pushKey = null;
      status.textContent = "Notifications are not supported in this browser. On iPhone or iPad, add LOUDERR to your Home Screen and open it there.";
      return;
    }
    toggle.hidden = false;
    const viewerId = state.viewer.id, csrf = state.csrf, social = state.messages;
    const revision = state.pushRefreshRevision = (state.pushRefreshRevision || 0) + 1;
    const current = () => state.viewer?.id === viewerId && state.csrf === csrf
      && state.messages === social && state.pushRefreshRevision === revision;
    let config = null;
    try { config = await api("/api/push/public-key"); } catch (_) { config = null; }
    if (!current()) return;
    if (!config?.enabled || !config.public_key) {
      section.hidden = false; toggle.hidden = true; state.pushKey = null;
      status.textContent = "Device notifications are unavailable right now. Your messages and notifications are still available on LOUDERR.";
      return;
    }
    state.pushKey = config.public_key;
    section.hidden = false;
    toggle.disabled = true;
    toggle.dataset.pushState = "unknown";
    const blocked = Notification.permission === "denied";
    let on = false;
    try {
      const subscription = blocked ? null : await pushSubscription();
      if (!current()) return;
      on = await pushSubscriptionActive(subscription);
    } catch (_) {
      if (current()) status.textContent = "Could not check notifications. Reopen settings to retry.";
      return;
    }
    if (!current()) return;
    toggle.dataset.pushState = on ? "on" : "off";
    const operation = state.pushOperation;
    const busy = operation?.viewerId === viewerId && operation?.csrf === csrf && operation?.social === social;
    toggle.textContent = busy ? operation.enable ? "Turning on…" : "Turning off…"
      : on ? "Turn off notifications" : "Turn on notifications";
    toggle.disabled = blocked || busy;
    status.textContent = busy ? operation.message : blocked ? PUSH_COPY.blocked : on ? PUSH_COPY.on : PUSH_COPY.off;
  }
  async function togglePush() {
    const toggle = $("#push-toggle");
    if (!toggle || toggle.disabled || !state.viewer || !state.pushKey) return;
    const operation = { viewerId: state.viewer.id, csrf: state.csrf, social: state.messages,
      enable: toggle.dataset.pushState !== "on", message: "Connecting this browser…" };
    const current = () => state.viewer?.id === operation.viewerId && state.csrf === operation.csrf
      && state.messages === operation.social && state.pushOperation === operation;
    state.pushOperation = operation;
    state.pushRefreshRevision = (state.pushRefreshRevision || 0) + 1;
    toggle.disabled = true;
    toggle.textContent = operation.enable ? "Turning on…" : "Turning off…";
    const status = $("#push-status");
    let failure = null;
    try {
      // Keep permission attached to the user's click, before network/status awaits.
      if (operation.enable) {
        operation.message = "Allow notifications when your browser asks.";
        if (status) status.textContent = operation.message;
        const permission = await Notification.requestPermission();
        if (!current()) return;
        if (permission !== "granted") { toast("Notifications stay off until you allow them.", true); return; }
      }
      operation.message = operation.enable ? "Connecting this browser…" : "Turning notifications off…";
      if (status) status.textContent = operation.message;
      const existing = await pushSubscription();
      if (!current()) return;
      // Preserve the requested action if another tab changed the device meanwhile.
      if (!operation.enable) {
        if (existing) {
          await api("/api/push/subscriptions/remove", { method: "POST", csrf: true, json: { endpoint: existing.endpoint } });
          if (!current()) return;
          await waitForPushSetup(existing.unsubscribe());
          if (!current()) return;
        }
        toast("Notifications are off on this device.");
      } else {
        if (await pushSubscriptionActive(existing)) {
          if (current()) toast("Notifications are on for this device.");
          return;
        }
        if (!current()) return;
        let subscription = existing;
        if (!subscription) {
          const registration = await waitForPushSetup(navigator.serviceWorker.register("/sw.js", { scope: "/" }));
          if (!current()) return;
          await waitForPushSetup(navigator.serviceWorker.ready);
          if (!current()) return;
          subscription = await waitForPushSetup(registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: pushKeyBytes(state.pushKey),
          }));
          if (!current()) return;
        }
        const details = subscription.toJSON();
        await api("/api/push/subscriptions", { method: "POST", csrf: true, json: {
          endpoint: details.endpoint, keys: { p256dh: details.keys.p256dh, auth: details.keys.auth },
        } });
        if (!current()) return;
        toast("Notifications are on for this device.");
      }
    } catch (error) {
      if (current()) failure = pushSetupError(error);
    } finally {
      const refresh = current();
      if (state.pushOperation === operation) state.pushOperation = null;
      if (refresh) {
        toggle.disabled = false;
        await refreshPushControls().catch(() => {});
        if (failure && status && state.viewer?.id === operation.viewerId && state.csrf === operation.csrf
          && state.messages === operation.social && !state.pushOperation
          && toggle.dataset.pushState !== (operation.enable ? "on" : "off")) status.textContent = failure;
      }
    }
  }

  // Topics: hashtags people create in their own words. The server decides what trends
  // from distinct authors and valid applause; the client only shows and links them.
  function extractTopicTags(text) {
    const found = [];
    for (const match of String(text || "").matchAll(/(?<![\w#/&])#([^\s#]{1,64})/g)) {
      const tag = match[1].toLowerCase().replace(/[.,;:!?)'"]+$/, "").replace(/-/g, "_");
      if (/^[a-z0-9][a-z0-9_]{2,29}$/.test(tag) && !found.includes(tag)) found.push(tag);
      if (found.length >= 5) break;
    }
    return found;
  }
  function renderComposerTopics() {
    const preview = $("#drop-tags-preview");
    if (!preview || !el.dropTitle || !el.dropBody) return;
    const tags = extractTopicTags(`${el.dropTitle.value} ${el.dropBody.value}`);
    clear(preview);
    if (!tags.length) { preview.hidden = true; return; }
    preview.append(create("span", "", "Topics: "));
    for (const tag of tags) preview.append(create("b", "drop-tag", `#${tag}`));
    preview.hidden = false;
  }
  function openTopic(tag, push = true) {
    const clean = String(tag || "").toLowerCase().replace(/^#/, "");
    if (!/^[a-z0-9][a-z0-9_]{2,29}$/.test(clean)) return;
    state.topic = clean;
    if (push) history.pushState({}, "", `/t/${encodeURIComponent(clean)}`);
    const loaded = setView("topic");
    void refreshTopicFollow();
    return loaded;
  }
  function paintTopicFollow(followed) {
    const button = el.topicFollow;
    if (!button) return;
    button.hidden = state.view !== "topic" || !state.topic || !state.viewer;
    button.setAttribute("aria-pressed", String(Boolean(followed)));
    button.textContent = followed ? "Following topic" : "Follow topic";
  }

  async function refreshTopicFollow() {
    const button = el.topicFollow;
    if (!button) return;
    if (state.view !== "topic" || !state.topic || !state.viewer) { paintTopicFollow(false); return; }
    const topic = state.topic;
    try {
      const summary = await api(`/api/topics/${encodeURIComponent(topic)}`);
      // The reader may have moved on while this was in flight.
      if (state.view === "topic" && state.topic === topic) paintTopicFollow(summary.followed);
    } catch (_) { paintTopicFollow(false); }
  }

  async function toggleTopicFollow() {
    const button = el.topicFollow;
    if (!button || !state.topic) return;
    const topic = state.topic, session = state.messages, viewerId = state.viewer?.id;
    const following = button.getAttribute("aria-pressed") === "true";
    button.disabled = true;
    try {
      await api(`/api/topics/${encodeURIComponent(topic)}/follow`, {
        method: following ? "DELETE" : "POST", csrf: true,
      });
      if (state.messages !== session || state.viewer?.id !== viewerId) return;
      state.feedWindows?.invalidate({ view: "topics", erase: following });
      if (state.topic === topic) paintTopicFollow(!following);
      toast(following ? `You no longer follow #${topic}.` : `You follow #${topic}.`);
    } catch (error) {
      toast(error.message, true);
    } finally {
      button.disabled = false;
    }
  }

  async function refreshDiscoverTopics() {
    const section = $("#discover-topics");
    const list = $("#discover-topic-list");
    if (!section || !list) return;
    try {
      const fresh = state.topicsFetchedAt && Date.now() - state.topicsFetchedAt < 60_000;
      if (!fresh) { state.topics = (await api("/api/topics/trending?limit=12")).items || []; state.topicsFetchedAt = Date.now(); }
    } catch (_) { state.topics = state.topics || []; }
    // Topics are public cached data, but this row belongs only to Discover search.
    // A late reply must not reveal it over Events, World, Records or another page.
    if (state.view !== "discover" || state.discovery.mode !== "search") return;
    const items = state.topics || [];
    clear(list);
    for (const item of items) {
      const chip = create("a", `topic-chip${item.status === "trending" ? " is-trending" : ""}`);
      chip.href = `/t/${encodeURIComponent(item.tag)}`;
      chip.append(create("b", "", `#${item.tag}`), create("small", "", item.status === "trending"
        ? `${item.authors} people · ${item.drops} Drops`
        : `${item.drops} ${item.drops === 1 ? "Drop" : "Drops"}`));
      chip.addEventListener("click", (event) => { event.preventDefault(); openTopic(item.tag); });
      list.append(chip);
    }
    section.hidden = items.length === 0;
  }

  function undiscoveredVisited() {
    try { return localStorage.getItem("louderr.start.undiscovered") === "1"; } catch (_) { return false; }
  }

  function markUndiscoveredVisited() {
    try { localStorage.setItem("louderr.start.undiscovered", "1"); } catch (_) { /* per-device convenience only */ }
  }

  async function refreshMemberHome() {
    const start = $("#home-start");
    const guide = $("#getting-started-dialog");
    if (!start || !guide?.open) return;
    const viewer = state.viewer;
    if (!viewer || state.sessionRestricted) { start.hidden = true; return; }
    const viewerId = viewer.id;
    const socialState = state.messages;
    const requestId = state.memberGuideRequestId = (state.memberGuideRequestId || 0) + 1;
    start.hidden = true;
    const [profileResult, waitingResult] = await Promise.allSettled([
      api(`/api/profiles/${encodeURIComponent(viewer.handle)}`),
      api("/api/discovery/undiscovered?limit=5"),
    ]);
    if (state.viewer?.id !== viewerId || state.messages !== socialState || state.sessionRestricted
        || requestId !== state.memberGuideRequestId || !guide.open) return;
    const counts = profileResult.status === "fulfilled" ? (profileResult.value?.counts || {}) : {};
    const waiting = waitingResult.status === "fulfilled" ? (waitingResult.value?.items || []).length : 0;
    let dismissed = false;
    try { dismissed = localStorage.getItem("louderr.start.dismissed") === "1"; } catch (_) { dismissed = false; }
    const steps = [
      { label: "Publish your first Drop", done: Number(counts.drops || 0) >= 1, cta: "Create", action: "create" },
      {
        label: waiting ? `Give a fair start to ${formatCount(waiting)} waiting ${waiting === 1 ? "Drop" : "Drops"}` : "Give new Drops a fair start",
        done: undiscoveredVisited(), cta: "Open", action: "unseen",
      },
      { label: "Follow three people", done: Number(counts.following || 0) >= 3, cta: "Find people", action: "discover" },
    ];
    const list = $("#home-start-steps");
    clear(list);
    for (const step of steps) {
      const item = create("li", `home-start-step${step.done ? " is-done" : ""}`);
      const mark = create("span", "home-start-mark", step.done ? "✓" : "");
      mark.setAttribute("aria-hidden", "true");
      const label = create("span", "home-start-label", step.label);
      item.append(mark, label);
      if (!step.done) {
        const button = create("button", "home-start-action", step.cta);
        button.type = "button";
        button.addEventListener("click", () => followGettingStartedAction(step.action));
        item.append(button);
      }
      list.append(item);
    }
    const head = start.querySelector(".home-start-head");
    if (head) {
      let progress = head.querySelector(".home-start-progress");
      if (!progress) {
        progress = create("span", "home-start-progress");
        head.insertBefore(progress, head.firstChild ? head.firstChild.nextSibling : null);
      }
      progress.textContent = `${steps.filter((step) => step.done).length} of ${steps.length}`;
    }
    const dismiss = $("#home-start-dismiss");
    if (dismiss && !dismiss.dataset.bound) {
      dismiss.dataset.bound = "true";
      dismiss.addEventListener("click", () => {
        try { localStorage.setItem("louderr.start.dismissed", "1"); } catch (_) { /* optional */ }
        start.hidden = true;
      });
    }
    start.hidden = dismissed || (steps[0].done && steps[2].done);
  }

  async function refreshHomeQuestion() {
    const today = $("#home-today");
    if (!today) return;
    const viewerId = state.viewer?.id;
    const socialState = state.messages;
    const requestId = state.homeQuestionRequestId = (state.homeQuestionRequestId || 0) + 1;
    today.hidden = true;
    if (!viewerId || state.sessionRestricted || state.view !== "all") return;
    let question = null;
    try { question = await api("/api/daily-question"); } catch (_) { /* Optional Home prompt. */ }
    if (state.viewer?.id !== viewerId || state.messages !== socialState || state.sessionRestricted
        || requestId !== state.homeQuestionRequestId || state.view !== "all") return;
    clear(today);
    const prompt = question ? (question.prompt || question.title || question.body || "") : "";
    if (prompt && question.status !== "removed") {
      const copy = create("div", "home-today-copy");
      copy.append(create("small", "", "Today's question"), create("p", "", prompt));
      const answer = create("button", "home-today-action", "Answer");
      answer.type = "button";
      answer.addEventListener("click", () => openDiscoverMode("world", "questions"));
      today.append(copy, answer);
      today.hidden = false;
    } else today.hidden = true;
  }

  function peopleRowSeen() {
    try { return new Set(JSON.parse(localStorage.getItem("louderr.people.seen") || "[]")); } catch (_) { return new Set(); }
  }

  function peopleRowMarkSeen(dropId) {
    try {
      const ids = [dropId, ...peopleRowSeen()].filter((id, index, all) => all.indexOf(id) === index).slice(0, 200);
      localStorage.setItem("louderr.people.seen", JSON.stringify(ids));
    } catch (_) { /* optional */ }
  }

  // Stories lead the Home row, like the story tray above a feed. One request, members only.
  async function refreshHomeStories() {
    // The pending static tray is replaced once both startup reads have resolved.
    if (state.startupPending) return;
    if (!state.viewer || state.sessionRestricted || !state.capabilities.flow || !state.capabilities.media_publication) {
      state.homeStories = [];
      if (state.view === "all") renderRailPeople();
      return;
    }
    // Your own tile needs no network response. Fill the reserved tray immediately.
    if (state.view === "all") renderRailPeople();
    const viewerId = state.viewer.id;
    // setView calls this without awaiting, so two runs overlap whenever Home is reopened while
    // the first request is still out. The token is checked on the failure branch too, which is
    // the branch that mattered: a slow failure landing after a fast success wiped the fresh rail
    // to empty, and nothing refetches it until the next navigation.
    const requestId = ++state.homeStoriesRequestId;
    const current = () => requestId === state.homeStoriesRequestId && state.viewer?.id === viewerId;
    void state.storiesV2?.refreshTray();
    void state.live?.refresh();
    try {
      const result = await api("/api/flow/stories?lens=now&limit=30");
      if (!current()) return;
      state.homeStories = Array.isArray(result?.items) ? result.items : [];
    } catch (_) {
      if (!current()) return;
      state.homeStories = [];
    }
    if (state.view === "all") renderRailPeople();
  }

  function openHomeStory(story) {
    state.flow.view = "stories";
    history.pushState({}, "", "/flow?view=stories");
    setView("flow");
    const storyId = story.id;
    let attempts = 0;
    const reveal = () => {
      const card = $(`[data-story-id="${storyId}"]`, el.flowStories);
      if (card) { card.scrollIntoView({ block: "start", behavior: motionBehavior() }); return; }
      if (attempts++ < 12 && state.view === "flow") setTimeout(reveal, 150);
    };
    requestAnimationFrame(reveal);
  }

  function renderPeopleRow(people) {
    if (state.startupPending) return;
    const row = $("#home-people");
    const list = $("#home-people-list");
    if (!row || !list) return;
    clear(list);
    const storiesByAuthor = new Map();
    for (const story of Array.isArray(state.homeStories) ? state.homeStories : []) {
      const author = story?.drop?.author;
      if (!author?.id || storiesByAuthor.has(author.id)) continue;
      storiesByAuthor.set(author.id, story);
    }
    const canPost = state.capabilities ? state.capabilities.community_writes !== false : true;
    const storyTray = state.viewer && state.capabilities?.media_publication ? state.storiesV2?.trayEntries() || [] : [];
    const trayAuthors = new Set(storyTray.map(entry => entry.author?.id).filter(Boolean));
    if (state.viewer && canPost && state.storiesV2 && state.capabilities?.media_publication) {
      state.storiesV2.renderTray(list);
    } else if (state.viewer && canPost) {
      const you = create("button", "people-row-item is-you");
      you.type = "button";
      you.setAttribute("aria-label", "Create a Drop");
      const avatar = create("span", "avatar people-row-avatar");
      styleAvatar(avatar, state.viewer);
      const plus = create("span", "people-row-plus", "+");
      plus.setAttribute("aria-hidden", "true");
      avatar.append(plus);
      you.append(avatar, create("span", "people-row-name", "Your Drop"));
      you.addEventListener("click", () => openComposer());
      list.append(you);
      for (const live of state.live?.trayItems() || []) list.append(live);
    }
    for (const story of storiesByAuthor.values()) {
      const author = story.drop.author;
      if (trayAuthors.has(author.id)) continue;
      const item = create("button", "people-row-item has-story");
      item.type = "button";
      const avatar = create("span", "avatar people-row-avatar");
      styleAvatar(avatar, author);
      item.append(avatar, accountNameNode(author, "span", "people-row-name"));
      item.setAttribute("aria-label", `Story from ${author.display_name || author.handle}`);
      item.addEventListener("click", () => openHomeStory(story));
      list.append(item);
    }
    // The row works like a story strip: a lit ring means a Drop from the last day you have not
    // opened yet, and a tap goes straight to it. The profile stays one tap away on the Drop.
    const newestByAuthor = new Map();
    for (const drop of Array.isArray(state.drops) ? state.drops : []) {
      const authorId = drop?.author?.id;
      if (!authorId || newestByAuthor.has(authorId)) continue;
      newestByAuthor.set(authorId, drop);
    }
    const seen = peopleRowSeen();
    const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
    for (const person of people) {
      if (storiesByAuthor.has(person.id) || trayAuthors.has(person.id)) continue;
      const newest = newestByAuthor.get(person.id) || null;
      const fresh = Boolean(newest) && new Date(newest.created_at).getTime() > dayAgo && !seen.has(newest.id);
      const item = create("button", `people-row-item${fresh ? " is-fresh" : ""}`);
      item.type = "button";
      const avatar = create("span", "avatar people-row-avatar");
      styleAvatar(avatar, person);
      item.append(avatar, accountNameNode(person, "span", "people-row-name"));
      item.title = newest ? `Newest Drop: ${newest.title}` : person.display_name || "";
      item.addEventListener("click", () => {
        if (!newest) { openCreator(person); return; }
        peopleRowMarkSeen(newest.id);
        item.classList.remove("is-fresh");
        history.pushState({}, "", `/d/${encodeURIComponent(newest.id)}`);
        routeLocation();
      });
      list.append(item);
    }
    row.hidden = people.length === 0 && storiesByAuthor.size === 0 && !storyTray.length && !(state.viewer && canPost);
    // Somebody live counts as a Story here: home.css shows the strip only when it carries one.
    const liveCount = state.viewer ? state.live?.liveCount() || 0 : 0;
    row.classList.toggle("has-stories", storiesByAuthor.size > 0 || storyTray.length > 0 || liveCount > 0);
    row.classList.toggle("can-post-story", Boolean(state.viewer && canPost && state.storiesV2 && state.capabilities?.media_publication));
  }

  // People to discover come from the creators already in the shared feed: no extra
  // request, no search budget spent on every page load, and it shows who is active.
  function renderRailPeople() {
    if (state.view === "all") {
      const viewerId = state.viewer?.id || null;
      const seen = new Set();
      const people = [];
      for (const drop of state.drops) {
        const author = drop?.author;
        if (!author?.id || author.id === viewerId || seen.has(author.id)) continue;
        seen.add(author.id);
        people.push(author);
      }
      state.railPeople = people.slice(0, 10);
    }
    const people = state.railPeople || [];
    const card = $("#rail-people");
    const list = $("#rail-people-list");
    if (card && list) {
      clear(list);
      for (const person of people.slice(0, 5)) {
        const row = create("button", "rail-person");
        row.type = "button";
        const avatar = create("span", "avatar avatar-small");
        styleAvatar(avatar, person);
        const copy = create("span", "rail-person-copy");
        copy.append(accountNameNode(person, "strong"), create("small", "", `@${person.handle}`));
        row.append(avatar, copy);
        row.addEventListener("click", () => openCreator(person));
        // Follow from the rail itself; the feed Drop by this author carries the viewer's follow state.
        const drop = state.viewer && person.id !== state.viewer.id
          ? state.drops.find((item) => item?.author?.id === person.id) : null;
        if (drop && state.capabilities.community_writes) {
          const line = create("div", "rail-person-row");
          const following = Boolean(drop.viewer_follows_author);
          const follow = create("button", `rail-follow${following ? " is-following" : ""}`, following ? "Following" : "Follow");
          follow.type = "button";
          follow.setAttribute("aria-label", `${following ? "Unfollow" : "Follow"} ${person.display_name || person.handle}`);
          if (state.followState) paintFollowControl(follow, { ...drop.author, id: drop.author_id }, following);
          follow.addEventListener("click", () => toggleDropAuthorFollow(drop, follow));
          line.append(row, follow);
          list.append(line);
        } else list.append(row);
      }
      card.hidden = people.length === 0;
    }
    renderPeopleRow(people);
  }

  function startStartupSupportReads() {
    let owner = null;
    const owns = value => value && state.messages === value.social
      && (state.viewer?.id || null) === value.viewerId && state.csrf === value.csrf;
    const workspace = () => {
      if (!owns(owner)) {
        owner = { social: state.messages, viewerId: state.viewer?.id || null, csrf: state.csrf,
          trending: "pending", room: state.viewer ? "pending" : "unused" };
        // A first visible read must never reveal the previous account's preview.
        clear(el.trendingPreview);
      }
      return owner;
    };
    const visible = node => Boolean(node?.isConnected && !node.closest("[hidden], details:not([open])")
      && typeof node.getClientRects === "function" && node.getClientRects().length);
    const refresh = () => {
      const current = workspace();
      if (state.startupPending || state.sessionRestricted || document.visibilityState === "hidden") return Promise.resolve();
      const reads = [];
      const begin = (key, reader) => {
        current[key] = "started";
        const read = reader();
        reads.push(Promise.resolve(read).finally(() => {
          // Attempted is not a freshness receipt. Manual retry and realtime keep their own reads.
          if (owner === current && owns(current)) current[key] = "attempted";
        }));
      };
      if (current.trending === "pending" && visible(el.trendingPreview)) {
        if (!el.trendingPreview.children.length) {
          const loading = create("p", "metric-note", "Loading Trending…");
          loading.setAttribute("role", "status"); el.trendingPreview.append(loading);
        }
        begin("trending", refreshTrendingPreview);
      }
      if (current.room === "pending" && $$(".room-now-desktop, .room-now-mobile").some(visible)) {
        begin("room", refreshRoomAffordances);
        renderRoomAffordances();
      }
      return Promise.all(reads);
    };
    state.startupSupport = { refresh };
    return refresh();
  }

  function bindStartupSupportReads() {
    const refresh = () => { void state.startupSupport?.refresh(); };
    addEventListener("resize", refresh);
    document.addEventListener("visibilitychange", refresh);
    $(".room-tools-mobile")?.addEventListener("toggle", refresh);
  }

  async function refreshTrendingPreview() {
    // The same overlap refreshTrending has: applyEvent refreshes the preview on realtime
    // Drop events while toggleSignal and submitDrop refresh it on user action. The catch is
    // the sharp edge -- an older request failing after a newer one rendered replaces a good
    // preview with a permanent "unavailable" note that nothing clears until the next event.
    const trending = state.trending, social = state.messages, csrf = state.csrf;
    const viewerId = state.viewer?.id || null;
    const requestId = ++trending.previewRequestId;
    const current = () => state.trending === trending && requestId === trending.previewRequestId
      && state.messages === social && state.csrf === csrf && (state.viewer?.id || null) === viewerId;
    try {
      const data = await api("/api/trending?scope=drops&period=day&limit=4");
      if (!current()) return;
      renderTrendingPreview(data);
    } catch (_) {
      if (!current()) return;
      clear(el.trendingPreview);
      el.trendingPreview.append(create("p", "metric-note", "Trending is temporarily unavailable."));
    }
  }

  function updateTrendingCountdown() {
    clearInterval(state.trendingTimer);
    const update = () => {
      const resetAt = state.trending.data?.reset_at;
      if (!resetAt) { el.trendingCountdown.textContent = "Ongoing"; return; }
      const remaining = Math.max(0, new Date(resetAt).getTime() - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      el.trendingCountdown.textContent = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    };
    update();
    state.trendingTimer = setInterval(update, 1000);
  }

  function trendingRow(item) {
    const button = create("button", "leaderboard-row");
    button.type = "button";
    button.dataset.trendingRank = String(item.rank);
    button.dataset.trendingEntityId = state.trending.scope === "people" ? item.user.id : item.drop.id;
    const rank = create("span", "leaderboard-rank", `#${item.rank}`);
    const identity = create("span", "leaderboard-identity");
    const avatar = create("span", "avatar avatar-small");
    const user = state.trending.scope === "people" ? item.user : item.drop.author;
    styleAvatar(avatar, user);
    const copy = create("span", "leaderboard-copy");
    if (state.trending.scope === "people") {
      copy.append(
        accountNameNode(item.user),
        create("small", "", `@${item.user.handle} · ${kindLabels[item.leading_category] || "Global"}`),
      );
    } else {
      copy.append(
        create("strong", "", item.drop.title),
        create("small", "", `@${item.drop.author.handle} · ${kindLabels[item.drop.kind] || item.drop.kind}`),
      );
    }
    identity.append(avatar, copy);
    const movement = create("span", `trend-movement is-${item.movement.direction}`, movementLabel(item.movement));
    const score = create("span", "leaderboard-score");
    score.append(
      create("strong", "", formatCount(item.points)),
      create("small", "", item.gap_to_next ? `${formatCount(item.gap_to_next)} behind` : "leading"),
    );
    button.append(rank, identity, movement, score);
    if (state.trending.scope === "people") {
      button.addEventListener("click", () => {
        track("trending_open", null, { scope: "people", rank: item.rank, target_user_id: item.user.id });
        openCreator(item.user);
      });
    } else {
      button.addEventListener("click", () => {
        track("trending_open", item.drop.id, { scope: "drops", rank: item.rank });
        openDrop(item.drop.id);
      });
    }
    return button;
  }

  let trendingImpressionObserver;
  function renderTrending() {
    const data = state.trending.data;
    clear(el.trendingList);
    clear(el.trendingFocus);
    trendingImpressionObserver?.disconnect();
    el.trendingError.hidden = true;
    if (data?.unavailable) {
      el.trendingEmpty.hidden = true;
      el.trendingFocus.hidden = true;
      el.trendingExplanation.textContent = "";
      clearInterval(state.trendingTimer);
      el.trendingError.querySelector("p").textContent = "Trending is temporarily unavailable. Other parts of LOUDERR remain available.";
      el.trendingError.hidden = false;
      return;
    }
    el.trendingEmpty.hidden = Boolean(data?.items?.length);
    if (!data) return;
    el.trendingExplanation.textContent = data.explanation;
    for (const item of data.items) el.trendingList.append(trendingRow(item));
    trendingImpressionObserver?.disconnect();
    trendingImpressionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
        const rank = Number(entry.target.dataset.trendingRank);
        const entityId = entry.target.dataset.trendingEntityId;
        const key = `trending:${state.trending.scope}:${state.trending.period}:${state.trending.category}:${entityId}`;
        if (state.observed.has(key)) continue;
        state.observed.add(key);
        track(
          "trending_impression",
          state.trending.scope === "drops" ? entityId : null,
          { scope: state.trending.scope, period: state.trending.period, rank, ...(state.trending.scope === "people" ? { target_user_id: entityId } : {}) },
        );
      }
    }, { threshold: [0.5] });
    $$(".leaderboard-row", el.trendingList).forEach((row) => trendingImpressionObserver.observe(row));
    if (data.viewer) {
      const heading = data.viewer.rank
        ? `Your live rank is #${data.viewer.rank}`
        : "Not ranked yet";
      const viewerMovement = data.viewer.movement ? movementLabel(data.viewer.movement) : null;
      const nextTarget = data.viewer.next_target;
      const targetProgress = nextTarget
        ? [
          `${formatCount(nextTarget.positions_to_target)} ${nextTarget.positions_to_target === 1 ? "position" : "positions"} to ${nextTarget.label}`,
          nextTarget.points_to_target > 0 ? `${formatCount(nextTarget.points_to_target)} momentum needed` : null,
        ].filter(Boolean).join(" · ")
        : null;
      const detail = data.viewer.rank
        ? `${formatCount(data.viewer.points)} momentum${viewerMovement && viewerMovement !== "No change" ? ` · ${viewerMovement}` : ""}${targetProgress ? ` · ${targetProgress}` : ""}`
        : "Rankings update as people interact.";
      el.trendingFocus.append(create("strong", "", heading), create("span", "", detail));
      el.trendingFocus.hidden = false;
      if (data.viewer.rank) {
        const positionKey = `trending-position:${state.trending.scope}:${state.trending.period}:${state.trending.category}:${data.viewer.rank}`;
        if (!state.observed.has(positionKey)) {
          state.observed.add(positionKey);
          track("trending_position", null, {
            scope: state.trending.scope,
            period: state.trending.period,
            category: state.trending.category,
            rank: data.viewer.rank,
            points: data.viewer.points || 0,
            gap_to_top_10: data.viewer.gap_to_top_10 || 0,
            ...(nextTarget ? {
              next_target_rank: nextTarget.target_rank,
              next_target_label: nextTarget.label,
              positions_to_target: nextTarget.positions_to_target,
              points_to_target: nextTarget.points_to_target,
            } : {}),
          });
        }
      }
    } else {
      el.trendingFocus.hidden = true;
    }
    updateTrendingCountdown();
  }

  function renderTrendingHistory(data) {
    clear(el.trendingHistory);
    if (!data?.snapshots?.length) {
      el.trendingHistory.append(create("p", "metric-note", "No completed period has been stored yet."));
      return;
    }
    for (const snapshot of data.snapshots.slice(0, 6)) {
      const row = create("div", "trending-history-row");
      const date = new Date(snapshot.period_end);
      const when = create("span", "", date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }));
      const winner = snapshot.top[0];
      const winnerName = winner
        ? (state.trending.scope === "people" ? winner.display_name : winner.title)
        : "No result";
      const result = create("span", "trending-history-winner", winner ? `#1 ${winnerName}` : winnerName);
      if (winner && state.trending.scope === "people") {
        result.textContent = "";
        result.append(create("span", "", "#1 "), accountNameNode(winner, "span"));
      }
      const viewer = create(
        "span",
        "trending-history-viewer",
        snapshot.viewer ? `You #${snapshot.viewer.rank}` : "",
      );
      row.append(when, result, viewer);
      el.trendingHistory.append(row);
    }
  }

  async function refreshTrendingHistory() {
    const params = new URLSearchParams({
      scope: state.trending.scope,
      period: state.trending.period,
      limit: "6",
    });
    // Two period or scope changes that both survive the refreshTrending token still race here.
    const requestId = ++state.trending.historyRequestId;
    const current = () => requestId === state.trending.historyRequestId;
    try {
      const data = await api(`/api/trending/history?${params}`);
      if (!current()) return;
      renderTrendingHistory(data);
    }
    catch (_) {
      if (!current()) return;
      clear(el.trendingHistory);
      el.trendingHistory.append(create("p", "metric-note", "Historical results are temporarily unavailable."));
    }
  }

  async function refreshTrending() {
    el.trendingLoading.hidden = false;
    el.trendingError.hidden = true;
    const params = new URLSearchParams({
      scope: state.trending.scope,
      period: state.trending.period,
      limit: "100",
    });
    if (state.trending.category !== "all") params.set("kind", state.trending.category);
    // applyEvent refreshes Trending on realtime Drop events while the scope, period and
    // category controls refresh it on change, so two runs overlap routinely. Without a token
    // the slower response wins and the table contradicts the selected chips. The spinner is
    // part of it: a stale response must not hide the loader a newer request still needs.
    const requestId = ++state.trending.requestId;
    const current = () => requestId === state.trending.requestId;
    try {
      const data = await api(`/api/trending?${params}`);
      if (!current()) return;
      state.trending.data = data;
      renderTrending();
      refreshTrendingHistory();
    } catch (error) {
      if (!current()) return;
      el.trendingError.hidden = false;
      toast(error.message, true);
    } finally {
      if (current()) el.trendingLoading.hidden = true;
    }
  }

  async function refreshRoom(quiet = false) {
    state.roomController?.abort();
    const controller = new AbortController();
    state.roomController = controller;
    const identity = state.messages;
    const viewerId = state.viewer?.id || null;
    const paidContentVersion = state.paidContentVersion || 0;
    const current = () => !controller.signal.aborted && state.roomController === controller
      && state.messages === identity && (state.viewer?.id || null) === viewerId
      && (state.paidContentVersion || 0) === paidContentVersion;
    try {
      // Feed data already has its own request; room refreshes only need the shared controls.
      const snapshot = await api("/api/state?include_feed=false", { signal: controller.signal });
      if (!current()) return;
      state.board = snapshot.board;
      state.boardQueue = snapshot.board_queue || { items: [], eligible_count: 0, locked_count: 0 };
      if (snapshot.product_name) applyProductName(snapshot.product_name);
      if (snapshot.translation) {
        state.translation = snapshot.translation;
        renderTranslationPreferences();
      }
      state.presence = snapshot.presence || 0;
      state.stats = snapshot.stats || state.stats;
      state.chatControls = snapshot.operational_controls?.chat || null;
      const uploadTypes = snapshot.operational_controls?.uploads?.types === "images" ? "images" : "all";
      const mediaInput = $("#drop-media");
      if (mediaInput) {
        if (!mediaInput.dataset.acceptAll) mediaInput.dataset.acceptAll = mediaInput.getAttribute("accept") || "";
        mediaInput.setAttribute("accept", uploadTypes === "images" ? "image/jpeg,image/png,image/webp,image/gif" : mediaInput.dataset.acceptAll);
      }
      const uploadLabel = $("#upload-label");
      const uploadCopy = { images: "Up to 35 photos", all: "Up to 35 photos or videos, or one audio file" };
      if (uploadLabel && Object.values(uploadCopy).includes(uploadLabel.textContent.trim())) uploadLabel.textContent = uploadCopy[uploadTypes];
      const surfaceMode = snapshot.operational_controls?.surfaces?.mode === "core" ? "core" : "full";
      document.documentElement.dataset.surfaceMode = surfaceMode;
      void state.startupSupport?.refresh();
      if (surfaceMode === "core" && ["world", "records"].includes(state.discovery.mode)) {
        state.discovery.mode = "search";
        if (state.view === "discover") refreshDiscover();
      }
      el.presenceNote.textContent = snapshot.presence_is_approximate ? "The online count is approximate." : "";
      renderBoard();
      renderStats();
      if (!quiet) toast("Room refreshed");
    } catch (error) {
      if (!current()) return;
      setConnection("offline", "Offline");
      if (!quiet) toast(error.message, true);
    } finally {
      if (state.roomController === controller) state.roomController = null;
    }
  }

  // Home categories: several at once, remembered on this device. state.filter stays a string so every
  // fence that compares it keeps working: "all", or a comma-joined list such as "music,art"; "first"
  // (the Here-first lens) joins the list like a category and travels as its own query flag.
  // The storage key is written out at each call so the security test can pin every use.
  function filterKinds(filter = state.filter) {
    return filter && filter !== "all" ? filter.split(",").filter(Boolean) : [];
  }
  function toggleHomeFilter(current, kind) {
    if (!kind || kind === "all") return "all";
    const kinds = filterKinds(current);
    const next = kinds.includes(kind) ? kinds.filter((item) => item !== kind) : [...kinds, kind];
    return next.length ? next.join(",") : "all";
  }
  function paintHomeFilter() {
    const kinds = filterKinds();
    $$('[data-filter]').forEach((button) => {
      const active = kinds.length ? kinds.includes(button.dataset.filter) : button.dataset.filter === "all";
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
    });
  }
  function rememberHomeFilter(filter) {
    try {
      if (filter === "all") localStorage.removeItem("louderr.home.filter");
      else localStorage.setItem("louderr.home.filter", filter);
    } catch (_) { /* A per-device convenience; nothing depends on it. */ }
  }
  function restoreHomeFilter() {
    let stored = null;
    try { stored = localStorage.getItem("louderr.home.filter"); } catch (_) { stored = null; }
    const known = new Set($$('[data-filter]').map((button) => button.dataset.filter));
    const kinds = filterKinds(stored).filter((kind) => kind !== "all" && known.has(kind));
    state.filter = kinds.length ? kinds.join(",") : "all";
    paintHomeFilter();
    // A remembered choice should be in view on a phone, where the strip scrolls.
    const firstActive = $$('[data-filter]').find((button) => button.classList.contains("is-active"));
    if (firstActive && firstActive.dataset.filter !== "all" && typeof centerInScroller === "function" && typeof el !== "undefined" && el.categoryStrip) centerInScroller(firstActive, el.categoryStrip);
  }

  function feedQuery(cursor = null) {
    const params = new URLSearchParams();
    const kinds = !state.filter || state.filter === "all" ? [] : state.filter.split(",").filter(Boolean);
    if (kinds.includes("first")) params.set("first_on_louderr", "true");
    const plainKinds = kinds.filter((kind) => kind !== "first");
    if (plainKinds.length) params.set("kind", plainKinds.join(","));
    if (state.view === "saved") {
      params.set("saved", "true");
      if (state.savedFolderId) params.set("saved_folder_id", state.savedFolderId);
    }
    if (state.view === "topics") params.set("followed_tags", "true");
    if (state.sort && state.sort !== "new") params.set("sort", state.sort);
    if (state.view === "mine" && state.viewer) params.set("author_id", state.viewer.id);
    if (state.authorId) params.set("author_id", state.authorId);
    if (cursor) params.set("cursor", cursor);
    if (state.view === "unseen") {
      // Fair start allocation, not a cursor feed: the allocator picks by outstanding exposure debt,
      // so category is the only filter that applies and "first" falls back to every category.
      const lens = new URLSearchParams({ limit: "30" });
      if (plainKinds.length) lens.set("kind", plainKinds.join(","));
      return `/api/discovery/undiscovered?${lens}`;
    }
    if (state.view === "topic" && state.topic) return `/api/topics/${encodeURIComponent(state.topic)}/drops?${params}`;
    return `${state.view === "following" ? "/api/following/feed" : "/api/drops"}?${params}`;
  }

  // A guest's first Home page, served in the HTML (guest_home_document in app/shell.py) so the first
  // image could paint before this script ran. Taken once, and used only by a guest on the default Home.
  function takeServedHomeFeed(query) {
    const node = document.getElementById("initial-home-feed");
    if (!node) return null;
    node.remove();
    if (state.viewer || query !== "/api/drops?") {
      // Not this page's reader (signed in meanwhile, or a Home of their own): the post drawn for a
      // guest must not stay on screen until the feed renders.
      for (const card of document.querySelectorAll("#feed > [data-server-drawn]")) card.remove();
      return null;
    }
    try {
      const page = JSON.parse(node.textContent);
      return Array.isArray(page?.items) ? page : null;
    } catch (_) {
      return null;
    }
  }

  function primeInitialHomeFeed(authResult) {
    // Taken before any other check, so a page served for a guest never outlives a signed-in start.
    const served = takeServedHomeFeed(feedQuery());
    if (!authResult || authResult.user !== state.viewer || authResult.csrf_token !== state.csrf
        || !state.startupPending || state.view !== "all" || location.pathname !== "/"
        || location.search || location.hash || state.sessionRestricted
        || state.ageAttestationRequired || state.ageRequirementNotMet) return;
    const viewer = state.viewer, socialState = state.messages, csrf = state.csrf;
    const query = feedQuery();
    state.initialHomeFeed?.controller?.abort();
    const controller = new AbortController();
    const current = () => !controller.signal.aborted && state.viewer === viewer && state.messages === socialState && state.csrf === csrf
      && state.view === "all" && location.pathname === "/" && !location.search && !location.hash
      && !state.sessionRestricted && !state.ageAttestationRequired && !state.ageRequirementNotMet
      && feedQuery() === query;
    // The server has identified this session. Overlap the first feed read with
    // capabilities; consume it once, only in that same session and Home query.
    state.initialHomeFeed = { query, current, controller,
      result: served ? Promise.resolve({ ok: true, value: served })
        : api(query, { signal: controller.signal }).then(value => ({ ok: true, value }), error => ({ ok: false, error })) };
  }

  function ordinaryFeedWindows() {
    if (state.feedWindows) return state.feedWindows;
    const views = new Set(["all", "saved", "mine", "following", "topics", "topic"]);
    // Write access recovering updates controls in place (applyCapabilities), as in feedContentKey:
    // it must not discard a loaded window and rebuild cards the reader is looking at.
    const tag = () => JSON.stringify([state.viewer?.id || null, state.viewer?.status || null,
      Boolean(state.sessionRestricted), Boolean(state.ageAttestationRequired), Boolean(state.ageRequirementNotMet),
      state.paidContentVersion || 0, state.capabilities?.media_publication]);
    const cache = { active: null, parked: null, session: state.messages, tag: tag(), serial: 0, timer: null, token: null, displayed: false };
    cache.valid = () => cache.session === state.messages && cache.tag === tag();
    cache.key = () => JSON.stringify([state.view, location.pathname, feedQuery()]);
    cache.current = entry => Boolean(entry && cache.valid() && views.has(state.view)
      && cache.active === entry && entry.key === cache.key());
    cache.cancel = () => {
      if (cache.timer !== null) clearTimeout(cache.timer);
      cache.timer = null; cache.token = null;
    };
    cache.leave = () => { cache.displayed = false; cache.cancel(); };
    cache.reset = () => {
      cache.cancel(); cache.serial++;
      cache.active = null; cache.parked = null; cache.displayed = false;
      cache.session = state.messages; cache.tag = tag();
    };
    cache.select = () => {
      if (!cache.valid()) cache.reset();
      const key = cache.key();
      if (cache.active?.key === key) return cache.active;
      const previous = cache.active;
      cache.active = cache.parked?.key === key ? cache.parked : {
        key, view: state.view, query: feedQuery(), items: [], nextCursor: null, pages: [],
        loaded: false, stale: false, revision: 0, updates: new Map(), dataRevision: 0,
      };
      cache.parked = previous;
      return cache.active;
    };
    cache.schedule = (view = null) => {
      if (!cache.valid() || !views.has(state.view) || (view && view !== state.view)) return;
      cache.cancel();
      const key = cache.key(), session = state.messages, owner = tag();
      const token = cache.token = {};
      cache.timer = setTimeout(() => {
        if (cache.token !== token) return;
        cache.timer = null; cache.token = null;
        if (state.feedWindows === cache && state.messages === session && tag() === owner
            && views.has(state.view) && cache.key() === key) void refreshFeed(false, true);
      }, 180);
    };
    cache.invalidate = ({ view = null, removeId = null, erase = false, refresh = true } = {}) => {
      if (!cache.valid()) { cache.reset(); return; }
      let activeChanged = false;
      for (const entry of [cache.active, cache.parked]) {
        if (!entry || (view && entry.view !== view)) continue;
        entry.revision++; entry.stale = true;
        if (removeId) {
          entry.items = entry.items.filter(drop => drop.id !== removeId);
          entry.updates.delete(removeId);
        }
        if (erase) {
          entry.countHint = Math.max(entry.countHint || 0, entry.items.length);
          entry.items = []; entry.nextCursor = null; entry.loaded = false;
          entry.updates.clear();
        }
        if (cache.current(entry)) {
          activeChanged = true;
          if (removeId || erase) {
            state.drops = entry.items.slice(); state.nextCursor = entry.nextCursor;
          }
        }
      }
      if (activeChanged) {
        state.feedRequestId = (state.feedRequestId || 0) + 1;
        state.feedController?.abort(); state.feedController = null;
        state.feedBusy = false; state.feedLoadingMore = false; state.feedRetry = null;
        el.loadMore.disabled = false; el.loadMore.setAttribute("aria-busy", "false");
        el.loading.hidden = true;
        if (erase) renderFeed();
      }
      if (refresh && (activeChanged || !cache.active)) cache.schedule(view);
      if (activeChanged && erase && cache.token) {
        el.empty.hidden = true; el.loading.hidden = false;
      }
    };
    cache.patch = (dropId, fields) => {
      if (!cache.valid()) { cache.reset(); return; }
      for (const entry of [cache.active, cache.parked]) {
        const index = entry?.items.findIndex(drop => drop.id === dropId) ?? -1;
        if (index < 0) continue;
        const previous = entry.updates.get(dropId)?.fields || {};
        const value = { ...previous, ...fields };
        entry.items[index] = { ...entry.items[index], ...fields };
        entry.updates.set(dropId, { revision: ++entry.dataRevision, fields: value });
      }
    };
    state.feedWindows = cache;
    return cache;
  }

  async function refreshOrdinaryFeed(append = false, revalidate = false, retry = null) {
    const cache = ordinaryFeedWindows();
    const previous = cache.active, window = cache.select();
    const sameDisplay = cache.displayed && previous === window && state.feedDisplayContext?.key === window.key
      && state.feedDisplayContext.socialState === state.messages;
    if (!sameDisplay) append = false;
    if (append && (state.feedBusy || cache.token || !window.nextCursor)) return;
    if (retry && (state.feedRetry !== retry || !retry.current())) return;
    cache.cancel(); state.feedRetry = null;
    const view = state.view, path = location.pathname, session = state.messages, csrf = state.csrf, viewerId = state.viewer?.id || null;
    const serial = cache.serial, revision = window.revision, dataRevision = window.dataRevision;
    const mutationVersion = state.feedMutationVersion || 0;
    // Only changes received after this read began need to be layered over its result.
    window.updates.clear();
    const requestId = state.feedRequestId = (state.feedRequestId || 0) + 1;
    state.feedController?.abort();
    const queryFor = cursor => {
      const [path, search = ""] = window.query.split("?");
      const params = new URLSearchParams(search);
      if (cursor) params.set("cursor", cursor); else params.delete("cursor");
      const suffix = String(params);
      return `${path}${suffix || window.query.includes("?") ? "?" : ""}${suffix}`;
    };
    const plan = retry?.plan || {
      append, cursor: append ? window.nextCursor : null,
      depth: append ? 1 : Math.max(1, window.pages.length),
      refill: !append && revalidate && view === "following", count: Math.max(window.countHint || 0, window.items.length),
    };
    append = plan.append;
    const query = queryFor(plan.cursor);
    const initial = state.initialHomeFeed; state.initialHomeFeed = null;
    const useInitial = !append && !window.loaded && initial?.query === query && initial.current();
    if (!useInitial) initial?.controller?.abort();
    const controller = useInitial ? initial.controller : new AbortController();
    state.feedController = controller;
    const slot = () => state.feedWindows === cache && cache.serial === serial && cache.active === window && window.key === cache.key()
      && state.messages === session && state.view === view && location.pathname === path
      && (state.viewer?.id || null) === viewerId
      && requestId === state.feedRequestId && state.feedController === controller;
    const owned = () => state.feedWindows === cache && cache.serial === serial && cache.current(window)
      && state.messages === session && state.csrf === csrf && window.revision === revision
      && requestId === state.feedRequestId && mutationVersion === (state.feedMutationVersion || 0);
    const current = () => owned() && state.feedController === controller && !controller.signal.aborted;
    if (!sameDisplay) {
      const changed = state.drops.length > 0 || window.loaded || Boolean(state.feedDisplayContext);
      state.drops = window.stale ? [] : window.items.slice(); state.nextCursor = window.stale ? null : window.nextCursor;
      if (changed) renderFeed();
    }
    cache.displayed = true;
    state.feedDisplayContext = { key: window.key, socialState: session };
    if (["saved", "mine", "following", "topics"].includes(view) && !state.viewer) {
      window.items = []; window.pages = []; window.nextCursor = null; window.loaded = true;
      state.drops = []; state.nextCursor = null; renderFeed(); state.feedController = null;
      state.feedBusy = false; state.feedLoadingMore = false; el.feedError.hidden = true;
      return true;
    }
    if (revalidate && view === "following") state.dropRefreshes?.clear();
    state.feedBusy = true; state.feedLoadingMore = append;
    el.feedError.hidden = true; el.loading.hidden = window.loaded && (sameDisplay || !window.stale);
    if (!el.loading.hidden) el.empty.hidden = true;
    el.loadMore.disabled = true; el.loadMore.setAttribute("aria-busy", "true");
    let incoming = plan.cursor;
    try {
      const stage = new Map(append ? window.items.map(drop => [drop.id, drop]) : []);
      const pages = append ? window.pages.slice() : [];
      const seen = new Set(pages.map(page => page.cursor).filter(Boolean));
      let remaining = plan.depth, result, early = useInitial ? await initial.result : null;
      if (!current()) return;
      do {
        if (incoming && seen.has(incoming)) throw Object.assign(new Error("Feed pages changed. Refresh to continue."), { feedCursorChanged: true });
        if (incoming) seen.add(incoming);
        if (early && initial.current()) {
          if (!early.ok) throw early.error;
          result = early.value; early = null;
        } else result = await api(queryFor(incoming), { signal: controller.signal });
        if (!current()) return;
        const next = result.next_cursor || null;
        if (next && (next === incoming || seen.has(next))) throw Object.assign(new Error("Feed pages changed. Refresh to continue."), { feedCursorChanged: true });
        for (const drop of result.items || []) stage.set(drop.id, drop);
        pages.push({ cursor: incoming, nextCursor: next });
        incoming = next; remaining--;
        // Membership removal may need one extra page to refill a previously loaded Following window.
        if (!remaining && plan.refill && pages.length === plan.depth && stage.size < plan.count && incoming) remaining = 1;
      } while (incoming && remaining > 0);
      if (!current()) return;
      for (const [id, update] of window.updates) {
        if (update.revision > dataRevision && stage.has(id)) stage.set(id, { ...stage.get(id), ...update.fields });
      }
      window.items = [...stage.values()]; window.pages = pages; window.nextCursor = incoming;
      window.countHint = window.items.length;
      window.loaded = true; window.stale = false;
      for (const id of window.updates.keys()) if (!stage.has(id)) window.updates.delete(id);
      state.drops = window.items.slice(); state.nextCursor = window.nextCursor;
      renderFeed(); return true;
    } catch (error) {
      if (error.name === "AbortError" || !current()) return;
      const unavailable = [401, 403, 404].includes(error.status);
      if (unavailable) {
        window.items = []; window.pages = []; window.nextCursor = null; window.loaded = false; window.updates.clear();
        if ([401, 403].includes(error.status) && cache.parked) {
          cache.parked.items = []; cache.parked.pages = []; cache.parked.nextCursor = null;
          cache.parked.loaded = false; cache.parked.updates.clear(); cache.parked.revision++;
        }
        state.drops = []; state.nextCursor = null; renderFeed(); el.empty.hidden = true;
      }
      const fresh = unavailable || error.feedCursorChanged || (error.status === 400 && incoming);
      const nextPlan = fresh ? { append: false, cursor: null, depth: unavailable ? 1 : Math.max(1, window.pages.length),
        refill: false, count: 0 } : plan;
      state.feedRetry = { current: owned, append: nextPlan.append, revalidate: nextPlan.refill, plan: nextPlan };
      el.loading.hidden = true; el.feedError.hidden = false;
      toast(error.message, true); return false;
    } finally {
      if (slot()) {
        state.feedBusy = false; state.feedLoadingMore = false;
        el.loading.hidden = true; el.loadMore.disabled = false; el.loadMore.setAttribute("aria-busy", "false");
        if (!cache.valid()) {
          cache.reset(); state.drops = []; state.nextCursor = null; state.feedDisplayContext = null;
          renderFeed(); el.empty.hidden = true; state.feedRetry = null; el.feedError.hidden = false;
        }
        if (csrf !== state.csrf && !controller.signal.aborted) { state.feedRetry = null; el.feedError.hidden = false; }
      }
      if (state.feedController === controller) state.feedController = null;
    }
  }

  function retryFeed() {
    const attempt = state.feedRetry;
    if (attempt?.current()) return refreshFeed(Boolean(attempt.append), Boolean(attempt.revalidate), attempt);
    return refreshFeed();
  }

  async function refreshFeed(append = false, revalidate = false, retry = null) {
    if (["all", "saved", "mine", "following", "topics", "topic"].includes(state.view)) {
      return refreshOrdinaryFeed(append, revalidate, retry);
    }
    // A single shared post and the exposure allocator do not own ordinary cursor windows.
    if (!["drop", "unseen"].includes(state.view) || append) return;
    state.feedWindows?.leave(); state.feedRetry = null;
    const view = state.view, path = location.pathname, query = feedQuery();
    const session = state.messages, viewerId = state.viewer?.id || null, csrf = state.csrf;
    const key = JSON.stringify([view, path, query, viewerId]);
    const previous = state.feedDisplayContext;
    if (previous && (previous.key !== key || previous.socialState !== session)) {
      state.drops = []; state.nextCursor = null; renderFeed(); el.empty.hidden = true;
    }
    state.feedDisplayContext = { key, socialState: session };
    const requestId = state.feedRequestId = (state.feedRequestId || 0) + 1;
    state.feedController?.abort(); state.initialHomeFeed?.controller?.abort(); state.initialHomeFeed = null;
    const controller = new AbortController(); state.feedController = controller;
    const owned = () => state.view === view && location.pathname === path && query === feedQuery()
      && state.messages === session && viewerId === (state.viewer?.id || null) && requestId === state.feedRequestId;
    const current = () => owned() && state.csrf === csrf && state.feedController === controller && !controller.signal.aborted;
    state.feedBusy = true; state.feedLoadingMore = false;
    el.feedError.hidden = true; el.loading.hidden = state.drops.length > 0;
    el.loadMore.disabled = true; el.loadMore.setAttribute("aria-busy", "true");
    try {
      if (view === "drop") {
        const drop = await api(`/api/drops/${encodeURIComponent(path.split("/")[2])}`, { signal: controller.signal });
        if (!current()) return;
        state.drops = [drop]; state.nextCursor = null;
        el.title.textContent = dropLabel(drop); document.title = `${dropLabel(drop)} · ${state.productName}`;
        renderFeed();
        if (typeof mountInlineComments === "function") mountInlineComments(drop);
      } else {
        const result = await api(query, { signal: controller.signal });
        if (!current()) return;
        state.drops = result.items || []; state.nextCursor = null; renderFeed();
      }
      return true;
    } catch (error) {
      if (error.name === "AbortError" || !current()) return;
      if (view === "drop" && [403, 404].includes(error.status)) {
        state.drops = []; state.nextCursor = null;
        el.title.textContent = "Drop unavailable"; document.title = `Drop unavailable · ${state.productName}`;
        renderFeed(); return false;
      }
      el.loading.hidden = true; el.feedError.hidden = false;
      state.feedRetry = { current: () => owned() && state.csrf === csrf, append: false, revalidate: false };
      toast(error.message, true); return false;
    } finally {
      if (owned() && state.feedController === controller) {
        state.feedBusy = false; state.feedLoadingMore = false;
        el.loading.hidden = true; el.loadMore.disabled = false; el.loadMore.setAttribute("aria-busy", "false");
        if (csrf !== state.csrf && !controller.signal.aborted) { state.feedRetry = null; el.feedError.hidden = false; }
      }
      if (state.feedController === controller) state.feedController = null;
    }
  }

  function makePersonCard(person) {
    const card = create("button", "person-card");
    card.type = "button";
    card.setAttribute("aria-label", `Open profile: ${accountAccessibleName(person)}`);
    const avatar = create("span", "avatar");
    styleAvatar(avatar, person);
    const copy = create("span", "person-card-copy");
    copy.append(
      accountNameNode(person),
      create("span", "", `@${person.handle}`),
    );
    if (person.bio) copy.append(create("p", "person-card-bio", person.bio));
    card.append(avatar, copy);
    card.addEventListener("click", () => openCreator(person));
    return card;
  }

  // Like Instagram's Explore: until the viewer starts a search, Discover is the search field and
  // the grid. Type tabs, Browse and section headings appear on focus, a query, a tapped type tab,
  // Browse > Search or a link that carries a search, and stay until Cancel, so a tap on a tab never
  // lands on a vanished control.
  function discoverCompact() {
    return state.discovery.mode === "search" && !state.query && state.search.type === "all"
      && !state.discovery.searchEngaged;
  }

  function paintDiscoverEngagement() {
    const compact = discoverCompact();
    const isSearch = state.discovery.mode === "search";
    el.discoverSearchTypeControl.hidden = !isSearch || compact;
    const browseRow = $(".discover-browse-row");
    if (browseRow) browseRow.hidden = compact;
    const cancel = $("#discover-search-cancel");
    if (cancel) cancel.hidden = !isSearch || compact;
    $("#discover")?.classList.toggle("is-compact", compact);
    syncDiscoverControls();
  }

  function syncDiscoverControls() {
    const controls = $(".discover-controls");
    if (controls) controls.hidden = el.discoverSearchTypeControl.hidden && el.discoverCategoryField.hidden;
  }

  // Cancel returns to the untouched grid, whatever the query, type or category had narrowed.
  // Its own read supersedes a debounced or in-flight one for the old search.
  function cancelDiscoverSearch() {
    const narrowed = Boolean(state.query) || state.search.type !== "all" || state.search.category !== "all";
    state.query = "";
    $$('[data-search-input]').forEach(input => { input.value = ""; });
    state.search.type = "all";
    state.search.category = "all";
    el.discoverCategory.value = "all";
    state.discovery.searchEngaged = false;
    el.discoverQuery.blur();
    if (location.search) history.replaceState(history.state, "", "/discover");
    paintDiscoverControls();
    // Also read when the grid on screen belongs to another search (a letter typed, then deleted).
    if (narrowed || state.search.loadedKey !== discoverSearchKey()) void refreshDiscover();
    else renderDiscover();
  }

  function paintDiscoverControls() {
    const mode = state.discovery.mode;
    const isSearch = mode === "search";
    $$('[data-search-type]').forEach(button => {
      const active = button.dataset.searchType === state.search.type;
      button.classList.toggle("is-active", active); button.setAttribute("aria-pressed", String(active));
    });
    $$('[data-discover-mode]').forEach((button) => {
      const active = button.dataset.discoverMode === mode;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-current", String(active));
    });
    // One control in place of eight tabs, carrying the name of the way you are looking right now.
    const browseLabel = $("#discover-browse-label");
    const chosen = $(`[data-discover-mode="${mode}"] span`) || $(`[data-discover-mode="${mode}"]`);
    if (browseLabel && chosen) browseLabel.textContent = chosen.textContent;
    const supportsCategory = ["search", "undiscovered", "right-now", "rising"].includes(mode);
    const hasQuery = Boolean(state.query);
    el.discoverSearchTools.hidden = false;
    el.discoverSearchField.hidden = false;
    // Keep People reachable with an empty query: it opens the paginated directory.
    el.discoverSearchTypeControl.hidden = !isSearch;
    el.discoverCategoryField.hidden = !supportsCategory || (isSearch && !hasQuery);
    paintDiscoverEngagement();
    el.worldSurface.hidden = mode !== "world";
    el.recordsSurface.hidden = mode !== "records";
    if (el.eventsSurface) el.eventsSurface.hidden = mode !== "events";
    el.discoverSurfaceNote.hidden = isSearch || ["world", "records", "events"].includes(mode);
  }

  function renderDiscoverDrops(items, optionsForDrop = () => ({}), { grid = false } = {}) {
    const retainedIds = new Set(items.map(drop => drop.id));
    // Search reads like Instagram's Explore: a square three-column grid whose
    // tiles open the post. Surfaces with allocation or impression analytics keep cards,
    // because observeCards() and the integrity observer watch .drop-card elements.
    const layout = grid ? "grid" : "cards";
    if (el.discoverDrops.dataset.layout !== layout) {
      clear(el.discoverDrops);
      el.discoverDrops.dataset.layout = layout;
    }
    el.discoverDrops.classList.toggle("discover-grid", grid);
    if (grid) {
      const tiles = new Map(Array.from(el.discoverDrops.children, tile => [tile.dataset.dropId, tile]));
      for (const child of [...el.discoverDrops.children]) {
        if (!retainedIds.has(child.dataset.dropId)) child.remove();
      }
      for (const [index, drop] of items.entries()) {
        // The cards' key: a finished language detection with no translation changes nothing shown.
        const key = feedContentKey(drop);
        let tile = tiles.get(drop.id);
        if (!tile || tile.discoverContentKey !== key) {
          tile?.remove();
          tile = profileTile(drop);
          tile.dataset.dropId = drop.id;
          tile.discoverContentKey = key;
          tile.filterDrop = drop;
          // Fold, never drop: counts, empty states and "Show anyway" stay as on cards.
          window.LouderrPersonalFilter?.applyToTile?.(tile, drop);
        }
        const current = el.discoverDrops.children[index];
        if (current !== tile) el.discoverDrops.insertBefore(tile, current || null);
      }
      return;
    }
    const existing = new Map($$(".drop-card", el.discoverDrops).map(card => [card.dataset.dropId, card]));
    for (const child of [...el.discoverDrops.children]) {
      if (!retainedIds.has(child.dataset.dropId)) child.remove();
    }
    for (const [index, drop] of items.entries()) {
      const previous = existing.get(drop.id);
      const card = updateFeedCard(previous, drop, optionsForDrop(drop));
      if (previous && card !== previous) previous.remove();
      const current = el.discoverDrops.children[index];
      if (current !== card) el.discoverDrops.insertBefore(card, current || null);
    }
  }

  function renderDiscover() {
    const mode = state.discovery.mode;
    const isSearch = mode === "search";
    state.search.renderedKey = isSearch ? state.search.loadedKey : null;
    paintDiscoverControls();
    el.discoverPeoplePaging.hidden = true;
    // Searching shows profiles before large posts; browsing stays content-first.
    // Move real sections so keyboard order matches, and keep order across pages.
    const peopleSection = el.discoverPeople.closest(".discover-people");
    const dropsSection = el.discoverDrops.closest(".discover-drops");
    const peopleFirst = isSearch && state.query.trim().length > 0;
    const firstSection = peopleFirst ? peopleSection : dropsSection;
    const secondSection = peopleFirst ? dropsSection : peopleSection;
    if (firstSection.nextElementSibling !== secondSection) el.discoverResults.insertBefore(firstSection, secondSection);
    if (mode === "events") renderEvents();
    el.discoverResults.hidden = ["world", "records", "events"].includes(mode);
    el.discoverEmpty.hidden = true;
    clear(el.discoverPeople);
    if (isSearch) refreshDiscoverTopics();
    else if ($("#discover-topics")) $("#discover-topics").hidden = true;
    if (!isSearch) {
      if (["world", "records", "events"].includes(mode)) return;
      const surface = mode === "right-now" ? "right_now" : mode;
      const labels = {
        undiscovered: ["Nobody has seen this yet", "Drops working toward their Discovery target of qualified accounts, as viewers are available."],
        "right-now": ["Right now", "What people are responding to now."],
        rising: ["Rising", "Drops gaining momentum."],
      };
      const [title, note] = labels[mode] || ["Drops", ""];
      $("#discover-drops-title").textContent = title;
      el.discoverSurfaceNote.textContent = note;
      const items = state.discovery.items || [];
      renderDiscoverDrops(items, drop => ({
        surface,
        analyticsSurface: surface,
        allocationId: mode === "undiscovered" ? drop.allocation_id : null,
        discoveryMode: mode,
      }));
      el.discoverPeople.closest(".discover-people").hidden = true;
      el.discoverDrops.closest(".discover-drops").hidden = false;
      el.discoverMorePeople.hidden = true;
      el.discoverMoreDrops.hidden = true;
      el.discoverDropsNote.textContent = `${items.length} ${items.length === 1 ? "Drop" : "Drops"}`;
      el.discoverResults.hidden = items.length === 0;
      el.discoverEmptyTitle.textContent = mode === "undiscovered" ? "Nothing is waiting for first viewers" : `Nothing ${title.toLowerCase()} yet`;
      el.discoverEmptyCopy.textContent = "Nothing here yet. The latest Drops are on Home, and yours could be next.";
      el.discoverEmpty.hidden = items.length > 0 || !state.discovery.data;
      observeCards();
      return;
    }
    $("#discover-drops-title").textContent = "Drops";
    for (const person of state.search.people) el.discoverPeople.append(makePersonCard(person));
    renderDiscoverDrops(state.search.drops, undefined, { grid: true });
    const showPeople = state.search.type !== "drops";
    const showDrops = state.search.type !== "people";
    if (showPeople && !state.search.people.length) {
      el.discoverPeople.append(create("p", "discover-section-empty", state.query ? "No matching people." : "No people to show yet."));
    }
    if (showDrops && !state.search.drops.length) {
      el.discoverDrops.append(create("p", "discover-section-empty", state.query ? "No matching Drops." : "No public Drops yet."));
    }
    el.discoverPeople.closest(".discover-people").hidden = !showPeople;
    el.discoverDrops.closest(".discover-drops").hidden = !showDrops;
    const peoplePage = state.search.peoplePage || 0;
    el.discoverPeoplePaging.hidden = !showPeople || (!peoplePage && !state.search.peopleCursor);
    el.discoverPreviousPeople.disabled = peoplePage === 0;
    el.discoverMorePeople.hidden = false;
    el.discoverMorePeople.disabled = !state.search.peopleCursor;
    el.discoverMoreDrops.hidden = !showDrops || !state.search.dropsCursor;
    const peopleCount = state.search.people.length;
    const dropCount = state.search.drops.length;
    $("#discover-people-title").textContent = state.query ? "People" : "New people";
    const peopleShown = `${peopleCount} ${peopleCount === 1 ? "person" : "people"} shown`;
    el.discoverPeopleNote.textContent = peoplePage || state.search.peopleCursor
      ? `Page ${peoplePage + 1} · ${peopleShown}` : peopleShown;
    // The count repeats what is on screen; it stays visible only as the pager's "Page N".
    el.discoverPeopleNote.classList?.toggle("is-paging", Boolean(peoplePage || state.search.peopleCursor));
    el.discoverDropsNote.textContent = `${dropCount} ${dropCount === 1 ? "Drop" : "Drops"}`;
    const count = (showPeople ? state.search.people.length : 0) + (showDrops ? state.search.drops.length : 0);
    const shortQuery = discoverSearchNeedsMoreCharacters();
    const filtered = Boolean(state.query) || state.search.category !== "all";
    el.discoverEmptyTitle.textContent = shortQuery ? "Keep typing" : filtered ? "No results found" : "Nothing to discover yet";
    el.discoverEmptyCopy.textContent = shortQuery
      ? "Search needs at least two characters."
      : filtered ? "Try a broader term or another category." : "Public profiles and Drops will appear here.";
    const canReturnToPeoplePage = showPeople && peoplePage > 0;
    el.discoverEmpty.hidden = count > 0 || !state.search.loaded || canReturnToPeoplePage;
    el.discoverResults.hidden = count === 0 && !canReturnToPeoplePage;
    observeCards();
  }

  function discoverSearchNeedsMoreCharacters(query = state.query) {
    // Match the API's NFKC -> Python whitespace split -> single-token @ rule.
    // Python includes NEL and U+001C..001F as whitespace, but excludes BOM.
    const display = String(query ?? "").normalize("NFKC")
      .split(/[\u0009-\u000d\u001c-\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+/u)
      .filter(Boolean).join(" ");
    const term = display.startsWith("@") && !display.includes(" ") ? display.slice(1) : display;
    // Case folding is deliberately later on the server; it must not admit ß.
    return Boolean(display) && Array.from(term).length < 2;
  }

  function applyDiscoverSearchRoute(params) {
    state.query = Array.from((params.get("q") || "").trim()).slice(0, 80).join("");
    state.search.type = ["all", "people", "drops"].includes(params.get("type")) ? params.get("type") : "all";
    const category = params.get("category") || "all";
    state.search.category = Array.from(el.discoverCategory.options).some(option => option.value === category) ? category : "all";
    el.discoverCategory.value = state.search.category;
    $$('[data-search-input]').forEach(input => { input.value = state.query; });
    // A link that carries a search opens it started, as the server shell does; Back keeps one.
    if (state.query || state.search.type !== "all" || state.search.category !== "all") state.discovery.searchEngaged = true;
  }

  function syncDiscoverSearchUrl() {
    if (state.view !== "discover" || state.discovery.mode !== "search" || location.pathname !== "/discover") return;
    const params = new URLSearchParams();
    if (state.query) params.set("q", state.query);
    if (state.search.type !== "all") params.set("type", state.search.type);
    if (state.search.category !== "all") params.set("category", state.search.category);
    const target = `/discover${params.size ? `?${params}` : ""}`;
    if (`${location.pathname}${location.search}` !== target) history.replaceState(history.state, "", target);
  }

  function discoverQuery(appendKind = null, peopleCursor = appendKind === "people" ? state.search.peopleCursor : null, dropsCursor = state.search.dropsCursor, scope = { query: state.query, type: state.search.type, category: state.search.category }) {
    const params = new URLSearchParams({ q: scope.query, type: appendKind || scope.type, limit: "12" });
    if (scope.category !== "all") params.set("category", scope.category);
    if (appendKind !== "drops" && peopleCursor) params.set("people_cursor", peopleCursor);
    if (appendKind === "drops" && dropsCursor) params.set("drops_cursor", dropsCursor);
    return `/api/search?${params}`;
  }

  function updateDiscoverContext() {
    const mode = state.discovery.mode;
    const labels = {
      search: [state.query ? "Search" : "Discover", state.query ? "People and Drops matching your search." : "Find people and Drops."],
      undiscovered: ["Nobody has seen this yet", "Drops receiving their first qualified views."],
      "right-now": ["Right now", "What people are responding to now."],
      rising: ["Rising", "Drops gaining momentum."],
      world: ["World", "What is happening across the room."],
      records: ["Records", "Scouts and completed rankings."],
      events: ["Events", "What is happening and when."],
    };
    const [title, description] = labels[mode] || labels.search;
    el.pageTitle.textContent = title;
    el.pageDescription.textContent = description;
    document.title = mode === "search" && state.query
      ? `${state.query} · Search · ${state.productName}`
      : `${title} · ${state.productName}`;
  }

  function discoverSearchKey() {
    return JSON.stringify([state.query, state.search.type, state.search.category,
      state.viewer?.id || null, state.viewer?.status || null, Boolean(state.sessionRestricted),
      state.paidContentVersion || 0]);
  }

  function retryDiscover() {
    const retry = state.search.retry;
    if (!retry) return refreshDiscover();
    if (!retry.current()) return;
    return refreshDiscover(retry.appendKind, retry.peopleDirection, retry);
  }

  async function readDiscoverPage(url, signal, current) {
    for (let attempt = 0; attempt < 2; attempt++) {
      if (!current()) throw Object.assign(new Error("Search cancelled"), { name: "AbortError" });
      try { return await api(url, { signal }); }
      catch (error) {
        if (!current() || signal.aborted || error.status !== 429 || attempt) throw error;
        const header = String(error.retryAfter ?? "").trim();
        const delay = /^\d+(?:\.\d+)?$/.test(header) ? Number(header) * 1000 : Date.parse(header) - Date.now();
        if (!header || !Number.isFinite(delay) || delay < 0 || delay > 2147483647) throw error;
        // Respect the server interval without replaying already-read pages.
        await new Promise((resolve, reject) => {
          let timer;
          const abort = () => {
            clearTimeout(timer); signal.removeEventListener("abort", abort);
            reject(Object.assign(new Error("Search cancelled"), { name: "AbortError" }));
          };
          if (signal.aborted) { abort(); return; }
          signal.addEventListener("abort", abort, { once: true });
          timer = setTimeout(() => { signal.removeEventListener("abort", abort); resolve(); }, delay);
        });
      }
    }
  }

  async function refreshDiscover(appendKind = null, peopleDirection = 1, retry = null, { keepPendingPeople = false } = {}) {
    if (state.view !== "discover") return;
    // Validate the failed operation before advancing either request ID.
    if (retry && (state.search.retry !== retry || !retry.current())) return;
    const mode = state.discovery.mode;
    updateDiscoverContext();
    // Controls describe the destination immediately, including while its read is pending.
    paintDiscoverControls();
    if (appendKind && mode !== "search") return;
    const discovery = state.discovery;
    const discoveryRequestId = discovery.requestId = (discovery.requestId || 0) + 1;
    discovery.controller?.abort();
    discovery.controller = null;
    if (mode !== "search") {
      state.search.requestId = (state.search.requestId || 0) + 1;
      state.search.controller?.abort();
      state.search.controller = null;
      state.search.retry = null;
      el.discoverPeople?.setAttribute("aria-busy", "false");
    }
    if (mode === "world") {
      await refreshWorld();
      return;
    }
    if (mode === "records") {
      await refreshRecords();
      return;
    }
    if (mode === "events") {
      await refreshEvents();
      return;
    }
    if (mode !== "search") {
      const controller = new AbortController();
      discovery.controller = controller;
      const viewerId = state.viewer?.id || null;
      const socialState = state.messages;
      const category = state.search.category;
      const current = () => !controller.signal.aborted && state.view === "discover"
        && state.discovery === discovery && discovery.requestId === discoveryRequestId
        && discovery.mode === mode && state.search.category === category
        && (state.viewer?.id || null) === viewerId && state.messages === socialState;
      el.discoverLoading.textContent = "Loading…";
      el.discoverLoading.hidden = false;
      el.discoverError.hidden = true;
      el.discoverResults.hidden = true;
      el.discoverEmpty.hidden = true;
      try {
        const params = new URLSearchParams({ limit: "30" });
        if (category !== "all") params.set("kind", category);
        const result = await api(`/api/discovery/${mode}?${params}`, { signal: controller.signal });
        if (!current()) return;
        state.discovery.data = result;
        state.discovery.items = result.items || [];
        renderDiscover();
      } catch (error) {
        if (error.name === "AbortError" || !current()) return;
        el.discoverError.hidden = false;
        toast(error.message, true);
      } finally {
        if (current()) el.discoverLoading.hidden = true;
        if (discovery.controller === controller) discovery.controller = null;
      }
      return;
    }
    if (appendKind && !["people", "drops"].includes(appendKind)) return;
    if (!appendKind) syncDiscoverSearchUrl();
    const search = state.search;
    const currentPeoplePage = search.peoplePage || 0;
    const requestKey = discoverSearchKey();
    // A People page the reader already asked for stays the destination when realtime recovery
    // (right after load, for one) arrives before that page's read has settled. Other refreshes, such
    // as returning from a profile, keep the settled page and cancel the pending one.
    const pendingPeople = keepPendingPeople && !appendKind && !retry && search.controller && !search.controller.signal.aborted
      && search.pendingPeople?.requestKey === requestKey ? search.pendingPeople : null;
    const keepPeoplePage = search.loaded && search.loadedKey === requestKey
      && search.renderedKey === requestKey
      && (!currentPeoplePage || Boolean(search.peoplePageCursors?.[currentPeoplePage]));
    const peoplePage = retry ? retry.plan.peoplePage : appendKind === "people" ? currentPeoplePage + peopleDirection
      : pendingPeople ? pendingPeople.page : keepPeoplePage ? currentPeoplePage : 0;
    if (appendKind === "people" && (![1, -1].includes(peopleDirection) || peoplePage < 0
      || (!retry && peopleDirection === 1 && !search.peopleCursor))) return;
    if (appendKind === "drops" && !retry && !search.dropsCursor) return;
    const peopleCursor = retry ? retry.plan.peopleCursor : appendKind === "people" && peopleDirection === 1
      ? search.peopleCursor
      : pendingPeople ? pendingPeople.cursor
      : appendKind === "people" || keepPeoplePage ? search.peoplePageCursors?.[peoplePage] || null : null;
    const loadedDropPages = Number.isSafeInteger(search.dropPages) && search.dropPages > 0
      ? search.dropPages : Math.max(1, Math.ceil(search.drops.length / 12));
    const scope = { query: state.query, type: search.type, category: search.category };
    const plan = retry ? retry.plan : {
      peoplePage, peopleCursor, dropsCursor: appendKind === "drops" ? search.dropsCursor : null,
      dropPages: !appendKind && (keepPeoplePage || pendingPeople) ? loadedDropPages : 1,
      scope, url: discoverQuery(appendKind, peopleCursor, search.dropsCursor, scope),
    };
    search.controller?.abort();
    const controller = new AbortController();
    const requestId = ++search.requestId;
    const identity = state.messages, csrf = state.csrf;
    search.controller = controller;
    search.pendingPeople = appendKind === "people" || pendingPeople
      ? { page: plan.peoplePage, cursor: plan.peopleCursor, requestKey } : null;
    search.retry = null;
    const destinationOwned = () => state.search === search && state.view === "discover"
      && state.messages === identity
      && state.discovery === discovery && discovery.requestId === discoveryRequestId
      && requestId === search.requestId && discovery.mode === "search" && requestKey === discoverSearchKey();
    const owned = () => destinationOwned() && state.csrf === csrf;
    const current = () => owned() && search.controller === controller && !controller.signal.aborted;
    const retainResults = !appendKind && keepPeoplePage;
    const changedPages = () => Object.assign(new Error("Search pages changed. Refresh the search before continuing."), { searchCursorChanged: true });
    el.discoverLoading.textContent = "Searching…";
    if (discoverSearchNeedsMoreCharacters()) {
      search.people = [];
      search.peoplePage = 0; search.peoplePageCursors = [null];
      search.drops = []; search.dropPages = 0; search.dropCursors = [];
      search.peopleCursor = null; search.dropsCursor = null;
      search.loaded = true; search.loadedKey = requestKey;
      el.discoverLoading.hidden = true; el.discoverError.hidden = true;
      el.discoverPeople.setAttribute("aria-busy", "false");
      el.discoverMoreDrops.disabled = false;
      search.controller = null; search.pendingPeople = null;
      renderDiscover(); return true;
    }
    if (!appendKind) {
      el.discoverLoading.hidden = retainResults;
      if (retainResults) {
        const hasResults = (search.type !== "drops" && (search.people.length > 0 || peoplePage > 0))
          || (search.type !== "people" && search.drops.length > 0);
        el.discoverResults.hidden = !hasResults;
        el.discoverEmpty.hidden = hasResults;
      } else {
        el.discoverResults.hidden = true; el.discoverEmpty.hidden = true;
        search.peopleCursor = null; search.dropsCursor = null;
      }
    }
    el.discoverError.hidden = true;
    el.discoverMorePeople.disabled = true; el.discoverPreviousPeople.disabled = true;
    el.discoverPeople.setAttribute("aria-busy", "true");
    el.discoverMoreDrops.disabled = true;
    let failedCursor = plan.peopleCursor || plan.dropsCursor;
    try {
      let result = await readDiscoverPage(plan.url, controller.signal, current);
      if (!current()) return;
      const people = result.people?.items || [];
      const nextPeopleCursor = result.people?.next_cursor || null;
      let drops = result.drops?.items || [];
      let dropsCursor = result.drops?.next_cursor || null;
      let dropPages = 1;
      const dropCursors = appendKind === "drops" ? [...(search.dropCursors || [])] : [];
      const seen = new Set(dropCursors);
      if (appendKind === "drops" && plan.dropsCursor) {
        if (seen.has(plan.dropsCursor)) throw changedPages();
        seen.add(plan.dropsCursor); dropCursors.push(plan.dropsCursor);
      }
      if (appendKind !== "people" && dropsCursor && seen.has(dropsCursor)) {
        throw changedPages();
      }
      if (!appendKind) {
        const window = new Map(drops.map(item => [item.id, item]));
        // Revalidate only the page depth the reader loaded, using serial cursor reads.
        // People is read once; later requests ask for Drops only.
        while (dropPages < plan.dropPages && dropsCursor) {
          if (seen.has(dropsCursor)) throw changedPages();
          seen.add(dropsCursor); dropCursors.push(dropsCursor);
          failedCursor = dropsCursor;
          result = await readDiscoverPage(discoverQuery("drops", null, dropsCursor, plan.scope), controller.signal, current);
          if (!current()) return;
          for (const item of result.drops?.items || []) window.set(item.id, item);
          dropsCursor = result.drops?.next_cursor || null;
          if (dropsCursor && seen.has(dropsCursor)) throw changedPages();
          dropPages++;
        }
        drops = [...window.values()];
      }
      if (appendKind === "people") {
        search.people = people;
        search.peoplePage = peoplePage;
        search.peoplePageCursors = (search.peoplePageCursors || [null]).slice(0, peoplePage + 1);
        search.peoplePageCursors[peoplePage] = peopleCursor;
      } else if (appendKind === "drops") {
        search.drops = [...new Map([...search.drops, ...drops].map(item => [item.id, item])).values()];
        search.dropPages = loadedDropPages + 1;
      } else {
        search.people = people;
        search.peoplePage = peoplePage;
        search.peoplePageCursors = (search.peoplePageCursors || [null]).slice(0, peoplePage + 1);
        search.peoplePageCursors[peoplePage] = peopleCursor;
        search.drops = drops; search.dropPages = dropPages;
      }
      if (appendKind !== "drops") search.peopleCursor = nextPeopleCursor;
      if (appendKind !== "people") { search.dropsCursor = dropsCursor; search.dropCursors = dropCursors; }
      search.loaded = true; search.loadedKey = requestKey;
      renderDiscover();
      if (appendKind === "people" || pendingPeople) {
        const first = el.discoverPeople.querySelector(".person-card");
        first?.focus({ preventScroll: true });
        el.discoverPeople.closest(".discover-people").scrollIntoView({ block: "start", behavior: "instant" });
      }
      return true;
    } catch (error) {
      if (error.name === "AbortError" || !current()) return;
      el.discoverError.hidden = false;
      const accessChanged = [401, 403, 404].includes(error.status);
      if (accessChanged) {
        search.loadedKey = null;
        search.retry = { appendKind: null, peopleDirection: 1, current: owned, plan: {
          peoplePage: 0, peopleCursor: null, dropsCursor: null, dropPages: 1, scope,
          url: discoverQuery(null, null, null, scope),
        } };
        el.discoverResults.hidden = true;
      } else if (error.searchCursorChanged || (error.status === 400 && failedCursor)) {
        search.retry = { appendKind: null, peopleDirection: 1, current: owned, plan: {
          peoplePage: error.status === 400 ? 0 : currentPeoplePage,
          peopleCursor: error.status === 400 ? null : search.peoplePageCursors?.[currentPeoplePage] || null,
          dropsCursor: null, dropPages: loadedDropPages, scope,
          url: discoverQuery(null, error.status === 400 ? null : search.peoplePageCursors?.[currentPeoplePage] || null, null, scope),
        } };
      } else search.retry = { appendKind, peopleDirection, current: owned, plan };
      if (!appendKind && !retainResults) el.discoverResults.hidden = true;
      toast(error.message, true);
      return false;
    } finally {
      // An auth refresh can retire this token without starting a replacement read.
      // Release only this destination's exact slot, without admitting its old data.
      if (destinationOwned() && search.controller === controller) {
        if (state.csrf !== csrf && !controller.signal.aborted) {
          search.retry = null;
          el.discoverError.hidden = false;
        }
        el.discoverLoading.hidden = true;
        el.discoverMorePeople.disabled = !search.peopleCursor;
        el.discoverPreviousPeople.disabled = !(search.peoplePage > 0);
        el.discoverPeople.setAttribute("aria-busy", "false");
        el.discoverMoreDrops.disabled = false;
      }
      if (search.controller === controller) { search.controller = null; search.pendingPeople = null; }
    }
  }

  function worldPanel(title, className = "") {
    const section = create("section", `world-panel${className ? ` ${className}` : ""}`);
    const header = create("header", "world-panel-header");
    header.append(create("h3", "", title));
    section.append(header);
    return section;
  }

  function appendWorldMetric(list, label, value) {
    const item = create("div", "world-metric");
    item.append(create("strong", "", formatCount(value || 0)), create("span", "", label));
    list.append(item);
  }

  function buildUtilityDialog(title) {
    const dialog = create("dialog", "utility-dialog");
    dialog.setAttribute("aria-label", title);
    const header = create("header", "dialog-header");
    const close = create("button", "dialog-icon-button");
    close.type = "button";
    close.setAttribute("aria-label", "Close");
    close.append(icon("x"));
    close.addEventListener("click", () => dialog.close());
    header.append(create("h2", "", title), close);
    dialog.append(header);
    dialog.addEventListener("close", () => dialog.remove(), { once: true });
    document.body.append(dialog);
    return dialog;
  }

  function utilityLabel(value) {
    return String(value || "").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  function localDateTimeValue(date) {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  }

  function utilityCard(title, eyebrow = "") {
    const card = create("article", "utility-card");
    if (eyebrow) card.append(create("span", "utility-eyebrow", eyebrow));
    card.append(create("h4", "", title));
    return card;
  }

  function utilityCardActions(card) {
    const actions = create("div", "utility-card-actions");
    card.append(actions);
    return actions;
  }

  function worldActionContext() {
    const world = state.world;
    const viewerId = state.viewer?.id || null;
    return () => state.world === world && (state.viewer?.id || null) === viewerId;
  }

  async function runWorldAction(current, { dialog = null, surface = dialog, error = null, request, receipt, accepted = null, refreshOnError = false }) {
    if (!current() || !state.capabilities.community_writes || !surface?.isConnected
        || (dialog && !dialog.open) || surface.dataset.worldActionPending) return;
    surface.dataset.worldActionPending = "true";
    const controls = $$("button", surface).filter((button) => !button.classList.contains("dialog-icon-button"));
    const previous = controls.map((button) => [button, button.disabled]);
    controls.forEach((button) => { button.disabled = true; });
    if (error) hideError(error);
    const refresh = async () => {
      if (current() && state.view === "discover" && state.discovery.mode === "world") await refreshWorld({ quiet: true });
    };
    try {
      let result;
      try { result = await request(); }
      catch (failure) {
        if (!current()) return;
        if (error && dialog?.open) showError(error, failure.message);
        else toast(failure.message, true);
        if (refreshOnError) await refresh();
        return;
      }
      if (!current()) return;
      if (accepted) accepted(result);
      if (dialog?.open) dialog.close();
      await refresh();
      if (current()) toast(typeof receipt === "function" ? receipt(result) : receipt);
    } finally {
      delete surface.dataset.worldActionPending;
      if (current()) previous.forEach(([button, disabled]) => { if (button.isConnected) button.disabled = disabled; });
    }
  }

  async function openQuestionDialog(question) {
    track("question_open", question.drop?.id || null, { question_id: question.id, question_type: question.question_type });
    const dialog = buildUtilityDialog(question.question_type === "daily" ? "Daily question" : "Question");
    dialog.dataset.worldWorkspace = "";
    const current = worldActionContext();
    dialog.dataset.dropId = question.drop?.id || "";
    const body = create("div", "utility-dialog-body");
    const loading = create("p", "utility-empty", "Loading answers…");
    body.append(loading); dialog.append(body); openDialog(dialog);
    try {
      const detail = await api(`/api/questions/${encodeURIComponent(question.id)}`);
      if (!current() || !dialog.open) return;
      clear(body);
      const heading = create("section", "utility-detail-heading");
      const prompt = create("h3", "", detail.prompt);
      heading.append(
        create("span", "utility-eyebrow", `${utilityLabel(detail.question_type)} · ${utilityLabel(detail.status)}`),
        prompt,
        create("p", "metric-note", `${formatCount(detail.verified_response_count)} verified ${detail.verified_response_count === 1 ? "answer" : "answers"}`),
      );
      const promptTranslation = translationControl(
        detail.translation,
        { prompt: detail.prompt },
        (fields) => { prompt.textContent = fields.prompt; },
      );
      if (promptTranslation) heading.append(promptTranslation);
      body.append(heading);

      if (detail.viewer_response) {
        const mine = create("section", "utility-own-response");
        const answerBody = create("p", "", detail.viewer_response.body);
        mine.append(
          create("strong", "", "Your answer"),
          answerBody,
          create("span", `utility-state-note${detail.viewer_response.counted ? " is-counted" : " is-discounted"}`, detail.viewer_response.counted ? "Counted in verified answers" : "Not counted in verified answers"),
        );
        const answerTranslation = translationControl(
          detail.viewer_response.translation,
          { body: detail.viewer_response.body },
          (fields) => { answerBody.textContent = fields.body; },
        );
        if (answerTranslation) mine.append(answerTranslation);
        if (state.capabilities.community_writes) {
          const remove = create("button", "text-button", "Remove answer"); remove.type = "button";
          remove.addEventListener("click", async () => {
            await runWorldAction(current, { dialog, receipt: "Answer removed",
              request: () => api(`/api/questions/${encodeURIComponent(detail.id)}/responses/${encodeURIComponent(detail.viewer_response.id)}`, { method: "DELETE", csrf: true }),
              accepted: () => track("question_response", detail.drop?.id || null, { question_id: detail.id, action: "remove" }),
            });
          });
          mine.append(remove);
        }
        body.append(mine);
      } else if (state.capabilities.community_writes && detail.open && !(detail.author && state.viewer && detail.author.id === state.viewer.id)) {
        if (!state.viewer) {
          const signIn = create("button", "publish-button utility-primary-action", "Sign in to answer"); signIn.type = "button";
          signIn.addEventListener("click", () => requireVerified()); body.append(signIn);
        } else if (state.viewer.email_verified) {
          const form = create("form", "utility-answer-form");
          const answer = create("textarea"); answer.required = true; answer.maxLength = 400; answer.rows = 3; answer.placeholder = "Write your answer";
          const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
          const submit = create("button", "publish-button", "Answer"); submit.type = "submit";
          form.append(dialogField("Your answer", answer), error, submit);
          form.addEventListener("submit", async (event) => {
            event.preventDefault();
            await runWorldAction(current, { dialog, error,
              request: () => api(`/api/questions/${encodeURIComponent(detail.id)}/responses`, {
                method: "POST", csrf: true, json: { body: answer.value.trim(), challenge_token: null },
              }),
              accepted: (result) => track("question_response", detail.drop?.id || null, { question_id: detail.id, action: result.status === "pending_review" ? "pending_review" : "posted" }),
              receipt: (result) => result.status === "pending_review" ? "Answer sent for review" : "Answer posted",
            });
          });
          body.append(form);
        } else {
          const verify = create("button", "publish-button utility-primary-action", "Verify email to answer"); verify.type = "button";
          verify.addEventListener("click", () => requireVerified()); body.append(verify);
        }
      }

      if (state.capabilities.community_writes && detail.question_type === "crowd" && detail.author?.id === state.viewer?.id && ["active", "closed"].includes(detail.status)) {
        const solve = create("button", "secondary-button utility-primary-action", "Mark solved"); solve.type = "button";
        solve.addEventListener("click", async () => {
          await runWorldAction(current, { dialog, receipt: "Question marked solved",
            request: () => api(`/api/questions/${encodeURIComponent(detail.id)}/solve`, { method: "POST", csrf: true }),
          });
        });
        body.append(solve);
      }

      const answers = create("section", "utility-response-list");
      answers.append(create("h4", "", "Answers"));
      for (const response of detail.responses || []) {
        const row = create("article", "utility-response");
        const responseBody = create("p", "", response.body);
        row.append(accountNameNode(response.user), create("span", "", `@${response.user.handle} · ${relativeTime(response.created_at)}`), responseBody);
        const responseTranslation = translationControl(
          response.translation,
          { body: response.body },
          (fields) => { responseBody.textContent = fields.body; },
        );
        if (responseTranslation) row.append(responseTranslation);
        answers.append(row);
      }
      if (!detail.responses?.length) answers.append(create("p", "utility-empty", "No public answers yet."));
      body.append(answers);
    } catch (error) {
      if (!current() || !dialog.open) return;
      loading.textContent = error.message; loading.classList.add("is-error");
    }
  }

  function questionCard(question, daily = false) {
    const card = utilityCard(question.prompt, daily ? "Today · UTC" : `${utilityLabel(question.category)} · ${utilityLabel(question.status)}`);
    const prompt = $("h4", card);
    const promptTranslation = translationControl(
      question.translation,
      { prompt: question.prompt },
      (fields) => { prompt.textContent = fields.prompt; },
    );
    if (promptTranslation) card.append(promptTranslation);
    const author = question.author ? question.author.display_name : (daily ? "Everyone answers the same question" : "LOUDERR");
    card.append(accountNameNode(question.author, "p", "utility-card-copy", author), create("span", "utility-card-meta", `${formatCount(question.verified_response_count)} verified ${question.verified_response_count === 1 ? "answer" : "answers"}`));
    const actions = utilityCardActions(card);
    const canAnswer = state.capabilities.community_writes && question.open && !(question.author && state.viewer && question.author.id === state.viewer.id);
    const view = create("button", canAnswer && !question.viewer_response ? "publish-button" : "secondary-button", question.viewer_response ? "View answer" : canAnswer ? "Answer" : "View answers");
    view.type = "button"; view.addEventListener("click", () => openQuestionDialog(question)); actions.append(view);
    return card;
  }

  async function openDailyQuestionArchive() {
    const dialog = buildUtilityDialog("Daily Question archive");
    dialog.dataset.worldWorkspace = "";
    const current = worldActionContext();
    const body = create("div", "utility-dialog-body"); body.append(create("p", "utility-empty", "Loading past questions…")); dialog.append(body); openDialog(dialog);
    try {
      const result = await api("/api/daily-question/archive?limit=30");
      if (!current() || !dialog.open) return;
      clear(body);
      const list = create("div", "utility-card-list");
      for (const question of result.items || []) list.append(questionCard(question, true));
      if (!list.childElementCount) list.append(create("p", "utility-empty", "No completed Daily Questions yet."));
      body.append(list);
    } catch (error) { if (!current() || !dialog.open) return; clear(body); body.append(create("p", "utility-empty is-error", error.message)); }
  }

  function openCreateQuestionDialog(initialType = "user") {
    if (!state.capabilities.community_writes) return;
    if (!requireVerified()) return;
    const dialog = buildUtilityDialog("Ask a question");
    dialog.dataset.worldWorkspace = "";
    const current = worldActionContext();
    const form = create("form", "utility-form");
    const type = create("select");
    [["user", "Question"], ["crowd", "Crowd question"]].forEach(([value, label]) => { const option = create("option", "", label); option.value = value; option.selected = value === initialType; type.append(option); });
    const category = create("select");
    ["question", "advice", "feedback", "recommendation", "help", "decision"].forEach((value) => { const option = create("option", "", utilityLabel(value)); option.value = value; category.append(option); });
    const prompt = create("textarea"); prompt.required = true; prompt.minLength = 8; prompt.maxLength = 280; prompt.rows = 3; prompt.placeholder = "What do you want to ask?";
    const context = create("textarea"); context.maxLength = 700; context.rows = 3; context.placeholder = "Add context (optional)";
    const link = create("input"); link.type = "url"; link.maxLength = 500; link.placeholder = "https:// (optional)";
    const closes = create("input"); closes.type = "datetime-local"; closes.min = localDateTimeValue(new Date(Date.now() + 60000));
    const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
    const submit = create("button", "publish-button", "Post question"); submit.type = "submit";
    form.append(dialogField("Type", type), dialogField("Category", category), dialogField("Question", prompt), dialogField("Context", context), dialogField("Link", link), dialogField("Closes · optional", closes), error, submit);
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!current() || !dialog.open || dialog.dataset.worldActionPending) return;
      await runWorldAction(current, { dialog, error,
        request: () => api("/api/questions", {
          method: "POST", csrf: true,
          json: {
            question_type: type.value, category: category.value, prompt: prompt.value.trim(), body: context.value.trim(),
            link_url: link.value.trim() || null, closes_at: closes.value ? new Date(closes.value).toISOString() : null, challenge_token: null,
          },
        }),
        receipt: (result) => result.status === "pending_review" ? "Question sent for review" : "Question posted",
      });
    });
    dialog.append(form); openDialog(dialog); requestAnimationFrame(() => prompt.focus());
  }

  function renderQuestionsWorld() {
    let dailyPanel = $('[data-world-daily-panel]', el.worldContent);
    if (!dailyPanel) {
      dailyPanel = worldPanel("Daily question", "world-questions-panel");
      dailyPanel.dataset.worldDailyPanel = "";
      const dailyHeader = $(".world-panel-header", dailyPanel);
      const archive = create("button", "text-button", "Archive"); archive.type = "button";
      archive.addEventListener("click", openDailyQuestionArchive); dailyHeader.append(archive);
      dailyPanel.append(create("div", "world-daily-content"));
      el.worldContent.append(dailyPanel);
    }
    const dailyContent = $(".world-daily-content", dailyPanel);
    clear(dailyContent);
    const dailyNotice = worldReadNotice("daily", "today’s question");
    if (dailyNotice) dailyContent.append(dailyNotice);
    else if (state.world.daily) dailyContent.append(questionCard(state.world.daily, true));
    else dailyContent.append(create("p", "utility-empty", "Today's question is being prepared."));
    let panel = $('[data-world-question-panel]', el.worldContent);
    if (!panel) {
      panel = worldPanel("Questions", "world-questions-panel");
      panel.dataset.worldQuestionPanel = "";
      const header = $(".world-panel-header", panel);
      if (state.capabilities.community_writes) {
        const questionActions = create("div", "compact-actions");
        const ask = create("button", "secondary-button", "Ask"); ask.type = "button"; ask.addEventListener("click", () => openCreateQuestionDialog("user"));
        const askCrowd = create("button", "secondary-button", "Ask crowd"); askCrowd.type = "button"; askCrowd.addEventListener("click", () => openCreateQuestionDialog("crowd"));
        questionActions.append(ask, askCrowd); header.append(questionActions);
      }
      const filters = create("div", "utility-inline-filters");
      const scopeLabel = create("label"); scopeLabel.append(create("span", "", "Show"));
      const scope = create("select"); scope.setAttribute("aria-label", "Question type"); scope.dataset.worldFilter = "questionScope";
      [["all", "All questions"], ["global", "Global"], ["crowd", "Ask Crowd"]].forEach(([value, label]) => {
        const option = create("option", "", label); option.value = value; scope.append(option);
      });
      scope.addEventListener("change", () => { state.world.questionScope = scope.value; void refreshWorld(); });
      scopeLabel.append(scope); filters.append(scopeLabel); panel.append(filters, create("div", "utility-card-list"));
      el.worldContent.append(panel);
    }
    const scope = $('[data-world-filter="questionScope"]', panel);
    const scopeValue = state.world.questionScope || "all";
    if (scope.value !== scopeValue) scope.value = scopeValue;
    const list = $(".utility-card-list", panel);
    clear(list);
    const dailyId = state.world.daily?.id;
    const notice = worldReadNotice("questions", "questions");
    if (notice) list.append(notice);
    else {
      for (const question of state.world.questions.filter((item) => item.id !== dailyId)) list.append(questionCard(question));
      if (!list.childElementCount) list.append(create("p", "utility-empty", "No other questions yet."));
    }
  }

  async function votePoll(poll, option, card, current = null) {
    if (!state.capabilities.community_writes) return;
    if (!requireVerified()) return;
    current ||= worldActionContext();
    await runWorldAction(current, { surface: card, receipt: "Vote counted", refreshOnError: true,
      request: () => api(`/api/polls/${encodeURIComponent(poll.id)}/votes`, { method: "POST", csrf: true, json: { option_id: option.id, challenge_token: null } }),
      accepted: () => track("poll_vote", poll.drop?.id || null, { poll_id: poll.id, option_id: option.id }),
    });
  }

  function pollCard(poll) {
    const card = utilityCard(poll.question, `${utilityLabel(poll.status)}${poll.category ? ` · ${utilityLabel(poll.category)}` : ""}`);
    const current = worldActionContext();
    const question = $("h4", card);
    card.dataset.analyticsEvent = "poll_open";
    card.dataset.analyticsKey = `poll:${poll.id}`;
    if (poll.drop?.id) card.dataset.dropId = poll.drop.id;
    const authorOwns = poll.author?.id === state.viewer?.id;
    card.append(accountNameNode(poll.author, "p", "utility-card-copy", "LOUDERR poll"));
    const options = create("div", "poll-options");
    const optionLabels = new Map();
    for (const option of poll.options || []) {
      const button = create("button", `poll-option${poll.viewer_option_id === option.id ? " is-selected" : ""}`); button.type = "button";
      const fill = create("span", "poll-option-fill"); fill.style.width = `${Math.max(0, Math.min(100, option.percentage || 0))}%`;
      const label = create("strong", "", option.label);
      optionLabels.set(option.id, label);
      button.append(fill, label, create("span", "", `${formatCount(option.votes)} · ${Number(option.percentage || 0).toLocaleString()}%`));
      const canVote = state.capabilities.community_writes && poll.open && !poll.viewer_option_id && !authorOwns;
      button.disabled = !state.capabilities.community_writes || !poll.open || (Boolean(state.viewer) && !canVote);
      if (canVote || (!state.viewer && poll.open)) button.addEventListener("click", () => votePoll(poll, option, card, current));
      options.append(button);
    }
    card.append(options);
    const translatedFields = {
      question: poll.question,
      ...Object.fromEntries((poll.options || []).map((option) => [`option:${option.id}`, option.label])),
    };
    const pollTranslation = translationControl(poll.translation, translatedFields, (fields) => {
      question.textContent = fields.question;
      for (const [optionId, label] of optionLabels) label.textContent = fields[`option:${optionId}`];
    });
    if (pollTranslation) card.append(pollTranslation);
    card.append(create("span", "utility-card-meta", `${formatCount(poll.verified_unique_votes)} verified ${poll.verified_unique_votes === 1 ? "vote" : "votes"}${poll.expires_at && poll.open ? ` · closes ${relativeTime(poll.expires_at)}` : ""}`));
    if (poll.drop?.id && !authorOwns) {
      const actions = utilityCardActions(card);
      const report = create("button", "secondary-button poll-report-action", "Report");
      report.type = "button";
      report.addEventListener("click", () => openDropReport(poll.drop, "poll"));
      actions.append(report);
    }
    return card;
  }

  function openCreatePollDialog() {
    if (!state.capabilities.community_writes) return;
    if (!requireVerified()) return;
    const dialog = buildUtilityDialog("Create poll");
    dialog.dataset.worldWorkspace = "";
    const current = worldActionContext();
    const form = create("form", "utility-form");
    const question = create("textarea"); question.required = true; question.minLength = 8; question.maxLength = 280; question.rows = 3; question.placeholder = "Ask one clear question";
    const choices = create("textarea"); choices.required = true; choices.rows = 5; choices.placeholder = "One choice per line\nChoice A\nChoice B";
    const category = create("input"); category.maxLength = 30; category.placeholder = "Optional";
    const expires = create("input"); expires.type = "datetime-local"; expires.min = localDateTimeValue(new Date(Date.now() + 60000));
    const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
    const submit = create("button", "publish-button", "Create poll"); submit.type = "submit";
    form.append(dialogField("Question", question), dialogField("Choices", choices, "2–6 unique choices, one per line"), dialogField("Category", category), dialogField("Closes · optional", expires), error, submit);
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!current() || !dialog.open || dialog.dataset.worldActionPending) return;
      hideError(error);
      const options = choices.value.split(/\r?\n/).map((value) => value.trim()).filter(Boolean);
      if (options.length < 2 || options.length > 6) { showError(error, "Add between 2 and 6 choices."); return; }
      await runWorldAction(current, { dialog, error,
        request: () => api("/api/polls", { method: "POST", csrf: true, json: {
          question: question.value.trim(), options, category: category.value.trim() || null,
          expires_at: expires.value ? new Date(expires.value).toISOString() : null, challenge_token: null,
        } }),
        receipt: (result) => result.status === "pending_review" ? "Poll sent for review" : "Poll created",
      });
    });
    dialog.append(form); openDialog(dialog); requestAnimationFrame(() => question.focus());
  }

  function renderPollsWorld() {
    const panel = worldPanel("Polls", "world-polls-panel");
    const header = $(".world-panel-header", panel);
    if (state.capabilities.community_writes) {
      const createPoll = create("button", "secondary-button", "New poll"); createPoll.type = "button"; createPoll.addEventListener("click", openCreatePollDialog); header.append(createPoll);
    }
    const list = create("div", "utility-card-list");
    const notice = worldReadNotice("polls", "polls");
    if (notice) list.append(notice);
    else {
      for (const poll of state.world.polls) list.append(pollCard(poll));
      if (!list.childElementCount) list.append(create("p", "utility-empty", "No polls yet."));
    }
    panel.append(list); el.worldContent.append(panel);
    observeUtilityAnalytics();
  }

  async function openCollaborationInterestDialog(collaboration) {
    if (!state.capabilities.community_writes) return;
    if (!requireVerified()) return;
    const dialog = buildUtilityDialog("I'm interested");
    dialog.dataset.worldWorkspace = "";
    const current = worldActionContext();
    dialog.dataset.dropId = collaboration.drop?.id || "";
    const form = create("form", "utility-form");
    const message = create("textarea"); message.maxLength = 280; message.rows = 4; message.placeholder = "Add a short note (optional)";
    const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
    const submit = create("button", "publish-button", "Send interest"); submit.type = "submit";
    form.append(create("p", "utility-copy", collaboration.drop?.title || `Looking for ${utilityLabel(collaboration.role_type)}`), dialogField("Message", message), error, submit);
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      await runWorldAction(current, { dialog, error,
        request: () => api(`/api/collaborations/${encodeURIComponent(collaboration.id)}/interests`, { method: "POST", csrf: true, json: { message: message.value.trim(), challenge_token: null } }),
        accepted: (result) => track("collaboration_interest", collaboration.drop?.id || null, { collaboration_id: collaboration.id, action: result.status === "pending_review" ? "pending_review" : "created" }),
        receipt: (result) => result.status === "pending_review" ? "Interest sent for review" : "Interest sent",
      });
    });
    dialog.append(form); openDialog(dialog); requestAnimationFrame(() => message.focus());
  }

  async function updateCollaborationStatus(collaboration, status, button, dialog = null, current = null) {
    if (!state.capabilities.community_writes) return;
    current ||= worldActionContext();
    await runWorldAction(current, { dialog, surface: dialog || button,
      request: () => api(`/api/collaborations/${encodeURIComponent(collaboration.id)}`, { method: "PATCH", csrf: true, json: { status } }),
      receipt: status === "completed" ? "Collaboration completed" : "Collaboration closed",
    });
  }

  async function openCollaborationDetail(collaboration) {
    track("collaboration_open", collaboration.drop?.id || null, { collaboration_id: collaboration.id });
    const dialog = buildUtilityDialog("Collaboration");
    dialog.dataset.worldWorkspace = "";
    const current = worldActionContext();
    dialog.dataset.dropId = collaboration.drop?.id || "";
    const body = create("div", "utility-dialog-body"); body.append(create("p", "utility-empty", "Loading collaboration…")); dialog.append(body); openDialog(dialog);
    try {
      const detail = await api(`/api/collaborations/${encodeURIComponent(collaboration.id)}`);
      if (!current() || !dialog.open) return;
      clear(body);
      const heading = create("section", "utility-detail-heading");
      heading.append(create("span", "utility-eyebrow", `${utilityLabel(detail.role_type)} · ${utilityLabel(detail.status)}`), create("h3", "", detail.drop?.title || `Looking for ${utilityLabel(detail.role_type)}`), create("p", "", detail.drop?.body || ""));
      const facts = create("div", "utility-facts");
      [utilityLabel(detail.location_mode), detail.location_text, utilityLabel(detail.compensation), ...(detail.skills || [])].filter(Boolean).forEach((value) => facts.append(create("span", "", value)));
      heading.append(facts); body.append(heading);
      const isOwner = detail.author?.id === state.viewer?.id;
      if (isOwner) {
        if (state.capabilities.community_writes) {
          const actions = create("div", "utility-card-actions");
          if (detail.status === "active") {
            const close = create("button", "secondary-button", "Close"); close.type = "button"; close.addEventListener("click", () => updateCollaborationStatus(detail, "closed", close, dialog, current)); actions.append(close);
          }
          if (["active", "closed"].includes(detail.status)) {
            const complete = create("button", "publish-button", "Complete"); complete.type = "button"; complete.addEventListener("click", () => updateCollaborationStatus(detail, "completed", complete, dialog, current)); actions.append(complete);
          }
          body.append(actions);
        }
        const interests = create("section", "utility-response-list"); interests.append(create("h4", "", "Interest"));
        for (const interest of detail.interests || []) {
          const row = create("article", "collaboration-interest-row");
          const copy = create("div", "utility-response"); copy.append(accountNameNode(interest.user), create("span", "", `@${interest.user.handle} · ${utilityLabel(interest.status)}`));
          if (interest.message) copy.append(create("p", "", interest.message)); row.append(copy);
          const controls = create("div", "compact-actions");
          const transitions = state.capabilities.community_writes
            ? interest.status === "interested" ? ["accepted", "declined"] : interest.status === "accepted" ? ["completed", "declined"] : []
            : [];
          for (const status of transitions) {
            const action = create("button", status === "accepted" || status === "completed" ? "secondary-button" : "text-button", utilityLabel(status)); action.type = "button";
            action.addEventListener("click", async () => {
              await runWorldAction(current, { dialog, receipt: `Interest ${status}`,
                request: () => api(`/api/collaborations/${encodeURIComponent(detail.id)}/interests/${encodeURIComponent(interest.id)}`, { method: "PATCH", csrf: true, json: { status } }),
              });
            }); controls.append(action);
          }
          row.append(controls); interests.append(row);
        }
        if (!detail.interests?.length) interests.append(create("p", "utility-empty", "No interest yet.")); body.append(interests);
      } else if (state.capabilities.community_writes && detail.status === "active") {
        if (detail.viewer_interest?.status === "interested") {
          const withdraw = create("button", "secondary-button utility-primary-action", "Withdraw interest"); withdraw.type = "button";
          withdraw.addEventListener("click", async () => {
            await runWorldAction(current, { dialog, receipt: "Interest withdrawn",
              request: () => api(`/api/collaborations/${encodeURIComponent(detail.id)}/interests/${encodeURIComponent(detail.viewer_interest.id)}`, { method: "DELETE", csrf: true }),
              accepted: () => track("collaboration_interest", detail.drop?.id || null, { collaboration_id: detail.id, action: "withdraw" }),
            });
          }); body.append(withdraw);
        } else if (!detail.viewer_interest) {
          const interest = create("button", "publish-button utility-primary-action", "I'm interested"); interest.type = "button";
          interest.addEventListener("click", () => { if (!current() || !dialog.open) return; dialog.close(); openCollaborationInterestDialog(detail); }); body.append(interest);
        } else body.append(create("p", "utility-state-note", `Your interest is ${utilityLabel(detail.viewer_interest.status).toLowerCase()}.`));
      }
    } catch (error) {
      if (!current() || !dialog.open) return;
      clear(body); body.append(create("p", "utility-empty is-error", error.message));
    }
  }

  function collaborationCard(collaboration) {
    const card = utilityCard(collaboration.drop?.title || `Looking for ${utilityLabel(collaboration.role_type)}`, `${utilityLabel(collaboration.status)} · ${utilityLabel(collaboration.role_type)}`);
    card.append(create("p", "utility-card-copy", collaboration.drop?.body || ""));
    const facts = create("div", "utility-facts");
    [utilityLabel(collaboration.location_mode), collaboration.location_text, utilityLabel(collaboration.compensation), ...(collaboration.skills || [])].filter(Boolean).slice(0, 6).forEach((value) => facts.append(create("span", "", value)));
    card.append(facts, create("span", "utility-card-meta", `${formatCount(collaboration.verified_interest_count)} verified interested${collaboration.expires_at && collaboration.status === "active" ? ` · closes ${relativeTime(collaboration.expires_at)}` : ""}`));
    const actions = utilityCardActions(card);
    const isOwner = collaboration.author?.id === state.viewer?.id;
    const canExpress = state.capabilities.community_writes && collaboration.status === "active" && !collaboration.viewer_interest && !isOwner;
    const label = isOwner ? "Manage" : collaboration.viewer_interest ? utilityLabel(collaboration.viewer_interest.status) : canExpress ? "I'm interested" : "View";
    const action = create("button", canExpress ? "publish-button" : "secondary-button", label); action.type = "button";
    action.addEventListener("click", () => canExpress ? openCollaborationInterestDialog(collaboration) : openCollaborationDetail(collaboration)); actions.append(action);
    return card;
  }

  function openCreateCollaborationDialog() {
    if (!state.capabilities.community_writes) return;
    if (!requireVerified()) return;
    const dialog = buildUtilityDialog("Find collaborators");
    dialog.dataset.worldWorkspace = "";
    const current = worldActionContext();
    const form = create("form", "utility-form");
    const role = create("select"); ["designer", "vocalist", "developer", "editor", "producer", "tester", "cofounder", "photographer", "other"].forEach((value) => { const option = create("option", "", utilityLabel(value)); option.value = value; role.append(option); });
    const description = create("textarea"); description.required = true; description.minLength = 12; description.maxLength = 700; description.rows = 4; description.placeholder = "Describe the project and what you need";
    const mode = create("select"); [["remote", "Remote"], ["hybrid", "Hybrid"], ["in_person", "In person"]].forEach(([value, label]) => { const option = create("option", "", label); option.value = value; mode.append(option); });
    const locationText = create("input"); locationText.maxLength = 100; locationText.placeholder = "Optional city or region";
    const compensation = create("select"); [["unspecified", "Not specified"], ["paid", "Paid"], ["unpaid", "Unpaid"]].forEach(([value, label]) => { const option = create("option", "", label); option.value = value; compensation.append(option); });
    const skills = create("input"); skills.placeholder = "Design, React, editing";
    const link = create("input"); link.type = "url"; link.maxLength = 500; link.placeholder = "https:// (optional)";
    const expires = create("input"); expires.type = "datetime-local"; expires.required = true; expires.value = localDateTimeValue(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)); expires.min = localDateTimeValue(new Date(Date.now() + 60000));
    const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
    const submit = create("button", "publish-button", "Post opening"); submit.type = "submit";
    form.append(dialogField("Looking for", role), dialogField("Project", description), dialogField("Location", mode), dialogField("Place · optional", locationText), dialogField("Compensation", compensation), dialogField("Skills · comma separated", skills), dialogField("Link", link), dialogField("Closes", expires), error, submit);
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!current() || !dialog.open || dialog.dataset.worldActionPending) return;
      const skillList = skills.value.split(",").map((value) => value.trim()).filter(Boolean);
      if (skillList.length > 8) { showError(error, "Add no more than 8 skills."); submit.disabled = false; return; }
      await runWorldAction(current, { dialog, error,
        request: () => api("/api/collaborations", { method: "POST", csrf: true, json: {
          role_type: role.value, description: description.value.trim(), location_mode: mode.value,
          location_text: locationText.value.trim() || null, compensation: compensation.value, skills: skillList,
          link_url: link.value.trim() || null, expires_at: new Date(expires.value).toISOString(), challenge_token: null,
        } }),
        receipt: (result) => result.status === "pending_review" ? "Opening sent for review" : "Opening posted",
      });
    });
    dialog.append(form); openDialog(dialog); requestAnimationFrame(() => description.focus());
  }

  function renderCollaborationsWorld() {
    let panel = $('[data-world-collaboration-panel]', el.worldContent);
    if (!panel) {
      panel = worldPanel("Collaborate", "world-collaborations-panel");
      panel.dataset.worldCollaborationPanel = "";
      const header = $(".world-panel-header", panel);
      if (state.capabilities.community_writes) {
        const createOpening = create("button", "secondary-button", "Post opening"); createOpening.type = "button"; createOpening.addEventListener("click", openCreateCollaborationDialog); header.append(createOpening);
      }
      const filters = create("div", "utility-inline-filters collaboration-filters");
      const roleLabel = create("label"); roleLabel.append(create("span", "", "Role"));
      const role = create("select"); role.setAttribute("aria-label", "Filter collaborations by role"); role.dataset.worldFilter = "collaborationRole";
      [["", "All roles"], ["designer", "Designer"], ["vocalist", "Vocalist"], ["developer", "Developer"], ["editor", "Editor"], ["producer", "Producer"], ["tester", "Tester"], ["cofounder", "Cofounder"], ["photographer", "Photographer"], ["other", "Other"]].forEach(([value, label]) => {
        const option = create("option", "", label); option.value = value; role.append(option);
      });
      const locationLabel = create("label"); locationLabel.append(create("span", "", "Location"));
      const location = create("select"); location.setAttribute("aria-label", "Filter collaborations by location"); location.dataset.worldFilter = "collaborationLocation";
      [["", "Anywhere"], ["remote", "Remote"], ["hybrid", "Hybrid"], ["in_person", "In person"]].forEach(([value, label]) => {
        const option = create("option", "", label); option.value = value; location.append(option);
      });
      const update = () => {
        state.world.collaborationRole = role.value;
        state.world.collaborationLocation = location.value;
        void refreshWorld();
      };
      role.addEventListener("change", update); location.addEventListener("change", update);
      roleLabel.append(role); locationLabel.append(location); filters.append(roleLabel, locationLabel);
      panel.append(filters, create("div", "utility-card-list")); el.worldContent.append(panel);
    }
    for (const key of ["collaborationRole", "collaborationLocation"]) {
      const control = $(`[data-world-filter="${key}"]`, panel);
      const value = state.world[key] || "";
      if (control.value !== value) control.value = value;
    }
    const list = $(".utility-card-list", panel);
    clear(list);
    const notice = worldReadNotice("collaborations", "collaborations");
    if (notice) list.append(notice);
    else {
      for (const collaboration of state.world.collaborations) list.append(collaborationCard(collaboration));
      if (!list.childElementCount) list.append(create("p", "utility-empty", "No open collaborations yet."));
    }
  }

  function openRegionDialog() {
    if (!state.capabilities.community_writes) return;
    if (!requireVerified()) return;
    track("world_pulse_interaction", null, { action: "open_region" });
    const dialog = buildUtilityDialog("Your region");
    dialog.dataset.worldWorkspace = "";
    const current = worldActionContext();
    let loaded = false;
    let reading = false;
    const form = create("form", "utility-form");
    const country = create("input");
    country.name = "country"; country.required = true; country.minLength = 2; country.maxLength = 2;
    country.autocapitalize = "characters"; country.placeholder = "NO";
    const region = create("input");
    region.name = "region"; region.maxLength = 80; region.placeholder = "Oslo";
    const city = create("input");
    city.name = "city"; city.maxLength = 80; city.placeholder = "Oslo";
    const field = (label, input, hint = "") => {
      const wrapper = create("label", "field");
      wrapper.append(create("span", "", label), input);
      if (hint) wrapper.append(create("small", "", hint));
      return wrapper;
    };
    const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
    const loading = create("p", "metric-note", "Loading your saved region…"); loading.setAttribute("role", "status");
    const retry = create("button", "secondary-button", "Retry"); retry.type = "button"; retry.hidden = true;
    const actions = create("div", "utility-actions");
    const remove = create("button", "text-button", "Remove"); remove.type = "button";
    const save = create("button", "publish-button", "Save"); save.type = "submit";
    actions.append(remove, save);
    form.append(
      create("p", "utility-copy", "Only a country and optional coarse labels are stored. Precise location is never requested."),
      field("Country code", country, "Two letters"), field("Region · optional", region), field("City · optional", city),
      loading, error, retry, actions,
    );
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!loaded) return;
      const countryCode = country.value.trim().toUpperCase();
      await runWorldAction(current, { dialog, error, receipt: "Region saved",
        request: () => api("/api/world/map/me", {
          method: "PUT", csrf: true,
          json: { country_code: countryCode, region_label: region.value.trim() || null, city_label: city.value.trim() || null, challenge_token: null },
        }),
        accepted: () => track("world_map_filter", null, { action: "save_region", country_code: countryCode }),
      });
    });
    remove.addEventListener("click", async () => {
      if (!loaded || remove.hidden) return;
      await runWorldAction(current, { dialog, error, receipt: "Region removed",
        request: () => api("/api/world/map/me", { method: "DELETE", csrf: true }),
        accepted: () => track("world_map_filter", null, { action: "remove_region" }),
      });
    });
    const controls = [country, region, city, save, remove];
    const loadSavedRegion = async () => {
      if (reading || !current() || !dialog.open) return;
      reading = true; loaded = false;
      controls.forEach((control) => { control.disabled = true; });
      hideError(error); retry.hidden = true; retry.disabled = true;
      loading.textContent = "Loading your saved region…";
      try {
        const result = await api("/api/world/map/me");
        if (!current() || !dialog.open) return;
        if (!result || !Object.hasOwn(result, "region")) throw new Error("Missing saved region response");
        const saved = result.region;
        country.value = saved?.country_code || "";
        region.value = saved?.region_label || "";
        city.value = saved?.city_label || "";
        loaded = true;
        controls.forEach((control) => { control.disabled = false; });
        remove.hidden = !saved;
        loading.textContent = saved ? "" : "You haven't shared a region.";
        if (document.activeElement === retry || document.activeElement === country) country.focus();
      } catch (failure) {
        if (!current() || !dialog.open) return;
        loading.textContent = "";
        showError(error, "Couldn't load your saved region. Try again.");
        retry.hidden = false;
      } finally {
        reading = false;
        if (current() && dialog.open) retry.disabled = false;
      }
    };
    retry.addEventListener("click", loadSavedRegion);
    controls.forEach((control) => { control.disabled = true; });
    dialog.append(form);
    openDialog(dialog);
    loadSavedRegion();
  }

  async function openMicEntryDialog() {
    if (!state.capabilities.community_writes) return;
    if (!requireVerified()) return;
    const world = state.world;
    const viewerId = state.viewer.id;
    const current = () => state.world === world && state.viewer?.id === viewerId;
    track("mic_interaction", null, { action: "open_entry" });
    const dialog = buildUtilityDialog("Enter the Mic");
    dialog.dataset.worldWorkspace = "";
    dialog.dataset.micEntry = "loading";
    const body = create("div", "utility-form");
    body.append(create("p", "utility-copy", "Choose one of your eligible Drops. The fair queue decides the next turn."));
    const loading = create("p", "metric-note", "Loading your Drops…");
    body.append(loading);
    dialog.append(body);
    openDialog(dialog);
    try {
      const result = await api(`/api/drops?author_id=${encodeURIComponent(state.viewer.id)}`);
      if (!current() || !dialog.open) return;
      dialog.dataset.micEntry = "ready";
      loading.remove();
      const items = (result.items || []).filter((drop) => !drop.structured_type);
      if (!items.length) {
        body.append(create("p", "utility-empty", "Publish an eligible Drop before entering the Mic."));
        return;
      }
      const form = create("form", "utility-form-inner");
      const select = create("select"); select.required = true; select.setAttribute("aria-label", "Drop for the Mic");
      for (const drop of items) {
        const option = create("option", "", dropLabel(drop)); option.value = drop.id; select.append(option);
      }
      const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
      const submit = create("button", "publish-button", "Join queue"); submit.type = "submit";
      form.append(select, error, submit);
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        if (!current() || !dialog.open) return;
        hideError(error); submit.disabled = true;
        try {
          const entry = await api("/api/world/mic/entries", {
            method: "POST", csrf: true, json: { drop_id: select.value, challenge_token: null },
          });
          if (!current()) return;
          world.micEntry = entry;
          track("mic_interaction", select.value, { action: "join_queue", entry_id: entry.id });
          clear(form);
          form.append(create("strong", "utility-success", "Added to the Mic queue"));
          const withdraw = create("button", "secondary-button", "Withdraw"); withdraw.type = "button";
          withdraw.addEventListener("click", () => withdrawMicEntry(entry, withdraw, dialog));
          form.append(create("p", "metric-note", "You can close this window. Your place remains in the queue."), withdraw);
        } catch (requestError) { if (!current()) return; showError(error, requestError.message); submit.disabled = false; }
      });
      body.append(form);
    } catch (error) {
      if (!current() || !dialog.open) return;
      dialog.dataset.micEntry = "ready";
      loading.textContent = error.message;
      loading.classList.add("is-error");
    }
  }

  async function withdrawMicEntry(entry, button, dialog = null) {
    if (!state.capabilities.community_writes) return;
    const world = state.world;
    const viewerId = state.viewer?.id || null;
    const current = () => state.world === world && (state.viewer?.id || null) === viewerId;
    button.disabled = true;
    try {
      await api(`/api/world/mic/entries/${encodeURIComponent(entry.id)}`, { method: "DELETE", csrf: true });
      if (!current()) return;
      world.micEntry = { ...entry, status: "withdrawn" };
      track("mic_interaction", entry.drop_id || null, { action: "withdraw", entry_id: entry.id });
      dialog?.close();
      if (state.discovery.mode === "world" && state.world.view === "pulse") renderWorld();
      toast("Mic entry withdrawn");
    } catch (error) { if (!current()) return; toast(error.message, true); button.disabled = false; }
  }

  function micHistoryCard(slot) {
    const title = slot.drop?.title || "Unavailable Drop";
    const holder = slot.holder?.display_name || "Unavailable creator";
    const card = utilityCard(title, `${utilityLabel(slot.status)} · ${new Date(slot.starts_at).toLocaleDateString()}`);
    card.dataset.worldMicSlot = slot.id;
    card.append(accountNameNode(slot.holder, "p", "utility-card-copy", holder));
    if (slot.reason) card.append(create("span", "utility-card-meta", slot.reason));
    return card;
  }

  function renderWorldHistory(dialog, key) {
    const body = $(".utility-dialog-body", dialog);
    const focused = document.activeElement;
    clear(body);
    const list = create("div", "utility-card-list");
    const notice = worldReadNotice(key, key === "micHistory" ? "Mic history" : "Moment history");
    if (notice) list.append(notice);
    else if (key === "micHistory") {
      for (const slot of state.world.micHistory || []) list.append(micHistoryCard(slot));
      if (!list.childElementCount) list.append(create("p", "utility-empty", "No completed Mic turns yet."));
    } else {
      for (const moment of state.world.moments || []) {
        const card = utilityCard(moment.title, `${utilityLabel(moment.status)} · ${new Date(moment.starts_at).toLocaleDateString()}`);
        card.dataset.worldMoment = moment.id;
        if (moment.target?.id) card.dataset.dropId = moment.target.id;
        if (moment.summary) card.append(create("p", "utility-card-copy", moment.summary));
        card.append(create("span", "utility-card-meta", `${new Date(moment.starts_at).toLocaleString()} – ${new Date(moment.ends_at).toLocaleString()}`));
        if (moment.target) {
          const open = create("button", "text-button", "Open Drop"); open.type = "button";
          open.addEventListener("click", () => openDrop(moment.target.id, dialog));
          utilityCardActions(card).append(open);
        }
        list.append(card);
      }
      if (!list.childElementCount) list.append(create("p", "utility-empty", "No completed Moments yet."));
    }
    body.append(list);
    if (focused && !focused.isConnected && dialog.open) $(".dialog-icon-button", dialog)?.focus({ preventScroll: true });
  }

  function openMicHistory() {
    track("mic_interaction", null, { action: "open_history" });
    const dialog = buildUtilityDialog("Mic history");
    dialog.dataset.worldWorkspace = "";
    dialog.dataset.worldHistory = "micHistory";
    dialog.append(create("div", "utility-dialog-body"));
    renderWorldHistory(dialog, "micHistory");
    openDialog(dialog);
  }

  function openMomentArchive() {
    const dialog = buildUtilityDialog("Moments");
    dialog.dataset.worldWorkspace = "";
    dialog.dataset.worldHistory = "moments";
    dialog.append(create("div", "utility-dialog-body"));
    renderWorldHistory(dialog, "moments");
    openDialog(dialog);
  }

  function worldReadStatus(key) {
    return state.world.readStatus ? (state.world.readStatus[key] || "idle") : "ready";
  }

  function beginWorldRead(keys) {
    const world = state.world;
    world.readAttempts ||= {};
    world.readStatus ||= {};
    const tokens = keys.map((key) => {
      const token = {};
      world.readAttempts[key] = token;
      world.readStatus[key] ||= "loading";
      return token;
    });
    return { world, keys, tokens };
  }

  function acceptWorldRead(batch, results) {
    const world = batch.world;
    if (state.world !== world) return false;
    const lists = new Set(["micHistory", "moments", "questions", "polls", "collaborations"]);
    const historiesChanged = new Set();
    let changed = false;
    batch.keys.forEach((key, index) => {
      if (world.readAttempts[key] !== batch.tokens[index]) return;
      const isHistory = key === "micHistory" || key === "moments";
      const previousStatus = world.readStatus[key];
      const previousHistory = isHistory ? JSON.stringify(world[key]) : null;
      const result = results[index];
      const ready = result.status === "fulfilled";
      world.readStatus[key] = ready ? "ready" : "error";
      world[key] = lists.has(key) ? (ready ? result.value?.items || [] : []) : (ready ? result.value : null);
      if (key === "mic" && ready && Object.hasOwn(world.mic || {}, "viewer_entry")) world.micEntry = world.mic.viewer_entry;
      if (isHistory && (previousStatus !== world.readStatus[key] || previousHistory !== JSON.stringify(world[key]))) historiesChanged.add(key);
      changed = true;
    });
    if (historiesChanged.size) $$(".utility-dialog[data-world-history]").forEach((dialog) => {
      if (dialog.open && historiesChanged.has(dialog.dataset.worldHistory)) renderWorldHistory(dialog, dialog.dataset.worldHistory);
    });
    return changed;
  }

  function worldReadNotice(key, label, retry = () => refreshWorld({ quiet: true })) {
    const status = worldReadStatus(key);
    if (status === "ready") return null;
    const world = state.world;
    const notice = create("div", "utility-read-state");
    notice.dataset.worldSource = key;
    notice.dataset.worldReadStatus = status;
    const message = status === "error" ? `Couldn't load ${label}. Try again.`
      : status === "loading" ? `Loading ${label}…` : `${label} hasn't loaded yet.`;
    const copy = create("p", status === "error" ? "utility-empty is-error" : "utility-empty", message);
    copy.setAttribute("role", status === "error" ? "alert" : "status");
    notice.append(copy);
    if (status !== "loading") {
      const button = create("button", "secondary-button", status === "error" ? "Retry" : "Load");
      button.type = "button";
      button.setAttribute("aria-label", `${status === "error" ? "Retry" : "Load"} ${label}`);
      button.addEventListener("click", async () => {
        if (state.world !== world || button.disabled) return;
        button.disabled = true;
        try { await retry(); } finally { button.disabled = false; }
      });
      notice.append(button);
    }
    return notice;
  }

  async function loadWorldLens(countryCode, region = null, city = null) {
    const params = new URLSearchParams({ country_code: countryCode, limit: "30" });
    if (region) params.set("region", region);
    if (city) params.set("city", city);
    // Both call sites are bare click handlers on country and region buttons, so picking a second
    // place before the first responds is ordinary use. Without the token the slower answer wins
    // and the map paints one place under another place's selection, with nothing to say so.
    const world = state.world;
    const viewerId = state.viewer?.id || null;
    const requestId = ++world.lensRequestId;
    const current = () => state.world === world && (state.viewer?.id || null) === viewerId
      && requestId === world.lensRequestId;
    const batch = beginWorldRead(["lens"]);
    try {
      const lens = await api(`/api/world/map/lens?${params}`);
      if (!current()) return;
      world.lensSelection = { countryCode, region, city };
      acceptWorldRead(batch, [{ status: "fulfilled", value: lens }]);
      track("world_map_filter", null, { country_code: countryCode, region: region || null, city: city || null });
      if (state.discovery.mode === "world") renderWorld();
    } catch (error) {
      if (!current()) return;
      world.lensSelection = { countryCode, region, city };
      acceptWorldRead(batch, [{ status: "rejected", reason: error }]);
      if (state.discovery.mode === "world") renderWorld();
      else toast(error.message, true);
    }
  }

  function renderRoomAffordances() {
    const pulse = state.world.pulse;
    const mic = state.world.mic?.current;
    const moment = state.world.moment?.current;
    const posts = Number(pulse?.metrics?.posts_last_hour);
    const available = (key) => worldReadStatus(key) === "ready";
    const pendingLabel = (key) => worldReadStatus(key) === "error" ? "Unavailable"
      : worldReadStatus(key) === "loading" ? "Loading…" : "Open World";
    $$('[data-room-now-pulse]').forEach((node) => {
      node.textContent = available("pulse") && Number.isFinite(posts) ? `${formatCount(posts)} this hour` : pendingLabel("pulse");
    });
    $$('[data-room-now-mic]').forEach((node) => {
      node.textContent = "";
      if (!available("mic")) node.textContent = pendingLabel("mic");
      else if (mic) node.append(accountNameNode(mic.holder, "span", "", "Live"), create("span", "", ` · ${Math.max(1, Math.ceil((mic.seconds_remaining || 0) / 60))}m`));
      else node.textContent = "Between turns";
    });
    $$('[data-room-now-moment]').forEach((node) => {
      node.textContent = !available("moment") ? pendingLabel("moment") : moment ? moment.title : "None active";
    });
    const allAvailable = ["pulse", "mic", "moment"].every(available);
    const quiet = allAvailable && !(Number.isFinite(posts) && posts > 0) && !mic && !moment;
    const here = Number(state.presence) || 0;
    const summary = quiet
      ? (here > 1 ? `Quiet right now · ${formatCount(here)} here` : "Quiet right now")
      : (available("mic") && mic ? "Someone is on the Mic" : available("moment") && moment ? "A Moment is happening"
        : available("pulse") && Number.isFinite(posts) && posts > 0 ? "Live right now"
          : allAvailable ? "Quiet right now"
            : ["pulse", "mic", "moment"].some((key) => worldReadStatus(key) === "error")
              ? "Some room updates are unavailable" : "Room updates");
    $$(".room-now").forEach((node) => { node.classList.toggle("is-quiet", quiet); node.dataset.summary = summary; });
  }

  async function refreshRoomAffordances() {
    const world = state.world;
    const viewerId = state.viewer?.id || null, social = state.messages, csrf = state.csrf;
    if (world.roomNowRequest && world.roomNowOwner?.viewerId === viewerId
        && world.roomNowOwner.social === social && world.roomNowOwner.csrf === csrf) return world.roomNowRequest;
    world.roomNowOwner = { viewerId, social, csrf };
    const batch = beginWorldRead(["pulse", "mic", "moment"]);
    const request = Promise.allSettled([
      api("/api/world/pulse"), api("/api/world/mic"), api("/api/world/moments/current"),
    ]).then((results) => {
      if (state.world !== world || world.roomNowRequest !== request || (state.viewer?.id || null) !== viewerId
          || state.messages !== social || state.csrf !== csrf) return;
      acceptWorldRead(batch, results);
      world.roomNowLoaded = true;
      if (state.view === "discover" && state.discovery.mode === "world") renderWorld();
      else renderRoomAffordances();
    }).finally(() => {
      if (world.roomNowRequest === request) { world.roomNowRequest = null; world.roomNowOwner = null; }
    });
    world.roomNowRequest = request;
    return request;
  }

  function scheduleRoomAffordanceRefresh() {
    clearTimeout(state.world.roomNowRefreshTimer);
    state.world.roomNowRefreshTimer = setTimeout(() => refreshRoomAffordances().catch(() => {}), 220);
  }

  async function openRoomFeature(feature) {
    state.discovery.mode = "world";
    state.world.view = "pulse";
    const world = state.world;
    world.pendingFocus = feature;
    const url = new URL("/discover", location.origin);
    url.searchParams.set("mode", "world");
    url.searchParams.set("view", "pulse");
    url.searchParams.set("focus", feature);
    history.pushState({}, "", `${url.pathname}${url.search}`);
    // The foreground render consumes this intent. Archives must not delay focus.
    const loading = setView("discover");
    if (state.world === world && (state.view !== "discover" || state.discovery.mode !== "world")) {
      world.pendingFocus = null;
    }
    return loading;
  }

  function worldCountryActivity(worldMap) {
    const figure = create("figure", "world-choropleth");
    const visual = create("div", "world-map-visual");
    const map = create("img", "world-map-image");
    map.src = "/static/world-map.svg";
    map.alt = "";
    map.setAttribute("aria-hidden", "true");
    const countries = worldMap?.countries || [];
    const max = Math.max(1, ...countries.map((country) => Number(country.active_accounts) || 0));
    const svgNamespace = "http://www.w3.org/2000/svg";
    const overlay = document.createElementNS(svgNamespace, "svg");
    overlay.classList.add("world-map-activity");
    overlay.setAttribute("viewBox", "0 0 2000 1001");
    overlay.setAttribute("preserveAspectRatio", "xMidYMid meet");
    overlay.setAttribute("aria-hidden", "true");
    const controls = create("div", "world-country-list world-country-choropleth");
    controls.setAttribute("role", "list");
    controls.setAttribute("aria-label", "Countries with enough people to show, shaded by activity");
    for (const country of countries) {
      const count = Math.max(0, Number(country.active_accounts) || 0);
      const level = Math.max(1, Math.min(4, Math.ceil((count / max) * 4)));
      const code = String(country.country_code || "").trim().toLowerCase();
      if (WORLD_COUNTRY_CODES.has(code)) {
        const countryShape = document.createElementNS(svgNamespace, "use");
        countryShape.setAttribute("href", `/static/world-map.svg#${code}`);
        countryShape.classList.add("world-country-shape", `activity-level-${level}`);
        overlay.append(countryShape);
      }
      const button = create("button", `world-country activity-level-${level}`); button.type = "button";
      button.setAttribute("role", "listitem");
      button.setAttribute("aria-label", `${country.country_code}: ${formatCount(count)} active accounts, activity level ${level} of 4`);
      button.append(create("strong", "", country.country_code), create("span", "", `${formatCount(count)} active`));
      button.addEventListener("click", () => loadWorldLens(country.country_code));
      controls.append(button);
    }
    if (!controls.childElementCount) controls.append(create("p", "utility-empty", "No country has enough people to show yet."));
    visual.append(map, overlay);
    const caption = create("figcaption", "metric-note");
    caption.append(document.createTextNode("Aggregate country activity only · geometry: "));
    const source = create("a", "", "VectorAtlas"); source.href = "https://github.com/melenaos/Menelabs.VectorAtlas"; source.target = "_blank"; source.rel = "noopener noreferrer";
    const license = create("a", "", "CC BY 4.0"); license.href = "https://creativecommons.org/licenses/by/4.0/"; license.target = "_blank"; license.rel = "noopener noreferrer";
    caption.append(source, document.createTextNode(" / Natural Earth · "), license, document.createTextNode(" · no coordinates or individual locations"));
    figure.append(visual, controls, caption);
    return figure;
  }

  function syncWorldView() {
    const views = ["pulse", "questions", "polls", "collaborations"];
    const selected = views.includes(state.world.view) ? state.world.view : "pulse";
    state.world.view = selected;
    $$('[data-world-view]').forEach((button) => {
      const view = button.dataset.worldView;
      const active = view === selected;
      button.id = `world-view-${view}`;
      button.setAttribute("aria-controls", `world-section-${view}`);
      button.setAttribute("aria-selected", String(active));
      button.classList.toggle("is-active", active);
      button.tabIndex = active ? 0 : -1;
    });
    for (const section of el.worldContent.children) {
      const view = section.dataset.worldSection;
      if (!views.includes(view)) continue;
      section.hidden = view !== selected;
      section.setAttribute("role", "tabpanel");
      section.setAttribute("aria-labelledby", `world-view-${view}`);
      section.tabIndex = 0;
    }
  }

  function renderWorld() {
    syncWorldView();
    const retained = Array.from(el.worldContent.children).find(section => section.dataset.worldSection === state.world.view);
    const owner = retained?.worldRenderOwner;
    const renderer = { questions: renderQuestionsWorld, collaborations: renderCollaborationsWorld }[state.world.view];
    // Keep filter controls attached through loading and result updates. Scope the
    // retained headers and handlers to the same account, World and capability state.
    if (renderer && owner?.world === state.world && owner.messages === state.messages
        && owner.viewerId === (state.viewer?.id || null) && owner.writes === Boolean(state.capabilities.community_writes)) {
      const host = el.worldContent;
      el.worldContent = retained;
      try { renderer(); } finally { el.worldContent = host; }
      renderRoomAffordances();
      observeCards();
      observeUtilityAnalytics();
      return;
    }
    clear(el.worldContent);
    // Each tab owns one section, so direct routes and clicks show the same content.
    const pulseAnchor = create("section", "world-section");
    pulseAnchor.id = "world-section-pulse";
    pulseAnchor.dataset.worldSection = "pulse";
    el.worldContent.append(pulseAnchor);
    const sectionRenderers = [
      ["questions", renderQuestionsWorld],
      ["polls", renderPollsWorld],
      ["collaborations", renderCollaborationsWorld],
    ];
    const worldHost = el.worldContent;
    for (const [view, renderSection] of sectionRenderers) {
      const section = create("section", "world-section");
      section.id = `world-section-${view}`;
      section.dataset.worldSection = view;
      el.worldContent = section;
      try {
        if (view === state.world.view) {
          renderSection();
          section.worldRenderOwner = { world: state.world, messages: state.messages,
            viewerId: state.viewer?.id || null, writes: Boolean(state.capabilities.community_writes) };
        }
      } finally { el.worldContent = worldHost; }
      worldHost.append(section);
    }
    if (state.world.view !== "pulse") {
      syncWorldView();
      renderRoomAffordances();
      observeCards();
      observeUtilityAnalytics();
      return;
    }
    const pulse = state.world.pulse;
    const pulsePanel = worldPanel("World pulse", "world-pulse-panel");
    pulsePanel.dataset.worldFeature = "pulse";
    const pulseNotice = worldReadNotice("pulse", "World activity");
    if (pulseNotice) pulsePanel.append(pulseNotice);
    else if (pulse?.metrics) {
      pulsePanel.dataset.analyticsEvent = "world_pulse_impression";
      pulsePanel.dataset.analyticsKey = "world-pulse";
      const metrics = create("div", "world-metrics");
      appendWorldMetric(metrics, "connections", pulse.metrics.connections_here_now);
      appendWorldMetric(metrics, "posts this hour", pulse.metrics.posts_last_hour);
      appendWorldMetric(metrics, "creators this hour", pulse.metrics.creators_posting_last_hour);
      appendWorldMetric(metrics, "countries visible", pulse.metrics.countries_above_privacy_threshold);
      pulsePanel.append(
        metrics,
        create("p", "metric-note", `Updated ${relativeTime(pulse.generated_at)}.`),
      );
    } else pulsePanel.append(create("p", "utility-empty", "World activity is warming up."));
    el.worldContent.append(pulsePanel);

    const moment = state.world.moment?.current;
    const momentNotice = worldReadNotice("moment", "current Moments");
    if (moment && !momentNotice) {
      const panel = worldPanel("Current moment", "world-moment-panel");
      panel.dataset.worldFeature = "moment";
      panel.dataset.analyticsEvent = "moment_open";
      panel.dataset.analyticsKey = `moment:${moment.id}`;
      if (moment.target?.id) panel.dataset.dropId = moment.target.id;
      const momentHeader = $(".world-panel-header", panel);
      const archive = create("button", "text-button", "Past moments"); archive.type = "button"; archive.addEventListener("click", openMomentArchive); momentHeader.append(archive);
      panel.append(
        create("strong", "world-feature-title", moment.title),
        create("p", "", moment.summary),
        create("span", "utility-card-meta", `${Math.max(1, Math.ceil((moment.seconds_remaining || 0) / 60))} min left`),
      );
      if (moment.target) panel.append(makeDropCard(moment.target));
      el.worldContent.append(panel);
    } else {
      const panel = worldPanel("Moments", "world-moment-panel");
      panel.dataset.worldFeature = "moment";
      const archive = create("button", "text-button", "View history"); archive.type = "button"; archive.addEventListener("click", openMomentArchive); $(".world-panel-header", panel).append(archive);
      panel.append(momentNotice || create("p", "utility-empty", "No global Moment is active.")); el.worldContent.append(panel);
    }

    const mic = state.world.mic?.current;
    const micPanel = worldPanel("The Mic", "world-mic-panel");
    micPanel.dataset.worldFeature = "mic";
    if (mic?.id && !mic.drop) {
      micPanel.dataset.analyticsEvent = "mic_impression";
      micPanel.dataset.analyticsKey = `mic:${mic.id}`;
    }
    const micHeader = $(".world-panel-header", micPanel);
    const micHeaderActions = create("div", "compact-actions");
    const micHistory = create("button", "text-button", "History"); micHistory.type = "button"; micHistory.addEventListener("click", openMicHistory);
    micHeaderActions.append(micHistory);
    if (state.capabilities.community_writes) {
      const micAction = create("button", "secondary-button", "Enter"); micAction.type = "button";
      micAction.addEventListener("click", openMicEntryDialog); micHeaderActions.append(micAction);
    }
    micHeader.append(micHeaderActions);
    const micEntry = state.world.micEntry || state.world.mic?.viewer_entry;
    const micNotice = worldReadNotice("mic", "the Mic");
    if (micNotice) micPanel.append(micNotice);
    else if (mic) {
      const meta = create("div", "world-feature-meta");
      meta.append(accountNameNode(mic.holder, "strong", "", "Mic holder"), create("span", "", `${Math.ceil((mic.seconds_remaining || 0) / 60)} min left · ${formatCount(mic.verified_unique_impressions)} verified viewers`));
      micPanel.append(meta);
      if (mic.drop) micPanel.append(makeDropCard(mic.drop, { surface: "mic", analyticsSurface: "mic" }));
      else micPanel.append(create("p", "utility-empty", "This Mic Drop is hidden by your safety controls."));
    } else micPanel.append(create("p", "utility-empty", "The Mic is between turns."));
    if (mic?.holder?.id && mic.holder.id === state.viewer?.id) {
      micPanel.append(create("p", "utility-state-note is-counted", "Your Mic turn is live."));
    } else if (micEntry) {
      const entryState = create("div", "utility-entry-state");
      entryState.append(create("span", "", micEntry.status === "eligible" ? "Your Drop is queued" : `Mic entry ${utilityLabel(micEntry.status).toLowerCase()}`));
      if (state.capabilities.community_writes && micEntry.status === "eligible") {
        const withdraw = create("button", "text-button", "Withdraw"); withdraw.type = "button";
        withdraw.addEventListener("click", () => withdrawMicEntry(micEntry, withdraw)); entryState.append(withdraw);
      }
      micPanel.append(entryState);
    }
    el.worldContent.append(micPanel);

    const worldMap = state.world.map;
    const mapPanel = worldPanel("Map", "world-map-panel");
    if (worldMap?.generated_at) {
      mapPanel.dataset.analyticsEvent = "world_map_open";
      mapPanel.dataset.analyticsKey = "world-map";
    }
    const mapHeader = $(".world-panel-header", mapPanel);
    if (state.viewer && state.capabilities.community_writes) {
      const locationButton = create("button", "secondary-button", "Your region");
      locationButton.type = "button"; locationButton.addEventListener("click", openRegionDialog); mapHeader.append(locationButton);
    }
    const mapNotice = worldReadNotice("map", "the map");
    if (mapNotice) mapPanel.append(mapNotice);
    else {
      mapPanel.append(worldCountryActivity(worldMap));
      const regions = create("div", "world-region-list");
      for (const region of (worldMap?.regions || []).slice(0, 8)) {
        const button = create("button", "world-region"); button.type = "button";
        button.textContent = `${region.label} · ${formatCount(region.active_accounts)}`;
        button.addEventListener("click", () => loadWorldLens(region.country_code, region.label)); regions.append(button);
      }
      if (regions.childElementCount) mapPanel.append(regions);
      if (worldMap) mapPanel.append(create("p", "metric-note", `Areas appear with ${formatCount(worldMap.minimum_crowd)} or more active accounts. Precise locations are not stored.`));
    }
    const lens = state.world.lens;
    const lensSelection = state.world.lensSelection;
    if (lens || lensSelection) {
      const lensBlock = create("section", "world-lens");
      const lensName = [lens?.lens?.city || lensSelection?.city, lens?.lens?.region || lensSelection?.region, lens?.lens?.country_code || lensSelection?.countryCode].filter(Boolean).join(", ");
      lensBlock.append(create("h4", "", lensName || "Selected area"));
      const lensNotice = worldReadNotice("lens", "this area", () => loadWorldLens(lensSelection.countryCode, lensSelection.region, lensSelection.city));
      if (lensNotice) lensBlock.append(lensNotice);
      else if (lens.suppressed) lensBlock.append(create("p", "utility-empty", lens.reason || "Too few people here to show this area."));
      else if (!lens.items?.length) lensBlock.append(create("p", "utility-empty", "No visible Drops in this area yet."));
      else for (const drop of lens.items) lensBlock.append(makeDropCard(drop));
      mapPanel.append(lensBlock);
    }
    el.worldContent.append(mapPanel);
    // Keep Pulse first in the actual reading order, without CSS reordering.
    // Moving the existing panels preserves their handlers and state.
    for (const panel of Array.from(el.worldContent.children)) {
      if (!panel.dataset.worldSection) pulseAnchor.append(panel);
    }
    syncWorldView();
    renderRoomAffordances();
    observeCards();
    observeUtilityAnalytics();
    if (state.world.pendingFocus && ["ready", "error"].includes(worldReadStatus(state.world.pendingFocus))) {
      const feature = state.world.pendingFocus;
      const world = state.world, identity = state.messages, discovery = state.discovery;
      const requestId = discovery.requestId;
      state.world.pendingFocus = null;
      requestAnimationFrame(() => {
        if (state.world !== world || state.messages !== identity || state.discovery !== discovery
            || discovery.requestId !== requestId || state.view !== "discover"
            || discovery.mode !== "world" || world.view !== "pulse") return;
        const panel = $(`[data-world-feature="${feature}"]`, el.worldContent);
        panel?.scrollIntoView({ block: "start", behavior: motionBehavior() });
        (panel?.querySelector("button, a") || panel)?.focus({ preventScroll: true });
      });
    }
  }

  function beginDiscoverRead(mode) {
    if (state.view !== "discover" || state.discovery.mode !== mode) return null;
    const discovery = state.discovery, identity = state.messages;
    const viewerId = state.viewer?.id || null;
    const requestId = discovery.requestId = (discovery.requestId || 0) + 1;
    discovery.controller?.abort();
    const controller = new AbortController();
    discovery.controller = controller;
    return {
      signal: controller.signal,
      current: () => !controller.signal.aborted && state.view === "discover"
        && state.discovery === discovery && discovery.requestId === requestId
        && discovery.mode === mode && state.messages === identity
        && (state.viewer?.id || null) === viewerId,
      finish: () => {
        if (discovery.controller === controller) discovery.controller = null;
        controller.abort();
      },
    };
  }

  function worldReadPlan(view = state.world.view) {
    const keys = {
      pulse: ["pulse", "map", "mic", "moment"],
      questions: ["daily", "questions"],
      polls: ["polls"],
      collaborations: ["collaborations"],
    }[view] || ["pulse", "map", "mic", "moment"];
    const paths = {
      pulse: "/api/world/pulse", map: "/api/world/map", mic: "/api/world/mic",
      moment: "/api/world/moments/current", daily: "/api/daily-question",
      questions: state.world.questionScope === "global" ? "/api/questions/global"
        : `/api/questions?${new URLSearchParams({ limit: "30", ...(state.world.questionScope === "crowd" ? { question_type: "crowd" } : {}) })}`,
      polls: "/api/polls?limit=30",
      collaborations: `/api/collaborations?${new URLSearchParams({
        limit: "30",
        ...(state.world.collaborationRole ? { role_type: state.world.collaborationRole } : {}),
        ...(state.world.collaborationLocation ? { location_mode: state.world.collaborationLocation } : {}),
      })}`,
      micHistory: "/api/world/mic/history?limit=20", moments: "/api/world/moments?limit=20",
    };
    // Keep open archive dialogs fresh, independently of the selected page content.
    const requested = [...keys, "micHistory", "moments"];
    return { keys: requested, paths: requested.map(key => paths[key]) };
  }

  async function refreshWorld({ quiet = false } = {}) {
    const read = beginDiscoverRead("world");
    if (!read) return;
    const world = state.world;
    const worldView = state.world.view;
    updateDiscoverContext();
    renderDiscover();
    el.discoverLoading.textContent = `Loading ${worldView === "pulse" ? "World" : utilityLabel(worldView)}…`;
    if (!quiet) el.discoverLoading.hidden = false;
    el.discoverError.hidden = true;
    const plan = worldReadPlan(worldView);
    const batch = beginWorldRead(plan.keys);
    // Reveal the destination immediately; requests fill only its content.
    if (!quiet) renderWorld();
    const requests = plan.paths.map(path => api(path, { signal: read.signal }));
    try {
      // History belongs to separately opened dialogs. A slow archive must not
      // hold the visible page behind its loading indicator or trigger a second
      // page repaint that resets a field the reader has already started using.
      const acceptPart = async (history) => {
        const indices = batch.keys.map((key, index) => ({ key, index }))
          .filter(({ key }) => ["micHistory", "moments"].includes(key) === history)
          .map(({ index }) => index);
        const results = await Promise.allSettled(indices.map(index => requests[index]));
        if (!read.current() || state.world !== world) return;
        acceptWorldRead({ world, keys: indices.map(index => batch.keys[index]),
          tokens: indices.map(index => batch.tokens[index]) }, results);
        if (history) return;
        el.discoverLoading.hidden = true;
        if (results.every((result) => result.status === "rejected")) el.discoverError.hidden = false;
        renderWorld();
      };
      await Promise.all([acceptPart(false), acceptPart(true)]);
    } finally { read.finish(); }
  }

  async function shareStatusCard(snapshotId, entityId, label, layout) {
    const url = new URL(`/api/status/cards/${encodeURIComponent(snapshotId)}/${encodeURIComponent(entityId)}.png?layout=${encodeURIComponent(layout)}`, location.origin).href;
    try {
      if (navigator.share) {
        const response = await fetch(url, { credentials: "same-origin" });
        if (!response.ok) throw new Error("Share card is unavailable.");
        const file = new File([await response.blob()], `louderr-record-${layout}.png`, { type: "image/png" });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ title: label, files: [file] });
          track("ranking_card_share", entityId, { snapshot_id: snapshotId, layout });
          return;
        }
      }
      await navigator.clipboard.writeText(url);
      track("ranking_card_share", entityId, { snapshot_id: snapshotId, layout });
      toast("Share-card link copied");
    } catch (error) {
      if (error.name !== "AbortError") toast(error.message, true);
    }
  }

  function openStatusShareDialog(snapshotId, entityId, label) {
    const dialog = buildUtilityDialog("Share record");
    const body = create("div", "utility-dialog-body");
    body.append(create("p", "utility-copy", label));
    const layouts = create("div", "share-layouts");
    [["story", "Story", "1080 × 1920"], ["landscape", "Landscape", "1200 × 630"], ["square", "Square", "1080 × 1080"]].forEach(([value, name, size]) => {
      const button = create("button", "share-layout-button"); button.type = "button";
      button.append(create("strong", "", name), create("span", "", size));
      button.addEventListener("click", async () => { button.disabled = true; await shareStatusCard(snapshotId, entityId, label, value); button.disabled = false; });
      layouts.append(button);
    });
    body.append(layouts); dialog.append(body); openDialog(dialog);
  }

  async function openScoutHistory() {
    const dialog = buildUtilityDialog("Scout archive");
    const body = create("div", "utility-dialog-body"); body.append(create("p", "utility-empty", "Loading Scout history…")); dialog.append(body); openDialog(dialog);
    try {
      const result = await api(`/api/status/scouts/history?${new URLSearchParams({ period: state.records.period, limit: "30" })}`);
      clear(body);
      const list = create("div", "utility-card-list");
      for (const snapshot of result.snapshots || []) {
        const card = utilityCard(new Date(snapshot.window_ended_at).toLocaleDateString(undefined, { dateStyle: "long" }), utilityLabel(result.period));
        card.append(create("span", "utility-card-meta", `Finalized ${relativeTime(snapshot.generated_at)}`)); list.append(card);
      }
      if (!list.childElementCount) list.append(create("p", "utility-empty", "No finalized Scout rankings yet."));
      body.append(list);
    } catch (error) { clear(body); body.append(create("p", "utility-empty is-error", error.message)); }
  }

  function recordRow({ rank, subject, user = null, detail, snapshotId, entityId, removed = false, open }) {
    const row = create("article", `record-row${removed ? " is-removed" : ""}`);
    row.append(create("strong", "record-rank", `#${rank}`));
    const copy = create("div", "record-copy");
    copy.append(user && !removed ? accountNameNode(user) : create("strong", "", subject), create("span", "", detail));
    if (open && !removed) {
      copy.tabIndex = 0; copy.setAttribute("role", "button");
      copy.addEventListener("click", open);
      copy.addEventListener("keydown", (event) => { if (["Enter", " "].includes(event.key)) { event.preventDefault(); open(); } });
    }
    row.append(copy);
    if (snapshotId && entityId && !removed) {
      const share = create("button", "text-button", "Share"); share.type = "button";
      share.addEventListener("click", () => openStatusShareDialog(snapshotId, entityId, `${subject} · #${rank}`));
      row.append(share);
    }
    return row;
  }

  function renderMyRecords() {
    const mine = state.records.mine;
    if (!state.viewer || !mine) return null;
    const firstHere = mine.firstHere?.items || [];
    const scouts = mine.scout?.items || [];
    const panel = create("section", "my-records");
    panel.append(create("h3", "", "Your records"));
    const summary = create("div", "my-record-summary");
    appendWorldMetric(summary, "First Here", firstHere.length);
    appendWorldMetric(summary, "Scout awards", scouts.filter((item) => item.active).length);
    panel.append(summary);
    if (firstHere.length) {
      const latest = firstHere.slice(0, 3);
      const list = create("div", "my-record-list");
      for (const item of latest) {
        list.append(create("p", "", `#${item.discovery_position} to find · ${item.status}${item.successful_at ? ` · ${relativeTime(item.successful_at)}` : ""}`));
      }
      panel.append(list);
    }
    return panel;
  }

  function syncRecordControls() {
    const view = state.records.view;
    el.recordsScopeField.hidden = view !== "winners";
    el.recordsPeriodField.hidden = view === "capsules";
    const allTime = $('option[value="all_time"]', el.recordsPeriod);
    allTime.disabled = view === "winners";
    if (view === "winners" && state.records.period === "all_time") {
      state.records.period = "month"; el.recordsPeriod.value = "month";
    }
  }

  function boardHistoryPath(slotId) {
    const params = new URLSearchParams({ mode: "records", view: "board", slot: slotId });
    return `/discover?${params}`;
  }

  async function openBoardHistorySlot(slot, { syncUrl = true } = {}) {
    track("board_history_open", slot.drop?.id || null, { slot_id: slot.id });
    const stablePath = boardHistoryPath(slot.id);
    if (syncUrl && `${location.pathname}${location.search}` !== stablePath) history.pushState({}, "", stablePath);
    const dialog = buildUtilityDialog("Board history");
    const body = create("div", "utility-dialog-body"); body.append(create("p", "utility-empty", "Loading Board turn…")); dialog.append(body); openDialog(dialog);
    try {
      const detail = await api(`/api/board/history/${encodeURIComponent(slot.id)}`);
      clear(body);
      const heading = create("section", "utility-detail-heading");
      heading.append(
        create("span", "utility-eyebrow", `${new Date(detail.started_at).toLocaleString()} · ${utilityLabel(detail.status)}`),
        create("h3", "", detail.drop?.title || "Unavailable Drop"),
        create("p", "", detail.selection_reason || "Recorded Board turn."),
      );
      body.append(heading);
      const facts = create("div", "utility-facts");
      facts.append(create("span", "", `${Math.max(1, Math.round((detail.duration_seconds || 0) / 60))} min`));
      if (Number.isFinite(detail.metrics?.verified_unique_impressions)) facts.append(create("span", "", `${formatCount(detail.metrics.verified_unique_impressions)} verified views`));
      body.append(facts);
      const actions = create("div", "button-row board-history-actions");
      const copy = create("button", "secondary-button", "Copy link"); copy.type = "button";
      copy.addEventListener("click", async () => {
        const url = new URL(stablePath, location.origin).href;
        try { await navigator.clipboard.writeText(url); toast("Board link copied"); }
        catch (_) { toast(url); }
      });
      const share = create("button", "secondary-button", "Share"); share.type = "button";
      share.addEventListener("click", async () => {
        const url = new URL(stablePath, location.origin).href;
        if (navigator.share) {
          try { await navigator.share({ title: detail.drop?.title || "Board history", url }); return; }
          catch (error) { if (error.name === "AbortError") return; }
        }
        try { await navigator.clipboard.writeText(url); toast("Board link copied"); }
        catch (_) { toast(url); }
      });
      actions.append(copy, share); body.append(actions);
      if (detail.drop) body.append(makeDropCard(detail.drop));
      else body.append(create("p", "utility-empty", "The historical slot remains recorded, but the Drop is no longer public."));
    } catch (error) { clear(body); body.append(create("p", "utility-empty is-error", error.message)); }
  }

  function renderBoardHistoryRecords() {
    const list = create("section", "record-list board-history-list");
    const items = state.records.data?.items || [];
    list.append(create("p", "record-period", `${utilityLabel(state.records.data?.period || "today")} Board turns`));
    for (const slot of items) {
      const row = create("article", `board-history-row${slot.tombstone ? " is-removed" : ""}`);
      const time = create("time", "", new Date(slot.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })); time.dateTime = slot.started_at;
      const copy = create("div", "record-copy");
      copy.append(create("strong", "", slot.drop?.title || "Unavailable Drop"), create("span", "", slot.selection_reason || utilityLabel(slot.selection_reason_code || slot.status)));
      const open = create("button", "text-button", "View"); open.type = "button"; open.addEventListener("click", () => openBoardHistorySlot(slot));
      row.append(time, copy, open); list.append(row);
    }
    if (!items.length) list.append(create("p", "utility-empty", "No Board turns in this period yet."));
    el.recordsContent.append(list);
    if (state.records.pendingSlotId) {
      const slotId = state.records.pendingSlotId;
      state.records.pendingSlotId = null;
      const selected = items.find((item) => item.id === slotId) || { id: slotId };
      queueMicrotask(() => openBoardHistorySlot(selected, { syncUrl: false }));
    }
  }

  function capsuleActivity(data) {
    const snapshot = data.snapshot || data;
    return snapshot.activity || {};
  }

  function renderCapsuleSummary(container, data) {
    const activity = capsuleActivity(data);
    const metrics = create("div", "world-metrics capsule-metrics");
    appendWorldMetric(metrics, "posts", activity.posts_created);
    appendWorldMetric(metrics, "verified views", activity.verified_impressions);
    appendWorldMetric(metrics, "reactions", activity.authoritative_reactions);
    appendWorldMetric(metrics, "Board turns", activity.board_slots ?? (data.board || []).length);
    container.append(metrics);
    const categories = (data.snapshot?.popular_categories || data.popular_categories || []).slice(0, 6);
    if (categories.length) {
      const facts = create("div", "utility-facts capsule-categories");
      categories.forEach((item) => facts.append(create("span", "", `${utilityLabel(item.category)} · ${formatCount(item.posts)}`)));
      container.append(facts);
    }
    const question = data.daily_question || null;
    const responseCount = data.snapshot?.daily_question_verified_responses ?? data.daily_question_verified_responses;
    if (question) {
      const daily = utilityCard(question.prompt, "Daily question");
      daily.append(create("span", "utility-card-meta", `${formatCount(responseCount)} verified answers`)); container.append(daily);
    } else if (Number.isFinite(responseCount)) {
      container.append(create("p", "utility-state-note", `${formatCount(responseCount)} verified Daily Question answers`));
    }
    container.append(create("p", "metric-note", "Chat bodies and individual locations are not archived."));
  }

  async function openTimeCapsule(capsuleDate = null) {
    track("time_capsule_open", null, { capsule_date: capsuleDate || "today" });
    const dialog = buildUtilityDialog(capsuleDate ? "Time Capsule" : "Today");
    const body = create("div", "utility-dialog-body"); body.append(create("p", "utility-empty", "Loading Time Capsule…")); dialog.append(body); openDialog(dialog);
    try {
      const data = await api(capsuleDate ? `/api/time-capsules/${encodeURIComponent(capsuleDate)}` : "/api/time-capsules/today");
      clear(body);
      const date = data.capsule_date || capsuleDate;
      const heading = create("section", "utility-detail-heading");
      heading.append(create("span", "utility-eyebrow", data.final === false ? "Live preview · not final" : utilityLabel(data.status)), create("h3", "", new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { dateStyle: "full" })));
      if (data.provenance) {
        heading.append(create("p", "metric-note", `Revision ${formatCount(data.revision)} · ${data.provenance.generated_by === "staff" ? "generated by staff" : "generated automatically"} · policy ${data.policy_version}`));
        if (data.provenance.amendment_reason) heading.append(create("p", "utility-state-note", `Amended: ${data.provenance.amendment_reason}`));
      }
      body.append(heading); renderCapsuleSummary(body, data);
      if (data.provenance?.recent_revisions?.length) {
        const revisions = create("details", "capsule-revisions");
        revisions.append(create("summary", "", `Revision history · ${formatCount(data.provenance.revision_count)}`));
        const list = create("div", "admin-history-list");
        for (const revision of data.provenance.recent_revisions) {
          const row = create("article", "admin-history-row");
          row.append(create("strong", "", `Revision ${revision.revision}`), create("p", "", revision.reason || "Recorded revision"), create("time", "", new Date(revision.created_at).toLocaleString())); list.append(row);
        }
        revisions.append(list); body.append(revisions);
      }
      if (data.winners?.length) {
        const winners = create("section", "utility-response-list"); winners.append(create("h4", "", "Winners"));
        for (const winner of data.winners) {
          const top = winner.top?.user?.display_name || winner.top?.drop?.title || "Unavailable record";
          const line = create("p", "capsule-record-line");
          line.append(create("span", "", `${utilityLabel(winner.scope)} · `), winner.top?.user ? accountNameNode(winner.top.user, "span") : create("span", "", top));
          winners.append(line);
        }
        body.append(winners);
      }
      if (data.board?.length) {
        const board = create("section", "utility-response-list"); board.append(create("h4", "", "Board"));
        for (const slot of data.board.slice(0, 20)) {
          const button = create("button", "capsule-record-button", slot.drop?.title || "Unavailable Drop"); button.type = "button"; button.addEventListener("click", () => openBoardHistorySlot(slot)); board.append(button);
        }
        body.append(board);
      }
    } catch (error) { clear(body); body.append(create("p", "utility-empty is-error", error.message)); }
  }

  function renderTimeCapsules() {
    const panel = create("section", "record-list capsule-list");
    const today = state.records.data?.today;
    const todayCard = utilityCard("Today", "Live preview · not final");
    todayCard.append(create("p", "utility-card-copy", "A live view of today's public record."));
    const todayActions = utilityCardActions(todayCard); const openToday = create("button", "publish-button", "Open"); openToday.type = "button"; openToday.addEventListener("click", () => openTimeCapsule()); todayActions.append(openToday); panel.append(todayCard);
    if (today) renderCapsuleSummary(todayCard, today);
    const yesterday = new Date();
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);
    const yesterdayDate = yesterday.toISOString().slice(0, 10);
    const find = create("form", "capsule-date-controls");
    const dateLabel = create("label"); dateLabel.append(create("span", "", "Past date"));
    const dateInput = create("input"); dateInput.type = "date"; dateInput.required = true; dateInput.max = yesterdayDate; dateInput.value = yesterdayDate;
    dateLabel.append(dateInput);
    const yesterdayButton = create("button", "secondary-button", "Yesterday"); yesterdayButton.type = "button";
    yesterdayButton.addEventListener("click", () => openTimeCapsule(yesterdayDate));
    const openDate = create("button", "secondary-button", "Open date"); openDate.type = "submit";
    find.append(dateLabel, yesterdayButton, openDate);
    find.addEventListener("submit", (event) => {
      event.preventDefault();
      if (dateInput.reportValidity()) openTimeCapsule(dateInput.value);
    });
    panel.append(find);
    const archive = create("div", "capsule-archive"); archive.append(create("h3", "", "Past days"));
    for (const item of state.records.data?.items || []) {
      const button = create("button", "capsule-row"); button.type = "button";
      button.append(
        create("strong", "", new Date(`${item.date}T00:00:00`).toLocaleDateString(undefined, { dateStyle: "medium" })),
        create("span", "", `${utilityLabel(item.status)} · ${new Date(item.generated_at).toLocaleDateString()}`),
      );
      button.addEventListener("click", () => openTimeCapsule(item.date)); archive.append(button);
    }
    if (!(state.records.data?.items || []).length) archive.append(create("p", "utility-empty", "No completed Time Capsules yet."));
    panel.append(archive); el.recordsContent.append(panel);
  }

  function renderRecords() {
    clear(el.recordsContent);
    syncRecordControls();
    if (state.records.view === "board") { renderBoardHistoryRecords(); return; }
    if (state.records.view === "capsules") { renderTimeCapsules(); return; }
    const mine = renderMyRecords();
    if (mine) el.recordsContent.append(mine);
    const data = state.records.data;
    const list = create("section", "record-list");
    if (state.records.view === "scouts") {
      if (data?.snapshot?.id) trackOnce(`scout:${data.snapshot.id}`, "scout_open", null, { snapshot_id: data.snapshot.id, period: data.period });
      const scoutMeta = create("div", "record-toolbar");
      scoutMeta.append(create("span", "metric-note", data?.snapshot?.generated_at ? `Updated ${relativeTime(data.snapshot.generated_at)}` : "Current Scout ranking"));
      const archive = create("button", "text-button", "Past rankings"); archive.type = "button"; archive.addEventListener("click", openScoutHistory); scoutMeta.append(archive);
      el.recordsContent.append(scoutMeta);
      const items = data?.items || [];
      for (const item of items) {
        const finds = item.early_finds ?? item.successful_finds ?? item.find_count ?? 0;
        const movement = item.previous_rank == null ? "New" : item.movement > 0 ? `Up ${item.movement}` : item.movement < 0 ? `Down ${Math.abs(item.movement)}` : "No change";
        list.append(recordRow({
          rank: item.rank,
          subject: item.user.display_name,
          user: item.user,
          detail: `@${item.user.handle} · ${formatCount(item.points)} points · ${formatCount(finds)} finds · ${formatCount(item.distinct_creators)} creators · ${movement}`,
          snapshotId: data.snapshot?.id,
          entityId: item.user.id,
          open: () => openCreator(item.user),
        }));
      }
      if (data?.viewer) {
        const movement = data.viewer.movement > 0 ? ` · up ${data.viewer.movement}` : data.viewer.movement < 0 ? ` · down ${Math.abs(data.viewer.movement)}` : "";
        const focus = create("div", "record-focus", `You are #${data.viewer.rank} · ${formatCount(data.viewer.points)} points${movement}`);
        el.recordsContent.prepend(focus);
      }
      if (!items.length) list.append(create("p", "utility-empty", "No Scout records for this period yet."));
    } else {
      const period = data?.periods?.[0];
      if (period?.snapshot_id) trackOnce(`winner:${period.snapshot_id}`, "winner_open", null, { snapshot_id: period.snapshot_id, period: state.records.period, scope: state.records.scope });
      for (const item of period?.items || []) {
        const user = item.user;
        const drop = item.drop;
        const removed = Boolean(item.disqualified || item.tombstone);
        list.append(recordRow({
          rank: item.rank,
          subject: removed ? "Record unavailable" : (user?.display_name || drop?.title || "Recorded result"),
          user,
          detail: removed ? (item.disqualification_reason || "The result remains in the archive without public attribution.") : `${formatCount(item.points)} momentum`,
          snapshotId: period.snapshot_id,
          entityId: user?.id || drop?.id,
          removed,
          open: user ? () => openCreator(user) : drop ? () => openDrop(drop.id) : null,
        }));
      }
      if (!period?.items?.length) list.append(create("p", "utility-empty", "No completed rankings for this period yet."));
      if (period) list.prepend(create("p", "record-period", `${new Date(period.period_start).toLocaleDateString()} – ${new Date(period.period_end).toLocaleDateString()}`));
    }
    el.recordsContent.append(list);
  }

  // Events. A stated time and the people who said they are coming, nothing inferred.
  function eventWhen(startsAt, endsAt) {
    const start = new Date(startsAt);
    const end = new Date(endsAt);
    if (Number.isNaN(start.getTime())) return "";
    const sameDay = start.toDateString() === end.toDateString();
    const day = start.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
    const from = start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const to = Number.isNaN(end.getTime())
      ? ""
      : end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (!to) return `${day}, ${from}`;
    // Reading a second date only helps when the event actually crosses midnight.
    return sameDay
      ? `${day}, ${from} to ${to}`
      : `${day}, ${from} to ${end.toLocaleDateString([], { day: "numeric", month: "short" })}, ${to}`;
  }

  function eventAttendance(item) {
    const going = item.going_count || 0;
    const maybe = item.maybe_count || 0;
    if (!going && !maybe) return "Nobody has answered yet";
    const parts = [];
    if (going) parts.push(`${going} going`);
    if (maybe) parts.push(`${maybe} maybe`);
    return parts.join(" \u00b7 ");
  }

  function makeEventCard(item) {
    const card = create("article", "event-card");
    card.dataset.eventId = item.id;
    const head = create("div", "event-card-head");
    head.append(create("h4", "", item.title));
    if (item.status === "cancelled") head.append(create("span", "event-cancelled", "Cancelled"));
    card.append(head);
    card.append(create("p", "event-when", eventWhen(item.starts_at, item.ends_at)));
    if (item.body) card.append(create("p", "event-body", item.body));
    card.append(create("p", "event-host", `Hosted by ${item.host?.display_name || item.host?.handle || "someone"}`));
    card.append(create("p", "event-attendance", eventAttendance(item)));
    if (item.id === state.events.focusId) card.classList.add("is-focused");
    if (state.viewer && item.status !== "cancelled" && state.events.when !== "past") {
      const actions = create("div", "event-actions");
      for (const [response, label] of [["going", "Going"], ["maybe", "Maybe"]]) {
        const button = create("button", "event-answer", label);
        button.type = "button";
        button.dataset.eventAnswer = response;
        const chosen = item.your_response === response;
        button.classList.toggle("is-active", chosen);
        button.setAttribute("aria-pressed", String(chosen));
        actions.append(button);
      }
      card.append(actions);
    }
    const share = create("button", "event-share", "Copy link");
    share.type = "button";
    share.dataset.eventShare = "1";
    (card.querySelector(".event-actions") || card).append(share);
    return card;
  }

  function focusEventCard() {
    if (!state.events.focusId || !el.eventsList) return;
    const card = el.eventsList.querySelector(`[data-event-id="${state.events.focusId}"]`);
    if (!card) return;
    card.scrollIntoView({ block: "center", behavior: typeof motionBehavior === "function" ? motionBehavior() : "auto" });
  }

  async function shareEvent(eventId) {
    const url = new URL(`/e/${encodeURIComponent(eventId)}`, location.origin).href;
    const item = (state.events.items || []).find((candidate) => candidate.id === eventId);
    if (navigator.share) {
      try {
        await navigator.share({ title: item?.title || "Event", url });
        return;
      } catch (error) { if (error.name === "AbortError") return; }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast("Event link copied");
    } catch (_) { toast("Could not copy the link", true); }
  }

  function renderEvents() {
    if (!el.eventsList) return;
    clear(el.eventsList);
    const items = state.events.items || [];
    for (const item of items) el.eventsList.append(makeEventCard(item));
    el.eventsEmpty.hidden = items.length > 0 || !state.events.loaded;
    el.eventsEmpty.textContent = state.events.when === "past"
      ? "Nothing has happened yet."
      : "Nothing is scheduled yet.";
    if (el.eventsNew) el.eventsNew.hidden = !state.viewer;
    for (const button of $$("[data-events-when]")) {
      const active = button.dataset.eventsWhen === state.events.when;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
    }
  }

  async function refreshEvents() {
    const read = beginDiscoverRead("events");
    if (!read) return;
    const events = state.events;
    const requestId = ++events.requestId;
    const when = events.when;
    const current = () => read.current() && state.events === events
      && requestId === events.requestId && events.when === when;
    updateDiscoverContext();
    renderDiscover();
    el.discoverLoading.textContent = "Loading events\u2026";
    el.discoverLoading.hidden = false;
    el.discoverError.hidden = true;
    try {
      const result = await api(`/api/calendar/events?${new URLSearchParams({
        past: String(when === "past"), limit: "30",
      })}`, { signal: read.signal });
      if (!current()) return;
      let items = result.items || [];
      const focusId = events.focusId;
      if (focusId && !items.some((item) => item.id === focusId)) {
        // A link to something that already finished would otherwise open an empty
        // "coming up" list. Fetch the one that was asked for and show it where it is.
        try {
          const one = await api(`/api/calendar/events/${encodeURIComponent(focusId)}`, { signal: read.signal });
          if (!current()) return;
          if (when === "upcoming" && new Date(one.ends_at) <= new Date()) {
            state.events.when = "past";
            renderEvents();
            refreshEvents();
            return;
          }
          items = [one, ...items];
        } catch (error) {
          if (!current() || error.name === "AbortError") return;
          events.focusId = null;
        }
      }
      if (!current()) return;
      events.items = items;
      events.loaded = true;
      renderEvents();
      focusEventCard();
    } catch (error) {
      if (!current() || error.name === "AbortError") return;
      el.discoverError.hidden = false;
    } finally {
      if (current()) el.discoverLoading.hidden = true;
      read.finish();
    }
  }

  async function answerEvent(eventId, response) {
    const item = (state.events.items || []).find((candidate) => candidate.id === eventId);
    if (!item) return;
    // Pressing the answer you already gave withdraws it, so the same control both
    // states and unstates without a separate "not going".
    const withdrawing = item.your_response === response;
    try {
      const updated = await api(`/api/calendar/events/${encodeURIComponent(eventId)}/rsvp`, {
        method: withdrawing ? "DELETE" : "PUT",
        csrf: true,
        json: withdrawing ? undefined : { response },
      });
      Object.assign(item, updated);
      renderEvents();
    } catch (error) {
      toast(error.message, true);
    }
  }

  async function refreshRecords() {
    const read = beginDiscoverRead("records");
    if (!read) return;
    const records = state.records;
    const recordsView = records.view, periodValue = records.period, scope = records.scope;
    const current = () => read.current() && state.records === records && records.view === recordsView
      && records.period === periodValue && records.scope === scope;
    updateDiscoverContext();
    renderDiscover();
    el.discoverLoading.textContent = "Loading records…";
    el.discoverLoading.hidden = false;
    el.discoverError.hidden = true;
    let requests;
    if (recordsView === "board") {
      const period = state.records.period === "day" ? "today" : state.records.period;
      requests = [api(`/api/board/history?${new URLSearchParams({ period, limit: "100" })}`, { signal: read.signal })];
    } else if (recordsView === "capsules") {
      requests = [api("/api/time-capsules/today", { signal: read.signal }), api("/api/time-capsules?limit=60", { signal: read.signal })];
    } else {
      const params = new URLSearchParams({ period: state.records.period });
      const mainPath = recordsView === "scouts"
        ? `/api/status/scouts?${new URLSearchParams({ ...Object.fromEntries(params), limit: "100" })}`
        : `/api/status/winners?${new URLSearchParams({ ...Object.fromEntries(params), scope: state.records.scope, limit: "12" })}`;
      requests = [api(mainPath, { signal: read.signal })];
      if (state.viewer) requests.push(api("/api/status/first-here", { signal: read.signal }), api("/api/status/scout", { signal: read.signal }));
    }
    try {
      const results = await Promise.all(requests);
      if (!current()) return;
      state.records.data = recordsView === "capsules" ? { today: results[0], items: results[1].items || [] } : results[0];
      state.records.mine = !["board", "capsules"].includes(recordsView) && state.viewer ? { firstHere: results[1], scout: results[2] } : null;
      renderRecords();
    } catch (error) {
      if (current() && error.name !== "AbortError") {
        el.discoverError.hidden = false;
        toast(error.message, true);
      }
    } finally {
      if (current()) el.discoverLoading.hidden = true;
      read.finish();
    }
  }

  function updateSocialBadges() {
    $$('[data-notification-unread]').forEach((node) => {
      node.textContent = formatCount(state.notifications.unreadCount);
      node.hidden = !state.viewer || state.notifications.unreadCount === 0;
    });
    $$('[data-message-unread]').forEach((node) => {
      node.textContent = formatCount(state.messages.unreadCount);
      node.hidden = !state.viewer || state.messages.unreadCount === 0;
    });
  }

  function trendingMilestoneLabel(entityType) {
    const match = /^trend_(people|drops)_(day|week|month|alltime)_(100|50|10|1)$/.exec(entityType || "");
    if (!match) return null;
    return match[3] === "1" ? "Reached #1" : `Reached Top ${match[3]}`;
  }

  function journeyModeLabel(mode) {
    return mode === "update" ? "Creator update" : mode === "response" ? "Response Drop" : "Project started";
  }

  function journeyPromptLabel(prompt) {
    return ({ feedback: "Feedback welcome", choice: "Help choose a version", remix: "Share your version", question: "A question for the room" })[prompt] || "Follow the progress";
  }

  function journeyDropPreview(drop, isRoot = false) {
    const article = create("article", `project-contribution${isRoot ? " is-project-root" : ""}`);
    article.dataset.dropId = drop.id;
    const header = create("header", "project-contribution-meta");
    header.append(accountNameNode(drop.author), create("span", "", `${journeyModeLabel(drop.journey?.mode)} · ${relativeTime(drop.created_at)}`));
    article.append(header, create(isRoot ? "h3" : "h4", "", drop.title), create("p", "project-contribution-body", drop.body));
    const media = mediaElement(drop);
    if (media.childElementCount) article.append(media);
    const actions = create("div", "project-contribution-actions");
    const open = create("a", "text-button", "Open Drop");
    open.href = `/d/${encodeURIComponent(drop.id)}`;
    open.addEventListener("click", (event) => {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button) return;
      event.preventDefault();
      el.project.close();
      history.pushState({}, "", open.href);
      routeLocation();
    });
    const comments = create("button", "text-button", `Comments (${formatCount(drop.comment_count || 0)})`);
    comments.type = "button";
    comments.addEventListener("click", () => { el.project.close(); openComments(drop); });
    actions.append(open, comments);
    article.append(actions);
    return article;
  }

  function renderJourney() {
    const journey = state.journey;
    const data = journey.data;
    el.projectLoading.hidden = !journey.loading;
    el.projectContent.hidden = !data;
    el.projectMore.hidden = !journey.nextCursor;
    el.projectMore.disabled = journey.loading || journey.subscribing;
    const writeEnabled = state.capabilities.community_writes;
    el.projectSubscribe.disabled = !writeEnabled || journey.loading || journey.subscribing;
    el.projectUpdate.disabled = !writeEnabled || journey.loading;
    el.projectRespond.disabled = !writeEnabled || journey.loading;
    if (!data) return;
    el.projectTitle.textContent = "Project";
    const contentSignature = JSON.stringify({ root: data.root, items: journey.items });
    if (journey.renderedContent !== contentSignature) {
      clear(el.projectRoot);
      el.projectRoot.append(journeyDropPreview(data.root, true));
      clear(el.projectTimeline);
      for (const drop of journey.items) el.projectTimeline.append(journeyDropPreview(drop));
      journey.renderedContent = contentSignature;
    }
    el.projectInvitation.textContent = `${journeyPromptLabel(data.prompt)} · ${data.allow_responses ? "Response Drops are welcome. Ask before reusing anyone’s media." : "Leave quick feedback in comments. Response Drops are not enabled."}`;
    const owner = state.viewer?.id === data.root.author_id;
    el.projectResponseSettingLabel.hidden = !writeEnabled || !data.can_update;
    el.projectResponseSetting.checked = Boolean(data.allow_responses);
    el.projectResponseSetting.disabled = !writeEnabled || journey.loading || journey.subscribing;
    el.projectSubscribe.hidden = !writeEnabled || owner;
    el.projectSubscribe.textContent = journey.subscribing ? "Saving…" : !state.viewer?.email_verified ? "Sign in to follow project" : data.subscribed ? "Unfollow project" : "Follow project";
    el.projectSubscribe.setAttribute("aria-pressed", String(Boolean(data.subscribed)));
    el.projectFollowNote.hidden = !writeEnabled || owner;
    el.projectUpdate.hidden = !writeEnabled || !data.can_update;
    el.projectRespond.hidden = !writeEnabled || owner || !(data.can_respond || (!state.viewer && data.allow_responses));
    el.projectRespond.textContent = state.viewer ? "Respond with a Drop" : "Sign in to respond";
    el.projectEmpty.hidden = journey.items.length > 0 || journey.loading;
  }

  async function refreshJourney(append = false) {
    const journey = state.journey;
    if (!journey.rootId || journey.loading || journey.subscribing || (append && !journey.nextCursor)) return false;
    const viewerId = state.viewer?.id || null;
    const requestId = ++journey.requestId;
    const current = () => state.journey === journey && requestId === journey.requestId
      && (state.viewer?.id || null) === viewerId && el.project.open;
    journey.loading = true;
    el.projectError.hidden = true;
    hideError(el.projectActionError);
    renderJourney();
    const query = new URLSearchParams({ limit: "20" });
    if (append) query.set("cursor", journey.nextCursor);
    try {
      const result = await api(`/api/journeys/${encodeURIComponent(journey.rootId)}?${query}`);
      if (!current()) return false;
      journey.data = result;
      journey.items = append ? [...new Map([...journey.items, ...result.items].map((item) => [item.id, item])).values()] : result.items;
      journey.nextCursor = result.next_cursor || null;
      return true;
    } catch (error) {
      if (current()) {
        const unavailable = [401, 403, 404].includes(error.status);
        journey.retryAppend = append && !unavailable;
        el.projectErrorCopy.textContent = error.message || "This project could not be loaded.";
        el.projectError.hidden = false;
        if (!append || unavailable) {
          journey.data = null; journey.items = []; journey.nextCursor = null;
          journey.renderedContent = null;
          clear(el.projectRoot); clear(el.projectTimeline);
        }
      }
      return false;
    } finally {
      if (current()) {
        journey.loading = false;
        renderJourney();
        if (journey.refreshAfterLoad) { journey.refreshAfterLoad = false; scheduleJourneyRefresh(); }
      }
    }
  }

  async function openJourney(rootId, respond = false) {
    if (!rootId) return;
    if (el.composer.open) el.composer.close();
    if (el.comments.open) el.comments.close();
    state.journey = { rootId, data: null, items: [], nextCursor: null, loading: false, requestId: 0, subscribing: false };
    const journey = state.journey;
    el.projectTitle.textContent = "Project";
    hideError(el.projectActionError);
    renderJourney();
    if (typeof openDialogWithHistory === "function") {
      const link = typeof dialogDeepLink === "function"
        ? (dialogDeepLink("project") || dialogDeepLink("respond")) : null;
      if (link) openDialogWithHistory(el.project, link.path, { returnTo: link.returnTo });
      else openDialogWithHistory(el.project);
    } else openDialog(el.project);
    if (await refreshJourney() && state.journey === journey && respond) {
      if (journey.data.can_respond || (!state.viewer && journey.data.allow_responses)) openJourneyComposer("response");
      else showError(el.projectActionError, "Response Drops are not available for this account or project. You can read the project and its comments.");
    }
  }

  function openJourneyComposer(mode) {
    if (!state.capabilities.community_writes) return;
    const data = state.journey.data;
    if (!data || !["update", "response"].includes(mode)) return;
    if (mode === "update" && !data.can_update) return;
    if (mode === "response" && !(data.can_respond || (!state.viewer && data.allow_responses))) return;
    const context = { mode, root_drop_id: data.root.id, title: data.root.title, kind: data.root.kind,
      root_media_url: data.root.media_url || null, root_handle: data.root.author?.handle || null, allow_responses: Boolean(data.allow_responses) };
    el.project.close();
    openComposer(null, context);
  }

  async function toggleJourneySubscription() {
    if (!state.capabilities.community_writes) return;
    const journey = state.journey;
    if (!journey.data || journey.loading || journey.subscribing) return;
    if (!state.viewer?.email_verified || state.sessionRestricted || state.viewer.status !== "active") {
      state.composerDraft.pending = { projectRoot: journey.rootId, ownerId: state.viewer?.id || null };
      renderComposerIntent();
      el.project.close();
      requireVerified({ announce: false });
      return;
    }
    const viewerId = state.viewer.id;
    const current = () => state.journey === journey && state.viewer?.id === viewerId && el.project.open;
    journey.subscribing = true;
    hideError(el.projectActionError);
    renderJourney();
    try {
      const result = await api(`/api/journeys/${encodeURIComponent(journey.rootId)}/subscription`, {
        method: "PUT", csrf: true, json: { subscribed: !journey.data.subscribed },
      });
      if (!current()) return;
      journey.data.subscribed = result.subscribed;
      toast(result.subscribed ? "Project followed. Future creator updates appear in Notifications." : "Project unfollowed");
      if (state.view === "notifications") refreshJourneyUpdates();
    } catch (error) {
      if (current()) showError(el.projectActionError, error.message);
    } finally {
      if (current()) {
        journey.subscribing = false;
        renderJourney();
        if (journey.refreshAfterLoad) { journey.refreshAfterLoad = false; scheduleJourneyRefresh(); }
      }
    }
  }

  async function setJourneyResponses() {
    if (!state.capabilities.community_writes) return;
    const journey = state.journey;
    if (!journey.data?.can_update || journey.loading || journey.subscribing) { renderJourney(); return; }
    const allowResponses = el.projectResponseSetting.checked;
    const viewerId = state.viewer?.id;
    const current = () => state.journey === journey && state.viewer?.id === viewerId && el.project.open;
    journey.subscribing = true;
    hideError(el.projectActionError);
    renderJourney();
    try {
      const result = await api(`/api/journeys/${encodeURIComponent(journey.rootId)}`, {
        method: "PATCH", csrf: true, json: { allow_responses: allowResponses },
      });
      if (!current()) return;
      journey.data.allow_responses = result.allow_responses;
      toast(result.allow_responses ? "Response Drops enabled" : "New response Drops disabled. Existing responses remain public.");
    } catch (error) {
      if (current()) showError(el.projectActionError, error.message);
    } finally {
      if (current()) {
        journey.subscribing = false;
        renderJourney();
        if (journey.refreshAfterLoad) { journey.refreshAfterLoad = false; scheduleJourneyRefresh(); }
      }
    }
  }

  async function copyJourneyLink() {
    const rootId = state.journey.rootId;
    if (!rootId) return;
    const url = new URL(`/d/${encodeURIComponent(rootId)}?project=1`, location.origin).href;
    try { await navigator.clipboard.writeText(url); toast("Project link copied"); }
    catch (_) { showError(el.projectActionError, `Copy this project link: ${url}`); }
  }

  function renderJourneyUpdates() {
    const updates = state.journeyUpdates;
    clear(el.projectUpdatesList);
    for (const drop of updates.items) {
      const button = create("button", `project-update-item${(updates.unreadIds || []).includes(drop.id) ? " is-unread" : ""}`);
      button.type = "button";
      const attribution = create("span", "identity-attribution");
      attribution.append(create("span", "", `${journeyModeLabel(drop.journey?.mode)} · `), accountNameNode(drop.author, "span"));
      button.append(attribution, create("strong", "", dropLabel(drop)), create("small", "", relativeTime(drop.created_at)));
      button.addEventListener("click", () => openJourney(drop.journey.root_drop_id));
      el.projectUpdatesList.append(button);
    }
    const readButton = el.projectUpdatesRead;
    if (readButton) {
      const count = Math.min(50, (updates.unreadIds || []).length);
      readButton.hidden = !count || !state.viewer || !el.projectUpdatesError.hidden;
      readButton.disabled = updates.loading || Boolean(updates.marking);
      readButton.textContent = `Mark ${count} displayed ${count === 1 ? "update" : "updates"} read`;
    }
    el.projectUpdatesLoading.hidden = !updates.loading;
    el.projectUpdatesMore.hidden = !updates.nextCursor;
    el.projectUpdatesMore.disabled = updates.loading || !el.projectUpdatesError.hidden;
    el.projectUpdatesEmpty.hidden = updates.loading || !el.projectUpdatesError.hidden || updates.items.length > 0
      || (Boolean(state.viewer) && !updates.loaded);
    el.projectUpdatesEmpty.textContent = state.viewer
      ? "Follow a project to see future creator updates here. Responses to your own projects appear here too."
      : "Sign in to follow projects and see their creator updates here.";
    // An empty Project updates block is an explainer, not an inbox: show the section only when it has something to say.
    const section = el.projectUpdatesSection;
    if (section) {
      section.hidden = Boolean(state.viewer) && updates.loaded && !updates.loading
        && el.projectUpdatesError.hidden && updates.items.length === 0;
    }
  }

  async function refreshJourneyUpdates(append = false) {
    const updates = state.journeyUpdates;
    if (!state.viewer) {
      updates.items = []; updates.nextCursor = null;
      updates.requestId += 1; updates.loading = false; updates.loaded = false;
      updates.retryAppend = false; updates.refreshAfterLoad = false;
      clearTimeout(updates.refreshTimer);
      el.projectUpdatesError.hidden = true;
      renderJourneyUpdates();
      return;
    }
    if (updates.loading || (append && !updates.nextCursor)) return;
    const viewerId = state.viewer.id;
    const requestId = ++updates.requestId;
    const current = () => state.journeyUpdates === updates && state.viewer?.id === viewerId && updates.requestId === requestId;
    updates.loading = true;
    el.projectUpdatesError.hidden = true;
    renderJourneyUpdates();
    const query = new URLSearchParams({ limit: "15" });
    if (append) query.set("cursor", updates.nextCursor);
    let refreshed = false;
    try {
      const result = await api(`/api/journeys/updates?${query}`);
      if (!current()) return;
      if (!result || !Array.isArray(result.items)
        || (result.next_cursor != null && typeof result.next_cursor !== "string")
        || result.items.some((item) => !item || typeof item.id !== "string" || typeof item.title !== "string"
          || typeof item.author?.display_name !== "string" || typeof item.journey?.root_drop_id !== "string")) {
        throw new Error("Project updates returned an invalid response. Please try again.");
      }
      updates.items = append ? [...new Map([...updates.items, ...result.items].map((item) => [item.id, item])).values()] : result.items;
      updates.nextCursor = result.next_cursor || null;
      updates.unreadIds = append
        ? [...new Set([...(updates.unreadIds || []), ...(result.unread_ids || [])])]
        : (result.unread_ids || []);
      updates.loaded = true;
      refreshed = true;
    } catch (error) {
      if (current()) {
        const accessLost = error.status === 401 || error.status === 403;
        if (accessLost) {
          updates.items = []; updates.nextCursor = null; updates.loaded = false;
        }
        updates.retryAppend = accessLost ? false : append;
        const cachedNote = updates.items.length ? " Previously loaded updates may be out of date." : "";
        if (error.status === 404 || error.status === 405) {
          el.projectUpdatesErrorCopy.textContent = "Project updates are unavailable on this server. A backend update is required. Other notifications load separately." + cachedNote;
        } else if (error.status === 401) {
          el.projectUpdatesErrorCopy.textContent = "Sign in again to load your project updates.";
        } else if (error.status === 403) {
          el.projectUpdatesErrorCopy.textContent = "Project updates access was denied. Check your email verification or account access.";
        } else {
          el.projectUpdatesErrorCopy.textContent = (error.message || "Project updates could not be loaded. Please try again.") + cachedNote;
        }
        el.projectUpdatesError.hidden = false;
      }
    } finally {
      if (current()) {
        updates.loading = false;
        renderJourneyUpdates();
        const refreshQueued = updates.refreshAfterLoad;
        updates.refreshAfterLoad = false;
        if (refreshed && refreshQueued) refreshJourneyUpdates();
      }
    }
  }

  function scheduleJourneyRefresh(deletedDropId = null) {
    const journey = state.journey;
    if (el.project.open && journey.rootId) {
      const removedVisibleDrop = deletedDropId && (deletedDropId === journey.rootId || journey.items.some((drop) => drop.id === deletedDropId));
      if (removedVisibleDrop && journey.loading) { journey.requestId += 1; journey.loading = false; }
      if (deletedDropId === journey.rootId) {
        journey.data = null; journey.items = []; journey.nextCursor = null;
        renderJourney();
      } else if (deletedDropId) {
        journey.items = journey.items.filter((drop) => drop.id !== deletedDropId);
        renderJourney();
      }
      if (journey.loading || journey.subscribing) journey.refreshAfterLoad = true;
      else {
        clearTimeout(journey.refreshTimer);
        journey.refreshTimer = setTimeout(() => {
          if (state.journey === journey && el.project.open) refreshJourney();
        }, 250);
      }
    }
    if (state.view === "notifications" && state.viewer) {
      const updates = state.journeyUpdates;
      if (deletedDropId) {
        // A pending response may contain this root/update even if this page does not.
        // Retire that read before scheduling the next authoritative refresh.
        updates.requestId += 1; updates.loading = false; updates.refreshAfterLoad = false;
        const removedIds = new Set(updates.items.filter((drop) => drop.id === deletedDropId || drop.journey.root_drop_id === deletedDropId).map((drop) => drop.id));
        updates.items = updates.items.filter((drop) => drop.id !== deletedDropId && drop.journey.root_drop_id !== deletedDropId);
        updates.unreadIds = (updates.unreadIds || []).filter((id) => id !== deletedDropId && !removedIds.has(id));
        renderJourneyUpdates();
      }
      if (updates.loading) updates.refreshAfterLoad = true;
      else {
        clearTimeout(updates.refreshTimer);
        updates.refreshTimer = setTimeout(() => {
          if (state.journeyUpdates === updates && state.view === "notifications") refreshJourneyUpdates();
        }, 250);
      }
    }
  }

  function resetJourneyState() {
    clearTimeout(state.journey.refreshTimer);
    clearTimeout(state.journeyUpdates.refreshTimer);
    state.journey = { rootId: null, data: null, items: [], nextCursor: null, loading: false, requestId: 0, subscribing: false };
    state.journeyUpdates = { items: [], nextCursor: null, loading: false, requestId: 0, loaded: false };
    el.project.close();
    clear(el.projectRoot);
    clear(el.projectTimeline);
    el.projectContent.hidden = true;
    el.projectError.hidden = true;
    el.projectUpdatesError.hidden = true;
    renderJourneyUpdates();
  }

  function notificationLabel(item) {
    const actor = item.actor?.display_name;
    const entityType = item.entity?.type || "";
    if (item.kind === "winner") return trendingMilestoneLabel(entityType) || "Your ranking was finalized";
    if (item.kind === "drop_coauthor") {
      const subject = actor || "Someone";
      return ({ invite: `${subject} invited you to co-author a post`, accept: `${subject} accepted the co-author invitation`,
        decline: `${subject} declined the co-author invitation`, withdraw: `${subject} withdrew the co-author invitation`,
        remove: `${subject} removed a co-author credit`, leave: `${subject} left the co-authored post` })[item.detail?.action]
        || "Your co-author invitation has an update";
    }
    const labels = {
      follow: actor ? `${actor} followed you` : "Someone followed you",
      follow_request: actor ? `${actor} asked to follow you` : "Someone asked to follow you",
      applause: actor ? `${actor} applauded your Drop` : "Your Drop received applause",
      chat_mention: actor ? `${actor} mentioned you in Chat` : "You were mentioned in Chat",
      chat_reply: actor ? `${actor} replied to you in Chat` : "Someone replied to you in Chat",
      drop_comment: actor ? `${actor} commented on your Drop` : "Your Drop has a new comment",
      board: "Your Drop reached the live Board",
      dm: actor ? `${actor} sent you a private message` : "You received a private message",
      first_here: "You were First Here",
      first_on_louderr: actor ? `${actor} posted something here first` : "Something new is here first",
      room_host: actor ? `${actor} is in a Chat room now` : "Someone you follow is in a Chat room now",
      scout: "Your Scout status changed",
      collaboration: actor ? `${actor} responded to your collaboration` : "Your collaboration has an update",
      safety_alert: entityType === "alert:media_review"
        ? "An upload is waiting for your review. Open Moderation."
        : entityType === "alert:video_review" ? "A video remix is waiting for your review. Open Moderation."
        : "Urgent report needs review. Open Moderation.",
      moderation_decision: "You have a moderation decision",
      ops_alert: "Operations alert. Open Moderation.",
      story_mention: actor ? `${actor} mentioned you in their story` : "You were mentioned in a story",
      live: actor ? `${actor} started a live video` : "Someone you follow started a live video",
      repost: actor ? `${actor} reposted your Drop` : "Your Drop was reposted",
      supervision: entityType === "supervision_started"
        ? `${actor || "Your guardian"} is now supervising your account`
        : entityType === "supervision_ended" ? "Supervision ended"
        : "Your daily limit or quiet hours changed",
    };
    return labels[item.kind] || `New activity in ${state.productName}`;
  }

  // A profile of somebody who is live right now offers to watch it.
  function syncProfileLive(user, isSelf) {
    const watchLive = $("#profile-watch-live");
    if (!watchLive) return;
    const live = !isSelf && user ? state.live?.liveFor(user.id) : null;
    watchLive.hidden = !live;
    watchLive.dataset.liveId = live ? live.id : "";
  }

  function syncLiveEntrances() {
    const goLive = $("#composer-go-live");
    if (goLive) goLive.hidden = !state.live?.canHost();
    const profile = state.profile?.data;
    if (profile?.user) syncProfileLive(profile.user, Boolean(profile.relationship?.is_self));
  }

  function notificationNameNode(item) {
    const label = notificationLabel(item);
    const node = create("strong", "notification-identity");
    const actor = item.actor?.display_name;
    if (actor && ["follow", "follow_request", "applause", "chat_mention", "chat_reply", "drop_comment", "dm", "collaboration", "drop_coauthor", "first_on_louderr", "room_host", "story_mention", "repost", "live"].includes(item.kind) && label.startsWith(`${actor} `)) {
      node.append(accountNameNode(item.actor, "span"), create("span", "", label.slice(actor.length)));
    } else node.textContent = label;
    return node;
  }

  function deleteDropPrompt(drop) {
    // Deleting within 15 minutes of publishing returns the day's post slot; later it does not.
    const withinWindow = Date.now() <= new Date(drop.created_at).getTime() + 15 * 60 * 1000;
    return withinWindow
      ? "Delete this Drop for everyone? You can post again right away."
      : "Delete this Drop for everyone? It still counts toward today's 3 posts.";
  }

  function safeNotificationAction(item) {
    const fallback = item.kind === "winner" ? "/trending" : null;
    if (!item.action_url) return fallback;
    try {
      const target = new URL(item.action_url, location.origin);
      if (target.origin !== location.origin || !target.pathname.startsWith("/")) return fallback;
      return `${target.pathname}${target.search}${target.hash}`;
    } catch (_) { return fallback; }
  }

  const groupableNotificationKinds = new Set(["applause", "drop_comment", "follow", "first_on_louderr", "collaboration", "chat_mention", "chat_reply"]);

  function notificationSection(createdAt, now = Date.now()) {
    const time = new Date(createdAt).getTime();
    if (!Number.isFinite(time)) return "Earlier";
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    if (time >= startOfToday.getTime()) return "Today";
    if (time >= now - 7 * 24 * 60 * 60 * 1000) return "This week";
    if (time >= now - 30 * 24 * 60 * 60 * 1000) return "This month";
    return "Earlier";
  }

  // Several people doing the same thing to the same Drop read as one line, the way a post's likes
  // do elsewhere: "Kian and 2 others applauded your Drop". Different Drops stay separate rows.
  function groupNotifications(items) {
    const groups = [];
    const index = new Map();
    for (const item of items) {
      const section = notificationSection(item.created_at);
      const target = safeNotificationAction(item) || "";
      const key = groupableNotificationKinds.has(item.kind) && !item.detail ? `${section}|${item.kind}|${target}` : null;
      const existing = key ? index.get(key) : null;
      if (existing) {
        existing.items.push(item);
        if (item.actor?.id && !existing.actors.some((actor) => actor.id === item.actor.id)) existing.actors.push(item.actor);
        existing.read = existing.read && Boolean(item.read);
        continue;
      }
      const group = { key, section, lead: item, items: [item], actors: item.actor?.id ? [item.actor] : [], read: Boolean(item.read) };
      groups.push(group);
      if (key) index.set(key, group);
    }
    return groups;
  }

  function notificationGroupNameNode(group) {
    if (group.actors.length < 2) return notificationNameNode(group.lead);
    const lead = group.lead;
    const actor = lead.actor?.display_name || "";
    const label = notificationLabel(lead);
    const others = group.actors.length - 1;
    const tail = actor && label.startsWith(`${actor} `) ? label.slice(actor.length) : ` ${label}`;
    const node = create("strong", "notification-identity");
    node.append(accountNameNode(lead.actor, "span"), create("span", "", ` and ${others} ${others === 1 ? "other" : "others"}${tail}`));
    return node;
  }

  // The Drop a notification points at, when this page already holds it; a title makes
  // "applauded your Drop" concrete without another request per row.
  function notificationDropTitle(item) {
    const match = /^\/d\/([^/?#]+)/.exec(safeNotificationAction(item) || "");
    if (!match) return "";
    const id = decodeURIComponent(match[1]);
    for (const pool of [state.drops, state.profile?.drops]) {
      const drop = Array.isArray(pool) ? pool.find((entry) => entry?.id === id) : null;
      if (drop?.title) return `\u201c${drop.title}\u201d`;
    }
    return "";
  }

  async function openNotification(group) {
    if (!state.viewer) return;
    const viewerId = state.viewer.id;
    const notifications = state.notifications;
    const unread = group.items.filter((entry) => !entry.read).map((entry) => entry.id);
    // Read-state persistence must not hold navigation hostage to a slow connection.
    // markNotificationsRead retains its own account guard and updates only on success.
    if (unread.length) void markNotificationsRead(unread);
    if (group.lead.kind === "follow_request" && state.followRequests) { openFollowRequests(); return; }
    const actionUrl = safeNotificationAction(group.lead);
    if (!actionUrl) return;
    history.pushState({}, "", actionUrl);
    try {
      const opening = routeLocation();
      // Set the viewport before the first paint, not after asynchronous page data arrives.
      scrollTo({ top: 0, behavior: "instant" });
      await opening;
    } catch (error) {
      if (state.viewer?.id === viewerId && state.notifications === notifications
          && `${location.pathname}${location.search}${location.hash}` === actionUrl) toast(error.message, true);
    }
  }

  function renderNotifications() {
    const notifications = notificationWorkspace();
    const viewerId = state.viewer?.id;
    const rows = notifications.rows ||= new Map();
    const headings = notifications.headings ||= new Map();
    const wanted = [], keys = new Set(), sections = new Set();
    const focused = el.notificationList.contains(document.activeElement) ? document.activeElement : null;
    const focusedIndex = [...el.notificationList.children].filter(node => node._notificationGroup).indexOf(focused);
    let section = null;
    for (const group of groupNotifications(state.notifications.items)) {
      if (group.section !== section) {
        section = group.section;
        if (!headings.has(section)) headings.set(section, create("h3", "notification-section", section));
        wanted.push(headings.get(section)); sections.add(section);
      }
      const item = group.lead;
      const key = group.key || `item:${item.id}`;
      keys.add(key);
      let button = rows.get(key);
      if (!button) {
        button = create("button", "notification-item"); button.type = "button";
        button.addEventListener("click", () => {
          if (state.viewer?.id !== viewerId || state.notifications !== notifications
              || !notificationAccountCurrent(notifications)) return;
          void openNotification(button._notificationGroup);
        });
        rows.set(key, button);
      }
      button._notificationGroup = group;
      button.dataset.notificationId = item.id;
      button.dataset.notificationIds = JSON.stringify(group.items.map(member => member.id));
      button.className = `notification-item${group.read ? "" : " is-unread"}`;
      // Keep the focusable row itself; its title/detail can become available after opening a post.
      button.replaceChildren();
      const avatars = create("span", `notification-avatars${group.actors.length > 1 ? " is-stacked" : ""}`);
      const faces = group.actors.length ? group.actors : [item.actor || { display_name: state.productName, id: "system" }];
      for (const actor of faces.slice(0, 2)) {
        const avatar = create("span", "avatar");
        styleAvatar(avatar, actor);
        avatars.append(avatar);
      }
      const copy = create("span", "notification-copy");
      const line = create("span", "notification-line");
      line.append(notificationGroupNameNode(group));
      copy.append(line);
      if (!group.read) copy.append(create("span", "sr-only", "Unread"));
      if (item.detail?.reason) {
        const verb = { remove_drop: "Removed", restore_drop: "Restored", warn: "Warning", suspend: "Suspended", ban: "Banned" }[item.detail.action] || "Decision";
        copy.append(create("span", "notification-detail", `${verb}: ${item.detail.reason}`));
      } else if (item.detail?.category) {
        copy.append(create("span", "notification-detail", `Category: ${String(item.detail.category).replaceAll("_", " ")}`));
      } else if (item.detail?.condition) {
        const state = item.detail.state === "resolved" ? "Resolved" : item.detail.state === "reminder" ? "Still active" : "Active";
        copy.append(create("span", "notification-detail", `${state}: ${String(item.detail.condition).replaceAll("_", " ")}`));
      } else {
        const dropTitle = notificationDropTitle(item);
        if (dropTitle) copy.append(create("span", "notification-detail", dropTitle));
      }
      copy.append(create("span", "notification-time", (typeof compactTime === "function" ? compactTime : relativeTime)(item.created_at)));
      const side = create("span", "notification-side");
      if (!group.read) side.append(create("i", "notification-dot"));
      button.append(avatars, copy, side);
      wanted.push(button);
    }
    for (const [key, node] of rows) if (!keys.has(key)) { node.remove(); rows.delete(key); }
    for (const [key, node] of headings) if (!sections.has(key)) { node.remove(); headings.delete(key); }
    // Reorder only when necessary: ordinary read-state updates leave each button in place.
    for (let index = 0; index < wanted.length; index++) {
      if (el.notificationList.children[index] !== wanted[index])
        el.notificationList.insertBefore(wanted[index], el.notificationList.children[index] || null);
    }
    for (const child of [...el.notificationList.children]) if (!wanted.includes(child)) child.remove();
    if (focused && state.view === "notifications" && document.activeElement !== focused
        && (!document.activeElement || document.activeElement === document.body)) {
      const survivors = wanted.filter(node => node._notificationGroup);
      const target = focused.isConnected ? focused : survivors[Math.min(focusedIndex, survivors.length - 1)];
      target?.focus({ preventScroll: true });
    }
    el.notificationsEmpty.hidden = !notifications.loaded || notifications.loading
      || notifications.items.length > 0 || Boolean(notifications.nextCursor);
    el.notificationsMore.hidden = !state.notifications.nextCursor;
    el.notificationsMore.disabled = Boolean(notifications.loading);
    el.notificationsReadAll.hidden = state.notifications.unreadCount === 0;
    el.notificationsReadAll.setAttribute("aria-disabled", String(Boolean(notifications.pendingAll)));
    // Only a private profile receives follow requests; the entry appears once one has arrived.
    const followRequestsEntry = typeof document.getElementById === "function" ? document.getElementById("open-follow-requests") : null;
    if (followRequestsEntry) followRequestsEntry.hidden = !notifications.items.some((item) => item?.kind === "follow_request");
    updateSocialBadges();
  }

  function notificationWorkspace() {
    let notifications = state.notifications;
    const viewerId = state.viewer?.id || null;
    if (notifications.owner && (notifications.owner.viewerId !== viewerId
        || notifications.owner.social !== state.messages || notifications.owner.csrf !== state.csrf)) {
      notifications.controller?.abort();
      notifications.countController?.abort();
      notifications = state.notifications = { items: [], nextCursor: null, unreadCount: 0, loaded: false };
      clear(el.notificationList);
      el.notificationsLoading.hidden = true;
      el.notificationsError.hidden = true;
      el.notificationsEmpty.hidden = true;
      el.notificationsMore.hidden = true;
      el.notificationsReadAll.hidden = true;
      el.notificationsReadAll.setAttribute("aria-disabled", "false");
      updateSocialBadges();
    }
    notifications.owner ||= { viewerId, social: state.messages, csrf: state.csrf };
    notifications.pages ||= [];
    notifications.confirmedRead ||= new Map();
    notifications.pendingRead ||= new Set();
    notifications.readQueue ||= [];
    return notifications;
  }

  function notificationAccountCurrent(notifications) {
    return state.notifications === notifications && notifications.owner?.viewerId === (state.viewer?.id || null)
      && notifications.owner.social === state.messages && notifications.owner.csrf === state.csrf;
  }

  function overlayNotificationReads(notifications, items) {
    return items.map(item => notifications.confirmedRead.has(item.id)
      ? { ...item, read: true, read_at: item.read_at || notifications.confirmedRead.get(item.id).at } : item);
  }

  async function refreshNotificationCount(notifications) {
    if (!notificationAccountCurrent(notifications) || !state.viewer || notifications.readBusy) return;
    const revision = notifications.countRevision = (notifications.countRevision || 0) + 1;
    notifications.countController?.abort();
    const controller = notifications.countController = new AbortController();
    const current = () => notificationAccountCurrent(notifications)
      && notifications.countRevision === revision && notifications.countController === controller
      && !controller.signal.aborted && !notifications.readBusy;
    try {
      // This read never installs a page or cancels a Back/recovery window read.
      const result = await api("/api/notifications?limit=1", { signal: controller.signal });
      if (!current()) return;
      notifications.unreadCount = result.unread_count || 0;
      if (notifications.countFailed && !notifications.pageError) el.notificationsError.hidden = true;
      notifications.countFailed = false;
      updateSocialBadges();
      el.notificationsReadAll.hidden = notifications.unreadCount === 0;
    } catch (error) {
      if (current()) { notifications.countFailed = true; el.notificationsError.hidden = false; }
    } finally {
      if (notifications.countController === controller) notifications.countController = null;
      if (state.notifications === notifications && !notificationAccountCurrent(notifications)) {
        notificationWorkspace();
        el.notificationsError.hidden = false;
      }
    }
  }

  async function drainNotificationReadQueue(notifications) {
    if (notifications.readBusy) return;
    notifications.readBusy = true;
    let confirmed = false;
    while (notifications.readQueue.length) {
      const job = notifications.readQueue.shift();
      let succeeded = false;
      try {
        if (!notificationAccountCurrent(notifications)) continue;
        if (job.all) {
          job.ids = notifications.items.map(item => item.id);
          for (const id of job.ids) notifications.pendingRead.add(id);
        }
        // A preceding all-read may have confirmed an explicit job while it waited.
        const writeIds = job.all ? job.ids : job.ids.filter(id => !notifications.confirmedRead.has(id));
        const chunks = job.all ? [writeIds] : Array.from({ length: Math.ceil(writeIds.length / 100) },
          (_, index) => writeIds.slice(index * 100, index * 100 + 100));
        for (const ids of chunks) {
          if (!notificationAccountCurrent(notifications)) break;
          const revision = notifications.countRevision = (notifications.countRevision || 0) + 1;
          const result = await api("/api/notifications/read", {
            method: "PUT", csrf: true, json: { notification_ids: job.all ? [] : ids, all: job.all },
          });
          if (!notificationAccountCurrent(notifications)) break;
          const countCurrent = notifications.countRevision === revision;
          notifications.countRevision = (notifications.countRevision || 0) + 1;
          const at = new Date().toISOString();
          const readRevision = notifications.readRevision = (notifications.readRevision || 0) + 1;
          for (const id of ids) notifications.confirmedRead.set(id, { at, revision: readRevision });
          notifications.items = overlayNotificationReads(notifications, notifications.items);
          if (countCurrent) notifications.unreadCount = result.unread_count || 0;
          confirmed = true;
          renderNotifications();
        }
        succeeded = notificationAccountCurrent(notifications);
      } catch (error) {
        if (notificationAccountCurrent(notifications)) toast(error.message, true);
      } finally {
        for (const id of job.ids) notifications.pendingRead.delete(id);
        if (notifications.pendingAll === job) notifications.pendingAll = null;
        job.resolve(succeeded);
      }
    }
    notifications.readBusy = false;
    if (state.notifications === notifications && !notificationAccountCurrent(notifications)) {
      notificationWorkspace();
      el.notificationsError.hidden = false;
    }
    if (notificationAccountCurrent(notifications)) {
      el.notificationsReadAll.setAttribute("aria-disabled", "false");
      if (confirmed) void refreshNotificationCount(notifications);
    }
  }

  async function refreshNotifications(append = false, quiet = false) {
    const notifications = notificationWorkspace();
    if (!state.viewer) {
      notifications.controller?.abort();
      notifications.items = []; notifications.pages = []; notifications.nextCursor = null;
      notifications.unreadCount = 0; notifications.loaded = false;
      el.notificationsLoading.hidden = true; renderNotifications();
      if (!quiet) openDialog(el.account);
      return;
    }
    if (append && (notifications.loading || !notifications.nextCursor)) return;
    const requestId = notifications.requestId = (notifications.requestId || 0) + 1;
    notifications.controller?.abort();
    const controller = notifications.controller = new AbortController();
    const countRevision = notifications.countRevision = (notifications.countRevision || 0) + 1;
    const readRevision = notifications.readRevision || 0;
    const ownsRead = () => state.notifications === notifications && notifications.requestId === requestId
      && notifications.controller === controller;
    const current = () => ownsRead() && notificationAccountCurrent(notifications) && !controller.signal.aborted;
    const depth = append ? 1 : Math.max(1, notifications.pages.length);
    let cursor = append ? notifications.nextCursor : null;
    const pages = append ? [...notifications.pages] : [];
    const seen = new Set(pages.map(page => page.cursor).filter(Boolean));
    const items = new Map((append ? notifications.items : []).map(item => [item.id, item]));
    notifications.loading = true; el.notificationsMore.disabled = true;
    el.notificationsLoading.hidden = notifications.loaded || append;
    notifications.pageError = false; el.notificationsError.hidden = true;
    if (!notifications.loaded) el.notificationsEmpty.hidden = true;
    try {
      let result;
      for (let index = 0; index < depth; index++) {
        if (!current()) return;
        if (cursor && seen.has(cursor)) throw new Error("Notifications could not be refreshed. Try again.");
        if (cursor) seen.add(cursor);
        const params = new URLSearchParams({ limit: "30" });
        if (cursor) params.set("cursor", cursor);
        result = await api(`/api/notifications?${params}`, { signal: controller.signal });
        if (!current()) return;
        const next = result.next_cursor || null;
        if (next && (next === cursor || seen.has(next))) throw new Error("Notifications could not be refreshed. Try again.");
        pages.push({ cursor, nextCursor: next });
        for (const item of result.items) items.set(item.id, item);
        cursor = next;
        if (!cursor) break;
      }
      if (!current()) return;
      for (const [id, acknowledgment] of notifications.confirmedRead) {
        if (items.get(id)?.read || (!items.has(id) && acknowledgment.revision <= readRevision))
          notifications.confirmedRead.delete(id);
      }
      notifications.items = overlayNotificationReads(notifications, [...items.values()]);
      notifications.pages = pages; notifications.nextCursor = cursor;
      if (notifications.countRevision === countRevision && !notifications.readBusy) {
        notifications.unreadCount = result.unread_count || 0; notifications.countFailed = false;
      }
      notifications.loaded = true; notifications.retryAppend = false; notifications.loading = false;
      renderNotifications();
      return true;
    } catch (error) {
      if (!current()) return;
      notifications.retryAppend = append; notifications.pageError = true; el.notificationsError.hidden = false;
      if (!quiet) toast(error.message, true);
      return false;
    } finally {
      if (ownsRead()) {
        notifications.controller = null; notifications.loading = false;
        el.notificationsLoading.hidden = true; el.notificationsMore.disabled = false;
        if (!notificationAccountCurrent(notifications)) {
          notificationWorkspace();
          el.notificationsError.hidden = false;
        }
      }
    }
  }

  async function markNotificationsRead(ids = [], all = false) {
    if (!state.viewer) return;
    const notifications = notificationWorkspace();
    if (all && notifications.pendingAll) return notifications.pendingAll.promise;
    const selected = [...new Set(ids)].filter(id => typeof id === "string" && id
      && !notifications.pendingRead.has(id) && !notifications.confirmedRead.has(id));
    if (!all && !selected.length) return;
    let resolve;
    const promise = new Promise(done => { resolve = done; });
    const job = { ids: selected, all, resolve, promise };
    for (const id of selected) notifications.pendingRead.add(id);
    if (all) { notifications.pendingAll = job; el.notificationsReadAll.setAttribute("aria-disabled", "true"); }
    notifications.readQueue.push(job);
    void drainNotificationReadQueue(notifications);
    return promise;
  }

  function newMessageSearchState() {
    return { query: "", open: false, items: [], nextCursor: null, loading: false, error: "",
      retryAppend: false, controller: null, requestId: 0, timer: null, startingId: null };
  }

  function messagePeopleSearch() {
    return state.messages.people ||= newMessageSearchState();
  }

  function resetMessageSearch(clearInput = true) {
    const search = messagePeopleSearch();
    clearTimeout(search.timer);
    search.controller?.abort();
    search.requestId += 1;
    state.messages.people = newMessageSearchState();
    if (clearInput) el.messagesSearch.value = "";
    renderMessageSearch();
  }

  function messageSearchTerm(query) {
    return query.startsWith("@") && !query.includes(" ") ? query.slice(1) : query;
  }

  function renderMessageSearch() {
    const search = messagePeopleSearch();
    const open = Boolean(state.viewer) && search.open;
    const hasQuery = Boolean(el.messagesSearch.value);
    el.messagesSearchPanel.hidden = !open;
    el.messagesInbox.hidden = open;
    el.messagesSearchClear.hidden = !open && !hasQuery;
    el.messagesSearchClear.setAttribute("aria-label", hasQuery ? "Clear people search" : "Close people search");
    el.messagesSearchPanel.setAttribute("aria-busy", String(search.loading || Boolean(search.startingId)));
    clear(el.messagesSearchResults);
    hideError(el.messagesSearchError);
    if (search.error) showError(el.messagesSearchError, search.error);
    el.messagesSearchRetry.hidden = !search.error || Boolean(search.startingId);
    el.messagesSearchMore.hidden = !search.nextCursor;
    el.messagesSearchMore.disabled = search.loading || Boolean(search.startingId);
    el.messagesSearchRetry.disabled = search.loading;
    if (!open) return;
    if (search.startingId) el.messagesSearchStatus.textContent = "Opening conversation…";
    else if (Array.from(messageSearchTerm(search.query)).length < 2) el.messagesSearchStatus.textContent = "Search by name or @username. Enter at least 2 characters.";
    else if (search.loading) el.messagesSearchStatus.textContent = "Searching people…";
    else if (search.error) el.messagesSearchStatus.textContent = "Your search is still here. Try again when you're ready.";
    else if (search.items.length) el.messagesSearchStatus.textContent = "Choose someone to message.";
    else el.messagesSearchStatus.textContent = search.nextCursor ? "No people on this page. Load more to continue." : "No people found. Try another name or @username.";
    for (const person of search.items) {
      const known = state.messages.conversations.find((item) => item.participant?.id === person.id);
      const button = create("button", "message-person");
      button.type = "button";
      button.disabled = Boolean(search.startingId);
      const action = known ? "Open" : "Message";
      button.setAttribute("aria-label", `${action} ${accountAccessibleName(person)}, @${person.handle}`);
      const avatar = create("span", "avatar avatar-small");
      styleAvatar(avatar, person);
      const copy = create("span", "message-person-copy");
      copy.append(accountNameNode(person), create("span", "", `@${person.handle}`));
      button.append(avatar, copy, create("span", "message-person-action", action));
      button.addEventListener("click", () => startMessageConversation(person));
      el.messagesSearchResults.append(button);
    }
  }

  function updateMessageSearch(value) {
    resetMessageSearch(false);
    // Keep the typed value and cursor intact while normalizing the server query.
    if (el.messagesSearch.value !== value) el.messagesSearch.value = value;
    const search = messagePeopleSearch();
    search.query = value.normalize("NFKC").trim().replace(/\s+/gu, " ");
    search.open = Boolean(search.query);
    if (Array.from(search.query).length > 80) search.error = "Use 80 characters or fewer.";
    renderMessageSearch();
    if (state.viewer && !search.error && Array.from(messageSearchTerm(search.query)).length >= 2) {
      search.timer = setTimeout(() => refreshMessagePeople(), 350);
    }
  }

  async function refreshMessagePeople(append = false) {
    const search = messagePeopleSearch();
    if (!state.viewer || state.view !== "messages" || !search.open || search.loading
      || Array.from(messageSearchTerm(search.query)).length < 2 || Array.from(search.query).length > 80
      || (append && !search.nextCursor)) return;
    clearTimeout(search.timer);
    search.controller?.abort();
    const controller = new AbortController();
    search.controller = controller;
    const context = messageContext();
    const requestId = ++search.requestId;
    const query = search.query;
    const current = () => context.accountCurrent() && state.view === "messages"
      && context.messages.people === search && search.open && search.query === query && search.requestId === requestId;
    search.loading = true;
    search.error = "";
    search.retryAppend = append;
    renderMessageSearch();
    try {
      const params = new URLSearchParams({ type: "people", q: query, limit: "12" });
      if (append) params.set("people_cursor", search.nextCursor);
      const result = await api(`/api/search?${params}`, { signal: controller.signal });
      if (!current()) return;
      const page = result.people;
      const known = new Set(append ? search.items.map((item) => item.id) : []);
      const items = (page?.items || []).filter((person) => {
        if (!person.id || person.id === state.viewer.id || known.has(person.id)) return false;
        known.add(person.id);
        return true;
      });
      search.items = append ? [...search.items, ...items] : items;
      search.nextCursor = page?.next_cursor || null;
    } catch (error) {
      if (current() && error.name !== "AbortError") search.error = error.message;
    } finally {
      if (current()) {
        search.loading = false;
        search.controller = null;
        renderMessageSearch();
      }
    }
  }

  function beginNewMessage() {
    if (state.sessionRestricted) return;
    if (!state.capabilities.community_writes) return;
    if (!state.viewer) { openDialog(el.account); return; }
    closeMessageThread();
    resetMessageSearch();
    messagePeopleSearch().open = true;
    renderMessageSearch();
    el.messagesSearch.focus();
  }

  async function startMessageConversation(person) {
    if (!state.capabilities.community_writes) return;
    if (!requireVerified() || state.view !== "messages" || !person?.id || person.id === state.viewer.id) return;
    const context = messageContext();
    const search = messagePeopleSearch();
    if (!search.open || !search.items.some((item) => item.id === person.id) || search.startingId) return;
    const starting = context.messages.startingRecipients ||= new Set();
    if (starting.has(person.id)) return;
    const selectionVersion = context.messages.selectionVersion;
    const current = () => context.accountCurrent() && state.view === "messages"
      && context.messages.people === search && search.open && context.messages.selectionVersion === selectionVersion;
    const existing = context.messages.conversations.find((item) => item.participant?.id === person.id);
    if (existing) {
      // Opening a known request must not implicitly accept it via the create endpoint.
      resetMessageSearch();
      await openMessageThread(existing.id);
      return;
    }
    starting.add(person.id);
    search.startingId = person.id;
    search.error = "";
    renderMessageSearch();
    try {
      const result = await api("/api/messages/conversations/open", {
        method: "POST", csrf: true, json: { recipient_user_id: person.id },
      });
      if (!current()) return;
      const conversation = result.conversation;
      if (!conversation?.id) throw new Error("The conversation could not be opened. Please try again.");
      context.messages.conversationRequestId += 1;
      context.messages.conversations = [conversation, ...context.messages.conversations.filter((item) => item.id !== conversation.id)];
      resetMessageSearch();
      void refreshConversations(true);
      await openMessageThread(conversation.id);
    } catch (error) {
      if (current()) {
        search.error = error.message;
        // The result stays visible so a failed/blocked start can be retried deliberately.
        search.retryAppend = false;
      }
    } finally {
      starting.delete(person.id);
      if (current()) {
        search.startingId = null;
        renderMessageSearch();
      }
    }
  }

  function conversationPreview(conversation) {
    if (conversation.kind === "group" && conversation.member_state === "invited") return "Group invitation";
    if (conversation.request_state === "pending") {
      return conversation.incoming_request ? "Message request" : "Message request sent";
    }
    if (conversation.request_state === "declined") return "Message request declined";
    const message = conversation.last_message;
    if (!message) return "Conversation started";
    if (message.deleted) return "Message deleted";
    return message.body || "New message";
  }

  function messageReceipts() {
    if (!state.viewer || state.sessionRestricted || !window.LobbyMessageReceipts) return null;
    if (!state.messages.receipts) {
      const context = messageContext(), owner = state.viewer.id;
      state.messages.receipts = window.LobbyMessageReceipts.create({
        root: el.messageList, owner, current: context.accountCurrent,
        readReceiptsEnabled: () => state.privacySettings?.readReceiptsEnabled() === true,
        active: () => state.view === "messages" && Boolean(state.messages.selectedId)
          && !state.sessionRestricted && !state.messages.conversation?.read_only
          && state.messages.conversation?.request_state === "accepted" && !state.messages.conversation?.blocked,
        submit: (json) => {
          if (!context.accountCurrent() || state.sessionRestricted) throw new Error("Account unavailable.");
          return api("/api/messages/receipts", {method: "PUT", csrf: true, json});
        },
        onSeen: async (ids, positions = []) => {
          if (!context.accountCurrent()) return;
          const conversations = new Map();
          for (const item of positions) {
            if (!item.conversation_id || !ids.includes(item.id)) continue;
            if (!conversations.has(item.conversation_id)) conversations.set(item.conversation_id, []);
            conversations.get(item.conversation_id).push(item);
          }
          for (const [conversationId, items] of conversations) {
            const times = items.map(item => Date.parse(item.created_at));
            const latest = Math.max(...times.filter(Number.isFinite));
            // The wire timestamp has milliseconds; /read orders full database
            // timestamps and IDs. Submit every collision (or unknown timestamp)
            // sequentially and let the server's monotone read position decide.
            const candidates = items.filter((item, index) => !Number.isFinite(times[index]) || times[index] === latest)
              .sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
            for (const item of candidates) {
              if (!context.accountCurrent()) return;
              await markConversationRead(item.id, conversationId);
            }
          }
        },
      });
    }
    return state.messages.receipts;
  }

  function observeMessageReceipts() {
    const tracker = messageReceipts();
    if (!tracker) return;
    const permitted = !state.sessionRestricted && !state.messages.conversation?.read_only
      && state.messages.conversation?.request_state === "accepted" && !state.messages.conversation?.blocked;
    if (permitted) tracker.receive(state.messages.items);
    tracker.observe(permitted ? state.messages.items : []);
  }

  function directMessageReceipt(message) {
    const status = window.LobbyMessageReceipts?.receiptState(message.receipt);
    if (!status || message.deleted) return null;
    const names = {sent: "Sent", delivered: "Delivered", seen: "Seen"};
    const receipt = create("span", `message-receipt is-${status}`);
    receipt.dataset.receipt = status;
    receipt.setAttribute("aria-label", names[status]);
    receipt.title = status === "sent" ? "Sent to LOUDERR" : status === "delivered"
      ? "Received by their browser" : "Seen in the conversation";
    const mark = icon(status === "sent" ? "message-sent" : "message-delivered");
    mark.setAttribute("aria-hidden", "true");
    receipt.append(mark, create("span", "receipt-label", names[status]));
    if (message.receipt.is_group) {
      const {seen_count: seen, delivered_count: delivered, total_count: total} = message.receipt;
      const label = seen ? `Seen by ${seen} of ${total}` : delivered ? `Delivered to ${delivered} of ${total}` : "Sent";
      receipt.setAttribute("aria-label", label); receipt.title = label;
      receipt.lastChild.textContent = label;
    }
    return receipt;
  }

  function messageDateLabel(value) {
    const date = new Date(value), today = new Date(), yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString(undefined, {month: "short", day: "numeric", year: date.getFullYear() === today.getFullYear() ? undefined : "numeric"});
  }

  async function pinDirectMessage(message, pinned) {
    const conversationId = state.messages.selectedId;
    if (!conversationId) return;
    const context = messageContext(conversationId), selectionVersion = context.messages.selectionVersion;
    const current = () => context.threadCurrent() && state.view === "messages"
      && context.messages.selectionVersion === selectionVersion;
    try {
      await api(`/api/messages/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(message.id)}/pin`, {
        method: "PUT", csrf: true, json: { pinned },
      });
      if (!current()) return;
      await openMessageThread(conversationId, false);
      if (current()) toast(pinned ? "Message pinned" : "Message unpinned");
    } catch (error) { if (current()) toast(error.message, true); }
  }

  function openDirectMessageActions(message) {
    const mine = message.sender?.id === state.viewer?.id;
    if (message.hidden_for_me) return;
    const context = messageContext(), dialog = $("#message-actions-dialog");
    globalThis.LouderrMessageTools?.actions($("#message-extra-actions"), message, dialog);
    globalThis.LouderrMessageHide?.actions($("#message-extra-actions"), message, dialog);
    $("#message-actions-time").textContent = new Date(message.created_at).toLocaleString();
    // Members with message access may pin in direct or group conversations.
    const pin = $("#message-action-pin");
    if (pin) {
      const conversation = state.messages.conversation;
      const selectionVersion = context.messages.selectionVersion;
      pin.hidden = Boolean(message.deleted) || state.sessionRestricted
        || !(state.messages.participant || conversation?.kind === "group")
        || conversation?.can_message === false || ["pending", "declined"].includes(conversation?.request_state);
      pin.textContent = message.pinned ? "Unpin message" : "Pin message";
      pin.onclick = () => {
        dialog.close();
        if (!context.threadCurrent() || state.view !== "messages"
          || context.messages.selectionVersion !== selectionVersion) return;
        void pinDirectMessage(message, !message.pinned);
      };
    }
    // Media a person was sent is theirs to keep. A photo had no way to save at all, and the
    // native control menu that carries one for video and audio is desktop-only. Withheld while the
    // conversation is set to disappear: keeping a copy is the one thing that mode promises not to do.
    const save = $("#message-action-save");
    if (save) {
      const media = message.deleted ? null : message.media;
      const mediaUrl = typeof media?.media_url === "string" ? media.media_url : "";
      const mediaType = typeof media?.media_type === "string" ? media.media_type : "";
      save.hidden = !mediaUrl || Boolean(state.messages.conversation?.disappearing);
      save.textContent = mediaType.startsWith("video/") ? "Save video"
        : mediaType.startsWith("audio/") ? "Save audio" : "Save photo";
      save.onclick = () => {
        dialog.close();
        const link = document.createElement("a");
        link.href = mediaUrl;
        const stamp = new Date(message.created_at).toISOString().slice(0, 10);
        const extension = (mediaType.split("/")[1] || "").split(";")[0] || "bin";
        link.download = `louderr-${stamp}.${extension}`;
        document.body.append(link);
        link.click();
        link.remove();
      };
    }
    const action = $("#message-action-confirm");
    action.textContent = mine ? "Delete for everyone" : "Report message";
    action.hidden = Boolean(message.removed_by_moderation || (mine && message.deleted));
    action.disabled = mine && (state.sessionRestricted || !state.capabilities.community_writes);
    action.onclick = () => {
      dialog.close();
      if (!context.threadCurrent() || state.view !== "messages") return;
      const current = state.messages.items.find(item => item.id === message.id);
      if (!current || current.removed_by_moderation || (mine && current.deleted)) return;
      if (mine) void deleteDirectMessage(message.id);
      else {
        state.messages.reportMessage = current;
        openReport({
          id: current.id,
          title: "Report this message",
          endpoint: () => `/api/messages/conversations/${encodeURIComponent(context.conversationId)}`
            + `/messages/${encodeURIComponent(current.id)}/reports`,
          // A report takes a moment, and the thread underneath can move on while it is in flight.
          current: () => context.threadCurrent() && state.messages.reportMessage === current,
          track: () => track("report", null, { target_type: "direct_message", target_id: current.id }),
          receipt: (category) => category === "child_safety"
            ? "Report sent to safety review" : "Private message report added to the moderator queue",
          done: () => { state.messages.reportMessage = null; },
        });
      }
    };
    dialog.onclose = () => {
      action.onclick = null;
      if (pin) pin.onclick = null;
      action.textContent = "";
      $("#message-actions-time").textContent = "";
      // A private-event refresh may have replaced the native dialog's opener.
      // Leave focus alone when another dialog or control already owns it.
      requestAnimationFrame(() => {
        if (context.threadCurrent() && state.view === "messages"
          && (document.activeElement === document.body || dialog.contains(document.activeElement))
          && !document.querySelector("dialog[open]")) {
          restoreDirectMessageFocus(message.id);
        }
      });
    };
    dialog.showModal();
  }

  function renderConversations() {
    // Whoever signed in, and whenever: this is the one place every route into Messages
    // passes through, and start() only ever ran once.
    ensureMessageModules();
    const list = el.conversationList, messages = state.messages;
    const viewerId = state.viewer?.id || null, folder = messages.folder || "inbox";
    let owner = list._conversationOwner;
    const sameOwner = owner?.messages === messages && owner.viewerId === viewerId && owner.folder === folder;
    const focused = document.activeElement;
    const focusIndex = sameOwner ? Array.from(list.children).findIndex(row => row.contains(focused)) : -1;
    if (!sameOwner) {
      clear(list);
      owner = list._conversationOwner = { messages, viewerId, folder };
    }
    const conversations = viewerId ? messages.conversations
      .filter(item => Boolean(item.preferences?.archived) === (folder === "archived"))
      .slice().sort((a, b) => Number(Boolean(b.preferences?.pinned)) - Number(Boolean(a.preferences?.pinned))) : [];
    const existing = new Map(Array.from(list.children).filter(row => row._conversation).map(row => [row._conversation.id, row]));
    const rows = [];
    for (const conversation of conversations) {
      const isGroup = conversation.kind === "group";
      if (!conversation.participant && !isGroup) continue;
      let button = existing.get(conversation.id);
      if (!button) {
        button = create("button", "conversation-item");
        button.type = "button";
        button.dataset.conversationId = conversation.id;
        const avatar = create("span", "avatar avatar-small"), copy = create("span", "conversation-copy");
        const name = create("strong"), preview = create("span");
        copy.append(name, preview); button.append(avatar, copy);
        button._conversation = { id: conversation.id, avatar, copy, name, preview,
          pin: create("small", "conversation-pinned", "Pinned"), count: create("b", "conversation-count") };
        button.addEventListener("click", () => {
          if (list._conversationOwner !== owner || state.messages !== owner.messages
            || state.viewer?.id !== owner.viewerId || (state.messages.folder || "inbox") !== owner.folder
            || state.view !== "messages" || button.parentElement !== list) return;
          const current = state.messages.conversations.find(item => item.id === button._conversation.id);
          if (current && (current.participant || current.kind === "group")
            && Boolean(current.preferences?.archived) === (owner.folder === "archived")) openMessageThread(current.id);
        });
      }
      const content = button._conversation;
      const person = isGroup ? { display_name: conversation.title } : conversation.participant;
      const avatarKey = JSON.stringify([person.id || null, person.display_name || null, person.avatar_url || null]);
      if (content.avatarKey !== avatarKey) {
        styleAvatar(content.avatar, person); content.avatarKey = avatarKey;
      }
      const nameKey = JSON.stringify([isGroup, person.display_name || null, isGroup ? null : accountBadge(person)]);
      if (content.nameKey !== nameKey) {
        if (isGroup) { content.name.className = ""; content.name.textContent = conversation.title || ""; }
        else renderAccountName(content.name, person);
        content.nameKey = nameKey;
      }
      const preview = conversationPreview(conversation);
      if (content.preview.textContent !== preview) content.preview.textContent = preview;
      button.classList.toggle("is-active", conversation.id === messages.selectedId);
      if (conversation.preferences?.pinned) {
        if (content.pin.parentElement !== content.copy) content.copy.append(content.pin);
      } else content.pin.remove();
      if (conversation.unread_count) {
        const count = formatCount(conversation.unread_count);
        if (content.count.textContent !== count) content.count.textContent = count;
        if (content.count.parentElement !== button) button.append(content.count);
      } else content.count.remove();
      rows.push(button);
    }
    const keep = new Set(rows);
    rows.forEach((row, index) => {
      if (list.children[index] !== row) list.insertBefore(row, list.children[index] || null);
    });
    for (const row of Array.from(list.children)) if (!keep.has(row)) row.remove();
    el.conversationsEmpty.hidden = conversations.length > 0;
    el.messagesShell.classList.toggle("is-empty", conversations.length === 0);
    el.conversationsMore.hidden = !messages.conversationCursor;
    const window = messages.conversationWindow;
    const needsReplay = Boolean(window?.resetRequired)
      || Boolean(window && window.requestId !== (messages.conversationRequestId || 0));
    el.conversationsMore.disabled = messages.conversationsLoading || needsReplay;
    if (needsReplay && !messages.conversationsLoading) el.messagesError.hidden = false;
    updateSocialBadges();
    globalThis.LouderrConversationOrganization?.render();
    // Retain the actual focused button; only restore focus if a necessary move/removal
    // detached it. Another account, surface, control or modal owns its own focus.
    if (focusIndex >= 0 && state.view === "messages" && document.activeElement === document.body
      && !document.querySelector("dialog[open]")) {
      const target = list.contains(focused) ? focused : rows[Math.min(focusIndex, rows.length - 1)] || el.messagesSearch;
      if (target && !target.disabled && !target.closest("[hidden]") && target.getClientRects().length) {
        target.focus({ preventScroll: true });
      }
    }
  }

  function conversationWindow() {
    const messages = state.messages, viewerId = state.viewer?.id || null;
    const csrf = state.csrf, folder = messages.folder || "inbox";
    let window = messages.conversationWindow, replayDepth = 0;
    if (window && (window.viewerId !== viewerId || window.csrf !== csrf || window.folder !== folder)) {
      if (window.viewerId === viewerId && window.folder === folder) {
        // Preserve only accepted depth intent across token rotation, never old DTOs/cursors.
        replayDepth = Math.max(1, window.pages.length, window.replayDepth || 0);
      }
      window.read?.controller.abort();
      messages.conversationRequestId = (messages.conversationRequestId || 0) + 1;
      messages.conversations = []; messages.conversationCursor = null;
      messages.loaded = false; messages.conversationsLoading = false;
      window = null;
    } else if (window && window.requestId !== (messages.conversationRequestId || 0)) {
      // Confirmed mutations can retire a list read without starting a replacement.
      // Its previous append cursor must not become a retry after that mutation.
      window.read?.controller.abort(); window.read = null;
      messages.conversationsLoading = false;
      window.resetRequired = true; window.retryAppend = false;
      window.requestId = messages.conversationRequestId || 0;
    }
    if (!window) messages.conversationWindow = window = {
      viewerId, csrf, folder, pages: [], replayDepth, requestId: messages.conversationRequestId || 0,
      read: null, retryAppend: false, resetRequired: false,
    };
    return window;
  }

  function retryConversations() {
    const window = conversationWindow();
    return refreshConversations(false, window.retryAppend && !window.resetRequired);
  }

  async function refreshConversations(quiet = false, append = false) {
    const window = conversationWindow(), messages = state.messages;
    if (!state.viewer) {
      window.read?.controller.abort(); window.read = null;
      window.pages = []; window.retryAppend = false; window.resetRequired = false;
      messages.conversations = []; messages.unreadCount = 0;
      messages.conversationCursor = null; messages.loaded = false;
      messages.conversationRequestId = (messages.conversationRequestId || 0) + 1;
      messages.conversationsLoading = false;
      closeMessageThread(false); messages.drafts.clear(); el.messageBody.value = "";
      el.messagesLoading.hidden = true;
      if (!quiet) openDialog(el.account);
      return;
    }
    if (append && messages.conversationsLoading) return;
    if (append && window.resetRequired) append = false;
    if (append && !messages.conversationCursor) return;
    const context = messageContext();
    const requestId = messages.conversationRequestId = (messages.conversationRequestId || 0) + 1;
    window.requestId = requestId;
    window.read?.controller.abort();
    const read = window.read = { requestId, controller: new AbortController(), readCounts: new Map(), readRevision: 0, keepUnreadCount: false };
    const ownsRead = () => state.messages === messages && messages.conversationWindow === window && window.read === read;
    const ownsWindow = () => state.messages === messages && messages.conversationWindow === window
      && context.accountCurrent() && state.csrf === window.csrf && (messages.folder || "inbox") === window.folder;
    const current = () => ownsRead() && ownsWindow() && messages.conversationRequestId === requestId && !read.controller.signal.aborted;
    const depth = append ? 1 : Math.max(1, window.pages.length, window.replayDepth || 0);
    const pages = append ? window.pages.slice() : [];
    const seen = new Set(pages.map(page => page.cursor).filter(Boolean));
    const items = new Map((append ? messages.conversations : []).map(item => [item.id, item]));
    const fetched = new Map();
    let cursor = append ? messages.conversationCursor : null, failedCursor = cursor;
    messages.conversationsLoading = true;
    el.messagesLoading.hidden = messages.loaded || append; el.messagesError.hidden = true;
    renderConversations();
    try {
      let result;
      for (let index = 0; index < depth; index++) {
        if (!current()) return;
        if (cursor && seen.has(cursor)) throw Object.assign(new Error("Conversations changed. Refresh the list to continue."), { conversationCursorChanged: true });
        if (cursor) seen.add(cursor);
        const params = new URLSearchParams({ limit: "50" });
        if (window.folder === "archived") params.set("folder", "archived");
        if (cursor) params.set("cursor", cursor);
        failedCursor = cursor;
        const pageReadRevision = read.readRevision;
        result = await api(`/api/messages/conversations?${params}`, { signal: read.controller.signal });
        if (!current()) return;
        if (!Array.isArray(result?.items)) throw new Error("The conversation list could not be confirmed. Try again.");
        const next = result.next_cursor || null;
        if (next && (typeof next !== "string" || next === cursor || seen.has(next))) {
          throw Object.assign(new Error("Conversations changed. Refresh the list to continue."), { conversationCursorChanged: true });
        }
        for (const conversation of result.items) {
          if (!conversation?.id) throw new Error("The conversation list could not be confirmed. Try again.");
          if (conversation.last_message) {
            const shown = globalThis.LouderrMessageHide?.filter([conversation.last_message], messages);
            if (shown) conversation.last_message = shown[0] || null;
          }
          items.set(conversation.id, conversation); fetched.set(conversation.id, conversation);
          if (read.readCounts.get(conversation.id)?.revision <= pageReadRevision) read.readCounts.delete(conversation.id);
        }
        read.keepUnreadCount = read.readRevision !== pageReadRevision;
        pages.push({ cursor, nextCursor: next }); cursor = next;
        if (!cursor) break;
      }
      if (!current()) return;
      let unreadCount = result.unread_count || 0;
      if (read.keepUnreadCount) {
        // Local subtraction cannot account for arrivals in unloaded conversations.
        // Repair the global count once, after overlapping receipts have committed.
        const countRevision = read.readRevision;
        const params = new URLSearchParams({ limit: "1" });
        if (window.folder === "archived") params.set("folder", "archived");
        failedCursor = null;
        const count = await api(`/api/messages/conversations?${params}`, { signal: read.controller.signal });
        if (!current()) return;
        if (read.readRevision !== countRevision || !Number.isSafeInteger(count?.unread_count) || count.unread_count < 0) {
          throw new Error("The inbox changed while refreshing. Try again.");
        }
        unreadCount = count.unread_count;
      }
      // A newer confirmed read receipt wins over a response captured before it.
      // The overlay exists only for this in-flight list read and never grows as a cache.
      for (const [id, receipt] of read.readCounts) if (items.has(id)) items.get(id).unread_count = receipt.count;
      messages.conversations = [...items.values()]; messages.conversationCursor = cursor;
      window.pages = pages; window.replayDepth = 0; window.retryAppend = false; window.resetRequired = false;
      messages.unreadCount = unreadCount;
      messages.loaded = true;
      const receipts = messageReceipts();
      for (const conversation of fetched.values()) {
        if (conversation.request_state === "accepted" && !conversation.blocked && conversation.last_message) receipts?.receive([conversation.last_message]);
      }
      const selected = messages.conversations.find(item => item.id === messages.selectedId);
      if (selected) { messages.conversation = selected; messages.participant = selected.participant; }
      return true;
    } catch (error) {
      if (!current()) return;
      window.resetRequired = Boolean(error.conversationCursorChanged || (error.status === 400 && failedCursor));
      window.retryAppend = append && !window.resetRequired;
      el.messagesError.hidden = false;
      if (!quiet) toast(error.message, true);
      return false;
    } finally {
      if (ownsRead()) {
        const acceptedOwner = current();
        window.read = null; messages.conversationsLoading = false; el.messagesLoading.hidden = true;
        if (!acceptedOwner) {
          window.resetRequired = true; window.retryAppend = false;
          // An acknowledged mutation or token/folder replacement can retire this
          // exact slot without starting another read. Leave usable recovery UI.
          conversationWindow(); el.messagesError.hidden = false;
        }
        renderConversations();
        if (messages.selectedId && state.view === "messages") renderMessageThread();
      }
    }
  }

  function scrollMessagesBottom(behavior = "auto") {
    el.messageList.scrollTo({ top: el.messageList.scrollHeight, behavior });
  }

  function restoreDirectMessageFocus(messageId, controlIndex = 0) {
    const message = Array.from(el.messageList.children).find(node => node.dataset.messageId === messageId);
    const control = message?.querySelectorAll("button, a[href]")[controlIndex];
    if (control && !control.disabled) control.focus({ preventScroll: true });
    else if (!el.messageBody.disabled && !el.messageForm.hidden) el.messageBody.focus({ preventScroll: true });
    else el.messageParticipant.focus({ preventScroll: true });
  }

  // An attachment on a direct message. The URL is only a request: /api/media/{id} asks
  // _asset_accessible, which requires this viewer to be in the conversation, so a node
  // rendered here still shows nothing to someone who is not.
  // What the composer may do with the attachment it is holding. Sending before the
  // safety pipeline has approved the asset would be refused by the server anyway, so the
  // decision is made here and said out loud instead of failing at submit.
  function messageAttachmentState(entry) {
    if (!entry) return { holding: false, ready: false, blocked: false, assetId: null, text: "" };
    if (entry.error) return { holding: true, ready: false, blocked: true, assetId: null, text: entry.error };
    if (entry.kind === "pending-media") return { holding: true, ready: false, blocked: false, assetId: null,
      text: "Loading attachment controls…" };
    if (entry.kind === "file") return { holding: true, ready: entry.state === "succeeded",
      blocked: ["failed", "retired", "deleted", "deleting"].includes(entry.state), assetId: null,
      fileId: entry.state === "succeeded" ? entry.id : null, text: entry.text };
    if (entry.state === "succeeded" && entry.id) {
      return { holding: true, ready: true, blocked: false, assetId: entry.id, text: "Ready to send." };
    }
    if (["rejected", "failed", "expired", "cancelled", "response_lost"].includes(entry.state)) {
      return { holding: true, ready: false, blocked: true, assetId: null, text: uploadStatusText(entry) };
    }
    return { holding: true, ready: false, blocked: false, assetId: null, text: uploadStatusText(entry) };
  }

  // One pending attachment at a time, and it lives on state rather than as a free
  // variable: the client test suites extract single functions into a sandbox, where a
  // free variable throws but state.messageAttachment is simply undefined. Held on the
  // composer rather than per conversation, because sending a photo to the wrong person
  // is the kind of mistake that cannot be taken back.
  function paintMessageAttachment() {
    const info = messageAttachmentState(state.messageAttachment);
    if (!el.messageMediaDraft) return info;
    el.messageMediaDraft.hidden = !info.holding;
    if (el.messageMediaStatus) el.messageMediaStatus.textContent = info.text;
    if (el.messageFileCheck) {
      const entry = state.messageAttachment;
      const preparing = entry?.kind === "pending-media";
      const terminalMedia = entry && entry.kind !== "file" && !preparing
        && ["failed", "rejected", "cancelled", "expired"].includes(entry.state);
      el.messageFileCheck.textContent = preparing ? "Retry attachment" : "Check status";
      el.messageFileCheck.hidden = !entry || info.ready || Boolean(entry.terminal)
        || terminalMedia || (preparing && !entry.error);
      el.messageFileCheck.disabled = preparing ? entry.loading
        : entry?.kind === "file" ? Boolean(entry.busy || !entry.payload) : Boolean(entry?.pending);
    }
    const preview = el.messageMediaPreview;
    if (preview) {
      // The check takes seconds - 7.7 s for a 12 MP photo, measured on the live host - and the person
      // used to watch an empty box for all of it. Their own file is already on the device, so show it
      // at once and let the server's thumbnail take over when the check finishes.
      const url = (info.ready && state.messageAttachment?.result?.thumbnail_url)
        || state.messageAttachment?.localPreviewUrl || "";
      preview.hidden = !url;
      if (url) preview.src = url;
    }
    return info;
  }

  function clearMessageAttachment() {
    globalThis.LouderrVoiceNote?.cancel();
    if (state.messageAttachment?.localPreviewUrl && typeof URL?.revokeObjectURL === "function") {
      URL.revokeObjectURL(state.messageAttachment.localPreviewUrl);
    }
    state.messageAttachmentRevision = (state.messageAttachmentRevision || 0) + 1;
    if (state.messageAttachment?.kind === "file") state.messageFileUploads?.detach(state.messageAttachment);
    else if (state.messageAttachment?.kind === "pending-media") state.messageAttachment.file = null;
    else if (state.messageAttachment && window.LobbyUploads) mediaUploads().detach(state.messageAttachment);
    state.messageAttachment = null;
    if (el.messageMediaInput) el.messageMediaInput.value = "";
    if (el.messageFileInput) el.messageFileInput.value = "";
    paintMessageAttachment();
  }

  async function prepareMessageAttachment(entry) {
    if (!entry || entry.kind !== "pending-media" || state.messageAttachment !== entry || entry.loading) return;
    if (!entry.current()) { clearMessageAttachment(); return; }
    entry.loading = true; entry.error = null; paintMessageAttachment();
    let attached = entry;
    try {
      if (!window.LobbyUploads) await window.LouderrOptional?.load("uploads");
      if (state.messageAttachment !== entry || !entry.current()) return;
      if (!window.LobbyUploads) throw new Error("Attachment controls could not load. Retry to keep this selection.");
      const upload = mediaUploads().select(entry.file, "", "post", entry.current);
      upload.localPreviewUrl = entry.localPreviewUrl;
      entry.localPreviewUrl = null; entry.file = null;
      attached = upload; state.messageAttachment = upload; paintMessageAttachment();
      await mediaUploads().prepare(upload);
    } catch (error) {
      if (state.messageAttachment === attached && attached.current()) {
        attached.error = error.message || "The attachment could not be prepared. Check its status or try again.";
        paintMessageAttachment();
      }
    } finally {
      entry.loading = false;
      if (state.messageAttachment === attached) {
        if (!attached.current()) clearMessageAttachment();
        else paintMessageAttachment();
      }
    }
  }

  async function selectMessageAttachment(file, context = messageContext()) {
    if (!file || state.view !== "messages" || el.messageBody.disabled
        || !context.threadCurrent() || context.current?.() === false || !context.messages?.selectedId) return;
    // The recorder's strict context authorizes this initial handoff only. The retained
    // attachment has its own owner and can survive a visit to the same person's profile.
    const owner = messageContext(context.messages.selectedId), csrf = state.csrf;
    clearMessageAttachment();
    const revision = state.messageAttachmentRevision;
    const current = () => owner.threadCurrent() && state.csrf === csrf && state.messageAttachmentRevision === revision;
    const entry = { kind: "pending-media", file, current, state: "selected", loading: false,
      submissionKey: crypto.randomUUID(), error: null, localPreviewUrl: null };
    if (file.type?.startsWith("image/") && typeof URL?.createObjectURL === "function") {
      try { entry.localPreviewUrl = URL.createObjectURL(file); } catch (_) { /* Preview is optional; retain the file. */ }
    }
    state.messageAttachment = entry; paintMessageAttachment();
    await prepareMessageAttachment(entry);
  }

  async function selectMessageFile(file) {
    if (!file || !state.capabilities.message_files || el.messageBody.disabled) return;
    const context = messageContext();
    if (state.view !== "messages" || !context.conversationId || !context.threadCurrent()) return;
    const csrf = state.csrf;
    clearMessageAttachment();
    const revision = state.messageAttachmentRevision;
    // A profile visit keeps this conversation and draft; account, token or attachment changes retire it.
    const current = () => context.threadCurrent() && state.csrf === csrf
      && state.messageAttachmentRevision === revision;
    let entry = null;
    try {
      state.messageFileUploads ||= globalThis.LouderrMessageFiles?.create({api, changed: paintMessageAttachment});
      if (!state.messageFileUploads) throw new Error("File controls did not load. Reload the page.");
      entry = state.messageFileUploads.select(file, current);
      state.messageAttachment = entry; paintMessageAttachment();
      await state.messageFileUploads.prepare(entry);
    } catch (error) { if (current()) showError(el.messageFormError, error.message); }
    finally {
      if (entry && state.messageAttachment === entry && !current()) clearMessageAttachment();
    }
  }

  // A Drop shared into the thread. The payload is decided per viewer at read time, so a
  // Drop taken down after sharing simply is not here -- nothing to hide on the client.
  // Sending a Drop into a conversation. The note is optional; without one the message
  // carries the Drop title, because a message body is required and an empty one would be
  // refused by the server for reasons that have nothing to do with sharing.
  function sendDropBody(note, drop) {
    const typed = typeof note === "string" ? note.trim() : "";
    // The reference is checked for each reader. Never copy its title into durable
    // message text automatically, which would outlive the post's visibility.
    return typed.slice(0, 2000);
  }

  function sendDropCurrent(session) {
    return Boolean(session && state.sendDropSession === session && el.sendDropDialog.open
      && state.viewer?.id === session.owner && state.messages === session.scope);
  }

  function renderSendDropRecipients(session) {
    if (!sendDropCurrent(session)) return;
    clear(el.sendDropList);
    for (const recipient of session.items) {
      const row = create("button", "send-drop-row");
      row.type = "button";
      row.disabled = session.sending;
      const avatar = create("span", "avatar send-drop-avatar");
      if (recipient.person) styleAvatar(avatar, recipient.person);
      else avatar.textContent = recipient.name.slice(0, 1).toUpperCase();
      const label = create("span", "send-drop-name");
      label.append(create("strong", "", recipient.name));
      if (recipient.person?.handle) label.append(create("small", "", `@${recipient.person.handle}`));
      row.append(avatar, label, create("span", "send-drop-action", "Send"));
      row.setAttribute("aria-label", `Send to ${recipient.name}${recipient.person?.handle ? ` (@${recipient.person.handle})` : ""}`);
      row.addEventListener("click", () => sendDropTo(recipient.conversationId, row, recipient, session));
      el.sendDropList.append(row);
      if (session.focusKey === recipient.key && !session.sending) row.focus({ preventScroll: true });
    }
    session.focusKey = null;
    el.sendDropList.setAttribute("aria-busy", String(session.loading));
    el.sendDropStatus.textContent = session.sending ? "Sending…" : session.loading ? "Finding people…"
      : session.query && Array.from(messageSearchTerm(session.query)).length < 2 ? "Type at least 2 characters to find a friend."
      : session.items.length ? "Tap Send beside a friend to share this post." : "";
    el.sendDropEmpty.hidden = session.loading || session.items.length > 0 || !session.owner
      || Boolean(session.query && Array.from(messageSearchTerm(session.query)).length < 2);
    el.sendDropEmpty.textContent = session.query ? "No people found. Try their name or @username."
      : "Find a friend by name or @username to send this post.";
    el.sendDropMore.hidden = !session.cursor;
    el.sendDropMore.disabled = session.loading || session.sending;
    el.sendDropRetry.hidden = !session.error;
    el.sendDropRetry.disabled = session.loading || session.sending;
    el.sendDropSearch.disabled = session.sending || !session.owner;
  }

  async function refreshSendDropRecipients(session, append = false) {
    if (!sendDropCurrent(session) || !session.owner || session.sending || (append && (session.loading || !session.cursor))) return;
    clearTimeout(session.timer);
    const request = ++session.request;
    session.controller?.abort();
    const controller = session.controller = new AbortController();
    const current = () => sendDropCurrent(session) && session.request === request;
    session.loading = true;
    session.error = false;
    session.retryAppend = append;
    hideError(el.sendDropError);
    if (!append) { session.items = []; session.cursor = null; }
    renderSendDropRecipients(session);
    const add = (items) => {
      const known = new Set(session.items.map(item => item.key));
      for (const item of items) if (!known.has(item.key)) { known.add(item.key); session.items.push(item); }
    };
    try {
      if (session.query) {
        const params = new URLSearchParams({ type: "people", q: session.query, limit: "12" });
        if (append) params.set("people_cursor", session.cursor);
        const result = await api(`/api/search?${params}`, { signal: controller.signal });
        if (!current()) return;
        add((result.people?.items || []).filter(person => person.id && person.id !== session.owner).map(person => ({
          key: person.id, person, name: person.display_name || person.handle || "Member",
          conversationId: session.conversations.get(person.id),
        })));
        session.cursor = result.people?.next_cursor || null;
      } else {
        const params = new URLSearchParams({ folder: "all", limit: "30", detail: "recipients" });
        if (append) params.set("cursor", session.cursor);
        const result = await api(`/api/messages/conversations?${params}`, { signal: controller.signal });
        if (!current()) return;
        const rows = [];
        for (const conversation of result.items || []) {
          const group = conversation.kind === "group";
          const person = group ? null : conversation.participant || conversation.other;
          if (person?.id) session.conversations.set(person.id, conversation.id);
          if (conversation.blocked || conversation.can_message === false
              || (group && conversation.member_state !== "joined")) continue;
          rows.push({ key: group ? `group:${conversation.id}` : person?.id || conversation.id,
            person, conversationId: conversation.id,
            name: group ? conversation.title || "Group" : person?.display_name || person?.handle || "Conversation" });
        }
        add(rows);
        session.cursor = result.next_cursor || null;
      }
    } catch (error) {
      if (current() && !controller.signal.aborted) {
        session.error = true;
        showError(el.sendDropError, "People could not be loaded. Try again.");
      }
    } finally {
      if (current()) { session.loading = false; session.controller = null; renderSendDropRecipients(session); }
    }
  }

  async function openSendDrop(drop, source = null) {
    if (!drop) return;
    state.sendDropSession?.close();
    const session = { drop, owner: state.viewer?.id, scope: state.messages, items: [], conversations: new Map(),
      attempts: new Map(), query: "", cursor: null, loading: false, sending: false, error: false, request: 0 };
    session.close = () => {
      clearTimeout(session.timer);
      session.controller?.abort();
      if (state.sendDropSession !== session) return;
      state.sendDropSession = null;
      state.sendDrop = null;
      if (el.sendDropDialog.open) el.sendDropDialog.close();
    };
    state.sendDropSession = session;
    state.sendDrop = drop;
    el.sendDropNote.value = "";
    el.sendDropNote.hidden = !session.owner;
    el.sendDropSearch.hidden = !session.owner;
    el.sendDropNote.disabled = !session.owner;
    el.sendDropSearch.value = "";
    el.sendDropSearch.disabled = !session.owner;
    el.sendDropSignIn.hidden = Boolean(session.owner);
    el.sendDropSignIn.onclick = () => { if (sendDropCurrent(session)) { session.close(); openDialog(el.account); } };
    el.sendDropSubject.textContent = drop.title || drop.body?.slice(0, 120) || "Share this post";
    hideError(el.sendDropError);
    paintShareActions(drop, source);
    el.sendDropDialog.onclose = () => { if (!el.sendDropDialog.open) session.close(); };
    el.sendDropSearch.oninput = () => {
      if (!sendDropCurrent(session) || session.sending) return;
      clearTimeout(session.timer);
      session.request += 1;
      session.controller?.abort();
      session.loading = false;
      session.items = []; session.cursor = null; session.error = false;
      hideError(el.sendDropError);
      session.query = el.sendDropSearch.value.normalize("NFKC").trim().replace(/\s+/gu, " ").slice(0, 80);
      renderSendDropRecipients(session);
      if (!session.query) void refreshSendDropRecipients(session);
      else if (Array.from(messageSearchTerm(session.query)).length >= 2) {
        session.timer = setTimeout(() => refreshSendDropRecipients(session), 300);
      }
    };
    el.sendDropMore.onclick = () => refreshSendDropRecipients(session, true);
    el.sendDropRetry.onclick = () => refreshSendDropRecipients(session, session.retryAppend);
    openDialog(el.sendDropDialog);
    renderSendDropRecipients(session);
    if (session.owner) await refreshSendDropRecipients(session);
  }

  // The share sheet, the way the feed apps lay it out: people and conversations first, with a
  // search box, then the link, the device share and Add to Story in one row at the bottom.
  function paintShareActions(drop, source = null) {
    const session = state.sendDropSession;
    if (el.shareCopyLink) el.shareCopyLink.onclick = () => { if (sendDropCurrent(session)) void copyDropLink(drop, source); };
    if (el.shareNative) {
      const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
      el.shareNative.hidden = !canShare;
      el.shareNative.onclick = async () => {
        if (!sendDropCurrent(session)) return;
        try {
          await navigator.share({ title: drop.title, text: drop.body, url: new URL(`/d/${drop.id}`, location.origin).href });
          if (!sendDropCurrent(session)) return;
          track("share", drop.id, surfaceMetadata(source));
          session.close();
        } catch (error) { if (sendDropCurrent(session) && error?.name !== "AbortError") showError(el.sendDropError, error.message); }
      };
    }
    if (el.shareStory) {
      el.shareStory.disabled = false;
      const own = Boolean(state.viewer && drop.author_id === state.viewer.id);
      const active = drop.status === undefined || drop.status === "active";
      el.shareStory.hidden = !own || !active || Boolean(drop.structured_type) || !state.capabilities.community_writes
        || !state.capabilities.media_publication || state.capabilities.profile === "no-cost-closed-beta-v1";
      el.shareStory.onclick = async () => {
        if (!sendDropCurrent(session) || el.shareStory.disabled || !requireVerified()) return;
        el.shareStory.disabled = true;
        try {
          await api("/api/flow/stories", { method: "POST", csrf: true, json: { drop_id: drop.id } });
          if (!sendDropCurrent(session)) return;
          session.close();
          toast("Added to your Story");
          if (state.view === "flow" && state.flow.view === "stories") refreshFlow();
        } catch (error) { if (sendDropCurrent(session)) showError(el.sendDropError, error.message); }
        finally { if (sendDropCurrent(session)) el.shareStory.disabled = false; }
      };
    }
  }

  async function sendDropTo(conversationId, row, recipient = null, session = state.sendDropSession) {
    if (!sendDropCurrent(session) || !session.owner || session.sending || (!conversationId && !recipient?.person?.id)) return;
    if (!state.capabilities.community_writes || state.sessionRestricted) { toast("Messaging is unavailable for this account right now.", true); return; }
    if (!requireVerified()) return;
    const key = recipient?.key || conversationId;
    let attempt = session.attempts.get(key);
    if (!attempt) {
      attempt = { conversationId, body: sendDropBody(el.sendDropNote.value, session.drop), token: crypto.randomUUID() };
      session.attempts.set(key, attempt);
    }
    session.sending = true;
    el.sendDropNote.disabled = true;
    if (row) row.disabled = true;
    hideError(el.sendDropError);
    renderSendDropRecipients(session);
    try {
      if (!attempt.conversationId) {
        const opened = await api("/api/messages/conversations/open", { method: "POST", csrf: true,
          json: { recipient_user_id: recipient.person.id } });
        if (!sendDropCurrent(session)) return;
        attempt.conversationId = opened?.conversation?.id;
        if (!attempt.conversationId) throw new Error("That conversation could not be opened.");
      }
      const result = await api(`/api/messages/conversations/${encodeURIComponent(attempt.conversationId)}/messages`, {
        method: "POST", csrf: true,
        json: { body: attempt.body, shared_drop_id: session.drop.id, send_token: attempt.token },
      });
      if (!sendDropCurrent(session)) return;
      const review = result?.accepted === true && typeof result.quarantine_id === "string" && Boolean(result.quarantine_id)
        && ["pending_review", "review_complete"].includes(result.status);
      const delivered = typeof result?.id === "string" && Boolean(result.id)
        && result.conversation_id === attempt.conversationId
        && (result.hidden_for_me === true || (typeof result.body === "string"
          && typeof result.created_at === "string" && Number.isFinite(Date.parse(result.created_at))));
      if (!review && !delivered) {
        throw new Error("Delivery could not be confirmed. Retry the same recipient to check safely.");
      }
      session.close();
      toast(review ? result.status === "pending_review" ? "Sent for review. Not delivered yet." : "Review finished. This message was not delivered." : "Sent");
    } catch (error) {
      if (sendDropCurrent(session)) {
        session.focusKey = key;
        showError(el.sendDropError, error?.message || "Delivery could not be confirmed. Retry the same recipient to check safely.");
      }
    } finally {
      session.sending = false;
      if (sendDropCurrent(session)) { if (row) row.disabled = false; renderSendDropRecipients(session); }
    }
  }

  function directMessageSharedDrop(message) {
    if (message && !message.deleted && message.shared_drop_unavailable) {
      return create("p", "direct-message-shared", "Shared post unavailable");
    }
    const shared = message && !message.deleted ? message.shared_drop : null;
    if (!shared || typeof shared.id !== "string" || !shared.id) return null;
    const card = create("button", "direct-message-shared");
    card.type = "button";
    const author = shared.author && shared.author.display_name ? shared.author.display_name : "";
    card.setAttribute("aria-label", author ? `Open ${shared.title} by ${author}` : `Open ${shared.title}`);
    if (shared.thumbnail_url) {
      const thumb = create("img", "direct-message-shared-thumb");
      thumb.src = shared.thumbnail_url;
      thumb.alt = "";
      thumb.loading = "lazy";
      card.append(thumb);
    }
    const text = create("span", "direct-message-shared-text");
    text.append(create("strong", "", shared.title || "Untitled Drop"));
    if (author) text.append(create("span", "direct-message-shared-author", author));
    card.append(text);
    card.addEventListener("click", () => openDrop(shared.id));
    return card;
  }

  function directMessageMedia(message) {
    const media = message && !message.deleted ? message.media : null;
    if (!media || typeof media.media_url !== "string" || !media.media_url) return null;
    const type = typeof media.media_type === "string" ? media.media_type : "";
    const figure = create("div", "direct-message-media");
    if (type.startsWith("audio/")) {
      // A voice note is a player, not a picture. Nothing is preloaded: a thread can
      // hold many of them and a phone should not fetch every one to draw the list.
      const audio = create("audio");
      audio.src = media.media_url;
      audio.controls = true;
      audio.preload = "none";
      audio.setAttribute("aria-label", "Voice message");
      figure.classList.add("is-voice");
      const seconds = Number.isFinite(media.duration_ms) ? Math.round(media.duration_ms / 1000) : null;
      figure.append(audio);
      globalThis.LouderrVoicePlayback?.attach(audio, message);
      if (seconds) {
        figure.append(create("span", "direct-message-voice-length",
          `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`));
      }
      return figure;
    }
    if (type.startsWith("video/")) {
      const video = create("video");
      video.src = media.media_url;
      video.controls = true;
      video.preload = "metadata";
      video.playsInline = true;
      if (media.thumbnail_url) video.poster = media.thumbnail_url;
      video.setAttribute("aria-label", "Video attached to this message");
      figure.append(video);
      return figure;
    }
    const image = create("img");
    image.src = media.media_url;
    image.loading = "lazy";
    image.decoding = "async";
    // Reserving the box stops the thread jumping while the picture loads.
    if (Number.isFinite(media.width) && Number.isFinite(media.height)) {
      image.width = media.width;
      image.height = media.height;
    }
    image.alt = "Image attached to this message";
    figure.append(image);
    return figure;
  }

  function renderMessageThread(scroll = false, prepend = false) {
    if (el.messageFileButton) el.messageFileButton.hidden = !state.capabilities.message_files;
    state.messagePolls?.sync();
    state.messageSearch?.sync();
    if (typeof paintMessageThreadRead === "function") paintMessageThreadRead();
    state.messageShared?.sync();
    state.messageAlbums?.sync();
    state.messages.scheduled?.sync();
    state.messageTyping?.sync();
    globalThis.LouderrCalls?.sync();
    globalThis.LouderrVoicePlayback?.observe(state.messages.items);
    globalThis.LouderrVoicePlayback?.layout();
    globalThis.LouderrMessageTools?.sync();
    globalThis.LouderrMessageHide?.sync();
    state.groupInvites?.sync();
    globalThis.LouderrMessageGroups?.sync();
    state.messages.items = globalThis.LouderrMessageHide?.filter(state.messages.items, state.messages) || state.messages.items;
    const previousHeight = el.messageList.scrollHeight;
    const previousTop = el.messageList.scrollTop;
    const previousViewportTop = el.messageList.getBoundingClientRect().top;
    const focused = document.activeElement;
    const focusedMessage = focused?.closest(".direct-message");
    const focusToRestore = focusedMessage?.parentElement === el.messageList ? {
      messageId: focusedMessage.dataset.messageId,
      controlIndex: Array.from(focusedMessage.querySelectorAll("button, a[href]")).indexOf(focused),
    } : null;
    const participant = state.viewer ? state.messages.participant : null;
    const conversation = state.messages.conversation;
    const isGroup = Boolean(state.viewer && conversation?.kind === "group");
    const hasThread = Boolean(participant || isGroup);
    globalThis.LouderrConversationOrganization?.render();
    const selectedThread = Boolean(state.viewer && state.messages.selectedId);
    el.messageThreadEmpty.hidden = hasThread || selectedThread;
    el.messageThreadContent.hidden = !hasThread && !selectedThread;
    if (el.messageThreadHeader) el.messageThreadHeader.hidden = !hasThread;
    if (el.messageNoticeSlot) el.messageNoticeSlot.hidden = !hasThread;
    el.messagesShell.classList.toggle("has-thread", hasThread || selectedThread);
    el.messagesSection.classList.toggle("has-open-thread", hasThread || selectedThread);
    syncMessageThreadClass();
    placeToast();
    if (!hasThread) {
      clear(el.messageList);
      state.messages.receipts?.observe([]);
      el.messageForm.hidden = true;
      el.messageRequest.hidden = true;
      el.messagesOlder.hidden = true;
      el.messageBody.disabled = el.messageSend.disabled = true;
      el.messageEmote.disabled = true;
      if (state.emotes.target === el.messageBody) closeEmotePicker();
      return;
    }
    styleAvatar(el.messageParticipantAvatar, isGroup ? {display_name: conversation.title} : participant);
    if (isGroup) {
      el.messageParticipantName.textContent = conversation.title;
      el.messageParticipantHandle.textContent = `${conversation.member_count} members · Group details`;
    } else {
      renderAccountName(el.messageParticipantName, participant);
      el.messageParticipantHandle.textContent = `@${participant.handle}`;
    }
    state.socialExtras?.syncThreadBanner(el.messageList, conversation);
    const requestState = conversation?.request_state || "accepted";
    const incomingPending = requestState === "pending" && Boolean(conversation?.incoming_request);
    const outgoingPending = requestState === "pending" && !incomingPending;
    const declined = requestState === "declined";
    const readOnly = state.sessionRestricted || conversation?.read_only;
    const canSendInitialRequest = outgoingPending
      && conversation?.requester_id === state.viewer?.id
      && !conversation?.last_message;
    const canMessage = !readOnly && state.capabilities.community_writes
      && (conversation?.can_message !== false || canSendInitialRequest)
      && !incomingPending && !declined;
    const unavailable = !canMessage && !incomingPending && !outgoingPending && !declined;
    const showRequestState = readOnly || incomingPending || outgoingPending || declined || unavailable;
    el.messageRequest.hidden = !showRequestState;
    el.messageRequestActions.hidden = readOnly || !incomingPending;
    const deciding = state.messages.deciding.has(state.messages.selectedId);
    el.messageRequest.setAttribute("aria-busy", String(deciding));
    el.messageRequestAccept.disabled = deciding;
    el.messageRequestDecline.disabled = deciding;
    hideError(el.messageRequestError);
    if (readOnly) {
      el.messageRequestTitle.textContent = "Read-only conversation";
      el.messageRequestDescription.textContent = "You can review earlier messages and report a problem. New messages are unavailable.";
    } else if (incomingPending) {
      el.messageRequestTitle.textContent = isGroup ? "Group invitation" : "Message request";
      el.messageRequestDescription.textContent = isGroup
        ? `Join ${conversation.title} to receive new messages. Earlier messages are not shared with new members.`
        : `${participant.display_name} wants to message you.`;
    } else if (outgoingPending) {
      if (!state.capabilities.community_writes) {
        el.messageRequestTitle.textContent = "Messaging is read-only";
        el.messageRequestDescription.textContent = "Message requests are unavailable during the closed beta.";
      } else {
        el.messageRequestTitle.textContent = canSendInitialRequest ? "Send a message request" : "Message request sent";
        el.messageRequestDescription.textContent = canSendInitialRequest
          ? `You can send one message. You can continue after ${participant.display_name} accepts.`
          : `You can reply after ${participant.display_name} accepts.`;
      }
    } else if (declined) {
      el.messageRequestTitle.textContent = "Message request declined";
      el.messageRequestDescription.textContent = "This conversation is closed.";
    } else if (unavailable) {
      el.messageRequestTitle.textContent = "Messaging unavailable";
      el.messageRequestDescription.textContent = conversation?.blocked
        ? "Messaging is unavailable for this conversation."
        : "You cannot reply in this conversation right now.";
    }
    if (!canMessage) globalThis.LouderrVoiceNote?.cancel();
    el.messageForm.hidden = !canMessage;
    if (canMessage) sizeMessageInput();
    el.messageBody.disabled = !canMessage;
    if (el.messageFileButton) el.messageFileButton.disabled = !canMessage || Boolean(globalThis.LouderrVoiceNote?.pending());
    el.messageSend.disabled = !canMessage || state.messages.sending.has(state.messages.selectedId)
      || Boolean(globalThis.LouderrVoiceNote?.pending());
    el.messageEmote.disabled = !canMessage;
    if (!canMessage && state.emotes.target === el.messageBody) closeEmotePicker();
    el.messageBody.placeholder = canSendInitialRequest ? "Write your message request" : "Message…";
    el.messagesOlder.hidden = !(state.messages.searchWindow?.older || state.messages.nextCursor);
    el.messagesOlder.textContent = state.messages.searchWindow ? "Older messages" : "Load older messages";
    const rows = new Map(Array.from(el.messageList.children).filter(node => node.dataset.messageId).map(node => [node.dataset.messageId, node]));
    const dates = new Map(Array.from(el.messageList.children).filter(node => node.dataset.messageDate).map(node => [node.dataset.messageDate, node]));
    const nodes = [];
    for (const [index, message] of state.messages.items.entries()) {
      const previous = state.messages.items[index - 1];
      const day = new Date(message.created_at).toDateString();
      const sameDay = previous && new Date(previous.created_at).toDateString() === day;
      const grouped = Boolean(sameDay && !previous.deleted && !message.deleted && previous.sender?.id === message.sender?.id
        && Date.parse(message.created_at) - Date.parse(previous.created_at) < 180000);
      if (!sameDay) {
        const label = messageDateLabel(message.created_at);
        const divider = dates.get(day) || create("div", "message-date", label);
        divider.dataset.messageDate = day;
        if (divider.textContent !== label) divider.textContent = label;
        nodes.push(divider);
      }
      const mine = message.sender?.id === state.viewer?.id;
      // Receipt changes must not replace message text, links or translation state.
      const { receipt, server_time: serverTime, ...contentState } = message;
      const renderKey = JSON.stringify([state.viewer?.id, state.viewer?.status, state.sessionRestricted,
        state.capabilities.community_writes, conversation?.can_message, conversation?.read_only, contentState]);
      let bubble = rows.get(message.id);
      if (!bubble || bubble.dmRenderKey !== renderKey) {
        bubble = create("article", `direct-message${mine ? " is-mine" : ""}${message.deleted ? " is-deleted" : ""}`);
        bubble.dataset.messageId = message.id;
        const content = create("div", "direct-message-bubble");
        const body = create("p");
        if (message.deleted) body.textContent = message.removed_by_moderation ? "Message removed by moderation" : "Message deleted";
        else appendChatText(body, message.body);
        const messageTranslation = message.deleted || message.poll ? null : translationControl(
          message.translation,
          { body: message.body },
          (fields) => { clear(body); appendChatText(body, fields.body); },
        );
        const footer = create("footer", "message-meta");
        const time = create("time", "", chatTime(message.created_at));
        time.setAttribute("datetime", message.created_at);
        footer.append(time);
        // Both participants see the same pin, so the marker names no one. contentState
        // already carries `pinned`, so the bubble rebuilds when it changes.
        if (message.pinned && !message.deleted) {
          const marker = create("span", "message-pinned");
          marker.append(icon("pin"));
          marker.append(create("span", "sr-only", "Pinned"));
          footer.append(marker);
        }
        if (!message.hidden_for_me) {
          const menu = create("button", "message-options"); menu.type = "button";
          menu.setAttribute("aria-label", "Message options"); menu.setAttribute("aria-haspopup", "dialog");
          const more = icon("more"); more.setAttribute("aria-hidden", "true"); menu.append(more);
          menu.addEventListener("click", () => openDirectMessageActions(message)); bubble.append(menu);
        }
        const poll = state.messagePolls?.render(message);
        content.append(poll || body);
        const attachment = directMessageMedia(message);
        if (attachment) content.append(attachment);
        const documentFile = globalThis.LouderrMessageFiles?.render(message, document);
        if (documentFile) content.append(documentFile);
        const sharedDrop = directMessageSharedDrop(message);
        if (sharedDrop) content.append(sharedDrop);
        if (messageTranslation) content.append(messageTranslation);
        globalThis.LouderrMessageTools?.decorate(content, message);
        state.socialExtras?.decorateMessage(content, message);
        bubble.append(content, footer);
        bubble.dmRenderKey = renderKey;
        bubble.dmFooter = footer;
      }
      if (bubble.classList.contains("is-grouped") !== grouped) bubble.classList.toggle("is-grouped", grouped);
      const receiptKey = mine && !message.deleted ? (receipt?.is_group ? JSON.stringify(receipt)
        : window.LobbyMessageReceipts?.receiptState(receipt)) : null;
      if (bubble.dmReceiptKey !== receiptKey) {
        bubble.dmReceipt?.remove();
        bubble.dmReceipt = mine ? directMessageReceipt(message) : null;
        if (bubble.dmReceipt) bubble.dmFooter.append(bubble.dmReceipt);
        bubble.dmReceiptKey = receiptKey;
      }
      nodes.push(bubble);
    }
    const retained = new Set(nodes);
    for (const child of Array.from(el.messageList.children)) if (!retained.has(child)) child.remove();
    nodes.forEach((node, index) => {
      if (el.messageList.children[index] !== node) el.messageList.insertBefore(node, el.messageList.children[index] || null);
    });
    const viewportShift = el.messageList.getBoundingClientRect().top - previousViewportTop;
    el.messageList.scrollTop = previousTop + (prepend ? el.messageList.scrollHeight - previousHeight : 0)
      + (scroll ? 0 : viewportShift);
    if (scroll) {
      const context = messageContext();
      const selectionVersion = context.messages.selectionVersion;
      const scheduledTop = el.messageList.scrollTop;
      requestAnimationFrame(() => {
        if (context.threadCurrent() && state.view === "messages"
          && context.messages.selectionVersion === selectionVersion
          && el.messageList.scrollTop === scheduledTop) scrollMessagesBottom();
      });
    }
    if (focusToRestore && focusToRestore.controlIndex >= 0 && document.activeElement !== focused) {
      restoreDirectMessageFocus(focusToRestore.messageId, focusToRestore.controlIndex);
    }
    observeMessageReceipts();
  }

  function messageContext(conversationId = state.messages.selectedId) {
    const messages = state.messages;
    const viewerId = state.viewer?.id;
    const accountCurrent = () => state.messages === messages && Boolean(viewerId) && state.viewer?.id === viewerId;
    return {
      messages, conversationId, accountCurrent,
      threadCurrent: () => accountCurrent() && messages.selectedId === conversationId,
    };
  }

  function rememberMessageDraft(edited = false) {
    const messages = state.messages;
    if (!messages.selectedId) return null;
    let draft = messages.drafts.get(messages.selectedId);
    const replyToId = globalThis.LouderrMessageTools?.replyId() || null;
    const attachmentKey = state.messageAttachment?.submissionKey || null;
    if (edited || !draft || draft.value !== el.messageBody.value || (draft.replyToId || null) !== replyToId
      || (draft.attachmentKey || null) !== attachmentKey) {
      draft = { value: el.messageBody.value, sendToken: crypto.randomUUID(),
        ...(replyToId ? { replyToId } : {}), ...(attachmentKey ? { attachmentKey } : {}) };
      messages.drafts.set(messages.selectedId, draft);
    }
    return draft;
  }

  function installScheduledMessages() {
    state.messages.scheduled ||= globalThis.LouderrScheduledMessages?.create({
      api, state: () => state, context: messageContext,
      later: $("#message-send-later"), inbox: $("#messages-scheduled"), threadList: $("#message-scheduled"),
      rememberDraft: rememberMessageDraft, consumed: consumeScheduledMessageDraft,
      voicePending: () => Boolean(globalThis.LouderrVoiceNote?.pending()),
      replyId: () => globalThis.LouderrMessageTools?.replyId(), verified: requireVerified,
      showError: message => showError(el.messageFormError, message),
    });
    state.messages.scheduled?.sync();
  }

  function consumeScheduledMessageDraft(context, draft) {
    if (!context.accountCurrent() || context.messages.drafts.get(context.conversationId) !== draft) return;
    // A selection that left and returned is a new composer context, even when its
    // text happens to match. Only the exact submitted draft can be consumed.
    if (!context.current()) return;
    if (el.messageBody.value !== draft.value || state.messageAttachment
        || globalThis.LouderrMessageTools?.replyId()) return;
    context.messages.drafts.delete(context.conversationId);
    el.messageBody.value = "";
    sizeMessageInput();
  }

  function sizeMessageInput() {
    el.messageBody.style.height = "auto";
    el.messageBody.style.height = `${Math.min(el.messageBody.scrollHeight, 116)}px`;
  }

  function closeMessageThread(pushHistory = true) {
    state.messages.threadWindow?.read?.controller?.abort();
    state.messages.threadWindow = null;
    state.messageSearch?.close();
    state.messageShared?.close();
    state.messageAlbums?.close();
    state.messageTyping?.stop();
    globalThis.LouderrVoiceNote?.cancel();
    rememberMessageDraft();
    state.messages.threadRequestId += 1;
    state.messages.selectionVersion += 1;
    state.messages.selectedId = null;
    state.messages.conversation = null;
    state.messages.participant = null;
    state.messages.items = [];
    state.messages.nextCursor = null;
    state.messages.searchWindow = null;
    el.messageBody.value = "";
    el.messageBody.style.height = "";
    if (pushHistory && location.pathname !== "/messages") history.pushState({}, "", "/messages");
    renderConversations();
    renderMessageThread();
  }

  async function markConversationRead(messageId, conversationId = state.messages.selectedId) {
    const original = conversationId === state.messages.selectedId ? state.messages.conversation
      : state.messages.conversations.find(item => item.id === conversationId);
    if (state.sessionRestricted || original?.read_only) return;
    if (!messageId || !conversationId || !state.viewer) return;
    const context = messageContext(conversationId), csrf = state.csrf;
    const pending = context.messages.conversationReadRequests ||= new Map();
    const request = {}; pending.set(conversationId, request);
    const current = () => context.accountCurrent() && state.csrf === csrf && pending.get(conversationId) === request;
    try {
      const result = await api(`/api/messages/conversations/${encodeURIComponent(context.conversationId)}/read`, {
        method: "PUT", csrf: true, json: { message_id: messageId },
      });
      if (!current()) return;
      const messages = context.messages;
      const conversation = messages.conversations.find(item => item.id === context.conversationId)
        || (messages.conversation?.id === context.conversationId ? messages.conversation : null);
      if (conversation) {
        messages.unreadCount = Math.max(0, messages.unreadCount - (conversation.unread_count || 0) + result.unread_count);
        conversation.unread_count = result.unread_count;
      }
      const window = messages.conversationWindow, read = window?.read;
      if (read && window.viewerId === state.viewer.id && window.csrf === csrf
          && window.folder === (messages.folder || "inbox") && messages.conversationRequestId === read.requestId
          && !read.controller.signal.aborted) {
        read.readCounts.set(conversationId, { count: result.unread_count, revision: ++read.readRevision });
        if (conversation) read.keepUnreadCount = true;
      }
      const threadRead = messages.threadWindow?.read;
      if (threadRead?.current() && threadRead.csrf === csrf && messages.selectedId === conversationId) threadRead.unread = result.unread_count;
      renderConversations();
    } catch (_) { /* Read receipts do not block viewing a private conversation. */ }
    finally { if (pending.get(conversationId) === request) pending.delete(conversationId); }
  }

  function messageThreadCursorPosition(cursor) {
    if (typeof cursor !== "string" || typeof atob !== "function") return null;
    try {
      const encoded = cursor.replace(/-/g, "+").replace(/_/g, "/");
      const value = atob(encoded + "=".repeat((4 - encoded.length % 4) % 4));
      // Server positions use fixed-width UTC microseconds. Row timestamps are rounded
      // to milliseconds, so they cannot decide whether a removed boundary was crossed.
      return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z\|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value)
        ? value : null;
    } catch (_) { return null; }
  }

  function messageThreadWindow() {
    const messages = state.messages, viewerId = state.viewer?.id, conversationId = messages.selectedId;
    if (!viewerId || !conversationId) return null;
    let window = messages.threadWindow;
    if (!window || window.viewerId !== viewerId || window.conversationId !== conversationId) {
      window?.read?.controller?.abort();
      window = messages.threadWindow = { viewerId, conversationId, read: null,
        loaded: false, rawCount: 0, pages: [], cursor: null, oldestId: null,
        error: "", retry: null };
    }
    return window;
  }

  function paintMessageThreadRead() {
    if (state.messages.searchWindow) return;
    const window = state.messages.threadWindow;
    const owned = window && window.viewerId === state.viewer?.id
      && window.conversationId === state.messages.selectedId;
    const busy = Boolean(owned && window.read), error = owned ? window.error : "";
    const list = el.messageList;
    const beforeTop = list?.getBoundingClientRect?.().top, beforeScroll = list?.scrollTop;
    if (el.messageHistoryNavigation) el.messageHistoryNavigation.hidden = !state.messages.nextCursor && !busy && !error;
    if (el.messageHistoryStatus) {
      el.messageHistoryStatus.hidden = !busy && !error;
      el.messageHistoryStatus.textContent = error || (busy ? "Loading messages…" : "");
    }
    if (el.messageHistoryRetry) {
      el.messageHistoryRetry.hidden = !error;
      el.messageHistoryRetry.disabled = false;
      el.messageHistoryRetry.setAttribute?.("aria-disabled", String(busy));
    }
    if (el.messageHistoryLatest) {
      el.messageHistoryLatest.hidden = !error;
      el.messageHistoryLatest.disabled = false;
      el.messageHistoryLatest.setAttribute?.("aria-disabled", String(busy));
    }
    if (el.messagesOlder) {
      el.messagesOlder.disabled = false;
      el.messagesOlder.setAttribute?.("aria-disabled", String(busy));
    }
    const afterTop = list?.getBoundingClientRect?.().top;
    if (state.view === "messages" && owned && Number.isFinite(beforeTop)
        && Number.isFinite(afterTop) && Number.isFinite(beforeScroll) && afterTop !== beforeTop) {
      list.scrollTop = beforeScroll + afterTop - beforeTop;
    }
  }

  function retryMessageThread() {
    if (state.messages.searchWindow || state.view !== "messages") return;
    const window = messageThreadWindow();
    if (!window || window.read || !window.retry) return;
    const retry = window.retry;
    return openMessageThread(state.messages.selectedId, false, retry.append, null, { latest: retry.latest, refreshDetail: retry.refreshDetail });
  }

  async function openMessageThread(conversationId, pushHistory = true, append = false, focused = null, options = {}) {
    if (!state.viewer) { closeMessageThread(false); openDialog(el.account); return; }
    if (!conversationId || state.view !== "messages") return;
    const messages = state.messages;
    if (options.signal?.aborted) return;
    if (append && (messages.selectedId !== conversationId || (!messages.nextCursor && !messages.searchWindow?.older))) return;
    if (messages.selectedId !== conversationId) {
      messages.threadWindow?.read?.controller?.abort(); messages.threadWindow = null;
      globalThis.LouderrVoiceNote?.cancel();
      if (state.messageAttachment) clearMessageAttachment();
      rememberMessageDraft();
      messages.selectionVersion += 1;
      messages.selectedId = conversationId;
      messages.conversation = null;
      messages.participant = null;
      messages.items = [];
      messages.nextCursor = null;
      messages.searchWindow = null;
      el.messageBody.value = messages.drafts.get(conversationId)?.value || "";
      hideError(el.messageFormError);
      hideError(el.messageRequestError);
      closeEmotePicker();
      renderConversations();
      renderMessageThread();
    }
    // Navigation belongs to the selection, not a particular read.
    if (pushHistory && location.pathname !== `/messages/${conversationId}`) {
      history.pushState({}, "", `/messages/${conversationId}`);
    }
    if (focused) {
      messages.threadWindow?.read?.controller?.abort(); messages.threadWindow = null;
      messages.threadRequestId++; messages.selectionVersion++;
      messages.conversation = focused.conversation; messages.participant = focused.conversation.participant;
      messages.items = globalThis.LouderrMessageHide?.filter(focused.items, messages) || focused.items;
      messages.nextCursor = null;
      messages.searchWindow = { anchor: focused.anchor_id, older: focused.older_anchor, newer: focused.newer_anchor };
      renderConversations(); renderMessageThread(); state.messageSearch?.focus(focused.anchor_id);
      return true;
    }
    if (messages.searchWindow && !options.latest) {
      if (!pushHistory) return state.messageSearch?.refresh(append ? "before" : "around");
      messages.searchWindow = null; messages.items = []; messages.nextCursor = null;
      messages.threadWindow?.read?.controller?.abort(); messages.threadWindow = null;
    }
    const window = messageThreadWindow(), expectedWindow = messages.searchWindow;
    const latest = Boolean(options.latest);
    if (window.read?.current()) {
      if (append || (!options.refreshDetail && window.read.append === append && window.read.latest === latest)) return window.read.promise;
    }
    window.read?.controller?.abort();
    const context = messageContext(conversationId), csrf = state.csrf;
    const selectionVersion = messages.selectionVersion, requestId = ++messages.threadRequestId;
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    const path = location.pathname, cursor = append ? messages.nextCursor : null;
    const loadedCursor = window.loaded ? window.cursor : messages.nextCursor;
    const oldestId = window.loaded ? window.oldestId : messages.items[0]?.id;
    const rawCount = window.loaded ? window.rawCount : messages.items.length;
    const initialMessageIds = new Set(messages.items.map(item => item.id));
    const deletionVersion = messages.deletionVersion, detailRevision = messages.conversationRequestId;
    const boundary = messageThreadCursorPosition(loadedCursor);
    const read = { controller, requestId, csrf, append, latest, promise: null, current: null, unread: null, accepted: false };
    const owns = () => state.messages === messages && messages.threadWindow === window
      && window.read === read;
    const current = () => owns() && messages.threadRequestId === requestId && context.threadCurrent() && state.csrf === csrf
      && state.view === "messages" && messages.selectionVersion === selectionVersion
      && messages.searchWindow === expectedWindow && location.pathname === path
      && !controller?.signal.aborted && !options.signal?.aborted;
    read.current = current;
    window.read = read; window.error = ""; window.retry = null;
    const cancel = () => controller?.abort();
    options.signal?.addEventListener("abort", cancel, { once: true });
    paintMessageThreadRead();
    read.promise = (async () => {
      try {
        let detail = messages.conversations.find(item => item.id === conversationId);
        const requestOptions = { signal: controller?.signal || options.signal };
        if (!detail || options.refreshDetail) detail = await api(`/api/messages/conversations/${encodeURIComponent(conversationId)}`, requestOptions);
        if (!current()) return;
        if (detail?.id !== conversationId) throw new Error("This conversation could not be confirmed. Try again.");
        const pages = [], visited = new Set(append ? window.pages.map(page => page.cursor).filter(value => value && value !== cursor) : []);
        let next = cursor, receivedItems = [], result = null;
        const replay = !append && !latest && rawCount > 0;
        // Revalidate the accepted range, allowing four extra head pages. A larger
        // discontinuity keeps the accepted window and offers Retry/Latest, never a truncation.
        const pageLimit = replay ? Math.max(1, Math.ceil(rawCount / 50)) + 4 : 1;
        for (;;) {
          if (!current()) return;
          if (next && visited.has(next)) throw new Error("Message history repeated a page. Try again.");
          if (next) visited.add(next);
          const params = new URLSearchParams({ limit: "50" });
          if (next) params.set("cursor", next);
          result = await api(`/api/messages/conversations/${encodeURIComponent(conversationId)}/messages?${params}`, requestOptions);
          if (!current()) return;
          if (!result || !Array.isArray(result.items) || (result.next_cursor != null
              && (typeof result.next_cursor !== "string" || !result.next_cursor || result.next_cursor.length > 300))) {
            throw new Error("Message history could not be confirmed. Try again.");
          }
          const after = result.next_cursor || null;
          if (after && (after === next || visited.has(after))) throw new Error("Message history repeated a page. Try again.");
          const fromPosition = messageThreadCursorPosition(next), afterPosition = messageThreadCursorPosition(after);
          if (fromPosition && afterPosition && afterPosition >= fromPosition) throw new Error("Message history did not advance. Try again.");
          pages.push({ cursor: next, nextCursor: after });
          const known = new Set(receivedItems.map(item => item.id));
          receivedItems = [...result.items.filter(item => !known.has(item.id)), ...receivedItems];
          if (!replay || !after) break;
          if (loadedCursor != null) {
            const oldestIndex = receivedItems.findIndex(item => item.id === oldestId);
            if (oldestIndex >= 0) {
              receivedItems = receivedItems.slice(oldestIndex);
              result = { ...result, next_cursor: loadedCursor };
              break;
            }
            const position = messageThreadCursorPosition(after);
            if (boundary && position && position <= boundary) break;
          }
          if (pages.length >= pageLimit) {
            throw new Error("Message history changed too much to refresh here. Retry or choose Latest messages.");
          }
          next = after;
        }
        if (!current()) return;
        if (options.refreshDetail && messages.conversationRequestId !== detailRevision) {
          throw new Error("This conversation changed while refreshing. Try again.");
        }
        const shownItems = globalThis.LouderrMessageHide?.filter(receivedItems, messages) || receivedItems;
        const items = shownItems.map(item => {
          if ((messages.deletedMessages.get(item.id) || 0) > deletionVersion) item = { ...item, body: "", deleted: true };
          if (messages.deletedMessages.has(item.reply_to?.id)) item = { ...item, reply_to: { ...item.reply_to, body: "", deleted: true } };
          return item;
        });
        const followNewest = latest || (!append && (initialMessageIds.size === 0
          || el.messageList.scrollHeight - el.messageList.scrollTop - el.messageList.clientHeight < 80));
        const list = el.messageList, viewport = list.getBoundingClientRect?.().top;
        const anchors = !append && !followNewest && Number.isFinite(viewport)
          ? Array.from(list.children || []).filter(node => node.dataset.messageId)
            .map(node => ({ id: node.dataset.messageId, rect: node.getBoundingClientRect?.() }))
            .filter(item => item.rect && item.rect.bottom > viewport)
            .map(item => ({ id: item.id, offset: item.rect.top - viewport })) : [];
        if (!options.refreshDetail) detail = messages.conversations.find(item => item.id === conversationId) || detail;
        if (read.unread != null) detail = { ...detail, unread_count: read.unread };
        if (detail.last_message) {
          const shown = globalThis.LouderrMessageHide?.filter([detail.last_message], messages);
          if (shown) detail = { ...detail, last_message: shown[0] || null };
        }
        messages.conversation = detail;
        messages.participant = detail.participant;
        if (append) {
          const known = new Set(messages.items.map(item => item.id));
          messages.items = [...items.filter(item => !known.has(item.id)), ...messages.items];
        } else {
          const received = new Set(items.map(item => item.id));
          const arrived = messages.items.filter(item => !initialMessageIds.has(item.id) && !received.has(item.id));
          messages.items = [...items, ...arrived];
        }
        messages.items = globalThis.LouderrMessageHide?.filter(messages.items, messages) || messages.items;
        if (latest) messages.searchWindow = null;
        messages.nextCursor = result.next_cursor || null;
        window.loaded = true;
        window.rawCount = append ? rawCount + receivedItems.length : receivedItems.length;
        window.oldestId = receivedItems[0]?.id || (append ? oldestId : null);
        window.cursor = messages.nextCursor;
        window.pages = append ? [...window.pages, ...pages] : pages;
        window.error = ""; window.retry = null;
        read.accepted = true;
        renderConversations();
        renderMessageThread(followNewest, append);
        if (!followNewest && !append && owns() && context.threadCurrent()
            && messages.threadRequestId === requestId && state.view === "messages" && location.pathname === path) {
          for (const anchor of anchors) {
            const node = Array.from(list.children || []).find(item => item.dataset.messageId === anchor.id);
            const rect = node?.getBoundingClientRect?.(), top = list.getBoundingClientRect?.().top;
            if (rect && Number.isFinite(top)) { list.scrollTop += rect.top - top - anchor.offset; break; }
          }
        }
        observeMessageReceipts();
        return true;
      } catch (error) {
        if (!current()) return;
        if (error.status === 403 || error.status === 404) {
          closeMessageThread(false); history.replaceState({}, "", "/messages");
        } else {
          window.error = error.message || "Message history could not be loaded. Try again.";
          window.retry = { append: error.status === 400 ? false : append, latest, refreshDetail: Boolean(options.refreshDetail) };
        }
        toast(error.message, true);
        return false;
      } finally {
        options.signal?.removeEventListener("abort", cancel);
        if (owns()) {
          const wasCurrent = current();
          window.read = null;
          if (!wasCurrent && !read.accepted && !window.error && !latest && context.threadCurrent()
              && state.view === "messages" && location.pathname === path && !messages.searchWindow) {
            // The exact slot settled after authority/cancellation changed. Keep it retryable;
            // do not retire or paint a different thread, window or owner's work.
            if (state.csrf !== csrf || controller?.signal.aborted || options.signal?.aborted
                || messages.threadRequestId !== requestId) {
              window.error = "Message history loading was interrupted. Try again.";
              window.retry = { append: false, latest: false, refreshDetail: Boolean(options.refreshDetail) };
            }
          }
          if (context.threadCurrent() && state.view === "messages" && location.pathname === path) paintMessageThreadRead();
        }
      }
    })();
    return read.promise;
  }

  async function submitDirectMessage(event) {
    event.preventDefault();
    state.messageTyping?.stop();
    if (globalThis.LouderrVoiceNote?.pending()) {
      showError(el.messageFormError, "Attach or cancel your recording before sending.");
      return;
    }
    if (state.sessionRestricted || state.messages.conversation?.read_only) return;
    if (!state.capabilities.community_writes) return;
    if (!requireVerified() || !state.messages.selectedId) return;
    const context = messageContext();
    const sendingSelectionVersion = context.messages.selectionVersion;
    if (context.messages.sending.has(context.conversationId)) return;
    const conversation = state.messages.conversation;
    const canSendInitialRequest = conversation?.request_state === "pending"
      && !conversation?.incoming_request
      && conversation?.requester_id === state.viewer?.id
      && !conversation?.last_message;
    if (conversation?.can_message === false && !canSendInitialRequest) {
      showError(el.messageFormError, "This conversation is not open for replies.");
      renderMessageThread();
      return;
    }
    const body = el.messageBody.value.trim();
    // An attachment the pipeline has not approved would be refused by the server, so it
    // is refused here with a reason instead of as a failed request.
    if (state.messageAttachment?.current?.() === false) {
      clearMessageAttachment();
      showError(el.messageFormError, "Your session or conversation changed. Choose the attachment again before sending.");
      return;
    }
    const attachmentEntry = state.messageAttachment;
    const attachment = messageAttachmentState(attachmentEntry);
    if (!body && !attachment.holding) return;
    if (attachment.holding && !attachment.ready) {
      showError(el.messageFormError, attachment.blocked ? attachment.text
        : "Wait for the attachment to finish its safety check before sending.");
      return;
    }
    const draft = rememberMessageDraft();
    context.messages.sending.add(context.conversationId);
    hideError(el.messageFormError);
    el.messageSend.disabled = true;
    try {
      const message = await api(`/api/messages/conversations/${encodeURIComponent(context.conversationId)}/messages`, {
        method: "POST", csrf: true, json: { body, send_token: draft.sendToken,
            ...(attachment.assetId ? { media_asset_id: attachment.assetId } : {}),
            ...(attachment.fileId ? { file_asset_id: attachment.fileId } : {}),
          ...(draft.replyToId ? { reply_to_id: draft.replyToId } : {}) },
      });
      if (!context.accountCurrent()) return;
      const pendingReview = ["pending_review", "review_complete"].includes(message?.status) && message.accepted === true
        && typeof message.quarantine_id === "string" && Boolean(message.quarantine_id);
      const hiddenRetry = message?.hidden_for_me === true && typeof message.id === "string" && Boolean(message.id)
        && message.conversation_id === context.conversationId;
      if (!pendingReview && !hiddenRetry && (!message || typeof message.id !== "string" || !message.id
        || typeof message.body !== "string" || typeof message.created_at !== "string"
        || !Number.isFinite(Date.parse(message.created_at)))) {
        throw new Error("Your message could not be confirmed. Check the conversation before trying again.");
      }
      const submittedDraftUnchanged = context.messages.drafts.get(context.conversationId) === draft;
      if (submittedDraftUnchanged) {
        context.messages.drafts.delete(context.conversationId);
        if (state.messageAttachment === attachmentEntry) clearMessageAttachment();
        globalThis.LouderrMessageTools?.sent(context, draft);
      }
      if (pendingReview) {
        if (context.threadCurrent()) {
          if (submittedDraftUnchanged && el.messageBody.value === draft.value) {
            el.messageBody.value = "";
            el.messageBody.style.height = "";
          }
          closeEmotePicker();
          showError(el.messageFormError, message.status === "review_complete"
            ? "Review finished. This message attempt was not delivered."
            : "Message sent for review. It has not been delivered.");
        }
        return;
      }
      const shown = globalThis.LouderrMessageHide?.filter([message], context.messages);
      if (hiddenRetry || shown?.length === 0) {
        if (context.threadCurrent() && submittedDraftUnchanged && el.messageBody.value === draft.value) {
          el.messageBody.value = "";
          el.messageBody.style.height = "";
        }
        await applyPersonalMessageHide(context, message.id);
        return;
      }
      context.messages.conversationRequestId += 1;
      const cached = context.messages.conversations.find((item) => item.id === context.conversationId);
      if (cached) cached.last_message = message;
      let returnToLatest = false;
      if (context.threadCurrent()) {
        const preserveWindow = state.messages.searchWindow && state.messages.selectionVersion !== sendingSelectionVersion;
        returnToLatest = Boolean(state.messages.searchWindow && !preserveWindow);
        if (returnToLatest) {
          state.messages.searchWindow = null; state.messages.items = []; state.messages.nextCursor = null;
          state.messages.threadRequestId++;
        }
        if (!preserveWindow && !state.messages.items.some((item) => item.id === message.id)) state.messages.items.push(message);
        if (state.messages.conversation) state.messages.conversation = { ...state.messages.conversation, last_message: message };
        if (submittedDraftUnchanged && el.messageBody.value === draft.value) {
          el.messageBody.value = "";
          el.messageBody.style.height = "";
        }
        closeEmotePicker();
        renderMessageThread(!preserveWindow);
      }
      await refreshConversations(true);
      if (returnToLatest && context.threadCurrent() && state.messages.selectionVersion === sendingSelectionVersion
        && !state.messages.searchWindow) await openMessageThread(context.conversationId, false);
    } catch (error) { if (context.threadCurrent()) showError(el.messageFormError, messageDeliveryError(error)); }
    finally {
      context.messages.sending.delete(context.conversationId);
      if (context.threadCurrent()) renderMessageThread();
    }
  }

  async function decideMessageRequest(action) {
    const conversation = state.messages.conversation;
    if (!state.capabilities.community_writes && !(conversation?.kind === "group" && action === "decline")) return;
    if (!conversation || !state.messages.selectedId || !conversation.incoming_request || conversation.request_state !== "pending") return;
    const context = messageContext();
    if (context.messages.deciding.has(context.conversationId)) return;
    context.messages.deciding.add(context.conversationId);
    hideError(el.messageRequestError);
    el.messageRequest.setAttribute("aria-busy", "true");
    el.messageRequestAccept.disabled = true;
    el.messageRequestDecline.disabled = true;
    try {
      const isGroup = conversation.kind === "group";
      const path = isGroup ? `/api/messages/groups/${encodeURIComponent(context.conversationId)}/membership`
        : `/api/messages/conversations/${encodeURIComponent(context.conversationId)}/request`;
      const result = await api(path, {
        method: isGroup && action === "decline" ? "DELETE" : "PUT", csrf: true,
        ...(!(isGroup && action === "decline") ? { json: { action } } : {}),
      });
      if (!context.accountCurrent()) return;
      if (isGroup && result.closed) {
        context.messages.conversations = context.messages.conversations.filter(item => item.id !== context.conversationId);
        if (context.threadCurrent()) closeMessageThread();
        refreshConversations(true); return;
      }
      const updated = result.conversation;
      if (!updated) throw new Error("The message request could not be updated.");
      context.messages.conversationRequestId += 1;
      context.messages.conversationsLoading = false;
      el.messagesLoading.hidden = true;
      const index = state.messages.conversations.findIndex((item) => item.id === updated.id);
      if (index >= 0) state.messages.conversations[index] = updated;
      else state.messages.conversations.unshift(updated);
      renderConversations();
      if (context.threadCurrent()) {
        state.messages.conversation = updated;
        state.messages.participant = updated.participant;
        renderMessageThread();
        if (isGroup && action === "accept") void openMessageThread(updated.id, false);
        if (action === "accept" && updated.can_message) requestAnimationFrame(() => {
          if (context.threadCurrent() && state.view === "messages") el.messageBody.focus();
        });
      }
      void refreshConversations(true);
    } catch (error) {
      if (context.threadCurrent()) showError(el.messageRequestError, error.message);
    } finally {
      context.messages.deciding.delete(context.conversationId);
      if (context.threadCurrent()) {
        el.messageRequest.removeAttribute("aria-busy");
        const pending = state.messages.conversation?.request_state === "pending" && state.messages.conversation?.incoming_request;
        el.messageRequestAccept.disabled = !pending;
        el.messageRequestDecline.disabled = !pending;
      }
    }
  }

  async function applyPersonalMessageHide(context, messageId, refresh = true) {
    if (!context.accountCurrent()) return;
    const messages = context.messages;
    globalThis.LouderrMessageHide?.filter([{ id: messageId, hidden_for_me: true }], messages);
    messages.items = globalThis.LouderrMessageHide?.filter(messages.items, messages)
      || messages.items.filter((item) => item.id !== messageId);
    globalThis.LouderrMessageTools?.invalidate(messageId, messages, context.conversationId);
    state.messageShared?.invalidate?.(messageId, context.conversationId);
    state.messageAlbums?.invalidate?.(messageId, context.conversationId);
    if (refresh) {
      state.messageSearch?.revalidate();
      state.messageShared?.revalidate();
      state.messageAlbums?.revalidate();
    }
    const draft = messages.drafts.get(context.conversationId);
    if (draft?.replyToId === messageId) messages.drafts.set(context.conversationId, { ...draft, replyToId: null });
    for (const conversation of messages.conversations) {
      if (conversation.last_message?.id === messageId) conversation.last_message = null;
    }
    if (messages.conversation?.last_message?.id === messageId) messages.conversation.last_message = null;
    if (messages.reportMessage?.id === messageId) {
      messages.reportMessage = null;
      if (context.threadCurrent() && el.report.open && state.report?.id === messageId) el.report.close();
    }
    messages.conversationRequestId += 1;
    if (context.threadCurrent()) {
      messages.threadRequestId += 1;
      if (el.messageActions?.open) el.messageActions.close();
      renderMessageThread();
      renderConversations();
    }
    if (!refresh) return;
    await refreshConversations(true);
    if (context.threadCurrent()) await openMessageThread(context.conversationId, false);
  }

  function expireDisplayedMessage(context, messageId) {
    const csrf = state.csrf;
    const visible = {...context, accountCurrent: () => context.accountCurrent() && state.csrf === csrf,
      threadCurrent: () => context.threadCurrent() && state.csrf === csrf && state.view === "messages"};
    void applyPersonalMessageHide(visible, messageId, false).then(() => {
      if (!visible.accountCurrent() || state.view !== "messages") return;
      const window = visible.threadCurrent() && context.messages.searchWindow;
      if (window) {
        const items = context.messages.items;
        if (window.anchor === messageId) window.anchor = items[Math.floor(items.length / 2)]?.id || null;
        if (window.older === messageId) window.older = items[0]?.id || null;
        if (window.newer === messageId) window.newer = items.at(-1)?.id || null;
        if (window.retryRead?.mid === messageId) window.retryRead = null;
        if (!items.length) {
          window.error = "These messages expired. Load latest messages."; window.unavailable = true;
        }
        renderMessageThread();
      }
      if (!visible.threadCurrent()) renderConversations();
    }).catch(() => {
      if (visible.accountCurrent()) toast("An expired message view could not be refreshed. Reload the page.", true);
    });
  }

  async function deleteDirectMessage(messageId) {
    if (!(await confirm("Delete this private message for all participants?"))) return;
    const context = messageContext();
    try {
      await api(`/api/messages/conversations/${encodeURIComponent(context.conversationId)}/messages/${encodeURIComponent(messageId)}`, {
        method: "DELETE", csrf: true,
      });
      if (!context.accountCurrent()) return;
      context.messages.deletedMessages.set(messageId, ++context.messages.deletionVersion);
      state.messageSearch?.revalidate();
      state.messageShared?.revalidate();
      state.messageAlbums?.revalidate();
      globalThis.LouderrVoicePlayback?.invalidate(messageId);
      if (context.threadCurrent()) {
        state.messages.items = state.messages.items.map((item) => {
          if (item.id === messageId) return { ...item, body: "", deleted: true };
          if (item.reply_to?.id === messageId) return { ...item, reply_to: { ...item.reply_to, body: "", deleted: true } };
          return item;
        });
        renderMessageThread();
      }
      refreshConversations(true);
    } catch (error) { if (context.threadCurrent()) toast(error.message, true); }
  }

  function syncProfileWriteControls() {
    if (!state.profile.data) return;
    const relationship = state.profile.data.relationship || {};
    const isSelf = Boolean(relationship.is_self);
    el.profileFollow.hidden = isSelf || !state.capabilities.community_writes || (Boolean(state.viewer) && relationship.can_follow === false);
    el.profileMessage.hidden = isSelf || relationship.blocked || !state.capabilities.community_writes;
    el.profileEdit.hidden = !isSelf || !state.capabilities.community_writes;
  }

  function renderProfileView({ refreshExtras = true, refreshReposts = true } = {}) {
    const profile = state.profile.data;
    if (!profile) return;
    const user = profile.user;
    const counts = profile.counts || {};
    const relationship = profile.relationship || {};
    styleAvatar(el.profileViewAvatar, user);
    renderAccountName(el.profileViewName, user);
    el.profileViewHandle.textContent = `@${user.handle}${relationship.follows_you ? " · follows you" : ""}`;
    el.profileViewBio.textContent = user.bio || "";
    el.profileViewBio.hidden = !user.bio?.trim();
    el.profileViewLocation.textContent = user.location || "";
    el.profileViewLocation.hidden = !user.location;
    el.profileViewWebsite.textContent = user.website ? new URL(user.website).hostname : "";
    el.profileViewWebsite.href = user.website || "";
    el.profileViewWebsite.hidden = !user.website;
    el.profileViewJoined.textContent = profile.joined_at ? `Joined ${new Date(profile.joined_at).toLocaleDateString(undefined, { month: "long", year: "numeric" })}` : "";
    // Neither Instagram nor TikTok puts a join date on the profile; it stays out of the header.
    el.profileViewJoined.hidden = true;
    if (el.profileFollowers && typeof el.profileFollowers.closest === "function") {
      const statsBlock = el.profileFollowers.closest(".profile-stats");
      const hero = document.querySelector(".profile-hero");
      const identity = hero ? hero.querySelector(".profile-identity") : null;
      if (statsBlock && hero && identity && statsBlock.parentElement !== hero) hero.insertBefore(statsBlock, identity);
    }
    $("strong", el.profileFollowers).textContent = formatCount(counts.followers || 0);
    $("strong", el.profileFollowing).textContent = formatCount(counts.following || 0);
    el.profileDropCount.textContent = formatCount(counts.profile_posts ?? counts.drops ?? 0);
    el.profileApplauseCount.textContent = formatCount(counts.applause_received || 0);
    const isSelf = Boolean(relationship.is_self);
    const menuContext = el.profileSafetyMenu._louderrContext;
    const viewerId = state.viewer?.id || null;
    // A repaint must not close the menu between a touch on More and its next action.
    // Changing profile, account, session or navigation still closes stale controls.
    if (isSelf || !menuContext || menuContext.profileId !== user.id
        || menuContext.viewerId !== viewerId || menuContext.session !== state.messages
        || menuContext.routeRequestId !== state.routeRequestId) el.profileSafetyMenu.open = false;
    el.profileSafetyMenu._louderrContext = {
      profileId: user.id, viewerId, session: state.messages, routeRequestId: state.routeRequestId,
    };
    el.profileSafetyMenu.hidden = isSelf;
    // A signed-out visitor keeps Follow as a sign-in entry point; a signed-in viewer who is blocked either way does not.
    syncProfileWriteControls();
    el.profileFollow.textContent = relationship.is_following ? "Following" : "Follow";
    el.profileFollow.className = relationship.is_following ? "secondary-button" : "publish-button";
    el.profileMute.hidden = isSelf || relationship.blocked;
    el.profileMute.textContent = relationship.muted ? "Unmute" : "Mute";
    if (el.profileRestrict) {
      el.profileRestrict.hidden = isSelf || relationship.blocked;
      el.profileRestrict.textContent = relationship.restricted ? "Remove restriction" : "Restrict";
    }
    el.profileBlock.hidden = isSelf;
    el.profileBlock.textContent = (relationship.blocking ?? relationship.blocked) ? "Unblock" : "Block";
    el.profileReport.hidden = isSelf;
    const selfTabs = $(".profile-self-tabs");
    if (selfTabs) selfTabs.hidden = !isSelf;
    const settingsEntry = $("#profile-settings");
    if (settingsEntry) settingsEntry.hidden = !isSelf;
    renderProfileDrops();
    // Optional calls: when a module is absent the arguments are never evaluated.
    if (refreshExtras) state.storiesV2?.decorateProfile({
      avatar: el.profileViewAvatar, highlights: $("#profile-highlights"), user, isSelf,
      current: () => state.profile.data?.user?.id === user.id,
    });
    // Only with the live module present, so sandboxed client tests never reach the helper.
    if (refreshExtras && state.live) syncProfileLive(user, isSelf);
    const repostContext = { session: state.messages, csrf: state.csrf, route: state.routeRequestId };
    state.socialExtras?.renderProfileTabs($("#profile-post-tabs"), (state.profile.repostsIdentityPending || (state.profile.postsPending && !state.profile.drops.length)) ? null : user, {
      posts: [el.profileDropGrid, el.profileDrops, el.profileDropsEmpty, el.profileDropsMore, el.profileDropsLoading, el.profileDropsError],
      repostList: $("#profile-repost-list"), repostMore: $("#profile-reposts-more"), revalidate: refreshReposts,
      active: () => state.view === "profile" && state.profile.data?.user?.id === user.id
        && (state.viewer?.id || null) === viewerId && state.messages === repostContext.session
        && state.routeRequestId === repostContext.route,
      refresh: () => renderProfileView({ refreshExtras: false }),
      current: () => state.view === "profile" && state.profile.data?.user?.id === user.id
        && (state.viewer?.id || null) === viewerId && state.messages === repostContext.session
        && state.csrf === repostContext.csrf && state.routeRequestId === repostContext.route,
    });
    if (el.profileDropsLoading) el.profileDropsLoading.hidden = !state.profile.postsPending || state.profile.drops.length > 0;
    if (el.profileDropsError) el.profileDropsError.hidden = !state.profile.postsError;
    el.profileDropsEmpty.hidden = state.profile.drops.length > 0 || Boolean(state.profile.postsPending || state.profile.postsError);
    $("h3", el.profileDropsEmpty).textContent = relationship.blocking === true
      ? "You've blocked this account" : "No public Drops yet";
    $("p", el.profileDropsEmpty).textContent = relationship.blocking === true
      ? "Their Drops are hidden. You can unblock them from the profile menu."
      : "This profile has not published anything active.";
    el.profileDropsMore.hidden = !state.profile.nextCursor;
    el.profileDropsMore.disabled = Boolean(state.profile.loading);
    if (state.followState) paintProfileFollowState();
    renderProfileRank(profile.trending);
    renderProfileStatus();
    renderProfileCommerce();
    el.profileContent.hidden = false;
    observeCards();
  }

  function profileLayout() {
    try { return localStorage.getItem("louderr.profile.layout") === "list" ? "list" : "grid"; } catch (_) { return "grid"; }
  }

  function setProfileLayout(layout) {
    try { localStorage.setItem("louderr.profile.layout", layout === "list" ? "list" : "grid"); } catch (_) { /* optional */ }
    renderProfileDrops();
    observeCards();
  }

  // A profile reads as a portfolio first: photos as tiles, text Drops as posters, three across.
  // The list keeps the full cards for reading and acting on each Drop.
  function loadedProfilePreviews() {
    const previews = new Map();
    // Reuse only completed, adequate same-origin thumbnails already held by this
    // document. Do not fetch or retain a cross-account cache of profile media.
    // naturalWidth is density-corrected CSS pixels for srcset images. The completed
    // thumbnail URL identifies its actual pixel variant, even on a narrow screen.
    for (const image of Array.from(document.images || [])) {
      if (!image.complete || image.naturalWidth <= 0 || !image.currentSrc) continue;
      if (image.closest?.("#profile-drop-grid")) continue;
      const url = new URL(image.currentSrc);
      if (url.origin !== location.origin || previewImageUrl(url.pathname, 480) === url.pathname
          || url.searchParams.size !== 1 || ![480, 960].includes(Number(url.searchParams.get("width")))) continue;
      const previous = previews.get(url.pathname);
      if (!previous || Number(url.searchParams.get("width")) < Number(new URL(previous.currentSrc).searchParams.get("width"))) previews.set(url.pathname, image);
    }
    return previews;
  }

  function renderProfileDrops() {
    const layout = el.profileDropGrid ? profileLayout() : "list";
    const toggles = document.querySelectorAll?.("[data-profile-layout]");
    for (const button of toggles && typeof toggles[Symbol.iterator] === "function" ? toggles : []) {
      button.setAttribute("aria-pressed", String(button.dataset.profileLayout === layout));
    }
    const profileContext = JSON.stringify([state.profile.data?.user?.id || state.profile.handle, state.viewer?.id || null, layout]);
    if (el.profileDropGrid) {
      if (el.profileDropGrid.profileContext !== profileContext || layout !== "grid") clear(el.profileDropGrid);
      el.profileDropGrid.profileContext = profileContext;
      el.profileDropGrid.hidden = layout !== "grid";
    }
    if (el.profileDrops.profileContext !== profileContext || layout === "grid") clear(el.profileDrops);
    el.profileDrops.profileContext = profileContext;
    el.profileDrops.hidden = layout === "grid";
    if (layout === "grid") {
      // Realtime reconciliation often returns the same posts. Keep their images connected;
      // changed posts and revoked membership still replace or remove the affected tiles.
      const grid = el.profileDropGrid;
      const previews = loadedProfilePreviews();
      const existing = new Map(Array.from(grid.children, tile => [tile.profileDropId, tile]));
      const visible = new Set(state.profile.drops.map(drop => drop.id));
      for (const [id, tile] of existing) if (!visible.has(id)) {
        tile.releaseProfilePreview?.();
        tile.remove();
      }
      for (const [index, drop] of state.profile.drops.entries()) {
        const key = JSON.stringify(drop);
        let tile = existing.get(drop.id);
        if (!tile || tile.profileContentKey !== key) {
          tile?.releaseProfilePreview?.();
          tile?.remove();
          tile = profileTile(drop, previews);
          tile.profileDropId = drop.id;
          tile.profileContentKey = key;
        }
        const position = grid.children[index];
        if (position !== tile) grid.insertBefore(tile, position || null);
      }
      return;
    }
    // Membership and metadata are authoritative, but a retained video's ancestor stays connected.
    const existing = new Map($$(".drop-card", el.profileDrops).map(card => [card.dataset.dropId, card]));
    const visible = new Set(state.profile.drops.map(drop => drop.id));
    for (const [id, card] of existing) if (!visible.has(id)) card.remove();
    for (const [index, drop] of state.profile.drops.entries()) {
      const previous = existing.get(drop.id);
      const card = updateFeedCard(previous, drop, { surface: "profile" });
      if (previous && card !== previous) previous.replaceWith(card);
      const position = $$(".drop-card", el.profileDrops)[index];
      if (position !== card) el.profileDrops.insertBefore(card, position || null);
    }
  }

  function profileTile(drop, previews = null) {
    const items = Array.isArray(drop.media)
      ? drop.media.filter((item) => item?.media_url)
      : drop.media_url ? [{ media_url: drop.media_url, thumbnail_url: drop.thumbnail_url, media_type: drop.media_type || "", media_alt_text: drop.media_alt_text }] : [];
    const label = dropLabel(drop);
    const tile = create("a", "profile-tile");
    tile.href = `/d/${encodeURIComponent(drop.id)}`;
    tile.dataset.kind = drop.kind || "";
    const first = items[0] || null;
    const type = String(first?.media_type || "");
    if (first && (type.startsWith("image/") || first.thumbnail_url)) {
      const loaded = previews?.get(first.thumbnail_url);
      // A new image or clone can refetch private thumbnails in WebKit. Borrow the
      // decoded element while its source page is hidden, then return it on Back.
      const image = loaded?.parentNode ? loaded : create("img");
      if (image === loaded) {
        previews.delete(first.thumbnail_url);
        const home = document.createComment("profile preview return");
        const attributes = ["id", "class", "style", "alt"].map(name => [name, image.getAttribute(name)]);
        image.replaceWith(home);
        for (const name of ["id", "class", "style"]) image.removeAttribute(name);
        tile.releaseProfilePreview = () => {
          delete tile.releaseProfilePreview;
          for (const [name, value] of attributes) {
            if (value === null) image.removeAttribute(name);
            else image.setAttribute(name, value);
          }
          // A removed feed or account context must never be resurrected.
          if (home.isConnected) home.replaceWith(image);
          else image.remove();
        };
      } else {
        image.src = previewImageUrl(first.thumbnail_url, 480) || first.media_url;
        image.loading = "lazy";
      }
      image.alt = first.media_alt_text || label;
      tile.append(image);
    } else {
      tile.classList.add("is-poster");
      const poster = create("span", "profile-tile-poster");
      poster.append(create("small", "", kindLabels[drop.kind] || drop.kind || ""), create("strong", "", label));
      tile.append(poster);
    }
    if (items.length > 1) tile.append(profileTileMark("layers", "Several photos"));
    else if (type.startsWith("video/")) tile.append(profileTileMark("video", "Video"));
    else if (type.startsWith("audio/")) tile.append(profileTileMark("music", "Audio"));
    const counts = drop.reactions || {};
    const stats = create("span", "profile-tile-stats");
    stats.setAttribute("aria-hidden", "true");
    for (const [name, value] of [["heart", counts.applaud], ["chat", drop.comment_count]]) {
      const stat = create("span", "");
      stat.append(icon(name), create("b", "", formatCount(Number(value || 0))));
      stats.append(stat);
    }
    tile.append(stats);
    const comments = Number(drop.comment_count || 0);
    tile.setAttribute("aria-label", `${label} · ${formatCount(Number(counts.applaud || 0))} applause · ${formatCount(comments)} ${comments === 1 ? "comment" : "comments"}`);
    tile.addEventListener("click", (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      // A tile folded by the personal filter is not a link until "Show anyway".
      if (!tile.getAttribute("href")) return;
      event.preventDefault();
      history.pushState({}, "", tile.getAttribute("href"));
      routeLocation();
    });
    return tile;
  }

  function profileTileMark(name, label) {
    const mark = create("span", "profile-tile-mark");
    mark.setAttribute("aria-hidden", "true");
    mark.title = label;
    mark.append(icon(name));
    return mark;
  }

  async function refreshProfile(handle = state.profile.handle, append = false, profileResult = null, userHint = null) {
    if (!handle) return;
    const sameHandle = value => typeof value === "string" && value.toLowerCase() === handle.toLowerCase();
    if (!sameHandle(state.profile.handle) || state.profile.windowRefreshRequired) append = false;
    if (append && (!state.profile.nextCursor || state.profile.loading)) return;
    const profileState = state.profile;
    const followVersion = state.followState?.version();
    const previousUserId = profileState.data?.user?.id || null;
    const loadedPages = Number.isSafeInteger(profileState.loadedPages) && profileState.loadedPages > 0
      ? profileState.loadedPages : profileState.data ? 1 : 0;
    const previousCursors = Array.isArray(profileState.pageCursors) ? [...profileState.pageCursors] : [null];
    const preserveVisible = !append && sameHandle(profileState.data?.user?.handle) && !el.profileContent.hidden;
    const viewerId = state.viewer?.id || null;
    const session = state.messages, csrf = state.csrf, route = state.routeRequestId;
    // Connections is an overlay on this profile, not a membership filter.
    const path = location.pathname;
    const view = state.view;
    const requestId = profileState.requestId = (profileState.requestId || 0) + 1;
    profileState.controller?.abort();
    const controller = new AbortController();
    profileState.controller = controller;
    state.profile.handle = handle;
    profileState.loading = true;
    profileState.postsPending = !append;
    if (!append) profileState.repostsIdentityPending = true;
    profileState.postsError = false;
    const cursor = append ? profileState.nextCursor : null;
    const ownsRead = () => state.profile === profileState && profileState.requestId === requestId
      && profileState.controller === controller;
    const sameRoute = () => profileState.handle === handle && state.routeRequestId === route
      && state.view === view && location.pathname === path;
    const sameAudience = () => (state.viewer?.id || null) === viewerId && state.messages === session;
    const current = () => ownsRead() && !controller.signal.aborted && sameRoute()
      && sameAudience() && state.csrf === csrf;
    const changedPages = () => Object.assign(new Error("Profile pages changed. Refresh the profile before continuing."), { profileCursorChanged: true });
    if (append) el.profileDropsMore.disabled = true;
    else {
      // Keep the existing layout while revalidating the same profile's window.
      el.profileLoading.hidden = preserveVisible;
      el.profileError.hidden = true;
      if (el.profileDropsError) el.profileDropsError.hidden = true;
      if (preserveVisible && !profileState.drops.length) {
        if (el.profileDropsLoading) el.profileDropsLoading.hidden = false;
        el.profileDropsEmpty.hidden = true;
      }
      if (!preserveVisible) {
        el.profileContent.hidden = true;
        el.profileViewName.textContent = "Profile";
      }
    }
    try {
      const readPosts = (id, pageCursor = cursor) => {
        const query = new URLSearchParams({ profile_id: id });
        if (pageCursor) query.set("cursor", pageCursor);
        return api(`/api/drops?${query}`, { signal: controller.signal }).then(
          value => ({ ok: true, value }), error => ({ ok: false, error }));
      };
      const identity = !profileResult && !(append && profileState.data)
        ? api(`/api/profiles/${encodeURIComponent(handle)}`, { signal: controller.signal }) : null;
      const hintedId = !append && !profileResult && sameHandle(userHint?.handle)
        && typeof userHint.id === "string" ? userHint.id : null;
      const earlyPosts = hintedId ? readPosts(hintedId) : null;
      const profile = append && profileState.data
        ? profileState.data
        : profileResult || await identity;
      if (!current()) return false;
      const identityChanged = Boolean(previousUserId && previousUserId !== profile.user.id);
      if (!append && (!preserveVisible || identityChanged)) {
        // Retire old membership before any new-identity control or renderer can paint.
        for (const media of $$("video, audio", el.profileContent)) media.pause();
        profileState.drops = [];
        profileState.nextCursor = null;
        profileState.loadedPages = 0;
        profileState.pageCursors = [];
        profileState.windowRefreshRequired = false;
        profileState.offers = [];
        profileState.offersFeeBps = null;
        profileState.status = null;
        clear(el.profileDrops);
        if (el.profileDropGrid) clear(el.profileDropGrid);
      }
      profileState.data = profile;
      if (!append) profileState.repostsIdentityPending = false;
      state.followState?.accept(profile.user, { profile }, followVersion);
      if (state.connectionsPanel) showProfileConnectionsForRoute(profile);
      const posts = earlyPosts && profile.user.id === hintedId ? earlyPosts : readPosts(profile.user.id);
      // Fresh identity is independent of the posts read. Same-ID rows keep their nodes.
      if (!append) renderProfileView({ refreshExtras: !preserveVisible || identityChanged });
      el.profileLoading.hidden = true;
      if (state.view === "profile") {
        el.pageTitle.textContent = profile.user.display_name;
        el.pageDescription.textContent = `@${profile.user.handle}`;
        document.title = `${profile.user.display_name} · ${state.productName}`;
      }
      if (!append) {
        const offersRead = state.capabilities.commerce
          ? api(`/api/creators/${encodeURIComponent(profile.user.handle)}/offers`, { signal: controller.signal })
          : Promise.resolve({ items: [] });
        void offersRead.catch(() => null).then(offers => {
          if (!current() || profileState.data !== profile) return;
          profileState.offers = offers?.items || [];
          profileState.offersFeeBps = Number.isFinite(Number(offers?.platform_fee_bps)) ? Number(offers.platform_fee_bps) : null;
          renderProfileCommerce();
        }).catch(error => console.error("Could not render profile offers", error));
        void api(`/api/status/profiles/${encodeURIComponent(profile.user.handle)}`, { signal: controller.signal })
          .catch(() => null).then(status => {
            if (!current() || profileState.data !== profile) return;
            profileState.status = status;
            if (status) state.followState?.accept(profile.user, { status }, followVersion);
            renderProfileStatus();
          }).catch(error => console.error("Could not render profile status", error));
      }
      const targetPages = !append && preserveVisible && !identityChanged ? Math.max(1, loadedPages) : 1;
      const pageCursors = append ? previousCursors : [];
      const seen = new Set(pageCursors);
      let nextCursor = cursor, reply = await posts, pagesRead = 0;
      const items = new Map((append ? profileState.drops : []).map(drop => [drop.id, drop]));
      while (true) {
        if (!current()) return false;
        if (!reply.ok) throw reply.error;
        if (!reply.value || !Array.isArray(reply.value.items)) throw new Error("Profile Drops could not be loaded.");
        if (seen.has(nextCursor)) throw changedPages();
        seen.add(nextCursor); pageCursors.push(nextCursor);
        for (const drop of reply.value.items) items.set(drop.id, drop);
        pagesRead += 1;
        nextCursor = reply.value.next_cursor || null;
        if (nextCursor && seen.has(nextCursor)) throw changedPages();
        if (!nextCursor || pagesRead >= targetPages) break;
        reply = await readPosts(profile.user.id, nextCursor);
      }
      // Commit rows, depth and outgoing cursor together only after the owned window succeeds.
      profileState.drops = [...items.values()];
      profileState.nextCursor = nextCursor;
      profileState.loadedPages = append ? loadedPages + pagesRead : pagesRead;
      profileState.pageCursors = pageCursors;
      profileState.windowRefreshRequired = false;
      profileState.loading = false;
      profileState.postsPending = false;
      renderProfileView({ refreshExtras: append || (preserveVisible && !identityChanged), refreshReposts: false });
      const reposts = await state.socialExtras?.profileRepostsReady?.(profile.user.id);
      if (!current()) return false;
      return reposts !== false;
    } catch (error) {
      if (!current()) return false;
      if (profileState.repostsIdentityPending || [401, 403, 404].includes(error.status)) state.socialExtras?.leaveProfileReposts?.();
      profileState.postsPending = false;
      profileState.loading = false;
      if ([401, 403, 404].includes(error.status)) {
        for (const media of $$("video, audio", el.profileContent)) media.pause();
        profileState.data = null;
        profileState.drops = [];
        profileState.nextCursor = null;
        profileState.loadedPages = 0;
        profileState.pageCursors = [];
        profileState.windowRefreshRequired = false;
        profileState.offers = [];
        profileState.offersFeeBps = null;
        profileState.status = null;
        clear(el.profileDrops);
        if (el.profileDropGrid) clear(el.profileDropGrid);
        el.profileContent.hidden = true;
        el.profileError.hidden = false;
      } else {
        if (error.profileCursorChanged || (error.status === 400 && cursor)) profileState.windowRefreshRequired = true;
        if ((!append || profileState.windowRefreshRequired) && sameHandle(profileState.data?.user?.handle) && !el.profileContent.hidden) {
          profileState.postsError = true;
          renderProfileView({ refreshExtras: false, refreshReposts: false });
        } else if (!append && !preserveVisible) {
          profileState.data = null;
          profileState.nextCursor = null;
          profileState.loadedPages = 0;
          profileState.pageCursors = [];
          el.profileError.hidden = false;
        }
      }
      toast(error.message, true);
      if (profileState.data?.user?.id) await state.socialExtras?.profileRepostsReady?.(profileState.data.user.id);
      return false;
    } finally {
      if (ownsRead()) {
        const interrupted = !current();
        profileState.loading = false;
        profileState.postsPending = false;
        if (interrupted) {
          profileState.postsError = true;
          profileState.windowRefreshRequired = true;
        }
        if (sameRoute()) {
          el.profileLoading.hidden = true;
          if (el.profileDropsLoading) el.profileDropsLoading.hidden = true;
          el.profileDropsMore.disabled = false;
          if (interrupted) {
            // Auth can rotate while a newer route read is pending. Leave a usable
            // Retry instead of accepting its old response or leaving a blank page.
            el.profileDropsEmpty.hidden = true;
            if (sameAudience() && profileState.data && !el.profileContent.hidden) {
              if (el.profileDropsError) el.profileDropsError.hidden = false;
            } else {
              el.profileContent.hidden = true;
              el.profileError.hidden = false;
            }
          }
        }
        // Keep this slot until replacement so optional details can finish after posts.
      }
    }
  }

  async function toggleProfileFollow() {
    const profile = state.profile.data;
    if (!profile?.user) return;
    // The button reads "Following", so a tap asks before it unfollows, as on Instagram.
    const following = state.followState?.get?.(profile.user, Boolean(profile.relationship?.is_following))?.following;
    if (following) {
      const name = profile.user.handle ? `@${profile.user.handle}` : (profile.user.display_name || "this account");
      if (!(await confirm(`Unfollow ${name}?`, { confirmLabel: "Unfollow", cancelLabel: "Cancel", destructive: true }))) return;
      if (state.profile.data !== profile) return;
      // Another tab may have unfollowed while the sheet was open; a toggle now would follow again.
      if (!state.followState?.get?.(profile.user, Boolean(profile.relationship?.is_following))?.following) return;
    }
    return toggleDropAuthorFollow({ author: profile.user, author_id: profile.user.id,
      viewer_follows_author: Boolean(profile.relationship?.is_following) }, el.profileFollow);
  }

  // Mute and Restrict are the two relationship changes the server reverses cleanly, so they act
  // at once and offer Undo instead of asking first. `restore` puts back what the change hid.
  async function undoRelationship(kind, author, restore = null) {
    const viewerId = state.viewer?.id || null, session = state.messages, csrf = state.csrf;
    try {
      await api(`/api/relationships/${kind}/${encodeURIComponent(author.id)}`, { method: "DELETE", csrf: true });
      if (!viewerId || state.viewer?.id !== viewerId || session !== state.messages || csrf !== state.csrf) return;
      state.relationships.loaded = false;
      if (kind !== "restrict") state.feedWindows?.invalidate({ erase: true });
      if (typeof restore === "function") restore();
      if (state.view === "profile" && state.profile.data?.user?.id === author.id) await refreshProfile(author.handle);
      toast(`${kind === "mute" ? "Unmuted" : "Restriction removed for"} ${author.display_name}`);
    } catch (error) { toast(error.message, true); }
  }

  async function toggleProfileRelationship(kind) {
    if (!requireVerified() || !state.profile.data) return;
    const profile = state.profile.data;
    const viewerId = state.viewer.id;
    const identity = state.messages;
    // Closing a confirmation can refresh this same profile before the write returns.
    // Keep the action bound to its people and session, not a replaced response object.
    const current = () => state.profile.data?.user?.id === profile.user.id
      && state.viewer?.id === viewerId && state.messages === identity && state.view === "profile";
    const active = kind === "mute" ? profile.relationship.muted
      : kind === "restrict" ? profile.relationship.restricted
        : (profile.relationship.blocking ?? profile.relationship.blocked);
    if (kind === "block" && !active && !(await confirm(`Block ${profile.user.display_name}? You will not see each other's Drops or comments, follows are removed, and private messages stop.`))) return;
    if (!current()) return;
    try {
      if (active) {
        const result = await api(`/api/relationships/${kind}/${encodeURIComponent(profile.user.id)}`, { method: "DELETE", csrf: true });
        if (result && result.removed === false) throw new Error(`You had not ${kind === "block" ? "blocked" : kind === "restrict" ? "restricted" : "muted"} this person.`);
      } else {
        await api("/api/relationships", { method: "POST", csrf: true, json: { target_user_id: profile.user.id, kind } });
      }
      if (state.viewer?.id === viewerId && state.messages === identity) {
        clearBusinessAds();
        if (kind !== "restrict") state.feedWindows?.invalidate({ erase: true });
      }
      if (!current()) return;
      state.relationships.loaded = false;
      await refreshProfile(profile.user.handle);
      if (!current()) return;
      if (!active) track(kind, null, { target_user_id: profile.user.id });
      const done = { block: ["Blocked", "Unblocked"], mute: ["Muted", "Unmuted"], restrict: ["Restricted", "Restriction removed for"] }[kind];
      const undo = !active && kind !== "block" ? { action: { label: "Undo", run: () => undoRelationship(kind, profile.user) } } : {};
      toast(`${active ? done[1] : done[0]} ${profile.user.display_name}`, false, undo);
    } catch (error) { if (current()) toast(error.message, true); }
  }

  function openProfileReport() {
    if (!requireVerified() || !state.profile.data || state.profile.data.relationship?.is_self) return;
    el.profileSafetyMenu.open = false;
    const target = state.profile.data.user;
    openReport({
      id: target.id,
      title: "Report this profile",
      endpoint: () => `/api/profiles/${encodeURIComponent(target.id)}/reports`,
      track: () => track("report", null, { target_type: "user", target_user_id: target.id }),
      receipt: () => "Profile report added to the moderator queue",
    });
  }

  async function startProfileConversation() {
    if (!state.capabilities.community_writes) return;
    if (!requireVerified() || !state.profile.data) return;
    const profile = state.profile.data;
    const context = messageContext();
    const current = () => context.accountCurrent() && state.profile.data === profile && state.view === "profile";
    try {
      const result = await api("/api/messages/conversations/open", {
        method: "POST", csrf: true, json: { recipient_user_id: profile.user.id },
      });
      if (!current()) return;
      setView("messages");
      const selectionVersion = state.messages.selectionVersion;
      await refreshConversations(true);
      if (!context.accountCurrent() || state.view !== "messages" || state.messages.selectionVersion !== selectionVersion) return;
      await openMessageThread(result.conversation.id);
    } catch (error) { if (context.accountCurrent()) toast(error.message, true); }
  }



  function showProfileConnectionsForRoute(profile) {
    if (!state.connectionsPanel || state.view !== "profile" || !profile?.user?.id
        || state.profile.data !== profile || state.ageAttestationRequired || state.ageRequirementNotMet) return;
    const parts = location.pathname.split("/").filter(Boolean);
    const direction = new URLSearchParams(location.search).get("connections");
    if (parts.length !== 2 || parts[0] !== "u" || !["followers", "following"].includes(direction)) return;
    let handle;
    try { handle = decodeURIComponent(parts[1]); } catch (_) { return; }
    if (handle.toLowerCase() !== profile.user.handle.toLowerCase()) return;
    if (!state.connectionsPanel.isOpenFor(profile.user.id, direction)) {
      void state.connectionsPanel.open({ profile, direction });
    }
  }

  function openConnections(direction) {
    const profile = state.profile.data;
    if (!state.connectionsPanel || !profile?.user?.id || state.view !== "profile"
        || !["followers", "following"].includes(direction)
        || state.ageAttestationRequired || state.ageRequirementNotMet) return;
    // The old identity can remain visible while the next profile is resolving.
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts.length !== 2 || parts[0] !== "u") return;
    let handle;
    try { handle = decodeURIComponent(parts[1]); } catch (_) { return; }
    if (handle.toLowerCase() !== profile.user.handle.toLowerCase()) return;
    // Keep a valid mixed-case URL so ongoing profile reads retain their route.
    const target = `${location.pathname}?connections=${direction}`;
    if (`${location.pathname}${location.search}` !== target) {
      history.pushState({ louderrConnections: true }, "", target);
    }
    showProfileConnectionsForRoute(profile);
  }

  function dismissConnections(reason) {
    if (reason !== "dismiss") return;
    const query = new URLSearchParams(location.search);
    if (!/^\/u\/[^/]+$/.test(location.pathname)
        || !["followers", "following"].includes(query.get("connections"))) return;
    if (history.state?.louderrConnections) history.back();
    else {
      query.delete("connections");
      const suffix = query.toString();
      history.replaceState({}, "", location.pathname + (suffix ? `?${suffix}` : ""));
      void routeLocation();
    }
  }

  function runConnectionFollow(user, following, context) {
    if (!context?.source || !context.current()) return;
    return toggleDropAuthorFollow({ author: user, author_id: user.id,
      viewer_follows_author: following }, context.source, context);
  }

  function refreshConnectionCounts() {
    const target = state.connectionsPanel?.target();
    const profile = state.profile.data;
    if (state.view === "profile" && profile?.user?.id === state.viewer?.id
        && target?.user.id === profile.user.id) void refreshProfile(profile.user.handle);
  }

  function privateMessageVisible(event) {
    if (state.view !== "messages" || !event.payload?.conversation_id || !event.payload?.message_id
      || state.messages.selectedId !== event.payload.conversation_id
      || document.visibilityState !== "visible" || !document.hasFocus() || document.querySelector("dialog[open]")) return false;
    const message = state.messages.items.find(item => item.id === event.payload.message_id);
    if (!message || message.deleted) return false;
    const node = Array.from(el.messageList.children).find(item => item.dataset.messageId === message.id);
    if (!node) return false;
    const bounds = el.messageList.getBoundingClientRect();
    const viewport = { top: Math.max(0, bounds.top), bottom: Math.min(window.innerHeight, bounds.bottom),
      left: Math.max(0, bounds.left), right: Math.min(window.innerWidth, bounds.right) };
    viewport.height = viewport.bottom - viewport.top;
    return viewport.height > 0 && viewport.right > viewport.left
      && window.LobbyMessageReceipts?.visibleMessage(node.getBoundingClientRect(), viewport) === true;
  }

  async function pollPrivateEvents() {
    if (!state.viewer || state.sessionRestricted) return;
    if (document.visibilityState === "hidden") return;
    const privateEvents = state.privateEvents;
    if (privateEvents.polling) return;
    privateEvents.polling = true;
    const originalCursor = privateEvents.cursor;
    const wasInitialized = privateEvents.initialized;
    const viewerId = state.viewer.id;
    const current = () => state.privateEvents === privateEvents && state.viewer?.id === viewerId;
    let pages = 0;
    let hasMore = false;
    const events = [];
    try {
      if (!state.privateEvents.initialized) {
        const head = await api("/api/private-events?after=0&limit=1&initialize=true");
        if (!current()) return;
        state.privateEvents.cursor = head.cursor;
        state.privateEvents.initialized = true;
        // The cursor prevents a historical replay burst. These authoritative reads
        // cover everything at or before that cursor; later changes remain replayable.
        if (await refreshSocialBadges() === false) throw new Error("Inbox refresh interrupted");
        if (!current()) return;
        if (state.view === "premium") businessInstance?.refresh();
        return;
      }
      do {
        const result = await api(`/api/private-events?after=${state.privateEvents.cursor}&limit=100`);
        if (!current()) return;
        events.push(...result.items);
        state.privateEvents.cursor = result.cursor;
        hasMore = result.has_more;
        pages += 1;
      } while (hasMore && pages < 10);
      // Paid-content changes can occur between the room snapshot and this first drain.
      // Process these invalidations even when older notification toasts are being skipped.
      if (events.some((item) => item.type === "paid_content.changed")) {
        await invalidatePaidContent({ viewerId });
        if (!current()) return;
        if (state.view === "premium") businessInstance?.refresh();
      }
      if (!events.length) return;
      globalThis.LouderrVoicePlayback?.events(events);
      globalThis.LouderrCalls?.events(events);
      state.live?.events(events);
      state.messages.scheduled?.events(events);
      state.messageSearch?.events(events);
      state.messageShared?.events(events);
      state.messageAlbums?.events(events);
      for (const event of events) {
        if (event.type === "drop.coauthors.changed") state.coauthors?.event(event.payload?.drop_id);
      }
      for (const event of events) {
        if (event.type !== "dm.message.hidden" || !event.payload?.conversation_id || !event.payload?.message_id) continue;
        await applyPersonalMessageHide(messageContext(event.payload.conversation_id), event.payload.message_id, false);
        if (!current()) return;
      }
      const notificationArrival = events.some((item) => item.type === "notification.created");
      const messageArrival = events.some((item) => item.type.startsWith("dm.message."));
      state.sharedCollections?.events(events);
      const conversationChange = events.some((item) => item.type === "dm.conversation.changed");
      const moderationArrival = events.some((item) => item.type.startsWith("moderation."));
      const membershipChanges = events.filter((item) => item.type === "commerce.membership.changed");
      if (notificationArrival && await refreshNotifications(false, true) === false) throw new Error("Notification refresh interrupted");
      if (!current()) return;
      if (messageArrival || conversationChange) {
        if (await refreshConversations(true) === false) throw new Error("Inbox refresh interrupted");
        if (!current()) return;
        if (state.messages.selectedId && await openMessageThread(state.messages.selectedId, false, false, null, { refreshDetail: true }) === false) {
          throw new Error("Message history refresh interrupted");
        }
      }
      if (!current()) return;
      if (moderationArrival) await refreshEnforcement();
      if (!current()) return;
      if (membershipChanges.length) {
        state.purchases.loaded = false;
        await refreshPurchases(true);
        if (!current()) return;
        const latestStatus = membershipChanges.at(-1)?.payload?.status;
        if (latestStatus === "nonrenewing") toast("Membership will not renew.");
        if (latestStatus === "ended") toast("Membership ended. Contact support if a refund or dispute review may be needed.");
      }
      const newMessages = events.filter(item => item.type === "dm.message.created");
      if (newMessages.length) requestAnimationFrame(() => {
        // Let the selected thread finish its scheduled scroll before measuring.
        if (current() && newMessages.some(item => !privateMessageVisible(item))) toast("New private message");
      });
    } catch (error) {
      if (!current()) return;
      privateEvents.cursor = originalCursor;
      privateEvents.initialized = wasInitialized;
      if (error.status === 401 && state.viewer) {
        try {
          // refreshAuth signs the tab out, with every cleanup, if the server no longer has the session.
          await refreshAuth();
        } catch (_) {
          /* A transient auth check failure is retried on the next bounded poll. */
        }
        return;
      }
      if (error.status === 403 && state.viewer?.status === "active") {
        try {
          await refreshAuth();
          if (state.viewer?.status !== "active") {
            clearTimeout(state.privateEvents.timer);
            resetSocialState();
            await refreshEnforcement(false);
            scheduleEnforcementPolling();
            toast(`Your account is now ${state.viewer.status}. Review the action and appeal options in Account.`, true);
          }
        } catch (_) { /* Authentication recovery is retried by the next explicit action. */ }
      }
      /* Public room connectivity is independent from private inbox polling. */
    } finally {
      privateEvents.polling = false;
    }
  }

  function schedulePrivatePolling(reset = false) {
    clearTimeout(state.privateEvents.timer);
    if (!state.viewer || state.sessionRestricted || state.viewer.status !== "active") return;
    if (reset) state.privateEvents = { cursor: 0, initialized: false, timer: null };
    if (document.visibilityState === "hidden") {
      state.privateEvents.timer = setTimeout(() => schedulePrivatePolling(), 60000);
      return;
    }
    pollPrivateEvents().finally(() => {
      if (state.viewer?.status === "active" && !state.sessionRestricted) state.privateEvents.timer = setTimeout(() => schedulePrivatePolling(), 4000);
    });
  }

  function scheduleEnforcementPolling() {
    clearTimeout(state.enforcement.timer);
    if (!state.viewer || state.viewer.status === "active") return;
    state.enforcement.timer = setTimeout(async () => {
      const priorStatus = state.viewer?.status;
      try {
        await refreshAuth();
        await refreshEnforcement();
        if (priorStatus !== "active" && state.viewer?.status === "active") {
          toast("Your account is active again.");
          await refreshSocialBadges();
          schedulePrivatePolling(true);
        }
      } catch (_) {
        // refreshAuth has already signed the tab out if the session ended; anything else waits for the next poll.
      } finally {
        if (state.viewer?.status !== "active") scheduleEnforcementPolling();
      }
    }, 15000);
  }

  function startupNotificationReceiptCurrent(receipt) {
    if (!receipt) return false;
    const notifications = receipt.notifications;
    return state.notifications === notifications && notificationAccountCurrent(notifications)
      && (state.viewer?.id || null) === receipt.viewerId && state.messages === receipt.social
      && state.csrf === receipt.csrf && state.view === "notifications"
      && state.routeRequestId === receipt.routeRequestId
      && `${location.pathname}${location.search}${location.hash}` === receipt.url
      && notifications.requestId === receipt.requestId && notifications.controller === null
      && Boolean(receipt.controller) && !receipt.controller.signal.aborted
      && (notifications.countRevision || 0) === receipt.countRevision
      && (notifications.readRevision || 0) === receipt.readRevision
      && !notifications.loading && !notifications.readBusy && !notifications.readQueue?.length;
  }

  function startupConversationReceiptCurrent(receipt) {
    if (receipt?.kind !== "conversations") return false;
    const messages = receipt.social;
    return state.messages === messages && Boolean(state.viewer?.id)
      && state.viewer.id === receipt.viewerId && state.csrf === receipt.csrf
      && !state.sessionRestricted && state.viewer.status === "active"
      && state.view === "messages" && state.routeRequestId === receipt.routeRequestId
      && `${location.pathname}${location.search}${location.hash}` === receipt.url
      && messages.conversationRequestId === receipt.requestId
      && (messages.folder || "inbox") === receipt.folder
      && messages.selectionVersion === receipt.selectionVersion && !messages.selectedId
      && messages.loaded && !messages.conversationsLoading
      && !receipt.hadPendingWrites && (state.startupWriteRevision || 0) === receipt.writeRevision
      && !state.updatePendingWrites;
  }

  async function routeStartup() {
    const owner = { viewerId: state.viewer?.id || null, social: state.messages, csrf: state.csrf,
      url: `${location.pathname}${location.search}${location.hash}`, writeRevision: state.startupWriteRevision || 0,
      hadPendingWrites: Boolean(state.updatePendingWrites) };
    const notificationRoute = location.pathname === "/notifications";
    const conversationRoute = /^\/messages\/?$/.test(location.pathname);
    const loading = routeLocation();
    // routeLocation/setView dispatch their primary read synchronously before yielding.
    const notifications = state.notifications;
    const receipt = conversationRoute ? { ...owner, kind: "conversations",
      requestId: owner.social.conversationRequestId, routeRequestId: state.routeRequestId,
      folder: owner.social.folder || "inbox", selectionVersion: owner.social.selectionVersion, used: false,
    } : { ...owner, notifications, requestId: notifications.requestId,
      controller: notifications.controller, routeRequestId: state.routeRequestId,
      countRevision: notifications.countRevision || 0, readRevision: notifications.readRevision || 0,
      used: false };
    const accepted = await loading;
    if (conversationRoute) return accepted === true && startupConversationReceiptCurrent(receipt) ? receipt : null;
    return notificationRoute && accepted === true && startupNotificationReceiptCurrent(receipt) ? receipt : null;
  }

  async function refreshSocialBadges(startupReceipt = null) {
    const reuseNotifications = Boolean(startupReceipt && !startupReceipt.used
      && startupReceipt.kind !== "conversations" && startupNotificationReceiptCurrent(startupReceipt));
    const reuseConversations = Boolean(startupReceipt && !startupReceipt.used
      && startupConversationReceiptCurrent(startupReceipt));
    if (startupReceipt) startupReceipt.used = true;
    if (!state.viewer || state.sessionRestricted || state.viewer.status !== "active") {
      state.notifications.unreadCount = 0;
      state.messages.unreadCount = 0;
      updateSocialBadges();
      return;
    }
    const results = await Promise.all([
      reuseNotifications ? Promise.resolve(true) : refreshNotifications(false, true),
      reuseConversations ? Promise.resolve(true) : refreshConversations(true),
    ]);
    // A retired receipt cannot claim completion or start work in a replacement session.
    if (reuseNotifications && !startupNotificationReceiptCurrent(startupReceipt)) return;
    if (reuseConversations && !startupConversationReceiptCurrent(startupReceipt)) return;
    return results.every(result => result !== false);
  }

  function resetSocialState() {
    state.socialExtras?.resetProfileReposts?.();
    state.messages.threadWindow?.read?.controller?.abort();
    state.followState?.reset();
    state.followRequests?.close({ navigation: true });
    state.verificationOperation?.finish();
    state.roomController?.abort();
    state.roomController = null;
    state.feedRetry = null;
    state.feedWindows?.reset(); state.feedWindows = null;
    state.feedBusy = false;
    state.feedController?.abort();
    state.feedController = null;
    state.initialHomeFeed?.controller?.abort();
    state.initialHomeFeed = null;
    if (state.search) {
      const search = state.search;
      search.controller?.abort();
      state.search = {
        type: search.type || "all", category: search.category || "all", people: [], drops: [],
        peopleCursor: null, peoplePage: 0, peoplePageCursors: [null], dropsCursor: null,
        dropPages: 0, dropCursors: [], retry: null, requestId: 0, controller: null,
        loaded: false, loadedKey: null, renderedKey: null,
      };
      if (el.discoverPeople) { clear(el.discoverPeople); el.discoverPeople.setAttribute("aria-busy", "false"); }
      if (el.discoverDrops) clear(el.discoverDrops);
      for (const node of [el.discoverResults, el.discoverLoading, el.discoverError, el.discoverEmpty,
        el.discoverPeoplePaging, el.discoverMoreDrops]) if (node) node.hidden = true;
      for (const node of [el.discoverMorePeople, el.discoverPreviousPeople, el.discoverMoreDrops]) if (node) node.disabled = true;
      for (const node of [el.discoverPeopleNote, el.discoverDropsNote]) if (node) node.textContent = "";
    }
    state.messageSearch?.close();
    state.messageShared?.close();
    state.messageAlbums?.close();
    globalThis.LouderrCalls?.reset();
    globalThis.LouderrVoicePlayback?.reset();
    resetRelatedDrops();
    if (state.exposure) syncExposureIdentity(true);
    state.security = { sessions: [], notifications: [], loaded: false };
    state.purchases = { items: [], loaded: false };
    state.creator = { status: null, dashboard: null, loaded: false };
    // Clear prior viewer flags and personal collections even if session expiry wins a logout race.
    state.drops = [];
    state.nextCursor = null;
    state.pending = [];
    el.pendingBanner.hidden = true;
    clear(el.feed);
    el.empty.hidden = true;
    el.loadMore.hidden = true;
    resetAdminWorkspace();
    resetChatWorkspace();
    clearBusinessAds();
    businessInstance?.reset();
    resetJourneyState();
    state.flow.controller?.abort();
    state.flow.requestId += 1;
    stopFlowTimers();
    state.flow.items = [];
    state.flow.totalPaged = 0;
    state.flow.pageCursors = [];
    state.flow.nextCursor = null;
    state.flow.snapshot = null;
    state.flow.feedOwner = null;
    state.flow.lastRevalidate = false;
    state.flow.resetRequired = false;
    state.flow.loading = false;
    state.flow.now = null;
    state.flow.chatItems = [];
    state.flow.chatReadStatus = null;
    state.flow.chatRetryAt = 0;
    state.flow.stories = [];
    state.flow.storyPages = [];
    state.flow.storyNextCursor = null;
    state.flow.storyHasUpdates = false;
    state.flow.storyResetRequired = false;
    state.flow.storySnapshot = null;
    state.flow.history = [];
    state.flow.historyCursor = null;
    state.flow.chatDraft = "";
    state.flow.chatDraftOwner = null;
    clear(el.flowFeed);
    clear(el.flowNow);
    clear(el.flowStories);
    clear(el.flowHistory);
    clearTimeout(state.privateEvents.timer);
    clearTimeout(state.enforcement.timer);
    state.sendDropSession?.close();
    state.notifications.controller?.abort();
    state.notifications.countController?.abort();
    state.notifications = { items: [], nextCursor: null, unreadCount: 0, loaded: false };
    state.messages.conversationWindow?.read?.controller.abort();
    state.messages.receipts?.destroy();
    state.messageTyping?.reset();
    const scheduledMessages = state.messages.scheduled;
    scheduledMessages?.reset();
    state.groupInvites?.reset();
    state.messagePolls?.reset();
    globalThis.LouderrMessageHide?.reset();
    globalThis.LouderrVoiceNote?.cancel();
    if (state.messageAttachment) clearMessageAttachment();
    if (el.messageActions?.open) el.messageActions.close();
    resetMessageSearch();
    state.messages = {
      conversations: [], unreadCount: 0, loaded: false, selectedId: null, folder: "inbox",
      people: newMessageSearchState(), startingRecipients: new Set(),
      conversationCursor: null, conversationsLoading: false,
      conversationRequestId: 0, threadRequestId: 0, selectionVersion: 0,
      drafts: new Map(), sending: new Set(), deciding: new Set(),
      deletionVersion: 0, deletedMessages: new Map(),
      items: [], nextCursor: null, conversation: null, participant: null, reportMessage: null,
    };
    state.messages.scheduled = scheduledMessages;
    scheduledMessages?.sync();
    state.profile?.controller?.abort();
    state.profile = { data: null, drops: [], offers: [], status: null, handle: null, nextCursor: null, requestId: 0, loading: false };
    el.messageBody.value = "";
    el.messagesLoading.hidden = true;
    el.notificationsLoading.hidden = true;
    hideError(el.messageFormError);
    if (el.report.open) el.report.close();
    resetComments();
    el.commentsBody.value = "";
    if (el.comments.open) el.comments.close();
    state.connectionsPanel?.reset();
    state.relationships = { items: [], loaded: false, loading: false };
    // Viewer-filtered World data belongs to this session, including inactive views.
    // Replacing the workspace also retires same-account session resets and A-B-A races.
    const world = state.world;
    clearTimeout(world.roomNowRefreshTimer);
    state.world = {
      view: world.view || "pulse", pulse: null, map: null, mic: null, moment: null,
      readStatus: {}, readAttempts: {}, lensSelection: null,
      lens: null, lensRequestId: 0, micEntry: null, micHistory: [], moments: [],
      daily: null, questions: [], questionScope: world.questionScope || "all", polls: [], collaborations: [],
      collaborationRole: world.collaborationRole || "", collaborationLocation: world.collaborationLocation || "",
      roomNowLoaded: false, roomNowRequest: null, roomNowRefreshTimer: null,
      mapSvgPromise: world.mapSvgPromise || null, pendingFocus: null,
    };
    if (state.discovery) {
      state.discovery.requestId = (state.discovery.requestId || 0) + 1;
      state.discovery.controller?.abort();
      state.discovery.controller = null;
    }
    clear(el.worldContent);
    const reloadWorld = create("button", "secondary-button", "Reload World");
    reloadWorld.type = "button";
    reloadWorld.addEventListener("click", () => refreshWorld());
    el.worldContent.append(create("p", "utility-copy", "Your session changed. Reload World to see current content."), reloadWorld);
    $$(".utility-dialog[data-world-workspace]").forEach((dialog) => {
      clear(dialog);
      if (dialog.open) dialog.close();
    });
    renderRoomAffordances();
    stopChatTyping({ clearPeople: true });
    state.privateEvents = { cursor: 0, initialized: false, timer: null };
    state.enforcement = { items: [], loaded: false, timer: null };
    updateSocialBadges();
    renderNotifications();
    renderConversations();
    renderMessageThread();
  }

  function chatWorkspace() {
    const chat = state.chat;
    chat.room ||= "global";
    chat.rooms ||= [];
    chat.drafts ||= new Map();
    chat.sending ||= new Set();
    chat.roomUnread ||= new Map();
    chat.seenEvents ||= new Set();
    chat.generation ||= 0;
    chat.requestId ||= 0;
    chat.roomsRequestId ||= 0;
    chat.readRequestId ||= 0;
    chat.selectionVersion ||= 0;
    chat.draftRevision ||= 0;
    chat.mutationVersion ||= 0;
    chat.mutations ||= new Map();
    chat.unreadEvents ||= new Map();
    chat.unreadVersion ||= new Map();
    chat.eventRequests ||= new Map();
    chat.eventVersion ||= 0;
    return chat;
  }

  function chatContext() {
    const chat = chatWorkspace();
    const viewerId = state.viewer?.id || null;
    const generation = chat.generation;
    const room = chat.room;
    const lens = chat.lens;
    const selectionVersion = chat.selectionVersion;
    return { chat, room, lens, viewerId, current: () => state.chat === chat
      && chat.generation === generation && (state.viewer?.id || null) === viewerId,
    selected: () => state.chat === chat && chat.generation === generation
      && (state.viewer?.id || null) === viewerId && chat.room === room && chat.lens === lens
      && chat.selectionVersion === selectionVersion };
  }

  function resetChatWorkspace() {
    const chat = chatWorkspace();
    stopChatTyping({ clearPeople: true });
    chat.controller?.abort();
    chat.controller = null;
    chat.generation += 1;
    chat.requestId += 1;
    chat.roomsRequestId += 1;
    chat.readRequestId += 1;
    chat.selectionVersion += 1;
    chat.typingRequestActive = false;
    chat.typingLastSentAt = 0;
    chat.items = [];
    chat.nextCursor = null;
    chat.loaded = false;
    chat.loading = false;
    chat.roomsLoading = false;
    chat.roomsPromise = null;
    chat.lens = "all";
    chat.replyTo = null;
    chat.reportMessage = null;
    chat.lastTrackedRead = null;
    chat.unreadCount = 0;
    chat.newWhileAway = 0;
    chat.drafts.clear();
    chat.sending.clear();
    chat.roomUnread.clear();
    chat.seenEvents.clear();
    chat.mutations.clear();
    chat.unreadEvents.clear();
    chat.unreadVersion.clear();
    chat.eventRequests.clear();
    state.flow.chatItems = [];
    el.chatBody.value = "";
    if (state.emotes.target === el.chatBody) closeEmotePicker();
    if (el.report.open) el.report.close();
    clear(el.chatMessages);
    cancelChatReply();
    renderChatRooms();
    updateChatBadges();
  }

  function chatRoomInfo() {
    const chat = chatWorkspace();
    return chat.rooms.find((room) => room.id === chat.room)
      || { id: chat.room, label: chat.room === "global" ? "Global" : "Chat", description: "A public conversation." };
  }

  function updateChatComposer() {
    const chat = chatWorkspace();
    const canChat = Boolean(state.viewer?.email_verified && state.viewer.status === "active" && !state.sessionRestricted && state.capabilities.community_writes);
    const slowControls = state.chatControls;
    const slowModeNote = slowControls && slowControls.mode === "slow" && Number(slowControls.slow_mode_seconds) > 0
      ? ` Slow mode: one message every ${Number(slowControls.slow_mode_seconds)} seconds.` : "";
    const sending = chat.sending.has(chat.room);
    el.chatBody.readOnly = !canChat;
    el.chatSend.disabled = !canChat || sending || !el.chatBody.value.trim();
    el.chatSend.setAttribute("aria-busy", String(sending));
    el.chatEmote.disabled = !canChat;
    if (el.chatComposerCount) el.chatComposerCount.textContent = `${el.chatBody.value.length} / 700`;
    el.chatNote.textContent = canChat
      ? `Public in ${chatRoomInfo().label} as @${state.viewer.handle}.${slowModeNote} Use Send to post.`
      : state.viewer && !state.capabilities.community_writes
        ? "Chat is read-only right now."
      : state.sessionRestricted || (state.viewer && state.viewer.status !== "active")
        ? "Messaging is unavailable while your account is restricted."
        : state.viewer ? "Verify your email to join the conversation." : "Sign in to join the conversation.";
    el.chatMarkUnread.hidden = !canChat;
    el.chatBody.style.height = "auto";
    el.chatBody.style.height = `${Math.min(el.chatBody.scrollHeight, 140)}px`;
  }

  function saveChatDraft() {
    const chat = chatWorkspace();
    if (!state.viewer) return;
    const previous = chat.drafts.get(chat.room);
    if (previous?.body === el.chatBody.value && previous.replyTo?.id === chat.replyTo?.id) return;
    chat.drafts.set(chat.room, { body: el.chatBody.value, replyTo: chat.replyTo, revision: ++chat.draftRevision });
  }

  function setChatRoomsOpen(open, restoreFocus = false) {
    el.chatShell?.classList.toggle("is-rooms-open", Boolean(open));
    el.chatRoomsOpen?.setAttribute("aria-expanded", String(Boolean(open)));
    if (open) el.chatRoomSearch?.focus();
    else if (restoreFocus) el.chatRoomsOpen?.focus();
  }

  // "The artist is in the room": who hosts the current room, and the viewer's own control.
  function renderChatHost(info) {
    const line = el.chatRoomHost;
    const toggle = el.chatHostToggle;
    if (!line || !toggle) return;
    const chat = chatWorkspace();
    const host = info?.host?.user ? info.host : null;
    if (host) {
      const ends = host.ends_at ? new Date(host.ends_at) : null;
      const when = ends && Number.isFinite(ends.getTime()) ? ends.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
      line.textContent = host.is_viewer ? "Hosting now" : `${host.user.display_name} is here`;
      line.title = host.is_viewer
        ? `You are hosting this room${when ? ` until ${when}` : ""}.`
        : `${host.user.display_name} (@${host.user.handle}) is in the room${when ? ` until ${when}` : ""}.`;
      line.hidden = false;
    } else {
      line.textContent = "";
      line.hidden = true;
    }
    const canHost = Boolean(state.viewer?.email_verified && state.viewer.status === "active" && !state.sessionRestricted && state.capabilities.community_writes);
    const hostingElsewhere = chat.rooms.some((room) => room.host?.is_viewer && room.id !== chat.room);
    if (!canHost || (host && !host.is_viewer) || hostingElsewhere) { toggle.hidden = true; return; }
    toggle.hidden = false;
    toggle.dataset.hosting = host?.is_viewer ? "1" : "";
    toggle.textContent = host?.is_viewer ? "End my session" : "I'm here";
    toggle.title = host?.is_viewer ? "Stop hosting this room" : "Tell the room and your followers that you are here for the next hour";
  }

  async function toggleChatHost() {
    const chat = chatWorkspace();
    const toggle = el.chatHostToggle;
    if (!toggle || toggle.disabled) return;
    const room = chat.room;
    toggle.disabled = true;
    try {
      if (toggle.dataset.hosting) {
        await api(`/api/chat/rooms/${encodeURIComponent(room)}/host`, { method: "DELETE", csrf: true });
        toast("Your session ended.");
      } else {
        const result = await api(`/api/chat/rooms/${encodeURIComponent(room)}/host`, { method: "POST", csrf: true, json: { duration_minutes: 60 } });
        const told = Number(result?.followers_notified) || 0;
        toast(told ? `You are in the room for the next hour. ${told} ${told === 1 ? "follower was" : "followers were"} told.` : "You are in the room for the next hour.");
      }
      await refreshChatRooms();
    } catch (error) {
      toast(error.message, true);
    } finally {
      toggle.disabled = false;
    }
  }

  function flagGlyph(code) {
    const upper = String(code || "").toUpperCase();
    if (!/^[A-Z]{2}$/.test(upper)) return "";
    return String.fromCodePoint(...[...upper].map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65));
  }

  // The country room matching the browser's region setting, if the catalog has it. A hint only:
  // nothing is stored, and a language without a region gives no suggestion.
  function suggestedCountryRoom(rooms) {
    if (typeof navigator === "undefined") return null;
    const tags = Array.isArray(navigator.languages) && navigator.languages.length ? navigator.languages : [navigator.language];
    for (const tag of tags) {
      let region = "";
      try { region = new Intl.Locale(String(tag || "")).region || ""; } catch (_) { region = ""; }
      if (!region) continue;
      const id = `country-${region.toLowerCase()}`;
      if (rooms.some((room) => room.id === id)) return id;
    }
    return null;
  }

  // The Countries fold: a search always shows its results; otherwise the person's explicit
  // choice wins, and with no choice made yet the group opens while a country room is active.
  function countriesGroupOpen(chat, query, matches) {
    if (query) return true;
    if (typeof chat.countriesOpen === "boolean") return chat.countriesOpen;
    return matches.some((room) => room.id === chat.room && room.category === "Countries");
  }

  function renderChatRooms() {
    if (!el.chatRoomList) return;
    const chat = chatWorkspace();
    const focusedRoom = document.activeElement?.dataset?.chatRoom;
    const roomScroll = el.chatRoomList.scrollTop;
    const query = (el.chatRoomSearch.value || "").trim().toLocaleLowerCase();
    const matches = chat.rooms.filter((room) => !query
      || `${room.label} ${room.description} ${room.category} ${room.country || ""}`.toLocaleLowerCase().includes(query));
    // Countries stay folded behind one row until asked for, searched, or already open.
    const countryTotal = matches.filter((room) => room.category === "Countries").length;
    const suggested = suggestedCountryRoom(matches);
    const countriesOpen = countriesGroupOpen(chat, query, matches);
    // Community and interest rooms are short enough to keep visible. Only the 185-country
    // catalog is folded, so people never need a second control to find a topic room.
    const rooms = matches.filter((room) =>
      room.category !== "Countries" || countriesOpen || room.id === suggested || room.id === chat.room);
    if (suggested && !query) {
      const index = rooms.findIndex((room) => room.id === suggested);
      const first = rooms.findIndex((room) => room.category === "Countries");
      if (index > first) rooms.splice(first, 0, rooms.splice(index, 1)[0]);
    }
    clear(el.chatRoomList);
    const groups = new Map();
    const tileLetters = { global: "G", feedback: "F", collaborations: "C", music: "M", art: "A", film: "V", gaming: "P", tech: "T" };
    const ensureGroup = (category) => {
      if (groups.has(category)) return groups.get(category);
      const group = create("section", "chat-room-group");
      if (category === "Countries" && !query) {
        group.classList.add("is-countries");
        const heading = create("h3", "");
        const toggle = create("button", "chat-room-toggle");
        toggle.type = "button";
        toggle.setAttribute("aria-expanded", String(countriesOpen));
        toggle.setAttribute("aria-label", `${countriesOpen ? "Hide" : "Show"} ${countryTotal} country rooms`);
        toggle.append(create("span", "", "Countries"), create("span", "chat-room-count", String(countryTotal)));
        toggle.addEventListener("click", () => { chat.countriesOpen = !countriesOpen; renderChatRooms(); });
        heading.append(toggle);
        group.append(heading);
      } else group.append(create("h3", "", category));
      groups.set(category, group);
      el.chatRoomList.append(group);
      return group;
    };
    for (const room of rooms) {
      const category = room.category || "Rooms";
      ensureGroup(category);
      const button = create("button", "chat-room-button");
      button.type = "button";
      button.dataset.chatRoom = room.id;
      button.title = room.description;
      const active = room.id === chat.room;
      button.classList.toggle("is-active", active);
      if (active) button.setAttribute("aria-current", "true");
      const flag = room.category === "Countries" ? flagGlyph(room.country) : "";
      const glyph = create("span", "chat-room-glyph", flag || tileLetters[room.id] || String(room.label || "#").trim().charAt(0).toUpperCase());
      glyph.setAttribute("aria-hidden", "true");
      if (flag) glyph.classList.add("is-flag");
      else glyph.style.background = `hsl(${avatarHue(room.id)} 34% 32%)`;
      button.append(glyph, create("span", "chat-room-label", room.label));
      if (room.id === suggested && !query) button.append(create("span", "chat-room-suggested", "Suggested"));
      if (room.host?.user) {
        const live = create("span", "chat-room-live", room.host.is_viewer ? "You are here" : `${room.host.user.display_name} is here`);
        live.setAttribute("aria-label", room.host.is_viewer ? "You are hosting this room" : `${room.host.user.display_name} is hosting this room now`);
        button.append(live);
      }
      const unread = chat.roomUnread.get(room.id) || 0;
      if (unread) {
        const badge = create("span", "chat-room-unread", unread > 99 ? "99+" : String(unread));
        badge.setAttribute("aria-label", `${unread} unread messages`);
        button.append(badge);
      } else if (!active && chat.drafts.get(room.id)?.body) button.append(create("span", "chat-room-draft", "Draft"));
      button.addEventListener("click", () => selectChatRoom(room.id));
      groups.get(category).append(button);
    }
    if (countryTotal && !query) ensureGroup("Countries");
    el.chatRoomEmpty.hidden = rooms.length > 0 || chat.roomsLoading;
    const info = chatRoomInfo();
    el.chatRoomTitle.textContent = info.label;
    el.chatRoomDescription.textContent = info.description;
    el.chatBody.placeholder = `Message ${info.label}…`;
    renderChatHost(info);
    el.chatRoomList.scrollTop = roomScroll;
    if (focusedRoom) $(`[data-chat-room="${CSS.escape(focusedRoom)}"]`, el.chatRoomList)?.focus({ preventScroll: true });
    if (state.view === "chat") document.title = `${info.label} · Chat · ${state.productName}`;
  }

  async function loadChatRooms() {
    const context = chatContext();
    const chat = context.chat;
    const requestId = ++chat.roomsRequestId;
    const unreadVersion = new Map(chat.unreadVersion);
    chat.roomsLoading = true;
    if (el.chatRoomStatus) el.chatRoomStatus.textContent = "Loading rooms…";
    try {
      const result = await api("/api/chat/rooms");
      if (!context.current() || requestId !== chat.roomsRequestId) return;
      if (!Array.isArray(result?.items) || !result.items.length || result.items.length > 400
        || result.items.some((room) => !/^[a-z][a-z0-9-]{0,39}$/.test(room.id || "")
          || typeof room.label !== "string" || typeof room.description !== "string"
          || !Number.isSafeInteger(room.unread_count) || room.unread_count < 0)
        || new Set(result.items.map((room) => room.id)).size !== result.items.length
        || !result.items.some((room) => room.id === "global")) throw new Error("The room list could not be read. Please retry.");
      chat.rooms = result.items;
      for (const room of result.items) {
        if ((unreadVersion.get(room.id) || 0) === (chat.unreadVersion.get(room.id) || 0)) {
          chat.roomUnread.set(room.id, context.viewerId ? room.unread_count : 0);
        }
      }
      chat.roomsLoaded = true;
      el.chatRoomError.hidden = true;
      const countryRooms = chat.rooms.filter((room) => room.category === "Countries").length;
      el.chatRoomStatus.textContent = countryRooms
        ? `${chat.rooms.length - countryRooms} rooms · ${countryRooms} countries`
        : `${chat.rooms.length} public rooms`;
      updateChatBadges();
      return true;
    } catch (error) {
      if (!context.current() || requestId !== chat.roomsRequestId) return;
      if (!chat.rooms.length) chat.rooms = [{ id: "global", label: "Global", category: "Community", description: "The whole community, one conversation.", unread_count: 0 }];
      if ([401, 403].includes(error.status)) { chat.roomUnread.clear(); chat.unreadCount = 0; }
      const copy = $("p", el.chatRoomError);
      copy.textContent = [404, 405].includes(error.status)
        ? "Global is available. More rooms need the updated server."
        : "Rooms could not be refreshed. Your current conversation and draft are kept.";
      el.chatRoomError.hidden = false;
      el.chatRoomStatus.textContent = "Room list unavailable";
      updateChatBadges();
      return false;
    } finally {
      if (context.current() && requestId === chat.roomsRequestId) {
        chat.roomsLoading = false;
        renderChatRooms();
      }
    }
  }

  async function refreshChatRooms() {
    const chat = chatWorkspace();
    if (chat.roomsPromise) return chat.roomsPromise;
    const operation = loadChatRooms();
    chat.roomsPromise = operation;
    try { return await operation; }
    finally { if (chat.roomsPromise === operation) chat.roomsPromise = null; }
  }

  async function enterChat() {
    const context = chatContext();
    const query = new URLSearchParams(location.search);
    const requested = query.get("room") || "global";
    const focusMessage = query.get("message");
    const entry = context.chat.entryId = (context.chat.entryId || 0) + 1;
    renderChatRooms();
    updateChatComposer();
    const roomsRefresh = refreshChatRooms();
    if (!focusMessage && (requested === "global" || context.chat.rooms.some((item) => item.id === requested))) {
      // The conversation does not depend on fresh sidebar counts. Global always
      // exists; known rooms are still authorized by the messages endpoint.
      await Promise.all([roomsRefresh, selectChatRoom(requested, false)]);
      return;
    }
    await roomsRefresh;
    if (!context.current() || state.view !== "chat" || context.chat.entryId !== entry) return;
    let room = requested;
    if (focusMessage && /^[0-9a-f-]{36}$/i.test(focusMessage)) {
      try {
        const message = await api(`/api/chat/messages/${encodeURIComponent(focusMessage)}`);
        if (!context.current() || state.view !== "chat" || context.chat.entryId !== entry) return;
        room = message.room || "global";
      } catch (_) { if (context.current()) toast("That message is unavailable", true); }
    }
    if (!context.chat.rooms.some((item) => item.id === room)) {
      toast("That room is unavailable. Global is open.", true);
      room = "global";
    }
    await selectChatRoom(room, false);
    if (focusMessage && context.current() && state.view === "chat" && context.chat.room === room) scrollToChatMessage(focusMessage);
  }

  async function selectChatRoom(room, pushHistory = true) {
    const chat = chatWorkspace();
    if (room !== "global" && !chat.rooms.some((item) => item.id === room)) return;
    if (pushHistory && room === chat.room && chat.loading && !chat.loaded
        && chat.controller?.signal.aborted === false) {
      // Another tap on the room must not restart the first page already loading.
      // Route/permalink resolution still owns its explicit selection below.
      setChatRoomsOpen(false, true);
      return;
    }
    saveChatDraft();
    closeEmotePicker();
    stopChatTyping({ clearPeople: true });
    chat.entryId = (chat.entryId || 0) + 1;
    chat.requestId += 1;
    chat.selectionVersion += 1;
    chat.room = room;
    // Leaving the country section hands the fold back to its automatic behaviour.
    if (!chat.rooms.some((entry) => entry.id === room && entry.category === "Countries")) chat.countriesOpen = undefined;
    chat.items = [];
    chat.nextCursor = null;
    chat.loaded = false;
    chat.loading = false;
    chat.newWhileAway = 0;
    chat.lastTrackedRead = null;
    chat.typingLastSentAt = 0;
    const draft = chat.drafts.get(room);
    el.chatBody.value = draft?.body || "";
    cancelChatReply();
    if (draft?.replyTo && !draft.replyTo.deleted) {
      chat.replyTo = draft.replyTo;
      el.chatReplyAuthor.textContent = `@${draft.replyTo.author.handle}`;
      el.chatReplyCopy.textContent = draft.replyTo.body;
      el.chatReply.hidden = false;
    }
    hideError($("#chat-form-error"));
    el.chatNew.hidden = true;
    clear(el.chatMessages);
    el.chatEmpty.hidden = true;
    el.chatOlder.hidden = true;
    renderChatRooms();
    updateChatComposer();
    setChatRoomsOpen(false, pushHistory);
    if (pushHistory) history.pushState({}, "", room === "global" ? "/chat" : `/chat?room=${encodeURIComponent(room)}`);
    await refreshChat();
  }

  function emojiCatalog() {
    return window.LoudErrChatEmojis || { categories: [], items: [], search: () => [], get: () => null };
  }

  function pickerEmote(code) {
    const custom = chatEmoteByCode.get(code);
    if (custom) return custom;
    const emoji = emojiCatalog().get(code);
    return emoji ? { ...emoji, code: emoji.glyph, name: emoji.id, native: true } : null;
  }

  function chatTime(value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? ""
      : new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(date);
  }

  function chatEmoteNode(emote) {
    const node = create("span", `chat-emote chat-emote-${emote.name}`, emote.glyph);
    node.dataset.emote = emote.name;
    node.setAttribute("role", "img");
    node.setAttribute("aria-label", `${emote.label} emote`);
    node.title = emote.code;
    return node;
  }

  function setEmoteMotion(enabled, persist = true) {
    state.emotes.motionEnabled = Boolean(enabled);
    document.documentElement.classList.toggle("emote-motion-off", !state.emotes.motionEnabled);
    el.emoteMotion.textContent = state.emotes.motionEnabled ? "Animation on" : "Animation off";
    el.emoteMotion.setAttribute("aria-pressed", String(state.emotes.motionEnabled));
    el.emoteMotion.setAttribute("aria-label", state.emotes.motionEnabled ? "Pause emote animation" : "Play emote animation");
    if (persist) {
      try { localStorage.setItem("lobby.emotes.motion", state.emotes.motionEnabled ? "on" : "off"); }
      catch (_) { /* The preference is optional when storage is unavailable. */ }
    }
  }

  function renderEmotePicker(filter = "") {
    const query = String(filter || "").trim().replaceAll(":", "").toLowerCase();
    const category = state.emotes.category || "all";
    const categories = [{ id: "all", label: "All", glyph: "✨" }, { id: "recent", label: "Recent", glyph: "🕘" },
      { id: "emotes", label: "Emotes", glyph: "🔥" }, ...emojiCatalog().categories];
    if (el.emoteCategories) {
      clear(el.emoteCategories);
      for (const item of categories) {
        const button = create("button", "emote-category");
        button.type = "button";
        button.title = item.label;
        button.setAttribute("aria-label", item.label);
        button.setAttribute("aria-pressed", String(category === item.id));
        button.classList.toggle("is-active", category === item.id);
        button.textContent = item.glyph;
        button.addEventListener("click", () => {
          state.emotes.category = item.id;
          renderEmotePicker(el.emoteSearch.value);
          $("button[aria-pressed='true']", el.emoteCategories)?.focus();
        });
        el.emoteCategories.append(button);
      }
    }
    const match = (item) => !query || `${item.name} ${item.label} ${item.keywords || ""}`.toLowerCase().includes(query) || item.glyph === query;
    let matches;
    if (category === "recent") matches = state.emotes.recent.map(pickerEmote).filter(Boolean).filter(match);
    else {
      const custom = ["all", "emotes"].includes(category) ? chatEmotes.filter(match) : [];
      const native = category === "emotes" ? [] : emojiCatalog().search(query, { category, limit: emojiCatalog().items.length })
        .map((item) => ({ ...item, code: item.glyph, name: item.id, native: true }));
      matches = [...custom, ...native];
      if (!query && category === "all") {
        const recent = state.emotes.recent.map(pickerEmote).filter(Boolean);
        const codes = new Set(recent.map((item) => item.code));
        matches = [...recent, ...matches.filter((item) => !codes.has(item.code))];
      }
    }
    clear(el.emoteGrid);
    for (const emote of matches) {
      const item = create("span", "emote-option-wrap");
      item.setAttribute("role", "listitem");
      const button = create("button", "emote-option");
      button.type = "button";
      button.dataset.emoteCode = emote.code;
      button.title = emote.label;
      button.setAttribute("aria-label", emote.native ? emote.label : `${emote.label}, ${emote.code}`);
      const glyph = emote.native ? create("span", "native-emoji", emote.glyph) : chatEmoteNode(emote);
      glyph.setAttribute("aria-hidden", "true");
      button.append(glyph);
      button.addEventListener("click", () => insertChatEmote(emote));
      item.append(button);
      el.emoteGrid.append(item);
    }
    if (!matches.length && (window.LoudErrChatEmojis || !window.LouderrOptional)) {
      const empty = create("p", "emote-empty", category === "recent" && !query ? "Your recent picks will appear here." : "No matches. Try another word.");
      empty.setAttribute("role", "status");
      el.emoteGrid.append(empty);
    }
    if (!window.LoudErrChatEmojis && window.LouderrOptional) {
      const failed = window.LouderrOptional.status("emojis") === "error";
      const notice = create("p", "emote-empty", failed ? "Emoji could not be loaded." : "Loading emoji…");
      notice.setAttribute("role", failed ? "alert" : "status");
      el.emoteGrid.append(notice);
      if (failed) {
        const retry = create("button", "secondary-button", "Retry emoji"); retry.type = "button";
        retry.addEventListener("click", () => {
          openEmotePicker(state.emotes.target, state.emotes.trigger, { query: el.emoteSearch.value });
        });
        el.emoteGrid.append(retry);
      }
    }
    el.emoteGrid.scrollTop = 0;
  }

  function rememberChatEmote(code) {
    state.emotes.recent = [code, ...state.emotes.recent.filter((item) => item !== code)].slice(0, 24);
    try { localStorage.setItem("lobby.emotes.recent", JSON.stringify(state.emotes.recent)); }
    catch (_) { /* Optional preference remains usable without storage. */ }
  }

  function closeEmotePicker(returnFocus = false) {
    const trigger = state.emotes.trigger;
    el.emotePicker.hidden = true;
    el.emoteSearch.value = "";
    if (trigger) trigger.setAttribute("aria-expanded", "false");
    state.emotes.target = null;
    state.emotes.trigger = null;
    state.emotes.autocomplete = false;
    if (returnFocus && trigger && !trigger.disabled) trigger.focus();
  }

  function openEmotePicker(target, trigger, { query = "", focusSearch = true, autocomplete = false } = {}) {
    if (!target || !trigger || target.disabled || target.readOnly || trigger.disabled) return;
    if (state.emotes.trigger && state.emotes.trigger !== trigger) state.emotes.trigger.setAttribute("aria-expanded", "false");
    target.form.append(el.emotePicker);
    state.emotes.target = target;
    state.emotes.trigger = trigger;
    state.emotes.autocomplete = autocomplete;
    if (!window.LoudErrChatEmojis && window.LouderrOptional) {
      const request = {};
      state.emotes.catalogRequest = request;
      const refresh = () => {
        if (state.emotes.catalogRequest !== request || state.emotes.target !== target || el.emotePicker.hidden) return;
        renderEmotePicker(el.emoteSearch.value);
      };
      window.LouderrOptional.load("emojis").then(refresh, refresh);
    }
    if (autocomplete) state.emotes.category = "all";
    trigger.setAttribute("aria-expanded", "true");
    el.emoteSearch.value = query;
    renderEmotePicker(query);
    el.emotePicker.hidden = false;
    if (focusSearch) requestAnimationFrame(() => { if (state.emotes.target === target && !el.emotePicker.hidden) el.emoteSearch.focus(); });
  }

  function toggleEmotePicker(target, trigger) {
    if (!el.emotePicker.hidden && state.emotes.target === target) {
      closeEmotePicker(true);
      return;
    }
    openEmotePicker(target, trigger);
  }

  function rememberTextareaSelection(target) {
    const start = Number.isInteger(target.selectionStart) ? target.selectionStart : target.value.length;
    const end = Number.isInteger(target.selectionEnd) ? target.selectionEnd : start;
    return { start, end };
  }

  function insertChatEmote(emote) {
    const target = state.emotes.target;
    if (!target || target.disabled || target.readOnly) return;
    const { start, end } = rememberTextareaSelection(target);
    const before = target.value.slice(0, start);
    const after = target.value.slice(end);
    const partial = state.emotes.autocomplete ? before.match(/(^|\s)(:[A-Za-z0-9_-]{0,24})$/) : null;
    const replaceStart = partial ? start - partial[2].length : start;
    let insertion = emote.code;
    if (!emote.native) {
      if (!partial && replaceStart > 0 && !/\s$/.test(target.value.slice(0, replaceStart))) insertion = ` ${insertion}`;
      if (!/^\s/.test(after)) insertion += " ";
    }
    const nextLength = target.value.length - (end - replaceStart) + insertion.length;
    if (target.maxLength > 0 && nextLength > target.maxLength) {
      toast("That emoji would make the message too long", true);
      return;
    }
    target.setRangeText(insertion, replaceStart, end, "end");
    target.dispatchEvent(new Event("input", { bubbles: true }));
    rememberChatEmote(emote.code);
    closeEmotePicker();
    target.focus();
  }

  function emoteAutocomplete(target, trigger) {
    const { start, end } = rememberTextareaSelection(target);
    if (start !== end) {
      if (state.emotes.autocomplete && state.emotes.target === target) closeEmotePicker();
      return;
    }
    const match = target.value.slice(0, start).match(/(^|\s):([A-Za-z0-9_-]{0,24})$/);
    if (match) {
      openEmotePicker(target, trigger, { query: match[2], focusSearch: false, autocomplete: true });
    } else if (state.emotes.autocomplete && state.emotes.target === target) closeEmotePicker();
  }

  function handleEmoteTextareaKeydown(event, target) {
    if (event.isComposing || event.keyCode === 229 || el.emotePicker.hidden || state.emotes.target !== target) return false;
    if (event.key === "Escape") {
      event.preventDefault(); closeEmotePicker(); target.focus(); return true;
    }
    if (event.key === "ArrowDown") {
      const first = $(".emote-option", el.emoteGrid);
      if (first) { event.preventDefault(); first.focus(); return true; }
    }
    if (event.key === "Enter" && !event.shiftKey && state.emotes.autocomplete && !matchMedia("(pointer: coarse)").matches) {
      const first = $(".emote-option", el.emoteGrid);
      if (first) { event.preventDefault(); first.click(); return true; }
    }
    return false;
  }

  function moveEmoteFocus(event) {
    const options = $$(".emote-option", el.emoteGrid);
    const index = options.indexOf(document.activeElement);
    if (index < 0 || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const firstTop = options[0]?.offsetTop;
    const columns = Math.max(1, options.filter((option) => option.offsetTop === firstTop).length);
    const next = event.key === "Home" ? 0 : event.key === "End" ? options.length - 1
      : event.key === "ArrowLeft" ? Math.max(0, index - 1)
        : event.key === "ArrowRight" ? Math.min(options.length - 1, index + 1)
          : event.key === "ArrowUp" ? Math.max(0, index - columns) : Math.min(options.length - 1, index + columns);
    options[next]?.focus();
  }

  function appendChatText(node, value) {
    const text = String(value || "");
    const pattern = /(https?:\/\/[^\s<>"']+|@[A-Za-z0-9][A-Za-z0-9_-]{2,29}\b|:[A-Za-z0-9][A-Za-z0-9_-]{1,23}:)/gi;
    let cursor = 0;
    for (const match of text.matchAll(pattern)) {
      if (match.index > cursor) node.append(document.createTextNode(text.slice(cursor, match.index)));
      const token = match[0];
      const emote = token.startsWith(":") ? chatEmoteByCode.get(token.toLowerCase()) : null;
      if (emote) node.append(chatEmoteNode(emote));
      else if (token.startsWith("@")) {
        const mention = create("button", "chat-mention", token);
        mention.type = "button";
        mention.addEventListener("click", async () => {
          const path = location.pathname;
          const requestId = state.profile.requestId = (state.profile.requestId || 0) + 1;
          const profileState = state.profile;
          const viewerId = state.viewer?.id || null;
          const current = () => state.profile === profileState && profileState.requestId === requestId
            && path === location.pathname && (state.viewer?.id || null) === viewerId;
          try {
            const profile = await api(`/api/profiles/${encodeURIComponent(token.slice(1).toLowerCase())}`);
            if (current()) await openCreator(profile.user, profile);
          } catch (error) { if (current()) toast(error.message, true); }
        });
        node.append(mention);
      } else {
        let valid = false;
        try { valid = ["http:", "https:"].includes(new URL(token).protocol); } catch (_) { valid = false; }
        if (valid) {
          const link = create("a", "", token);
          link.href = token;
          link.target = "_blank";
          link.rel = "noopener noreferrer nofollow ugc";
          node.append(link);
        } else node.append(document.createTextNode(token));
      }
      cursor = match.index + token.length;
    }
    if (cursor < text.length) node.append(document.createTextNode(text.slice(cursor)));
  }

  // Answering a comment. The target is held on the open comment view, so closing the Drop
  // or switching to another one drops it rather than carrying an answer somewhere it was
  // never meant for.
  function commentReplyLabel(target) {
    const name = target && target.author ? target.author.display_name : null;
    return name ? `Replying to ${name}` : "";
  }

  function paintCommentReply() {
    const target = state.comments ? state.comments.replyTo : null;
    if (!el.commentsReplyDraft) return;
    el.commentsReplyDraft.hidden = !target;
    if (el.commentsReplyName) el.commentsReplyName.textContent = commentReplyLabel(target);
  }

  function startCommentReply(comment) {
    if (!state.comments) return;
    // One level: answering an answer belongs to the same top comment, which is what the
    // server stores anyway, so the marker must not claim otherwise.
    state.comments.replyTo = comment.reply_to && !comment.reply_to.unavailable
      ? { id: comment.reply_to.id, author: comment.reply_to.author || comment.author }
      : comment;
    paintCommentReply();
    el.commentsBody?.focus();
  }

  function cancelCommentReply() {
    if (state.comments) state.comments.replyTo = null;
    paintCommentReply();
  }

  function commentNode(comment) {
    const answering = comment.reply_to && !comment.reply_to.unavailable ? comment.reply_to : null;
    const row = create("article", `chat-message drop-comment${comment.reply_to ? " is-reply" : ""}`);
    row.dataset.commentId = comment.id;
    const avatarButton = create("button", "chat-avatar-button");
    avatarButton.type = "button";
    avatarButton.dataset.commentAction = "profile-avatar";
    avatarButton.setAttribute("aria-label", `Open ${comment.author.display_name}'s profile`);
    const avatar = create("span", "avatar");
    styleAvatar(avatar, comment.author);
    avatarButton.append(avatar);
    avatarButton.addEventListener("click", () => openCreator(comment.author));

    const main = create("div", "chat-message-main");
    const meta = create("div", "chat-message-meta");
    const author = accountNameNode(comment.author, "button", "chat-author");
    author.type = "button";
    author.dataset.commentAction = "profile-name";
    author.addEventListener("click", () => openCreator(comment.author));
    meta.append(author);
    const body = create("p", "chat-message-body");
    appendChatText(body, comment.body);
    // One level, so the marker names who is answered instead of nesting again. It sits
    // between the name and the text: above the name it read "Replying to X" followed by
    // "X", which is nonsense.
    const answeringNote = comment.reply_to
      ? create("p", "drop-comment-answering",
        answering && answering.author ? `Replying to ${answering.author.display_name}` : "Replying to a deleted comment")
      : null;
    main.append(meta);
    if (answeringNote) main.append(answeringNote);
    main.append(body);
    // The member's own word filter. Collapsed rather than removed, and reversible in
    // one press: a reading preference, not moderation, so it never claims the comment
    // was acted on. Appended after the body so it reads in place of the text.
    if (comment.muted_by_word) {
      body.hidden = true;
      const veil = create("button", "comment-muted-veil", "Hidden by your word filter. Show");
      veil.type = "button";
      veil.dataset.commentAction = "reveal";
      veil.addEventListener("click", () => {
        const focused = document.activeElement === veil;
        body.hidden = false; veil.remove();
        if (focused) { body.tabIndex = -1; body.focus({ preventScroll: true }); }
      });
      main.append(veil);
    }
    const translation = translationControl(
      comment.translation,
      { body: comment.body },
      (fields) => { clear(body); appendChatText(body, fields.body); },
    );
    if (translation) {
      const toggle = translation.querySelector(".translation-toggle");
      if (toggle) toggle.dataset.commentAction = "translation";
      main.append(translation);
    }

    // One quiet line under the text, as Instagram has it: the age ("2d", never a bare clock time),
    // Reply, and pin, report and delete behind More instead of three icons under every comment.
    const actions = create("div", "chat-message-actions drop-comment-actions");
    const time = create("time", "drop-comment-time", (typeof compactTime === "function" ? compactTime : chatTime)(comment.created_at));
    time.dateTime = comment.created_at || "";
    time.title = new Date(comment.created_at).toLocaleString();
    actions.append(time);
    const secondary = [];
    if (state.viewer && state.capabilities.community_writes && !comment.deleted) {
      const reply = create("button", "drop-comment-reply", "Reply");
      reply.type = "button";
      reply.dataset.commentAction = "reply";
      reply.setAttribute("aria-label", `Reply to ${comment.author.display_name}`);
      reply.addEventListener("click", () => startCommentReply(comment));
      actions.append(reply);
    }
    // Only the Drop's author pins, and the server refuses anyone else regardless.
    if (state.viewer && state.comments.drop && state.comments.drop.author_id === state.viewer.id
      && !comment.deleted) {
      const pin = create("button");
      pin.type = "button";
      pin.dataset.commentAction = "pin";
      const pinned = Boolean(comment.pinned);
      pin.disabled = false;
      pin.setAttribute("aria-disabled", String(Boolean(state.comments.pinMutation?.current())));
      pin.setAttribute("aria-label", pinned ? "Unpin this comment" : "Pin this comment");
      pin.setAttribute("aria-pressed", String(pinned));
      pin.classList.toggle("is-active", pinned);
      pin.append(icon("pin"));
      pin.addEventListener("click", () => pinDropComment(comment, !pinned));
      actions.append(pin);
      secondary.push(pin);
    }
    if (state.viewer && comment.author.id !== state.viewer.id) {
      const report = create("button");
      report.type = "button";
      report.dataset.commentAction = "report";
      report.setAttribute("aria-label", `Report ${comment.author.display_name}'s comment`);
      report.append(icon("flag"));
      report.addEventListener("click", () => openChatReport(comment, "comment"));
      actions.append(report);
      secondary.push(report);
    }
    if (state.capabilities.community_writes && comment.can_delete) {
      const remove = create("button");
      remove.type = "button";
      remove.dataset.commentAction = "delete";
      remove.setAttribute("aria-label", "Delete comment");
      remove.append(icon("trash"));
      remove.addEventListener("click", () => deleteDropComment(comment));
      actions.append(remove);
      secondary.push(remove);
    }
    if (secondary.length) {
      const more = create("button", "drop-comment-more");
      more.type = "button";
      more.dataset.commentAction = "more";
      more.setAttribute("aria-label", `More options for ${comment.author.display_name}'s comment`);
      more.setAttribute("aria-expanded", "false");
      more.append(icon("more"));
      for (const button of secondary) button.hidden = true;
      more.addEventListener("click", () => {
        const open = secondary[0].hidden;
        for (const button of secondary) button.hidden = !open;
        more.setAttribute("aria-expanded", String(open));
        if (open) secondary[0].focus?.({ preventScroll: true });
      });
      actions.insertBefore(more, secondary[0]);
    }
    row.append(avatarButton, main, actions);
    return row;
  }

  async function pinDropComment(comment, pinned) {
    const comments = state.comments, drop = comments.drop;
    if (!drop || !state.viewer || drop.author_id !== state.viewer.id || !comment?.id) return;
    if (comments.pinMutation?.current()) return comments.pinMutation.promise;
    if (comments.pinned?.id !== comment.id && !comments.items.some(item => item.id === comment.id)) return;
    const dialogVersion = comments.dialogVersion, viewerId = state.viewer.id;
    const session = state.messages, csrf = state.csrf;
    const operation = { current: null, promise: null };
    const current = () => state.comments === comments && comments.pinMutation === operation
      && comments.dialogVersion === dialogVersion && comments.drop?.id === drop.id
      && state.viewer?.id === viewerId && state.messages === session && state.csrf === csrf;
    operation.current = current; comments.pinMutation = operation;
    renderComments();
    operation.promise = (async () => {
      try {
        const result = await api(`/api/drops/${encodeURIComponent(drop.id)}/comments/${encodeURIComponent(comment.id)}/pin`, {
          method: "PUT", csrf: true, json: { pinned },
        });
        if (!current()) return;
        if (result?.comment_id !== comment.id || result.pinned !== pinned) {
          throw new Error("The comment pin could not be confirmed. Check the conversation before trying again.");
        }
        // A read admitted before this confirmed write cannot install its old pin.
        // Start a fresh read even when an older request is still settling.
        invalidateCommentRead();
        await refreshComments();
        if (current()) toast(pinned ? "Comment pinned" : "Comment unpinned");
      } catch (error) { if (current()) toast(error.message, true); }
      finally {
        const repaint = comments.pinMutation === operation && state.comments === comments
          && comments.dialogVersion === dialogVersion && comments.drop?.id === drop.id
          && state.viewer?.id === viewerId && state.messages === session;
        if (comments.pinMutation === operation) comments.pinMutation = null;
        if (repaint) renderComments();
      }
    })();
    return operation.promise;
  }

  function renderComments() {
    const comments = state.comments, list = el.commentsList, active = document.activeElement;
    const previousOwner = comments.renderOwner;
    const sameOwner = previousOwner && previousOwner.viewerId === (state.viewer?.id || null)
      && previousOwner.session === state.messages && previousOwner.dropId === comments.drop?.id
      && previousOwner.dialogVersion === comments.dialogVersion;
    comments.renderOwner = { viewerId: state.viewer?.id || null, session: state.messages,
      dropId: comments.drop?.id, dialogVersion: comments.dialogVersion };
    const previousRows = Array.from(list.children);
    const previousOrder = new Map(previousRows.map((row, index) => [row.dataset.commentId, index]));
    const existing = sameOwner ? new Map(previousRows.map(row => [row.dataset.commentId, row])) : new Map();
    const focusedRow = sameOwner && active?.closest?.("[data-comment-id]");
    const focusedIndex = focusedRow?.parentElement === list ? previousRows.indexOf(focusedRow) : -1;
    const focusAction = focusedIndex >= 0 ? active.dataset.commentAction : null;
    const listRect = sameOwner && list.getBoundingClientRect?.();
    const inList = listRect && typeof getComputedStyle === "function"
      && ["auto", "scroll"].includes(getComputedStyle(list).overflowY);
    const viewportTop = inList ? listRect.top : 0;
    const viewportBottom = inList ? listRect.bottom : typeof window !== "undefined" ? window.innerHeight : 0;
    const anchors = sameOwner && listRect && listRect.bottom > 0 && listRect.top < viewportBottom
      ? previousRows.map(row => ({ id: row.dataset.commentId, rect: row.getBoundingClientRect?.() }))
        .filter(item => item.rect && item.rect.bottom > viewportTop && item.rect.top < viewportBottom)
        .map(item => ({ id: item.id, top: item.rect.top - viewportTop })) : [];
    const rows = [], seen = new Set();
    const visible = comments.pinned ? [comments.pinned, ...comments.items] : comments.items;
    for (const comment of visible) {
      if (!comment?.id || seen.has(comment.id)) continue;
      seen.add(comment.id);
      const pinned = comments.pinned?.id === comment.id;
      const signature = JSON.stringify([comment, pinned, state.viewer?.id || null,
        Boolean(state.capabilities.community_writes), comments.drop?.author_id]);
      const contentKey = JSON.stringify([comment.body, comment.muted_by_word, comment.translation]);
      const previous = existing.get(comment.id);
      let row = previous;
      if (!row || row._commentRender?.signature !== signature) {
        row = commentNode(comment);
        row.dataset.commentId = comment.id;
        row.tabIndex = -1;
        // Retain explicit reveal/original-language choice when only metadata changed.
        // Both controls close over their body node, so move the whole owned set together.
        if (previous?._commentRender?.contentKey === contentKey) {
          const oldBody = previous.querySelector(".chat-message-body"), body = row.querySelector(".chat-message-body");
          if (oldBody && body) {
            body.replaceWith(oldBody);
            for (const selector of [".comment-muted-veil", ".translation-control"]) {
              const oldControl = previous.querySelector(selector), control = row.querySelector(selector);
              if (oldControl && control) control.replaceWith(oldControl);
              else if (control) control.remove();
            }
          }
        }
        row._commentRender = { signature, contentKey };
        if (pinned) {
          row.classList.add("is-pinned");
          const marker = create("p", "drop-comment-pinned", "Pinned by the author");
          const main = row.querySelector(".chat-message-main");
          if (main) main.insertBefore(marker, main.firstChild);
        }
      }
      const pin = row.querySelector('[data-comment-action="pin"]');
      if (pin) {
        pin.disabled = false;
        pin.setAttribute("aria-disabled", String(Boolean(comments.pinMutation?.current())));
      }
      rows.push(row);
    }
    const keep = new Set(rows);
    rows.forEach((row, index) => {
      if (list.children[index] !== row) list.insertBefore(row, list.children[index] || null);
    });
    for (const row of Array.from(list.children)) if (!keep.has(row)) row.remove();
    if (focusedIndex >= 0 && (document.activeElement === active || document.activeElement === document.body)
        && !(document.activeElement === active && list.contains(active))) {
      const row = rows.find(item => item.dataset.commentId === focusedRow.dataset.commentId)
        || rows.find(item => previousOrder.get(item.dataset.commentId) > focusedIndex)
        || [...rows].reverse().find(item => previousOrder.has(item.dataset.commentId));
      const target = row?.contains(active) ? active
        : row && focusAction ? row.querySelector(`[data-comment-action="${focusAction}"]`) || row : row;
      if (target && target !== document.activeElement) target.focus({ preventScroll: true });
      else if (!row && el.commentsTitle) {
        el.commentsTitle.setAttribute("tabindex", "-1"); el.commentsTitle.focus({ preventScroll: true });
      }
    }
    const count = Math.max(0, Number(comments.totalCount) || 0);
    el.commentsSummary.textContent = !comments.drop ? "Comments unavailable" : count
      ? `${formatCount(count)} ${count === 1 ? "comment" : "comments"} · one shared conversation`
      : "Join the conversation.";
    el.commentsLoading.hidden = !comments.loading;
    el.commentsEmpty.hidden = comments.loading || comments.error || rows.length > 0;
    el.commentsMore.hidden = !comments.nextCursor;
    el.commentsMore.disabled = false;
    el.commentsMore.setAttribute("aria-disabled", String(Boolean(comments.loading)));
    if (sameOwner && active === el.commentsMore && el.commentsMore.hidden
        && (document.activeElement === active || document.activeElement === document.body)) {
      const target = rows.find(row => !existing.has(row.dataset.commentId)) || rows[0] || el.commentsTitle;
      if (target) { target.setAttribute("tabindex", "-1"); target.focus({ preventScroll: true }); }
    }
    const canComment = Boolean(comments.drop && state.viewer?.email_verified);
    el.commentsBody.placeholder = !comments.drop ? "Comments unavailable"
      : canComment ? "Add a comment…" : "Sign in to comment";
    el.commentsBody.readOnly = !canComment;
    el.commentsSend.disabled = !comments.drop || Boolean(comments.submission);
    el.commentsSend.textContent = comments.submission ? "Posting…" : "Post";
    for (const anchor of anchors) {
      const row = rows.find(item => item.dataset.commentId === anchor.id), rect = row?.getBoundingClientRect?.();
      const top = inList ? list.getBoundingClientRect().top : 0;
      if (!rect) continue;
      const delta = rect.top - top - anchor.top;
      if (Number.isFinite(delta) && Math.abs(delta) > 0.5) {
        if (inList) list.scrollTop += delta;
        else window.scrollBy(0, delta);
      }
      break;
    }
  }

  function commentCursorPosition(cursor) {
    if (typeof cursor !== "string" || typeof atob !== "function") return null;
    try {
      const encoded = cursor.replace(/-/g, "+").replace(/_/g, "/");
      const value = atob(encoded + "=".repeat((4 - encoded.length % 4) % 4));
      // Server cursor precision is microseconds; comment DTOs round to milliseconds.
      const match = /^2\|global\|comments\|(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z\|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/.exec(value);
      return match ? match[1] : null;
    } catch (_) { return null; }
  }

  function commentReadWindow() {
    const comments = state.comments, dropId = comments.drop?.id;
    if (!dropId) return null;
    const viewerId = state.viewer?.id || null, session = state.messages, dialogVersion = comments.dialogVersion;
    let window = comments.window;
    if (!window || window.dropId !== dropId || window.viewerId !== viewerId
        || window.session !== session || window.dialogVersion !== dialogVersion) {
      if (window) {
        window.read?.controller?.abort();
        comments.items = []; comments.pinned = null; comments.nextCursor = null;
        comments.totalCount = 0; comments.loading = false; comments.error = false;
      }
      window = comments.window = { dropId, viewerId, session, dialogVersion,
        loaded: false, pages: [], cursor: null, pinId: null, read: null, resetRequired: false };
    }
    return window;
  }

  function invalidateCommentRead(revalidate = true) {
    const comments = state.comments;
    comments.window?.read?.controller?.abort();
    if (comments.window) {
      comments.window.read = null;
      if (revalidate) comments.window.resetRequired = true;
      else comments.window.pinId = comments.pinned?.id || null;
    }
    comments.requestId += 1; comments.loading = false; comments.error = false;
    hideError(el.commentsError);
  }

  function retireCommentAccess(dropId) {
    const pane = $("#comments-media");
    if (pane) for (const media of $$("video, audio", pane)) {
      media.pause(); media.removeAttribute("src"); media.load();
    }
    paintCommentsMedia(null);
    resetComments();
    removeDrop(dropId);
    if (state.board?.drop?.id === dropId) { state.board.drop = null; renderBoard(); }
    state.comments.error = true;
    showError(el.commentsError, "This post's comments are no longer available.");
    renderComments();
  }

  async function refreshComments(append = false, { latest = false } = {}) {
    const comments = state.comments, drop = comments.drop;
    if (!drop) return;
    const window = commentReadWindow();
    if (window.read?.current()) return window.read.promise;
    if (comments.loading && !window.read) return;
    window.read?.controller?.abort();
    append = Boolean(append && !window.resetRequired && !latest);
    if (append && !comments.nextCursor) return;
    const cursor = append ? comments.nextCursor : null;
    const boundary = window.loaded ? commentCursorPosition(window.cursor) : null;
    const replay = !append && !latest && window.loaded;
    const pageLimit = replay ? Math.max(1, window.pages.length) + 4 : 1;
    const requestId = ++comments.requestId, csrf = state.csrf;
    const viewerId = state.viewer?.id || null, session = state.messages, dialogVersion = comments.dialogVersion;
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    const read = { requestId, controller, promise: null, current: null };
    const scope = () => state.comments === comments && comments.window === window
      && comments.requestId === requestId && comments.dialogVersion === dialogVersion && comments.drop?.id === drop.id
      && (state.viewer?.id || null) === viewerId && state.messages === session;
    const owns = () => scope() && window.read === read;
    const current = () => owns() && state.csrf === csrf && !controller?.signal.aborted;
    read.current = current; window.read = read;
    comments.loading = true; comments.error = false;
    const readError = (message) => {
      comments.error = true;
      showError(el.commentsError, message);
      const retry = create("button", "secondary-button", "Try again");
      retry.type = "button";
      retry.addEventListener("click", () => {
        if (scope()) void refreshComments(append && !window.resetRequired, { latest });
      });
      el.commentsError.append(retry);
      if (replay || window.resetRequired) {
        const newest = create("button", "secondary-button", "Show latest comments");
        newest.type = "button";
        newest.addEventListener("click", () => {
          if (scope()) void refreshComments(false, { latest: true });
        });
        el.commentsError.append(newest);
      }
    };
    hideError(el.commentsError); renderComments();
    read.promise = (async () => {
      try {
        const pages = [], visited = new Set(append ? window.pages.map(page => page.cursor).filter(value => value && value !== cursor) : []);
        let next = cursor, items = [], result = null, pinId;
        for (;;) {
          if (!current()) return;
          if (next && (!commentCursorPosition(next) || visited.has(next))) throw new Error("Comment history could not advance. Try again.");
          if (next) visited.add(next);
          const query = new URLSearchParams({ limit: "30" });
          if (next) query.set("cursor", next);
          result = await api(`/api/drops/${encodeURIComponent(drop.id)}/comments?${query}`, { signal: controller?.signal });
          if (!current()) return;
          if (!Array.isArray(result?.items) || result.items.some(item => !item || typeof item.id !== "string" || !item.id)
              || (result.drop_id != null && result.drop_id !== drop.id)
              || (result.pinned != null && (typeof result.pinned.id !== "string" || !result.pinned.id))) {
            throw new Error("Comments could not be confirmed. Try again.");
          }
          const after = result.next_cursor ?? null;
          const afterPosition = after == null ? null : commentCursorPosition(after);
          const fromPosition = next ? commentCursorPosition(next) : null;
          if (after != null && (!afterPosition || after.length > 300 || visited.has(after)
              || (fromPosition && afterPosition >= fromPosition))) throw new Error("Comment history could not advance. Try again.");
          const observedPin = result.pinned?.id || null;
          if ((pinId !== undefined && pinId !== observedPin)
              || (append && window.loaded && window.pinId !== observedPin)) {
            window.resetRequired = true;
            throw new Error("The pinned comment changed. Refresh the conversation to continue.");
          }
          pinId = observedPin;
          pages.push({ cursor: next, nextCursor: after });
          const known = new Set(items.map(item => item.id));
          items = [...result.items.filter(item => !known.has(item.id)), ...items];
          if (!replay || !after || (boundary && afterPosition <= boundary)) break;
          if (pages.length >= pageLimit) throw new Error("Comment history changed too much to refresh here. Retry or show the latest comments.");
          next = after;
        }
        if (!current()) return;
        if (append) {
          const known = new Set(comments.items.map(item => item.id));
          items = [...items.filter(item => !known.has(item.id)), ...comments.items];
        }
        const unique = new Set();
        comments.items = items.filter(item => item.id !== pinId && !unique.has(item.id) && unique.add(item.id));
        comments.pinned = result.pinned || null;
        comments.nextCursor = result.next_cursor ?? null;
        comments.totalCount = Math.max(0, Number(result.total_count) || 0);
        window.loaded = true; window.cursor = comments.nextCursor; window.pinId = pinId;
        window.pages = append ? [...window.pages, ...pages] : pages;
        window.resetRequired = false;
        setDropCommentCount(drop.id, comments.totalCount);
        return true;
      } catch (error) {
        if (current()) {
          if ([403, 404].includes(error.status)) retireCommentAccess(drop.id);
          else readError(error.message);
          return false;
        }
      } finally {
        if (owns()) {
          comments.loading = false;
          if (!current()) readError("Comment loading was interrupted. Try again.");
          window.read = null;
          renderComments();
        }
      }
    })();
    return read.promise;
  }

  function setDropCommentCount(dropId, count) {
    state.feedWindows?.patch(dropId, { comment_count: count });
    const update = (drop) => drop?.id === dropId ? { ...drop, comment_count: count } : drop;
    state.drops = state.drops.map(update);
    state.search.drops = state.search.drops.map(update);
    state.discovery.items = state.discovery.items.map(update);
    state.profile.drops = state.profile.drops.map(update);
    state.flow.items = state.flow.items.map(update);
    if (state.flow.now?.current?.drop) state.flow.now.current.drop = update(state.flow.now.current.drop);
    state.flow.stories = state.flow.stories.map((story) => ({ ...story, drop: update(story.drop) }));
    if (state.board?.drop) state.board.drop = update(state.board.drop);
    if (state.comments.drop) state.comments.drop = update(state.comments.drop);
  }

  function commentSessionCurrent(owner) {
    return Boolean(owner && owner.viewerId === (state.viewer?.id || null)
      && owner.session === state.messages && owner.csrf === state.csrf);
  }

  function rememberCommentDraft() {
    const comments = state.comments;
    if (!commentSessionCurrent(comments.draftOwner)) {
      comments.drafts = new Map();
      comments.draftOwner = { viewerId: state.viewer?.id || null, session: state.messages, csrf: state.csrf };
    }
    if (!comments.drop || !commentSessionCurrent(comments.owner)) return;
    const text = el.commentsBody.value;
    if (text) comments.drafts.set(comments.drop.id, text);
    else comments.drafts.delete(comments.drop.id);
  }

  function activateInlineComments(drop, compose = false) {
    const host = $("#drop-comments-inline");
    if (state.view !== "drop" || state.comments.inlineDropId !== drop.id || !host || host.hidden) return;
    // Focus happens in the user's click, before a read can delay it. Merely
    // mounting the conversation must not scroll the page or open a keyboard.
    const target = compose && state.viewer?.email_verified ? el.commentsBody : el.commentsTitle;
    if (target === el.commentsTitle) target.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: true });
    target.scrollIntoView({ block: target === el.commentsBody ? "center" : "start", behavior: "auto" });
  }

  async function openComments(drop, { activate = true, compose = false } = {}) {
    if (!drop?.id) return;
    const active = state.comments.drop?.id === drop.id && commentSessionCurrent(state.comments.owner)
      && (state.comments.inlineDropId === drop.id || el.comments.open);
    if (active) {
      if (activate && state.comments.inlineDropId === drop.id) activateInlineComments(drop, compose);
      return;
    }
    resetComments();
    state.comments.drop = drop;
    state.comments.owner = state.comments.draftOwner;
    el.commentsBody.value = state.comments.drafts.get(drop.id) || "";
    state.comments.items = [];
    state.comments.nextCursor = null;
    state.comments.totalCount = Number(drop.comment_count || 0);
    state.comments.requestId += 1;
    hideError(el.commentsError);
    hideError(el.commentsFormError);
    el.commentsTitle.textContent = "Comments";
    renderComments();
    if (typeof paintCommentsMedia === "function") paintCommentsMedia(drop);
    if (state.comments.inlineDropId !== drop.id) {
      if (typeof openDialogWithHistory === "function") {
        const link = typeof dialogDeepLink === "function" ? dialogDeepLink("comments") : null;
        if (link) openDialogWithHistory(el.comments, link.path, { returnTo: link.returnTo });
        else openDialogWithHistory(el.comments);
      } else openDialog(el.comments);
    } else if (activate) activateInlineComments(drop, compose);
    await refreshComments();
  }

  // On a large screen the conversation sits beside the post, like a post page: the media, or
  // the text, on the left and the comments on the right. Phones keep the half sheet over the card,
  // and the Drop page keeps its inline conversation.
  function paintCommentsMedia(drop) {
    const pane = $("#comments-media");
    if (!pane) return;
    clear(pane);
    const wide = typeof matchMedia === "function" && matchMedia("(min-width: 821px)").matches;
    if (!wide || !drop || state.comments.inlineDropId === drop.id) {
      pane.hidden = true;
      el.comments.classList.remove("has-media");
      return;
    }
    const media = mediaElement(drop, false, { privateArchive: true });
    if (media.childElementCount) pane.append(media);
    else {
      const text = create("div", "comments-media-text");
      text.append(create("h3", "comments-media-title", drop.title || ""), create("p", "comments-media-body", drop.body || ""));
      pane.append(text);
    }
    const byline = create("p", "comments-media-byline");
    byline.append(accountNameNode(drop.author, "strong"), create("span", "", ` \u00b7 ${dropLabel(drop)}`));
    pane.append(byline);
    pane.hidden = false;
    el.comments.classList.add("has-media");
  }
  // The Drop page shows the conversation alongside the post on wide screens
  // and below it on narrow screens. The same comments shell is hosted inline and
  // returned to its dialog when the view changes, so one implementation serves both.
  function mountInlineComments(drop) {
    const host = $("#drop-comments-inline");
    const shell = $(".comments-shell", el.comments);
    if (!host || !shell || !drop) return;
    if (el.comments.open) el.comments.close();
    host.append(shell);
    host.hidden = false;
    state.comments.inlineDropId = drop.id;
    document.body.classList.add("comments-inline-open");
    if (el.comments.classList.contains("has-media")) paintCommentsMedia(drop);
    openComments(drop, { activate: false }).catch(() => {});
  }

  function unmountInlineComments() {
    const host = $("#drop-comments-inline");
    const shell = host ? $(".comments-shell", host) : null;
    if (shell) el.comments.append(shell);
    if (host) host.hidden = true;
    if (state.comments.inlineDropId) {
      state.comments.inlineDropId = null;
      resetComments();
      renderComments();
    }
    document.body.classList.remove("comments-inline-open");
  }

  function resetComments() {
    rememberCommentDraft();
    state.comments.window?.read?.controller?.abort();
    state.comments.window = null;
    state.comments.requestId += 1;
    state.comments.dialogVersion = (state.comments.dialogVersion || 0) + 1;
    state.comments.loading = false;
    state.comments.submission = null;
    state.comments.pinMutation = null;
    state.comments.drop = null;
    state.comments.owner = null;
    state.comments.items = [];
    state.comments.nextCursor = null;
    state.comments.pinned = null;
    state.comments.replyTo = null;
    state.comments.totalCount = 0;
    state.comments.error = false;
    el.commentsBody.value = "";
    el.commentsSend.disabled = false;
    el.commentsSend.textContent = "Post";
    paintCommentReply();
  }

  async function submitComment(event) {
    event.preventDefault();
    if (!requireVerified() || !state.comments.drop) return;
    const comments = state.comments;
    if (comments.submission) return;
    const dropId = comments.drop.id;
    const dialogVersion = comments.dialogVersion;
    const viewerId = state.viewer.id;
    const session = state.messages, csrf = state.csrf;
    const owns = () => state.comments === comments && comments.dialogVersion === dialogVersion
      && comments.drop?.id === dropId && state.viewer?.id === viewerId
      && state.messages === session;
    const current = () => owns() && comments.submission === submission && state.csrf === csrf;
    const draft = el.commentsBody.value;
    const replyTo = comments.replyTo;
    const body = draft.trim();
    if (!body) return;
    const submission = comments.submission = {};
    hideError(el.commentsFormError);
    el.commentsSend.disabled = true;
    el.commentsSend.textContent = "Posting…";
    try {
      const comment = await api(`/api/drops/${encodeURIComponent(dropId)}/comments`, {
        method: "POST", csrf: true,
        json: { body, ...(replyTo ? { reply_to_id: replyTo.id } : {}) },
      });
      if (!current()) return;
      // Retire only the draft that was sent; a newer reply choice owns its text.
      if (el.commentsBody.value === draft && comments.replyTo === replyTo) {
        comments.replyTo = null;
        el.commentsBody.value = "";
        paintCommentReply();
      }
      const reload = comments.loading;
      if (comment.status === "pending_review") {
        toast("Comment sent for review");
      } else {
        if (!comments.items.some((item) => item.id === comment.id)) {
          comments.items = [...comments.items, comment];
          comments.totalCount += 1;
        }
        // Do not let a read started before this acknowledgement erase it.
        invalidateCommentRead(false);
        setDropCommentCount(dropId, comments.totalCount);
        toast("Comment posted");
      }
      renderComments();
      if (reload && comment.status !== "pending_review") await refreshComments();
    } catch (error) {
      if (current()) showError(el.commentsFormError, error.message);
    } finally {
      const owned = comments.submission === submission;
      if (owned) comments.submission = null;
      if (owned && owns()) {
        el.commentsSend.disabled = false;
        el.commentsSend.textContent = "Post";
      }
    }
  }

  async function deleteDropComment(comment) {
    if (!state.capabilities.community_writes) return;
    const comments = state.comments;
    const drop = comments.drop;
    if (!drop) return;
    const dialogVersion = comments.dialogVersion;
    const viewerId = state.viewer?.id;
    const session = state.messages, csrf = state.csrf;
    const current = () => state.comments === comments && comments.dialogVersion === dialogVersion
      && comments.drop?.id === drop.id && state.viewer?.id === viewerId
      && state.messages === session && state.csrf === csrf;
    if (!(await confirm("Delete this comment?")) || !current()) return;
    try {
      await api(`/api/drops/${encodeURIComponent(drop.id)}/comments/${encodeURIComponent(comment.id)}`, {
        method: "DELETE", csrf: true,
      });
      if (!current()) return;
      const reload = comments.loading;
      const wasPinned = comments.pinned?.id === comment.id;
      const wasPresent = wasPinned || comments.items.some((item) => item.id === comment.id);
      comments.items = comments.items.filter((item) => item.id !== comment.id);
      if (wasPinned) comments.pinned = null;
      state.comments.totalCount = Math.max(0, state.comments.totalCount - Number(wasPresent));
      invalidateCommentRead(false);
      setDropCommentCount(drop.id, state.comments.totalCount);
      renderComments();
      toast("Comment deleted");
      if (reload) void refreshComments();
    } catch (error) { if (current()) toast(error.message, true); }
  }

  function chatNearBottom() {
    return el.chatMessages.scrollHeight - el.chatMessages.scrollTop - el.chatMessages.clientHeight < 90;
  }

  function scrollChatBottom(behavior = "auto") {
    el.chatMessages.scrollTo({ top: el.chatMessages.scrollHeight, behavior });
    el.chatNew.hidden = true;
    state.chat.newWhileAway = 0;
  }

  function updateChatBadges() {
    const chat = chatWorkspace();
    const count = chat.roomUnread.size
      ? [...chat.roomUnread.values()].reduce((sum, value) => sum + value, 0)
      : Math.max(0, Number(chat.unreadCount) || 0);
    chat.unreadCount = count;
    $$('[data-chat-unread]').forEach((badge) => {
      badge.textContent = count > 99 ? "99+" : String(count);
      badge.hidden = count === 0;
    });
  }

  function cancelChatReply() {
    state.chat.replyTo = null;
    el.chatReply.hidden = true;
    el.chatReplyAuthor.textContent = "";
    el.chatReplyCopy.textContent = "";
  }

  function setChatReply(message) {
    if (!state.capabilities.community_writes) return;
    if (!requireVerified() || message.deleted) return;
    if ((message.room || "global") !== chatWorkspace().room) return;
    state.chat.replyTo = message;
    saveChatDraft();
    el.chatReplyAuthor.textContent = `@${message.author.handle}`;
    el.chatReplyCopy.textContent = message.body;
    el.chatReply.hidden = false;
    el.chatBody.focus();
  }

  function scrollToChatMessage(messageId) {
    const target = $(`[data-chat-message-id="${CSS.escape(messageId)}"]`, el.chatMessages);
    if (!target) { toast("That earlier message is outside the loaded history"); return; }
    target.scrollIntoView({ block: "center", behavior: motionBehavior() });
    target.classList.add("is-mentioned");
    setTimeout(() => target.classList.remove("is-mentioned"), 1600);
  }

  async function deleteChatMessage(message) {
    if (!state.capabilities.community_writes) return;
    if (!(await confirm("Delete this message from public Chat?"))) return;
    const context = chatContext();
    try {
      await api(`/api/chat/messages/${encodeURIComponent(message.id)}`, { method: "DELETE", csrf: true });
      if (!context.current()) return;
      const updated = { ...message, body: "", deleted: true };
      context.chat.mutations.set(message.id, { message: updated, version: ++context.chat.mutationVersion });
      if (context.selected()) {
        state.chat.items = state.chat.items.map((item) => item.id === message.id ? updated : item);
        if (state.chat.replyTo?.id === message.id) { cancelChatReply(); saveChatDraft(); }
        const top = el.chatMessages.scrollTop;
        renderChat(); el.chatMessages.scrollTop = top;
      }
      toast("Message deleted");
    } catch (error) { if (context.current()) toast(error.message, true); }
  }

  function openChatReport(message, surface = "chat") {
    if (!requireVerified()) return;
    state.chat.reportMessage = message;
    state.chat.reportSurface = surface;
    const comment = surface === "comment" && Boolean(state.comments.drop);
    const drop = comment ? state.comments.drop : null;
    openReport({
      id: message.id,
      title: comment ? "Report this comment" : "Report this message",
      endpoint: () => comment
        ? `/api/drops/${encodeURIComponent(drop.id)}/comments/${encodeURIComponent(message.id)}/reports`
        : `/api/chat/messages/${encodeURIComponent(message.id)}/reports`,
      track: () => track("report", null, { target_type: "chat_message", target_id: message.id }),
      receipt: () => `${comment ? "Comment" : "Message"} report added to the moderator queue`,
      done: () => { state.chat.reportMessage = null; state.chat.reportSurface = "chat"; },
    });
  }

  function chatMessageNode(message) {
    const row = create("article", "chat-message");
    row.dataset.chatMessageId = message.id;
    row.setAttribute("aria-label", `${accountAccessibleName(message.author)}, ${new Date(message.created_at).toLocaleString()}`);
    const mentioned = state.viewer && (message.mentions || []).some((user) => user.id === state.viewer.id);
    row.classList.toggle("is-mentioned", Boolean(mentioned));

    const avatarButton = create("button", "chat-avatar-button");
    avatarButton.type = "button";
    avatarButton.setAttribute("aria-label", `Open ${message.author.display_name}'s profile`);
    const avatar = create("span", "avatar");
    styleAvatar(avatar, message.author);
    avatarButton.append(avatar);
    avatarButton.addEventListener("click", () => openCreator(message.author));

    const main = create("div", "chat-message-main");
    const meta = create("div", "chat-message-meta");
    const author = accountNameNode(message.author, "button", "chat-author");
    author.type = "button";
    author.addEventListener("click", () => openCreator(message.author));
    const time = create("span", "", `${message.author.handle ? `@${message.author.handle} · ` : ""}${chatTime(message.created_at)}`);
    time.title = new Date(message.created_at).toLocaleString();
    meta.append(author, time);
    main.append(meta);

    if (message.reply_to) {
      const quote = create("button", "chat-reply-quote");
      quote.type = "button";
      const unavailable = message.reply_to.unavailable;
      const replyName = unavailable ? "Earlier message" : `@${message.reply_to.author?.handle || "unknown"}`;
      const replyBody = unavailable
        ? "Unavailable"
        : message.reply_to.deleted
          ? "Message deleted"
          : message.reply_to.body;
      quote.append(create("strong", "", replyName), create("span", "", replyBody));
      quote.addEventListener("click", () => scrollToChatMessage(message.reply_to.id));
      main.append(quote);
    }

    if (message.deleted) {
      main.append(create("p", "chat-deleted", message.removed_by_moderation ? "Removed by moderation" : "Message deleted"));
    } else {
      const body = create("p", "chat-message-body");
      appendChatText(body, message.body);
      main.append(body);
      const messageTranslation = translationControl(
        message.translation,
        { body: message.body },
        (fields) => { clear(body); appendChatText(body, fields.body); },
      );
      if (messageTranslation) main.append(messageTranslation);
    }

    const actions = create("div", "chat-message-actions");
    if (!message.deleted) {
      if (state.capabilities.community_writes) {
        const reply = create("button");
        reply.type = "button";
        reply.setAttribute("aria-label", `Reply to ${message.author.display_name}`);
        reply.append(icon("reply"));
        reply.addEventListener("click", () => setChatReply(message));
        actions.append(reply);
      }
      if (state.capabilities.community_writes && message.can_delete) {
        const remove = create("button");
        remove.type = "button";
        remove.setAttribute("aria-label", "Delete message");
        remove.append(icon("trash"));
        remove.addEventListener("click", () => deleteChatMessage(message));
        actions.append(remove);
      }
    }
    if (!message.removed_by_moderation && state.viewer && message.author.id !== state.viewer.id) {
      const report = create("button");
      report.type = "button";
      report.setAttribute("aria-label", `Report ${message.author.display_name}'s message`);
      report.append(icon("flag"));
      report.addEventListener("click", () => openChatReport(message));
      actions.append(report);
    }
    row.append(avatarButton, main, actions);
    return row;
  }

  function renderChat() {
    const chat = chatWorkspace();
    const rows = new Map($$(".chat-message", el.chatMessages).map((row) => [row.dataset.chatMessageId, row]));
    const dividers = new Map($$(".chat-day-divider", el.chatMessages).map((node) => [node.dataset.chatDay, node]));
    const nodes = [];
    let previous = null;
    for (const message of chat.items) {
      const day = new Date(message.created_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
      const previousDay = previous && new Date(previous.created_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
      if (day !== previousDay) {
        const divider = dividers.get(day) || create("div", "chat-day-divider", day);
        divider.dataset.chatDay = day;
        divider.setAttribute("role", "separator");
        divider.setAttribute("aria-label", day);
        nodes.push(divider);
      }
      let row = rows.get(message.id);
      if (!row || row.chatMessage !== message || row.chatViewerId !== state.viewer?.id) {
        row = chatMessageNode(message);
        row.chatMessage = message;
        row.chatViewerId = state.viewer?.id;
      }
      const grouped = previous && day === previousDay && previous.author.id === message.author.id
        && !previous.deleted && !message.deleted && !message.reply_to
        && new Date(message.created_at) - new Date(previous.created_at) < 5 * 60 * 1000;
      row.classList.toggle("is-grouped", Boolean(grouped));
      nodes.push(row);
      previous = message;
    }
    const retained = new Set(nodes);
    for (const child of [...el.chatMessages.children]) if (!retained.has(child)) child.remove();
    nodes.forEach((node, index) => {
      if (el.chatMessages.children[index] !== node) el.chatMessages.insertBefore(node, el.chatMessages.children[index] || null);
    });
    el.chatLoading.hidden = true;
    el.chatEmpty.hidden = chat.items.length > 0;
    $("h3", el.chatEmpty).textContent = chat.lens === "mentions" ? "No mentions here yet" : `Welcome to ${chatRoomInfo().label}`;
    $("p", el.chatEmpty).textContent = chat.lens === "mentions"
      ? "Messages that mention you in this room will appear here."
      : `${chatRoomInfo().description} Say hello to start the conversation.`;
    el.chatOlder.hidden = !chat.nextCursor;
  }

  function renderChatTyping() {
    if (!el.chatTyping) return;
    const now = Date.now();
    for (const [userId, entry] of state.chat.typingUsers) {
      if (entry.expiresAt <= now) state.chat.typingUsers.delete(userId);
    }
    const entries = [...state.chat.typingUsers.values()];
    if (!entries.length || state.view !== "chat") {
      el.chatTyping.hidden = true;
      el.chatTyping.textContent = "";
      return;
    }
    const names = entries.slice(0, 3).map((entry) => entry.user.display_name);
    const remaining = entries.length - names.length;
    const people = remaining > 0 ? `${names.join(", ")} and ${remaining} ${remaining === 1 ? "other" : "others"}` : names.join(", ");
    el.chatTyping.textContent = `${people} ${entries.length === 1 ? "is" : "are"} typing…`;
    el.chatTyping.hidden = false;
  }

  function scheduleChatTypingExpiry() {
    clearTimeout(state.chat.typingExpiryTimer);
    const expiries = [...state.chat.typingUsers.values()].map((entry) => entry.expiresAt);
    if (!expiries.length) { renderChatTyping(); return; }
    state.chat.typingExpiryTimer = setTimeout(() => {
      renderChatTyping();
      scheduleChatTypingExpiry();
    }, Math.max(20, Math.min(...expiries) - Date.now() + 20));
  }

  function receiveChatTyping(payload) {
    const user = payload?.user;
    if ((payload?.room || "global") !== chatWorkspace().room || state.view !== "chat" || payload?.state !== "typing"
      || !user?.id || !user?.handle || !user?.display_name || user.id === state.viewer?.id) return;
    const expiresIn = Math.max(500, Math.min(5000, Number(payload.expires_in_ms) || 4000));
    state.chat.typingUsers.set(user.id, { user, expiresAt: Date.now() + expiresIn });
    renderChatTyping(); scheduleChatTypingExpiry();
  }

  function stopChatTyping({ clearPeople = false } = {}) {
    clearTimeout(state.chat.typingHeartbeatTimer);
    state.chat.typingHeartbeatTimer = null;
    if (clearPeople) {
      clearTimeout(state.chat.typingExpiryTimer);
      state.chat.typingExpiryTimer = null;
      state.chat.typingUsers.clear();
      renderChatTyping();
    }
  }

  function canSendChatTyping() {
    return state.view === "chat"
      && Boolean(state.viewer?.email_verified)
      && state.viewer.status === "active" && !state.sessionRestricted
      && Boolean(state.csrf)
      && navigator.onLine
      && document.activeElement === el.chatBody
      && Boolean(el.chatBody.value.trim());
  }

  async function sendChatTypingHeartbeat() {
    const context = chatContext();
    const chat = context.chat;
    clearTimeout(chat.typingHeartbeatTimer);
    chat.typingHeartbeatTimer = null;
    if (!canSendChatTyping() || chat.typingRequestActive) return;
    const elapsed = Date.now() - chat.typingLastSentAt;
    if (elapsed < 2800) { chat.typingHeartbeatTimer = setTimeout(sendChatTypingHeartbeat, 2800 - elapsed); return; }
    chat.typingRequestActive = true;
    chat.typingLastSentAt = Date.now();
    let nextDelay = 3000;
    try {
      const response = await api(`/api/chat/typing?room=${encodeURIComponent(context.room)}`, { method: "POST", csrf: true });
      nextDelay = Math.max(2500, Math.min(3200, Number(response?.expires_in_ms || 4000) - 900));
    } catch (_) { /* Ephemeral typing never blocks a message. */ }
    finally {
      if (context.current()) {
        chat.typingRequestActive = false;
        if (context.selected() && canSendChatTyping()) chat.typingHeartbeatTimer = setTimeout(sendChatTypingHeartbeat, nextDelay);
      }
    }
  }

  function beginChatTyping() {
    if (!canSendChatTyping()) { stopChatTyping(); return; }
    sendChatTypingHeartbeat();
  }

  async function markChatRead(messageId = null) {
    const context = chatContext();
    if (!context.viewerId || !state.csrf || context.lens !== "all") return;
    const latest = messageId || context.chat.items.at(-1)?.id;
    if (!latest) return;
    const requestId = ++context.chat.readRequestId;
    const unreadEvents = context.chat.unreadEvents.get(context.room) || 0;
    const unreadBefore = context.chat.roomUnread.get(context.room) || 0;
    try {
      const result = await api(`/api/chat/read-state?room=${encodeURIComponent(context.room)}`, {
        method: "PUT", csrf: true, json: { message_id: latest },
      });
      if (!context.current() || requestId !== context.chat.readRequestId) return;
      const arrived = Math.max(0, (context.chat.unreadEvents.get(context.room) || 0) - unreadEvents);
      context.chat.roomUnread.set(context.room, Math.max(Number(result.unread_count) || 0, arrived));
      context.chat.unreadVersion.set(context.room, (context.chat.unreadVersion.get(context.room) || 0) + 1);
      updateChatBadges(); renderChatRooms();
      if (context.selected() && latest !== context.chat.lastTrackedRead) {
        context.chat.lastTrackedRead = latest;
        track("chat_read", null, { message_id: latest, messages_read: Math.max(1, unreadBefore) });
      }
    } catch (_) { /* Best-effort receipt; no change is claimed on failure. */ }
  }

  async function refreshChat(older = false) {
    const context = chatContext();
    const chat = context.chat;
    if (older && (chat.loading || !chat.nextCursor)) return;
    const requestId = ++chat.requestId;
    chat.controller?.abort();
    const controller = new AbortController();
    chat.controller = controller;
    const current = () => !controller.signal.aborted && context.selected() && requestId === chat.requestId;
    const mutationVersion = chat.mutationVersion;
    const unreadVersion = chat.unreadVersion.get(context.room) || 0;
    const cursor = older ? chat.nextCursor : null;
    const previousHeight = el.chatMessages.scrollHeight;
    const previousTop = el.chatMessages.scrollTop;
    const previousViewportTop = el.chatMessages.getBoundingClientRect?.().top || 0;
    chat.loading = true;
    chat.retryOlder = older;
    el.chatOlder.disabled = true;
    el.chatLoading.hidden = older;
    el.chatError.hidden = true;
    try {
      const params = new URLSearchParams({ room: context.room, lens: context.lens, limit: "60" });
      if (cursor) params.set("cursor", cursor);
      const result = await api(`/api/chat/messages?${params}`, { signal: controller.signal });
      if (!current()) return;
      if (!Array.isArray(result?.items) || result.items.some((message) => !message?.id || !message.author?.id
        || typeof message.body !== "string" || !Number.isFinite(Date.parse(message.created_at))
        || (message.room || "global") !== context.room)
        || (result.room && result.room !== context.room)
        || (result.next_cursor != null && (typeof result.next_cursor !== "string" || result.next_cursor === cursor))) {
        throw new Error("The conversation could not be read. Please retry.");
      }
      const known = new Set(older ? chat.items.map((item) => item.id) : []);
      const incoming = result.items.filter((item) => { if (known.has(item.id)) return false; known.add(item.id); return true; });
      chat.items = older ? [...incoming, ...chat.items] : incoming;
      const merged = new Map(chat.items.map((item) => [item.id, item]));
      for (const { message, version } of chat.mutations.values()) {
        if (version <= mutationVersion || (message.room || "global") !== context.room) continue;
        if (context.lens === "all" || (message.mentions || []).some((user) => user.id === context.viewerId) || merged.has(message.id)) merged.set(message.id, message);
      }
      chat.items = [...merged.values()].sort((a, b) => new Date(a.created_at) - new Date(b.created_at) || a.id.localeCompare(b.id));
      chat.nextCursor = result.next_cursor || null;
      chat.loaded = true;
      if (result.read_state && unreadVersion === (chat.unreadVersion.get(context.room) || 0)) chat.roomUnread.set(context.room, Number(result.read_state.unread_count) || 0);
      renderChat();
      updateChatBadges();
      renderChatRooms();
      if (older) el.chatMessages.scrollTop = previousTop + (el.chatMessages.scrollHeight - previousHeight)
        + ((el.chatMessages.getBoundingClientRect?.().top || 0) - previousViewportTop);
      else if (state.view === "chat") {
        scrollChatBottom();
        if (context.lens === "all") await markChatRead();
      }
    } catch (error) {
      if (!current()) return;
      if ([401, 403].includes(error.status)) {
        chat.items = []; chat.nextCursor = null; chat.loaded = false;
        clear(el.chatMessages); chat.roomUnread.clear(); chat.unreadCount = 0; updateChatBadges(); renderChatRooms();
      }
      el.chatLoading.hidden = true;
      el.chatError.hidden = false;
      $("p", el.chatError).textContent = error.message || "Please try again. Your draft is kept.";
      el.chatEmpty.hidden = true;
    } finally {
      if (current()) {
        chat.loading = false;
        el.chatOlder.disabled = false;
        el.chatLoading.hidden = true;
      }
      if (chat.controller === controller) chat.controller = null;
    }
  }

  async function refreshChatReadState() {
    const context = chatContext();
    if (!context.viewerId || state.sessionRestricted) {
      context.chat.unreadCount = 0; context.chat.roomUnread.clear(); updateChatBadges(); return;
    }
    await refreshChatRooms();
  }

  async function submitChat(event) {
    event.preventDefault();
    if (!requireVerified()) return;
    const context = chatContext();
    const chat = context.chat;
    if (chat.sending.has(context.room) || state.sessionRestricted || state.viewer.status !== "active") return;
    const original = el.chatBody.value;
    const body = original.trim();
    if (!body) return;
    const replyTo = chat.replyTo;
    saveChatDraft();
    const revision = chat.drafts.get(context.room)?.revision;
    hideError($("#chat-form-error"));
    chat.sending.add(context.room);
    updateChatComposer();
    try {
      const message = await api(`/api/chat/messages?room=${encodeURIComponent(context.room)}`, {
        method: "POST", csrf: true, json: { body, reply_to_id: replyTo?.id || null },
      });
      if (!context.current()) return;
      if (message?.accepted === true && message.status === "pending_review"
        && /^[0-9a-f-]{36}$/i.test(message.quarantine_id || "") && /^[0-9a-f-]{36}$/i.test(message.decision_id || "")) {
        const draft = chat.drafts.get(context.room);
        const unchanged = draft?.revision === revision;
        if (unchanged) chat.drafts.delete(context.room);
        if (unchanged && chat.room === context.room && el.chatBody.value === original && chat.replyTo?.id === replyTo?.id) {
          el.chatBody.value = ""; cancelChatReply(); closeEmotePicker(); stopChatTyping();
        }
        toast("Message submitted for review. It is not visible in the room yet.");
        return;
      }
      if (typeof message?.id !== "string" || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(message.id)
        || message.author?.id !== context.viewerId || typeof message.body !== "string"
        || !Number.isFinite(Date.parse(message.created_at)) || (message.room || "global") !== context.room) {
        throw new Error("The server did not confirm delivery. Your draft is kept; check the conversation before retrying.");
      }
      const draft = chat.drafts.get(context.room);
      const unchanged = draft?.revision === revision;
      if (unchanged) chat.drafts.delete(context.room);
      chat.mutations.set(message.id, { message, version: ++chat.mutationVersion });
      if (chat.mutations.size > 500) chat.mutations.delete(chat.mutations.keys().next().value);
      if (chat.room === context.room) {
        const showInLens = chat.lens === "all" || (message.mentions || []).some((user) => user.id === context.viewerId);
        if (showInLens) appendBounded(chat.items, message, 500);
        if (unchanged && el.chatBody.value === original && chat.replyTo?.id === replyTo?.id) {
          el.chatBody.value = "";
          cancelChatReply();
          closeEmotePicker();
        }
        stopChatTyping(); renderChat(); scrollChatBottom(motionBehavior());
        await markChatRead(message.id);
      } else toast(`Message sent in ${chat.rooms.find((room) => room.id === context.room)?.label || context.room}`);
      if (context.room === "global") appendFlowChat(message);
      track("chat_message", null, { message_id: message.id });
    } catch (error) {
      if (!context.current()) return;
      if (context.selected()) {
        const notice = $("#chat-form-error");
        showError(notice, messageDeliveryError(error));
      } else toast(`Message in ${chat.rooms.find((room) => room.id === context.room)?.label || context.room} was not confirmed. Your draft is kept.`, true);
    } finally {
      if (context.current()) {
        chat.sending.delete(context.room);
        updateChatComposer(); renderChatRooms();
        const notice = $("#chat-form-error");
        if (context.selected() && !notice.hidden) notice.scrollIntoView?.({ block: "nearest", behavior: "instant" });
      }
    }
  }

  async function applyChatEvent(type, messageId, eventRoom = "global") {
    const context = chatContext();
    const chat = context.chat;
    const eventVersion = ++chat.eventVersion;
    chat.eventRequests.set(messageId, eventVersion);
    if (chat.eventRequests.size > 500) chat.eventRequests.delete(chat.eventRequests.keys().next().value);
    const wasNearBottom = chatNearBottom();
    const previousTop = el.chatMessages.scrollTop;
    try {
      const message = await api(`/api/chat/messages/${encodeURIComponent(messageId)}`);
      if (!context.current() || chat.eventRequests.get(messageId) !== eventVersion) return;
      if (!message?.id || !message.author?.id || typeof message.body !== "string" || !Number.isFinite(Date.parse(message.created_at))) return;
      const room = message.room || "global";
      chat.mutations.set(message.id, { message, version: ++chat.mutationVersion });
      if (chat.mutations.size > 500) chat.mutations.delete(chat.mutations.keys().next().value);
      const known = chat.seenEvents.has(`${type}:${messageId}`);
      chat.seenEvents.add(`${type}:${messageId}`);
      if (chat.seenEvents.size > 500) chat.seenEvents.delete(chat.seenEvents.values().next().value);
      if (room === "global") {
        appendFlowChat(message);
        state.flow.chatItems.sort((a, b) => new Date(a.created_at) - new Date(b.created_at) || a.id.localeCompare(b.id));
        if (state.view === "flow" && state.flow.view === "now") renderFlowLiveChat();
      }
      const selected = context.selected() && chat.room === room;
      const inLens = chat.lens === "all" || (message.mentions || []).some((user) => user.id === context.viewerId);
      const index = chat.items.findIndex((item) => item.id === message.id);
      if (selected && (inLens || index >= 0)) {
        if (index >= 0) chat.items[index] = message;
        else appendBounded(chat.items, message, 500);
        chat.items.sort((a, b) => new Date(a.created_at) - new Date(b.created_at) || a.id.localeCompare(b.id));
        if (chat.typingUsers.delete(message.author.id)) { renderChatTyping(); scheduleChatTypingExpiry(); }
        if (chat.loaded || state.view === "chat") renderChat();
        el.chatMessages.scrollTop = previousTop;
      }
      const fromOther = message.author.id !== context.viewerId;
      if (type === "chat.message.created" && fromOther && !known) {
        if (selected && state.view === "chat" && wasNearBottom && chat.lens === "all") {
          scrollChatBottom(motionBehavior()); await markChatRead(message.id);
        } else {
          if (context.viewerId) {
            chat.roomUnread.set(room, (chat.roomUnread.get(room) || 0) + 1);
            chat.unreadEvents.set(room, (chat.unreadEvents.get(room) || 0) + 1);
            chat.unreadVersion.set(room, (chat.unreadVersion.get(room) || 0) + 1);
          }
          if (selected && inLens) {
            chat.newWhileAway += 1;
            el.chatNew.textContent = `${chat.newWhileAway} new ${chat.newWhileAway === 1 ? "message" : "messages"}`;
            el.chatNew.hidden = state.view !== "chat";
          }
        }
      }
      updateChatBadges(); renderChatRooms();
    } catch (_) {
      if (!context.current() || chat.eventRequests.get(messageId) !== eventVersion) return;
      if (type === "chat.message.deleted") {
        const existing = chat.items.find((item) => item.id === messageId) || state.flow.chatItems.find((item) => item.id === messageId);
        if (existing) chat.mutations.set(messageId, { message: { ...existing, body: "", deleted: true }, version: ++chat.mutationVersion });
        if (context.selected() && context.room === eventRoom) {
          chat.items = chat.items.map((item) => item.id === messageId ? { ...item, body: "", deleted: true } : item);
          if (chat.loaded || state.view === "chat") { renderChat(); el.chatMessages.scrollTop = previousTop; }
        }
        if (eventRoom === "global") {
          state.flow.chatItems = state.flow.chatItems.map((item) => item.id === messageId ? { ...item, body: "", deleted: true } : item);
          if (state.view === "flow" && state.flow.view === "now") renderFlowLiveChat();
        }
      }
    }
  }

  async function applyDropCommentEvent(payload) {
    if (!payload.drop_id) return;
    const viewerId = state.viewer?.id || null, session = state.messages, csrf = state.csrf;
    try {
      const drop = await api(`/api/drops/${encodeURIComponent(payload.drop_id)}`);
      if ((state.viewer?.id || null) !== viewerId || state.messages !== session || state.csrf !== csrf || drop?.id !== payload.drop_id) return;
      replaceDropEverywhere(drop);
      if (state.view === "flow") renderFlow();
      else if (state.view === "discover") renderDiscover();
      else if (state.view === "profile") renderProfileView();
      else renderFeed();
      if ((el.comments.open || state.comments.inlineDropId === payload.drop_id) && state.comments.drop?.id === payload.drop_id) {
        invalidateCommentRead();
        await refreshComments();
      }
    } catch (_) { /* the Drop may no longer be visible to this viewer */ }
  }

  function updateSurfaceChrome() {
    const shared = state.view === "drop";
    const personal = ["saved", "mine", "following", "notifications", "messages"].includes(state.view);
    document.body.classList.toggle("is-shared-drop", shared);
    document.body.classList.toggle("is-personal-view", personal);
    const intro = $("#home-intro");
    if (intro) intro.hidden = state.view !== "all";
    if (intro && !intro.hidden) {
      const guide = $("#home-intro [data-open-guide]");
      if (guide) guide.textContent = state.viewer ? "Get started" : "How it works";
      if (!state.viewer) renderGuestIntroCopy(intro);
      else {
        const copy = $("#home-intro p");
        if (copy) copy.hidden = true;
        const join = $("#home-join");
        if (join) join.hidden = true;
      }
    }
    const scope = $("#feed-scope");
    if (scope) {
      scope.hidden = !["all", "following", "topics", "unseen"].includes(state.view);
      // Guests get the shared lenses; Following and Topics need an account.
      for (const personal of ["following", "topics"]) {
        const lens = $(`#feed-scope [data-view="${personal}"]`);
        if (lens) lens.hidden = !state.viewer;
      }
    }
    paintFeedOrder();
    paintSaveFolders();
    $$('[data-room-chrome]').forEach((node) => {
      const homeOnly = node.id === "board" || node.classList?.contains?.("mobile-search-wrap")
        || node.classList?.contains?.("room-tools-mobile");
      node.hidden = homeOnly ? state.view !== "all" : shared || personal || state.view === "profile";
    });
    // The live room layer (activity, Mic, event) is for members; guests get the feed and Discover.
    $$(".room-tools-mobile, .room-now-desktop").forEach((node) => { if (!state.viewer) node.hidden = true; });
    void state.startupSupport?.refresh();
  }

  function stopInactiveBrowseWork(view) {
    // Invalidate before aborting: even a response already queued for delivery must
    // not repaint or focus a page the reader has left. Keep loaded cards for Back.
    state.feedWindows?.leave();
    state.feedRetry = null;
    state.feedBusy = false;
    state.feedController?.abort();
    state.feedController = null;
    state.feedLoadingMore = false;
    if (view !== "chat" && state.chat) {
      state.chat.entryId = (state.chat.entryId || 0) + 1;
      state.chat.requestId = (state.chat.requestId || 0) + 1;
      state.chat.controller?.abort();
      state.chat.controller = null;
      state.chat.loading = false;
      el.chatLoading.hidden = true;
      el.chatOlder.disabled = false;
    }
    // Initial routing to Home must still consume its already-started read.
    if (view !== "all" || !state.initialHomeFeed?.current()) {
      state.initialHomeFeed?.controller?.abort();
      state.initialHomeFeed = null;
    }
    if (view !== "profile" && state.profile) {
      state.socialExtras?.leaveProfileReposts?.();
      state.profile.requestId = (state.profile.requestId || 0) + 1;
      state.profile.controller?.abort();
      state.profile.controller = null;
      state.profile.loading = false;
      state.profile.postsPending = false;
    }
    if (view !== "discover" && state.search) {
      state.search.requestId = (state.search.requestId || 0) + 1;
      state.search.controller?.abort();
      state.search.controller = null;
      state.search.retry = null;
      if (el.discoverLoading) el.discoverLoading.hidden = true;
      el.discoverPeople?.setAttribute("aria-busy", "false");
    }
    if (view !== "discover" && state.discovery) {
      state.discovery.requestId = (state.discovery.requestId || 0) + 1;
      state.discovery.controller?.abort();
      state.discovery.controller = null;
    }
    if (view === "flow") return;
    state.flow.requestId = (state.flow.requestId || 0) + 1;
    state.flow.controller?.abort();
    state.flow.controller = null;
    state.flow.loading = false;
    if (el.flowLoading) el.flowLoading.hidden = true;
    stopFlowTimers();
    state.flow.observer?.disconnect();
    state.flow.activeDropId = null;
    state.flow.activeVideo = null;
    state.flow.renderedView = null;
    state.flow.previewObserver?.disconnect();
    for (const video of state.flow.previewQueue || []) video.flowPreviewQueued = false;
    state.flow.previewQueue = [];
    state.flow.previewActive?.cancel();
    cancelAnimationFrame(state.flow.resizeFrame);
    state.flow.resizeFrame = null;
    $$("video, audio", el.flowSection).forEach((media) => media.pause());
  }

  function setView(view) {
    if (state.viewer && (state.ageAttestationRequired || state.ageRequirementNotMet)
        && !["settings", "messages"].includes(view)) {
      history.replaceState({}, "", "/settings/profile");
      setAccountSection("profile", false);
      view = "settings";
    }
    if (view !== "profile") state.connectionsPanel?.close({ navigation: true });
    if (view !== "notifications") state.followRequests?.close({ navigation: true });
    state.storiesV2?.cancelViewer?.();
    state.live?.cancelWatch?.();
    state.sendDropSession?.close();
    state.savedFolderControls?.close();
    state.sharedCollections?.close();
    state.postArchive?.close();
    state.coauthors?.navigate();
    state.originalSounds?.close();
    state.videoRemixControls?.close();
    state.videoRemixRemoval?.close();
    globalThis.LouderrVideoRemixStudio?.close();
    resetRelatedDrops();
    if (state.comments?.inlineDropId && typeof unmountInlineComments === "function") unmountInlineComments();
    if (view === "flow" && !state.capabilities.flow) view = "all";
    if (["advertise", "premium"].includes(view) && !state.capabilities.commerce) view = "all";
    if (state.view !== view) clearBusinessAds();
    if (state.view !== view) resetMessageSearch();
    state.routeRequestId = (state.routeRequestId || 0) + 1;
    closeEmotePicker();
    if (view !== "messages") {
      state.messageSearch?.close();
      state.messageShared?.close();
      state.messageAlbums?.close();
      state.messages.searchWindow = null;
      globalThis.LouderrVoiceNote?.cancel();
      state.messages.threadRequestId += 1;
      state.messages.selectionVersion += 1;
    }
    if (view !== "profile") {
      for (const tile of Array.from(el.profileDropGrid?.children || [])) {
        if (tile.releaseProfilePreview) {
          tile.releaseProfilePreview();
          tile.remove();
        }
      }
    }
    if (view !== "chat") stopChatTyping({ clearPeople: true });
    stopInactiveBrowseWork(view);
    const previousView = state.view;
    state.view = view;
    state.groupInvites?.sync();
    state.messagePolls?.sync();
    globalThis.louderrSoundPlayback?.syncScope();
    globalThis.LouderrVoicePlayback?.layout();
    state.feedRequestId += 1;
    state.feedLoadingMore = false;
    state.authorId = null;
    document.body.dataset.view = view;
    globalThis.LouderrHome?.show(view === "all" ? "home" : view === "creative" ? "creative" : false);
    if (view === "all" && typeof refreshHomeStories === "function") refreshHomeStories();
    if (view === "all" && typeof refreshHomeQuestion === "function") refreshHomeQuestion();
    $$("[data-open-self-profile]").forEach((button) => {
      button.classList.remove("is-active");
      button.removeAttribute("aria-current");
    });
    updateSurfaceChrome();
    const chosenKinds = !state.filter || state.filter === "all" ? [] : state.filter.split(",").filter(Boolean);
    $$('[data-filter]').forEach((button) => {
      const active = chosenKinds.length ? chosenKinds.includes(button.dataset.filter) : button.dataset.filter === "all";
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
    });
    $$("button[data-view]").forEach((button) => {
      // Following and the unseen lens are lenses on Home, so the Home entries in the navigation stay lit while one is open.
      const active = button.dataset.view === view
        || (["following", "topics", "unseen"].includes(view) && button.dataset.view === "all" && Boolean(button.closest?.(".primary-nav, .mobile-bottom-nav")));
      button.classList.toggle("is-active", active);
      if (active) button.setAttribute("aria-current", "page");
      else button.removeAttribute("aria-current");
    });
    const isTrending = view === "trending";
    const isChat = view === "chat";
    const isDiscover = view === "discover";
    const isNotifications = view === "notifications";
    const isMessages = view === "messages";
    const isFlow = view === "flow";
    const isCreative = view === "creative";
    const isSettings = view === "settings";
    const isBusiness = view === "advertise" || view === "premium";
    const isStandalone = isTrending || isChat || isDiscover || isNotifications || isMessages || isFlow || isBusiness || isCreative || isSettings;
    $("#advertiser-view").hidden = view !== "advertise";
    $("#premium-view").hidden = view !== "premium";
    const creativeSection = $("#creative"); if (creativeSection) creativeSection.hidden = !isCreative;
    if (el.settings) el.settings.hidden = !isSettings;
    el.pageContext.hidden = isStandalone;
    syncMessageThreadClass();
    placeToast();
    el.trendingSection.hidden = !isTrending;
    el.chatSection.hidden = !isChat;
    el.discoverSection.hidden = !isDiscover;
    el.notificationsSection.hidden = !isNotifications;
    el.messagesSection.hidden = !isMessages;
    el.flowSection.hidden = !isFlow;
    el.profileSection.hidden = true;
    const isSharedDrop = view === "drop";
    el.boardSection.hidden = view !== "all";
    el.feedSection.hidden = isStandalone;
    el.categoryRail.hidden = isStandalone || isSharedDrop;
    el.categoryStrip.hidden = isStandalone || isSharedDrop;
    // Home only, the same rule as updateSurfaceChrome: Back to /topics must not show it when a load hides it.
    el.mobileSearch.hidden = view !== "all";
    el.live.hidden = isSharedDrop;
    el.pendingBanner.hidden = true;
    state.pending = [];
    if (isSharedDrop) {
      state.drops = [];
      state.nextCursor = null;
      clear(el.feed);
      el.empty.hidden = true;
    }
    if (isBusiness) {
      const title = view === "advertise" ? "Advertise" : "Premium";
      document.title = `${title} · ${state.productName}`;
      void mountBusinessSurface(view, $(view === "advertise" ? "#advertiser-view" : "#premium-view"));
      return;
    }
    if (isFlow) {
      if (!state.capabilities.media_publication) state.flow.view = "feed";
      el.pageTitle.textContent = "Video";
      el.pageDescription.textContent = state.capabilities.media_publication
        ? "One public video order for everyone."
        : "One shared Drop queue. Newest first during beta.";
      document.title = `Video · ${state.productName}`;
      renderFlowTabs();
      return resumeFlow();
    }
    if (isCreative) {
      el.pageTitle.textContent = "Creative";
      el.pageDescription.textContent = "Projects, Studio and the work behind your Drops.";
      document.title = `Creative · ${state.productName}`;
      return;
    }
    if (isChat) {
      el.pageTitle.textContent = "Chat";
      el.pageDescription.textContent = "";
      document.title = `Chat · ${state.productName}`;
      el.chatNew.hidden = true;
      return enterChat();
    }
    if (isTrending) {
      el.pageTitle.textContent = "Trending";
      el.pageDescription.textContent = "What people are talking about now.";
      document.title = `Trending · ${state.productName}`;
      return refreshTrending();
    }
    if (isDiscover) {
      updateDiscoverContext();
      return refreshDiscover();
    }
    if (isNotifications) {
      el.pageTitle.textContent = "Notifications";
      el.pageDescription.textContent = "Replies, mentions and account activity.";
      document.title = `Notifications · ${state.productName}`;
      const primary = refreshNotifications(false);
      refreshJourneyUpdates();
      state.coauthors?.refreshInbox();
      return primary;
    }
    if (isMessages) {
      el.pageTitle.textContent = "Messages";
      el.pageDescription.textContent = "Private one-to-one conversations, separate from the public Chat.";
      document.title = `Messages · ${state.productName}`;
      return refreshConversations();
    }
    if (isSettings) {
      document.title = `${accountSectionLabels[state.accountSection] || "Settings and privacy"} · ${state.productName}`;
      return;
    }
    const labels = {
      all: ["Latest Drops", "Newest first."],
      following: ["Following", "Drops from people you follow, newest first."],
      topics: ["Topics you follow", "Drops under the topics you subscribed to. You chose every one of them."],
      unseen: ["Nobody has seen this yet", "Drops still waiting for their first qualified accounts. Nothing here is ranked for you."],
      saved: ["Saved Drops", "Drops you saved for later."],
      mine: ["My Drops", "Everything you have shared."],
      drop: ["Drop", "Shared with the room."],
      topic: [state.topic ? `#${state.topic}` : "Topic", "Drops under this topic, newest first."],
    };
    const [viewTitle, viewDescription] = labels[view] || labels.all;
    el.pageTitle.textContent = view === "all" ? "Home" : viewTitle;
    el.pageDescription.textContent = view === "all" ? "Browse the public feed." : viewDescription;
    [el.title.textContent, el.subtitle.textContent] = [viewTitle, viewDescription];
    requestAnimationFrame(updateCategoryScrollControls);
    document.title = view === "all"
      ? state.productName
      : `${viewTitle} · ${state.productName}`;
    return refreshFeed();
  }

  async function toggleSignal(dropId, kind, source = null) {
    if (!state.capabilities.community_writes) { writesPausedNotice(); return; }
    if (!requireVerified()) return;
    const signalOwner = state.viewer?.id, signalScope = state.messages, signalCsrf = state.csrf;
    const current = () => signalOwner === state.viewer?.id && signalScope === state.messages && signalCsrf === state.csrf;
    const signalPath = location.pathname, signalView = state.view;
    const analyticsMetadata = surfaceMetadata(source);
    const drop = state.socialExtras?.profileRepost?.(dropId)
      || state.drops.find((item) => item.id === dropId)
      || state.search.drops.find((item) => item.id === dropId)
      || state.discovery.items.find((item) => item.id === dropId)
      || state.profile.drops.find((item) => item.id === dropId)
      || state.flow.items.find((item) => item.id === dropId)
      || (state.flow.now?.current?.drop?.id === dropId ? state.flow.now.current.drop : null)
      || state.flow.stories.find((item) => item.drop?.id === dropId)?.drop
      || (state.board?.drop?.id === dropId ? state.board.drop : null);
    const key = kind === "applaud" ? "applauded" : kind === "save" ? "saved" : "nominated";
    const active = Boolean(drop?.viewer?.[key]);
    if (!active) haptic(kind === "applaud" ? 8 : 5);
    try {
      const result = active
        ? await api(`/api/drops/${dropId}/signals/${kind}`, { method: "DELETE", csrf: true })
        : await api(`/api/drops/${dropId}/signals`, { method: "POST", csrf: true, json: { kind } });
      if (!current()) return;
      if (result?.drop === null) {
        // Withdrawing a signal stays valid after the post becomes unreadable.
        // A successful null projection retires the post; it is never a list item.
        removeDrop(dropId);
        if (state.board?.drop?.id === dropId) state.board.drop = null;
        if (state.view === "discover") renderDiscover();
        if (state.view === "profile") renderProfileView({ refreshExtras: false });
        if (state.view === "flow") refreshFlowControls();
        renderBoard();
        refreshTrendingPreview();
        if (state.view === "trending") refreshTrending();
        scheduleActiveSurfaceRefresh("drop");
        scheduleFlowRefresh("drop");
        if (kind === "save") {
          void refreshSaveFolders();
          if (state.view === "saved") void refreshFeed();
        }
        return;
      }
      if (result?.drop?.id !== dropId) throw new Error("The change could not be confirmed. Refresh this post before trying again.");
      state.feedWindows?.patch(dropId, result.drop);
      // Mutation acknowledgements update present cards; the destination read owns feed membership.
      const feedIndex = state.drops.findIndex((item) => item.id === dropId);
      if (feedIndex >= 0) state.drops[feedIndex] = result.drop;
      if (!active && result.signal_authority && result.signal_authority.counted_publicly === false) {
        const noun = kind === "applaud" ? "Applause" : kind === "save" ? "Save" : "Nomination";
        toast(`${noun} recorded. Public counts include only established accounts.`);
      }
      const searchIndex = state.search.drops.findIndex((item) => item.id === dropId);
      if (searchIndex >= 0) state.search.drops[searchIndex] = result.drop;
      const discoveryIndex = state.discovery.items.findIndex((item) => item.id === dropId);
      if (discoveryIndex >= 0) {
        const existing = state.discovery.items[discoveryIndex];
        state.discovery.items[discoveryIndex] = {
          ...result.drop,
          allocation_id: existing.allocation_id,
          guaranteed_exposure: existing.guaranteed_exposure,
          rank: existing.rank,
          movement: existing.movement,
          score_points: existing.score_points,
          score_components: existing.score_components,
        };
      }
      const profileIndex = state.profile.drops.findIndex((item) => item.id === dropId);
      if (profileIndex >= 0) state.profile.drops[profileIndex] = result.drop;
      const flowIndex = state.flow.items.findIndex((item) => item.id === dropId);
      if (flowIndex >= 0) state.flow.items[flowIndex] = { ...state.flow.items[flowIndex], ...result.drop };
      if (state.flow.now?.current?.drop?.id === dropId) {
        state.flow.now.current.drop = { ...state.flow.now.current.drop, ...result.drop };
      }
      state.flow.stories = state.flow.stories.map((story) => story.drop?.id === dropId
        ? { ...story, drop: { ...story.drop, ...result.drop } }
        : story);
      if (state.board?.drop?.id === dropId) state.board.drop = result.drop;
      if (feedIndex >= 0) renderFeed();
      if (state.view === "discover") renderDiscover();
      if (state.view === "profile") renderProfileView();
      if (state.view === "flow") refreshFlowControls();
      renderBoard();
      refreshTrendingPreview();
      if (state.view === "trending") refreshTrending();
      if (kind === "save") {
        state.feedWindows?.invalidate({ view: "saved", removeId: active ? dropId : null });
        void refreshSaveFolders();
        if (state.view === "saved") void refreshFeed();
        if (!active && signalPath === location.pathname && signalView === state.view && !$("dialog[open]")) {
          state.savedFolderControls?.show(result.drop, source);
        }
      }
      if (!active) {
        const eventType = kind === "applaud" ? "applause" : kind === "nominate" ? "nomination" : kind;
        track(eventType, dropId, analyticsMetadata);
      }
    } catch (error) { if (current()) toast(error.message, true); }
  }

  function requireVerified({ announce = true } = {}) {
    if (!state.viewer) { openDialog(el.account); if (announce) toast("Sign in to participate"); return false; }
    if (state.ageAttestationRequired || state.ageRequirementNotMet) {
      openAccountDialog("profile");
      if (announce) toast(state.ageAttestationRequired ? "Confirm your date of birth first." : "Your account tools remain available.");
      return false;
    }
    if (!state.viewer.email_verified) { openAccountDialog("profile"); if (announce) toast("Verify your email first"); return false; }
    return true;
  }

  let mediaStudio = null;
  let mediaStudioOpening = false;
  let mediaUploadManager = null;
  function mediaUploads() {
    if (!mediaUploadManager) {
      if (!window.LobbyUploads) throw new Error("The upload controls did not load. Reload this page before uploading.");
      mediaUploadManager = window.LobbyUploads.createUploadManager({
        api, upload: uploadFile, owner: () => state.viewer?.id || null,
        visible: () => document.visibilityState !== "hidden", changed: uploadChanged,
        randomUUID: () => crypto.randomUUID(),
      });
    }
    return mediaUploadManager;
  }

  function uploadStatusText(entry) {
    if (entry.error) return entry.error;
    return ({ selected: "Ready to upload. Media is checked before publication.", uploading: "Uploading privately…",
      receiving: "Receiving your upload…", queued: "Queued for safety checks.", processing: "Checking and sanitizing media…",
      retry_wait: "Processing is delayed; a bounded retry is scheduled.", awaiting_review: "Waiting for a safety review.",
      succeeded: "Ready. Choose Publish or Save to use this media.", rejected: "This media was not approved. Select another file.",
      failed: "Processing could not finish. You can explicitly retry.", expired: "The upload expired. Check or retry it.",
      cancelled: "Upload cancelled.", response_lost: "Upload receipt was lost. Check its status before retrying.",
    })[entry.state] || "Upload status is unavailable.";
  }

  // How long Publish waits for media the stack is still uploading, scanning or sanitizing before
  // handing the draft back. A human review or a failure is never waited out.
  const MEDIA_READY_WAIT_MS = 180000;
  const MEDIA_READY_POLL_MS = 1500;

  function mediaWaitDecision(entries, elapsedMs, limitMs) {
    const automatic = new Set(["uploading", "receiving", "queued", "processing", "retry_wait"]);
    if (entries.every(entry => entry.state === "succeeded")) return "ready";
    if (entries.some(entry => entry.error || (entry.state !== "succeeded" && !automatic.has(entry.state)))) return "stop";
    return elapsedMs < limitMs ? "wait" : "stop";
  }

  function galleryUploadContext(entry = null) {
    return { surface: "gallery", selection: state.gallery, ownerId: state.viewer?.id,
      epoch: state.composerDraft?.epoch, entry, alertRevision: el.formError.alertRevision || 0 };
  }

  function avatarUploadContext(selection) {
    return { surface: "avatar", selection, ownerId: selection.entry.ownerId, entry: selection.entry,
      alertRevision: el.profileSaveError.alertRevision || 0 };
  }

  function uploadContextCurrent(context) {
    if (context.ownerId !== state.viewer?.id) return false;
    if (context.surface === "gallery") return context.selection === state.gallery && context.epoch === state.composerDraft?.epoch;
    return context.selection === profileAvatarUpload
      && el.profileAvatarInput.files[0] === context.selection.file;
  }

  function mediaPendingMessage(context, items = context.selection.items) {
    const entries = context.surface === "gallery" ? items.map(item => item.upload).filter(Boolean) : [context.entry];
    const attention = entries.some(entry => entry.error || ["selected", "failed", "expired", "cancelled", "rejected", "verification_lost"].includes(entry.state));
    const humanReview = entries.some(entry => entry.state === "awaiting_review");
    if (context.surface === "gallery") return attention
      ? "Some media needs attention. Check each item's status before choosing Publish again."
      : humanReview
        ? "A person is reviewing your photo. Publishing waits for that decision; keep this tab open or come back later and choose Publish again."
        : "Media is still being checked. Your upload IDs are saved in this tab. Choose Publish again after every item is ready.";
    return attention ? "Your avatar needs attention. Check its upload status before choosing Save again. Your profile edits are kept."
      : "Your avatar is being checked, not published. Your profile edits are kept. Choose Save again when it is ready.";
  }

  function showUploadError(node, error, context = error.uploadAlert, recoverable = true) {
    // A response belongs to the alert state at invocation, not to an older
    // rendered row or a newer unrelated authorization/validation message.
    if (context && (context.alertRevision !== (node.alertRevision || 0) || !uploadContextCurrent(context)
      || (context.surface === "gallery" && context.entry && !context.selection.items.some(item => item.upload === context.entry)))) return;
    showError(node, error.message);
    // HTTP authorization/validation failures and uncertain cancellation are not
    // receipts: a later upload status must never dismiss those form errors.
    if (!context || !recoverable || (error.status && error.status < 500 && ![408, 429].includes(error.status))) return;
    node.mediaAlert = { ...context, kind: context.kind || "recovery" };
    reconcileMediaAlert(node);
  }

  function reconcileMediaAlert(node, removedEntry = null) {
    const context = node.mediaAlert;
    if (!context || node.hidden || !uploadContextCurrent(context)) return;
    if (context.kind === "recovery" && context.entry !== removedEntry && (context.entry?.error || !context.entry?.id)) return;
    if (context.surface === "gallery") {
      const items = context.selection.items.filter(item => !removedEntry || item.upload !== removedEntry);
      if (!items.length || items.every(item => item.id && (!item.upload || item.upload.state === "succeeded"))) { hideError(node); return; }
      node.textContent = mediaPendingMessage(context, items);
    } else {
      if (context.entry === removedEntry || (context.entry.state === "succeeded" && !context.entry.error)) { hideError(node); return; }
      node.textContent = mediaPendingMessage(context);
    }
    node.mediaAlert = { ...context, kind: "pending", entry: context.surface === "gallery" ? null : context.entry };
  }

  function renderGalleryUploadLabel() {
    delete el.uploadLabel.mediaPreparation;
    const count = state.gallery.items.length;
    el.uploadLabel.textContent = count ? `${count} ${count === 1 ? "item" : "items"} selected`
      : "Up to 35 photos or videos, or one audio file";
  }

  function applyReadyUpload(item, entry) {
    if (entry.id) item.id = entry.id;
    if (entry.state !== "succeeded") return;
    const uploaded = entry.result;
    item.existing = true; item.file = null;
    item.url = uploaded.media_url; item.thumbnailUrl = uploaded.thumbnail_url;
    item.mediaType = uploaded.content_type; item.originalAlt = uploaded.alt_text || "";
  }

  function updateUploadButton(button, entry, action, label) {
    const localCancel = action === "cancel" && ["selected", "succeeded", "rejected"].includes(entry.state);
    const delay = localCancel ? 0 : mediaUploads().remaining(entry, action);
    const wait = delay > 86400000 ? "more than a day" : delay > 3600000 ? `${Math.ceil(delay / 3600000)}h`
      : delay > 60000 ? `${Math.ceil(delay / 60000)}m` : `${Math.ceil(delay / 1000)}s`;
    const text = delay > 0 ? `${label} in ${wait}` : label;
    if (button.textContent !== text) button.textContent = text;
    button.disabled = delay > 0 || !!entry.cancelling || (action !== "cancel" && !!(entry.pending || entry.retrying));
  }

  function updateAvatarUploadButtons(entry) {
    const host = $(".avatar-upload-status", el.profileAvatarField);
    if (!host) return;
    for (const [action, label] of [["check", "Check status"], ["retry", "Retry upload"], ["cancel", "Cancel selection"]]) {
      const button = $(`.upload-job-${action}`, host);
      if (button) updateUploadButton(button, entry, action, label);
    }
  }

  function uploadChanged(entry, { controlsOnly = false } = {}) {
    // The composer holds at most one attachment; repaint it when its upload moves.
    if (entry === state.messageAttachment) paintMessageAttachment();
    const galleries = new Set([state.gallery, ...[...state.composerDraft.saved.values()].map(saved => saved.gallery)]);
    for (const gallery of galleries) for (const item of gallery.items) if (item.upload === entry) applyReadyUpload(item, entry);
    state.gallery.items.forEach((item, index) => {
      if (item.upload !== entry) return;
      const row = $(`[data-gallery-index="${index}"]`, el.dropGallery);
      if (!row) return;
      const status = $(".upload-job-status", row);
      if (status && status.textContent !== uploadStatusText(entry)) status.textContent = uploadStatusText(entry);
      const check = $(".upload-job-check", row);
      if (check) { check.hidden = ["selected", "succeeded", "rejected"].includes(entry.state); updateUploadButton(check, entry, "check", "Check status"); }
      const retry = $(".upload-job-retry", row);
      if (retry) { retry.hidden = !["response_lost", "failed", "cancelled", "expired"].includes(entry.state); updateUploadButton(retry, entry, "retry", "Retry upload"); }
      const discard = $(".upload-job-discard", row); if (discard) discard.hidden = !entry.error;
    });
    if (state.gallery.items.some(item => item.upload === entry)) {
      reconcileMediaAlert(el.formError);
      const edit = $(".gallery-studio", $("[data-gallery-index=\"" + state.gallery.items.findIndex(item => item.upload === entry) + "\"]", el.dropGallery));
      if (edit) edit.disabled = !["selected", "succeeded"].includes(entry.state);
      renderComposerStep();
    }
    if (profileAvatarUpload?.entry === entry) {
      if (controlsOnly) updateAvatarUploadButtons(entry);
      else { reconcileMediaAlert(el.profileSaveError); renderAvatarUploadStatus(); }
    }
  }

  function renderAvatarUploadStatus() {
    let host = $(".avatar-upload-status", el.profileAvatarField);
    if (!host) { host = create("div", "avatar-upload-status"); el.profileAvatarField.append(host); }
    clear(host);
    const selection = profileAvatarUpload;
    if (!selection) return;
    const { entry } = selection;
    const context = avatarUploadContext(selection);
    const saving = profileSaveBusy?.selection === selection && !profileSaveBusy.cancelled;
    const statusText = saving && ["uploading", "receiving", "queued", "processing", "retry_wait", "succeeded"].includes(entry.state)
      ? "Checking your photo. Your profile will save when it is ready." : uploadStatusText(entry);
    const status = create("p", "metric-note", statusText); status.setAttribute("role", "status");
    host.append(status);
    for (const [label, operation] of [
      ["Check status", () => mediaUploads().check(entry)],
      ...(["response_lost", "failed", "cancelled", "expired"].includes(entry.state)
        ? [["Retry upload", () => mediaUploads().retry(entry)]] : []),
      ["Cancel selection", async () => {
        if (!["selected", "succeeded", "rejected"].includes(entry.state)
          && !await mediaUploads().cancel(entry)) return;
        if (!uploadContextCurrent(context)) return;
        reconcileMediaAlert(el.profileSaveError, entry);
        mediaUploads().detach(entry); profileAvatarUpload = null;
        if (el.profileAvatarInput.files[0] === selection.file) el.profileAvatarInput.value = "";
        renderProfileAvatarControl(); renderAvatarUploadStatus();
      }],
      ...(entry.error ? [["Discard selection locally", async () => {
        const ownerId = state.viewer?.id;
        if (profileAvatarUpload !== selection || entry.ownerId !== ownerId) return;
        if (!(await confirm("Server cancellation is unconfirmed. Private processing may continue. This only discards the selection in this tab and does not publish it. Discard locally?", { confirmLabel: "Discard" }))) return;
        if (state.viewer?.id !== ownerId || profileAvatarUpload !== selection) return;
        mediaUploads().detach(entry); profileAvatarUpload = null;
        if (el.profileAvatarInput.files[0] === selection.file) el.profileAvatarInput.value = "";
        renderProfileAvatarControl(); renderAvatarUploadStatus();
      }]] : []),
    ]) {
      const button = create("button", "secondary-button", label); button.type = "button";
      const action = ({ "Check status": "check", "Retry upload": "retry", "Cancel selection": "cancel" })[label];
      if (action) { button.className += ` upload-job-${action}`; updateUploadButton(button, entry, action, label); }
      button.addEventListener("click", async () => {
        const alertContext = avatarUploadContext(selection);
        button.disabled = true;
        try { await operation(); }
        catch (error) { showUploadError(el.profileSaveError, error, alertContext, label !== "Cancel selection"); }
        finally { if (action && uploadContextCurrent(context)) updateUploadButton(button, entry, action, label); else button.disabled = false; }
      });
      host.append(button);
    }
  }

  async function uploadFile(file, altText = "", submissionKey, purpose = "post") {
    return new Promise((resolve, reject) => {
      const ownerId = state.viewer?.id;
      if (!ownerId || !state.csrf) { reject(new Error("Your session needs to be refreshed. Sign in before uploading.")); return; }
      const body = new FormData();
      body.append("file", file);
      body.append("submission_key", submissionKey);
      body.append("purpose", purpose);
      if (altText.trim()) body.append("alt_text", altText.trim());
      const xhr = new XMLHttpRequest();
      xhr.open("POST", "/api/uploads");
      xhr.withCredentials = true;
      xhr.timeout = 125000;
      xhr.setRequestHeader("X-CSRF-Token", state.csrf);
      xhr.setRequestHeader("X-Account-ID", ownerId);
      const progressCurrent = () => ownerId === state.viewer?.id && el.uploadProgress.uploadRequest === xhr;
      const finishProgress = () => { if (progressCurrent()) { el.uploadProgress.hidden = true; el.uploadProgress.uploadRequest = null; } };
      el.uploadProgress.uploadRequest = xhr;
      el.uploadProgress.removeAttribute("value");
      el.uploadProgress.hidden = false;
      xhr.upload.addEventListener("progress", (event) => {
        if (progressCurrent() && event.lengthComputable && event.total > 0) el.uploadProgress.value = Math.round((event.loaded / event.total) * 100);
      });
      xhr.addEventListener("load", () => {
        finishProgress();
        let payload = {};
        try { payload = JSON.parse(xhr.responseText); } catch (_) { payload = {}; }
        if (xhr.status >= 200 && xhr.status < 300) resolve(payload);
        else {
          const error = new Error(detailMessage(payload, xhr.status));
          error.status = xhr.status;
          error.retryAfter = xhr.getResponseHeader("Retry-After");
          reject(error);
        }
      });
      xhr.addEventListener("error", () => { finishProgress(); reject(new Error("Upload connection failed.")); });
      xhr.addEventListener("timeout", () => { finishProgress(); reject(new Error("Upload timed out. Check its status before retrying.")); });
      xhr.addEventListener("abort", () => { finishProgress(); reject(new Error("Upload response was interrupted. Check its status.")); });
      try { xhr.send(body); } catch (error) { finishProgress(); reject(error); }
    });
  }

  function galleryVideoTextUpdate() {
    const item = state.gallery.items.find(entry => entry.remixText);
    if (!item) return null;
    if (state.gallery.items.length !== 1) throw new Error("Final remix text requires its single finished video.");
    const text = item.remixText, patch = {};
    if (text.captionImportToken) throw new Error("Wait for the caption file to finish reading, then save again.");
    for (const [key, original, field] of [["captions", "originalCaptions", "captions_vtt"], ["transcript", "originalTranscript", "transcript_text"]]) {
      const value = String(text[key] || "").trim();
      if (text[`${key}Removed`]) {
        if (text[`${key}Present`] || text[original]) patch[field] = null;
      } else if (value !== String(text[original] || "").trim()) {
        patch[field] = value && key === "captions" ? normalizeCaptionImport(value, text.durationMs) : value || null;
      }
    }
    return Object.keys(patch).length ? patch : null;
  }

  function previewRemixCaptions(item, player) {
    const text = item.remixText;
    const captions = text.captionsRemoved || !String(text.captions || "").trim()
      ? null : normalizeCaptionImport(text.captions, text.durationMs);
    for (const track of player.querySelectorAll("track")) {
      const source = track.src; track.remove();
      if (state.gallery.previewUrls.delete(source)) URL.revokeObjectURL(source);
    }
    if (captions) {
      const url = URL.createObjectURL(new Blob([captions], { type: "text/vtt" }));
      state.gallery.previewUrls.add(url);
      appendMediaCaptions(player, { captions_url: url });
    }
  }

  async function submitExistingVideoText(path, method, payload) {
    const post = state.editingDrop, binding = post?.video_binding;
    const item = state.gallery.items.find(entry => entry.remixText), current = videoScope(item);
    if (method !== "PATCH" || !binding || path !== `/api/drops/${post.id}` || item?.id !== binding.clean_asset_id
        || post.author_id !== state.viewer?.id || !Number.isSafeInteger(post.video_binding_version))
      throw new Error("Reopen the current post before saving its remix text.");
    const original = { ...payload, video_text: galleryVideoTextUpdate() };
    let attempt = state.gallery.videoTextAttempt;
    if (attempt && (attempt.path !== path || attempt.ownerId !== state.viewer.id || soundJson(attempt.original) !== soundJson(original)))
      throw new Error("Retry the unchanged text save first, or reopen the current post. The earlier request is kept in this tab.");
    if (!attempt) {
      if (!original.video_text) throw new Error("No final remix text was changed.");
      attempt = { path, ownerId: state.viewer.id, original: structuredClone(original), payload: {
        ...structuredClone(original), video_binding: { render_id: binding.render_id, render_version: binding.render_version },
        expected_video_binding_version: post.video_binding_version, publication_token: crypto.randomUUID(),
      } };
      state.gallery.videoTextAttempt = attempt;
    }
    try {
      const result = await api(path, { method, csrf: true, json: attempt.payload });
      soundAssert(current); return result;
    } catch (error) {
      if (current() && [400, 403, 404, 409, 422].includes(error.status) && state.gallery.videoTextAttempt === attempt)
        delete state.gallery.videoTextAttempt;
      throw error;
    }
  }

  function galleryMetadataUpdates() {
    const updates = [];
    for (const item of state.gallery.items) {
      if (item.captionImportToken) throw new Error("Wait for the caption file to finish reading, then save again.");
      const metadata = {};
      const alt = String(item.alt || "").trim();
      if (alt !== String(item.originalAlt || "").trim()) metadata.alt_text = alt || null;
      // Existing caption/transcript text is deliberately not loaded into the form.
      // Omitted siblings stay unchanged; explicit removal is different from blank.
      for (const [key, originalKey, apiKey] of [
        ["captions", "originalCaptions", "captions_vtt"],
        ["transcript", "originalTranscript", "transcript_text"],
      ]) {
        if (item.remixText || item.videoHandoff || item.remixTextUnavailable) continue;
        const value = String(item[key] || "").trim();
        const original = String(item[originalKey] || "").trim();
        if (item[`${key}Removed`]) {
          if (item[`${key}Present`] || original) metadata[apiKey] = null;
        } else if (value !== original) metadata[apiKey] = value || null;
      }
      if (Object.keys(metadata).length) {
        if (!item.id) throw new Error("One media item could not be prepared.");
        updates.push({ media_asset_id: item.id, metadata });
      }
    }
    return updates;
  }

  async function prepareGallerySelection({ waitForMedia = true } = {}) {
    const selectionError = gallerySelectionIsValid(state.gallery.items);
    if (selectionError) throw new Error(selectionError);
    const context = galleryUploadContext(), items = [...context.selection.items];
    const selectionCurrent = () => uploadContextCurrent(context) && state.gallery.items.length === items.length
      && items.every((item, index) => state.gallery.items[index] === item);
    const operation = { context };
    el.uploadLabel.mediaPreparation = operation;
    const ids = []; let waiting = false;
    try {
      if (items.some((item) => item.file || item.upload) && typeof window !== "undefined" && window.LouderrOptional) {
        await window.LouderrOptional.load("uploads");
        if (!uploadContextCurrent(context) || !state.capabilities.uploads || !state.capabilities.community_writes
            || state.gallery.items.length !== items.length || items.some((item, index) => state.gallery.items[index] !== item)) {
          throw new Error("The upload selection or account changed.");
        }
      }
      for (let index = 0; index < items.length; index += 1) {
        if (!selectionCurrent()) throw new Error("The upload selection or account changed.");
        const item = items[index];
        if (item.file || item.upload) {
          if (!item.upload) item.upload = mediaUploads().select(item.file, item.alt);
          item.upload.altText = item.alt;
          el.uploadLabel.textContent = `Preparing ${index + 1} of ${items.length}`;
          try { await mediaUploads().prepare(item.upload); }
          catch (error) { error.uploadAlert = { ...context, entry: item.upload }; throw error; }
          if (!selectionCurrent()) throw new Error("The upload selection or account changed.");
          applyReadyUpload(item, item.upload);
          if (item.upload.state !== "succeeded") { waiting = true; continue; }
        }
        if (!item.id) throw new Error("One media item could not be prepared.");
        ids.push(item.id);
      }
      if (waiting && waitForMedia) {
        // Publish stays pressed while the stack is still uploading, scanning or sanitizing:
        // nobody should have to come back and press it again for a check that takes seconds.
        const entries = () => items.map(item => item.upload).filter(Boolean);
        let waited = 0, last = Date.now();
        let decision = mediaWaitDecision(entries(), 0, MEDIA_READY_WAIT_MS);
        while (decision === "wait") {
          el.uploadLabel.textContent = "Checking your media. Publishing continues as soon as it is ready.";
          await new Promise(resolve => setTimeout(resolve, MEDIA_READY_POLL_MS));
          const now = Date.now();
          const away = typeof document !== "undefined" && document.visibilityState === "hidden";
          if (!away) waited += Math.min(now - last, MEDIA_READY_POLL_MS * 4);
          last = now;
          if (!selectionCurrent()) throw new Error("The upload selection or account changed.");
          for (const entry of entries()) {
            // The manager polls on its own while the tab is visible; nudge it only once it has stopped.
            if (!entry.timer && !entry.pending) await mediaUploads().poll(entry, { manual: true }).catch(() => null);
          }
          decision = mediaWaitDecision(entries(), waited, MEDIA_READY_WAIT_MS);
        }
        if (decision === "ready") return prepareGallerySelection({ waitForMedia: false });
      }
      if (waiting) {
        const error = new Error(mediaPendingMessage(context));
        error.uploadAlert = { ...context, kind: "pending" }; throw error;
      }
      return ids;
    } finally {
      // Only restore this operation's header; do not recreate the editable rows
      // or overwrite a newer gallery/account's presentation.
      if (el.uploadLabel.mediaPreparation === operation) {
        delete el.uploadLabel.mediaPreparation;
        if (uploadContextCurrent(context)) renderGalleryUploadLabel();
      }
    }
  }

  function hereFirstDaysFor(drop) {
    // The stored window is an end date; the select speaks in whole days from publication.
    if (!drop || !drop.first_on_louderr || !drop.first_on_louderr_until) return "";
    const days = Math.round((new Date(drop.first_on_louderr_until).getTime() - new Date(drop.created_at).getTime()) / 86400000);
    return ["7", "30", "90"].includes(String(days)) ? String(days) : "";
  }

  function composerJourneyPayload() {
    if (state.editingDrop) return null;
    if (state.composerJourney) return { mode: state.composerJourney.mode, root_drop_id: state.composerJourney.root_drop_id };
    if (el.projectStart.checked) return { mode: "start", prompt: el.projectPrompt.value, allow_responses: el.projectAllowResponses.checked };
    return null;
  }

  function restoreComposerJourney(context = null) {
    state.composerJourney = context && context.mode !== "start" ? { ...context } : null;
    el.projectStart.checked = context?.mode === "start";
    el.projectPrompt.value = context?.mode === "start" ? context.prompt || "feedback" : "feedback";
    el.projectAllowResponses.checked = context?.mode === "start" && Boolean(context.allow_responses);
    renderComposerJourney();
  }

  function renderComposerJourney() {
    const editing = Boolean(state.editingDrop);
    const context = state.composerJourney;
    el.projectComposer.hidden = editing && !state.editingDrop.journey;
    el.projectStartLabel.hidden = editing || Boolean(context);
    el.projectStartOptions.hidden = editing || Boolean(context) || !el.projectStart.checked;
    el.projectComposerContext.hidden = editing || !context;
    el.projectEditNote.hidden = !editing || !state.editingDrop.journey;
    el.projectComposerLabel.textContent = context?.mode === "update" ? "Creator update for" : "Response to";
    el.projectComposerTitle.textContent = context?.title || "Original project";
    // Reusing the original's sound is a server switch, off until the Terms grant that licence.
    const useSound = $("#composer-use-original-sound");
    if (useSound) {
      useSound.hidden = editing || context?.mode !== "response" || !context?.root_media_url || !context?.allow_responses
        || !state.capabilities.studio_response_audio;
      if (!useSound.hidden) useSound.textContent = "Choose an offered original sound in Studio";
    }
  }

  function addJourneyPromptTemplate() {
    if (!el.projectStart.checked || state.editingDrop || state.composerJourney || state.composerDraft.busy) return;
    const text = ({
      feedback: "What would make this better?",
      choice: "Which version works best for you, and why?",
      remix: "What would your version look or sound like? Ask before reusing anyone’s media.",
      question: "What would you like to know about this project?",
    })[el.projectPrompt.value];
    if (!text) return;
    const body = el.dropBody.value.trimEnd();
    if (body.includes(text)) { toast("That question is already in your description."); return; }
    const next = `${body}${body ? "\n\n" : ""}${text}`;
    if (next.length > 700) { toast("Make a little room in the description before adding this question.", true); return; }
    el.dropBody.value = next;
    updateComposerCopy();
    el.dropBody.focus();
  }

  function syncHereFirstWindow() {
    if (el.dropHereFirstWindow) el.dropHereFirstWindow.hidden = !el.dropHereFirst?.checked;
  }

  function composerFields() {
    return { kind: el.dropKind.value, title: el.dropTitle.value, body: el.dropBody.value, link: el.dropLink.value,
      hereFirst: Boolean(el.dropHereFirst?.checked),
      hereFirstDays: el.dropHereFirst?.checked ? (el.dropHereFirstDays?.value || "") : "",
      journey: state.editingDrop ? null : state.composerJourney ? { ...state.composerJourney } : composerJourneyPayload() };
  }

  function composerFingerprint(fields = composerFields(), gallery = state.gallery) {
    return JSON.stringify({ ...fields, media: gallery.items.map((item) => [
      item.key, item.alt, item.captions || "", item.transcript || "",
      Boolean(item.captionsRemoved), Boolean(item.transcriptRemoved),
      item.soundHandoff?.recipeSha256 || null, item.soundHandoff?.source || null, gallery.soundDetach?.expectedVersion ?? null,
      item.videoHandoff?.recipeSha256 || null, item.videoHandoff?.source?.id || null,
      item.remixText?.captions || "", item.remixText?.transcript || "",
      Boolean(item.remixText?.captionsRemoved), Boolean(item.remixText?.transcriptRemoved),
    ]) });
  }

  function composerHasChanges() {
    return Boolean(state.composerDraft.key && state.composerDraft.baseline !== composerFingerprint());
  }

  function hasUnsavedComposerDrafts() {
    return composerHasChanges() || [...state.composerDraft.saved.keys()].some((key) => key !== state.composerDraft.key);
  }

  function protectComposerDraftOnUnload(event) {
    if (state.accountChangeReload || (!state.composerDraft.busy && !hasUnsavedComposerDrafts())) return;
    event.preventDefault();
    event.returnValue = "";
  }

  function captureComposerDraft() {
    const draft = state.composerDraft;
    if (!draft.key) return;
    if (!composerHasChanges()) { draft.saved.delete(draft.key); return; }
    draft.saved.set(draft.key, {
      fields: composerFields(), gallery: state.gallery, editingDrop: state.editingDrop,
      baseline: draft.baseline,
    });
  }

  function updateComposerCopy() {
    const editing = Boolean(state.editingDrop);
    el.composerTitle.textContent = editing ? "Edit Drop" : state.composerJourney?.mode === "update" ? "Project update" : state.composerJourney?.mode === "response" ? "Response Drop" : el.projectStart.checked ? "Start a project" : "New Drop";
    el.submitDrop.textContent = editing ? "Save changes" : "Publish";
    const discard = $("#discard-drop-draft");
    if (discard) discard.textContent = editing ? "Discard changes" : "Discard draft";
    const status = $("#composer-draft-status");
    if (status) status.textContent = state.gallery.items.some(item => item.videoHandoff)
      ? "This video remix and its requests are kept on this device. Preview the finished video before publishing. Browser storage can be cleared."
      : state.gallery.items.some(item => item.soundHandoff)
      ? "One sound publication draft per account can be kept on this device. Prepare and play its server mix before publishing. Device storage can be cleared by your browser."
      : "Your draft stays in this tab until you publish, discard, reload, or sign out.";
    $("#title-count").textContent = `${el.dropTitle.value.length} / 120`;
    $("#body-count").textContent = `${el.dropBody.value.length} / 700`;
    renderComposerJourney();
  }

  function clearComposerIntent() {
    state.composerDraft.pending = null;
    try { sessionStorage.removeItem("lobby.composer.return"); } catch (_) { /* Optional return hint only. */ }
    renderComposerIntent();
  }

  function cancelComposerIntent() {
    clearComposerIntent();
    const registerForm = $("#register-form");
    registerForm?.reset?.();
    for (const control of registerForm?.elements || []) control.setCustomValidity?.("");
    authTab("login");
    toast("Creating canceled. You can still sign in.");
  }

  function guestIntroBetaCopy() {
    // What a visitor should actually do next, which depends on whether a place is left. Sending
    // somebody to find an invite code is wrong once the room is full: a code is refused at the same
    // seat count that refuses a sign-up, so the advice would cost them the favour and get a no.
    const seats = state.capacity;
    if (seats && !seats.open) {
      return "Every place in this round is taken. More open as the room grows, so look in again soon.";
    }
    if (seats && !seatsAcceptRegistration(seats)) {
      return "The remaining places are held for invited members. You can browse now and check back when a place opens.";
    }
    // A half-empty room reads as quiet rather than scarce, so the size of the round is the honest
    // thing to lead with until enough places are gone for the number left to mean anything. The
    // last place is the moment this line is read most closely, so it has to be grammatical there.
    const round = !seats ? ""
      : seats.taken * 2 >= seats.limit
        ? seats.remaining === 1
          ? ` Only one of ${formatCount(seats.limit)} places is left in this round.`
          : ` Only ${formatCount(seats.remaining)} of ${formatCount(seats.limit)} places are left in this round.`
        : ` This round is capped at ${formatCount(seats.limit)} ${seats.limit === 1 ? "member" : "members"}.`;
    if (state.federatedRegistration) {
      return `Share what you're making and get feedback. Join with Google, no invitation code needed.${round}`;
    }
    return `Browse the latest Drops below. To take part you need a one-use invite code from a member: open Sign in, then Join with a code.${round}`;
  }

  function renderGuestIntroCopy(intro) {
    // "Everyone's welcome" is not true while joining needs an invite code, so the markup no longer
    // says it and this decides. Until capabilities arrive nothing is claimed either way, which on a
    // slow connection is around eight seconds of a stranger reading the front page.
    const copy = typeof intro.querySelector === "function" ? intro.querySelector("p") : null;
    if (!copy) return;
    const join = $("#home-join");
    if (join) join.hidden = !(state.federatedRegistration || state.capabilities.registration)
      || Boolean(state.capacity && !seatsAcceptRegistration(state.capacity));
    copy.dataset.registrationOpen = String(Boolean(state.capabilities.registration));
    if (state.capabilities.registration) {
      const canJoin = !state.capacity || seatsAcceptRegistration(state.capacity);
      // An open room needs no sentence: the Join button is the invitation, and age is confirmed at
      // registration. Only a full or held room has something a visitor must be told.
      const message = canJoin ? "" : guestIntroBetaCopy();
      if (copy.dataset.betaCopy === message) return;
      copy.dataset.betaCopy = message;
      clear(copy);
      copy.hidden = !message;
      if (message) copy.append(create("strong", "", "Try LOUDERR."), ` ${message}`);
      return;
    }
    if (!state.capabilities.beta_access) return;
    const seats = state.capacity;
    // Seats change; the copy usually does not. Rewriting the paragraph would drop a text selection
    // somebody is in the middle of making, so it is redrawn only when the wording would differ.
    const drawn = `${Boolean(state.federatedRegistration)}:${seats ? `${seats.limit}:${seats.taken}:${seats.open}:${seatsAcceptRegistration(seats)}` : "no-seats"}`;
    if (copy.dataset.betaCopy === drawn) return;
    copy.dataset.betaCopy = drawn;
    const text = guestIntroBetaCopy();
    clear(copy);
    const title = state.federatedRegistration ? "Try LOUDERR." : "LOUDERR is in closed beta.";
    copy.hidden = false;
    copy.append(create("strong", "", title), ` ${text}`);
  }

  function renderComposerIntent() {
    const note = $("#composer-auth-intent");
    if (note) note.hidden = !state.composerDraft.pending;
    const copy = $("#composer-auth-intent > p");
    const verifyStep = state.capabilities.email_delivery ? " and verify your email" : "";
    if (copy) copy.textContent = state.composerDraft.pending?.projectRoot
      ? `Sign in${verifyStep} to return to this project. Following is your choice.`
      : `Sign in${verifyStep} to continue creating your Drop.`;
    const cancel = $("#cancel-composer-intent");
    if (cancel) cancel.textContent = state.composerDraft.pending?.projectRoot ? "Cancel" : "Cancel creating";
  }

  function rememberComposerAuthReturn() {
    // Only the Create action survives a provider redirect. Private draft content stays in memory.
    try {
      sessionStorage.removeItem("lobby.composer.return");
      if (state.composerDraft.pending?.projectRoot) {
        sessionStorage.setItem("lobby.composer.return", JSON.stringify({ action: "project", root_drop_id: state.composerDraft.pending.projectRoot, at: Date.now() }));
      } else if (state.composerDraft.pending && !state.composerDraft.pending.drop) {
        const context = state.composerDraft.pending.journey;
        const hint = { action: "create", at: Date.now() };
        // A public project identifier is only a navigation hint; never persist draft text or media.
        if (context && ["update", "response"].includes(context.mode)) hint.journey = { mode: context.mode, root_drop_id: context.root_drop_id };
        else if (context?.mode === "start" && ["feedback", "choice", "remix", "question"].includes(context.prompt)) {
          hint.journey = { mode: "start", prompt: context.prompt };
        }
        sessionStorage.setItem("lobby.composer.return", JSON.stringify(hint));
      }
    } catch (_) { /* Sign-in can still proceed when browser storage is unavailable. */ }
  }

  function resumeComposerIntent() {
    const pending = state.composerDraft.pending;
    if (!pending || !state.viewer?.email_verified || state.viewer.status !== "active" || state.sessionRestricted) return false;
    if (pending.ownerId && pending.ownerId !== state.viewer.id) { clearComposerIntent(); return false; }
    clearComposerIntent();
    el.account.close();
    if (pending.projectRoot) openJourney(pending.projectRoot);
    else openComposer(pending.drop, pending.journey);
    return true;
  }

  function syncComposerIdentity() {
    if (state.composerDraft.ownerId !== (state.viewer?.id || null)) mediaStudio?.cancel();
    const draft = state.composerDraft;
    const ownerId = state.viewer?.id || null;
    if (draft.ownerId === ownerId) return;
    if (draft.ownerId !== null) {
      const hadChanges = hasUnsavedComposerDrafts();
      const previews = new Set(state.gallery.previewUrls);
      for (const saved of draft.saved.values()) for (const url of saved.gallery.previewUrls) previews.add(url);
      for (const url of previews) URL.revokeObjectURL(url);
      draft.saved.clear();
      draft.key = null; draft.baseline = null; draft.epoch += 1;
      state.editingDrop = null;
      state.composerJourney = null;
      state.gallery = { items: [], dirty: false, locked: false, previewUrls: new Set() };
      el.dropForm.reset();
      renderDropMediaControl();
      clearComposerIntent();
      el.composer.close();
      if (hadChanges) toast("Drafts cleared because the signed-in account changed.");
    }
    draft.ownerId = ownerId;
  }

  async function discardComposerDraft() {
    if (state.composerDraft.busy) return;
    if (composerHasChanges() && !(await confirm(state.gallery.items.some(item => item.videoHandoff?.render)
      ? "Discard this device draft? A submitted video job or post may already exist. Its retry record will be removed from this device; check your posts before submitting again."
      : state.editingDrop ? "Discard these unpublished changes?" : "Discard this unpublished draft?"))) return;
    const videoHandoff = state.gallery.items.find(item => item.videoHandoff)?.videoHandoff;
    void clearVideoDraft(state.viewer?.id, videoHandoff?.draftId, videoHandoff?.journalRevision)?.catch(error => toast(`Device video draft could not be removed: ${error.message}`, true));
    const soundHandoff = state.gallery.items.find(item => item.soundHandoff)?.soundHandoff;
    void clearSoundDraft(state.viewer?.id, soundHandoff?.draftId, soundHandoff?.journalRevision)?.catch(error => toast(`Device draft could not be removed: ${error.message}`, true));
    if (state.gallery.soundDetach) sessionStorage.removeItem(soundDetachKey(state.gallery.soundDetach.ownerId, state.gallery.soundDetach.dropId));
    clearGalleryPreviews();
    state.composerDraft.saved.delete(state.composerDraft.key);
    state.composerDraft.key = null;
    state.composerDraft.baseline = null;
    state.editingDrop = null;
    state.composerJourney = null;
    el.dropForm.reset();
    initializeComposerGallery();
    el.composer.close();
    toast("Unpublished draft discarded");
  }

  function openComposer(drop = null, journey = null, { reconcileOnly = false } = {}) {
    if (state.composerDraft.busy) { toast("Wait for your current Drop to finish saving."); return; }
    if (typeof hidePostingBar === "function" && $("#posting-bar")?.classList?.contains?.("is-failed")) hidePostingBar();
    if (!state.capabilities.community_writes && !reconcileOnly) {
      toast("Community changes are read-only until safety operations are ready.", true);
      return;
    }
    if (state.viewer && (state.sessionRestricted || state.viewer.status !== "active")) {
      clearComposerIntent();
      openAccountDialog("profile");
      toast("Your account cannot publish Drops right now.", true);
      return;
    }
    if (!state.viewer?.email_verified) {
      state.composerDraft.pending = { drop, journey, ownerId: state.viewer?.id || null };
      renderComposerIntent();
      requireVerified({ announce: false });
      return;
    }
    syncComposerIdentity();
    const draft = state.composerDraft;
    const key = drop ? `edit:${drop.id}` : journey?.mode === "start" ? `project:start:${journey.prompt || "feedback"}` : journey ? `journey:${journey.mode}:${journey.root_drop_id}` : "new";
    if (draft.key !== key) {
      captureComposerDraft();
      // A parked draft keeps its File objects and previews until discard, publish, or sign-out.
      if (!draft.saved.has(draft.key)) clearGalleryPreviews();
      const saved = draft.saved.get(key);
      state.gallery = { items: [], dirty: false, locked: false, previewUrls: new Set() };
      state.editingDrop = saved?.editingDrop || drop;
      el.dropForm.reset();
      restoreComposerJourney(drop ? null : saved?.fields.journey || journey);
      const fields = saved?.fields || (drop ? { kind: drop.kind, title: drop.title, body: drop.body, link: drop.link_url || "", hereFirst: Boolean(drop.first_on_louderr), hereFirstDays: hereFirstDaysFor(drop) } : null);
      if (el.dropHereFirst) {
        el.dropHereFirst.checked = Boolean(fields?.hereFirst);
        if (el.dropHereFirstDays) el.dropHereFirstDays.value = fields?.hereFirstDays || "";
        if (el.dropHereFirstWindow) el.dropHereFirstWindow.hidden = !el.dropHereFirst.checked;
      }
      if (fields) {
        el.dropKind.value = fields.kind;
        el.dropTitle.value = fields.title;
        el.dropBody.value = fields.body;
        el.dropLink.value = fields.link;
      } else if (journey?.kind) el.dropKind.value = journey.kind;
      if (saved) { state.gallery = saved.gallery; renderDropMediaControl(); }
      else initializeComposerGallery(drop);
      draft.key = key;
      draft.baseline = saved?.baseline || composerFingerprint();
    }
    restoreSoundDetachReceipt();
    renderDropMediaControl();
    renderSoundDraftResume();
    renderVideoDraftResume();
    hideError(el.formError);
    el.formError.classList?.remove?.("is-notice");
    updateComposerCopy();
    el.uploadField.hidden = !state.capabilities.uploads;
    if (!state.gallery.step) state.gallery.step = state.editingDrop ? "details" : "media";
    renderComposerStep();
    if (typeof syncComposerStudioDraft === "function") syncComposerStudioDraft();
    if (typeof document !== "undefined") {
      if (!state.composerTitleBeforeOpen) state.composerTitleBeforeOpen = document.title;
      setComposerDocumentTitle();
    }
    if (typeof openDialogWithHistory === "function") openDialogWithHistory(el.composer, "/create");
    else openDialog(el.composer);
    if (state.capabilities.uploads && typeof window !== "undefined" && window.LouderrOptional) {
      window.LouderrOptional.load("uploads").catch(() => {});
    }
  }

  function showPostingBar(text, drop = null, { onOpen = null } = {}) {
    const bar = $("#posting-bar");
    if (!bar) {
      if (drop) toast(text);
      if (onOpen) { toast(text, true); onOpen(); }
      return;
    }
    clearTimeout(bar.hideTimer);
    bar.hidden = false;
    bar.classList?.toggle?.("is-done", Boolean(drop));
    bar.classList?.toggle?.("is-failed", Boolean(onOpen));
    bar.setAttribute?.("role", onOpen ? "alert" : "status");
    const label = $("#posting-bar-text");
    if (label) label.textContent = text;
    const view = $("#posting-bar-view");
    if (view) {
      view.hidden = !drop && !onOpen;
      view.textContent = onOpen ? "Open" : "View";
      view.onclick = drop ? () => {
        hidePostingBar();
        history.pushState({}, "", `/d/${encodeURIComponent(drop.id)}`);
        void routeLocation();
      } : onOpen ? () => { hidePostingBar(); onOpen(); } : null;
    }
    if (drop) bar.hideTimer = setTimeout(hidePostingBar, 6000);
  }

  function hidePostingBar() {
    const bar = $("#posting-bar");
    if (!bar) return;
    clearTimeout(bar.hideTimer);
    bar.hidden = true;
  }

  function replaceDropEverywhere(drop) {
    state.feedWindows?.patch(drop.id, drop);
    for (const items of [state.drops, state.search.drops, state.discovery.items, state.profile.drops, state.flow.items]) {
      const index = items.findIndex((item) => item.id === drop.id);
      if (index >= 0) items[index] = { ...items[index], ...drop };
    }
    state.flow.stories = state.flow.stories.map((story) => story.drop?.id === drop.id
      ? { ...story, drop: { ...story.drop, ...drop } }
      : story);
    if (state.flow.now?.current?.drop?.id === drop.id) {
      state.flow.now.current.drop = { ...state.flow.now.current.drop, ...drop };
    }
    if (state.board?.drop?.id === drop.id) state.board.drop = drop;
    if (state.menuDrop?.id === drop.id) state.menuDrop = drop;
  }

  async function submitDrop(event) {
    event.preventDefault();
    if (state.composerDraft.busy) return;
    const publishesStudioEdit = state.gallery.items.some(item => item.studioProject);
    if (state.gallery.items.some(item => item.captionImportToken || item.remixText?.captionImportToken)) {
      showError(el.formError, "Wait for the caption file to finish reading, then save again.");
      return;
    }
    if (!state.viewer?.email_verified || state.sessionRestricted || state.viewer.status !== "active") {
      el.composer.close();
      openComposer(state.editingDrop, state.composerJourney);
      return;
    }
    hideError(el.formError);
    el.formError.classList?.remove?.("is-notice");
    const button = el.submitDrop;
    const editing = state.editingDrop;
    const creating = !editing;
    const draftKey = state.composerDraft.key;
    const draftEpoch = state.composerDraft.epoch;
    const soundOwner = state.viewer.id;
    const videoHandoff = state.gallery.items.find(item => item.videoHandoff)?.videoHandoff;
    const soundHandoff = state.gallery.items.find(item => item.soundHandoff)?.soundHandoff;
    const soundDetach = state.gallery.soundDetach;
    const controls = $$("input, textarea, select, button", el.composer).map((control) => [control, control.disabled]);
    let createdDropId = null;
    let confirmedPublication = false;
    state.composerDraft.busy = true;
    el.dropForm.setAttribute("aria-busy", "true");
    controls.forEach(([control]) => { control.disabled = true; });
    button.textContent = editing ? "Saving…" : "Publishing…";
    if (creating) state.dropSubmissionActive = true;
    // With media still on its way, the composer steps aside and the post finishes in the background,
    // the way Instagram returns you to the feed after Share. A problem brings the composer back.
    const background = creating && state.gallery.items.some(item => item.upload && item.upload.state !== "succeeded");
    if (background) {
      el.composer.close();
      if (typeof showPostingBar === "function") showPostingBar("Posting your Drop…");
    }
    try {
      const mediaAssetIds = soundHasPendingPublication() ? state.gallery.items.map(item => item.id) : await prepareGallerySelection();
      if (state.composerDraft.epoch !== draftEpoch) throw new Error("The signed-in account changed. This draft was not submitted.");
      const metadataUpdates = galleryMetadataUpdates();
      let drop;
      if (editing) {
        const hereFirst = { hereFirst: Boolean(el.dropHereFirst?.checked), hereFirstDays: el.dropHereFirst?.checked ? (el.dropHereFirstDays?.value || "") : "" };
        const payload = {
          kind: el.dropKind.value,
          ...(el.dropTitle.value !== editing.title ? { title: el.dropTitle.value } : {}),
          ...(el.dropBody.value !== (editing.body || "") ? { body: el.dropBody.value } : {}),
          ...((el.dropLink.value || null) !== (editing.link_url || null) ? { link_url: el.dropLink.value || null } : {}),
          ...(metadataUpdates.length ? { media_metadata_updates: metadataUpdates } : {}),
          challenge_token: null,
          first_on_louderr: hereFirst.hereFirst,
          ...(hereFirst.hereFirst && hereFirst.hereFirstDays ? { first_on_louderr_days: Number(hereFirst.hereFirstDays) } : {}),
        };
        if (state.gallery.dirty) payload.media_asset_ids = mediaAssetIds;
        drop = await submitSoundPublication(`/api/drops/${encodeURIComponent(editing.id)}`, "PATCH", payload);
      } else {
        const journey = composerJourneyPayload();
        const hereFirst = { hereFirst: Boolean(el.dropHereFirst?.checked), hereFirstDays: el.dropHereFirst?.checked ? (el.dropHereFirstDays?.value || "") : "" };
        drop = await submitSoundPublication("/api/drops", "POST", {
            kind: el.dropKind.value,
            title: el.dropTitle.value,
            body: el.dropBody.value,
            link_url: el.dropLink.value || null,
            media_asset_ids: mediaAssetIds,
            ...(metadataUpdates.length ? { media_metadata_updates: metadataUpdates } : {}),
            ...(journey ? { journey } : {}),
            ...(hereFirst.hereFirst ? { first_on_louderr: true } : {}),
            ...(hereFirst.hereFirst && hereFirst.hereFirstDays ? { first_on_louderr_days: Number(hereFirst.hereFirstDays) } : {}),
        });
        if (state.composerDraft.epoch !== draftEpoch) throw new Error("The signed-in account changed. Check the original account before submitting again.");
        if (drop?.status === "pending_review") {
          // A held Drop is a waiting state, not a failure: same slot, calmer colours.
          el.formError.classList?.add?.("is-notice");
          showError(el.formError, "This Drop is held for moderation review, not published. Your draft and project link are kept in this tab. If approved, submit it again to publish; approval alone does not publish it.");
          captureComposerDraft();
          return;
        }
      }
      if (state.composerDraft.epoch !== draftEpoch) throw new Error("The signed-in account changed. Check the original account before submitting again.");
      if (!drop || typeof drop.id !== "string" || !drop.id.trim()
          || (drop.author_id && drop.author_id !== state.viewer?.id)
          || (editing && drop.id !== editing.id)
          || (drop.status && drop.status !== "active")) {
        throw new Error("We couldn't confirm publication. Your draft is kept. Check My Drops before trying again.");
      }
      confirmedPublication = true;
      // Reads started before this write must not remove the new post or restore an old edit.
      state.feedMutationVersion = (state.feedMutationVersion || 0) + 1;
      state.feedWindows?.invalidate();
      void clearVideoDraft(soundOwner, videoHandoff?.draftId, videoHandoff?.journalRevision)?.catch(error => toast(`Your post is published; its device video draft could not be removed: ${error.message}`, true));
      void clearSoundDraft(soundOwner, soundHandoff?.draftId, soundHandoff?.journalRevision)?.catch(error => toast(`Your post is published; its device draft could not be removed: ${error.message}`, true));
      if (soundDetach) sessionStorage.removeItem(soundDetachKey(soundOwner, soundDetach.dropId));
      if (creating) createdDropId = drop.id;
      // Clear a confirmed submission before view rendering, which may fail independently.
      state.composerDraft.saved.delete(draftKey);
      state.composerDraft.key = null;
      state.composerDraft.baseline = null;
      el.dropForm.reset();
      initializeComposerGallery();
      state.editingDrop = null;
      state.composerJourney = null;
      el.composer.close();
      if (editing) replaceDropEverywhere(drop); else upsertDrop(drop);
      renderFeed();
      if (state.view === "discover") renderDiscover();
      if (state.view === "profile") renderProfileView();
      renderBoard();
      if (editing) {
        refreshTrendingPreview();
        if (state.view === "trending") refreshTrending();
        toast("Drop changes published");
      } else {
        track("drop_created", drop.id);
        if (background && typeof showPostingBar === "function") showPostingBar("Your Drop is posted", drop);
        else toast("Drop published");
      }
      // The edit is on LOUDERR now; the device copy of the draft has done its job.
      if (publishesStudioEdit && typeof window !== "undefined" && typeof studioDraftKey === "function") window.LobbyStudio?.drafts?.clear(studioDraftKey());
      if (drop.journey?.root_drop_id) openJourney(drop.journey.root_drop_id);
    } catch (error) {
      if (state.composerDraft.epoch !== draftEpoch) toast(error.message, true);
      else if (confirmedPublication) toast("Your Drop was published, but the page could not refresh. Reload to see it.", true);
      else if (!error.uploadAlert && error.name === "TypeError"
        && ["Failed to fetch", "Load failed", "NetworkError when attempting to fetch resource."].includes(error.message)) {
        // A lost response does not prove whether the server committed the mutation.
        showError(el.formError, creating
          ? "We couldn't confirm publication. Your draft is still here. Check My Drops before trying again."
          : "We couldn't confirm your changes. Your draft is still here. Check the Drop before saving again.");
      }
      else showUploadError(el.formError, error);
    }
    finally {
      state.composerDraft.busy = false;
      el.dropForm.removeAttribute("aria-busy");
      controls.forEach(([control, disabled]) => { control.disabled = disabled; });
      button.disabled = false;
      button.textContent = state.editingDrop ? "Save changes" : "Publish";
      // Upload status can change while Publish waits. Restore its current state,
      // not the disabled flag captured before the server supplied Retry-After; this
      // comes after the reset above, so an upload that now needs attention or a
      // review holds Publish again.
      if (state.composerDraft.epoch === draftEpoch) {
        for (const item of state.gallery.items) if (item.upload) uploadChanged(item.upload, { controlsOnly: true });
      }
      if (creating) {
        state.dropSubmissionActive = false;
        const missedAnotherDrop = [...state.deferredCreatedDropIds]
          .some((dropId) => dropId !== createdDropId);
        state.deferredCreatedDropIds.clear();
        if (missedAnotherDrop) refreshFeed();
      }
      if (background && !confirmedPublication) {
        const reopen = () => {
          if (state.composerDraft.epoch !== draftEpoch || el.composer.open) return;
          if (typeof openDialogWithHistory === "function") openDialogWithHistory(el.composer, "/create");
          else openDialog(el.composer);
        };
        if (state.composerDraft.epoch !== draftEpoch || el.composer.open) {
          if (typeof hidePostingBar === "function") hidePostingBar();
        } else if (typeof showPostingBar === "function") {
          showPostingBar("Your Drop wasn't posted", null, { onOpen: reopen });
        } else {
          reopen();
          toast("Your Drop wasn't posted. The composer says why.", true);
        }
      }
      if (state.composerDraft.epoch === draftEpoch && el.composer.open
        && (state.gallery.soundDetach || state.gallery.items.some(item => item.soundHandoff || item.videoHandoff))) renderDropMediaControl();
    }
  }

  function applyRepostState(dropId, value) {
    if (typeof value.reposted !== "boolean") return;
    const items = [
      ...state.drops, ...state.search.drops, ...state.discovery.items, ...state.profile.drops, ...state.flow.items,
      ...state.flow.stories.map(story => story.drop), state.flow.now?.current?.drop, state.board?.drop, state.menuDrop,
    ];
    for (const drop of items) if (drop?.id === dropId) {
      drop.viewer = { ...drop.viewer, reposted: value.reposted };
    }
  }

  function openActionMenu(drop, anchor) {
    closeActionMenu();
    state.menuDrop = drop;
    state.menuAnchor = anchor;
    const rect = anchor.getBoundingClientRect();
    const ownDrop = Boolean(state.viewer && drop.author_id === state.viewer.id);
    const withinEditWindow = Date.now() <= new Date(drop.created_at).getTime() + 15 * 60 * 1000;
    // Public Drop responses contain active records and omit status. The server
    // checks current eligibility again on publication, including stale menus.
    const activeDrop = drop.status === undefined || drop.status === "active";
    const remixItem = $("[data-menu-action='remix-video']", el.menu);
    if (remixItem) remixItem.hidden = !state.viewer || !state.capabilities.studio_video_remix || !state.capabilities.community_writes
      || !activeDrop || !globalThis.LouderrVideoRemixControls?.sourceCandidates(drop).length;
    const removalItem = $("[data-menu-action='remove-video-remix']", el.menu);
    if (removalItem) removalItem.hidden = !ownDrop || !state.viewer?.email_verified || state.sessionRestricted
      || Boolean(drop.structured_type) || !(drop.kind === "video" || drop.video_binding || drop.video_remix || state.videoRemixRemoval?.hasPending(drop.id));
    const videoPermissionItem = $("[data-menu-action='video-permissions']", el.menu);
    if (videoPermissionItem) videoPermissionItem.hidden = !ownDrop || !activeDrop || drop.structured_type === "sponsor";
    const soundItem = $("[data-menu-action='original-sound']", el.menu);
    if (soundItem) soundItem.hidden = !ownDrop || !activeDrop || drop.structured_type === "sponsor";
    const archiveItem = $("[data-menu-action='archive']", el.menu);
    if (archiveItem) archiveItem.hidden = !ownDrop || !state.viewer?.email_verified || state.sessionRestricted
      || !activeDrop || drop.structured_type === "sponsor";
    const sharedItem = $("[data-menu-action='shared-collection']", el.menu);
    if (sharedItem) sharedItem.hidden = !state.viewer || !state.capabilities.community_writes || !activeDrop;
    const repostItem = $("[data-menu-action='repost']", el.menu);
    if (repostItem) repostItem.hidden = !state.socialExtras?.canRepost(drop);
    const folderItem = $("[data-menu-action='saved-folder']", el.menu);
    if (folderItem) folderItem.hidden = !state.viewer || !state.capabilities.community_writes || !drop.viewer?.saved;
    const coauthorsItem = $("[data-menu-action='coauthors']", el.menu);
    if (coauthorsItem) coauthorsItem.hidden = !state.viewer
      || !(ownDrop && activeDrop || drop.my_coauthor_invitation);
    $("[data-menu-action='story']", el.menu).hidden = state.capabilities.profile === "no-cost-closed-beta-v1" || !state.capabilities.community_writes || !state.capabilities.media_publication || !ownDrop || !activeDrop || drop.structured_type === "sponsor";
    $("[data-menu-action='edit']", el.menu).hidden = !state.capabilities.community_writes || !ownDrop || !withinEditWindow || Boolean(drop.structured_type);
    $("[data-menu-action='mute']", el.menu).hidden = ownDrop;
    $("[data-menu-action='block']", el.menu).hidden = ownDrop;
    const restrictItem = $("[data-menu-action='restrict']", el.menu);
    if (restrictItem) restrictItem.hidden = ownDrop;
    $("[data-menu-action='report']", el.menu).hidden = ownDrop;
    // Sending needs somewhere to send it: a signed-in viewer, writes enabled, and a Drop
    // that is still active. Hidden rather than shown and then failing.
    const sendItem = $("[data-menu-action='send']", el.menu);
    if (sendItem) sendItem.hidden = !state.viewer || !state.capabilities.community_writes || !activeDrop;
    $("[data-menu-action='delete']", el.menu).hidden = !state.capabilities.community_writes || !ownDrop;
    const nominate = $("[data-menu-action='nominate']", el.menu);
    if (nominate) {
      nominate.hidden = !state.capabilities.community_writes || !activeDrop;
      nominate.lastChild.textContent = drop.viewer?.nominated ? "Remove Board nomination" : "Nominate for Board";
    }
    const hide = $("[data-menu-action='hide']", el.menu);
    if (hide) hide.hidden = ownDrop;
    el.menu.hidden = false;
    // Measure the visible actions rather than assuming a fixed menu height.
    const viewport = window.visualViewport;
    const leftEdge = (viewport?.offsetLeft || 0) + 12;
    const topEdge = (viewport?.offsetTop || 0) + 12;
    const availableWidth = Math.max(0, (viewport?.width || innerWidth) - 24);
    const availableHeight = Math.max(0, (viewport?.height || innerHeight) - 24);
    el.menu.style.maxWidth = `${availableWidth}px`;
    el.menu.style.maxHeight = `${availableHeight}px`;
    const menuRect = el.menu.getBoundingClientRect();
    const rightEdge = leftEdge + availableWidth;
    const bottomEdge = topEdge + availableHeight;
    const top = rect.bottom + 8 + menuRect.height <= bottomEdge
      ? rect.bottom + 8 : rect.top - menuRect.height - 8;
    el.menu.style.left = `${Math.max(leftEdge, Math.min(rect.right - menuRect.width, rightEdge - menuRect.width))}px`;
    el.menu.style.top = `${Math.max(topEdge, Math.min(top, bottomEdge - menuRect.height))}px`;
    anchor.setAttribute("aria-expanded", "true");
    requestAnimationFrame(() => {
      if (!el.menu.hidden) $("button:not([hidden])", el.menu)?.focus({ preventScroll: true });
    });
  }

  function openDropReport(drop, surface = null) {
    if (!drop?.id || !requireVerified()) return;
    if (drop.author_id === state.viewer?.id || drop.author?.id === state.viewer?.id) return;
    openReport({
      id: drop.id,
      title: "Report this Drop",
      endpoint: () => `/api/drops/${encodeURIComponent(drop.id)}/reports`,
      track: () => track("report", drop.id, { target_type: "drop", ...(surface ? { surface } : {}) }),
      receipt: () => "Report added to the moderator queue",
    });
  }

  function closeActionMenu(restoreFocus = false) {
    if (state.menuAnchor) state.menuAnchor.setAttribute("aria-expanded", "false");
    el.menu.hidden = true;
    if (restoreFocus) state.menuAnchor?.focus();
    state.menuAnchor = null;
  }

  async function actionMenu(action) {
    const drop = state.menuDrop;
    const source = state.menuAnchor;
    const surface = engagementSurface(source);
    closeActionMenu();
    if (!drop) return;
    if (action === "archive") { state.postArchive?.show(drop, source); return; }
    if (action === "repost") { await state.socialExtras?.toggleRepost(drop); return; }
    if (action === "shared-collection") { state.sharedCollections?.show(null, drop, source); return; }
    if (action === "saved-folder") { state.savedFolderControls?.show(drop, source); return; }
    if (action === "coauthors") { state.coauthors?.show(drop.id, source); return; }
    if (action === "original-sound") { await state.originalSounds?.show(drop, source); return; }
    if (action === "remove-video-remix") { await state.videoRemixRemoval?.show(drop, source); return; }
    if (action === "video-permissions") {
      await loadDeferred("video-remix").catch(() => {});
      await state.videoRemixControls?.show(drop, source);
      return;
    }
    if (action === "remix-video") { await remixPostVideo(drop); return; }
    if (action === "copy") { await copyDropLink(drop, source); return; }
    if (action === "send") { await openSendDrop(drop); return; }
    if (action === "nominate") { await toggleSignal(drop.id, "nominate", source); return; }
    if (action === "hide") {
      // Session-only: the Drop leaves this feed without a server record or a stored key.
      state.hiddenDropIds = state.hiddenDropIds || new Set();
      state.hiddenDropIds.add(drop.id);
      removeDrop(drop.id);
      renderFeed();
      if (state.view === "discover") renderDiscover();
      toast("Hidden from your feed for now");
      return;
    }
    if (action === "edit") { if (state.capabilities.community_writes) openComposer(drop); return; }
    if (action === "story") {
      if (!state.capabilities.community_writes || !state.capabilities.media_publication) return;
      if (!requireVerified()) return;
      try {
        await api("/api/flow/stories", {
          method: "POST", csrf: true, json: { drop_id: drop.id },
        });
        toast("Added to your Story");
        if (state.view === "flow" && state.flow.view === "stories") refreshFlow();
      } catch (error) { toast(error.message, true); }
      return;
    }
    if (action === "report") {
      openDropReport(drop, surface);
      return;
    }
    if (action === "delete") {
      if (!state.capabilities.community_writes) return;
      if (!state.viewer || drop.author_id !== state.viewer.id) {
        toast("Only the author can delete this Drop", true);
        return;
      }
      const owner = state.viewer.id, session = state.messages, csrf = state.csrf;
      const current = () => state.viewer?.id === owner && state.messages === session && state.csrf === csrf;
      if (!(await confirm(deleteDropPrompt(drop))) || !current()) return;
      const deletingOpenDetail = state.view === "drop"
        && location.pathname === `/d/${encodeURIComponent(drop.id)}`;
      try {
        await api(`/api/drops/${drop.id}`, { method: "DELETE", csrf: true });
        if (!current()) return;
        removeDrop(drop.id);
        if (deletingOpenDetail && state.view === "drop" && location.pathname === `/d/${encodeURIComponent(drop.id)}`) {
          history.replaceState({}, "", "/");
          await setView("all");
        } else {
          renderFeed();
          if (state.view === "discover") renderDiscover();
          if (state.view === "profile") renderProfileView();
          if (state.view === "flow") renderFlow();
        }
        toast("Drop deleted");
      }
      catch (error) { if (current()) toast(error.message, true); }
      return;
    }
    if (["block", "mute", "restrict"].includes(action)) {
      if (!requireVerified()) return;
      const viewerId = state.viewer.id;
      if (action === "block" && !(await confirm(`Block ${drop.author.display_name}? You will not see each other's Drops or comments, follows are removed, and private messages stop.`))) return;
      if (state.viewer?.id !== viewerId) return;
      const session = state.messages, csrf = state.csrf;
      try {
        await api("/api/relationships", { method: "POST", csrf: true, json: { target_user_id: drop.author_id, kind: action } });
        if (state.viewer?.id !== viewerId || state.messages !== session || state.csrf !== csrf) return;
        clearBusinessAds();
        state.relationships.loaded = false;
        // Blocking and muting hide the person from this viewer, so their cards go.
        // Restricting hides nothing from the restrictor -- it changes what everyone
        // else sees on the restrictor's own Drops -- so the feed is left alone.
        if (action !== "restrict") {
          // A block can hide an entire account family, not only this profile.
          state.feedWindows?.invalidate({ erase: true });
          state.drops = state.drops.filter((item) => item.author_id !== drop.author_id);
          renderFeed();
        }
        track(action, drop.id, {
          target_user_id: drop.author_id,
          ...(surface ? { surface } : {}),
        });
        const undo = action === "block" ? {} : { action: { label: "Undo", run: () => undoRelationship(action, drop.author) } };
        toast(`${{ block: "Blocked", restrict: "Restricted" }[action] || "Muted"} ${drop.author.display_name}`, false, undo);
      } catch (error) { toast(error.message, true); }
    }
  }

  async function copyDropLink(drop, source = null) {
    const url = new URL(`/d/${drop.id}`, location.origin).href;
    try { await navigator.clipboard.writeText(url); toast("Drop link copied"); }
    catch (_) { toast(url); }
    track("share", drop.id, surfaceMetadata(source));
  }

  async function shareDrop(drop, source = null) {
    await openSendDrop(drop, source);
  }

  // One report sheet for everything a person can report. The question and its fourteen reasons are
  // identical wherever you report from, so four copies of them were four places to fix anything and
  // three chances to miss one. What differs is the title, where it goes, what we count and what the
  // receipt says, and that is what a caller passes in.
  // The six categories /legal/law-enforcement promises escalate immediately rather than joining the
  // ordinary queue. Saying so at the moment one is chosen is the difference between a form and being
  // told what you are about to set in motion. tests/test_urgent_report_categories_are_selectable.py
  // holds this set to the one that page commits to, so the two cannot drift apart.
  const REPORT_GOES_TO_SAFETY = new Set([
    "child_safety", "self_harm_concern", "threat", "illegal_content", "doxxing", "ncii",
  ]);

  function syncReportGuidance() {
    const note = $("#report-urgent-note");
    const chosen = $("select[name='category']", el.reportForm)?.value || "";
    if (note) note.hidden = !REPORT_GOES_TO_SAFETY.has(chosen);
  }

  function openReport(request) {
    state.report = request;
    $("#report-title").textContent = request.title;
    el.reportForm.reset();
    hideError(el.reportError);
    // reset() clears the category, so the sheet opens promising nothing until one is chosen.
    const note = $("#report-urgent-note");
    if (note) note.hidden = true;
    openDialog(el.report);
  }

  async function submitReport(event) {
    event.preventDefault();
    const request = state.report;
    if (!request) return;
    hideError(el.reportError);
    const data = new FormData(el.reportForm);
    const category = data.get("category");
    const submit = $("button[type='submit']", el.reportForm);
    // A report takes a moment, and what it is about can move on while it is in flight.
    const current = () => state.report === request && (request.current ? request.current() : true);
    submit.disabled = true;
    try {
      const result = await api(request.endpoint(), {
        method: "POST", csrf: true, json: { category, details: data.get("details") },
      });
      if (!current()) return;
      el.report.close();
      el.reportForm.reset();
      state.report = null;
      request.done?.();
      request.track();
      toast(result?.created === false
        ? `Your report is already ${result.status}` : request.receipt(category));
    } catch (error) {
      if (!current()) return;
      const networkFailure = error?.name === "TypeError"
        && ["Failed to fetch", "Load failed", "NetworkError when attempting to fetch resource."].includes(error.message);
      // Every report now gets the care the Drop report already had: the draft stays where it is and
      // the person is told what to do about it, rather than being shown a raw fetch error.
      showError(el.reportError, networkFailure
        ? "We couldn't confirm your report. Your draft is still here. Check your connection and try again."
        : error.message);
    } finally { submit.disabled = false; }
  }

  function syncProfileAchievements() {
    const details = el.profileAchievements;
    const context = details._louderrContext;
    const userId = state.profile.data?.user?.id;
    if (!context || context.userId !== userId || context.routeRequestId !== state.routeRequestId) {
      details.open = false;
    }
    details._louderrContext = { userId, routeRequestId: state.routeRequestId };
    details.hidden = el.profileRank.hidden && el.profileStatus.hidden;
    if (details.hidden) details.open = false;
  }

  function renderProfileRank(summary) {
    clear(el.profileRank);
    if (!summary) { el.profileRank.hidden = true; syncProfileAchievements(); return; }
    const hasAchievement = Object.values(summary.achievements || {})
      .some((value) => typeof value === "number" && value > 0);
    if (!summary.rank && !hasAchievement && !(Number(summary.board_appearances) > 0)) {
      // Nothing to report yet: an empty ranking block is noise on a new profile.
      el.profileRank.hidden = true;
      syncProfileAchievements();
      return;
    }
    const rank = summary.rank ? `#${summary.rank} today` : "Not ranked today";
    const detail = summary.rank
      ? `${formatCount(summary.points)} momentum points · ${movementLabel(summary.movement)}`
      : "Rankings update as people interact.";
    const copy = create("span", "profile-rank-copy");
    copy.append(create("strong", "", rank), create("small", "", detail));
    const achievements = summary.achievements || {};
    const boardHistory = summary.board_history || {};
    const best = [
      achievements.highest_daily ? `Daily #${achievements.highest_daily}` : null,
      achievements.highest_weekly ? `Weekly #${achievements.highest_weekly}` : null,
      achievements.highest_monthly ? `Monthly #${achievements.highest_monthly}` : null,
      achievements.current_all_time ? `All-time #${achievements.current_all_time}` : null,
    ].filter(Boolean);
    if (best.length) copy.append(create("small", "profile-rank-best", best.join(" · ")));
    const stats = create("span", "profile-board-count");
    const statsParts = [
      achievements.daily_number_one_wins ? `${formatCount(achievements.daily_number_one_wins)} Daily #1 wins` : null,
      achievements.top_10_finishes ? `${formatCount(achievements.top_10_finishes)} Top 10 finishes` : null,
      summary.board_appearances ? `${formatCount(summary.board_appearances)} Board appearances` : null,
      boardHistory.first_appearance_at ? `First Board ${new Date(boardHistory.first_appearance_at).toLocaleDateString()}` : null,
      boardHistory.largest_verified_audience ? `${formatCount(boardHistory.largest_verified_audience)} largest verified Board audience` : null,
      boardHistory.peak_live_audience_status === "not_measured" ? "Peak live audience not measured" : null,
      boardHistory.best_response_points ? `${formatCount(boardHistory.best_response_points)} best Board response points` : null,
    ].filter(Boolean);
    stats.textContent = statsParts.join(" · ");
    el.profileRank.append(copy);
    if (statsParts.length) el.profileRank.append(stats);
    el.profileRank.hidden = false;
    syncProfileAchievements();
  }

  function renderProfileStatus() {
    if (state.followState) paintProfileFollowState();
    clear(el.profileStatus);
    const status = state.profile.status;
    if (!status) { el.profileStatus.hidden = true; syncProfileAchievements(); return; }
    const scout = status.scout || {};
    const counts = status.achievement_counts || {};
    const parts = [];
    if (scout.month?.rank) parts.push(`Scout #${scout.month.rank} this month`);
    if (scout.successful_early_finds) parts.push(`${formatCount(scout.successful_early_finds)} successful early ${scout.successful_early_finds === 1 ? "find" : "finds"}`);
    const wins = Object.entries(counts).filter(([key]) => key.startsWith("winner_")).reduce((sum, [, value]) => sum + Number(value || 0), 0);
    if (wins) parts.push(`${formatCount(wins)} ranking ${wins === 1 ? "record" : "records"}`);
    if (!parts.length) { el.profileStatus.hidden = true; syncProfileAchievements(); return; }
    el.profileStatus.append(create("strong", "", "Records"), create("span", "", parts.join(" · ")));
    el.profileStatus.hidden = false;
    syncProfileAchievements();
  }

  function closeProfileLists(path, pushHistory) {
    const lists = [el.comments].filter(Boolean);
    const owner = lists.find(dialog => dialog.dataset.historyEntry && history.state?.louderrDialog === dialog.id);
    const dismissSameProfile = pushHistory && owner?.dataset.historyEntry === "push"
      && history.state.louderrReturn === path && location.pathname === path;
    if (owner && !dismissSameProfile) {
      // A profile takes the place of the read-only list. Its queued close event must
      // not restore the old URL or leave another copy of that page in Back history.
      if (pushHistory && owner.dataset.historyEntry === "replace" && history.state.louderrReturn !== path) {
        history.replaceState({}, "", history.state.louderrReturn || "/");
        history.pushState({}, "", path);
      } else history.replaceState({}, "", pushHistory ? path : `${location.pathname}${location.search}`);
    }
    for (const dialog of lists) {
      if (dialog === owner && dismissSameProfile) dialog.dataset.userClose = "true";
      else {
        delete dialog.dataset.historyEntry;
        delete dialog.dataset.userClose;
      }
      if (dialog.open) dialog.close();
    }
    return Boolean(owner);
  }

  async function openCreator(user, profileResult = null, pushHistory = true) {
    state.connectionsPanel?.close({ navigation: true });
    state.followRequests?.close({ navigation: true });
    state.live?.cancelWatch?.();
    state.messageSearch?.close();
    state.messageShared?.close();
    state.messageAlbums?.close();
    state.messages.searchWindow = null;
    state.routeRequestId = (state.routeRequestId || 0) + 1;
    state.messages.threadRequestId += 1;
    state.messages.selectionVersion += 1;
    if (state.comments?.inlineDropId && typeof unmountInlineComments === "function") unmountInlineComments();
    stopInactiveBrowseWork("profile");
    const path = `/u/${encodeURIComponent(user.handle)}`;
    const replacedList = closeProfileLists(path, pushHistory);
    if (pushHistory && !replacedList && `${location.pathname}${location.search}` !== path) {
      history.pushState({}, "", path);
    }
    state.socialExtras?.leaveProfileReposts?.({ returnScroll: !pushHistory ? history.state?.louderrScroll : null });
    state.view = "profile";
    placeToast();
    state.feedRequestId += 1;
    state.authorId = null;
    document.body.dataset.view = "profile";
    globalThis.LouderrHome?.show(false);
    updateSurfaceChrome();
    syncMessageThreadClass();
    el.pageContext.hidden = true;
    state.profile.handle = user.handle;
    el.trendingSection.hidden = true;
    el.flowSection.hidden = true;
    el.chatSection.hidden = true;
    el.discoverSection.hidden = true;
    el.notificationsSection.hidden = true;
    el.messagesSection.hidden = true;
    const creativeSection = $("#creative"); if (creativeSection) creativeSection.hidden = true;
    // A profile can be reached by Back from Settings. Settings is a sibling screen, not a
    // child of the profile, so clear it explicitly when profile navigation bypasses setView().
    if (el.settings) el.settings.hidden = true;
    el.boardSection.hidden = true;
    el.feedSection.hidden = true;
    el.categoryRail.hidden = true;
    el.categoryStrip.hidden = true;
    el.mobileSearch.hidden = true;
    el.profileSection.hidden = false;
    $$("button[data-view]").forEach((button) => {
      button.classList.remove("is-active");
      button.removeAttribute("aria-current");
    });
    // The Profile tab lights up only for the viewer's own profile, like a native tab bar.
    $$("[data-open-self-profile]").forEach((button) => {
      const own = Boolean(state.viewer && user.id === state.viewer.id);
      button.classList.toggle("is-active", own);
      if (own) button.setAttribute("aria-current", "page");
      else button.removeAttribute("aria-current");
    });
    el.pageTitle.textContent = user.display_name;
    el.pageDescription.textContent = `@${user.handle}`;
    document.title = `${user.display_name} · ${state.productName}`;
    const loading = refreshProfile(user.handle, false, profileResult, user);
    scrollTo({ top: 0, behavior: "instant" });
    const loaded = await loading;
    if (!loaded) return;
    const creator = state.profile.data?.user || user;
    track("profile_view", null, { target_user_id: creator.id });
  }

  function openSelfProfile() {
    if (!state.viewer) {
      openAccountDialog();
      return;
    }
    if (state.ageAttestationRequired || state.ageRequirementNotMet) { openAccountDialog("profile"); return; }
    if (el.account.open) el.account.close();
    openCreator(state.viewer);
  }

  function openDrop(id, dialog = null) {
    const path = `/d/${encodeURIComponent(id)}`;
    // A read-only history sheet gives its temporary entry to the post. Closing
    // it must not queue a Back or leave a closed dialog in the return history.
    if (dialog?.dataset.historyEntry && history.state?.louderrDialog === dialogName(dialog)) {
      history.replaceState({}, "", path);
    } else if (`${location.pathname}${location.search}${location.hash}` !== path) {
      history.pushState({}, "", path);
    }
    if (dialog) {
      delete dialog.dataset.historyEntry;
      delete dialog.dataset.userClose;
      if (dialog.open) dialog.close();
    }
    const loading = routeLocation();
    scrollTo({ top: 0, behavior: "instant" });
    return loading;
  }

  async function routeLocation() {
    // The age step lives on the account screen. A guest sign-in dialog above it makes
    // the date field inert, so route new/recovered accounts there without an overlay.
    if (state.viewer && (state.ageAttestationRequired || state.ageRequirementNotMet)
        && !["settings", "messages"].includes(location.pathname.split("/")[1])) {
      history.replaceState({}, "", "/settings/profile");
    }
    globalThis.louderrSoundPlayback?.syncScope();
    const routeRequestId = state.routeRequestId = (state.routeRequestId || 0) + 1;
    const routePath = location.pathname;
    const viewerId = state.viewer?.id || null;
    const socialState = state.messages;
    const current = () => routeRequestId === state.routeRequestId && routePath === location.pathname
      && (state.viewer?.id || null) === viewerId
      && state.messages === socialState;
    const parts = location.pathname.split("/").filter(Boolean);
    const routeQuery = new URLSearchParams(location.search);
    state.connectionsPanel?.close({ navigation: true });
    if (parts[0] !== "notifications" || routeQuery.get("follow_requests") !== "1") state.followRequests?.close({ navigation: true });
    if (!parts.length) {
      await setView("all");
    } else if (parts[0] === "discover") {
      const mode = routeQuery.get("mode");
      if (["search", "undiscovered", "right-now", "rising", "events", "world", "records"].includes(mode)) {
        state.discovery.mode = mode;
      } else state.discovery.mode = "search";
      if (state.discovery.mode === "search") applyDiscoverSearchRoute(routeQuery);
      if (state.discovery.mode === "world" && ["pulse", "questions", "polls", "collaborations"].includes(routeQuery.get("view"))) {
        state.world.view = routeQuery.get("view");
      }
      state.world.pendingFocus = state.discovery.mode === "world" && state.world.view === "pulse"
        && ["pulse", "mic", "moment"].includes(routeQuery.get("focus")) ? routeQuery.get("focus") : null;
      if (state.discovery.mode === "records" && ["scouts", "winners", "board", "capsules"].includes(routeQuery.get("view"))) {
        state.records.view = routeQuery.get("view");
      }
      if (state.records.view === "board" && routeQuery.get("slot")) state.records.pendingSlotId = routeQuery.get("slot");
      await setView("discover");
    } else if (parts[0] === "flow") {
      if (["feed", "now", "stories", "history"].includes(routeQuery.get("view"))) {
        state.flow.view = routeQuery.get("view");
      } else state.flow.view = "feed";
      if (["now", "rising", "following", "random"].includes(routeQuery.get("lens"))) {
        state.flow.storyLens = routeQuery.get("lens");
      }
      await setView("flow");
    } else if (parts[0] === "studio") {
      setView("all");
      void openStudioStart();
    } else if (["chat", "trending", "following", "topics", "unseen", "notifications", "saved", "mine", "advertise", "premium", "creative"].includes(parts[0])) {
      if (!state.viewer && ["following", "topics", "notifications", "saved", "mine"].includes(parts[0])) {
        // A cold link to a personal screen should have the same exit as the in-app guard above.
        // Replace the protected URL before opening the sheet so Close returns to public Home.
        history.replaceState({}, "", "/");
        setView("all");
        openAccountDialog();
        return;
      }
      if (parts[0] === "saved") state.savedFolderId = routeQuery.get("folder") || null;
      const loading = setView(parts[0]);
      // Opening /saved directly does not go through openLens, so the folders would
      // never load for anyone who followed a link or refreshed the page. Start
      // these controls with the page, before an old read can outlive its route.
      if (parts[0] === "saved" && state.view === "saved") {
        if (state.viewer) void refreshSaveFolders();
      }
      if (parts[0] === "notifications" && state.view === "notifications" && routeQuery.get("follow_requests") === "1") {
        void state.followRequests?.open();
      }
      if (parts[0] === "notifications" && state.view === "notifications" && routeQuery.get("coauthor")) {
        state.coauthors?.show(routeQuery.get("coauthor"), null, `${location.pathname}${location.search}`);
      }
      const accepted = await loading;
      if (parts[0] === "notifications") return accepted;
    } else if (parts[0] === "messages" && parts[1] === "join") {
      if (!state.viewer) {
        history.replaceState({}, "", "/");
        setView("all");
        openAccountDialog();
        return;
      }
      // Apply route intent before any inbox read can yield to a newer navigation.
      setView("messages"); closeMessageThread(false); state.groupInvites?.showJoin();
    } else if (parts[0] === "messages") {
      if (!state.viewer) {
        history.replaceState({}, "", "/");
        setView("all");
        openAccountDialog();
        return;
      }
      const inbox = setView("messages");
      // A linked conversation is the primary content. Its read and selection must
      // not wait for the inbox (or run late over a newer route after it settles).
      if (parts[1]) await openMessageThread(parts[1], false);
      else { closeMessageThread(false); return await inbox; }
    } else if (parts[0] === "stories" && parts[1]) {
      setView("all");
      void state.storiesV2?.openItem(parts[1]);
    } else if (parts[0] === "live" && parts[1]) {
      setView("all");
      void state.live?.watch(parts[1]);
    } else if (parts[0] === "settings" && ["time", "supervision", "close-friends"].includes(parts[1])) {
      setView("all");
      if (parts[1] === "time") void state.socialExtras?.openTimeSettings();
      else if (parts[1] === "supervision") {
        const inviteToken = new URLSearchParams(location.hash.slice(1)).get("invite");
        // The token works once; it should not stay in the address bar or the history.
        if (inviteToken) history.replaceState(history.state, "", location.pathname);
        void state.socialExtras?.openSupervision({ inviteToken });
      } else void state.storiesV2?.openCloseFriends();
    } else if (parts[0] === "settings") {
      if (!state.viewer) setView("all");
      const actionId = routeQuery.get("moderation_action");
      if (actionId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(actionId)) {
        openAccountDialog("profile");
        if (!state.viewer) return;
        const navigationVersion = state.routeRequestId;
        const enforcement = state.enforcement;
        const query = location.search;
        const refreshed = await refreshEnforcement(false);
        if (!refreshed || state.routeRequestId !== navigationVersion || location.pathname !== routePath
            || location.search !== query || state.viewer?.id !== viewerId || state.enforcement !== enforcement
            || state.view !== "settings" || state.accountSection !== "profile") return;
        const card = Array.from(el.enforcementList.children).find((item) => item.dataset.moderationAction === actionId);
        if (card) {
          card.focus({ preventScroll: true });
          card.scrollIntoView({ block: "center", behavior: motionBehavior() });
        } else toast("This decision is not available for this account", true);
      } else openAccountDialog(accountSections.has(parts[1]) ? parts[1] : "settings");
    } else if (parts[0] === "create") {
      setView("all");
      if (state.viewer) openComposer();
      else {
        document.title = `Create · ${state.productName}`;
        openDialog(el.account);
      }
    } else if (parts[0] === "admin") {
      setView("all");
      if (state.viewer && staffRoles.has(state.viewer.role)) {
        const navigationVersion = state.routeRequestId;
        await openAdmin();
        const caseId = routeQuery.get("safety_case");
        if (navigationVersion !== state.routeRequestId || location.pathname !== "/admin"
          || (state.viewer?.id || null) !== viewerId || !adminWorkspaceCurrent(state.adminWorkspace)) return;
        if (caseId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(caseId)) {
          state.adminWorkspace.panels.get("safety-reports").filters.reportId = caseId;
          await selectAdminSection("safety-reports", true);
        }
      }
      else {
        openDialog(el.account);
        toast("Moderator access is required");
      }
    } else if (parts[0] === "d" && parts[1]) {
      const query = location.search;
      const loading = setView("drop");
      const navigationVersion = state.routeRequestId;
      await loading;
      if (state.routeRequestId !== navigationVersion || location.pathname !== routePath || location.search !== query
          || (state.viewer?.id || null) !== viewerId || state.messages !== socialState) return;
      const drop = state.view === "drop" && location.pathname.split("/")[2] === parts[1]
        ? state.drops.find((item) => item.id === parts[1]) : null;
      // Once, on arrival. Deliberately not part of realtime reconciliation: the
      // list cannot change in realtime, and re-running its bounded scan on every
      // reconcile would be exactly the read-time cost this feature avoids.
      // A failure leaves the Drop readable and the section absent.
      if (drop) loadRelatedDrops(drop.id);
      if (drop?.journey?.root_drop_id && (routeQuery.get("project") === "1" || routeQuery.get("respond") === "1")) {
        await openJourney(drop.journey.root_drop_id, routeQuery.get("respond") === "1");
      } else if (drop && routeQuery.get("comments") === "1") await openComments(drop);
    } else if (parts[0] === "e" && parts[1]) {
      state.discovery.mode = "events";
      state.events.focusId = parts[1];
      state.events.when = "upcoming";
      state.events.items = [];
      state.events.loaded = false;
      await setView("discover");
    } else if (parts[0] === "t" && parts[1]) {
      await openTopic(decodeURIComponent(parts[1]), false);
    } else if (parts[0] === "u" && parts[1]) {
      try {
        const handle = decodeURIComponent(parts[1]);
        const user = state.viewer?.handle?.toLowerCase() === handle.toLowerCase()
          ? state.viewer : { handle, display_name: "Profile" };
        await openCreator(user, null, false);
      }
      catch (error) { if (current()) toast(error.message, true); }
    }
  }

  async function reconcileRealtimeState(snapshotPayload) {
    const paidContentVersion = state.paidContentVersion || 0;
    const socialBefore = state.messages;
    const conversationRequestBefore = socialBefore.conversationRequestId;
    const conversationFolderBefore = socialBefore.folder || "inbox";
    const authCsrfBefore = state.csrf;
    const activeNotifications = state.notifications;
    const activeNotificationRequestId = activeNotifications?.requestId;
    const activeView = state.view;
    const activePath = location.pathname;
    const activeProfileState = state.profile;
    const activeProfileRequestId = activeProfileState.requestId;
    const activeFeedRequest = state.feedRequestId;
    const activeFeedCache = state.feedWindows, activeFeedWindow = activeFeedCache?.active;
    const activeFeedQuery = ["all", "following", "saved", "mine", "topics", "topic"].includes(activeView) ? feedQuery() : null;
    const activeFeedMutation = state.feedMutationVersion || 0;
    const activeFilter = state.filter;
    const activeWorldView = state.world.view;
    const activeDiscoveryState = state.discovery;
    const activeDiscoveryRequestId = activeDiscoveryState?.requestId;
    const activeRecordView = state.records.view;
    const activeFlowState = state.flow;
    const activeFlowView = state.flow.view;
    const activeFlowRequestId = state.flow.requestId;
    const activeStoryLens = state.flow.storyLens;
    const activeProfileHandle = state.profile.handle || (location.pathname.startsWith("/u/") ? decodeURIComponent(location.pathname.split("/")[2] || "") : null);
    const activeConversationId = state.messages.selectedId;
    const activeThreadRequest = socialBefore.threadRequestId, activeThreadSelection = socialBefore.selectionVersion;
    const activeThreadSearch = socialBefore.searchWindow;
    const activeSearchState = state.search;
    const activeSearchRequestId = activeSearchState?.requestId;
    const activeSearchScope = [state.query, activeSearchState?.type, activeSearchState?.category,
      activeSearchState?.loadedKey, activeSearchState?.renderedKey];
    const viewerObjectBefore = state.viewer;
    const viewerBefore = viewerObjectBefore?.id || null;

    const authResult = await api("/api/auth/me");
    if (state.messages !== socialBefore || state.csrf !== authCsrfBefore || state.viewer !== viewerObjectBefore
        || (state.viewer?.id || null) !== viewerBefore) return;
    const viewerAfter = authResult.user?.id || null;
    state.viewer = authResult.user;
    state.csrf = authResult.csrf_token;
    state.sessionRestricted = Boolean(authResult.session_restricted);
    state.ageAttestationRequired = Boolean(authResult.age_attestation_required);
    state.ageRequirementNotMet = Boolean(authResult.age_requirement_not_met);
    state.minimumAge = Number(authResult.minimum_age) || 16;
    if (viewerBefore !== viewerAfter) resetSocialState();
    renderAccount();
    const viewerObjectAfter = state.viewer;
    const messagesState = state.messages;
    const profileState = state.profile;
    const profileRequestId = profileState.requestId;
    // Flow reads below start after auth has installed its current token.
    const flowReadCsrf = state.csrf;
    const flowReadCapabilityProfile = state.capabilities?.profile;
    const flowReadMediaPublication = state.capabilities?.media_publication;
    const accountCurrent = () => state.messages === messagesState && state.viewer === viewerObjectAfter
      && (state.viewer?.id || null) === viewerAfter;
    const chatSnapshot = chatContext();
    const chatRequestId = chatSnapshot.chat.requestId;
    const chatMutationVersion = chatSnapshot.chat.mutationVersion;
    const chatUnreadVersion = chatSnapshot.chat.unreadVersion.get(chatSnapshot.room) || 0;

    const requests = new Map();
    let storyReadStartedAt = null;
    const add = (name, promise) => requests.set(name, promise);
    const optionalTrending = (path) => api(path).catch((error) => {
      const transient = !error.status || error.status === 429 || (error.status >= 500 && error.status <= 599);
      if (!transient) throw error;
      return { unavailable: true, items: [] };
    });
    const roomQuery = "/api/state?include_feed=false";
    add("room", api(roomQuery));
    add("trendingPreview", optionalTrending("/api/trending?scope=drops&period=day&limit=4"));

    const floorViews = new Set(["all", "following", "saved", "mine", "topics", "topic"]);
    if (floorViews.has(activeView) && state.view === activeView && location.pathname === activePath
        && feedQuery() === activeFeedQuery) {
      const sameFeed = viewerBefore === viewerAfter && state.feedRequestId === activeFeedRequest
        && state.feedWindows === activeFeedCache && state.feedWindows?.active === activeFeedWindow;
      const resetFeed = viewerBefore !== viewerAfter && accountCurrent();
      if (sameFeed || resetFeed) {
        const refresh = refreshFeed(false, true);
        const cache = state.feedWindows, window = cache?.active, requestId = state.feedRequestId;
        const controller = state.feedController, csrf = state.csrf, query = feedQuery();
        add("feedRefresh", refresh.then((ok) => {
          const current = accountCurrent() && state.csrf === csrf && state.view === activeView
            && location.pathname === activePath && query === feedQuery() && state.feedRequestId === requestId
            && state.feedWindows === cache && cache?.active === window && cache?.current(window)
            && (!controller || !controller.signal.aborted) && (!state.feedController || state.feedController === controller);
          if (ok === false && current) throw new Error("Feed recovery could not be completed.");
          return ok;
        }));
      }
    }
    if (activeView === "drop") {
      const dropId = location.pathname.split("/")[2];
      if (dropId) add("dropDetail", api(`/api/drops/${encodeURIComponent(dropId)}`));
    }
    if (activeView === "trending") {
      const params = new URLSearchParams({ scope: state.trending.scope, period: state.trending.period, limit: "100" });
      if (state.trending.category !== "all") params.set("kind", state.trending.category);
      add("trending", optionalTrending(`/api/trending?${params}`));
    }
    if (activeView === "flow") {
      if (activeFlowView === "stories") storyReadStartedAt = performance.now();
      const flowRequest = activeFlowView === "feed" ? revalidateFlowPages()
        : activeFlowView === "now" ? api("/api/flow/now")
          : activeFlowView === "stories"
            ? revalidateStoryPages().catch((storyError) => ({ storyError }))
            : state.viewer ? api("/api/flow/history/me?limit=50") : Promise.resolve({ items: [] });
      add("flow", flowRequest);
    }
    if (activeView === "discover") {
      const mode = state.discovery.mode;
      if (mode === "search") {
        const scope = [state.query, state.search?.type, state.search?.category,
          state.search?.loadedKey, state.search?.renderedKey];
        const sameSearch = viewerBefore === viewerAfter
          && state.search === activeSearchState && state.search.requestId === activeSearchRequestId
          && state.discovery === activeDiscoveryState && state.discovery.requestId === activeDiscoveryRequestId
          && scope.every((value, index) => value === activeSearchScope[index]);
        const resetSearch = viewerBefore !== viewerAfter && accountCurrent()
          && scope.slice(0, 3).every((value, index) => value === activeSearchScope[index]);
        if (state.view === activeView && location.pathname === activePath && (sameSearch || resetSearch)) {
          const refresh = refreshDiscover(null, 1, null, { keepPendingPeople: true });
          const search = state.search, discovery = state.discovery;
          const searchRequestId = search.requestId, discoveryRequestId = discovery.requestId;
          const controller = search.controller, identity = state.messages, csrf = state.csrf;
          const requestKey = discoverSearchKey();
          add("discoverRefresh", refresh.then((ok) => {
            const current = state.view === activeView && location.pathname === activePath
              && accountCurrent() && state.messages === identity && state.csrf === csrf
              && state.search === search && search.requestId === searchRequestId
              && state.discovery === discovery && discovery.requestId === discoveryRequestId
              && discovery.mode === "search" && requestKey === discoverSearchKey()
              && (!controller || !controller.signal.aborted)
              && (!search.controller || search.controller === controller);
            // The interactive reader handles its own UI; required recovery must still fail.
            if (ok === false && current) throw new Error("Search recovery could not be completed.");
            return ok;
          }));
        }
      }
      else if (["undiscovered", "right-now", "rising"].includes(mode)) {
        const params = new URLSearchParams({ limit: "30" });
        if (state.search.category !== "all") params.set("kind", state.search.category);
        add("discoverSurface", api(`/api/discovery/${mode}?${params}`));
      } else if (mode === "world") {
        const plan = worldReadPlan(activeWorldView);
        const batch = beginWorldRead(plan.keys);
        const worldRequests = plan.paths.map(path => api(path));
        add("world", Promise.allSettled(worldRequests).then((results) => ({ view: activeWorldView, batch, results })));
      } else if (mode === "records") {
        let recordRequests;
        if (activeRecordView === "board") {
          const period = state.records.period === "day" ? "today" : state.records.period;
          recordRequests = [api(`/api/board/history?${new URLSearchParams({ period, limit: "100" })}`)];
        } else if (activeRecordView === "capsules") {
          recordRequests = [api("/api/time-capsules/today"), api("/api/time-capsules?limit=60")];
        } else {
          const mainPath = activeRecordView === "scouts"
            ? `/api/status/scouts?${new URLSearchParams({ period: state.records.period, limit: "100" })}`
            : `/api/status/winners?${new URLSearchParams({ period: state.records.period, scope: state.records.scope, limit: "12" })}`;
          recordRequests = [api(mainPath)];
          if (state.viewer) recordRequests.push(api("/api/status/first-here"), api("/api/status/scout"));
        }
        add("records", Promise.all(recordRequests).then((results) => ({ view: activeRecordView, results })));
      }
    }
    if (activeView === "profile" && activeProfileHandle && accountCurrent()
        && state.view === activeView && location.pathname === activePath
        && state.profile === profileState && profileState.requestId === profileRequestId
        && (viewerBefore !== viewerAfter || (profileState === activeProfileState
          && profileRequestId === activeProfileRequestId && profileState.handle === activeProfileHandle))) {
      // Share the route's depth, identity and atomic membership guards after auth recovery.
      const profileRead = refreshProfile(activeProfileHandle);
      const requiredProfileRequestId = profileState.requestId;
      const requiredProfileController = profileState.controller;
      const requiredProfileCsrf = state.csrf, requiredProfileRoute = state.routeRequestId;
      add("profileRefresh", profileRead.then(refreshed => {
        // Interactive reads handle their own Retry UI, but current membership is
        // required before the socket may certify its snapshot as reconciled.
        if (refreshed === false && accountCurrent() && state.csrf === requiredProfileCsrf
            && state.view === activeView && location.pathname === activePath
            && state.routeRequestId === requiredProfileRoute && state.profile === profileState
            && profileState.handle === activeProfileHandle
            && profileState.requestId === requiredProfileRequestId
            && profileState.controller === requiredProfileController
            && !requiredProfileController.signal.aborted) {
          throw new Error("Profile state recovery failed.");
        }
      }));
    }

    const chatWasLoaded = state.chat.loaded || activeView === "chat"
      || (activeView === "flow" && activeFlowView === "now");
    if (chatWasLoaded) {
      const params = new URLSearchParams({ room: chatSnapshot.room, lens: chatSnapshot.lens, limit: "60" });
      add("chat", api(`/api/chat/messages?${params}`));
      if (activeView === "flow" && activeFlowView === "now" && chatSnapshot.room !== "global") {
        add("flowChat", api("/api/chat/messages?room=global&limit=40"));
      }
    }
    if (state.viewer?.status === "active" && !state.sessionRestricted) {
      add("chatRead", api(`/api/chat/read-state?room=${encodeURIComponent(chatSnapshot.room)}`));
      const notificationReadUnchanged = viewerBefore !== viewerAfter
        || (state.notifications === activeNotifications && state.notifications.requestId === activeNotificationRequestId);
      if (notificationReadUnchanged) {
        const readingNotifications = refreshNotifications(false, true);
        const notificationState = state.notifications;
        const notificationRequestId = notificationState.requestId;
        const notificationOwner = { viewerId: state.viewer?.id, social: state.messages, csrf: state.csrf };
        add("notifications", readingNotifications.then((accepted) => {
          if (accepted === false && state.notifications === notificationState
              && notificationState.requestId === notificationRequestId
              && state.viewer?.id === notificationOwner.viewerId && state.messages === notificationOwner.social
              && state.csrf === notificationOwner.csrf) throw new Error("Notification refresh interrupted");
          return accepted;
        }));
      }
      const conversationReadUnchanged = viewerBefore !== viewerAfter
        || (messagesState === socialBefore && messagesState.conversationRequestId === conversationRequestBefore
          && (messagesState.folder || "inbox") === conversationFolderBefore);
      if (conversationReadUnchanged) {
        const readingConversations = refreshConversations(true);
        const conversationRequestId = messagesState.conversationRequestId;
        const conversationCsrf = state.csrf, conversationFolder = messagesState.folder || "inbox";
        add("conversations", readingConversations.then(accepted => {
          if (accepted === false && accountCurrent() && state.csrf === conversationCsrf
              && messagesState.conversationRequestId === conversationRequestId
              && (messagesState.folder || "inbox") === conversationFolder) throw new Error("Inbox refresh interrupted");
          return accepted;
        }));
      }
      if (state.viewer.email_verified) add("relationships", api("/api/relationships"));
      if (activeView === "messages" && state.view === activeView && location.pathname === activePath
          && viewerBefore === viewerAfter && messagesState === socialBefore && activeConversationId
          && messagesState.selectedId === activeConversationId && messagesState.threadRequestId === activeThreadRequest
          && messagesState.selectionVersion === activeThreadSelection && !activeThreadSearch && !messagesState.searchWindow) {
        const readingThread = openMessageThread(activeConversationId, false, false, null, { refreshDetail: true });
        const threadRequest = messagesState.threadRequestId, threadCsrf = state.csrf;
        add("selectedConversation", readingThread.then(accepted => {
          if (accepted === false && accountCurrent() && state.csrf === threadCsrf
              && state.view === activeView && location.pathname === activePath
              && messagesState.selectedId === activeConversationId && messagesState.threadRequestId === threadRequest
              && messagesState.selectionVersion === activeThreadSelection && !messagesState.searchWindow) {
            throw new Error("Message history refresh interrupted");
          }
          return accepted;
        }));
      }
    }

    const entries = await Promise.all([...requests].map(async ([name, promise]) => [name, await promise]));
    if (!accountCurrent()) return;
    const result = Object.fromEntries(entries);
    const room = result.room || snapshotPayload;
    if ((state.viewer?.id || null) === viewerAfter && (state.paidContentVersion || 0) === paidContentVersion) {
      state.board = room.board;
      state.boardQueue = room.board_queue || { items: [], eligible_count: 0, locked_count: 0 };
    }
    state.stats = room.stats || state.stats;
    state.presence = room.presence || 0;
    if (room.product_name) applyProductName(room.product_name);
    if (room.translation) {
      state.translation = room.translation;
      renderTranslationPreferences();
    }
    if (typeof room.presence_is_approximate === "boolean") {
      el.presenceNote.textContent = room.presence_is_approximate ? "The online count is approximate." : "";
    }
    renderBoard();
    renderStats();

    const feedStillCurrent = state.view === activeView && location.pathname === activePath
      && state.filter === activeFilter && state.feedRequestId === activeFeedRequest
      && activeFeedMutation === (state.feedMutationVersion || 0)
      && viewerAfter === (state.viewer?.id || null);
    if (result.dropDetail && feedStillCurrent && state.view === "drop") {
      state.drops = [result.dropDetail];
      state.nextCursor = null;
      renderFeed();
    }
    renderTrendingPreview(result.trendingPreview);
    if (result.trending && state.view === "trending") {
      state.trending.data = result.trending;
      renderTrending();
    }
    if (result.flow && state.view === "flow" && state.flow === activeFlowState
      && state.flow.view === activeFlowView && state.flow.requestId === activeFlowRequestId
      && state.csrf === flowReadCsrf
      && (activeFlowView !== "feed" || (state.capabilities?.profile === flowReadCapabilityProfile
        && state.capabilities?.media_publication === flowReadMediaPublication))
      && (activeFlowView !== "stories" || state.flow.storyLens === activeStoryLens)) {
      if (activeFlowView === "feed") acceptFlowPage(result.flow);
      else if (activeFlowView === "now") state.flow.now = result.flow;
      else if (activeFlowView === "stories") {
        if (result.flow.storyError) recordStoryReadFailure(result.flow.storyError);
        else { el.flowError.hidden = true; acceptStorySnapshot(result.flow, storyReadStartedAt); }
      }
      else {
        state.flow.history = result.flow.items || [];
        state.flow.historyCursor = result.flow.next_cursor || null;
      }
      renderFlow();
    }
    if (result.discoverSurface && state.view === "discover") {
      state.discovery.data = result.discoverSurface;
      state.discovery.items = result.discoverSurface.items || [];
      renderDiscover();
    }
    if (result.world && state.view === "discover" && state.discovery.mode === "world"
        && state.discovery === activeDiscoveryState && state.discovery.requestId === activeDiscoveryRequestId) {
      const { view, batch, results } = result.world;
      if (state.world.view === view && acceptWorldRead(batch, results)) renderWorld();
    }
    if (result.records && state.view === "discover" && state.discovery.mode === "records") {
      const { view, results } = result.records;
      if (state.records.view === view) {
        state.records.data = view === "capsules" ? { today: results[0], items: results[1].items || [] } : results[0];
        state.records.mine = !["board", "capsules"].includes(view) && state.viewer ? { firstHere: results[1], scout: results[2] } : null;
        renderRecords();
      }
    }
    if (result.chat && chatSnapshot.selected() && chatSnapshot.chat.requestId === chatRequestId) {
      const merged = new Map(result.chat.items.map((item) => [item.id, item]));
      for (const { message, version } of state.chat.mutations.values()) {
        if (version <= chatMutationVersion || (message.room || "global") !== chatSnapshot.room) continue;
        if (chatSnapshot.lens === "all" || (message.mentions || []).some((user) => user.id === state.viewer?.id) || merged.has(message.id)) merged.set(message.id, message);
      }
      state.chat.items = [...merged.values()].sort((a, b) => new Date(a.created_at) - new Date(b.created_at) || a.id.localeCompare(b.id));
      state.chat.nextCursor = result.chat.next_cursor || null;
      state.chat.loaded = true;
      if (result.chat.read_state && chatUnreadVersion === (state.chat.unreadVersion.get(chatSnapshot.room) || 0)) state.chat.roomUnread.set(chatSnapshot.room, result.chat.read_state.unread_count || 0);
      renderChat();
      if (chatSnapshot.room === "global" && state.view === "flow" && state.flow.view === "now") {
        state.flow.chatItems = state.chat.items.slice(-60);
        renderFlowLiveChat();
      }
    }
    if (result.flowChat && accountCurrent() && state.view === "flow" && state.flow.view === "now") {
      const merged = new Map((result.flowChat.items || []).map((item) => [item.id, item]));
      for (const { message, version } of state.chat.mutations.values()) {
        if (version > chatMutationVersion && (message.room || "global") === "global") merged.set(message.id, message);
      }
      state.flow.chatItems = [...merged.values()].sort((a, b) => new Date(a.created_at) - new Date(b.created_at) || a.id.localeCompare(b.id)).slice(-60);
      renderFlowLiveChat();
    }
    if (result.chatRead && chatSnapshot.current() && chatUnreadVersion === (state.chat.unreadVersion.get(chatSnapshot.room) || 0)) state.chat.roomUnread.set(chatSnapshot.room, result.chatRead.unread_count || 0);
    updateChatBadges();
    if (result.relationships && accountCurrent()) {
      state.relationships.items = result.relationships.items || [];
      state.relationships.loaded = true;
      state.relationships.loading = false;
      renderRelationships();
    }

  }

  function failRealtimeRecovery(socket, generation) {
    if (generation !== state.realtime.generation) return;
    state.eventCursor = 0;
    state.realtime.recovery = null;
    setConnection("offline", "Reconnecting");
    document.dispatchEvent(new CustomEvent("lobby:realtime-recovery-failed"));
    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      socket.close(4001, "State recovery failed");
    }
  }

  async function handleRealtimeFrame(socket, generation, message) {
    if (generation !== state.realtime.generation || socket !== state.socket) return;
    if (message.type === "snapshot") {
      const snapshotCursor = Number.isInteger(message.id) ? message.id : 0;
      state.realtime.recovery = {
        snapshotCursor,
        requested: Boolean(message.payload?.replay_requested),
        gap: false,
        replayCount: 0,
        reconciled: false,
      };
      await reconcileRealtimeState(message.payload || {});
      if (generation !== state.realtime.generation || socket !== state.socket) return;
      state.realtime.recovery.reconciled = true;
      return;
    }

    const recovery = state.realtime.recovery;
    if (message.type === "replay.gap") {
      if (!recovery) throw new Error("Replay gap arrived without a snapshot.");
      recovery.gap = true;
      return;
    }
    if (message.type === "replay.complete") {
      if (!recovery || !recovery.reconciled) throw new Error("Replay completed before reconciliation.");
      const availableThrough = Number(message.payload?.available_through);
      if (!Number.isInteger(availableThrough) || availableThrough !== recovery.snapshotCursor || message.id !== recovery.snapshotCursor) {
        throw new Error("Replay completion cursor did not match the authoritative snapshot.");
      }
      state.eventCursor = availableThrough;
      state.realtime.recovery = null;
      state.reconnectAttempt = 0;
      setConnection("live", "Live");
      document.dispatchEvent(new CustomEvent("lobby:realtime-recovered", {
        detail: { cursor: availableThrough, gap: recovery.gap, replayCount: recovery.replayCount, requested: recovery.requested },
      }));
      return;
    }

    if (recovery) {
      if (Number.isInteger(message.id) && message.id <= recovery.snapshotCursor) {
        recovery.replayCount += 1;
        return;
      }
      throw new Error("A live frame arrived before replay completion.");
    }
    if (Number.isInteger(message.id) && message.id <= state.eventCursor) return;
    applyEvent(message.type, message.payload || {});
    if (Number.isInteger(message.id)) state.eventCursor = message.id;
  }

  const signedOutRealtimeLabel = "Updates refresh with the page";

  function realtimeOwnerId() {
    return state.viewer?.status === "active" && !state.sessionRestricted ? state.viewer.id : null;
  }

  function stopRealtime(label = signedOutRealtimeLabel) {
    clearTimeout(state.reconnectTimer);
    clearInterval(state.heartbeatTimer);
    stopChatTyping({ clearPeople: true });
    const socket = state.socket;
    state.socket = null;
    state.reconnectTimer = null;
    state.heartbeatTimer = null;
    state.reconnectAttempt = 0;
    state.realtime.generation += 1;
    state.realtime.chain = Promise.resolve();
    state.realtime.recovery = null;
    state.realtime.ownerUserId = null;
    if (socket && socket.readyState <= WebSocket.OPEN) {
      socket.close(4000, "Realtime session ended");
    }
    setConnection("offline", label);
  }

  function syncRealtimeWithAuth({ restart = false } = {}) {
    const ownerUserId = realtimeOwnerId();
    if (!ownerUserId) {
      stopRealtime();
      return;
    }
    const currentOwnedSocket = state.realtime.ownerUserId === ownerUserId
      && state.socket && state.socket.readyState <= WebSocket.OPEN;
    const ownershipChanged = state.socket && state.realtime.ownerUserId !== ownerUserId;
    if (restart || ownershipChanged) {
      stopRealtime("Connecting");
    }
    if (!currentOwnedSocket || restart) connectSocket(ownerUserId);
  }

  function connectSocket(ownerUserId = realtimeOwnerId()) {
    if (!ownerUserId || ownerUserId !== realtimeOwnerId()) {
      stopRealtime();
      return;
    }
    if (state.realtime.ownerUserId === ownerUserId
        && state.socket && state.socket.readyState <= WebSocket.OPEN) return;
    clearTimeout(state.reconnectTimer);
    stopChatTyping({ clearPeople: true });
    if (!navigator.onLine) {
      setConnection("offline", "Offline");
      return;
    }
    const protocol = location.protocol === "https:" ? "wss" : "ws";
    setConnection("connecting", "Connecting");
    const replayCursor = state.eventCursor > 0 ? `?after=${state.eventCursor}` : "";
    const socket = new WebSocket(`${protocol}://${location.host}/ws${replayCursor}`);
    const generation = ++state.realtime.generation;
    state.socket = socket;
    state.realtime.chain = Promise.resolve();
    state.realtime.recovery = null;
    state.realtime.ownerUserId = ownerUserId;
    socket.addEventListener("open", () => {
      if (generation !== state.realtime.generation || socket !== state.socket
          || ownerUserId !== realtimeOwnerId()) return;
      setConnection("connecting", "Syncing");
      clearInterval(state.heartbeatTimer);
      state.heartbeatTimer = setInterval(() => socket.readyState === WebSocket.OPEN && socket.send(JSON.stringify({ type: "ping" })), 20000);
    });
    socket.addEventListener("message", (event) => {
      let message;
      try { message = JSON.parse(event.data); } catch (_) { return; }
      state.realtime.chain = state.realtime.chain
        .then(() => handleRealtimeFrame(socket, generation, message))
        .catch(() => failRealtimeRecovery(socket, generation));
    });
    socket.addEventListener("close", () => {
      if (generation !== state.realtime.generation) return;
      state.socket = null;
      clearInterval(state.heartbeatTimer);
      state.heartbeatTimer = null;
      if (ownerUserId !== realtimeOwnerId()) {
        stopRealtime();
        return;
      }
      setConnection("offline", navigator.onLine ? "Reconnecting" : "Offline");
      if (!navigator.onLine) return;
      const delay = Math.min(30000, 800 * (2 ** state.reconnectAttempt++)) + Math.random() * 400;
      state.reconnectTimer = setTimeout(() => connectSocket(ownerUserId), delay);
    });
    socket.addEventListener("error", () => socket.close());
  }

  function scheduleFollowingRefresh() {
    const cache = state.feedWindows || (state.view === "following" ? ordinaryFeedWindows() : null);
    if (!cache) return;
    if (state.view === "following") state.dropRefreshes?.clear();
    cache.invalidate({ view: "following" });
    if (state.view === "following" && !cache.active) cache.schedule("following");
  }

  function scheduleActiveSurfaceRefresh(reason = "drop") {
    if (state.view !== "discover") return;
    const mode = state.discovery.mode;
    const relevant = reason === "drop" || reason === "board"
      || (mode === "world" && (
        ["world.pulse.updated", "world.map.updated", "mic.changed", "moment.changed"].includes(reason)
        || reason === "question.changed"
        || reason === "poll.changed"
        || reason === "collaboration.changed"
      ))
      || (mode === "records" && reason === "capsule.created" && state.records.view === "capsules");
    if (!relevant) return;
    clearTimeout(state.utilityRefreshTimer);
    state.utilityRefreshTimer = setTimeout(() => {
      state.utilityRefreshTimer = null;
      if (state.view === "discover") refreshDiscover();
    }, 180);
  }

  function scheduleFlowRefresh(reason) {
    if (state.view !== "flow") return;
    if (state.flow.view === "feed" && reason === "new-drop") {
      state.flow.hasUpdates = true;
      renderFlowPaging();
      return;
    }
    const relevant = reason === "drop" || reason === "new-drop"
      || (reason === "flow.now.changed" && state.flow.view === "now")
      || (reason === "story.changed" && state.flow.view === "stories");
    if (!relevant) return;
    clearTimeout(state.flow.eventRefreshTimer);
    state.flow.eventRefreshTimer = setTimeout(() => {
      state.flow.eventRefreshTimer = null;
      if (state.view === "flow") refreshFlow(false, state.flow.view === "feed");
    }, 180);
  }

  function applyEvent(type, payload) {
    if (type === "sound.changed") { globalThis.louderrSoundPlayback?.invalidate(payload.sound_id); return; }
    if (type === "video.remix.changed") { globalThis.louderrSoundPlayback?.invalidate(payload.source_id); return; }
    if (type === "account.deleted") globalThis.louderrSoundPlayback?.invalidate(null, null, payload.user_id);
    if (["snapshot", "replay.gap"].includes(type)) globalThis.louderrSoundPlayback?.invalidate();
    if (["drop.updated", "drop.deleted"].includes(type)) globalThis.louderrSoundPlayback?.invalidate(null, payload.drop_id);
    if (type === "drop.updated") state.coauthors?.event(payload.drop_id, false);
    if (["drop.updated", "drop.deleted"].includes(type)) state.postArchive?.event(payload.drop_id);
    if (["snapshot", "replay.gap"].includes(type)) state.postArchive?.event();
    if (type === "notification.created" && payload.kind === "drop_coauthor") state.coauthors?.event();
    if (["drop.created", "drop.updated", "drop.restored"].includes(type)) globalThis.LouderrHome?.queueRefresh();
    if (["drop.deleted", "moment.changed", "relationship.changed", "snapshot", "replay.gap"].includes(type)
      || type.startsWith("moderation.") || type.startsWith("user.")) globalThis.LouderrHome?.invalidate();
    if (["world.pulse.updated", "world.map.updated", "mic.changed", "moment.changed", "question.changed", "poll.changed", "collaboration.changed", "capsule.created"].includes(type)) {
      scheduleActiveSurfaceRefresh(type);
    }
    if (["world.pulse.updated", "mic.changed", "moment.changed"].includes(type)) scheduleRoomAffordanceRefresh();
    if (["flow.now.changed", "story.changed"].includes(type)) scheduleFlowRefresh(type);
    if (type === "story.changed" && state.viewer && !state.storyTrayTimer) {
      state.storyTrayTimer = setTimeout(() => { state.storyTrayTimer = 0; void state.storiesV2?.refreshTray(); }, 2500);
    }
    if (type === "snapshot") {
      state.board = payload.board;
      state.boardQueue = payload.board_queue || { items: [], eligible_count: 0, locked_count: 0 };
      state.stats = payload.stats || state.stats;
      const adoptedDrops = state.view === "all" && state.filter === "all"
        && !state.drops.length && !state.feedBusy && !state.feedWindows?.active && Array.isArray(payload.drops);
      if (adoptedDrops) state.drops = payload.drops;
      renderBoard(); renderStats();
      if (adoptedDrops) renderFeed();
      return;
    }
    if (type === "presence") {
      state.presence = payload.presence || 0;
      renderStats();
      return;
    }
    if (type === "replay.gap") {
      return;
    }
    if (type === "chat.typing") {
      receiveChatTyping(payload);
      return;
    }
    if (type === "chat.host.changed") {
      if (typeof refreshChatRooms === "function") refreshChatRooms().catch(() => {});
      return;
    }
    if (["chat.message.created", "chat.message.deleted", "chat.message.restored"].includes(type) && payload.message_id) {
      applyChatEvent(type, payload.message_id, payload.room || "global");
      return;
    }
    if (["drop.comment.created", "drop.comment.deleted", "drop.comment.restored"].includes(type)) {
      applyDropCommentEvent(payload);
      return;
    }
    if (type === "board.changed") {
      state.stats = payload.stats || state.stats;
      renderStats();
      refreshRoom(true);
      refreshTrendingPreview();
      if (state.view === "trending") refreshTrending();
      scheduleActiveSurfaceRefresh("board");
      return;
    }
    if (["drop.created", "drop.updated", "drop.deleted", "drop.restored"].includes(type) && payload.drop_id) {
      scheduleJourneyRefresh(type === "drop.deleted" ? payload.drop_id : null);
    }
    if (type === "drop.created" && payload.drop_id) {
      const deferUntilSubmissionCompletes = state.dropSubmissionActive;
      if (deferUntilSubmissionCompletes) state.deferredCreatedDropIds.add(payload.drop_id);
      const alreadyRendered = state.drops.some((drop) => drop.id === payload.drop_id);
      const isFloorView = ["all", "saved", "mine", "following"].includes(state.view);
      if (!deferUntilSubmissionCompletes && isFloorView && (state.paused || state.drops.length > 0) && !alreadyRendered) {
        if (!state.pending.includes(payload.drop_id)) state.pending.unshift(payload.drop_id);
        state.pending = state.pending.slice(0, 100);
        el.pendingCount.textContent = state.pending.length >= 100 ? "100+" : state.pending.length;
        el.pendingBanner.hidden = false;
      } else if (!deferUntilSubmissionCompletes && isFloorView && !alreadyRendered) refreshFeed();
      if (state.view === "trending") refreshTrending();
      scheduleActiveSurfaceRefresh("drop");
      scheduleFlowRefresh("new-drop");
    }
    // A restored Drop is an update from the client's side: it was removed from the loaded
    // collections, so it reappears through the surface refreshes rather than in place.
    // The drop_id guard is nested rather than conjoined because feed_in_place_updates pins
    // `if (type === "drop.updated"` as a literal and reads this branch in isolation.
    if (type === "drop.updated" || type === "drop.restored") {
      if (payload.drop_id) {
        // A reaction (signal) never changes who is on Following, so there too it updates a loaded card.
        const membership = state.view === "following" && !payload.signal;
        const loaded = !membership && ([state.drops, state.flow.items, state.search.drops, state.discovery.items, state.profile.drops,
          state.feedWindows?.active?.items || [], state.feedWindows?.parked?.items || []]
          .some((items) => items.some((item) => item.id === payload.drop_id)) || state.board?.drop?.id === payload.drop_id);
        if (loaded) refreshDropInPlace(payload.drop_id);
        if (!payload.signal) scheduleFollowingRefresh();
        if (payload.signal === "save") state.feedWindows?.invalidate({ view: "saved" });
        if (state.view === "trending") refreshTrending();
        scheduleActiveSurfaceRefresh("drop");
        scheduleFlowRefresh("drop");
        if (state.view === "profile") refreshProfile();
      }
    }
    if (type === "drop.deleted" && payload.drop_id) {
      removeDrop(payload.drop_id);
      if (state.view === "trending") refreshTrending();
      scheduleActiveSurfaceRefresh("drop");
      scheduleFlowRefresh("drop");
    }
    if (type === "account.deleted" && payload.user_id) {
      state.feedWindows?.invalidate({ view: "following", erase: true });
      const authored = new Set();
      for (const collection of [state.drops, state.flow.items, state.search.drops, state.discovery.items, state.profile.drops,
          state.feedWindows?.active?.items || [], state.feedWindows?.parked?.items || []]) {
        for (const item of collection) {
          if (item.author_id === payload.user_id || item.author?.id === payload.user_id) authored.add(item.id);
        }
      }
      for (const dropId of authored) removeDrop(dropId);
      refreshRoom(true);
      refreshTrendingPreview();
      if (state.view === "trending") refreshTrending();
      scheduleFlowRefresh("drop");
    }
    if (payload.stats) { state.stats = payload.stats; renderStats(); }
  }

  function track(eventType, dropId = null, extras = {}) {
    if (state.sessionRestricted || state.analyticsRedirecting || closedBetaTelemetryDisabled()) return;
    state.analyticsQueue.push({
      event_key: crypto.randomUUID(), event_type: eventType, session_key: state.analyticsSession,
      drop_id: dropId, duration_ms: extras.duration_ms ?? null,
      completion_percent: extras.completion_percent ?? null,
      metadata: Object.fromEntries(Object.entries(extras).filter(([key]) => !["duration_ms", "completion_percent"].includes(key))),
      occurred_at: new Date().toISOString(),
    });
    clearTimeout(state.analyticsTimer);
    state.analyticsTimer = setTimeout(flushAnalytics, 700);
  }

  function trackOnce(key, eventType, dropId = null, extras = {}) {
    if (state.analyticsObserved.has(key)) return;
    state.analyticsObserved.add(key);
    track(eventType, dropId, extras);
  }

  async function flushAnalytics() {
    if (state.sessionRestricted || state.analyticsRedirecting || closedBetaTelemetryDisabled()) {
      clearTimeout(state.analyticsTimer);
      state.analyticsTimer = null;
      state.analyticsQueue.splice(0);
      return;
    }
    if (!state.analyticsQueue.length) return;
    const events = state.analyticsQueue.splice(0, 50);
    try { await api("/api/analytics/events", { method: "POST", json: { events } }); }
    catch (_) { /* Analytics must never block the product path. */ }
    if (state.analyticsQueue.length) state.analyticsTimer = setTimeout(flushAnalytics, 700);
  }

  function recordActiveDuration() {
    if (state.activeSince === null) return;
    const duration = Math.round(performance.now() - state.activeSince);
    state.activeSince = null;
    if (duration >= 1000) track("active_duration", null, { duration_ms: duration });
  }

  function flushAnalyticsBeacon() {
    if (state.sessionRestricted || state.analyticsRedirecting || closedBetaTelemetryDisabled()) {
      state.analyticsQueue.splice(0);
      return;
    }
    if (!state.analyticsQueue.length || !navigator.sendBeacon) return;
    const events = state.analyticsQueue.slice(0, 50);
    const body = new Blob([JSON.stringify({ events })], { type: "application/json" });
    try {
      if (navigator.sendBeacon("/api/analytics/events", body)) state.analyticsQueue.splice(0, events.length);
    } catch (_) { /* Optional analytics must not interrupt pagehide or visibility changes. */ }
  }

  function handleCommerceReturn() {
    if (!state.capabilities.commerce) return;
    const url = new URL(location.href);
    let changed = false;
    const checkout = url.searchParams.get("checkout");
    if (checkout) {
      const messages = {
        "studio-success": "Checkout returned. Creator Pro updates after payment confirmation.",
        "studio-canceled": "Creator Pro checkout canceled.",
        "offer-success": "Checkout returned. Your purchase remains pending until payment is confirmed.",
        "offer-canceled": "Purchase checkout canceled.",
        "sponsor-success": "Checkout returned. Promotion starts only after payment confirmation.",
        "sponsor-canceled": "Promotion checkout canceled.",
        "ads-success": "Checkout returned. Advertising starts only after payment confirmation.",
        "ads-canceled": "Advertising checkout canceled.",
        "premium-success": "Checkout returned. Premium begins only after payment confirmation.",
        "premium-canceled": "Premium checkout canceled.",
      };
      if (messages[checkout]) {
        toast(messages[checkout]);
        state.creator.loaded = false;
        state.purchases.loaded = false;
        url.searchParams.delete("checkout");
        changed = true;
      }
    }
    const purchase = url.searchParams.get("purchase");
    const adsReturn = url.searchParams.get("ads");
    if (["checkout-return", "checkout-canceled"].includes(adsReturn)) {
      toast(adsReturn === "checkout-return"
        ? "Checkout returned. Advertising starts only after payment confirmation."
        : "Advertising checkout canceled.");
      url.searchParams.delete("ads");
      url.pathname = "/advertise";
      changed = true;
    }
    if (url.searchParams.get("premium") === "billing-return") {
      toast("Billing settings returned. Refreshing Premium status.");
      clearBusinessAds();
      businessInstance?.refresh();
      url.searchParams.delete("premium");
      changed = true;
    }
    if (purchase === "billing-return") {
      toast("Billing settings returned. Refreshing purchase status.");
      state.purchases.loaded = false;
      url.searchParams.delete("purchase");
      changed = true;
    }
    if (changed) history.replaceState(history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }

  function handleFederatedAuthReturn() {
    if (!state.capabilities.social_login) return;
    const url = new URL(location.href);
    const outcome = url.searchParams.get("auth");
    const outcomes = new Set([
      "oauth-success", "identity-linked", "account-exists", "identity-in-use",
      "provider-already-linked", "oauth-canceled", "oauth-error",
      "room-full", "registration-closed",
    ]);
    if (!outcomes.has(outcome)) return;
    let continueCreating = false;
    let returnJourney = null;
    let returnStart = null;
    let returnProject = null;
    try {
      const stored = sessionStorage.getItem("lobby.composer.return");
      sessionStorage.removeItem("lobby.composer.return");
      const pending = JSON.parse(stored || "null");
      const age = Date.now() - pending?.at;
      continueCreating = pending?.action === "create" && age >= 0 && age < 15 * 60 * 1000;
      if (pending?.action === "project" && age >= 0 && age < 15 * 60 * 1000
        && /^[0-9a-f-]{36}$/i.test(pending.root_drop_id || "")) returnProject = pending.root_drop_id;
      if (continueCreating && ["update", "response"].includes(pending.journey?.mode)
        && /^[0-9a-f-]{36}$/i.test(pending.journey.root_drop_id || "")) returnJourney = pending.journey;
      else if (continueCreating && pending.journey?.mode === "start"
        && ["feedback", "choice", "remix", "question"].includes(pending.journey.prompt)) {
        returnStart = { mode: "start", prompt: pending.journey.prompt, allow_responses: false };
      }
    } catch (_) { /* Ignore malformed or unavailable optional return hints. */ }
    const provider = federatedProviderLabels.has(url.searchParams.get("provider"))
      ? url.searchParams.get("provider")
      : null;
    const label = provider ? federatedProviderLabels.get(provider) : "provider";
    url.searchParams.delete("auth");
    url.searchParams.delete("provider");
    history.replaceState(history.state, "", `${url.pathname}${url.search}${url.hash}`);

    if (outcome === "oauth-success" && state.viewer) {
      if (state.sessionRestricted || state.viewer.status !== "active") {
        clearComposerIntent();
        state.chat.loaded = false;
        resetSocialState();
        if (el.account.open) el.account.close();
        if (typeof openAccountDialog === "function") openAccountDialog("profile");
        else {
          if (!el.account.open) el.account.showModal();
          setAccountSection("profile", false);
        }
        refreshEnforcement(false);
        scheduleEnforcementPolling();
        toast(state.ageAttestationRequired ? "Confirm your date of birth to continue."
          : state.ageRequirementNotMet ? "Your account tools are available. Participation is restricted by age."
          : "Signed in for account review. Normal product access remains disabled.", true);
        return;
      }
      toast(`Signed in with ${label}`);
      if (returnProject && !state.sessionRestricted && state.viewer.status === "active") openJourney(returnProject);
      else if (continueCreating) {
        if (returnStart) openComposer(null, returnStart);
        else if (returnJourney) openJourney(returnJourney.root_drop_id, returnJourney.mode === "response");
        else openComposer();
      }
      return;
    }
    if (outcome === "identity-linked" && state.viewer) {
      state.security.loaded = false;
      openAccountDialog("security");
      toast(`${label} connected`);
      return;
    }
    const messages = {
      "account-exists": "That verified email already belongs to an account. Sign in first, then connect the provider in Security.",
      "identity-in-use": "That provider identity is already connected to another account.",
      "provider-already-linked": "A different identity from that provider is already connected to this account.",
      "oauth-canceled": "Provider sign-in was canceled.",
      "oauth-error": "Provider sign-in could not be completed. Please try again.",
      "room-full": "Every place in this round is taken. Existing members can still sign in. Please try joining again later.",
      "registration-closed": "New registrations are paused. Existing members can still sign in.",
      "oauth-success": "Provider sign-in did not create a session. Please try again.",
      "identity-linked": "Provider linking did not complete. Please try again.",
    };
    if (state.viewer) {
      openAccountDialog("security");
      showError(accountErrorSlot(), messages[outcome]);
      return;
    }
    authTab("login");
    if (!el.account.open) el.account.showModal();
    const returnError = $("#federated-return-error");
    showError(returnError, messages[outcome]);
    returnError.focus({ preventScroll: true });
    returnError.scrollIntoView({ block: "nearest" });
  }

  function authTab(name) {
    if (!["login", "register", "reset", "invite"].includes(name)) return;
    if (name === "register" && !state.capabilities.registration) name = "login";
    if (name === "reset" && !state.capabilities.password_recovery) name = "login";
    if (name === "invite" && (!state.capabilities.beta_access || state.federatedRegistration || state.capabilities.registration)) name = "login";
    $$('[data-auth-tab]').forEach((button) => {
      const active = button.dataset.authTab === name;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-selected", String(active));
      button.tabIndex = active ? 0 : -1;
    });
    $("#login-form").hidden = name !== "login";
    $("#register-form").hidden = name !== "register";
    $("#reset-form").hidden = name !== "reset";
    if (name === "reset" && !$("#reset-form").dataset.busy) setPasswordResetStep("request");
    const invitePanel = $("#invite-panel");
    if (invitePanel) invitePanel.hidden = name !== "invite";
    $(".auth-tabs", el.guestAccount).hidden = name === "reset";
    renderFederatedAuth();
    hideError(accountErrorSlot());
    hideError($("#federated-return-error"));
  }

  async function authSubmit(event, mode) {
    event.preventDefault();
    // Both forms establish the same session. Switching tabs must not start
    // a second request while the first account response is still pending.
    if (state.authSubmissionActive) return;
    state.authSubmissionActive = true;
    hideError(accountErrorSlot());
    const submitted = event.currentTarget;
    const previousBusy = submitted.getAttribute("aria-busy");
    const button = submitted.querySelector("button[type='submit']");
    const previousLabel = button?.textContent;
    const previousDisabled = button?.disabled;
    submitted.setAttribute("aria-busy", "true");
    if (button) { button.disabled = true; button.textContent = mode === "register" ? "Creating account…" : "Signing in…"; }
    let submissionIdentity = state.messages, submissionViewer = state.viewer?.id || null;
    const currentSubmission = () => state.messages === submissionIdentity
      && (state.viewer?.id || null) === submissionViewer;
    try {
      const data = Object.fromEntries(new FormData(submitted));
      let result, recoveredRegistration = false;
      try {
        result = await api(`/api/auth/${mode}`, { method: "POST", json: data });
      } catch (error) {
        const uncertain = error.status ? error.status >= 500
          : ["TimeoutError", "TypeError", "SyntaxError"].includes(error.name);
        if (mode !== "register" || submissionViewer || !uncertain) throw error;
        const current = () => state.messages === submissionIdentity && !state.viewer;
        if (!current()) return;
        // The session cookies may have arrived before the response body failed.
        // Read that session once; never replay the account-creation POST.
        let session = null;
        try { session = await api("/api/auth/me"); } catch (_) { /* Outcome remains unknown. */ }
        if (!current()) return;
        const sameEmail = typeof session?.user?.email === "string" && typeof data.email === "string"
          && session.user.email.trim().toLowerCase() === data.email.trim().toLowerCase();
        if (!sameEmail || !session.user.id || session.user.primary_account_id !== session.user.id
            || typeof session.user.email_verified !== "boolean"
            || typeof session.csrf_token !== "string" || !session.csrf_token
            || typeof session.session_restricted !== "boolean"
            || typeof session.age_attestation_required !== "boolean"
            || typeof session.age_requirement_not_met !== "boolean"
            || ((session.age_attestation_required || session.age_requirement_not_met) && !session.session_restricted)) {
          throw new Error("We couldn't confirm whether your account was created. Your details are still here. Try signing in with this email and password before registering again.");
        }
        result = session;
        recoveredRegistration = true;
      }
      if (!currentSubmission()) return;
      // The credentials have served their purpose; do not leave them in the form.
      if (typeof submitted.reset === "function") submitted.reset();
      applyAuthResponse(result);
      submissionIdentity = state.messages;
      submissionViewer = state.viewer?.id || null;
      if (result.session_restricted) {
        clearComposerIntent();
        state.chat.loaded = false;
        resetSocialState();
        submissionIdentity = state.messages;
        if (el.account.open) el.account.close();
        if (typeof openAccountDialog === "function") openAccountDialog("profile");
        else {
          if (!el.account.open) el.account.showModal();
          setAccountSection("profile", false);
        }
        scheduleEnforcementPolling();
        syncRealtimeWithAuth({ restart: true });
        toast(state.ageAttestationRequired ? "Confirm your date of birth to continue."
          : state.ageRequirementNotMet ? "Your account tools are available. Participation is restricted by age."
          : "Signed in for account review. Normal product access remains disabled.", true);
        void refreshEnforcement(false);
        if (location.pathname === "/settings" && new URLSearchParams(location.search).has("moderation_action")) await routeLocation();
        return;
      }
      const authViewerId = state.viewer?.id;
      const authSocialState = state.messages;
      const currentAuth = () => state.viewer?.id === authViewerId && state.messages === authSocialState;
      if (mode === "register" && !recoveredRegistration) track("registration");
      else if (mode === "login") track("login");
      if (mode === "login" && state.pendingVerification && result.user && !result.user.email_verified) {
        el.verificationToken.value = state.pendingVerification;
        state.pendingVerification = null;
        await verifyAccount();
        if (!currentAuth()) return;
      }
      toast(recoveredRegistration ? (result.user.email_verified ? "You're signed in." : "You're signed in. Verify your email.")
        : mode === "register" ? "Account created. Verify your email." : "Signed in");
      if (mode === "register" || !result.user?.email_verified) openAccountDialog("profile");
      if (mode === "register" && result.verification_mail_sent === false) {
        showError(el.verificationError, "Your account is created, but we couldn't send the verification email. Choose Send another to retry. You don't need to register again.");
      }
      if (mode === "login" && result.user?.email_verified) {
        if (!resumeComposerIntent()) el.account.close();
        if (sessionStorage.getItem("lobby.pending.offer")) {
          sessionStorage.removeItem("lobby.pending.offer");
          toast("Signed in. Select Buy again to continue.");
        }
      }
      state.chat.loaded = false;
      schedulePrivatePolling(true);
      syncRealtimeWithAuth({ restart: true });
      if (location.pathname === "/messages/join") await routeLocation();
      if (!currentAuth()) return;
      if (location.pathname === "/settings" && new URLSearchParams(location.search).has("moderation_action")) await routeLocation();
      if (!currentAuth()) return;
      // The session is established. Content readers own their loading/error UI;
      // a slow badge or feed response must not hold the sign-in form busy.
      void Promise.allSettled([
        refreshFeed(), refreshChatReadState(),
        refreshSocialBadges(), refreshEnforcement(),
        state.view === "chat" ? refreshChat() : Promise.resolve(),
        state.view === "flow" ? refreshFlow() : Promise.resolve(),
        state.view === "discover" ? refreshDiscover() : Promise.resolve(),
      ]);
    } catch (error) { if (currentSubmission()) showError(accountErrorSlot(), error.message); }
    finally {
      state.authSubmissionActive = false;
      if (previousBusy == null) submitted.removeAttribute("aria-busy");
      else submitted.setAttribute("aria-busy", previousBusy);
      if (button) { button.disabled = previousDisabled; button.textContent = previousLabel; }
      renderCapacity();
    }
  }

  async function acceptBetaAccess(event) {
    event.preventDefault();
    if (!state.capabilities.beta_access) return;
    const form = event.currentTarget;
    const button = $("button[type='submit']", form);
    if (button.disabled) return;
    button.disabled = true;
    hideError(accountErrorSlot());
    try {
      const payload = Object.fromEntries(new FormData(form));
      if (!payload.birth_date) delete payload.birth_date;
      const result = await api("/api/auth/beta-access/accept", { method: "POST", json: payload });
      form.reset();
      authTab("login");
      toast(result.purpose === "recover"
        ? "Password replaced. Sign in again on every device."
        : "Beta access accepted. Sign in to continue.");
    } catch (error) {
      showError(accountErrorSlot(), error.message);
    } finally {
      button.disabled = false;
    }
  }

  async function confirmPlausibleBirthDate(value) {
    // The age gate answers an under-age date with a 24-hour lockout of this browser, on purpose
    // and without explanation. A typo in the year must not cost a real invitee a day.
    const birth = new Date(`${value}T00:00:00Z`);
    if (!value || Number.isNaN(birth.getTime())) return true;
    const age = Math.floor((Date.now() - birth.getTime()) / (365.25 * 86400000));
    if (age >= 16) return true;
    return confirm(`You entered ${value}, which would make you ${age}. Is that right?`,
      { confirmLabel: "Yes, that is right", cancelLabel: "Fix the date", destructive: false });
  }

  async function acceptFriendInvite(event) {
    event.preventDefault();
    if (!state.capabilities.beta_access) return;
    const form = event.currentTarget;
    const button = $("button[type='submit']", form);
    if (button.disabled) return;
    button.disabled = true;
    hideError(accountErrorSlot());
    try {
      const payload = Object.fromEntries(new FormData(form));
      payload.code = String(payload.code || "").trim().toUpperCase();
      payload.handle = String(payload.handle || "").trim().toLowerCase();
      if (!(await confirmPlausibleBirthDate(payload.birth_date))) return;
      await api("/api/auth/friend-invite/accept", { method: "POST", json: payload });
      const email = payload.email;
      const password = payload.new_password;
      form.reset();
      authTab("login");
      const loginForm = $("#login-form");
      loginForm.elements.email.value = email;
      // The person just chose this password; sign them in instead of asking them to type it again.
      if (password && typeof loginForm.requestSubmit === "function") {
        loginForm.elements.password.value = password;
        toast("Invite accepted. Signing you in…");
        loginForm.requestSubmit();
      } else toast("Invite accepted. Sign in to enter LOUDERR.");
    } catch (error) {
      showError(accountErrorSlot(), error.message);
    } finally {
      button.disabled = false;
    }
  }

  function setPasswordResetStep(step) {
    const form = $("#reset-form");
    form.dataset.resetStep = step;
    $$('[data-reset-panel]', form).forEach((panel) => {
      panel.hidden = panel.dataset.resetPanel !== step;
      // A disabled first submit button still blocks implicit Enter submission.
      $("[data-reset-submit]", panel).type = panel.hidden ? "button" : "submit";
      $$("input", panel).forEach((input) => { input.disabled = panel.hidden; });
      $$("button", panel).forEach((button) => { button.disabled = panel.hidden || !!form.dataset.busy; });
    });
  }

  async function requestReset(event) {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.dataset.busy || !form.reportValidity()) return;
    form.dataset.busy = "true";
    form.setAttribute("aria-busy", "true");
    $$("button", form).forEach((button) => { button.disabled = true; });
    hideError(accountErrorSlot());
    try {
      if (form.dataset.resetStep === "confirm") {
        await confirmReset();
        return;
      }
      const result = await api("/api/auth/password/request", { method: "POST", json: { email: form.elements.email.value } });
      form.elements.token.value = result.development_reset_token || "";
      form.elements.new_password.value = "";
      setPasswordResetStep("confirm");
      (form.elements.token.value ? form.elements.new_password : form.elements.token).focus({ preventScroll: true });
      toast(result.message);
    } catch (error) { showError(accountErrorSlot(), error.message); }
    finally {
      delete form.dataset.busy;
      form.removeAttribute("aria-busy");
      $$("button", form).forEach((button) => { button.disabled = false; });
      setPasswordResetStep(form.dataset.resetStep);
    }
  }

  async function confirmReset() {
    const form = $("#reset-form");
    try {
      const result = await api("/api/auth/password/confirm", { method: "POST", json: { token: form.elements.token.value, new_password: form.elements.new_password.value } });
      form.reset();
      setPasswordResetStep("request");
      applyAuthResponse(result);
      if (result.session_restricted) {
        clearComposerIntent();
        openAccountDialog("profile");
        syncRealtimeWithAuth({ restart: true });
        toast(result.age_attestation_required ? "Password changed. Confirm your date of birth to continue."
          : "Password changed. Your account tools are available.");
        return;
      }
      if (!resumeComposerIntent()) el.account.close();
      toast("Password changed and signed in");
      state.chat.loaded = false;
      schedulePrivatePolling(true);
      syncRealtimeWithAuth({ restart: true });
      // The password change and session are committed; background readers own
      // their errors and must not keep the reset form busy.
      void Promise.allSettled([
        refreshChatReadState(), refreshSocialBadges(), refreshEnforcement(),
        state.view === "flow" ? refreshFlow() : Promise.resolve(),
      ]);
    } catch (error) { showError(accountErrorSlot(), error.message); }
  }

  function beginEmailVerification(kind) {
    const viewerId = state.viewer?.id, identity = state.messages;
    if (!viewerId || state.viewer.email_verified) return null;
    const previous = state.verificationOperation;
    if (previous?.viewerId === viewerId && previous.identity === identity) return null;
    previous?.finish();
    const controls = [$("#verify-account"), $("#resend-verification")].map(button => ({
      button, text: button.textContent, disabled: button.disabled,
    }));
    const tokenDisabled = el.verificationToken.disabled;
    const operation = {
      viewerId, identity,
      current: () => state.verificationOperation === operation
        && state.viewer?.id === viewerId && state.messages === identity,
      finish: () => {
        if (state.verificationOperation !== operation) return;
        state.verificationOperation = null;
        for (const control of controls) {
          control.button.disabled = control.disabled;
          control.button.textContent = control.text;
        }
        el.verificationToken.disabled = tokenDisabled;
        el.verificationPanel.setAttribute("aria-busy", "false");
      },
    };
    state.verificationOperation = operation;
    for (const control of controls) control.button.disabled = true;
    controls[kind === "confirm" ? 0 : 1].button.textContent = kind === "confirm" ? "Verifying…" : "Sending…";
    el.verificationToken.disabled = true;
    el.verificationPanel.setAttribute("aria-busy", "true");
    return operation;
  }

  async function verifyAccount() {
    const operation = beginEmailVerification("confirm");
    if (!operation) return;
    hideError(el.verificationError);
    try {
      const result = await api("/api/auth/verification/confirm", { method: "POST", csrf: true, json: { token: el.verificationToken.value } });
      if (!operation.current()) return;
      if (result?.user?.id !== operation.viewerId || result.user.email_verified !== true) {
        throw new Error("Email verification was not confirmed. Please try again.");
      }
      state.viewer = result.user; renderAccount();
      if (!resumeComposerIntent()) el.account.close();
      toast("Email verified");
      schedulePrivatePolling(true);
      syncRealtimeWithAuth({ restart: true });
      void Promise.allSettled([
        refreshSocialBadges(), refreshEnforcement(),
        state.view === "flow" ? refreshFlow() : Promise.resolve(),
      ]);
    } catch (error) {
      if (operation.current() && !state.viewer.email_verified) showError(el.verificationError, error.message);
    } finally { operation.finish(); }
  }

  async function confirmAge() {
    const button = el.confirmAge, viewerId = state.viewer?.id, identity = state.messages;
    if (!viewerId) return;
    const gateRoute = state.view === "settings" && state.accountSection === "profile"
      ? { version: state.routeRequestId, path: location.pathname, query: location.search } : null;
    let operation = button.ageOperation;
    if (!operation || operation.viewerId !== viewerId || operation.identity !== identity) {
      operation = { viewerId, identity, pending: false, checkOnly: false, saved: false };
      button.ageOperation = operation;
    }
    if (operation.pending) return;
    const current = () => button.ageOperation === operation && state.viewer?.id === viewerId && state.messages === identity;
    const request = async action => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try { return await action(controller.signal); }
      finally { clearTimeout(timer); }
    };
    hideError(el.ageError);
    // The user has reached this step. Its old reminder must not cover inline
    // validation or recovery feedback on a short screen.
    if (["Confirm your date of birth to continue.", "Password changed. Confirm your date of birth to continue."]
      .includes(el.toast?.textContent)) {
      clearTimeout(el.toast._timer);
      el.toast.classList.remove("is-visible");
    }
    const value = el.ageBirthDate.value;
    if (!operation.checkOnly) {
      el.ageBirthDate.max = new Date().toISOString().slice(0, 10);
      if (!value || !el.ageBirthDate.checkValidity()) {
        showError(el.ageError, value ? el.ageBirthDate.validationMessage : "Enter your date of birth.");
        el.ageBirthDate.focus();
        el.ageBirthDate.reportValidity();
        return;
      }
    }
    operation.pending = true;
    button.disabled = true; el.ageBirthDate.disabled = true;
    button.textContent = operation.checkOnly ? "Checking…" : "Confirming…";
    try {
      let result = null;
      if (!operation.checkOnly) {
        try {
          const saved = await request(signal => api("/api/auth/age", { method: "POST", csrf: true, json: { birth_date: value }, signal }));
          if (!current()) return;
          operation.saved = true;
          // A complete committed response already carries the server's age AND
          // account restrictions. Older/incomplete replies still need /me.
          if (saved?.attested === true && saved.user?.id === viewerId
              && saved.age_attestation_required === false
              && typeof saved.age_requirement_not_met === "boolean"
              && typeof saved.session_restricted === "boolean"
              && (!saved.age_requirement_not_met || saved.session_restricted)) result = saved;
        } catch (error) {
          if (!current()) return;
          // A changed CSRF cookie or ended session needs an authoritative read,
          // without automatically sending this declaration again. Other validation
          // and admission failures remain definitive.
          const refreshSession = error.status === 401
            || error.status === 403 && error.message === "CSRF token mismatch.";
          if (error.status && error.status < 500 && error.status !== 409 && !refreshSession) {
            showError(el.ageError, error.message); return;
          }
        }
        operation.checkOnly = true;
      }
      if (!current()) return;
      if (result) {
        const wasRestricted = state.sessionRestricted;
        state.viewer = result.user;
        state.sessionRestricted = result.session_restricted;
        state.ageAttestationRequired = result.age_attestation_required;
        state.ageRequirementNotMet = result.age_requirement_not_met;
        state.minimumAge = Number(result.minimum_age) || state.minimumAge || 16;
        renderAccount();
        syncRealtimeWithAuth();
        if (wasRestricted && !state.sessionRestricted) schedulePrivatePolling(true);
      } else {
        button.textContent = "Checking…";
        result = await request(signal => refreshAuth({ signal }));
      }
      if (!current()) return;
      if (!result || result.user?.id !== viewerId || typeof result.age_attestation_required !== "boolean") {
        throw new Error("Account status was not confirmed.");
      }
      if (result.age_attestation_required) {
        operation.checkOnly = false; operation.saved = false;
        showError(el.ageError, "Your date of birth is not confirmed yet. Check the date and try again.");
      } else {
        operation.checkOnly = false;
        hideError(el.ageError);
        toast(result.age_requirement_not_met ? "Your date of birth is saved. Your account tools remain available." : "Thanks, that is confirmed.");
        if (gateRoute && result.age_requirement_not_met === false && result.session_restricted === false
            && state.view === "settings" && state.accountSection === "profile"
            && state.routeRequestId === gateRoute.version && location.pathname === gateRoute.path
            && location.search === gateRoute.query) {
          navigateToView("all");
        }
      }
    } catch (error) {
      if (current()) showError(el.ageError, operation.saved
        ? "Your date of birth was saved. Check your connection, then choose Check status to continue."
        : "We couldn't check your account. Your date is still here. Check your connection, then choose Check status.");
    } finally {
      if (current()) {
        operation.pending = false;
        button.disabled = false;
        el.ageBirthDate.disabled = operation.checkOnly;
        button.textContent = operation.checkOnly ? "Check status" : "Confirm";
      }
    }
  }

  function handleMailLinks() {
    const url = new URL(location.href);
    const verify = url.searchParams.get("verify");
    const reset = url.searchParams.get("reset");
    if (verify === null && reset === null) return;
    url.searchParams.delete("verify");
    url.searchParams.delete("reset");
    history.replaceState(history.state, "", `${url.pathname}${url.search}${url.hash}`);
    const valid = (value) => typeof value === "string" && /^[A-Za-z0-9_-]{20,300}$/.test(value);
    if (valid(reset)) {
      if (!state.capabilities.password_recovery) { toast("Password recovery is not available right now.", true); return; }
      if (!el.account.open) el.account.showModal();
      authTab("reset");
      el.guestAccount.hidden = false;
      const form = $("#reset-form");
      setPasswordResetStep("confirm");
      form.elements.token.value = reset;
      form.elements.new_password.focus({ preventScroll: true });
      toast("Choose a new password to finish resetting.");
      return;
    }
    if (!valid(verify)) return;
    if (state.viewer) {
      if (state.viewer.email_verified) { toast("Your email is already verified."); return; }
      openAccountDialog("profile");
      el.verificationToken.value = verify;
      verifyAccount();
      return;
    }
    state.pendingVerification = verify;
    if (!el.account.open) el.account.showModal();
    authTab("login");
    toast("Sign in to finish verifying your email.");
  }

  function markAge(mark) {
    const recorded = Date.parse(mark?.recorded_at || "");
    if (!Number.isFinite(recorded)) return "never";
    const hours = Math.max(0, (Date.now() - recorded) / 3600000);
    if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min ago`;
    if (hours < 48) return `${hours.toFixed(1)} h ago`;
    return `${Math.round(hours / 24)} d ago`;
  }

  async function resendVerification() {
    const operation = beginEmailVerification("resend");
    if (!operation) return;
    hideError(el.verificationError);
    try {
      const result = await api("/api/auth/verification/request", { method: "POST", csrf: true, json: {} });
      if (!operation.current()) return;
      if (result.already_verified) {
        await refreshAuth();
        if (operation.current() && state.viewer.email_verified) toast("Your email is already verified.");
        return;
      }
      if (result.development_verification_token) el.verificationToken.value = result.development_verification_token;
      toast("Verification code sent");
    } catch (error) {
      if (operation.current() && !state.viewer.email_verified) showError(el.verificationError, error.message);
    } finally { operation.finish(); }
  }

  function profileFields(form) {
    return {
      ...(form.elements.handle ? { handle: form.elements.handle.value } : {}),
      display_name: form.elements.display_name.value, bio: form.elements.bio.value,
      location: form.elements.location.value || null,
      ...state.profilePlaces?.fields(),
      website: form.elements.website.value || null,
      ...(state.translation?.enabled ? {
        preferred_language: form.elements.preferred_language.value,
        auto_translate_public: form.elements.auto_translate_public.checked,
        translate_private_messages: form.elements.translate_private_messages.checked,
      } : {}),
    };
  }

  async function saveProfile(event) {
    event.preventDefault();
    if (!state.capabilities.community_writes) return;
    const ownerId = state.viewer?.id;
    if (!ownerId || profileSaveBusy?.ownerId === ownerId) return;
    const form = event.currentTarget;
    if (state.profilePlaces && !state.profilePlaces.validate()) return;
    const fields = profileFields(form), fingerprint = JSON.stringify(fields);
    const file = state.capabilities.uploads ? form.elements.avatar.files[0] : null;
    const operation = { ownerId, phase: file ? "media" : "profile", cancelled: false, accountWasOpen: Boolean(el.account?.open) };
    const button = event.submitter || form.elements["profile-save"]
      || form.querySelector?.('button[type="submit"]');
    if (button) {
      operation.buttonText = button.textContent; operation.buttonDisabled = button.disabled;
      button.profileSave = operation; button.disabled = true; button.textContent = "Saving…";
    }
    profileSaveBusy = operation;
    hideError(el.profileSaveError);
    operation.alertRevision = el.profileSaveError.alertRevision || 0;
    try {
      let avatarAssetId = state.viewer.avatar_asset_id || null;
      if (file) {
        if (!state.viewer.email_verified) {
          showError(el.profileSaveError, "Verify your email before uploading a profile photo. Your selection and profile edits are kept.");
          return;
        }
        if (typeof window !== "undefined" && window.LouderrOptional) {
          await window.LouderrOptional.load("uploads");
          if (state.viewer?.id !== ownerId || profileSaveBusy !== operation || form.elements.avatar.files[0] !== file
              || !state.capabilities.uploads || !state.capabilities.community_writes) return;
        }
        if (profileAvatarUpload && profileAvatarUpload.file !== file) {
          const previous = profileAvatarUpload;
          if (!["selected", "succeeded", "rejected"].includes(previous.entry.state)) {
            if (!await mediaUploads().cancel(previous.entry)) return;
            if (state.viewer?.id !== ownerId || profileAvatarUpload !== previous) return;
          }
          mediaUploads().detach(previous.entry); profileAvatarUpload = null;
        }
        if (!profileAvatarUpload) profileAvatarUpload = { file, entry: mediaUploads().select(file, "", "profile_avatar") };
        const selection = profileAvatarUpload;
        const context = { ...avatarUploadContext(selection), alertRevision: operation.alertRevision };
        operation.selection = selection;
        renderAvatarUploadStatus();
        try { await mediaUploads().prepare(selection.entry); }
        catch (error) { error.uploadAlert = context; throw error; }
        if (state.viewer?.id !== ownerId || profileAvatarUpload !== selection || operation.cancelled || (operation.accountWasOpen && !el.account?.open)) return;
        const started = Date.now();
        let decision = mediaWaitDecision([selection.entry], 0, MEDIA_READY_WAIT_MS);
        while (decision === "wait") {
          await new Promise(resolve => setTimeout(resolve, MEDIA_READY_POLL_MS));
          if (state.viewer?.id !== ownerId || profileAvatarUpload !== selection || operation.cancelled || (operation.accountWasOpen && !el.account?.open)) return;
          if (form.elements.avatar.files[0] !== file || JSON.stringify(profileFields(form)) !== fingerprint) {
            throw new Error("Your profile changed while the photo was being prepared. Your newer edits are kept; choose Save again.");
          }
          if (!selection.entry.timer && !selection.entry.pending) {
            await mediaUploads().poll(selection.entry, { manual: true }).catch(() => null);
          }
          decision = mediaWaitDecision([selection.entry], Date.now() - started, MEDIA_READY_WAIT_MS);
        }
        if (state.viewer?.id !== ownerId || profileAvatarUpload !== selection || operation.cancelled || (operation.accountWasOpen && !el.account?.open)) return;
        if (selection.entry.state !== "succeeded") {
          showUploadError(el.profileSaveError, new Error(mediaPendingMessage(context)), { ...context, kind: "pending" });
          return;
        }
        avatarAssetId = selection.entry.id;
      }
      if (state.viewer?.id !== ownerId) return;
      if (form.elements.avatar.files[0] !== file || JSON.stringify(profileFields(form)) !== fingerprint) {
        throw new Error("Your profile changed while media was being prepared. Your newer edits are kept; choose Save again.");
      }
      if (operation.cancelled || (operation.accountWasOpen && !el.account?.open) || !state.capabilities.community_writes || (file && !state.capabilities.uploads)) return;
      operation.phase = "profile";
      const result = await api("/api/account/profile", {
        method: "PATCH", csrf: true,
        json: {
          ...fields,
          challenge_token: null,
          ...(state.capabilities.uploads ? { avatar_asset_id: avatarAssetId } : {}),
        },
      });
      if (state.viewer?.id !== ownerId) return;
      const unchanged = form.elements.avatar.files[0] === file && JSON.stringify(profileFields(form)) === fingerprint;
      operation.saved = true;
      state.viewer = result.user;
      if (state.managedAccounts) state.managedAccounts.loaded = false;
      const ownProfile = state.view === "profile" && state.profile?.data?.user?.id === ownerId;
      if (ownProfile) {
        state.profile.handle = result.user.handle;
        history.replaceState({}, "", `/u/${encodeURIComponent(result.user.handle)}`);
      }
      profileDraftDirty = !unchanged;
      if (unchanged) {
        form.elements.avatar.value = "";
        if (profileAvatarUpload && profileAvatarUpload.file === file) {
          mediaUploads().detach(profileAvatarUpload.entry); profileAvatarUpload = null;
        }
        renderAvatarUploadStatus();
      }
      renderAccount();
      state.chat.loaded = false;
      await Promise.all([
        refreshFeed(), refreshRoom(true),
        ownProfile ? refreshProfile(result.user.handle) : Promise.resolve(),
        state.view === "chat" ? refreshChat() : Promise.resolve(),
        state.view === "messages" && state.messages.selectedId
          ? openMessageThread(state.messages.selectedId, false) : Promise.resolve(),
        state.view === "discover" && state.discovery.mode === "world"
          ? refreshWorld({ quiet: true }) : Promise.resolve(),
        state.view === "flow" ? refreshFlow() : Promise.resolve(),
      ]);
      if (state.viewer?.id === ownerId) toast(unchanged ? "Profile saved" : "Earlier profile changes saved. Your newer edits are still here.");
    } catch (error) {
      if (state.viewer?.id === ownerId) {
        if (operation.saved) {
          showError(el.profileSaveError, "Your profile was saved, but the page couldn't refresh. Reload to see the changes.");
        } else if (operation.phase === "profile" && error?.name === "TypeError"
            && ["Failed to fetch", "Load failed", "NetworkError when attempting to fetch resource."].includes(error.message)) {
          showError(el.profileSaveError, "We couldn't confirm the save. Your edits are still here. Check your connection and try again.");
        } else showUploadError(el.profileSaveError, error);
      }
    }
    finally {
      if (profileSaveBusy === operation) {
        profileSaveBusy = false;
        if (profileAvatarUpload) renderAvatarUploadStatus();
      }
      if (button?.profileSave === operation) {
        delete button.profileSave; button.disabled = operation.buttonDisabled; button.textContent = operation.buttonText;
      }
    }
  }

  function clearCredentialForms() {
    // A shared browser must not keep an email and a plaintext password filled in
    // for whoever opens the dialog next. Never let this stop a sign-out.
    try {
      for (const selector of ["#login-form", "#register-form", "#reset-form"]) {
        const form = document.querySelector?.(selector);
        if (form && typeof form.reset === "function") form.reset();
      }
    } catch (_) { /* no document in a test sandbox */ }
  }

  function finishLocalLogout() {
    if (typeof clearCredentialForms === "function") clearCredentialForms();
    // A signed-out device keeps no private edit: every account's Studio drafts and unfinished sound and video
    // publications, and any standalone result, go. A publication copy already being written finishes first so
    // it cannot come back afterwards; a new one is refused, as nobody is signed in.
    if (typeof window !== "undefined" && window.LobbyStudio?.drafts) {
      const writing = [typeof soundDraftWrites === "undefined" ? [] : [...soundDraftWrites.values()],
        typeof videoDraftWrites === "undefined" ? [] : [...videoDraftWrites.values()]].flat();
      void Promise.allSettled(writing).then(() => window.LobbyStudio.drafts.clearAccounts());
    }
    if (typeof mediaStudio !== "undefined") mediaStudio?.cancel();
    state.studioStandalone = null;
    state.viewer = null; state.csrf = null; state.sessionRestricted = false;
    state.security = { sessions: [], notifications: [], loaded: false };
    state.purchases = { items: [], loaded: false };
    state.creator = { status: null, dashboard: null, loaded: false };
    state.chat = { ...state.chat, items: [], nextCursor: null, lens: "all", replyTo: null, reportMessage: null, unreadCount: 0, newWhileAway: 0, loaded: false, lastTrackedRead: null };
    resetSocialState();
    renderAccount(); updateChatBadges(); el.account.close();
    history.pushState({}, "", "/"); setView("all");
    stopRealtime();
  }

  async function logout() {
    const viewerId = state.viewer?.id || null;
    const socialState = state.messages;
    const current = () => state.messages === socialState && (state.viewer?.id || null) === viewerId;
    try {
      state.endingSession = (state.endingSession || 0) + 1;
      try { await api("/api/auth/logout", { method: "POST", csrf: true, json: {} }); }
      finally { state.endingSession -= 1; }
      if (!current()) return;
      finishLocalLogout();
      toast("Logged out");
    }
    catch (error) {
      if (!current()) return;
      // A 401 means the session had already ended, which is what logging out asks for.
      if (error.status === 401) { finishLocalLogout(); toast("Logged out"); }
      else showError(accountErrorSlot(), error.message);
    }
  }

  async function logoutAllSessions() {
    if (!(await confirm("Log out this account on every browser and device?"))) return;
    const viewerId = state.viewer?.id || null;
    const socialState = state.messages;
    const current = () => state.messages === socialState && (state.viewer?.id || null) === viewerId;
    try {
      state.endingSession = (state.endingSession || 0) + 1;
      let result;
      try { result = await api("/api/auth/logout-all", { method: "POST", csrf: true, json: {} }); }
      finally { state.endingSession -= 1; }
      if (!current()) return;
      finishLocalLogout();
      toast(`${result.revoked_sessions} active ${result.revoked_sessions === 1 ? "session" : "sessions"} logged out`);
    } catch (error) {
      if (!current()) return;
      // A 401 means this session had already ended, so the other devices were not logged out.
      if (error.status === 401) {
        finishLocalLogout();
        toast("Your session had already ended. Sign in again to log out other devices.", true);
      } else showError(accountErrorSlot(), error.message);
    }
  }

  // Creators keep their audience: the follower list downloads as a CSV they can take anywhere.
  async function exportFollowers() {
    const viewerId = state.viewer?.id;
    if (!viewerId) return;
    const button = $("#export-followers");
    if (!button) return;
    button.disabled = true;
    hideError(accountErrorSlot());
    try {
      const text = await runRecentAction(async () => {
        if (state.viewer?.id !== viewerId) throw new DOMException("Account changed", "AbortError");
        const response = await window.LobbyChallenges.fetch("/api/account/followers/export", { credentials: "same-origin" });
        if (!response.ok) {
          const type = response.headers.get("content-type") || "";
          const payload = type.includes("application/json") ? await response.json() : await response.text();
          const error = new Error(detailMessage(payload, response.status));
          error.status = response.status;
          error.reauthenticationRequired = response.headers.get("X-Reauthentication-Required") === "true";
      error.reauthenticationMethod = response.headers.get("X-Reauthentication-Method");
          error.staffMfaRequired = response.headers.get("X-Staff-MFA-Required") === "true";
          throw error;
        }
        return response.text();
      });
      if (state.viewer?.id !== viewerId) return;
      const link = create("a");
      link.href = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
      link.download = `louderr-followers-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      toast("Your followers list is downloading.");
    } catch (error) {
      if (state.viewer?.id === viewerId && error.name !== "AbortError") showError(accountErrorSlot(), error.message);
    } finally {
      button.disabled = !state.viewer;
    }
  }

  async function exportAccount() {
    const viewerId = state.viewer?.id;
    const identity = state.messages;
    const current = () => Boolean(viewerId) && state.viewer?.id === viewerId && state.messages === identity;
    if (!current()) return;
    const button = $("#export-account");
    button.disabled = true;
    hideError(accountErrorSlot());
    try {
      const data = await runRecentAction(() => {
        if (!current()) throw new DOMException("Account changed", "AbortError");
        return api("/api/account/export");
      });
      if (!current()) return;
      const link = create("a");
      link.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      link.download = `louderr-export-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (error) {
      if (current() && error.name !== "AbortError") showError(accountErrorSlot(), error.message);
    } finally {
      button.disabled = !state.viewer;
    }
  }

  async function deleteAccount() {
    const viewerId = state.viewer?.id;
    const identity = state.messages;
    const current = () => Boolean(viewerId) && state.viewer?.id === viewerId && state.messages === identity;
    if (!current()) return;
    if (!(await confirm("Delete this account permanently? Your profile and Drops will no longer be visible. Export your data first if you want a copy."))) return;
    const button = $("#delete-account");
    button.disabled = true;
    hideError(accountErrorSlot());
    try {
      await runRecentAction(async () => {
        if (!current()) throw new DOMException("Account changed", "AbortError");
        state.endingSession = (state.endingSession || 0) + 1;
        try { return await api("/api/account", { method: "DELETE", csrf: true }); }
        finally { state.endingSession -= 1; }
      });
      if (!current()) return;
      finishLocalLogout();
      await refreshFeed(); toast("Account deleted");
    }
    catch (error) {
      if (current() && error.name !== "AbortError") showError(accountErrorSlot(), error.message);
    } finally {
      button.disabled = !state.viewer;
    }
  }

  function adminWorkspaceCurrent(workspace) {
    return Boolean(workspace && state.adminWorkspace === workspace && el.admin.open
      && state.viewer?.id === workspace.viewerId && state.viewer?.role === workspace.role
      && state.viewer?.status === "active" && !state.sessionRestricted && staffRoles.has(state.viewer.role));
  }

  function resetAdminWorkspace() {
    // The verification prompt outlives the workspace on purpose: the admin dialog's own
    // close event runs this again right after a stale-proof 403 opened the prompt, and
    // must not take the prompt with it. syncAdminAccess closes it when the viewer changes.
    const workspace = state.adminWorkspace;
    state.adminWorkspace = null;
    for (const dialog of workspace?.dialogs || []) { if (dialog.open) dialog.close(); }
    clear(el.adminContent);
    if (el.adminSections) clear(el.adminSections);
    if (el.adminStatus) el.adminStatus.textContent = "";
    if (el.admin.open) el.admin.close();
  }

  function syncAdminAccess() {
    if (state.adminWorkspace && !adminWorkspaceCurrent(state.adminWorkspace)) resetAdminWorkspace();
    const verification = state.staffVerificationDialog;
    if (verification && (state.viewer?.id !== verification.viewerId || state.viewer?.role !== verification.role
      || state.viewer?.status !== "active" || state.sessionRestricted || !staffRoles.has(state.viewer.role))) {
      state.staffVerificationDialog = null;
      if (verification.dialog.open) verification.dialog.close();
    }
  }

  function promptStaffVerification() {
    if (!state.viewer || state.viewer.status !== "active" || state.sessionRestricted || !staffRoles.has(state.viewer.role)) return;
    if (state.staffVerificationDialog?.dialog.open) return;
    const dialog = buildUtilityDialog("Verify staff access");
    const verification = { dialog, viewerId: state.viewer.id, role: state.viewer.role };
    state.staffVerificationDialog = verification;
    dialog.addEventListener("close", () => {
      if (state.staffVerificationDialog === verification) state.staffVerificationDialog = null;
    }, { once: true });
    if (el.adminAccount) el.adminAccount.textContent = "Open moderation workspace (verification needed)";
    const link = create("a", "publish-button", "Continue to staff verification");
    link.href = "/staff-mfa";
    dialog.append(create("p", "utility-copy", "Your staff verification has expired, or the security policy changed since it was issued. Staff tools need fresh verification with your enrolled identity provider. Password confirmation cannot replace it."),
      create("p", "utility-copy", "No blocked action will be retried automatically. Save any unfinished work before continuing, then reopen the workspace after verification."), link);
    openDialog(dialog);
  }

  async function runAdminAction(action, workspace = state.adminWorkspace) {
    const guarded = async () => {
      if (!adminWorkspaceCurrent(workspace)) throw new DOMException("Staff session changed. Reopen the workspace.", "AbortError");
      let result;
      try { result = await action(); }
      catch (error) {
        if (!adminWorkspaceCurrent(workspace)) throw new DOMException("Staff session changed.", "AbortError");
        throw error;
      }
      if (!adminWorkspaceCurrent(workspace)) throw new DOMException("Staff session changed.", "AbortError");
      return result;
    };
    return runRecentAction(guarded);
  }

  function registerStaffDialog(dialog) {
    const workspace = state.adminWorkspace;
    if (adminWorkspaceCurrent(workspace)) {
      workspace.dialogs.add(dialog);
      dialog.addEventListener("close", () => workspace.dialogs.delete(dialog), { once: true });
    }
    return dialog;
  }

  function adminSectionDefinitions() {
    const sections = [
      ["reports", "Reports", "Review reported content and accounts. Decisions require a reason and are audited."],
      ["safety-reports", "Safety reports", "Review safety cases and record their progress. Changing a case status does not remove content or suspend an account."],
      ["appeals", "Appeals", "Review pending appeals independently. The original moderator cannot decide their own appeal."],
      ["history", "Decision history", "Recorded moderation decisions, action IDs, and staff-only notes."],
      ["invites", "Invite friends", "Create capacity-bounded, one-use beta codes. Codes are shown once."],
      ["connected", "Community tools", "Manage the Mic, questions, Moments, capsules, and Scout authority."],
      ["media", "Media review", "Approve or reject uploads waiting for a person. Nothing reaches the feed before this decision."],
    ];
    if (state.capabilities.studio_video_remix) sections.push(
      ["video-review", "Video remixes", "Review the complete composed video, record a decision, or escalate it for further review."],
    );
    if (state.viewer?.role === "admin") {
      sections.push(
        ["overview", "System overview", "Operational health, abuse signals, and measured activity."],
      );
      if (state.capabilities.commerce) sections.push(
        ["sponsors", "Sponsor review", "Review submitted sponsor campaigns before payment and placement."],
        ["advertising", "Advertising", "Review and manage native advertising campaigns."],
      );
      if (state.capabilities.profile === "no-cost-closed-beta-v1" && state.capabilities.beta_access) sections.push(
        ["beta-access", "Beta access", "Issue and revoke short-lived, one-use enrollment and recovery grants."],
      );
      sections.push(
        ["users", "Accounts & roles", "Find accounts, manage staff roles, and review role changes."],
        ["security", "Security", "Security monitoring and audit integrity require recent authentication."],
      );
    }
    return sections;
  }

  async function openAdmin() {
    if (!state.viewer || state.viewer.status !== "active" || state.sessionRestricted || !staffRoles.has(state.viewer.role)) {
      resetAdminWorkspace(); openDialog(el.account); toast("Moderator access is required"); return;
    }
    if (el.account.open) el.account.close();
    if (adminWorkspaceCurrent(state.adminWorkspace)) {
      await selectAdminSection(state.adminWorkspace.active, true); return;
    }
    resetAdminWorkspace();
    const workspace = { viewerId: state.viewer.id, role: state.viewer.role, active: "reports", panels: new Map(), dialogs: new Set() };
    state.adminWorkspace = workspace;
    for (const [key, label, copy] of adminSectionDefinitions()) {
      const button = create("button", "admin-section-button", label);
      button.type = "button"; button.dataset.adminSection = key; button.setAttribute("aria-pressed", "false");
      button.addEventListener("click", () => { if (adminWorkspaceCurrent(workspace)) selectAdminSection(key); });
      el.adminSections.append(button);
      const panel = create("section", "admin-workspace-panel"); panel.hidden = true;
      panel.dataset.adminPanel = key; panel.setAttribute("aria-label", label);
      workspace.panels.set(key, { panel, button, label, copy, loaded: false, version: 0, filters: {} });
      el.adminContent.append(panel);
    }
    openDialog(el.admin);
    if (el.adminAccount) el.adminAccount.textContent = "Open moderation workspace";
    await selectAdminSection("reports");
  }

  async function selectAdminSection(key, refresh = false) {
    const workspace = state.adminWorkspace;
    if (!adminWorkspaceCurrent(workspace) || !workspace.panels.has(key)) return;
    const previous = workspace.panels.get(workspace.active);
    if (previous) previous.scrollTop = el.adminContent.scrollTop;
    workspace.active = key;
    for (const [name, entry] of workspace.panels) {
      const active = name === key;
      entry.panel.hidden = !active; entry.button.classList.toggle("is-active", active);
      entry.button.setAttribute("aria-pressed", String(active));
    }
    const entry = workspace.panels.get(key);
    el.adminContent.scrollTop = entry.scrollTop || 0;
    el.adminStatus.textContent = `${entry.label} · ${workspace.role === "admin" ? "Administrator" : "Moderation"}`;
    if (refresh || !entry.loaded) await loadAdminSection(workspace, key, entry);
  }

  function adminLoadError(container, error, retry) {
    const notice = create("div", "admin-section-error"); notice.setAttribute("role", "alert");
    notice.append(create("p", "", error.status === 404 || error.status === 405
      ? "This tool is unavailable on the running server. A server update may be required."
      : error.message || "Unable to load this section."));
    const button = create("button", "secondary-button", "Retry"); button.type = "button";
    button.addEventListener("click", retry); notice.append(button); container.append(notice);
  }

  function handleAdminReadAccessLoss(error, workspace) {
    if (error.status === 403 && error.staffMfaRequired) {
      if (adminWorkspaceCurrent(workspace)) { resetAdminWorkspace(); promptStaffVerification(); return true; }
      // Sibling reads of the workspace that was just reset fail the same way: keep the one
      // prompt and stay quiet. A denial from an older workspace never touches a replacement.
      if (workspace && state.adminWorkspace === null) { promptStaffVerification(); return true; }
      return false;
    }
    if (!adminWorkspaceCurrent(workspace) || ![401, 403].includes(error.status) || error.reauthenticationRequired) return false;
    resetAdminWorkspace();
    toast("Staff access is no longer available. Refresh your account or sign in again.", true);
    return true;
  }

  async function loadAdminSection(workspace, key, entry) {
    const version = ++entry.version;
    const current = () => adminWorkspaceCurrent(workspace) && entry.version === version;
    clear(entry.panel); entry.loaded = true;
    const header = create("header", "admin-panel-header");
    header.append(create("h3", "", entry.label), create("p", "admin-panel-copy", entry.copy));
    entry.panel.append(header);
    if (key === "reports" || key === "appeals" || key === "history") {
      await loadAdminReviewSection(workspace, key, entry, current); return;
    }
    if (key === "safety-reports") {
      await loadSafetyReports(workspace, entry, current); return;
    }
    // Each tool owns its own loading/error area. A failing tool cannot erase another tool's data.
    const component = async (label, load, render) => {
      const region = create("section", "admin-tool-region"); region.setAttribute("aria-label", label);
      entry.panel.append(region);
      const run = async () => {
        if (!current()) return;
        clear(region); region.append(create("p", "admin-section-loading", `Loading ${label.toLowerCase()}…`));
        try {
          const result = await load(); if (!current()) return;
          clear(region); region.append(render(result));
        } catch (error) {
          if (!current()) return;
          if (handleAdminReadAccessLoss(error, workspace)) return;
          clear(region); adminLoadError(region, error, run);
        }
      };
      await run();
    };
    if (key === "connected") await component("Community tools", loadConnectedStaffTools, (node) => node);
    if (key === "invites") entry.panel.append(adminFriendInvitesPanel());
    if (key === "overview") await Promise.all([
      component("System health", () => api("/api/moderation/admin/overview"), adminOverviewPanel),
      component("Activity analytics", () => api("/api/analytics/summary"), analyticsPanel),
    ]);
    if (key === "media") await component("Uploads waiting for review", () => api("/api/safety/media/queue?limit=100"),
      (data) => data.items.length ? adminMediaReviewPanel(data.items, workspace) : create("p", "admin-section-empty", "No uploads are waiting for review."));
    if (key === "video-review") {
      if (!window.LouderrVideoReview || !state.capabilities.studio_video_remix) return;
      const region = create("section", "admin-tool-region"); entry.panel.append(region);
      await window.LouderrVideoReview.mount(region, { viewerId: workspace.viewerId, current,
        api, recent: (action) => runAdminAction(action, workspace) });
    }
    if (key === "sponsors") await component("Sponsor review", () => api("/api/admin/sponsors/campaigns?status=pending_review"),
      (data) => data.items.length ? adminSponsorPanel(data.items) : create("p", "admin-section-empty", "No sponsor campaigns awaiting review."));
    if (key === "advertising") {
      if (!state.capabilities.commerce) return;
      const nativeAds = create("section", "admin-queue"); entry.panel.append(nativeAds);
      try {
        if (!businessFeatures()) await window.LouderrOptional?.load("business");
        if (!current() || !state.capabilities.commerce) return;
        const features = businessFeatures();
        if (!features) throw new Error("Advertising tools could not be loaded.");
        await features.mountAdmin(nativeAds);
      } catch (error) {
        if (current()) adminLoadError(nativeAds, error, () => selectAdminSection(key, true));
      }
    }
    if (key === "users") await Promise.all([
      component("Accounts", () => api("/api/moderation/admin/users?limit=50"), adminUsersPanel),
      component("Role history", () => api("/api/moderation/admin/role-actions"), (data) => roleHistoryPanel(data.items)),
    ]);
    if (key === "beta-access") await component(
      "Beta access grants",
      () => runAdminAction(() => api("/api/admin/beta-access-grants?limit=100"), workspace),
      adminBetaAccessPanel,
    );
    if (key === "security") await component("Security", () => runAdminAction(loadAdminSecurityPanel, workspace), (node) => node);
  }

  function adminBetaAccessPanel(initial) {
    const workspace = state.adminWorkspace;
    const recentAction = (action) => runAdminAction(action, workspace);
    const section = create("section", "admin-beta-access");
    const capacity = create("dl", "admin-beta-capacity");
    const list = create("div", "admin-beta-grant-list");
    const notice = create("p", "metric-note");
    notice.setAttribute("role", "status");
    const oneTime = create("section", "admin-beta-token");
    oneTime.hidden = true;
    const tokenInput = create("input");
    tokenInput.type = "text"; tokenInput.readOnly = true; tokenInput.spellcheck = false;
    tokenInput.setAttribute("aria-label", "One-time beta access token");
    const copyToken = create("button", "secondary-button", "Copy token"); copyToken.type = "button";
    const dismissToken = create("button", "text-button", "Dismiss token"); dismissToken.type = "button";
    const tokenStatus = create("p", "metric-note"); tokenStatus.setAttribute("role", "status");
    oneTime.append(
      create("strong", "", "Copy this one-time token now"),
      create("p", "", "It is shown only in this response. Refreshing or dismissing this panel removes it."),
      tokenInput,
      create("div", "button-row", ""),
      tokenStatus,
    );
    oneTime.querySelector(".button-row").append(copyToken, dismissToken);

    const validCapacity = (value) => value && ["current_members", "max_members", "pending_enrollments", "available_slots"]
      .every((key) => Number.isInteger(value[key]) && value[key] >= 0)
      && value.max_members >= 1 && value.current_members + value.pending_enrollments <= value.max_members
      && value.available_slots === value.max_members - value.current_members - value.pending_enrollments;
    const validGrant = (grant) => grant && typeof grant.id === "string"
      && ["enroll", "recover"].includes(grant.purpose)
      && ["pending", "used", "revoked", "expired"].includes(grant.state)
      && typeof grant.verification_method === "string"
      && typeof grant.target?.label === "string";
    if (!Array.isArray(initial?.items) || initial.items.some((grant) => !validGrant(grant))
      || !validCapacity(initial?.capacity)) throw new Error("The server returned an invalid beta access grant list.");
    const grants = initial.items.slice();
    let capacityState = { ...initial.capacity };
    let issuing = false;
    const revokeBusy = new Set();

    const clearOneTimeToken = () => {
      tokenInput.value = ""; tokenStatus.textContent = ""; oneTime.hidden = true;
    };
    dismissToken.addEventListener("click", clearOneTimeToken);
    copyToken.addEventListener("click", async () => {
      if (!tokenInput.value) return;
      try {
        await navigator.clipboard.writeText(tokenInput.value);
        tokenStatus.textContent = "One-time token copied.";
      } catch (_) {
        tokenInput.focus(); tokenInput.select();
        tokenStatus.textContent = "Automatic copy was unavailable. Copy the selected token manually.";
      }
    });

    const renderCapacity = () => {
      clear(capacity);
      for (const [label, value] of [
        ["Active members", capacityState.current_members],
        ["Pending enrollments", capacityState.pending_enrollments],
        ["Available slots", capacityState.available_slots],
        ["Configured ceiling", capacityState.max_members],
      ]) {
        const metric = create("div");
        metric.append(create("dt", "", label), create("dd", "", formatCount(value)));
        capacity.append(metric);
      }
    };
    const renderList = () => {
      clear(list);
      for (const grant of grants) {
        const row = create("article", "admin-beta-grant-row");
        const target = create("strong", "", grant.target.label);
        const reference = grant.target.kind === "recovery" && grant.target.user_id
          ? create("code", "", grant.target.user_id) : null;
        row.append(
          target,
          create("span", "", `${utilityLabel(grant.purpose)} · ${utilityLabel(grant.verification_method)} · ${utilityLabel(grant.state)}`),
          create("time", "", `Expires ${new Date(grant.expires_at).toLocaleString()}`),
        );
        if (reference) row.append(reference);
        if (grant.state === "pending") {
          const revoke = create("button", "danger-button", "Revoke"); revoke.type = "button";
          revoke.disabled = revokeBusy.has(grant.id);
          revoke.addEventListener("click", async () => {
            if (revokeBusy.has(grant.id) || !adminWorkspaceCurrent(workspace)) return;
            revokeBusy.add(grant.id); revoke.disabled = true; notice.textContent = "Revoking grant…";
            try {
              const result = await recentAction(() => api(`/api/admin/beta-access-grants/${encodeURIComponent(grant.id)}`, {
                method: "DELETE", csrf: true,
              }));
              if (result?.grant_id !== grant.id || typeof result.revoked !== "boolean" || !validCapacity(result.capacity)) {
                throw new Error("The server did not confirm the grant revocation. Refresh before retrying.");
              }
              capacityState = { ...result.capacity };
              if (result.revoked) grant.state = "revoked";
              notice.textContent = result.revoked ? "Grant revoked." : "Grant was already unavailable.";
              renderCapacity(); renderList();
            } catch (error) {
              if (handleAdminReadAccessLoss(error, workspace)) return;
              if (error.name !== "AbortError") notice.textContent = error.message;
            } finally {
              revokeBusy.delete(grant.id);
              if (adminWorkspaceCurrent(workspace)) renderList();
            }
          });
          row.append(revoke);
        }
        list.append(row);
      }
      if (!grants.length) list.append(create("p", "admin-section-empty", "No beta access grants have been issued."));
    };

    const verificationSelect = () => {
      const select = create("select");
      for (const [value, label] of [["in_person", "In person"], ["video_call", "Video call"], ["known_channel", "Known channel"]]) {
        const option = create("option", "", label); option.value = value; select.append(option);
      }
      return select;
    };
    const issue = async (form, submit, payload) => {
      if (issuing || !adminWorkspaceCurrent(workspace) || !form.reportValidity()) return;
      issuing = true; submit.disabled = true; notice.textContent = "Issuing one-use grant…"; clearOneTimeToken();
      try {
        const result = await recentAction(() => api("/api/admin/beta-access-grants", { method: "POST", csrf: true, json: payload() }));
        if (!validGrant(result?.grant) || !validCapacity(result?.capacity)
          || typeof result.token !== "string" || !/^[A-Za-z0-9_-]{43}$/.test(result.token)) {
          throw new Error("The server did not return a valid one-time grant. Refresh before retrying.");
        }
        grants.unshift(result.grant); capacityState = { ...result.capacity };
        tokenInput.value = result.token; oneTime.hidden = false;
        tokenStatus.textContent = "Copy this token before leaving or refreshing this section.";
        form.reset(); notice.textContent = "Grant issued. The token will not be shown again.";
        renderCapacity(); renderList(); tokenInput.focus(); tokenInput.select();
      } catch (error) {
        if (handleAdminReadAccessLoss(error, workspace)) return;
        if (error.name !== "AbortError") notice.textContent = error.message;
      } finally {
        issuing = false;
        if (adminWorkspaceCurrent(workspace)) submit.disabled = false;
      }
    };

    const enrollment = create("form", "admin-beta-form");
    const email = create("input"); email.type = "email"; email.required = true; email.autocomplete = "off";
    const handle = create("input"); handle.required = true; handle.minLength = 3; handle.maxLength = 30; handle.autocomplete = "off";
    const displayName = create("input"); displayName.required = true; displayName.minLength = 2; displayName.maxLength = 60; displayName.autocomplete = "off";
    const enrollmentMethod = verificationSelect();
    const enrollSubmit = create("button", "publish-button", "Issue enrollment grant"); enrollSubmit.type = "submit";
    enrollment.append(
      create("h4", "", "Enroll a verified beta member"),
      dialogField("Email", email), dialogField("Handle", handle), dialogField("Display name", displayName),
      dialogField("Operator verification", enrollmentMethod), enrollSubmit,
    );
    enrollment.addEventListener("submit", (event) => {
      event.preventDefault();
      issue(enrollment, enrollSubmit, () => ({
        purpose: "enroll", verification_method: enrollmentMethod.value,
        email: email.value, handle: handle.value, display_name: displayName.value,
      }));
    });

    const recovery = create("form", "admin-beta-form");
    const userId = create("input"); userId.required = true; userId.minLength = 36; userId.maxLength = 36; userId.autocomplete = "off";
    const recoveryMethod = verificationSelect();
    const recoverySubmit = create("button", "publish-button", "Issue recovery grant"); recoverySubmit.type = "submit";
    recovery.append(
      create("h4", "", "Recover a verified member"),
      dialogField("Member user ID", userId, "Use the exact account ID from Accounts & roles."),
      dialogField("Operator verification", recoveryMethod), recoverySubmit,
    );
    recovery.addEventListener("submit", (event) => {
      event.preventDefault();
      issue(recovery, recoverySubmit, () => ({
        purpose: "recover", verification_method: recoveryMethod.value, user_id: userId.value,
      }));
    });

    const forms = create("div", "admin-beta-forms"); forms.append(enrollment, recovery);
    section.append(
      create("h3", "", "Closed beta membership"),
      create("p", "metric-note", "Enrollment and recovery tokens expire within 15 minutes, work once, and are never recoverable from storage."),
      capacity, forms, oneTime, notice,
      create("h4", "", "Recent grants"), list,
    );
    renderCapacity(); renderList();
    return section;
  }

  function adminFriendInvitesPanel() {
    const workspace = state.adminWorkspace;
    const section = create("section", "admin-beta-access");
    const form = create("form", "auth-form");
    const count = create("input"); count.type = "number"; count.min = "1"; count.max = "10"; count.value = "1"; count.required = true;
    const days = create("input"); days.type = "number"; days.min = "1"; days.max = "30"; days.value = "30"; days.required = true;
    const countLabel = create("label", "field"); countLabel.append(create("span", "", "Number of codes"), count);
    const daysLabel = create("label", "field"); daysLabel.append(create("span", "", "Expires after days"), days);
    const submit = create("button", "publish-button", "Create invite codes"); submit.type = "submit";
    const output = create("textarea"); output.readOnly = true; output.rows = 8; output.hidden = true; output.spellcheck = false;
    output.setAttribute("aria-label", "One-use beta invite codes");
    const status = create("p", "metric-note"); status.setAttribute("role", "status");
    const copy = create("button", "secondary-button", "Copy all"); copy.type = "button"; copy.hidden = true;
    const dismiss = create("button", "text-button", "Dismiss codes"); dismiss.type = "button"; dismiss.hidden = true;
    form.append(countLabel, daysLabel, submit);
    section.append(form, output, create("div", "button-row", ""), status);
    section.querySelector(".button-row").append(copy, dismiss);
    const clearCodes = () => { output.value = ""; output.hidden = true; copy.hidden = true; dismiss.hidden = true; };
    dismiss.addEventListener("click", () => { clearCodes(); status.textContent = "Codes removed from this screen."; });
    copy.addEventListener("click", async () => {
      try { await navigator.clipboard.writeText(output.value); status.textContent = "Invite codes copied."; }
      catch (_) { output.focus(); output.select(); status.textContent = "Copy the selected codes manually."; }
    });
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!form.reportValidity() || !adminWorkspaceCurrent(workspace)) return;
      clearCodes(); submit.disabled = true; status.textContent = "Creating one-use codes…";
      try {
        const result = await runAdminAction(() => api("/api/staff/friend-invites", {
          method: "POST", csrf: true, json: { count: Number(count.value), expires_days: Number(days.value) },
        }), workspace);
        if (!adminWorkspaceCurrent(workspace) || !Array.isArray(result?.codes)
          || result.codes.some((code) => typeof code !== "string")) return;
        output.value = result.codes.join("\n"); output.hidden = false; copy.hidden = false; dismiss.hidden = false;
        status.textContent = `${result.codes.length} one-use ${result.codes.length === 1 ? "code" : "codes"} created. ${result.capacity?.available_slots ?? 0} beta slots remain.`;
      } catch (error) {
        if (error.name !== "AbortError") status.textContent = error.message;
      } finally { if (adminWorkspaceCurrent(workspace)) submit.disabled = false; }
    });
    return section;
  }

  async function loadAdminReviewSection(workspace, key, entry, current) {
    const filters = create("div", "admin-filters");
    const search = create("input"); search.type = "search"; search.placeholder = "Filter loaded items";
    search.setAttribute("aria-label", "Filter loaded items"); search.value = entry.filters.query || "";
    filters.append(dialogField("Filter loaded items", search));
    const groups = [];
    const applySearch = () => {
      entry.filters.query = search.value;
      const query = search.value.trim().toLocaleLowerCase();
      for (const group of groups) {
        let visible = 0;
        for (const row of group.rows) {
          row.hidden = Boolean(query && !row.textContent.toLocaleLowerCase().includes(query));
          if (!row.hidden) visible += 1;
        }
        group.matches.textContent = query ? `${visible} of ${group.rows.length} loaded items match. Load more to search further.` : "";
        group.matches.hidden = !query;
      }
    };
    search.addEventListener("input", applySearch);
    let status = entry.filters.status || (key === "history" ? "all" : "pending");
    if (key !== "history") {
      const select = create("select"); select.setAttribute("aria-label", "Filter by review status");
      const values = key === "appeals" ? ["pending", "upheld", "overturned", "all"] : ["pending", "reviewing", "resolved", "dismissed", "all"];
      for (const value of values) { const option = create("option", "", utilityLabel(value)); option.value = value; select.append(option); }
      select.value = status;
      select.addEventListener("change", () => { entry.filters.status = select.value; if (current()) loadAdminSection(workspace, key, entry); });
      filters.append(dialogField("Status", select));
    }
    let category = entry.filters.category || "all";
    if (key === "reports") {
      const select = create("select"); select.setAttribute("aria-label", "Filter report type");
      for (const [value, label] of [["all", "All report types"], ["drop", "Drops"], ["profile", "Profiles"], ["chat", "Public chat"], ["dm", "Private messages"]]) {
        const option = create("option", "", label); option.value = value; select.append(option);
      }
      select.value = category;
      select.addEventListener("change", () => { entry.filters.category = select.value; if (current()) loadAdminSection(workspace, key, entry); });
      filters.append(dialogField("Report type", select));
    }
    entry.panel.append(filters);
    const definitions = key === "reports" ? [
      ["drop", "Drop reports", "reports", reportReview],
      ["profile", "Profile reports", "user-reports", userReportReview],
      ["chat", "Public chat reports", "chat-reports", (item) => communicationReportReview(item, "chat")],
      ["dm", "Private message reports", "dm-reports", (item) => communicationReportReview(item, "dm")],
    ].filter(([kind]) => category === "all" || category === kind)
      : [[key, key === "appeals" ? "Appeals" : "Moderation actions", key === "appeals" ? "appeals" : "actions", key === "appeals" ? appealReview : moderationHistoryRow]];
    await Promise.all(definitions.map(async ([kind, title, endpoint, render]) => {
      const section = create("section", "admin-report-group"); section.dataset.queue = kind;
      const heading = create("h4", "", title); const count = create("p", "metric-note");
      const matches = create("p", "metric-note"); matches.hidden = true;
      const list = create("div", "admin-report-list"); const notice = create("div");
      const more = create("button", "secondary-button admin-load-more", "Load more"); more.type = "button"; more.hidden = true;
      more.setAttribute("aria-label", `Load more ${title.toLowerCase()}`);
      section.append(heading, count, matches, list, notice, more); entry.panel.append(section);
      const group = { rows: [], matches, cursor: null, ids: new Set(), loading: false }; groups.push(group);
      const load = async (append = false) => {
        if (!current() || group.loading || (append && !group.cursor)) return;
        group.loading = true; more.disabled = true; clear(notice);
        notice.append(create("p", "admin-section-loading", "Loading…"));
        const params = new URLSearchParams({ limit: "50" });
        if (key !== "history") params.set("status", status);
        if (append) params.set("cursor", group.cursor);
        try {
          const data = await api(`/api/moderation/${endpoint}?${params}`);
          if (!current()) return;
          if (!Array.isArray(data?.items) || data.items.some((item) => !item || typeof item.id !== "string")
            || (data.next_cursor != null && typeof data.next_cursor !== "string")
            || (append && data.next_cursor && data.next_cursor === group.cursor)) {
            throw new Error("The server returned an invalid queue. Please retry.");
          }
          const rows = [];
          const batchIds = new Set(group.ids);
          for (const item of data.items) {
            if (batchIds.has(item.id)) continue;
            batchIds.add(item.id);
            const row = render(item); rows.push([item.id, row]);
          }
          clear(notice);
          for (const [id, row] of rows) { group.ids.add(id); group.rows.push(row); list.append(row); }
          group.cursor = data.next_cursor || null;
          more.hidden = !group.cursor;
          const total = Number.isInteger(data.total) && data.total >= 0 ? data.total : null;
          count.textContent = total === null ? `${group.rows.length} loaded · server update required for complete queue counts and pagination.`
            : `${group.rows.length} loaded · ${total} matching ${total === 1 ? "item" : "items"} at last load`;
          if (!group.rows.length) notice.append(create("p", "admin-section-empty", total === 0 ? "No items match this status." : "No items returned by the server."));
          applySearch();
        } catch (error) {
          if (!current()) return;
          if (handleAdminReadAccessLoss(error, workspace)) return;
          clear(notice); adminLoadError(notice, error, () => load(append));
        } finally {
          group.loading = false; if (current()) more.disabled = false;
        }
      };
      more.addEventListener("click", () => load(true));
      await load();
    }));
  }


  function safetyContentRemoval(item, reviewed, workspace, current, onSaved) {
    const box = create("div", "safety-content-removal");
    box.append(create("p", "metric-note", "Remove this post or message from view. Its author will receive your reason and can appeal. Their account stays unchanged."));
    const reason = create("textarea"); reason.rows = 3; reason.minLength = 3; reason.maxLength = 500; reason.required = true;
    reason.setAttribute("aria-label", "Content removal reason");
    reason.placeholder = "Explain which rule this content breaks.";
    const remove = create("button", "danger-button", "Remove reported content"); remove.type = "button";
    const notice = create("p", "form-error"); notice.hidden = true; notice.setAttribute("role", "alert");
    box.append(dialogField("Reason sent to the author", reason), remove, notice);
    let busy = false, finished = false, submission = null;
    remove.addEventListener("click", async () => {
      if (busy || finished || !current()) return;
      if (!submission) {
        if (!reason.reportValidity() || reason.value.trim().length < 3) return;
        submission = {reason: reason.value.trim(), request_token: crypto.randomUUID(),
          expected_report_updated_at: item.updated_at, expected_content_revision: reviewed.content_revision};
      }
      busy = true; remove.disabled = true; reason.readOnly = true; hideError(notice);
      try {
        const result = await runAdminAction(() => {
          if (!current()) throw new DOMException("Content view changed.", "AbortError");
          return api(`/api/moderation/safety-reports/${encodeURIComponent(item.id)}/remove-content`, {
            method: "POST", csrf: true, json: submission,
          });
        }, workspace);
        if (!current()) return;
        if (result?.report_id !== item.id || result.request_token !== submission.request_token
          || result.action?.action !== `remove_${item.target_type}` || typeof result.action.id !== "string"
          || result.action[`target_${item.target_type}_id`] !== item.target_id || typeof result.replayed !== "boolean") {
          throw new Error("The removal could not be confirmed. Retry to check the same decision.");
        }
        finished = true; clear(box);
        box.append(create("p", "", `Removal decision confirmed · ${result.action.id}`),
          create("p", "metric-note", "Reload the case to see current content status, history and any appeal."));
        onSaved?.(result);
      } catch (error) {
        if (!current() || error.name === "AbortError" || handleAdminReadAccessLoss(error, workspace)) return;
        showError(notice, error.message || "The removal could not be confirmed.");
        if ([400, 404, 409, 422].includes(error.status)) {
          finished = true;
          notice.append(create("span", "", " Reload the case and review it before another decision."));
        } else remove.textContent = "Retry removal confirmation";
      } finally { busy = false; if (current()) remove.disabled = finished; }
    });
    return box;
  }

  function safetyContentReview(item, workspace, current, onSaved) {
    const section = create("section", "safety-content-review");
    section.setAttribute("aria-label", "Reported content");
    const toggle = create("button", "secondary-button", "View reported content"); toggle.type = "button";
    toggle.setAttribute("aria-expanded", "false");
    const content = create("div", "safety-content-body"); content.hidden = true;
    content.setAttribute("role", "status");
    let generation = 0, expanded = false;
    const hide = () => {
      generation++; expanded = false; clear(content); content.hidden = true;
      toggle.textContent = "View reported content"; toggle.setAttribute("aria-expanded", "false");
    };
    toggle.addEventListener("click", async () => {
      if (!current()) return;
      if (expanded) { hide(); return; }
      expanded = true; const version = ++generation;
      toggle.textContent = "Hide reported content"; toggle.setAttribute("aria-expanded", "true");
      content.hidden = false; clear(content); content.append(create("p", "metric-note", "Loading reported content…"));
      try {
        const data = await runAdminAction(() => {
          if (!current() || version !== generation) throw new DOMException("Content view closed.", "AbortError");
          return api(`/api/safety/reports/${encodeURIComponent(item.id)}/context`);
        }, workspace);
        if (!current() || version !== generation) return;
        if (data?.report_id !== item.id || data.target_id !== item.target_id || data.target_type !== item.target_type
          || data.source !== "current_record" || !Array.isArray(data.fields)
          || !["available", "unavailable", "restricted", "metadata_only"].includes(data.availability)
          || !Number.isFinite(Date.parse(data.observed_at))
          || data.fields.some(field => typeof field?.label !== "string" || typeof field.value !== "string")) {
          throw new Error("The reported content could not be confirmed. Close this view and try again.");
        }
        clear(content);
        content.append(create("p", "metric-note", `Current stored record, checked ${new Date(data.observed_at).toLocaleString()}. This is not a preserved copy from when the report was filed.`));
        if (data.availability === "restricted") {
          content.append(create("p", "", "Content is restricted and cannot be shown in this review view.")); return;
        }
        if (data.availability === "unavailable") {
          content.append(create("p", "", "The reported content is unavailable or has been deleted.")); return;
        }
        if (data.record_status) content.append(create("p", "metric-note", `Content status: ${data.record_status}`));
        if (data.matches_report_fingerprint === false) content.append(create("p", "form-error", "The current record does not match the fingerprint saved with the report."));
        if (data.availability === "metadata_only") content.append(create("p", "metric-note", "Media information only. No image, video or audio preview is included."));
        for (const field of data.fields) {
          content.append(create("strong", "", field.label), create("blockquote", "report-evidence", field.value || "No text saved."));
        }
        if (data.can_remove === true && data.availability === "available" && data.record_status === "active"
          && ["drop", "chat_message", "direct_message"].includes(item.target_type)
          && /^[0-9a-f]{64}$/.test(data.content_revision || "")) {
          content.append(safetyContentRemoval(item, data, workspace,
            () => current() && expanded && generation === version, onSaved));
        }
      } catch (error) {
        if (!current() || version !== generation || error.name === "AbortError") return;
        if (handleAdminReadAccessLoss(error, workspace)) { hide(); return; }
        clear(content); content.append(create("p", "form-error", error.message || "Unable to load reported content."));
        expanded = false; toggle.textContent = "Retry reported content"; toggle.setAttribute("aria-expanded", "false");
      }
    });
    section.append(toggle, content); return section;
  }

  function safetyAccountActions(item, workspace, current, onSaved) {
    const section = create("section", "safety-case-actions");
    section.setAttribute("aria-label", "Actions for this case");
    const open = create("button", "secondary-button", "Account actions and case history"); open.type = "button";
    const history = create("div", "safety-action-history");
    const controls = create("div");
    const notice = create("div"); notice.setAttribute("role", "status");
    const more = create("button", "secondary-button", "More decisions"); more.type = "button"; more.hidden = true;
    section.append(open, notice, controls, history, more);
    let loading = false, initialized = false, cursor = null;
    const seen = new Set();
    const load = async () => {
      if (loading || !current()) return;
      loading = true; open.disabled = true; more.disabled = true;
      clear(notice); notice.append(create("p", "metric-note", "Loading case decisions…"));
      try {
        const params = new URLSearchParams({limit: "20"}); if (cursor) params.set("cursor", cursor);
        const data = await runAdminAction(() => {
          if (!current()) throw new DOMException("Case view changed.", "AbortError");
          return api(`/api/safety/reports/${encodeURIComponent(item.id)}/actions?${params}`);
        }, workspace);
        if (!current()) return;
        if (data?.report_id !== item.id || !Array.isArray(data.items) || typeof data.can_act !== "boolean"
          || (data.account !== null && (data.account?.id !== item.target_owner_id || typeof data.account.display_name !== "string" || typeof data.account.handle !== "string"))
          || data.items.some(action => typeof action?.id !== "string" || typeof action.action !== "string" || typeof action.reason !== "string")) {
          throw new Error("These case decisions could not be confirmed. Try again.");
        }
        clear(notice); open.hidden = true;
        if (!initialized) {
          initialized = true;
          controls.append(create("h4", "", "Account action"));
          if (data.can_act && data.account) {
            const identity = create("p", "metric-note", `${data.account.display_name} · @${data.account.handle}`);
            controls.append(identity, create("p", "metric-note", "This changes the reported account and records the decision in this case. It does not remove individual posts or messages."));
            const action = adminSanctionControl({user: data.account, status: data.account.status}, callback => runAdminAction(() => {
              if (!current()) throw new DOMException("Case view changed.", "AbortError");
              return callback();
            }, workspace), identity, {
              reportId: item.id, expectedReportUpdatedAt: item.updated_at,
              onSaved: result => {
                if (!current()) return;
                clear(controls); controls.append(create("p", "", `Decision confirmed · ${result.action.id}`),
                  create("p", "metric-note", "Reload the case before another update."));
                clear(history); history.append(create("p", "metric-note", "Decision history changed. Reload the case to read the current history.")); more.hidden = true;
                onSaved(result);
              },
            });
            controls.append(action);
          } else controls.append(create("p", "metric-note", "Account actions are unavailable for this account or your role."));
          history.append(create("h4", "", "Decisions recorded in this case"));
          if (!data.items.length) history.append(create("p", "metric-note", "No decisions recorded."));
        }
        const labels = {warn: "Warning", suspend: "Suspension", ban: "Ban", restore: "Access restored",
          remove_drop: "Post removed", remove_chat_message: "Message removed", remove_direct_message: "Private message removed"};
        for (const action of data.items) {
          if (seen.has(action.id)) continue;
          seen.add(action.id);
          const row = create("article", "safety-action-entry"); row.dataset.caseActionId = action.id;
          row.append(create("strong", "", `${labels[action.action] || action.action}${action.reverted_at ? " · Reversed" : ""}`),
            create("p", "", action.reason), create("p", "metric-note", `${new Date(action.starts_at).toLocaleString()} · ${action.id}`));
          if (action.ends_at) row.append(create("p", "metric-note", `Decision end: ${new Date(action.ends_at).toLocaleString()}`));
          if (action.appeal) {
            const appealLabels = {pending: "Awaiting review", upheld: "Decision upheld", overturned: "Decision overturned"};
            row.append(create("p", "metric-note", `Appeal · ${appealLabels[action.appeal.status] || action.appeal.status}`));
            if (action.appeal.resolution) row.append(create("p", "", action.appeal.resolution));
          }
          history.append(row);
        }
        cursor = data.next_cursor || null; more.hidden = !cursor;
      } catch (error) {
        if (!current() || error.name === "AbortError" || handleAdminReadAccessLoss(error, workspace)) return;
        clear(notice); adminLoadError(notice, error, load);
      } finally { loading = false; if (current()) { open.disabled = false; more.disabled = false; } }
    };
    open.addEventListener("click", load); more.addEventListener("click", load);
    return section;
  }

  async function loadSafetyReports(workspace, entry, current) {
    const filter = create("select"); filter.setAttribute("aria-label", "Safety case status");
    const labels = { open: "Open", triaged: "Triaged", under_review: "Under review", appealed: "Appealed",
      actioned: "Action taken", no_violation: "No violation", closed: "Closed" };
    for (const [value, label] of Object.entries(labels)) {
      const option = create("option", "", label); option.value = value; filter.append(option);
    }
    filter.value = entry.filters.state || "open";
    filter.addEventListener("change", () => {
      if (!current()) return;
      entry.filters.state = filter.value;
      entry.filters.reportId = "";
      void selectAdminSection("safety-reports", true);
    });
    const statusControl = create("div", "admin-filters");
    statusControl.append(dialogField("Status", filter));
    const search = create("form", "admin-filters");
    const input = create("input"); input.type = "search"; input.maxLength = 36;
    input.placeholder = "Case ID"; input.setAttribute("aria-label", "Find safety case by ID");
    input.value = entry.filters.reportId || "";
    const find = create("button", "secondary-button", "Find case"); find.type = "submit";
    const reset = create("button", "text-button", "Back to status queue"); reset.type = "button";
    reset.hidden = !entry.filters.reportId;
    search.append(dialogField("Case ID", input), find, reset);
    search.addEventListener("submit", event => {
      event.preventDefault(); if (!current()) return;
      const value = input.value.trim();
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
        input.setCustomValidity("Enter the complete case ID."); input.reportValidity(); return;
      }
      entry.filters.reportId = value;
      void selectAdminSection("safety-reports", true);
    });
    input.addEventListener("input", () => input.setCustomValidity(""));
    reset.addEventListener("click", () => {
      if (!current()) return;
      entry.filters.reportId = ""; void selectAdminSection("safety-reports", true);
    });
    entry.panel.append(statusControl, search, create("p", "metric-note", entry.filters.reportId
      ? "Showing the matching case across all statuses."
      : "Urgent cases first, then oldest. Load more to continue; refresh to check for new cases. P0 cases are available only to specialist staff."));
    const region = create("section", "admin-queue");
    const pageNotice = create("div"); pageNotice.setAttribute("role", "status");
    const more = create("button", "secondary-button", "Load more cases"); more.type = "button"; more.hidden = true;
    entry.panel.append(region, pageNotice, more);
    const seen = new Set(); let cursor = null, loading = false;
    const load = async () => {
      if (loading || !current()) return;
      loading = true; more.disabled = true;
      clear(pageNotice); pageNotice.append(create("p", "metric-note", "Loading cases…"));
      const params = new URLSearchParams({limit: "50"});
      if (entry.filters.reportId) params.set("report_id", entry.filters.reportId);
      else params.set("state", filter.value);
      if (cursor) params.set("cursor", cursor);
      try {
        const data = await api(`/api/safety/reports/queue?${params}`);
        if (!current()) return;
        clear(pageNotice);
        if (!data.items.length && !seen.size) region.append(create("p", "admin-section-empty", entry.filters.reportId
          ? "Case not found or unavailable to your role." : "No safety cases in this status."));
        for (const item of data.items) {
          if (seen.has(item.id)) continue;
          seen.add(item.id);
          const card = create("section", "safety-case"); card.dataset.safetyReportId = item.id;
          const form = create("form", "report-review");
          form.append(create("h3", "", `${item.severity.toUpperCase()} · ${item.category.replaceAll("_", " ")}`),
            create("p", "metric-note", `Case ${item.id} · ${labels[item.state] || item.state}`),
            create("p", "metric-note", `${item.target_type.replaceAll("_", " ")} · ${item.target_id}`),
            create("p", "metric-note", `Assigned to: ${item.assigned_to_id || "Unassigned"}`),
            create("p", "metric-note", `Updated ${new Date(item.updated_at).toLocaleString()}`));
          if (item.details) form.append(create("p", "", item.details));
          else form.append(create("p", "metric-note", "No report text is available in this queue."));
          if (item.resolution) form.append(create("p", "", `Latest note: ${item.resolution}`));
          const contentReview = safetyContentReview(item, workspace, () => current() && !stale, () => {
            stale = true; save.disabled = true; reload.hidden = false;
            showError(notice, "A content removal was recorded. Reload the case before another update.");
          });
          const choice = create("select"); choice.setAttribute("aria-label", "New case status");
          for (const value of ["triaged", "under_review", "actioned", "no_violation", "closed"]) {
            const option = create("option", "", labels[value]); option.value = value; choice.append(option);
          }
          choice.value = "under_review";
          const note = create("textarea"); note.rows = 3; note.required = true; note.minLength = 3; note.maxLength = 1000;
          note.setAttribute("aria-label", "Case decision reason"); note.placeholder = "Record your assessment and any action already taken.";
          const save = create("button", "publish-button", "Save case update"); save.type = "submit";
          const notice = create("p", "form-error"); notice.hidden = true; notice.setAttribute("role", "alert");
          const reload = create("button", "secondary-button", "Reload queue"); reload.type = "button"; reload.hidden = true;
          reload.addEventListener("click", () => {
            if (current()) void selectAdminSection("safety-reports", true);
          });
          let busy = false, stale = false;
          form.addEventListener("submit", async event => {
            event.preventDefault();
            if (busy || stale || !current() || !form.reportValidity()) return;
            if (note.value.trim().length < 3) { note.focus(); return; }
            const payload = { state: choice.value, resolution: note.value.trim(), expected_updated_at: item.updated_at };
            busy = true; save.disabled = true; hideError(notice);
            choice.disabled = true; note.readOnly = true;
            try {
              const updated = await runAdminAction(() => {
                if (!current()) throw new DOMException("Queue changed. Review the current case.", "AbortError");
                return api(`/api/safety/reports/${encodeURIComponent(item.id)}`, {
                  method: "PUT", csrf: true, json: payload,
                });
              }, workspace);
              if (!current()) return;
              if (updated?.id !== item.id || updated.state !== payload.state
                || updated.resolution !== payload.resolution || typeof updated.updated_at !== "string"
                || updated.updated_at === item.updated_at) throw new Error("The update could not be confirmed. Reload the queue before trying again.");
              item.updated_at = updated.updated_at;
              clear(form); form.append(create("p", "", `Saved: ${labels[updated.state] || updated.state}`),
                create("p", "", updated.resolution));
            } catch (error) {
              if (!current() || error.name === "AbortError") return;
              showError(notice, error.message);
              reload.hidden = false;
              // Never reapply a stale decision automatically; retain the note for review/copying.
              stale = error.status === 409;
              if (!stale) save.disabled = false;
            } finally { busy = false; choice.disabled = false; note.readOnly = false; }
          });
          form.append(choice, note, save, notice, reload);
          card.append(form, contentReview, safetyAccountActions(item, workspace, () => current() && !stale, () => {
            stale = true; save.disabled = true; reload.hidden = false;
            showError(notice, "An account decision was recorded. Your note is kept here; reload the case before another update.");
          }));
          region.append(card);
        }
        cursor = data.next_cursor || null; more.hidden = !cursor;
        if (seen.size) pageNotice.append(create("p", "metric-note", `${seen.size} ${seen.size === 1 ? "case" : "cases"} loaded`));
      } catch (error) {
        if (!current() || handleAdminReadAccessLoss(error, workspace)) return;
        clear(pageNotice); adminLoadError(pageNotice, error, load);
      } finally { loading = false; if (current()) more.disabled = false; }
    };
    more.addEventListener("click", load);
    await load();
  }

  function formatBytes(value) {
    const units = ["B", "KB", "MB", "GB"];
    let size = Math.max(0, Number(value) || 0); let unit = 0;
    while (size >= 1024 && unit < units.length - 1) { size /= 1024; unit += 1; }
    return `${unit === 0 ? size : size.toFixed(1)} ${units[unit]}`;
  }

  function adminMediaReviewPanel(items, workspace) {
    const section = create("section", "admin-queue admin-media-queue");
    section.append(create("h3", "admin-queue-title", `Uploads waiting for review · ${items.length}`));
    for (const item of items) {
      const form = create("form", "report-review media-review");
      const kind = (item.content_type || "").split("/")[0];
      if (item.preview_url && kind === "image") {
        const figure = create("figure", "media-review-preview");
        const image = create("img"); image.src = item.preview_url; image.alt = "Upload waiting for review"; image.loading = "lazy"; image.decoding = "async";
        figure.append(image); form.append(figure);
      } else if (item.preview_url && (kind === "video" || kind === "audio")) {
        const figure = create("figure", "media-review-preview");
        const player = create(kind); player.src = item.preview_url; player.controls = true; player.preload = "metadata";
        player.setAttribute("aria-label", `Upload waiting for review (${kind})`);
        figure.append(player); form.append(figure);
      } else {
        form.append(create("p", "metric-note", "No preview is available for this upload yet."));
      }
      const owner = item.owner || null;
      form.append(
        create("h3", "", owner ? owner.display_name || `@${owner.handle}` : "Unknown member"),
        create("p", "", owner ? `@${owner.handle}` : "Owner unavailable"),
        create("p", "metric-note", `${item.content_type || "unknown type"} · ${formatBytes(item.size_bytes || 0)} · ${new Date(item.created_at).toLocaleString()}`),
        create("p", "metric-note", `Waiting because: ${(item.reason_codes || []).map((code) => String(code).replaceAll("_", " ")).join(", ") || "review requested"}`),
      );
      const reason = create("textarea"); reason.maxLength = 1000; reason.minLength = 3; reason.rows = 2; reason.required = true;
      reason.value = "Reviewed by staff in the closed beta"; reason.setAttribute("aria-label", "Review reason");
      const actions = create("div", "button-row");
      const reject = create("button", "danger-button", "Reject"); reject.type = "button";
      const approve = create("button", "publish-button", "Approve"); approve.type = "button";
      const decide = async (action) => {
        const text = reason.value.trim();
        if (text.length < 3) { toast("Write a short reason first", true); reason.focus(); return; }
        reject.disabled = true; approve.disabled = true;
        try {
          const result = await runAdminAction(() => api(`/api/safety/media/${encodeURIComponent(item.asset_id)}`, {
            method: "PUT", csrf: true, json: { action, reason: text },
          }), workspace);
          clear(form);
          form.append(
            create("strong", "", action === "approve" ? "Approved. The member can now use it in a Drop." : "Rejected"),
            create("p", "metric-note", `Media is now ${String(result.processing_status || action).replaceAll("_", " ")}.`),
          );
          toast(action === "approve" ? "Upload approved" : "Upload rejected");
        } catch (error) { toast(error.message, true); reject.disabled = false; approve.disabled = false; }
      };
      reject.addEventListener("click", () => decide("reject")); approve.addEventListener("click", () => decide("approve"));
      actions.append(reject, approve); form.append(reason, actions); section.append(form);
    }
    return section;
  }

  function adminSponsorPanel(items) {
    const section = create("section", "admin-queue admin-sponsor-queue");
    section.append(create("h3", "admin-queue-title", `Sponsor review · ${items.length}`));
    for (const campaign of items) {
      const form = create("form", "report-review sponsor-review");
      const destinationUrl = providerUrl(campaign.cta?.url);
      const destination = create("p", "metric-note sponsor-destination");
      destination.append(document.createTextNode(`${campaign.disclosure === "Sponsored" ? campaign.disclosure : "Sponsored"} · ${campaign.cta?.label || "Learn more"} · `));
      if (destinationUrl) {
        const link = create("a", "", new URL(destinationUrl).hostname);
        link.href = destinationUrl; link.target = "_blank"; link.rel = "noopener noreferrer nofollow";
        destination.append(link);
      } else destination.append(create("strong", "", "Invalid destination"));
      form.append(
        create("h3", "", campaign.name),
        accountNameNode(campaign.sponsor, "p"),
        create("p", "", `@${campaign.sponsor.handle}`),
        create("p", "", `${campaign.creative.title} · ${formatCount(campaign.slot_budget)} Board slots · ${formatMoney(campaign.price_per_slot_minor, campaign.currency)} each`),
        create("p", "metric-note", `${new Date(campaign.starts_at).toLocaleString()} – ${new Date(campaign.ends_at).toLocaleString()}`),
        destination,
      );
      const note = create("textarea"); note.maxLength = 500; note.rows = 2; note.placeholder = "Review note · optional"; note.setAttribute("aria-label", `Review note for ${campaign.name}`);
      const actions = create("div", "button-row");
      const reject = create("button", "danger-button", "Reject"); reject.type = "button";
      const approve = create("button", "publish-button", "Approve"); approve.type = "button";
      approve.disabled = !destinationUrl;
      const decide = async (decision) => {
        reject.disabled = true; approve.disabled = true;
        try {
          const result = await api(`/api/sponsors/campaigns/${encodeURIComponent(campaign.id)}/decision`, {
            method: "POST", csrf: true, json: { decision, note: note.value.trim() },
          });
          clear(form);
          form.append(create("strong", "", decision === "approve" ? "Approved for payment" : "Rejected"), create("p", "metric-note", `Campaign remains ${result.status.replaceAll("_", " ")}.`));
          toast("Sponsor review recorded");
        } catch (error) { toast(error.message, true); reject.disabled = false; approve.disabled = !destinationUrl; }
      };
      reject.addEventListener("click", () => decide("reject")); approve.addEventListener("click", () => decide("approve"));
      actions.append(reject, approve); form.append(note, actions); section.append(form);
    }
    return section;
  }

  function promptRecentReauthentication() {
    return new Promise((resolve, reject) => {
      const dialog = buildUtilityDialog("Confirm your password");
      registerStaffDialog(dialog);
      const form = create("form", "utility-form");
      const username = create("input"); username.type = "email"; username.name = "username"; username.autocomplete = "username"; username.value = state.viewer?.email || ""; username.tabIndex = -1; username.setAttribute("aria-hidden", "true"); username.hidden = true;
      const password = create("input"); password.type = "password"; password.required = true; password.autocomplete = "current-password";
      const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
      const submit = create("button", "publish-button", "Continue"); submit.type = "submit";
      form.append(create("p", "utility-copy", "This sensitive action needs a recent password confirmation."), username, dialogField("Password", password), error, submit);
      let settled = false;
      form.addEventListener("submit", async (event) => {
        event.preventDefault(); hideError(error); submit.disabled = true;
        try {
          const result = await reauthenticate(password.value);
          settled = true; dialog.close(); resolve(result);
        } catch (requestError) { showError(error, requestError.message); submit.disabled = false; }
      });
      dialog.addEventListener("close", () => { if (!settled) reject(new DOMException("Canceled", "AbortError")); }, { once: true });
      dialog.append(form); openDialog(dialog); requestAnimationFrame(() => password.focus());
    });
  }

  async function runRecentAction(action) {
    const viewer = state.viewer;
    try { return await action(); }
    catch (error) {
      if (error.status === 403 && error.staffMfaRequired) {
        if (state.viewer === viewer) promptStaffVerification();
        throw error;
      }
      if (error.status !== 403 || !error.reauthenticationRequired) throw error;
      if (state.viewer !== viewer) throw new DOMException("Account changed", "AbortError");
      const method = error.reauthenticationMethod || (viewer?.password_login_enabled === false ? "provider" : "password");
      if (method !== "password") {
        throw new Error("Sign out and sign in again with your connected provider, then retry this sensitive action.");
      }
      await promptRecentReauthentication();
      if (state.viewer !== viewer) throw new DOMException("Account changed", "AbortError");
      return action();
    }
  }

  function staffReasonDialog({ title, copy, minimum = 5, confirmLabel = "Confirm", action }) {
    const dialog = buildUtilityDialog(title);
    registerStaffDialog(dialog);
    const form = create("form", "utility-form staff-reason-form");
    const reason = create("textarea"); reason.required = true; reason.minLength = minimum; reason.maxLength = 500; reason.rows = 4;
    const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
    const submit = create("button", "danger-button", confirmLabel); submit.type = "submit";
    form.append(create("p", "utility-copy", copy), dialogField("Reason", reason), error, submit);
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      hideError(error); submit.disabled = true;
      try {
        await action(reason.value.trim());
        dialog.close();
        await openAdmin();
      } catch (requestError) {
        if (requestError.name === "AbortError") { dialog.close(); return; }
        showError(error, requestError.message); submit.disabled = false;
      }
    });
    dialog.append(form); openDialog(dialog); requestAnimationFrame(() => reason.focus());
  }

  function staffAction(label, callback, danger = false) {
    const button = create("button", danger ? "danger-button" : "secondary-button", label);
    button.type = "button"; button.addEventListener("click", callback); return button;
  }

  function staffMicPanel(data, notice = "") {
    const workspace = state.adminWorkspace;
    const recentAction = (action) => runAdminAction(action, workspace);
    const section = create("section", "admin-queue connected-staff-tools");
    const current = data?.current || null;
    const status = create("p", "metric-note", notice);
    status.hidden = !notice;
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    const error = create("p", "form-error");
    error.hidden = true;
    error.setAttribute("role", "alert");
    const rotate = staffAction("Rotate Mic", async () => {
      if (!state.capabilities.community_writes) return;
      hideError(error);
      rotate.disabled = true;
      rotate.textContent = "Rotating…";
      rotate.setAttribute("aria-busy", "true");
      status.textContent = "Rotating the Mic…";
      status.hidden = false;
      try {
        const result = await recentAction(() => api("/api/world/mic/rotate", {
          method: "POST", csrf: true,
        }));
        state.world.mic = { current: result.current || null };
        renderRoomAffordances();
        if (state.discovery.mode === "world" && state.world.view === "pulse") renderWorld();
        const message = result.current?.holder
          ? `${result.current.holder.display_name} now has the Mic.`
          : "Rotation complete. The Mic is between turns.";
        section.replaceWith(staffMicPanel(result, message));
        toast(message);
      } catch (requestError) {
        if (requestError.name === "AbortError") {
          status.textContent = "Rotation canceled.";
          status.hidden = false;
        } else {
          status.hidden = true;
          showError(error, requestError.message);
        }
        rotate.disabled = false;
        rotate.textContent = "Rotate Mic";
        rotate.removeAttribute("aria-busy");
      }
    });
    rotate.setAttribute("aria-label", "Rotate Mic to the next eligible queued Drop");
    const header = create("div", "staff-tools-heading");
    header.append(create("h3", "admin-queue-title", "The Mic"));
    if (state.capabilities.community_writes) header.append(rotate);
    section.append(header);

    const row = create("article", "staff-tool-row");
    if (current) {
      const holder = current.holder?.display_name || "Current holder";
      const handle = current.holder?.handle ? ` · @${current.holder.handle}` : "";
      const drop = current.drop?.title || "Mic Drop unavailable in this view";
      const ends = current.ends_at ? new Date(current.ends_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "end time unavailable";
      row.append(
        accountNameNode(current.holder, "strong", "", holder),
        create("span", "metric-note", handle.replace(/^ · /, "")),
        create("span", "metric-note", `${drop} · ends ${ends}`),
      );
    } else {
      row.append(
        create("strong", "", "Between turns"),
        create("span", "metric-note", "No active Mic turn. Rotation selects the next eligible queued Drop when available."),
      );
    }
    section.append(row, status, error);
    return section;
  }

  function staffQuestionPanel(data) {
    const workspace = state.adminWorkspace;
    const recentAction = (action) => runAdminAction(action, workspace);
    const section = create("section", "admin-queue connected-staff-tools");
    const header = create("div", "staff-tools-heading");
    header.append(create("h3", "admin-queue-title", `Questions · ${formatCount(data.items?.length || 0)}`));
    if (state.viewer.role === "admin" && state.capabilities.community_writes) header.append(staffAction("Schedule", openPlatformQuestionDialog));
    section.append(header);
    for (const question of data.items || []) {
      const row = create("article", "staff-tool-row");
      row.append(
        create("strong", "", question.prompt),
        create("span", "metric-note", `${utilityLabel(question.question_type)} · ${utilityLabel(question.status)} · opens ${new Date(question.opens_at).toLocaleString()}`),
      );
      const actions = create("div", "compact-actions");
      const options = [];
      if (["scheduled", "active"].includes(question.status)) options.push(["close", "Close", false]);
      if (question.status === "closed") options.push(["reopen", "Reopen", false]);
      if (["scheduled", "active", "closed", "solved"].includes(question.status)) options.push(["remove", "Remove", true]);
      for (const [action, label, danger] of state.capabilities.community_writes ? options : []) {
        actions.append(staffAction(label, () => staffReasonDialog({
          title: `${label} question`,
          copy: `${label} “${question.prompt}”. This action is recorded.`,
          confirmLabel: label,
          action: (note) => recentAction(() => api(`/api/questions/${encodeURIComponent(question.id)}/moderate`, {
            method: "POST", csrf: true, json: { action, note },
          })),
        }), danger));
      }
      if (actions.childElementCount) row.append(actions);
      section.append(row);
    }
    if (!data.items?.length) section.append(create("p", "utility-empty", "No platform questions in this view."));
    return section;
  }

  function openPlatformQuestionDialog() {
    if (!state.capabilities.community_writes) return;
    const workspace = state.adminWorkspace;
    const recentAction = (action) => runAdminAction(action, workspace);
    const dialog = buildUtilityDialog("Schedule question");
    registerStaffDialog(dialog);
    const form = create("form", "utility-form");
    const type = create("select");
    [["platform", "Platform"], ["daily", "Daily"]].forEach(([value, label]) => { const option = create("option", "", label); option.value = value; type.append(option); });
    const prompt = create("textarea"); prompt.required = true; prompt.minLength = 8; prompt.maxLength = 280; prompt.rows = 3;
    const scheduled = create("input"); scheduled.type = "date"; scheduled.min = new Date().toISOString().slice(0, 10);
    const opens = create("input"); opens.type = "datetime-local";
    const closes = create("input"); closes.type = "datetime-local";
    const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
    const submit = create("button", "publish-button", "Schedule"); submit.type = "submit";
    const sync = () => { scheduled.required = type.value === "daily"; scheduled.closest("label").hidden = type.value !== "daily"; };
    type.addEventListener("change", sync);
    form.append(
      dialogField("Type", type), dialogField("Question", prompt), dialogField("Daily date", scheduled),
      dialogField("Opens · optional", opens), dialogField("Closes · optional", closes), error, submit,
    );
    sync();
    form.addEventListener("submit", async (event) => {
      event.preventDefault(); if (!form.reportValidity()) return; hideError(error); submit.disabled = true;
      try {
        await recentAction(() => api("/api/questions/platform", {
          method: "POST", csrf: true, json: {
            question_type: type.value,
            prompt: prompt.value.trim(),
            translations: {},
            scheduled_for: type.value === "daily" ? scheduled.value : null,
            opens_at: opens.value ? new Date(opens.value).toISOString() : null,
            closes_at: closes.value ? new Date(closes.value).toISOString() : null,
          },
        }));
        dialog.close(); toast("Question scheduled"); await openAdmin();
      } catch (requestError) { if (requestError.name !== "AbortError") showError(error, requestError.message); submit.disabled = false; }
    });
    dialog.append(form); openDialog(dialog); requestAnimationFrame(() => prompt.focus());
  }

  function staffMomentPanel(data) {
    const workspace = state.adminWorkspace;
    const recentAction = (action) => runAdminAction(action, workspace);
    const section = create("section", "admin-queue connected-staff-tools");
    const header = create("div", "staff-tools-heading");
    header.append(create("h3", "admin-queue-title", `Moments · ${formatCount(data.items?.length || 0)}`));
    if (state.viewer.role === "admin" && state.capabilities.community_writes) header.append(staffAction("Create", openCreateMomentDialog));
    section.append(header);
    for (const moment of data.items || []) {
      const row = create("article", "staff-tool-row");
      row.append(
        create("strong", "", moment.title),
        create("span", "metric-note", `${utilityLabel(moment.status)} · ${new Date(moment.starts_at).toLocaleString()} – ${new Date(moment.ends_at).toLocaleString()}`),
        create("p", "", moment.summary),
      );
      if (moment.moderation_note) row.append(create("p", "staff-private-note", `Staff note: ${moment.moderation_note}`));
      const actions = create("div", "compact-actions");
      const options = [];
      if (["scheduled", "active"].includes(moment.status)) options.push(["cancel", "Cancel", true]);
      if (moment.status === "active") options.push(["complete", "Complete", false]);
      if (["scheduled", "active", "complete", "cancelled"].includes(moment.status)) options.push(["remove", "Remove", true]);
      for (const [action, label, danger] of state.capabilities.community_writes ? options : []) {
        actions.append(staffAction(label, () => staffReasonDialog({
          title: `${label} Moment`, copy: `${label} “${moment.title}”. The reason remains staff-only.`, confirmLabel: label,
          action: (note) => recentAction(() => api(`/api/world/moments/${encodeURIComponent(moment.id)}/moderate`, {
            method: "POST", csrf: true, json: { action, note },
          })),
        }), danger));
      }
      if (actions.childElementCount) row.append(actions);
      section.append(row);
    }
    if (!data.items?.length) section.append(create("p", "utility-empty", "No Global Moments in this view."));
    return section;
  }

  function openCreateMomentDialog() {
    if (!state.capabilities.community_writes) return;
    const workspace = state.adminWorkspace;
    const recentAction = (action) => runAdminAction(action, workspace);
    const dialog = buildUtilityDialog("Create Global Moment");
    registerStaffDialog(dialog);
    const form = create("form", "utility-form");
    const title = create("input"); title.required = true; title.minLength = 3; title.maxLength = 120;
    const summary = create("textarea"); summary.required = true; summary.minLength = 8; summary.maxLength = 400; summary.rows = 3;
    const trigger = create("select");
    ["editorial", "board", "milestone", "scheduled"].forEach((value) => { const option = create("option", "", utilityLabel(value)); option.value = value; trigger.append(option); });
    const reason = create("textarea"); reason.required = true; reason.minLength = 10; reason.maxLength = 500; reason.rows = 3;
    const targetType = create("select");
    [["", "No target"], ["drop", "Drop"], ["poll", "Poll"], ["question", "Question"], ["board_slot", "Board slot"]].forEach(([value, label]) => { const option = create("option", "", label); option.value = value; targetType.append(option); });
    const targetId = create("input"); targetId.maxLength = 64; targetId.placeholder = "Target ID · optional";
    const starts = create("input"); starts.type = "datetime-local"; starts.required = true;
    const ends = create("input"); ends.type = "datetime-local"; ends.required = true;
    const startDate = new Date(Date.now() + 5 * 60 * 1000); const endDate = new Date(Date.now() + 65 * 60 * 1000);
    const localValue = (date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    starts.value = localValue(startDate); ends.value = localValue(endDate);
    const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
    const submit = create("button", "publish-button", "Create Moment"); submit.type = "submit";
    const targetSync = () => { targetId.required = Boolean(targetType.value); targetId.hidden = !targetType.value; };
    targetType.addEventListener("change", targetSync);
    form.append(
      dialogField("Title", title), dialogField("Summary", summary), dialogField("Trigger", trigger), dialogField("Trigger reason", reason),
      dialogField("Target", targetType), dialogField("Target ID", targetId), dialogField("Starts", starts), dialogField("Ends", ends), error, submit,
    );
    targetSync();
    form.addEventListener("submit", async (event) => {
      event.preventDefault(); if (!form.reportValidity()) return; hideError(error); submit.disabled = true;
      try {
        await recentAction(() => api("/api/world/moments", { method: "POST", csrf: true, json: {
          title: title.value.trim(), summary: summary.value.trim(), trigger_type: trigger.value, trigger_reason: reason.value.trim(),
          target_type: targetType.value || null, target_id: targetType.value ? targetId.value.trim() : null,
          starts_at: new Date(starts.value).toISOString(), ends_at: new Date(ends.value).toISOString(),
        } }));
        dialog.close(); toast("Global Moment created"); await openAdmin();
      } catch (requestError) { if (requestError.name !== "AbortError") showError(error, requestError.message); submit.disabled = false; }
    });
    dialog.append(form); openDialog(dialog); requestAnimationFrame(() => title.focus());
  }

  function staffCapsulePanel() {
    const workspace = state.adminWorkspace;
    const recentAction = (action) => runAdminAction(action, workspace);
    const section = create("section", "admin-queue connected-staff-tools");
    section.append(create("h3", "admin-queue-title", "Time Capsules"));
    if (!state.capabilities.community_writes) {
      section.append(create("p", "metric-note", "Time Capsule changes are unavailable while community publishing is read only."));
      return section;
    }
    if (state.viewer.role !== "admin") {
      section.append(create("p", "metric-note", "Capsule generation and amendments require an administrator."));
      return section;
    }
    const form = create("form", "utility-form staff-capsule-form");
    const date = create("input"); date.type = "date"; date.required = true;
    const yesterday = new Date(); yesterday.setUTCDate(yesterday.getUTCDate() - 1); date.max = yesterday.toISOString().slice(0, 10); date.value = date.max;
    const mode = create("select"); [["generate", "Generate missing"], ["amend", "Amend existing"]].forEach(([value, label]) => { const option = create("option", "", label); option.value = value; mode.append(option); });
    const reason = create("textarea"); reason.required = true; reason.minLength = 10; reason.maxLength = 500; reason.rows = 3;
    const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
    const submit = create("button", "secondary-button", "Continue"); submit.type = "submit";
    form.append(dialogField("UTC date", date), dialogField("Action", mode), dialogField("Reason", reason), error, submit);
    form.addEventListener("submit", async (event) => {
      event.preventDefault(); if (!form.reportValidity()) return; hideError(error); submit.disabled = true;
      try {
        await recentAction(() => api(`/api/time-capsules/${encodeURIComponent(date.value)}/${mode.value}`, {
          method: "POST", csrf: true, json: { reason: reason.value.trim() },
        }));
        toast(mode.value === "generate" ? "Time Capsule generated" : "Time Capsule amended");
        reason.value = ""; await openAdmin();
      } catch (requestError) { if (requestError.name !== "AbortError") showError(error, requestError.message); submit.disabled = false; }
    });
    section.append(form); return section;
  }

  function appendScoutRows(container, data) {
    for (const item of data.items || []) {
      const row = create("article", "staff-tool-row scout-moderation-row");
      const authority = [item.authority?.successful_first_here ? "First Here" : null, item.authority?.scout_ranking_entry ? "Scout ranking" : null].filter(Boolean).join(" · ") || "Authority no longer active";
      row.append(
        accountNameNode(item.user),
        create("span", "", `@${item.user.handle}`),
        create("span", "metric-note", `${utilityLabel(item.user.role)} · ${authority}`),
      );
      if (item.active_disqualification) {
        row.append(create("p", "staff-private-note", `Disqualified ${new Date(item.active_disqualification.created_at).toLocaleString()} · ${item.active_disqualification.reason}`));
      }
      const actions = create("div", "compact-actions");
      if (item.allowed_actions?.disqualify) actions.append(staffAction("Disqualify", () => staffReasonDialog({
        title: "Disqualify Scout", copy: `Remove @${item.user.handle} from global Scout authority.`, minimum: 10, confirmLabel: "Disqualify",
        action: (reason) => runRecentAction(() => api("/api/status/moderation/scouts/disqualifications", {
          method: "POST", csrf: true, json: { user_id: item.user.id, reason },
        })),
      }), true));
      if (item.allowed_actions?.revert && item.active_disqualification) actions.append(staffAction("Revert", () => staffReasonDialog({
        title: "Revert Scout disqualification", copy: "Remove this active disqualification. Any Scout authority remains subject to current eligibility.", minimum: 10, confirmLabel: "Revert",
        action: (reason) => runRecentAction(() => api(`/api/status/moderation/scouts/disqualifications/${encodeURIComponent(item.active_disqualification.id)}/revert`, {
          method: "POST", csrf: true, json: { reason },
        })),
      })));
      if (actions.childElementCount) row.append(actions);
      else row.append(create("span", "metric-note", "No action available for your role."));
      container.append(row);
    }
  }

  function staffScoutPanel(data) {
    const section = create("section", "admin-queue connected-staff-tools");
    section.append(create("h3", "admin-queue-title", "Scout authority"));
    const list = create("div", "staff-tool-list"); appendScoutRows(list, data); section.append(list);
    if (!data.items?.length) list.append(create("p", "utility-empty", "No active or currently disqualified Scout candidates."));
    if (data.has_more && data.next_offset != null) {
      const more = create("button", "secondary-button", "More Scouts"); more.type = "button";
      more.addEventListener("click", async () => {
        more.disabled = true;
        try {
          const next = await api(`/api/status/moderation/scouts?${new URLSearchParams({ limit: "30", offset: String(data.next_offset) })}`);
          appendScoutRows(list, next);
          if (next.has_more) { data.next_offset = next.next_offset; more.disabled = false; }
          else more.remove();
        } catch (error) { toast(error.message, true); more.disabled = false; }
      });
      section.append(more);
    }
    return section;
  }

  async function loadConnectedStaffTools() {
    const wrapper = create("div", "connected-staff-stack");
    const [mic, questions, moments, scouts] = await Promise.allSettled([
      api("/api/world/mic"),
      api("/api/questions/staff?limit=50"),
      api("/api/world/moments/staff?limit=50"),
      api("/api/status/moderation/scouts?limit=30&offset=0"),
    ]);
    const appendResult = (result, render, title) => {
      if (result.status === "fulfilled") wrapper.append(render(result.value));
      else {
        const section = create("section", "admin-queue connected-staff-tools");
        section.append(create("h3", "admin-queue-title", title), create("p", "form-error", result.reason.message)); wrapper.append(section);
      }
    };
    appendResult(mic, staffMicPanel, "The Mic");
    appendResult(questions, staffQuestionPanel, "Questions");
    appendResult(moments, staffMomentPanel, "Moments");
    wrapper.append(staffCapsulePanel());
    appendResult(scouts, staffScoutPanel, "Scout authority");
    return wrapper;
  }

  async function loadAdminSecurityPanel() {
    const [dashboard, auditState, events] = await Promise.all([
      api("/api/security/admin/dashboard"),
      api("/api/security/admin/audit-integrity"),
      api("/api/security/admin/events?limit=50"),
    ]);
    const section = create("section", "admin-security");
    section.append(create("h3", "", "Security"));
    const integrity = create("div", `admin-integrity ${auditState.valid ? "is-valid" : "is-invalid"}`);
    integrity.append(
      create("strong", "", auditState.valid ? "Audit chain valid" : "Audit chain failure"),
      create("span", "", `${formatCount(auditState.events_checked)} events checked${auditState.failure_at ? ` · failure at ${auditState.failure_at}` : ""}`),
    );
    section.append(integrity);
    const counters = create("dl", "admin-overview-grid");
    const securityCount = (dashboard.security_events || []).reduce((sum, item) => sum + Number(item.count || 0), 0);
    const reportCount = (dashboard.report_queue || []).reduce((sum, item) => sum + Number(item.count || 0), 0);
    [["Security events · 24h", securityCount], ["Reports · 24h", reportCount], ["Abuse decisions · 24h", Object.values(dashboard.abuse_decisions || {}).reduce((sum, value) => sum + Number(value || 0), 0)], ["Media decisions · 24h", Object.values(dashboard.media_decisions || {}).reduce((sum, value) => sum + Number(value || 0), 0)]].forEach(([label, value]) => {
      const row = create("div"); row.append(create("dt", "", label), create("dd", "", String(value))); counters.append(row);
    });
    section.append(counters);
    const recent = create("details", "admin-history"); recent.append(create("summary", "", `Recent security events · ${events.items.length}`));
    const list = create("div", "admin-history-list");
    for (const item of events.items.slice(0, 50)) {
      const row = create("article", "admin-history-row");
      row.append(create("strong", "", item.event_type.replaceAll("_", " ")), create("p", "", `${item.severity} · ${item.outcome}`), create("time", "", new Date(item.created_at).toLocaleString()));
      list.append(row);
    }
    if (!events.items.length) list.append(create("p", "metric-note", "No security events in this view."));
    recent.append(list); section.append(recent); return section;
  }

  function openTrustDialog(item) {
    const workspace = state.adminWorkspace;
    const recentAction = (action) => runAdminAction(action, workspace);
    const dialog = buildUtilityDialog(`Trust · @${item.user.handle}`);
    registerStaffDialog(dialog);
    const form = create("form", "utility-form");
    const trustState = create("select");
    [["new", "New"], ["limited", "Limited"], ["normal", "Normal"], ["trusted", "Trusted"], ["restricted", "Restricted"]].forEach(([value, label]) => {
      const option = create("option", "", label); option.value = value; trustState.append(option);
    });
    const reason = create("textarea"); reason.required = true; reason.minLength = 10; reason.maxLength = 500; reason.rows = 3;
    const risk = create("input"); risk.type = "number"; risk.min = "-1000"; risk.max = "1000"; risk.value = "0";
    const error = create("div", "form-error"); error.hidden = true; error.setAttribute("role", "alert");
    const submit = create("button", "danger-button", "Set trust state"); submit.type = "submit";
    form.append(
      create("p", "utility-copy", "This explicitly sets the account trust state. Restricted revokes active sessions."),
      dialogField("New state", trustState), dialogField("Required reason", reason), dialogField("Risk adjustment", risk), error, submit,
    );
    form.addEventListener("submit", async (event) => {
      event.preventDefault(); hideError(error); submit.disabled = true;
      try {
        const result = await recentAction(() => api(`/api/security/admin/users/${encodeURIComponent(item.user.id)}/trust`, {
          method: "PUT", csrf: true,
          json: { state: trustState.value, reason: reason.value.trim(), risk_delta: Number(risk.value || 0) },
        }));
        dialog.close(); toast(`Trust state set to ${result.state}`);
      } catch (requestError) {
        if (requestError.name !== "AbortError") showError(error, requestError.message);
        submit.disabled = false;
      }
    });
    dialog.append(form); openDialog(dialog); requestAnimationFrame(() => trustState.focus());
  }

  function appealReview(appeal) {
    const workspace = state.adminWorkspace;
    const form = create("form", "report-review appeal-review");
    form.append(
      create("h3", "", `Appeal · ${appeal.moderation_action_id}`),
      create("p", "", appeal.statement),
    );
    form.append(create("p", "metric-note", `${utilityLabel(appeal.status)} · ${new Date(appeal.created_at).toLocaleString()}`));
    if (typeof appeal.safety_case_id === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(appeal.safety_case_id)) {
      const openCase = create("button", "secondary-button", "Open safety case"); openCase.type = "button";
      openCase.addEventListener("click", async () => {
        if (!adminWorkspaceCurrent(workspace)) return;
        workspace.panels.get("safety-reports").filters.reportId = appeal.safety_case_id;
        await selectAdminSection("safety-reports", true);
      });
      form.append(openCase);
    }
    if (appeal.action) {
      const original = create("section", "report-evidence");
      original.append(
        create("strong", "", `Original decision · ${utilityLabel(appeal.action.action)}`),
        create("p", "", appeal.action.reason),
        create("time", "metric-note", new Date(appeal.action.starts_at).toLocaleString()),
      );
      if (appeal.action.ends_at) original.append(create("p", "metric-note", `Ends ${new Date(appeal.action.ends_at).toLocaleString()}`));
      if (appeal.action.reverted_at) original.append(create("p", "metric-note", `Overturned ${new Date(appeal.action.reverted_at).toLocaleString()}`));
      form.append(original);
    } else form.append(create("p", "metric-note", "The original decision is not available here. Check Decision history before reviewing."));
    if (appeal.status !== "pending") {
      form.append(create("p", "", appeal.resolution || "No resolution text recorded."));
      return form;
    }
    if (appeal.action?.moderator_id === workspace?.viewerId) {
      form.append(create("p", "admin-section-empty", "Another moderator must review this appeal because you made the original decision."));
      return form;
    }
    if (appeal.action === null) {
      form.append(create("p", "admin-section-error", "The enforcement record is incomplete. This appeal cannot be decided until it is restored."));
      return form;
    }
    const select = create("select");
    select.setAttribute("aria-label", `Appeal decision for ${appeal.moderation_action_id}`);
    ["upheld", "overturned"].forEach((value) => {
      const option = create("option", "", value); option.value = value; select.append(option);
    });
    const resolution = create("textarea");
    resolution.required = true; resolution.minLength = 3; resolution.maxLength = 500;
    resolution.placeholder = "Required appeal resolution";
    resolution.setAttribute("aria-label", "Required appeal resolution");
    const submit = create("button", "danger-button", "Resolve appeal"); submit.type = "submit";
    form.append(select, resolution, submit);
    let busy = false;
    form.addEventListener("submit", async (event) => {
      event.preventDefault(); if (busy || !form.reportValidity()) return;
      busy = true;
      const controls = [select, resolution, submit]; controls.forEach((control) => { control.disabled = true; });
      const payload = { status: select.value, resolution: resolution.value };
      try {
        const result = await runAdminAction(() => api(`/api/moderation/appeals/${encodeURIComponent(appeal.id)}/decision`, {
          method: "POST", csrf: true,
          json: payload,
        }), workspace);
        if (result?.id !== appeal.id || result.action_id !== appeal.moderation_action_id
          || result.status !== payload.status || typeof result.resolution !== "string" || typeof result.replayed !== "boolean") {
          throw new Error("The server did not confirm this appeal decision. Keep this form open and retry to check the same decision.");
        }
        clear(form);
        form.append(create("strong", "", `Appeal ${result.status}`), create("p", "", result.resolution));
        toast(result.replayed ? "Previous appeal decision confirmed" : "Appeal decision recorded");
      } catch (error) { if (error.name !== "AbortError") toast(error.message, true); }
      finally {
        busy = false;
        controls.forEach((control) => { control.disabled = false; });
      }
    });
    return form;
  }

  function adminOverviewPanel(data) {
    const section = create("section", "admin-overview");
    section.append(create("h3", "", `System and abuse overview · ${data.health.status}`));
    const checks = create("dl", "admin-overview-grid");
    for (const [name, value] of Object.entries(data.health.checks)) {
      const row = create("div");
      row.append(create("dt", "", name.replaceAll("_", " ")), create("dd", `is-${value}`, value));
      checks.append(row);
    }
    const abuse = data.abuse;
    const metrics = create("dl", "admin-overview-grid");
    [
      ["Pending reports", abuse.pending_reports.total],
      ["Urgent pending", abuse.urgent_pending ?? 0],
      ["Server errors · 24h", abuse.errors_24h ?? 0],
      ["Active alerts", (abuse.active_alerts || []).length
        ? abuse.active_alerts.map((alert) => alert.condition.replaceAll("_", " ")).join(", ")
        : "none"],
      ["Last verified backup", markAge(abuse.operational_marks?.backup_verified)],
      ["Last restore drill", markAge(abuse.operational_marks?.restore_drill)],
      ["Oldest pending", (() => {
        const ages = Object.values(abuse.queue_age_seconds || {}).filter((value) => typeof value === "number");
        if (!ages.length) return "none";
        const seconds = Math.max(...ages);
        return seconds < 3600 ? `${Math.max(1, Math.round(seconds / 60))} min` : `${(seconds / 3600).toFixed(1)} h`;
      })()],
      ["Reports · 24h", abuse.reports_received_24h.total],
      ["Unique reporters · 24h", abuse.unique_reporters_24h],
      ["Pending appeals", abuse.pending_appeals],
      ["Warnings · 24h", abuse.warnings_24h],
      ["Active suspensions", abuse.active_suspensions],
      ["Banned accounts", abuse.banned_accounts],
      ["Active sessions", abuse.active_sessions],
    ].forEach(([label, value]) => {
      const row = create("div"); row.append(create("dt", "", label), create("dd", "", String(value))); metrics.append(row);
    });
    section.append(checks, metrics);
    if (abuse.repeat_offenders.length) {
      const repeat = create("details", "admin-repeat");
      repeat.append(create("summary", "", `Repeat enforcement · ${abuse.repeat_offenders.length}`));
      for (const item of abuse.repeat_offenders) {
        repeat.append(create("p", "", `@${item.user.handle} · ${item.action_count} actions · ${item.status}`));
      }
      section.append(repeat);
    }
    return section;
  }

  function adminCreatorBadgeControl(item, workspace, onChange, isCurrent = () => true) {
    const control = create("div", "admin-creator-badge");
    const note = create("p", "metric-note");
    note.setAttribute("role", "status");
    if (typeof item.creator_badge_enabled !== "boolean" || typeof item.creator_badge_mutable !== "boolean") {
      note.textContent = "Top Creator approval is unavailable until this server is updated.";
      control.append(note);
      return control;
    }
    if (!item.creator_badge_mutable) {
      note.textContent = "This account’s identity badge is protected and cannot be changed here.";
      control.append(note);
      return control;
    }
    const reason = create("input");
    reason.type = "text"; reason.maxLength = 500;
    reason.placeholder = "Top Creator approval reason (10–500 characters)";
    reason.setAttribute("aria-label", `Top Creator approval reason for ${item.user.display_name}`);
    const button = create("button", "secondary-button creator-badge-action");
    button.type = "button";
    let busy = false;
    const render = () => {
      button.textContent = item.creator_badge_enabled ? "Remove Top Creator approval" : "Approve Top Creator";
      button.disabled = busy || !item.creator_badge_mutable;
      reason.disabled = button.disabled;
      note.textContent = item.creator_badge_enabled
        ? (accountBadge(item.user)?.kind === "staff" ? "Top Creator approved. The staff badge takes precedence." : "Top Creator approved.")
        : "Top Creator approval is separate from email verification and staff roles.";
    };
    button.addEventListener("click", async () => {
      if (busy || !isCurrent() || !adminWorkspaceCurrent(workspace) || !item.creator_badge_mutable) return;
      const explanation = reason.value.trim();
      if (explanation.length < 10 || explanation.length > 500) {
        note.textContent = "Enter a reason of 10–500 characters before changing Top Creator approval.";
        reason.focus();
        return;
      }
      const enabled = !item.creator_badge_enabled;
      const userId = item.user.id;
      busy = true; render(); note.textContent = "Saving Top Creator approval…";
      try {
        const result = await runAdminAction(() => api(`/api/moderation/admin/users/${encodeURIComponent(userId)}/creator-badge`, {
          method: "PUT", csrf: true, json: { enabled, reason: explanation },
        }), workspace);
        if (!isCurrent() || !adminWorkspaceCurrent(workspace)) return;
        if (typeof result?.changed !== "boolean" || result.user?.id !== userId
          || typeof result.user.display_name !== "string" || result.creator_badge_enabled !== enabled
          || typeof result.creator_badge_mutable !== "boolean"
          || !(result.user.identity_badge === null || accountBadge(result.user))
          || (result.changed && (!Number.isSafeInteger(result.audit_event_id) || result.audit_event_id <= 0))) {
          throw new Error("The server did not confirm this badge change. Refresh before retrying.");
        }
        item.user = result.user;
        item.creator_badge_enabled = result.creator_badge_enabled;
        item.creator_badge_mutable = result.creator_badge_mutable;
        reason.value = "";
        onChange(item.user);
        busy = false; render();
        note.textContent += " Other views update when refreshed.";
        toast(result.changed ? "Top Creator approval updated" : "Top Creator approval was already set");
      } catch (error) {
        if (!isCurrent() || !adminWorkspaceCurrent(workspace) || error.name === "AbortError") return;
        note.textContent = error.message;
      } finally {
        busy = false;
        if (isCurrent() && adminWorkspaceCurrent(workspace)) {
          button.disabled = !item.creator_badge_mutable;
          reason.disabled = button.disabled;
        }
      }
    });
    render(); control.append(reason, button, note);
    return control;
  }

  function adminSanctionControl(item, recentAction, identity, caseOptions = null) {
    // Warn, suspend, ban or restore on sight. Report decisions stay the usual path; this exists
    // because a moderator who sees abuse directly should not have to report the member first.
    const form = create("form", "admin-sanction-row");
    const action = create("select"); action.setAttribute("aria-label", `Sanction for ${item.user.display_name}`);
    [["warn", "Warn"], ["suspend", "Suspend"], ["ban", "Ban"], ["restore", "Restore access"]].forEach(([value, label]) => {
      const option = create("option", "", label); option.value = value; action.append(option);
    });
    const duration = create("select"); duration.setAttribute("aria-label", "Suspension length");
    [["24", "1 day"], ["72", "3 days"], ["168", "1 week"], ["720", "30 days"]].forEach(([value, label]) => {
      const option = create("option", "", label); option.value = value; duration.append(option);
    });
    duration.hidden = true;
    action.addEventListener("change", () => { duration.hidden = action.value !== "suspend"; });
    const reason = create("input"); reason.required = true; reason.minLength = 3; reason.maxLength = 500; reason.placeholder = "Reason the member will see";
    reason.setAttribute("aria-label", "Reason for account action");
    const submit = create("button", "secondary-button", "Apply"); submit.type = "submit";
    form.append(action, duration, reason, submit);
    let busy = false, submission = null;
    form.addEventListener("submit", async (event) => {
      event.preventDefault(); if (busy || !form.reportValidity()) return;
      const chosen = action.value;
      const label = {warn: "Warn", suspend: "Suspend", ban: "Ban", restore: "Restore access for"}[chosen];
      if (!(await confirm(`${label} ${item.user.display_name}?`))) return;
      const fields = { action: chosen, reason: reason.value, ...(chosen === "suspend" ? { duration_hours: Number(duration.value) } : {}) };
      if (caseOptions) Object.assign(fields, {report_id: caseOptions.reportId, expected_report_updated_at: caseOptions.expectedReportUpdatedAt});
      const key = JSON.stringify(fields);
      if (!submission || submission.key !== key) submission = {key, payload: {...fields, request_token: crypto.randomUUID()}};
      const payload = submission.payload;
      busy = true;
      const controls = [action, duration, reason, submit]; controls.forEach(control => { control.disabled = true; });
      try {
        const result = await recentAction(() => api(`/api/moderation/admin/users/${encodeURIComponent(item.user.id)}/sanction`, {
          method: "POST", csrf: true,
          json: payload,
        }));
        if (!["active", "suspended", "banned"].includes(result?.status) || typeof result?.action?.id !== "string"
          || result.action.action !== chosen || result.action.target_user_id !== item.user.id
          || result.user?.id !== item.user.id || result.request_token !== payload.request_token
          || typeof result.replayed !== "boolean" || (caseOptions && result.report_id !== caseOptions.reportId)) throw new Error("The server did not confirm this action. Keep this form open and retry to check the same decision.");
        item.status = result.status;
        const small = identity.querySelector("small");
        if (small) small.textContent = `@${item.user.handle} · ${item.status}`;
        reason.value = "";
        submission = null;
        if (caseOptions) caseOptions.onSaved(result);
        toast(`${result.replayed ? "Previous decision confirmed" : chosen === "restore" ? "Access restored" : "Sanction recorded"} · ${result.action.id}`);
      } catch (error) { if (error.name !== "AbortError") toast(error.message, true); }
      finally { busy = false; controls.forEach(control => { control.disabled = false; }); }
    });
    return form;
  }

  function adminUsersPanel(initial) {
    const workspace = state.adminWorkspace;
    const recentAction = (action) => runAdminAction(action, workspace);
    const section = create("section", "admin-users");
    section.append(create("h3", "", "Staff roles and Top Creator approval"));
    const directory = {
      items: initial.items || [], nextCursor: initial.next_cursor || null,
      requestId: 0, timer: null, loading: false, canAppend: true, filterKey: "[\"\",\"\",\"\"]",
    };
    const filters = create("div", "admin-user-filters");
    const search = create("input"); search.type = "search"; search.placeholder = "Search name, handle, or email"; search.setAttribute("aria-label", "Search role-management accounts");
    const roleFilter = create("select"); roleFilter.setAttribute("aria-label", "Filter accounts by role");
    [["", "All roles"], ...Object.entries(roleLabels)].forEach(([value, label]) => {
      const option = create("option", "", label); option.value = value; roleFilter.append(option);
    });
    const statusFilter = create("select"); statusFilter.setAttribute("aria-label", "Filter accounts by status");
    [["", "All statuses"], ["active", "Active"], ["suspended", "Suspended"], ["banned", "Banned"], ["deleted", "Deleted"]].forEach(([value, label]) => {
      const option = create("option", "", label); option.value = value; statusFilter.append(option);
    });
    filters.append(search, roleFilter, statusFilter);
    const list = create("div", "admin-user-list");
    const statusNote = create("p", "metric-note");
    const more = create("button", "secondary-button", "Load more accounts"); more.type = "button";
    const retry = create("button", "secondary-button", "Retry account search"); retry.type = "button"; retry.hidden = true;
    let retryAppend = false;
    let renderVersion = 0;
    const render = () => {
      const version = ++renderVersion;
      clear(list);
      for (const item of directory.items) {
        const entry = create("div", "admin-user-entry");
        const row = create("form", "admin-user-row");
        const identity = create("span", "admin-user-identity");
        const identityName = accountNameNode(item.user);
        identity.append(identityName, create("small", "", `@${item.user.handle} · ${item.status}`), create("code", "admin-user-id", item.user.id));
        const role = create("select"); role.setAttribute("aria-label", `Role for ${item.user.display_name}`);
        const roleOptions = item.role === "admin" ? ["admin"] : assignableStaffRoles;
        roleOptions.forEach((value) => {
          const option = create("option", "", roleLabels[value]); option.value = value; option.selected = item.role === value; role.append(option);
        });
        const reason = create("input"); reason.required = true; reason.minLength = 10; reason.maxLength = 500; reason.placeholder = "Required reason";
        const submit = create("button", "secondary-button", "Update role"); submit.type = "submit";
        const trust = create("button", "text-button", "Trust"); trust.type = "button";
        const protectedAccount = item.role === "admin" || item.user.id === state.viewer.id;
        role.disabled = protectedAccount; reason.disabled = protectedAccount; submit.disabled = protectedAccount;
        trust.hidden = item.user.id === state.viewer.id;
        trust.addEventListener("click", () => openTrustDialog(item));
        row.append(identity, create("span", "admin-user-signals", `${item.moderation_action_count} actions · ${item.active_session_count} sessions`), role, reason, submit, trust);
        const badgeControl = adminCreatorBadgeControl(item, workspace, (user) => renderAccountName(identityName, user), () => version === renderVersion && directory.items.includes(item));
        row.addEventListener("submit", async (event) => {
          event.preventDefault(); if (!row.reportValidity()) return; submit.disabled = true;
          const requestedRole = role.value;
          const payload = { role: requestedRole, reason: reason.value };
          role.disabled = true; reason.disabled = true;
          try {
            const result = await recentAction(() => api(`/api/moderation/admin/users/${item.user.id}/role`, {
              method: "PUT", csrf: true, json: payload,
            }));
            if (typeof result?.changed !== "boolean" || result.role !== requestedRole || result.user?.id !== item.user.id
              || (result.changed && (typeof result.action_id !== "string" || !result.action_id.trim()))) {
              throw new Error("The server did not confirm this role change. Refresh before retrying.");
            }
            item.role = result.role;
            item.user = result.user;
            renderAccountName(identityName, item.user);
            reason.value = "";
            toast(result.changed ? `Role changed · ${result.action_id}` : "Role was already set");
          } catch (error) { if (error.name !== "AbortError") toast(error.message, true); }
          finally { role.disabled = protectedAccount; reason.disabled = protectedAccount; submit.disabled = protectedAccount; }
        });
        entry.append(row, badgeControl);
        if (item.user.id !== state.viewer.id && item.role !== "admin") entry.append(adminSanctionControl(item, recentAction, identity));
        list.append(entry);
      }
      statusNote.textContent = directory.items.length
        ? `${directory.items.length} ${directory.items.length === 1 ? "account" : "accounts"} loaded`
        : "No accounts match these filters.";
      more.hidden = !directory.canAppend || !directory.nextCursor;
      more.disabled = directory.loading;
    };
    const load = async (append = false) => {
      if (!adminWorkspaceCurrent(workspace)) return;
      const filterKey = JSON.stringify([search.value.trim(), roleFilter.value, statusFilter.value]);
      if (append && (directory.loading || !directory.canAppend || !directory.nextCursor || directory.filterKey !== filterKey)) return;
      const requestId = ++directory.requestId;
      directory.loading = true; more.disabled = true; retry.hidden = true;
      if (!append) { directory.canAppend = false; directory.nextCursor = null; more.hidden = true; }
      statusNote.textContent = "Loading accounts… Previous results remain until the search completes.";
      const params = new URLSearchParams({ limit: "50" });
      if (search.value.trim()) params.set("q", search.value.trim());
      if (roleFilter.value) params.set("role", roleFilter.value);
      if (statusFilter.value) params.set("status", statusFilter.value);
      if (append && directory.nextCursor) params.set("cursor", directory.nextCursor);
      try {
        const result = await api(`/api/moderation/admin/users?${params}`);
        if (requestId !== directory.requestId || !adminWorkspaceCurrent(workspace)) return;
        if (!Array.isArray(result?.items) || result.items.some((item) => typeof item?.user?.id !== "string")
          || (result.next_cursor != null && typeof result.next_cursor !== "string")) throw new Error("The server returned an invalid account list.");
        if (append) {
          const known = new Set(directory.items.map((item) => item.user.id));
          directory.items.push(...result.items.filter((item) => !known.has(item.user.id)));
        } else directory.items = result.items;
        directory.nextCursor = result.next_cursor || null;
        directory.filterKey = filterKey; directory.canAppend = true; directory.loading = false;
        render();
      } catch (error) {
        if (requestId !== directory.requestId || !adminWorkspaceCurrent(workspace)) return;
        if (handleAdminReadAccessLoss(error, workspace)) return;
        directory.loading = false; more.disabled = false;
        statusNote.textContent = "Account search failed. Previous results may not match the selected filters.";
        retryAppend = append; retry.hidden = false;
        toast(error.message, true);
      }
    };
    const reset = () => load(false);
    search.addEventListener("input", () => {
      directory.requestId += 1;
      directory.canAppend = false; directory.nextCursor = null; more.hidden = true; retry.hidden = true;
      more.disabled = true;
      clearTimeout(directory.timer);
      directory.timer = setTimeout(reset, 250);
    });
    roleFilter.addEventListener("change", reset);
    statusFilter.addEventListener("change", reset);
    more.addEventListener("click", () => load(true));
    retry.addEventListener("click", () => load(retryAppend));
    section.append(filters, list, statusNote, more, retry);
    render();
    return section;
  }

  function roleHistoryPanel(items) {
    const details = create("details", "admin-history");
    details.append(create("summary", "", `Role-change audit history · ${items.length}`));
    const list = create("div", "admin-history-list");
    for (const item of items) {
      const row = create("article", "admin-history-row");
      row.append(
        create("strong", "", `${item.previous_role} → ${item.new_role}`),
        create("code", "", item.action_id),
        create("p", "", item.reason),
        create("time", "", new Date(item.created_at).toLocaleString()),
      );
      list.append(row);
    }
    if (!items.length) list.append(create("p", "metric-note", "No role changes recorded."));
    details.append(list);
    return details;
  }

  function moderationHistoryRow(action) {
    const row = create("article", "admin-history-row report-review");
    row.append(
      create("strong", "", utilityLabel(action.action)),
      create("code", "", action.id),
      create("p", "", action.reason),
      create("time", "", new Date(action.starts_at).toLocaleString()),
    );
    if (action.reverted_at) row.append(create("p", "metric-note", `Overturned ${new Date(action.reverted_at).toLocaleString()}`));
    if (action.internal_note) {
      const note = create("div", "staff-note");
      note.append(create("strong", "", "Staff-only internal note"), create("p", "", action.internal_note));
      row.append(note);
    }
    return row;
  }

  function moderationOutcome(form, result) {
    if (typeof result?.action_id !== "string" || !result.action_id.trim()) {
      throw new Error("The server did not confirm an action ID. Refresh before retrying this decision.");
    }
    clear(form);
    form.classList.add("moderation-outcome");
    form.append(
      create("strong", "", "Decision recorded"),
      create("span", "", "Stable action ID"),
      create("code", "", result.action_id),
    );
  }

  function analyticsPanel(data) {
    const section = create("section", "analytics-panel");
    section.append(create("h3", "", "Core experiment: measured events"));
    const grid = create("dl", "analytics-grid");
    const metrics = [
      ["Concurrent users", `${data.concurrent_users}${data.concurrent_users_is_approximate ? " approx." : ""}`],
      ["Active users · 24h", data.active_users_24h], ["Active users · 7d", data.active_users_7d],
      ["New users · 24h", data.new_users_24h], ["New users · 7d", data.new_users_7d],
      ["Returning identities", data.returning_identities], ["Return rate", data.return_rate],
      ["Viewer / poster", data.viewers_to_posters_ratio], ["Posts / session", data.posts_per_session_mean],
      ["Posts / registered user", data.posts_per_registered_user],
      ["Chat / session", data.chat_messages_per_session_mean], ["Median active time", `${Math.round((data.median_active_duration_ms || 0) / 1000)}s`],
      ["Median Drops viewed", data.median_drops_viewed_per_session], ["Viewed before posting", data.median_drops_viewed_before_posting],
      ["Viewed after posting", data.median_drops_viewed_after_posting], ["Chat read after posting", data.median_chat_messages_read_after_posting],
      ["Home feed impressions", data.floor_impressions], ["Board impressions", data.board_impressions],
      ["Home feed engagement rate", data.floor_engagement_rate], ["Board engagement rate", data.board_engagement_rate],
      ["Home feed applause rate", data.applause_rate_per_floor_impression], ["Home feed save rate", data.save_rate_per_floor_impression],
      ["Home feed nomination rate", data.nomination_rate_per_floor_impression], ["Home feed outbound rate", data.outbound_click_rate_per_floor_impression],
      ["Home feed report rate", data.report_rate_per_floor_impression], ["Trending views", data.trending_impressions],
      ["Trending opens", data.trending_opens], ["Trending positions", data.trending_positions],
      ["Trending open rate", data.trending_open_rate],
      ["Media completion", `${data.median_media_completion_percent}%`],
    ];
    metrics.forEach(([label, value]) => { const row = create("div"); row.append(create("dt", "", label), create("dd", "", String(value))); grid.append(row); });
    section.append(grid);
    const exposure = data.creator_board_exposure || [];
    if (exposure.length) {
      section.append(
        create("h4", "analytics-subtitle", "Creator Board exposure"),
        create(
          "p",
          "metric-note",
          exposure.slice(0, 10).map((item) => `@${item.handle}: ${item.slots}`).join(" · "),
        ),
      );
    }
    section.append(create("p", "metric-note", data.scope));
    return section;
  }

  function moderationDecisionFields() {
    const reason = create("textarea");
    reason.required = true;
    reason.minLength = 3;
    reason.maxLength = 500;
    reason.placeholder = "Required user-facing decision reason";
    const reasonLabel = create("label", "field");
    reasonLabel.append(create("span", "", "User-facing decision reason"), reason);

    const internalNote = create("textarea");
    internalNote.maxLength = 2000;
    internalNote.rows = 3;
    internalNote.placeholder = "Optional context for authorized staff only";
    const internalNoteLabel = create("label", "field staff-note-field");
    internalNoteLabel.append(
      create("span", "", "Staff-only internal note · optional"),
      internalNote,
      create("small", "", "Never included in account actions, appeals, public events, or account exports."),
    );
    return { reason, reasonLabel, internalNote, internalNoteLabel };
  }

  function reportReviewMetadata(form, report) {
    const reporter = report.reporter?.handle ? ` · reported by @${report.reporter.handle}` : "";
    form.append(create("p", "metric-note", `${utilityLabel(report.status)} · ${new Date(report.created_at).toLocaleString()}${reporter}`));
    const reference = create("details", "admin-report-reference");
    reference.append(create("summary", "", "Report reference"), create("code", "", report.id));
    form.append(reference);
    if (!["resolved", "dismissed"].includes(report.status)) return false;
    form.append(create("p", "", report.resolution || "No decision reason recorded."));
    if (report.internal_note) form.append(create("p", "staff-private-note", `Staff-only note: ${report.internal_note}`));
    return true;
  }

  function moderationDurationControl(select, duration) {
    const sync = () => {
      duration.hidden = select.value !== "suspend";
      duration.disabled = select.value !== "suspend";
      duration.required = select.value === "suspend";
    };
    select.addEventListener("change", sync); sync();
  }

  function reportReview(report) {
    const workspace = state.adminWorkspace;
    const form = create("form", "report-review");
    form.append(create("h3", "", report.drop?.title || "Unavailable Drop"), create("p", "", `${report.category} · ${report.details || "No additional details"}`));
    if (report.drop?.author) form.append(create("p", "metric-note", `Creator · @${report.drop.author.handle}`));
    form.append(create("blockquote", "report-evidence", report.drop?.body || "No original text available."));
    if (report.drop?.id) {
      const link = create("a", "secondary-button", "Open Drop in a new tab");
      link.href = `/d/${encodeURIComponent(report.drop.id)}`; link.target = "_blank"; link.rel = "noopener noreferrer";
      form.append(link);
    }
    if (report.drop?.english_translation) {
      const derivative = create("section", "moderation-translation");
      derivative.append(create("strong", "", "Automated English translation"));
      if (report.drop.english_translation.title) derivative.append(create("h4", "", report.drop.english_translation.title));
      if (report.drop.english_translation.body) derivative.append(create("p", "", report.drop.english_translation.body));
      derivative.append(create("small", "", "Use the preserved original above as the authoritative evidence."));
      form.append(derivative);
    }
    if (reportReviewMetadata(form, report)) return form;
    const select = create("select");
    select.setAttribute("aria-label", `Decision for ${report.drop?.title || "reported Drop"}`);
    ["dismiss", "remove_drop", "restore_drop", "warn", "suspend", "ban"].forEach((value) => { const option = create("option", "", value.replaceAll("_", " ")); option.value = value; select.append(option); });
    const fields = moderationDecisionFields();
    const duration = create("input"); duration.type = "number"; duration.min = "1"; duration.max = String(24 * 365); duration.value = "24"; duration.setAttribute("aria-label", "Suspension duration in hours");
    moderationDurationControl(select, duration);
    const submit = create("button", "danger-button", "Apply decision"); submit.type = "submit";
    form.append(select, fields.reasonLabel, fields.internalNoteLabel, duration, submit);
    form.addEventListener("submit", async (event) => {
      event.preventDefault(); if (!form.reportValidity()) return;
      const controls = [select, fields.reason, fields.internalNote, duration, submit]; controls.forEach((control) => { control.disabled = true; });
      const payload = {
            action: select.value,
            reason: fields.reason.value,
            internal_note: fields.internalNote.value.trim() || null,
            duration_hours: select.value === "suspend" ? Number(duration.value) : null,
          };
      try {
        const result = await runAdminAction(() => api(`/api/moderation/reports/${encodeURIComponent(report.id)}/decision`, {
          method: "POST", csrf: true,
          json: payload,
        }), workspace);
        moderationOutcome(form, result); toast("Moderation decision recorded");
      } catch (error) { if (error.name !== "AbortError") toast(error.message, true); }
      finally {
        controls.forEach((control) => { control.disabled = false; });
        duration.disabled = select.value !== "suspend";
      }
    });
    return form;
  }

  function communicationReportReview(report, kind) {
    const workspace = state.adminWorkspace;
    const form = create("form", "report-review communication-report");
    const message = report.message;
    const author = message?.author || message?.sender;
    const label = kind === "chat" ? "Public chat message" : "Private message";
    form.append(
      create("h3", "", `${label}${author ? ` · @${author.handle}` : ""}`),
      create("p", "", `${report.category} · ${report.details || "No additional reporter context"}`),
      create("blockquote", "report-evidence", message?.body || "Message evidence unavailable"),
    );
    if (message?.english_translation?.body) {
      const derivative = create("section", "moderation-translation");
      derivative.append(
        create("strong", "", "Automated English translation"),
        create("p", "", message.english_translation.body),
        create("small", "", "Use the preserved original above as the authoritative evidence."),
      );
      form.append(derivative);
    }
    if (reportReviewMetadata(form, report)) return form;
    const select = create("select");
    select.setAttribute("aria-label", `Decision for ${label}`);
    ["dismiss", "remove_message", "warn", "suspend", "ban"].forEach((value) => {
      const option = create("option", "", value.replaceAll("_", " "));
      option.value = value;
      select.append(option);
    });
    const fields = moderationDecisionFields();
    const duration = create("input");
    duration.type = "number"; duration.min = "1"; duration.max = String(24 * 365); duration.value = "24";
    duration.setAttribute("aria-label", "Suspension duration in hours");
    moderationDurationControl(select, duration);
    const submit = create("button", "danger-button", "Apply decision"); submit.type = "submit";
    form.append(select, fields.reasonLabel, fields.internalNoteLabel, duration, submit);
    form.addEventListener("submit", async (event) => {
      event.preventDefault(); if (!form.reportValidity()) return;
      const controls = [select, fields.reason, fields.internalNote, duration, submit]; controls.forEach((control) => { control.disabled = true; });
      const payload = {
            action: select.value,
            reason: fields.reason.value,
            internal_note: fields.internalNote.value.trim() || null,
            duration_hours: select.value === "suspend" ? Number(duration.value) : null,
          };
      try {
        const endpoint = kind === "chat" ? "chat-reports" : "dm-reports";
        const result = await runAdminAction(() => api(`/api/moderation/${endpoint}/${encodeURIComponent(report.id)}/decision`, {
          method: "POST", csrf: true,
          json: payload,
        }), workspace);
        moderationOutcome(form, result);
        toast("Moderation decision recorded");
      } catch (error) { if (error.name !== "AbortError") toast(error.message, true); }
      finally {
        controls.forEach((control) => { control.disabled = false; });
        duration.disabled = select.value !== "suspend";
      }
    });
    return form;
  }

  function userReportReview(report) {
    const workspace = state.adminWorkspace;
    const form = create("form", "report-review user-report-review");
    const profile = report.profile;
    const identity = profile ? `${profile.display_name} · @${profile.handle}` : "Unavailable profile";
    form.append(
      profile ? accountNameNode(profile, "h3") : create("h3", "", identity),
      create("p", "", `${report.category} · ${report.details || "No additional reporter context"}`),
    );
    if (profile) {
      const link = create("a", "secondary-button", "Open profile in a new tab");
      link.href = `/u/${encodeURIComponent(profile.handle)}`;
      link.target = "_blank"; link.rel = "noopener noreferrer";
      form.append(link);
    }
    if (reportReviewMetadata(form, report)) return form;
    const select = create("select");
    ["dismiss", "warn", "suspend", "ban"].forEach((value) => {
      const option = create("option", "", value); option.value = value; select.append(option);
    });
    select.setAttribute("aria-label", `Decision for ${identity}`);
    const fields = moderationDecisionFields();
    const duration = create("input");
    duration.type = "number"; duration.min = "1"; duration.max = String(24 * 365); duration.value = "24";
    duration.setAttribute("aria-label", "Suspension duration in hours");
    moderationDurationControl(select, duration);
    const submit = create("button", "danger-button", "Apply decision"); submit.type = "submit";
    form.append(select, fields.reasonLabel, fields.internalNoteLabel, duration, submit);
    form.addEventListener("submit", async (event) => {
      event.preventDefault(); if (!form.reportValidity()) return;
      const controls = [select, fields.reason, fields.internalNote, duration, submit]; controls.forEach((control) => { control.disabled = true; });
      const payload = {
            action: select.value,
            reason: fields.reason.value,
            internal_note: fields.internalNote.value.trim() || null,
            duration_hours: select.value === "suspend" ? Number(duration.value) : null,
          };
      try {
        const result = await runAdminAction(() => api(`/api/moderation/user-reports/${encodeURIComponent(report.id)}/decision`, {
          method: "POST", csrf: true,
          json: payload,
        }), workspace);
        moderationOutcome(form, result);
        toast("Profile moderation decision recorded");
      } catch (error) { if (error.name !== "AbortError") toast(error.message, true); }
      finally {
        controls.forEach((control) => { control.disabled = false; });
        duration.disabled = select.value !== "suspend";
      }
    });
    return form;
  }

  function categoryScrollLimit() {
    return Math.max(0, el.categoryStrip.scrollWidth - el.categoryStrip.clientWidth);
  }

  function updateCategoryScrollControls() {
    const limit = categoryScrollLimit();
    const showControls = innerWidth > 820 && !el.categoryRail.hidden && limit > 2;
    el.categoryPrevious.hidden = !showControls || el.categoryStrip.scrollLeft <= 2;
    el.categoryNext.hidden = !showControls || el.categoryStrip.scrollLeft >= limit - 2;
  }

  function scrollCategories(direction) {
    const distance = Math.max(220, Math.round(el.categoryStrip.clientWidth * 0.72));
    el.categoryStrip.scrollBy({ left: distance * direction, behavior: motionBehavior() });
  }

  // Touch gestures the big apps taught everyone: pull down at the top to refresh the current screen,
  // and swipe sideways on Home to move between the feed lenses. Touches that start
  // on a horizontal scroller, media or a form control never count, and nothing happens while a dialog is open.
  function bindTouchGestures() {
    if (!("ontouchstart" in window) && !coarsePointer()) return;
    const indicator = document.createElement("div");
    indicator.className = "pull-indicator";
    indicator.setAttribute("aria-hidden", "true");
    const svgNamespace = "http://www.w3.org/2000/svg";
    const indicatorIcon = document.createElementNS(svgNamespace, "svg");
    const indicatorUse = document.createElementNS(svgNamespace, "use");
    indicatorUse.setAttribute("href", "#icon-refresh");
    indicatorIcon.append(indicatorUse);
    indicator.append(indicatorIcon);
    document.body.append(indicator);
    const refreshers = {
      all: () => Promise.all([refreshRoom(true), refreshFeed()]),
      following: () => refreshFeed(),
      unseen: () => refreshFeed(),
      saved: () => refreshFeed(),
      mine: () => refreshFeed(),
      chat: () => refreshChat(),
      notifications: () => refreshNotifications(false, true),
      discover: () => refreshDiscover(),
      flow: () => refreshFlow(),
    };
    const threshold = 72;
    const noSwipe = ".category-strip, .people-row, .discover-tabs, .feed-scope, .drop-media, .drop-gallery, "
      + ".chat-room-pane, .flow-shell, video, audio, input, textarea, select, [data-no-swipe]";
    let startX = null;
    let startY = null;
    let startView = null;
    let pulling = false;
    let swipeable = false;
    let armed = false;
    let busy = false;
    const reset = () => {
      indicator.classList.remove("is-armed", "is-refreshing", "is-visible");
      indicator.style.removeProperty("--pull");
      pulling = false;
      swipeable = false;
      armed = false;
      startX = null;
      startY = null;
      startView = null;
    };
    const phone = () => matchMedia("(max-width: 820px)").matches;
    addEventListener("touchstart", (event) => {
      if (busy) return;
      if (event.touches.length !== 1 || !phone() || document.querySelector("dialog[open]")) { reset(); return; }
      const target = event.target instanceof Element ? event.target : null;
      // The phone video feed scrolls inside #flow-feed, so "at the top" is its own scroll there:
      // a downward drag back to the previous video must not refresh the feed.
      const scroller = state.view === "chat" ? el.chatMessages : state.view === "flow" ? el.flowFeed : null;
      const atTop = (scroller ? scroller.scrollTop <= 0 : true) && (state.view === "chat" || (window.scrollY || 0) <= 0);
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      startView = state.view;
      pulling = atTop && Boolean(refreshers[state.view]) && !(target && target.closest("video, audio, input, textarea"));
      swipeable = ["all", "following", "unseen"].includes(state.view) && !(target && target.closest(noSwipe));
    }, { passive: true });
    addEventListener("touchmove", (event) => {
      if (startY === null) return;
      if (event.touches.length !== 1 || !phone() || state.view !== startView || document.querySelector("dialog[open]")) { reset(); return; }
      if (!pulling) return;
      const dy = event.touches[0].clientY - startY;
      const dx = Math.abs(event.touches[0].clientX - startX);
      if (dy <= 0 || dx > dy) {
        armed = false;
        indicator.classList.remove("is-armed", "is-visible");
        indicator.style.removeProperty("--pull");
        return;
      }
      const pull = Math.min(dy * 0.5, threshold + 24);
      indicator.style.setProperty("--pull", `${pull}px`);
      indicator.classList.add("is-visible");
      const nowArmed = pull >= threshold;
      if (nowArmed !== armed) {
        armed = nowArmed;
        indicator.classList.toggle("is-armed", armed);
        if (armed) haptic(10);
      }
    }, { passive: true });
    addEventListener("touchend", async (event) => {
      if (startX === null) return;
      if (event.touches.length || !phone() || state.view !== startView || document.querySelector("dialog[open]")) { reset(); return; }
      const touch = event.changedTouches[0];
      const dx = touch ? touch.clientX - startX : 0;
      const dy = touch ? touch.clientY - startY : 0;
      const wasArmed = pulling && armed && dy >= threshold * 2 && Math.abs(dx) <= dy;
      const wasSwipe = swipeable && Math.abs(dx) > 70 && Math.abs(dy) < 40;
      const view = state.view;
      reset();
      if (wasArmed && !busy && refreshers[view]) {
        busy = true;
        indicator.classList.add("is-visible", "is-refreshing");
        haptic(12);
        try {
          await refreshers[view]();
        } catch (_error) {
          /* the refresh path reports its own failures */
        } finally {
          busy = false;
          indicator.classList.remove("is-visible", "is-refreshing");
        }
        return;
      }
      if (wasSwipe && !document.querySelector("dialog[open]") && !$("#feed-scope")?.hidden) {
        // Move one lens along the visible strip; the strip's own order is the swipe order.
        // Hidden by attribute or by a stylesheet (Topics and "Nobody has seen this yet" are CSS-hidden):
        // a lens nobody can see is not a swipe target.
        const lenses = $$('#feed-scope [data-view]').filter((button) => !button.hidden
          && (typeof button.getClientRects !== "function" || button.getClientRects().length > 0));
        const index = lenses.findIndex((button) => button.dataset.view === view);
        const next = index < 0 ? null : lenses[index + (dx < 0 ? 1 : -1)];
        if (next) next.click();
      }
    }, { passive: true });
    addEventListener("touchcancel", reset, { passive: true });
  }

  // The Messages header keeps the title, one more menu and New. The menu carries Archived, Scheduled,
  // Search messages, New group and Notification settings; the Inbox/Archived boxes are hidden, and their
  // buttons are still the way the folder controller switches folders, so the menu item just presses them.
  function bindMessagesMore() {
    const trigger = $("#messages-more"), menu = $("#messages-more-menu");
    if (!trigger || !menu) return;
    const items = () => [...menu.querySelectorAll("button")].filter((button) => !button.hidden && button.offsetParent !== null);
    const setOpen = (open, { focusTrigger = false } = {}) => {
      menu.hidden = !open; trigger.setAttribute("aria-expanded", String(open));
      if (open) items()[0]?.focus({ preventScroll: true });
      else if (focusTrigger) trigger.focus();
    };
    // WebKit can blur a focused item to <main> during a touch-generated mouse press.
    // Keep focus inside until click so focusout cannot remove the tapped button first.
    trigger.addEventListener("mousedown", (event) => { if (event.button === 0) event.preventDefault(); });
    menu.addEventListener("mousedown", (event) => {
      if (event.button === 0 && event.target.closest("button")) event.preventDefault();
    });
    trigger.addEventListener("click", () => setOpen(menu.hidden, { focusTrigger: !menu.hidden }));
    menu.addEventListener("click", (event) => { if (event.target.closest("button")) setOpen(false); });
    menu.addEventListener("keydown", (event) => {
      const list = items(), index = list.indexOf(document.activeElement);
      if (event.key === "ArrowDown" || event.key === "ArrowUp") { event.preventDefault(); list[(index + (event.key === "ArrowDown" ? 1 : -1) + list.length) % list.length]?.focus(); }
      else if (event.key === "Home" || event.key === "End") { event.preventDefault(); list[event.key === "Home" ? 0 : list.length - 1]?.focus(); }
    });
    // Focus leaving the menu (Tab, or a tap elsewhere on a phone) closes it; a click inside stays inside.
    menu.addEventListener("focusout", (event) => { if (!menu.hidden && !(event.relatedTarget && event.relatedTarget.closest(".messages-more"))) setOpen(false); });
    document.addEventListener("click", (event) => { if (!menu.hidden && !event.target.closest(".messages-more")) setOpen(false); });
    document.addEventListener("keydown", (event) => { if (event.key === "Escape" && !menu.hidden) setOpen(false, { focusTrigger: true }); });
    const folder = (name) => $(`[data-conversation-folder="${name}"]`)?.click();
    $("#messages-folder-toggle")?.addEventListener("click", () => folder((state.messages?.folder || "inbox") === "archived" ? "inbox" : "archived"));
    $("#messages-back-to-inbox")?.addEventListener("click", () => folder("inbox"));
  }

  function navigateFromDialog(path, dialog) {
    if (dialog.dataset.historyEntry && history.state?.louderrDialog === dialogName(dialog)) {
      if (history.state.louderrReturn === path) {
        // Choosing the page already under the sheet is simply a dismissal.
        dialog.dataset.userClose = "true";
        dialog.close();
        return;
      }
      // The destination replaces the sheet, so Back reaches Discover once.
      history.replaceState({}, "", path);
    } else if (`${location.pathname}${location.search}${location.hash}` !== path) {
      history.pushState({}, "", path);
    }
    delete dialog.dataset.historyEntry;
    delete dialog.dataset.userClose;
    if (dialog.open) dialog.close();
  }

  function followGettingStartedAction(action) {
    const guide = $("#getting-started-dialog");
    if (action === "create") { guide.close(); openComposer(); return; }
    if (!["discover", "browse", "unseen"].includes(action)) return;
    const discover = action === "discover";
    const view = discover ? "discover" : action === "unseen" ? "unseen" : "all";
    const path = discover ? "/discover?type=people" : view === "unseen" ? "/unseen" : "/";
    if (view === "unseen") { markUndiscoveredVisited(); state.savedFolderId = null; }
    if (discover) {
      state.discovery.mode = "search";
      state.query = "";
      state.search.type = "people";
      state.search.category = "all";
      if (el.discoverCategory) el.discoverCategory.value = "all";
      $$('[data-search-input]').forEach(input => { input.value = ""; });
    }
    let source = guide;
    if (el.account.open) {
      // Help can be stacked over sign-in/reset. Unwind its parent marker now;
      // the queued close event must not put the account route over the new page.
      settleDialogHistory(guide);
      guide.close();
      source = el.account;
    }
    navigateFromDialog(path, source);
    setView(view);
    scrollTo({ top: 0, behavior: "instant" });
  }

  function navigateToView(view, source = null) {
    if (source?.closest?.(".mobile-bottom-nav, .feed-scope")) haptic(6);
    // Personal screens must open the account gate before their route is pushed. Otherwise a
    // guest can land on /notifications or /messages, close the sign-in sheet, and immediately
    // get the same sheet back when the protected route re-renders.
    if (!state.viewer && ["messages", "notifications", "saved", "mine", "following", "topics"].includes(view)) {
      openAccountDialog();
      return;
    }
    if (state.ageAttestationRequired || state.ageRequirementNotMet) { openAccountDialog("profile"); return; }
    const path = view === "all" ? "/" : `/${view}`;
    const current = `${location.pathname}${location.search}${location.hash}`;
    const sheet = source?.closest?.("#discover-browse-dialog[open]");
    const reading = view === "all" ? state.feedController?.signal.aborted === false
      : view === "flow" ? state.flow.view === "feed" && state.flow.loading && state.flow.controller?.signal.aborted === false
      : view === "chat" ? state.chat?.room === "global" && state.chat.loading && !state.chat.loaded
        && state.chat.controller?.signal.aborted === false
      : view === "discover" ? state.discovery.mode === "search" && !state.query
        && state.search.type === "all" && state.search.category === "all" && state.search.controller?.signal.aborted === false
      : false;
    if (!sheet && state.view === view && current === path && reading) {
      // Repeated taps must not abort and restart the page the person is waiting for.
      if (view === "discover" && state.discovery.searchEngaged) {
        state.discovery.searchEngaged = false;
        el.discoverQuery.blur();
        paintDiscoverEngagement();
      }
      scrollTo({ top: 0, behavior: "instant" });
      return;
    }
    if (view === "flow") state.flow.view = "feed";
    if (view === "discover") {
      // A Discover tap is a fresh start: the compact grid. Back keeps a started search.
      state.discovery.searchEngaged = false;
      state.discovery.mode = "search";
      state.query = "";
      state.search.type = "all";
      state.search.category = "all";
      if (el.discoverCategory) el.discoverCategory.value = "all";
      $$('[data-search-input]').forEach((input) => { input.value = ""; });
    }
    if (sheet) navigateFromDialog(path, sheet);
    else if (current !== path) history.pushState({}, "", path);
    if (view === "messages") {
      if (state.messages.people?.open) resetMessageSearch();
      if (state.messages.selectedId) closeMessageThread(false);
    }
    if (view === "saved") state.savedFolderId = null;
    setView(view);
    if (view === "saved" && state.view === "saved" && state.viewer) void refreshSaveFolders();
    // A different page starts at its top immediately; smooth scrolling here exposes
    // the old page's offset and makes the destination appear to slide into place.
    scrollTo({ top: 0, behavior: "instant" });
  }

  function navigateHomeLink(event) {
    // Keep native new-tab/window and already handled link gestures intact.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigateToView("all", event.currentTarget);
  }

  function bindEvents() {
    $("#feed-empty-action")?.addEventListener("click", () => {
      const action = $("#feed-empty-action").dataset.emptyAction;
      if (action === "create") { openComposer(); return; }
      if (action === "join") {
        openAccountDialog(null, "register");
        return;
      }
      if (action === "signin") { openAccountDialog(); return; }
      if (action === "clear-filter") {
        state.filter = "all";
        rememberHomeFilter("all");
        paintHomeFilter();
        refreshFeed();
        requestAnimationFrame(updateCategoryScrollControls);
        return;
      }
      const view = action === "discover" ? "discover" : "all";
      if (view === "all") { state.filter = "all"; rememberHomeFilter("all"); }
      const path = view === "all" ? "/" : "/discover";
      if (location.pathname !== path) history.pushState({}, "", path);
      setView(view);
      scrollTo({ top: 0, behavior: "instant" });
    });
    setEmoteMotion(state.emotes.motionEnabled, false);
    el.chatEmote.addEventListener("click", () => toggleEmotePicker(el.chatBody, el.chatEmote));
    el.messageEmote.addEventListener("click", () => toggleEmotePicker(el.messageBody, el.messageEmote));
    el.emoteMotion.addEventListener("click", () => setEmoteMotion(!state.emotes.motionEnabled));
    $("#emote-close").addEventListener("click", () => closeEmotePicker(true));
    el.emoteSearch.addEventListener("input", () => renderEmotePicker(el.emoteSearch.value));
    el.emoteSearch.addEventListener("keydown", (event) => {
      if (event.isComposing || event.keyCode === 229 || !["ArrowDown", "Enter"].includes(event.key)) return;
      event.preventDefault();
      const first = $(".emote-option", el.emoteGrid);
      if (!first) return;
      if (event.key === "Enter") first.click();
      else first.focus();
    });
    el.emoteGrid.addEventListener("keydown", moveEmoteFocus);
    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || el.emotePicker.hidden) return;
      event.preventDefault();
      const target = state.emotes.target;
      closeEmotePicker();
      target?.focus();
    });
    document.addEventListener("pointerdown", (event) => {
      if (el.emotePicker.hidden || el.emotePicker.contains(event.target) || state.emotes.trigger?.contains(event.target)) return;
      closeEmotePicker();
    });
    $$('[data-close-dialog]').forEach((button) => button.addEventListener("click", () => {
      const dialog = button.closest("dialog");
      dialog.dataset.userClose = "true";
      dialog.close();
    }));
    $$('[data-open-account]').forEach((button) => button.addEventListener("click", () =>
      openAccountDialog(null, button.dataset.openAccount || "login")));
    $$('[data-open-settings]').forEach((button) => button.addEventListener("click", () => openAccountDialog(button.dataset.openSettings || "settings")));
    $$('[data-open-business]').forEach((button) => button.addEventListener("click", () => {
      el.account.close();
      history.pushState({}, "", `/${button.dataset.openBusiness}`);
      setView(button.dataset.openBusiness);
    }));
    $$('[data-open-guide]').forEach((button) => button.addEventListener("click", () => {
      openDialogWithHistory($("#getting-started-dialog"));
      void refreshMemberHome();
    }));
    $$('[data-guide-action]').forEach((button) => button.addEventListener("click", () =>
      followGettingStartedAction(button.dataset.guideAction)));
    $$('[data-open-composer]').forEach((button) => button.addEventListener("click", () => openComposer()));
    $$('[data-room-now]').forEach((button) => button.addEventListener("click", () => openRoomFeature(button.dataset.roomNow)));
    $$('[data-scroll-board]').forEach((button) => button.addEventListener("click", () => {
      if (location.pathname !== "/") history.pushState({}, "", "/");
      setView("all");
      requestAnimationFrame(() => $("#board").scrollIntoView({ behavior: motionBehavior() }));
    }));
    $$("a.brand").forEach((link) => link.addEventListener("click", navigateHomeLink));
    // Body carries the current view for styling; only navigation buttons handle view clicks.
    $$('button[data-view]').forEach((button) => button.addEventListener("click", () => navigateToView(button.dataset.view, button)));
    $$('#feed-order [data-sort]').forEach((button) => button.addEventListener("click", () => {
      haptic(6);
      setFeedOrder(button.dataset.sort);
    }));
    $("#feed-order-toggle")?.addEventListener("click", (event) => {
      const open = document.body.classList.toggle("feed-order-open");
      event.currentTarget.setAttribute("aria-expanded", String(open));
    });
    el.topicFollow?.addEventListener("click", () => { haptic(6); toggleTopicFollow(); });
    // Arrow keys walk the visible lenses, the same order a sideways swipe takes on a phone.
    $$('#feed-scope [data-view]').forEach((button) => button.addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
      const lenses = $$('#feed-scope [data-view]').filter((item) => !item.hidden);
      const index = lenses.indexOf(button);
      if (index < 0) return;
      event.preventDefault();
      const next = lenses[(index + (event.key === "ArrowRight" ? 1 : -1) + lenses.length) % lenses.length];
      next.click();
      next.focus();
    }));
    $$('[data-flow-view]').forEach((button) => {
      button.addEventListener("click", () => {
        if (state.flow.view === button.dataset.flowView && state.flow.loading
            && state.flow.controller?.signal.aborted === false) return;
        stopFlowTimers();
        state.flow.view = button.dataset.flowView;
        const query = new URLSearchParams({ view: state.flow.view });
        if (state.flow.view === "stories") query.set("lens", state.flow.storyLens);
        history.replaceState(history.state, "", `/flow?${query}`);
        renderFlow();
        centerInScroller(button, button.closest(".flow-tabs"));
        refreshFlow();
      });
      button.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        const tabs = $$('[data-flow-view]');
        const index = tabs.indexOf(button);
        const next = event.key === "Home" ? 0
          : event.key === "End" ? tabs.length - 1
            : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
        tabs[next].click();
        tabs[next].focus();
      });
    });
    $$('[data-story-lens]').forEach((button) => {
      button.addEventListener("click", () => {
        if (state.flow.view === "stories" && state.flow.storyLens === button.dataset.storyLens
            && state.flow.loading && state.flow.controller?.signal.aborted === false) return;
        state.flow.storyLens = button.dataset.storyLens;
        history.replaceState(history.state, "", `/flow?view=stories&lens=${encodeURIComponent(state.flow.storyLens)}`);
        renderFlow();
        centerInScroller(button, el.storyLenses);
        refreshFlow();
      });
      button.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        const tabs = $$('[data-story-lens]');
        const index = tabs.indexOf(button);
        const next = event.key === "Home" ? 0
          : event.key === "End" ? tabs.length - 1
            : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
        tabs[next].click();
        tabs[next].focus();
      });
    });
    $$('[data-chat-lens]').forEach((button) => button.addEventListener("click", () => {
      if (button.dataset.chatLens === "mentions" && !state.viewer) {
        openDialog(el.account);
        toast("Sign in to view your mentions");
        return;
      }
      if (state.chat.lens === button.dataset.chatLens) return;
      state.chat.lens = button.dataset.chatLens;
      chatWorkspace().selectionVersion += 1;
      state.chat.items = [];
      state.chat.nextCursor = null;
      state.chat.loaded = false;
      clear(el.chatMessages);
      el.chatEmpty.hidden = true;
      el.chatOlder.hidden = true;
      $$('[data-chat-lens]').forEach((item) => {
        const active = item === button;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-pressed", String(active));
      });
      refreshChat();
    }));
    $$('[data-trending-scope]').forEach((button) => button.addEventListener("click", () => {
      state.trending.scope = button.dataset.trendingScope;
      $$('[data-trending-scope]').forEach((item) => {
        const active = item === button;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-pressed", String(active));
      });
      refreshTrending();
    }));
    $$('[data-trending-period]').forEach((button) => button.addEventListener("click", () => {
      state.trending.period = button.dataset.trendingPeriod;
      $$('[data-trending-period]').forEach((item) => {
        const active = item === button;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-pressed", String(active));
      });
      refreshTrending();
    }));
    el.trendingCategory.addEventListener("change", () => {
      state.trending.category = el.trendingCategory.value;
      refreshTrending();
    });
    el.categoryPrevious.addEventListener("click", () => scrollCategories(-1));
    el.categoryNext.addEventListener("click", () => scrollCategories(1));
    el.categoryStrip.addEventListener("scroll", updateCategoryScrollControls, { passive: true });
    el.categoryStrip.addEventListener("wheel", (event) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const limit = categoryScrollLimit();
      const multiplier = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? 16
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE ? el.categoryStrip.clientWidth : 1;
      const delta = event.deltaY * multiplier;
      const canMove = delta > 0 ? el.categoryStrip.scrollLeft < limit - 1 : el.categoryStrip.scrollLeft > 1;
      if (!canMove) return;
      event.preventDefault();
      el.categoryStrip.scrollLeft = Math.max(0, Math.min(limit, el.categoryStrip.scrollLeft + delta));
    }, { passive: false });
    el.categoryStrip.addEventListener("keydown", (event) => {
      if (event.target !== el.categoryStrip || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      if (event.key === "Home" || event.key === "End") {
        el.categoryStrip.scrollTo({ left: event.key === "Home" ? 0 : categoryScrollLimit(), behavior: motionBehavior() });
      } else scrollCategories(event.key === "ArrowRight" ? 1 : -1);
    });
    if ("ResizeObserver" in window) new ResizeObserver(updateCategoryScrollControls).observe(el.categoryStrip);
    if ("ResizeObserver" in window) {
      const chromeObserver = new ResizeObserver(updateMobileChromeSize);
      $$(".mobile-header, .mobile-bottom-nav").forEach((node) => chromeObserver.observe(node));
    }
    updateMobileChromeSize();
    bindStartupSupportReads();
    addEventListener("resize", updateMobileChromeSize);
    addEventListener("resize", updateCategoryScrollControls);
    addEventListener("resize", () => {
      if (state.view !== "flow" || !["feed", "stories"].includes(state.flow.view)) return;
      cancelAnimationFrame(state.flow.resizeFrame);
      state.flow.resizeFrame = requestAnimationFrame(() => {
        state.flow.resizeFrame = null;
        if (state.flow.view === "stories") observeFlowStories();
        else observeFlowCards();
      });
    });
    requestAnimationFrame(updateCategoryScrollControls);
    $$('[data-filter]').forEach((button) => button.addEventListener("click", () => {
      state.filter = toggleHomeFilter(state.filter, button.dataset.filter);
      rememberHomeFilter(state.filter);
      paintHomeFilter();
      centerInScroller(button, el.categoryStrip);
      refreshFeed();
    }));
    let searchTimer;
    $$('[data-search-input]').forEach((input) => input.addEventListener("input", () => {
      clearTimeout(searchTimer);
      const previousMode = state.discovery.mode;
      state.search.requestId = (state.search.requestId || 0) + 1;
      state.search.controller?.abort();
      state.search.controller = null;
      state.discovery.requestId = (state.discovery.requestId || 0) + 1;
      state.discovery.controller?.abort();
      state.discovery.controller = null;
      input.value = Array.from(input.value).slice(0, 80).join("");
      state.query = input.value.trim();
      state.discovery.mode = "search";
      $$('[data-search-input]').forEach((other) => { if (other !== input) other.value = input.value; });
      if (state.view !== "discover") {
        history.pushState({}, "", "/discover");
        state.discovery.mode = "search";
        setView("discover");
        requestAnimationFrame(() => {
          if (state.view !== "discover" || state.discovery.mode !== "search") return;
          el.discoverQuery.focus();
          el.discoverQuery.setSelectionRange(el.discoverQuery.value.length, el.discoverQuery.value.length);
        });
      } else {
        if (previousMode !== "search") {
          history.replaceState({}, "", "/discover");
          renderDiscover();
        }
        state.discovery.searchEngaged = true;
        el.discoverSearchTypeControl.hidden = false;
        el.discoverCategoryField.hidden = !state.query;
        syncDiscoverControls();
        if (discoverSearchNeedsMoreCharacters()) void refreshDiscover();
        else {
          const search = state.search, discovery = state.discovery;
          const searchRequest = search.requestId, discoveryRequest = discovery.requestId;
          const query = state.query, type = search.type, category = search.category;
          const viewerId = state.viewer?.id || null, social = state.messages, csrf = state.csrf;
          searchTimer = setTimeout(() => {
            if (state.view !== "discover" || state.discovery !== discovery || discovery.mode !== "search"
                || state.search !== search || search.requestId !== searchRequest || discovery.requestId !== discoveryRequest
                || state.query !== query || search.type !== type || search.category !== category
                || (state.viewer?.id || null) !== viewerId || state.messages !== social || state.csrf !== csrf) return;
            return refreshDiscover();
          }, 300);
        }
      }
    }));
    const focusSearch = () => {
      const previousMode = state.discovery.mode;
      state.discovery.mode = "search";
      if (state.view !== "discover") {
        clearTimeout(searchTimer);
        history.pushState({}, "", "/discover");
        setView("discover");
      } else if (previousMode !== "search") {
        clearTimeout(searchTimer);
        renderDiscover();
        void refreshDiscover();
      }
      requestAnimationFrame(() => {
        if (state.view === "discover" && state.discovery.mode === "search") el.discoverQuery.focus();
      });
    };
    $$('[data-focus-search]').forEach((button) => button.addEventListener("click", focusSearch));
    document.addEventListener("keydown", (event) => {
      if (isGlobalSearchShortcut(event)) { event.preventDefault(); focusSearch(); }
    });
    el.discoverQuery.addEventListener("focus", () => {
      if (state.view !== "discover" || state.discovery.mode !== "search" || state.discovery.searchEngaged) return;
      state.discovery.searchEngaged = true;
      paintDiscoverEngagement();
    });
    $("#discover-search-cancel")?.addEventListener("click", cancelDiscoverSearch);
    document.addEventListener("click", (event) => { if (!el.menu.contains(event.target) && !event.target.closest(".more-button")) closeActionMenu(); });
    el.menu.addEventListener("keydown", (event) => {
      const buttons = $$("button:not([hidden])", el.menu);
      const index = buttons.indexOf(document.activeElement);
      if (event.key === "Escape") { event.preventDefault(); closeActionMenu(true); return; }
      if (event.key === "Tab") { closeActionMenu(true); return; }
      if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === "Home" ? 0
        : event.key === "End" ? buttons.length - 1
          : (index + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length;
      buttons[next]?.focus();
    });
    addEventListener("resize", () => closeActionMenu(true));
    $$('[data-search-type]').forEach((button) => button.addEventListener("click", () => {
      state.search.type = button.dataset.searchType;
      state.discovery.searchEngaged = true;
      $$('[data-search-type]').forEach((item) => {
        const active = item === button;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-pressed", String(active));
      });
      refreshDiscover();
    }));
    $$('[data-discover-mode]').forEach((button) => {
      button.addEventListener("click", () => {
        state.search.controller?.abort();
        state.discovery.mode = button.dataset.discoverMode;
        // Browse > Search is itself a search start: its tabs and Cancel stay under the finger.
        if (state.discovery.mode === "search") state.discovery.searchEngaged = true;
        const params = new URLSearchParams();
        if (state.discovery.mode !== "search") params.set("mode", state.discovery.mode);
        if (state.discovery.mode === "world") params.set("view", state.world.view);
        if (state.discovery.mode === "records") params.set("view", state.records.view);
        const path = `/discover${params.size ? `?${params}` : ""}`;
        const sheet = button.closest("dialog[open]");
        if (sheet) navigateFromDialog(path, sheet);
        else history.pushState({}, "", path);
        renderDiscover();
        refreshDiscover();
      });
    });
    $("#discover-browse")?.addEventListener("click", () => openDialog($("#discover-browse-dialog")));
    el.discoverCategory.addEventListener("change", () => {
      state.search.category = el.discoverCategory.value;
      refreshDiscover();
    });
    $$("[data-events-when]").forEach((button) => button.addEventListener("click", () => {
      if (state.events.when === button.dataset.eventsWhen) return;
      state.events.when = button.dataset.eventsWhen;
      state.events.items = [];
      state.events.loaded = false;
      state.events.focusId = null;
      renderEvents();
      refreshEvents();
    }));
    el.eventsList?.addEventListener("click", (event) => {
      const card = event.target.closest?.("[data-event-id]");
      if (!card) return;
      const answer = event.target.closest?.("[data-event-answer]");
      if (answer) { answerEvent(card.dataset.eventId, answer.dataset.eventAnswer); return; }
      if (event.target.closest?.("[data-event-share]")) shareEvent(card.dataset.eventId);
    });
    el.eventsNew?.addEventListener("click", () => {
      const error = $("#event-error");
      if (error) { error.hidden = true; error.textContent = ""; }
      $("#event-form")?.reset();
      // Default to a whole hour tomorrow, so the common case is two taps rather than
      // eight. datetime-local wants local time with no zone, hence the offset shift.
      const start = new Date(Date.now() + 24 * 3600 * 1000);
      start.setMinutes(0, 0, 0);
      const end = new Date(start.getTime() + 3600 * 1000);
      const localValue = (date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString().slice(0, 16);
      $("#event-starts").value = localValue(start);
      $("#event-ends").value = localValue(end);
      openDialogWithHistory(el.eventDialog);
    });
    $("#event-form")?.addEventListener("submit", async (event) => {
      event.preventDefault();
      const error = $("#event-error");
      const save = $("#event-save");
      const starts = $("#event-starts").value;
      const ends = $("#event-ends").value;
      if (!starts || !ends) return;
      if (error) { error.hidden = true; error.textContent = ""; }
      save.disabled = true;
      try {
        // The inputs are local time with no zone; toISOString hands the server UTC.
        await api("/api/calendar/events", {
          method: "POST", csrf: true,
          json: {
            title: $("#event-title").value.trim(),
            body: $("#event-body").value.trim() || null,
            room: null,
            starts_at: new Date(starts).toISOString(),
            ends_at: new Date(ends).toISOString(),
          },
        });
        el.eventDialog?.close();
        state.events.when = "upcoming";
        await refreshEvents();
        toast("Your event is up.");
      } catch (failure) {
        if (error) { error.textContent = failure.message; error.hidden = false; }
      } finally {
        save.disabled = false;
      }
    });
    $$('[data-world-view]').forEach((button) => {
      button.addEventListener("click", () => {
        if (state.world.view === button.dataset.worldView) return;
        state.world.view = button.dataset.worldView;
        history.replaceState({}, "", `/discover?${new URLSearchParams({ mode: "world", view: state.world.view })}`);
        centerInScroller(button, button.closest(".utility-view-tabs"));
        state.world.pendingFocus = null;
        void refreshWorld({ quiet: true });
        renderWorld();
        el.worldSurface.scrollIntoView({ behavior: motionBehavior(), block: "start" });
      });
      button.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        const tabs = $$('[data-world-view]');
        const index = tabs.indexOf(button);
        const next = event.key === "Home" ? 0
          : event.key === "End" ? tabs.length - 1
            : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
        tabs[next].click(); tabs[next].focus();
      });
    });
    $$('[data-record-view]').forEach((button) => button.addEventListener("click", () => {
      state.records.view = button.dataset.recordView;
      history.replaceState({}, "", `/discover?${new URLSearchParams({ mode: "records", view: state.records.view })}`);
      $$('[data-record-view]').forEach((item) => {
        const active = item === button;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-pressed", String(active));
      });
      centerInScroller(button, button.closest(".record-view-tabs"));
      syncRecordControls();
      refreshRecords();
    }));
    $$('[data-record-view]').forEach((button) => button.addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      const tabs = $$('[data-record-view]');
      const index = tabs.indexOf(button);
      const next = event.key === "Home" ? 0
        : event.key === "End" ? tabs.length - 1
          : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
      tabs[next].click(); tabs[next].focus();
    }));
    el.recordsPeriod.addEventListener("change", () => {
      state.records.period = el.recordsPeriod.value;
      refreshRecords();
    });
    el.recordsScope.addEventListener("change", () => {
      state.records.scope = el.recordsScope.value;
      if (state.records.view === "winners") refreshRecords();
    });
    el.discoverMorePeople.addEventListener("click", () => refreshDiscover("people"));
    el.discoverPreviousPeople.addEventListener("click", () => refreshDiscover("people", -1));
    el.discoverMoreDrops.addEventListener("click", () => refreshDiscover("drops"));
    $("#retry-discover").addEventListener("click", retryDiscover);
    el.notificationsReadAll.addEventListener("click", () => markNotificationsRead([], true));
    el.notificationsMore.addEventListener("click", () => refreshNotifications(true));
    $("#retry-notifications").addEventListener("click", () => refreshNotifications(Boolean(state.notifications.retryAppend)));
    $("#project-updates-refresh").addEventListener("click", () => refreshJourneyUpdates());
    $("#project-updates-read").addEventListener("click", async () => {
      const updates = state.journeyUpdates;
      const ids = (updates.unreadIds || []).slice(0, 50);
      const owner = state.viewer?.id;
      if (!owner || !ids.length || updates.marking) return;
      updates.marking = true; renderJourneyUpdates();
      try {
        await api("/api/journeys/updates/read", { method: "POST", csrf: true, json: { drop_ids: ids } });
        if (state.viewer?.id !== owner || state.journeyUpdates !== updates) return;
        await refreshJourneyUpdates();
        document.dispatchEvent(new CustomEvent("louderr:projects-read"));
      } catch (error) {
        if (state.viewer?.id === owner) toast(error.message || "Could not mark project updates read.", true);
      } finally { updates.marking = false; if (state.journeyUpdates === updates) renderJourneyUpdates(); }
    });
    document.addEventListener("louderr:projects-read", () => {
      if (state.view === "notifications") refreshJourneyUpdates();
    });
    $("#project-updates-retry").addEventListener("click", () => refreshJourneyUpdates(Boolean(state.journeyUpdates.retryAppend)));
    el.projectUpdatesMore.addEventListener("click", () => refreshJourneyUpdates(true));
    $("#project-retry").addEventListener("click", () => refreshJourney(Boolean(state.journey.retryAppend)));
    el.projectMore.addEventListener("click", () => refreshJourney(true));
    el.projectSubscribe.addEventListener("click", toggleJourneySubscription);
    el.projectResponseSetting.addEventListener("change", setJourneyResponses);
    el.projectUpdate.addEventListener("click", () => openJourneyComposer("update"));
    el.projectRespond.addEventListener("click", () => openJourneyComposer("response"));
    $("#project-copy-link").addEventListener("click", copyJourneyLink);
    el.project.addEventListener("close", () => {
      state.journey.requestId += 1;
      state.journey.loading = false;
      state.journey.subscribing = false;
      clearTimeout(state.journey.refreshTimer);
      $$("video, audio", el.project).forEach((media) => media.pause());
    });
    $("#retry-messages").addEventListener("click", () => retryConversations());
    $("#message-tools-retry").addEventListener("click", () => {
      if (!state.viewer) return;
      if (messageModulesStatus === "reload") { location.reload(); return; }
      if (messageModulesStatus === "error") ensureMessageModules(true);
    });
    el.messagesNew.addEventListener("click", beginNewMessage);
    el.messagesEmptyNew.addEventListener("click", beginNewMessage);
    el.messagesSearch.addEventListener("input", (event) => {
      if (!event.isComposing) updateMessageSearch(el.messagesSearch.value);
    });
    el.messagesSearch.addEventListener("compositionend", () => updateMessageSearch(el.messagesSearch.value));
    el.messagesSearch.addEventListener("keydown", (event) => {
      if (event.isComposing || event.keyCode === 229) return;
      if (event.key === "Enter") { event.preventDefault(); refreshMessagePeople(); }
      if (event.key === "Escape") { resetMessageSearch(); el.messagesSearch.focus(); }
    });
    el.messagesSearchClear.addEventListener("click", () => { resetMessageSearch(); el.messagesSearch.focus(); });
    el.messagesSearchMore.addEventListener("click", () => refreshMessagePeople(true));
    el.messagesSearchRetry.addEventListener("click", () => refreshMessagePeople(messagePeopleSearch().retryAppend));
    el.messagesOlder.addEventListener("click", () => openMessageThread(state.messages.selectedId, false, true));
    el.messageHistoryRetry?.addEventListener("click", () => { if (!state.messages.searchWindow) retryMessageThread(); });
    el.messageHistoryLatest?.addEventListener("click", () => {
      if (!state.messages.searchWindow && !state.messages.threadWindow?.read) openMessageThread(state.messages.selectedId, false, false, null, { latest: true });
    });
    el.messageParticipant.addEventListener("click", () => {
      if (state.messages.conversation?.kind === "group") globalThis.LouderrMessageGroups?.info();
      else if (state.messages.participant) openCreator(state.messages.participant);
    });
    el.messageRequestAccept.addEventListener("click", () => decideMessageRequest("accept"));
    el.messageRequestDecline.addEventListener("click", () => decideMessageRequest("decline"));
    el.messageForm.addEventListener("submit", submitDirectMessage);
    el.messageMediaButton?.addEventListener("click", () => el.messageMediaInput?.click());
    el.messageMediaInput?.addEventListener("change", (event) => {
      selectMessageAttachment(event.target.files && event.target.files[0]);
    });
    el.messageMediaRemove?.addEventListener("click", clearMessageAttachment);
    el.messageFileButton?.addEventListener("click", () => el.messageFileInput?.click());
    el.messageFileInput?.addEventListener("change", event => selectMessageFile(event.target.files?.[0]));
    el.messageFileCheck?.addEventListener("click", async () => {
      const entry = state.messageAttachment;
      if (!entry) return;
      if (entry.current?.() === false) {
        clearMessageAttachment();
        showError(el.messageFormError, "Your session or conversation changed. Choose the attachment again before sending.");
        return;
      }
      if (entry.loading || entry.busy || entry.pending) return;
      try {
        if (entry.kind === "pending-media") await prepareMessageAttachment(entry);
        else if (entry.kind === "file") await state.messageFileUploads?.check(entry);
        else await mediaUploads().check(entry);
      } catch (error) {
        if (state.messageAttachment === entry && entry.current?.() !== false) {
          entry.error = error.message || "Attachment status is unavailable. Check again before sending.";
          paintMessageAttachment();
        }
      } finally {
        if (state.messageAttachment === entry && entry.current?.() === false) clearMessageAttachment();
      }
    });
    el.commentsReplyCancel?.addEventListener("click", cancelCommentReply);
    el.messageBody.addEventListener("focus", () => {
      if (!state.viewer) { el.messageBody.blur(); openDialog(el.account); }
      else if (!state.viewer.email_verified) { el.messageBody.blur(); requireVerified(); }
    });
    el.messageBody.addEventListener("input", () => {
      rememberMessageDraft(true);
      sizeMessageInput();
      emoteAutocomplete(el.messageBody, el.messageEmote);
    });
    el.messageBody.addEventListener("keydown", (event) => {
      if (event.isComposing || event.keyCode === 229) return;
      if (handleEmoteTextareaKeydown(event, el.messageBody)) return;
      if (event.key === "Enter" && !event.shiftKey && matchMedia("(hover: hover) and (pointer: fine)").matches) {
        event.preventDefault();
        el.messageForm.requestSubmit();
      }
    });
    el.profileFollow.addEventListener("click", toggleProfileFollow);
    el.profileMessage.addEventListener("click", startProfileConversation);
    el.profileMute.addEventListener("click", () => toggleProfileRelationship("mute"));
    el.profileBlock.addEventListener("click", () => toggleProfileRelationship("block"));
    el.profileReport.addEventListener("click", openProfileReport);
    el.profileEdit.addEventListener("click", () => openAccountDialog("profile"));
    $("#profile-settings")?.addEventListener("click", () => openAccountDialog("settings"));
    el.profileFollowers.addEventListener("click", () => openConnections("followers"));
    el.profileFollowing.addEventListener("click", () => openConnections("following"));
    el.conversationsMore.addEventListener("click", () => refreshConversations(false, true));
    el.profileDropsMore.addEventListener("click", () => refreshProfile(state.profile.handle, true));
    {
      const layoutButtons = document.querySelectorAll?.("[data-profile-layout]");
      for (const button of layoutButtons && typeof layoutButtons[Symbol.iterator] === "function" ? layoutButtons : []) {
        if (typeof button.addEventListener === "function") button.addEventListener("click", () => setProfileLayout(button.dataset.profileLayout));
      }
    }
    $("#word-filter-form")?.addEventListener("submit", saveWordFilter);
    el.relationshipsRefresh.addEventListener("click", () => {
      state.relationships.loaded = false;
      refreshRelationships();
    });
    $("#retry-profile").addEventListener("click", () => {
      state.socialExtras?.leaveProfileReposts?.({ returnScroll: history.state?.louderrScroll });
      return refreshProfile();
    });
    $("#retry-profile-drops").addEventListener("click", () => {
      state.socialExtras?.leaveProfileReposts?.({ returnScroll: history.state?.louderrScroll });
      return refreshProfile();
    });
    $$('[data-menu-action]').forEach((button) => button.addEventListener("click", () => actionMenu(button.dataset.menuAction)));
    el.dropForm.addEventListener("submit", submitDrop);
    el.projectStart.addEventListener("change", updateComposerCopy);
    $("#project-template").addEventListener("click", addJourneyPromptTemplate);
    $("#project-view-original").addEventListener("click", () => {
      const rootId = state.composerJourney?.root_drop_id;
      if (!rootId || state.composerDraft.busy) return;
      el.composer.close();
      openJourney(rootId);
    });
    el.composer.addEventListener("close", () => {
      state.composerCaptureSequence = (state.composerCaptureSequence || 0) + 1;
      stopServerSoundPreviews(); globalThis.LouderrVideoRemixStudio?.sync(); saveCurrentSoundDraft(true); saveCurrentVideoDraft(true); captureComposerDraft();
    });
    el.dropForm.addEventListener("change", saveCurrentSoundDraft);
    el.dropForm.addEventListener("change", saveCurrentVideoDraft);
    addEventListener("pagehide", stopServerSoundPreviews);
    addEventListener("pagehide", () => globalThis.LouderrVideoRemixStudio?.close());
    el.composer.addEventListener("cancel", (event) => {
      if (state.composerDraft.busy) { event.preventDefault(); toast("Wait for your Drop to finish saving."); }
    });
    $("#discard-drop-draft")?.addEventListener("click", discardComposerDraft); $("#creative-start-project")?.addEventListener("click", () => openComposer(null, { mode: "start", prompt: "feedback", allow_responses: false })); $("#creative-open-studio")?.addEventListener("click", () => void openStudioStart());
    $("#cancel-composer-intent")?.addEventListener("click", cancelComposerIntent);
    el.account.addEventListener("close", () => {
      if (!el.account.keepComposerIntent) clearComposerIntent();
      delete el.account.keepComposerIntent;
      state.profilePlaces?.dismiss();
      if (profileSaveBusy?.phase === "media") profileSaveBusy.cancelled = true;
    });
    addEventListener("beforeunload", protectComposerDraftOnUnload);
    el.dropTitle.addEventListener("input", () => { $("#title-count").textContent = `${el.dropTitle.value.length} / 120`; });
    el.dropBody.addEventListener("input", () => { $("#body-count").textContent = `${el.dropBody.value.length} / 700`; });
    bindComposerMediaPicker(el.dropMedia, addSelectedMediaFiles);
    bindComposerMediaPicker($("#drop-camera-photo"), addSelectedMediaFiles);
    bindComposerMediaPicker($("#drop-camera-video"), addSelectedMediaFiles);
    $("#composer-next").addEventListener("click", nextComposerStep);
    $("#composer-back").addEventListener("click", () => { state.gallery.step = "media"; renderComposerStep(); });
    $("#composer-review-media").addEventListener("click", () => { state.gallery.step = "media"; renderComposerStep(); });
    $("#composer-text-only").addEventListener("click", startTextPost);
    $("#composer-open-story")?.addEventListener("click", () => {
      if (state.composerDraft.busy || !state.storiesV2) return;
      el.composer.close();
      state.storiesV2.openCreate();
    });
    $("#composer-go-live")?.addEventListener("click", () => {
      if (state.composerDraft.busy || !state.live?.canHost()) return;
      el.composer.close();
      void state.live.openHost();
    });
    $("#profile-watch-live")?.addEventListener("click", event => {
      const liveId = event.currentTarget.dataset.liveId;
      if (liveId) void state.live?.watch(liveId);
    });
    $("#composer-resume-studio")?.addEventListener("click", () => { void resumeStudioDraftIntoComposer(); });
    $("#composer-use-original-sound")?.addEventListener("click", () => {
      const context = state.composerJourney;
      if (!context?.root_media_url || !state.capabilities.studio_response_audio) return;
      const item = state.gallery.items.length === 1 && state.gallery.items[0];
      if (!item?.mediaType?.startsWith("video/")) { toast("Choose your video first, then choose an offered original sound."); return; }
      void chooseGallerySound(item).catch(error => showError(el.formError, error.message));
    });
    $$("[data-open-studio]").forEach(button => button.addEventListener("click", () => void openStudioStart()));
    // A member may have an unfinished sound or video publication whose draft store lives in studio.js,
    // and it is no longer in the first load. Fetch it shortly after boot so the resume prompt still
    // appears on its own; a guest has no drafts and never pays for it. Opening Studio awaits it anyway,
    // so a member whose session resolves later than this only waits at the point they ask for it.
    setTimeout(() => {
      if (!state.viewer) return;
      void loadStudio().catch(() => {});
      void loadDeferred("video-remix").catch(() => {});
    }, 1500);
    $("#studio-start-files")?.addEventListener("change", event => {
      const files = [...event.target.files]; event.target.value = "";
      if (files.length) void openStandaloneStudio({ clips: files.map(file => ({ file })) });
    });
    $("#studio-start-resume")?.addEventListener("click", async () => {
      const found = await studioDraftLookup();
      if (!found) { renderStudioStart(); return; }
      void openStandaloneStudio(draftToStudioInput(found.value), found.key);
    });
    $("#studio-start-delete")?.addEventListener("click", async () => {
      const found = await studioDraftLookup();
      if (found) await window.LobbyStudio?.drafts?.clear(found.key);
      renderStudioStart();
    });
    $("#studio-start-publish")?.addEventListener("click", () => publishStandaloneStudio());
    $("#studio-start-reopen")?.addEventListener("click", () => {
      const result = state.studioStandalone;
      if (result?.project) void openStandaloneStudio(draftToStudioInput({ project: result.project }));
    });
    bindComposerMediaPicker($("#studio-new-files"), event => {
      const files = [...event.target.files]; event.target.value = ""; if (files.length) openStudioFiles(files);
    });
    el.dropForm.addEventListener("invalid", () => { state.gallery.step = "details"; renderComposerStep(); }, true);
    el.profileAvatarInput.addEventListener("change", () => {
      const source = el.profileAvatarInput.files[0];
      setProfilePhotoFile(profilePhotoFile);
      if (source) adjustProfilePhoto(source);
    });
    $("#profile-photo-adjust").addEventListener("click", () => adjustProfilePhoto(profilePhotoFile || state.viewer?.avatar_url));
    el.account.addEventListener("close", () => window.LouderrProfilePhoto?.cancel());
    el.profileForm.addEventListener("input", () => { profileDraftDirty = true; });
    el.profileForm.addEventListener("change", () => { profileDraftDirty = true; });
    el.reportForm.addEventListener("submit", submitReport);
    el.reportForm.addEventListener("change", syncReportGuidance);
    el.chatForm.addEventListener("submit", submitChat);
    el.commentsForm.addEventListener("submit", submitComment);
    // Enter sends the comment, as the phone keyboard promises; Shift+Enter keeps a line break.
    el.commentsBody?.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" || event.shiftKey || event.isComposing || event.keyCode === 229) return;
      event.preventDefault();
      if (typeof el.commentsForm.requestSubmit === "function") el.commentsForm.requestSubmit();
      else el.commentsForm.dispatchEvent(new Event("submit", { cancelable: true }));
    });
    el.commentsMore.addEventListener("click", () => refreshComments(true));
    el.commentsBody.addEventListener("focus", () => {
      if (!state.viewer) { el.commentsBody.blur(); openDialog(el.account); }
      else if (!state.viewer.email_verified) { el.commentsBody.blur(); requireVerified(); }
    });
    el.commentsBody.addEventListener("keydown", (event) => {
      if (event.isComposing || event.keyCode === 229) return;
      if (event.key === "Enter" && !event.shiftKey && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        el.commentsForm.requestSubmit();
      }
    });
    el.comments.addEventListener("close", () => {
      // A native close event can arrive after the shell has moved inline or a
      // newer dialog has opened. It no longer owns either conversation.
      if (el.comments.open || state.comments.inlineDropId) return;
      const commentsMediaPane = $("#comments-media"); if (commentsMediaPane) { clear(commentsMediaPane); commentsMediaPane.hidden = true; el.comments.classList.remove("has-media"); }
      resetComments();
    });
    el.chatOlder.addEventListener("click", () => refreshChat(true));
    $("#retry-chat").addEventListener("click", () => refreshChat(Boolean(state.chat.retryOlder)));
    $("#chat-cancel-reply").addEventListener("click", () => { cancelChatReply(); saveChatDraft(); });
    el.chatNew.addEventListener("click", () => { scrollChatBottom(motionBehavior()); markChatRead(); });
    el.chatMarkUnread.addEventListener("click", async () => {
      const context = chatContext();
      const requestId = ++context.chat.readRequestId;
      try {
        const result = await api(`/api/chat/read-state?room=${encodeURIComponent(context.room)}`, { method: "DELETE", csrf: true });
        if (!context.current() || requestId !== context.chat.readRequestId) return;
        context.chat.roomUnread.set(context.room, Number(result.unread_count) || 0);
        context.chat.unreadVersion.set(context.room, (context.chat.unreadVersion.get(context.room) || 0) + 1);
        updateChatBadges(); renderChatRooms();
        toast(`${context.chat.rooms.find((room) => room.id === context.room)?.label || "Room"} marked unread`);
      } catch (error) { if (context.current()) toast(error.message, true); }
    });
    el.chatRoomSearch.addEventListener("input", renderChatRooms);
    $("#chat-room-retry").addEventListener("click", refreshChatRooms);
    el.chatRoomsOpen.addEventListener("click", () => setChatRoomsOpen(true));
    el.chatHostToggle?.addEventListener("click", toggleChatHost);
    const chatRoomOptions = $("#chat-room-options");
    chatRoomOptions.addEventListener("click", (event) => {
      if (event.target.closest("button")) chatRoomOptions.open = false;
    });
    document.addEventListener("click", (event) => {
      if (chatRoomOptions.open && !chatRoomOptions.contains(event.target)) chatRoomOptions.open = false;
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && chatRoomOptions.open) {
        event.preventDefault();
        chatRoomOptions.open = false;
        $("summary", chatRoomOptions).focus();
      }
    });
    el.dropHereFirst?.addEventListener("change", syncHereFirstWindow);
    $("#chat-rooms-close").addEventListener("click", () => setChatRoomsOpen(false, true));
    el.chatRoomSearch.addEventListener("keydown", (event) => {
      if (event.key === "Escape") { event.preventDefault(); setChatRoomsOpen(false, true); }
    });
    el.chatBody.addEventListener("focus", () => {
      if (!state.viewer) { el.chatBody.blur(); openAccountDialog(); }
      else if (!state.viewer.email_verified) { el.chatBody.blur(); requireVerified(); }
      else beginChatTyping();
    });
    el.chatBody.addEventListener("input", (event) => {
      updateChatComposer(); saveChatDraft();
      if (!event.isComposing) emoteAutocomplete(el.chatBody, el.chatEmote);
      beginChatTyping();
    });
    el.chatBody.addEventListener("blur", () => stopChatTyping());
    el.chatBody.addEventListener("keydown", (event) => {
      if (event.isComposing || event.keyCode === 229) return;
      if (handleEmoteTextareaKeydown(event, el.chatBody)) return;
      if (event.key === "Enter" && !event.shiftKey && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        el.chatForm.requestSubmit();
      }
    });
    el.chatMessages.addEventListener("scroll", () => {
      if (state.view === "chat" && chatNearBottom()) {
        el.chatNew.hidden = true;
        state.chat.newWhileAway = 0;
      }
    }, { passive: true });
    el.live.addEventListener("click", () => {
      state.paused = !state.paused;
      const action = state.paused ? "Resume" : "Pause";
      el.live.setAttribute("aria-pressed", String(state.paused));
      el.live.setAttribute("aria-label", `${action} live updates`);
      el.live.title = `${action} live updates`;
      el.live.classList.toggle("is-paused", state.paused);
      $("span", el.live).textContent = action;
    });
    $("#show-pending").addEventListener("click", () => {
      state.pending = [];
      el.pendingBanner.hidden = true;
      refreshFeed();
    });
    el.loadMore.addEventListener("click", () => refreshFeed(true));
    $("#refresh-state").addEventListener("click", () => {
      refreshRoom();
      refreshFeed();
      refreshTrendingPreview();
      if (state.view === "trending") refreshTrending();
    });
    $("#retry-trending").addEventListener("click", refreshTrending);
    $("#retry-flow").addEventListener("click", () => {
      if (state.flow.view === "stories") {
        const append = state.flow.lastAppend && !state.flow.storyResetRequired;
        refreshFlow(append, !append && !state.flow.storyResetRequired && Boolean(currentStorySnapshot()));
      } else if (state.flow.view === "feed" && state.flow.lastRevalidate && !state.flow.resetRequired) resumeFlow();
      else refreshFlow(state.flow.lastAppend && !state.flow.resetRequired);
    });
    el.flowMore.addEventListener("click", () => refreshFlow(true));
    el.flowRefresh.addEventListener("click", () => refreshFlow());
    el.storyMore.addEventListener("click", () => refreshFlow(true));
    el.storyRefresh.addEventListener("click", () => refreshFlow());
    $("#retry-feed").addEventListener("click", retryFeed);
    $$('[data-auth-tab]').forEach((button) => {
      button.addEventListener("click", () => authTab(button.dataset.authTab));
      button.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        // Arrow keys move between the tabs that are actually offered; withheld ones are skipped.
        const tabs = $$('[data-auth-tab]').filter((tab) => !tab.hidden && !tab.classList.contains("capability-withheld"));
        const index = tabs.indexOf(button);
        const next = event.key === "Home" ? 0
          : event.key === "End" ? tabs.length - 1
            : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
        authTab(tabs[next].dataset.authTab);
        tabs[next].focus();
      });
    });
    $$('[data-auth-view]').forEach((button) => button.addEventListener("click", () => {
      const view = button.dataset.authView;
      if (view === "login") state.passwordSignInExpanded = true;
      authTab(view);
      $(`#${view}-form input`)?.focus({ preventScroll: true });
    }));
    $$('[data-open-self-profile]').forEach((button) => button.addEventListener("click", openSelfProfile));
    $("#guest-open-settings").addEventListener("click", () => {
      el.account.close();
      openAccountDialog("settings");
    });
    $("#login-form").addEventListener("submit", (event) => authSubmit(event, "login"));
    window.LouderrRegistration.create($("#register-form"), event => authSubmit(event, "register"), () => state.minimumAge);
    $("#friend-invite-form").addEventListener("submit", acceptFriendInvite);
    $("#beta-access-form").addEventListener("submit", acceptBetaAccess);
    $("#reset-form").addEventListener("submit", requestReset);
    $$("#reset-form button[data-reset-step]").forEach((button) => button.addEventListener("click", () => {
      if ($("#reset-form").dataset.busy) return;
      hideError(accountErrorSlot());
      setPasswordResetStep(button.dataset.resetStep);
      const form = $("#reset-form");
      (button.dataset.resetStep === "request" ? form.elements.email : form.elements.token).focus({ preventScroll: true });
    }));
    $("#password-sign-in-toggle").addEventListener("click", () => {
      state.passwordSignInExpanded = !state.passwordSignInExpanded;
      renderPasswordSignIn(configuredFederatedProviders());
      if (state.passwordSignInExpanded) $("#login-form input")?.focus({ preventScroll: true });
    });
    $("#verify-account").addEventListener("click", verifyAccount);
    $("#resend-verification").addEventListener("click", resendVerification);
    el.agePanel.addEventListener("submit", event => { event.preventDefault(); void confirmAge(); });
    $("#age-review-messages").addEventListener("click", () => {
      el.account.close();
      setView("messages");
    });
    const today = new Date().toISOString().slice(0, 10);
    $$("input[name='birth_date'], #age-birth-date").forEach((input) => { input.max = today; });
    el.profileForm.addEventListener("submit", saveProfile);
    for (const field of [el.dropTitle, el.dropBody]) field?.addEventListener("input", renderComposerTopics);
    $$('[data-account-section]').forEach((button) => {
      button.addEventListener("click", () => { accountEditorDirect = false; setAccountSection(button.dataset.accountSection); });
    });
    $("#push-toggle")?.addEventListener("click", togglePush);
    $$('[data-theme-choice]').forEach((input) => input.addEventListener("change", () => {
      if (!input.checked) return;
      const applied = window.lobbyTheme?.setPreference?.(input.value);
      if (!applied) {
        toast("That appearance option is unavailable.", true);
        syncThemeControls();
        return;
      }
      syncThemeControls();
      toast(input.value === "system" ? "Appearance now follows this device." : `${input.value === "light" ? "Light" : "Dark"} mode selected.`);
    }));
    addEventListener("lobby:themechange", syncThemeControls);
    $("#security-refresh").addEventListener("click", () => refreshSecurity(true));
    el.securityRevokeOthers.addEventListener("click", revokeOtherSessions);
    $("#security-password-form").addEventListener("submit", submitPasswordChange);
    $("#security-email-form").addEventListener("submit", submitEmailChange);
    $("#logout-account").addEventListener("click", logout);
    $("#logout-all-account").addEventListener("click", logoutAllSessions);
    $("#export-account").addEventListener("click", exportAccount);
    $("#export-followers")?.addEventListener("click", exportFollowers);
    $$("[data-account-billing]").forEach((button) => {
      button.addEventListener("click", () => manageAccountBilling(button.dataset.accountBilling, button));
    });
    $("#delete-account").addEventListener("click", deleteAccount);
    $("#account-switcher-trigger").addEventListener("click", openManagedAccounts);
    $("#managed-account-add").addEventListener("click", () => setManagedAccountPanel("create"));
    $("#managed-account-back").addEventListener("click", () => setManagedAccountPanel("list"));
    $("#managed-account-close").addEventListener("click", closeManagedAccounts);
    $("#account-switcher-dialog").addEventListener("cancel", cancelManagedAccounts);
    $("#account-switcher-dialog").addEventListener("close", () => {
      $("#account-switcher-trigger").setAttribute("aria-expanded", "false");
    });
    $("#account-switcher-dialog").addEventListener("click", (event) => {
      const dialog = event.currentTarget;
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeManagedAccounts();
    });
    $("#managed-account-form").addEventListener("submit", createManagedAccount);
    $("#managed-account-retry").addEventListener("click", refreshManagedAccounts);
    $("#managed-signin-primary").addEventListener("click", () => switchManagedAccount(state.viewer.primary_account_id, "security"));
    addEventListener("storage", (event) => {
      if (event.key === "louderr-account-change") reloadAfterAccountChange();
    });
    el.adminNav.addEventListener("click", openAdmin);
    el.adminAccount.addEventListener("click", openAdmin);
    el.adminRefresh.addEventListener("click", () => {
      if (state.adminWorkspace) selectAdminSection(state.adminWorkspace.active, true);
    });
    el.admin.addEventListener("close", () => { if (!el.admin.open) resetAdminWorkspace(); });
    for (const dialog of [el.account, el.composer, el.comments, el.project, el.connections, el.studioStart,
      $("#getting-started-dialog"), $("#appearance-dialog"), $("#coauthors-dialog"), $("#saved-folder-dialog"), $("#post-archive-dialog")]) {
      if (!dialog) continue;
      dialog.dataset.historyWired = "true";
      dialog.addEventListener("cancel", (event) => { if (!event.defaultPrevented) dialog.dataset.userClose = "true"; });
      dialog.addEventListener("close", () => {
        const previousTitle = dialog === el.composer
          ? state.composerTitleBeforeOpen
          : dialog === el.studioStart ? state.studioTitleBeforeOpen : "";
        settleDialogHistory(dialog);
        if (previousTitle) {
          if (typeof document !== "undefined") document.title = previousTitle;
          if (dialog === el.composer) state.composerTitleBeforeOpen = "";
          if (dialog === el.studioStart) state.studioTitleBeforeOpen = "";
        }
      });
    }
    addEventListener("popstate", (event) => {
      const position = event.state?.louderrScroll;
      const samePage = finishDialogHistoryBack();
      // Leaving a dialog's history entry closes it; its close handler then finds the entry gone.
      const current = history.state?.louderrDialog || null;
      for (const dialog of $$("dialog[open]")) {
        if (dialog.dataset.historyEntry && dialogName(dialog) !== current) {
          delete dialog.dataset.historyEntry;
          dialog.close();
        }
      }
      if (el.account.open && location.pathname.split("/")[1] !== "settings") el.account.close();
      nav.entries = Math.max(0, nav.entries - 1);
      const operation = samePage ? null : routeLocation();
      restoreRouteScroll(operation, position);
      updateBackControl();
    });
    countRoutePushes();
    watchDialogs();
    document.addEventListener("click", closeSheetOnBackdrop);
    $("#back-control")?.addEventListener("click", goBack);
    updateBackControl();
    // Trending is one more way to look, so it sits with the others rather than beside them.
    const trendingLink = $(".discover-trending-link");
    const browseList = $("#discover-browse-dialog .browse-list");
    const goToGroup = $$("#discover-browse-dialog .browse-group").pop();
    if (trendingLink && browseList && goToGroup) browseList.insertBefore(trendingLink, goToGroup);
    // On a phone the Board folds to its header; the chevron opens the spotlight.
    $("#board-fold")?.addEventListener("click", () => {
      const board = $("#board"), fold = $("#board-fold");
      const open = !board.classList.contains("is-open");
      board.classList.toggle("is-open", open);
      fold.setAttribute("aria-expanded", String(open));
      fold.setAttribute("aria-label", open ? "Hide the Board" : "Show the Board");
    });
    // The phone header slides away on scroll down and returns on scroll up, like a native feed.
    let headerScrollY = window.scrollY || 0;
    let headerFrame = 0;
    addEventListener("scroll", () => {
      if (headerFrame) return;
      headerFrame = requestAnimationFrame(() => {
        headerFrame = 0;
        const y = window.scrollY || 0;
        const delta = y - headerScrollY;
        if (Math.abs(delta) < 8) return;
        document.documentElement.classList.toggle("is-header-hidden", delta > 0 && y > 80);
        headerScrollY = y;
      });
    }, { passive: true });
    bindTouchGestures();
    addEventListener("online", () => {
      void resumeCapabilityRecovery();
      state.messages.scheduled?.refresh();
      refreshRoom(true);
      syncRealtimeWithAuth();
      if (state.view === "flow" && state.flow.view === "now") scheduleNowRefresh(0);
      if (state.view === "flow" && state.flow.view === "stories") { renderFlow(); scheduleStoriesRefresh(0); }
    });
    addEventListener("offline", () => {
      state.flow.audienceChannel?.stop({ markUnavailable: true });
      if (state.flow.view === "stories") { clearTimeout(state.flow.refreshTimer); state.flow.refreshTimer = null; }
      stopChatTyping({ clearPeople: true });
      clearTimeout(state.reconnectTimer);
      if (!realtimeOwnerId()) {
        stopRealtime();
      } else {
        setConnection("offline", "Offline");
        if (state.socket && state.socket.readyState <= WebSocket.OPEN) state.socket.close(4000, "Network offline");
      }
    });
    document.addEventListener("visibilitychange", () => {
      void resumeCapabilityRecovery();
      if (document.visibilityState === "visible") state.messages.scheduled?.refresh();
      if (document.visibilityState === "hidden") {
        state.flow.audienceChannel?.stop();
        if (state.view === "flow" && state.flow.view === "stories") stopFlowTimers();
        mediaUploadManager?.pauseAll();
        if (state.view === "flow") $$("video, audio", el.flowSection).forEach((media) => media.pause());
        for (const card of state.integrityPending.keys()) cancelIntegrityExposure(card);
        recordActiveDuration();
        flushAnalyticsBeacon();
      } else {
        mediaUploadManager?.resumeVisible();
        if (state.activeSince === null) state.activeSince = performance.now();
        observeIntegrityCards();
        if (state.viewer?.status === "active") schedulePrivatePolling();
        if (state.view === "flow" && state.flow.view === "now") scheduleNowRefresh(0);
        if (state.view === "flow" && state.flow.view === "stories") { renderFlow(); scheduleStoriesRefresh(0); }
      }
    });
    document.addEventListener("keydown", closeNestedDialogOnEscape);
    addEventListener("pagehide", () => { state.flow.audienceChannel?.stop(); recordActiveDuration(); flushAnalyticsBeacon(); });
  }

  function refreshSoundPlaybackCard(card, drop) {
    const previous = card.soundPlaybackDrop;
    if (previous?.id !== drop.id || !Array.isArray(previous.media) || !previous.media.length
        || !Array.isArray(drop.media) || previous.media.length !== drop.media.length
        || previous.media_asset_id !== drop.media_asset_id || previous.media_type !== drop.media_type) return;
    // Selected bytes, source credit and accessibility text were updated in place.
    // Asset/order/poster and other metadata still require ordinary rendering.
    const metadata = item => {
      const { media_url, media_selection_revision, media_status, scanner_status,
        captions_url, transcript_url, media_text_revision, ...rest } = item;
      return JSON.stringify(rest);
    };
    if (previous.media.some((item, index) => !item?.media_asset_id || !drop.media[index]
        || metadata(item) !== metadata(drop.media[index]))) return;
    const rendered = { ...previous, sound: drop.sound, video_remix: drop.video_remix, media_url: drop.media_url,
      media_selection_revision: drop.media_selection_revision,
      captions_url: drop.captions_url, transcript_url: drop.transcript_url, media_text_revision: drop.media_text_revision,
      media_status: drop.media_status, scanner_status: drop.scanner_status,
      media: previous.media.map((item, index) => ({ ...item,
        media_url: drop.media[index].media_url,
        media_selection_revision: drop.media[index].media_selection_revision,
        captions_url: drop.media[index].captions_url, transcript_url: drop.media[index].transcript_url,
        media_text_revision: drop.media[index].media_text_revision,
        // These describe the already-authorized selection, and have no card UI.
        media_status: drop.media[index].media_status, scanner_status: drop.media[index].scanner_status })) };
    card.soundPlaybackDrop = rendered;
    if (card.classList.contains("flow-card")) {
      card.flowContentKey = flowContentKey(rendered);
      const story = card.closest(".story-card");
      if (story?.storyPlaybackMetadata) {
        story.storyContentKey = JSON.stringify([card.flowContentKey, ...story.storyPlaybackMetadata]);
      }
    } else {
      card.feedContentKey = feedContentKey(rendered, card.feedOptions);
      card.feedMediaKey = feedMediaKey(rendered, card.feedOptions);
    }
  }

  function acceptSoundPlaybackDrop(drop) {
    const update = previous => previous?.id === drop.id ? { ...previous,
      media: drop.media, sound: drop.sound, video_remix: drop.video_remix, media_url: drop.media_url, media_type: drop.media_type,
      media_status: drop.media_status, scanner_status: drop.scanner_status,
      media_selection_revision: drop.media_selection_revision,
      captions_url: drop.captions_url, transcript_url: drop.transcript_url, media_text_revision: drop.media_text_revision,
      video_binding: drop.video_binding, video_binding_version: drop.video_binding_version, video_text: drop.video_text } : previous;
    state.feedWindows?.patch(drop.id, update({ id: drop.id }));
    state.drops = state.drops.map(update);
    state.search.drops = state.search.drops.map(update);
    state.discovery.items = state.discovery.items.map(update);
    state.profile.drops = state.profile.drops.map(update);
    state.flow.items = state.flow.items.map(update);
    if (state.flow.now?.current?.drop) state.flow.now.current.drop = update(state.flow.now.current.drop);
    state.flow.stories = state.flow.stories.map(story => ({ ...story, drop: update(story.drop) }));
    if (state.board?.drop) state.board.drop = update(state.board.drop);
    if (state.comments?.drop) state.comments.drop = update(state.comments.drop);
    for (const card of $$(".drop-card, .flow-card")) {
      if (card.dataset.dropId !== drop.id) continue;
      const host = $(".drop-copy, .flow-overlay", card);
      globalThis.LouderrOriginalSounds?.appendCredit(host, drop, {
        openSource: id => { history.pushState({}, "", `/d/${encodeURIComponent(id)}`); routeLocation(); }, openProfile: openCreator,
      });
      refreshSoundPlaybackCard(card, drop);
    }
  }

  function installSoundPlayback() {
    if (!globalThis.LouderrSoundPlayback || globalThis.louderrSoundPlayback) return;
    const selections = new WeakMap();
    const notice = (media, message) => {
      const host = media.closest(".flow-visual, .board-visual, .post-archive-media");
      if (!host) return;
      let status = $(".sound-playback-status", host);
      if (!status) { status = create("p", "sound-playback-status"); status.setAttribute("role", "status"); host.append(status); }
      status.textContent = message; status.hidden = !message;
    };
    const control = globalThis.LouderrSoundPlayback.create({
      identity: () => state.viewer?.id || null,
      scope: () => JSON.stringify([state.viewer?.id || null, location.pathname, state.view, state.filter,
        state.view === "flow" ? state.flow.view : null]),
      visible: media => {
        if (document.visibilityState === "hidden" || media.closest("[hidden], [data-gallery-active='false']")) return false;
        const rect = media.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
      },
      read: (id, signal, context) => context
        ? state.postArchive.readPost(id, signal, context)
        : api(`/api/drops/${encodeURIComponent(id)}`, {signal, headers: {"Cache-Control": "no-cache"}}),
      accept: (drop, context) => { if (!context) acceptSoundPlaybackDrop(drop); },
      text: syncPlaybackMediaText,
      notice,
      hold: (media, message) => {
        selections.set(media, Symbol());
        if (!window.louderrVideo?.holdSource(media, message)) {
          media.pause(); media.removeAttribute("src"); media.load(); notice(media, message);
        }
      },
      select: (media, url, options) => {
        if (window.louderrVideo?.selectSource(media, url, options)) return;
        const token = Symbol(); selections.set(media, token);
        if (media.classList.contains("flow-video")) media.flowMediaSource = url;
        media.src = url; media.preload = "metadata";
        media.addEventListener("loadedmetadata", () => {
          if (selections.get(media) !== token || !media.isConnected || !Number.isFinite(media.duration)) return;
          media.currentTime = Math.min(options.position, Math.max(0, media.duration - 0.05));
        }, {once: true});
        notice(media, options.notice);
      },
    });
    globalThis.louderrSoundPlayback = control;
    setInterval(() => { if (document.visibilityState !== "hidden") control.tick(); }, 1000);
    addEventListener("online", () => control.invalidate());
    addEventListener("pageshow", () => control.invalidate());
    addEventListener("pagehide", () => control.suspend());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") control.invalidate();
      else control.suspend();
    });
  }

  // Everything the Messages surface needs wired, in the order start() wired it. Called once, when
  // the deferred group has loaded, which only happens for a signed-in viewer.
  // `start()` runs once. A visitor who signs in during the session never reached the line that
  // loads Messages, so its seventeen modules stayed absent and the conversation menu, search,
  // calls and polls were simply missing until the next page load. Asking again is cheap -
  // loadDeferred dedupes by group and forgets a failed one - but wiring twice would bind every
  // connect() twice, so the flag guards the wiring rather than the asking.
  let messageModulesWired = false;
  let messageModulesStatus = "idle";
  let messageModulesRequest = null;
  let messageModulesFeedback = false;

  function renderMessageModulesStatus() {
    const panel = $("#message-tools-status"), text = $("#message-tools-status-text"), retry = $("#message-tools-retry");
    if (!panel || !text || !retry) return;
    panel.hidden = !state.viewer || !messageModulesFeedback || messageModulesStatus === "ready";
    text.textContent = messageModulesStatus === "reload" ? "Some messaging tools could not start. Reload the page to try again."
      : messageModulesStatus === "loading" ? "Loading messaging tools…"
      : "Some messaging tools could not be loaded. Check your connection and try again.";
    retry.textContent = messageModulesStatus === "reload" ? "Reload page" : "Retry";
    retry.disabled = messageModulesStatus === "loading";
  }

  function ensureMessageModules(retry = false) {
    renderMessageModulesStatus();
    if (!state.viewer || messageModulesWired || messageModulesRequest || messageModulesStatus === "reload"
        || (messageModulesStatus === "error" && !retry)) return;
    messageModulesStatus = "loading";
    const request = loadDeferred("messages");
    messageModulesRequest = request;
    renderMessageModulesStatus();
    void request.then(() => {
      if (messageModulesRequest !== request) return;
      messageModulesRequest = null;
      if (!state.viewer) { messageModulesStatus = "idle"; renderMessageModulesStatus(); return; }
      // Some connect() calls register listeners. A partial wiring exception must
      // never cause those already completed calls to run a second time.
      messageModulesWired = true;
      try { wireMessageModules(); messageModulesStatus = "ready"; }
      catch (_) { messageModulesStatus = "reload"; messageModulesFeedback = true; }
      renderMessageModulesStatus();
    }, (error) => {
      if (messageModulesRequest !== request) return;
      messageModulesRequest = null;
      messageModulesStatus = error?.code === "DEFERRED_LOAD_TIMEOUT" ? "reload" : "error";
      messageModulesFeedback = true;
      renderMessageModulesStatus();
    });
  }

  function wireMessageModules() {
    globalThis.LouderrCalls?.connect({api, state: () => state, notify: toast});
    globalThis.LouderrVoicePlayback?.connect({
      api, state: () => state, notify: toast,
      open: (id) => { history.pushState({}, "", `/messages/${encodeURIComponent(id)}`); routeLocation(); },
    });
    globalThis.LouderrMessageTools?.connect({
      api, state: () => state, context: messageContext, refresh: openMessageThread,
      rememberDraft: rememberMessageDraft, input: el.messageBody,
      replyBar: $("#message-reply-draft"),
      showError: (message) => showError(el.messageFormError, message),
      showNotice: toast,
    });
    state.messageSearch ||= globalThis.LouderrMessageSearch?.create({
      api, state: () => state, open: openMessageThread, ordinary: paintMessageThreadRead,
      render: () => { renderConversations(); renderMessageThread(); }, list: el.messageList,
      filter: (items, messages) => globalThis.LouderrMessageHide?.filter(items, messages) || items,
      inbox: $("#messages-search-history"), thread: $("#message-search-button"),
      navigation: $("#message-history-navigation"), status: $("#message-history-status"),
      older: el.messagesOlder, newer: $("#messages-newer"), latest: $("#messages-latest"), retry: $("#message-history-retry"),
    });
    state.messageAlbums ||= globalThis.LouderrMessageAlbums?.create({
      api, state: () => state, verified: requireVerified, button: $("#conversation-media-albums"),
      filter: (items, messages) => globalThis.LouderrMessageHide?.filter(items, messages) || items.filter(item => item.expires_at == null),
    });
    state.messageShared ||= globalThis.LouderrMessageSharedItems?.create({
      api, state: () => state, open: openMessageThread, button: $("#conversation-shared-items"),
      filter: (items, messages) => globalThis.LouderrMessageHide?.filter(items, messages) || items.filter(item => item.expires_at == null),
      albums: () => state.messageAlbums?.open(),
    });
    installScheduledMessages();
    state.messagePolls ||= globalThis.LouderrMessagePolls?.create({
      api, state: () => state, context: messageContext, refresh: openMessageThread,
      compose: $("#message-create-poll"), verified: requireVerified, notify: toast,
      repaint: () => {
        for (const row of el.messageList.children) if (row.querySelector(".message-poll")) row.dmRenderKey = null;
        renderMessageThread();
      },
      adopt: (message) => {
        const index = state.messages.items.findIndex(item => item.id === message.id);
        if (index < 0 || !state.messages.items[index].poll || state.messages.items[index].deleted
            || state.messages.items[index].poll.version > message.poll.version) return;
        state.messages.items[index] = message;
        renderMessageThread();
      },
    });
    state.messageTyping ||= globalThis.LouderrMessageTyping?.create({
      api, state: () => state, consent: () => state.privacySettings?.typingConsent(),
      refreshConsent: () => state.privacySettings?.refresh(),
      input: el.messageBody, form: el.messageForm, indicator: $("#message-typing-status"),
      scheduleButtons: [$("#message-send-later"), $("#message-scheduled"), $("#messages-scheduled")],
    });
    globalThis.LouderrMessageHide?.install({
      api, state: () => state, context: messageContext,
      onHidden: applyPersonalMessageHide, notify: toast,
      onExpired: expireDisplayedMessage,
    });
    globalThis.LouderrVoiceNote?.connect({
      button: $("#message-voice-button"),
      toast,
      // The panel goes above the composer rather than replacing it, so a typed
      // draft survives a cancelled recording.
      mount: (panel) => el.messageForm?.prepend(panel),
      attach: (file, context) => selectMessageAttachment(file, context),
      context: () => {
        const context = messageContext();
        const version = context.messages.selectionVersion;
        return { ...context, threadCurrent: () => context.threadCurrent() && state.view === "messages"
          && context.messages.selectionVersion === version };
      },
      onState: (recording, phase) => {
        const control = $("#message-voice-button");
        if (control) control.setAttribute("aria-pressed", recording ? "true" : "false");
        const pending = phase !== "idle";
        if (!pending && el.messageFormError.textContent === "Attach or cancel your recording before sending.") {
          hideError(el.messageFormError);
        }
        if (el.messageMediaButton) el.messageMediaButton.disabled = pending || el.messageBody.disabled;
        if (el.messageFileButton) el.messageFileButton.disabled = pending || el.messageBody.disabled;
        if (el.messageSend) el.messageSend.disabled = pending || el.messageBody.disabled
          || state.messages.sending.has(state.messages.selectedId);
      },
    });
    state.groupInvites ||= globalThis.LouderrGroupInvites?.create({
      api, state: () => state, context: messageContext, open: openMessageThread,
      leave: () => { history.replaceState({}, "", "/messages"); void routeLocation(); },
      login: () => { authTab("login"); openDialog(el.account); },
    });
    globalThis.LouderrMessageGroups?.connect({
      inviteLink: () => state.groupInvites?.manager(),
      confirm: message => confirm(message),
      api, state: () => state, context: messageContext, create: $("#messages-new-group"),
      open: openMessageThread, close: closeMessageThread, refresh: () => refreshConversations(true),
      invalidate: () => {
        state.messages.conversationRequestId += 1;
        const window = state.messages.conversationWindow;
        if (window) { window.resetRequired = true; window.retryAppend = false; window.read?.controller.abort(); }
      },
      adopt: (conversation) => {
        state.messages.conversationRequestId += 1;
        const window = state.messages.conversationWindow;
        if (window) { window.resetRequired = true; window.retryAppend = false; }
        const index = state.messages.conversations.findIndex(item => item.id === conversation.id);
        if (index < 0) state.messages.conversations.unshift(conversation);
        else state.messages.conversations[index] = conversation;
        if (state.messages.selectedId === conversation.id) {
          state.messages.conversation = conversation; state.messages.participant = conversation.participant;
          renderMessageThread();
        }
        renderConversations();
      },
    });
    globalThis.LouderrConversationOrganization?.connect({
      showCollections: () => state.sharedCollections?.show(state.messages.conversation, null, $("#conversation-organization summary")),
      state: () => state, context: messageContext, api,
      render: () => { renderConversations(); renderMessageThread(); },
      refresh: () => refreshConversations(true), close: () => closeMessageThread(false),
      reload: (id) => openMessageThread(id, false), notify: toast,
    });
  }

  function safeToRefreshApp() {
    if (state.startupPending || state.updatePendingWrites || state.composerDraft.busy
        || hasUnsavedComposerDrafts() || profileDraftDirty || state.messages.drafts.size
        || state.messages.sending.size || globalThis.LouderrVoiceNote?.pending()
        || el.chatBody?.value.trim() || el.messageBody?.value.trim()) return false;
    if (!["all", "following", "discover", "browse", "profile", "flow", "saved", "notifications"].includes(state.view)) return false;
    if (document.querySelector("dialog[open], .call-panel:not([hidden])")) return false;
    if (document.activeElement?.matches("input, textarea, select, [contenteditable=true]")) return false;
    return !$$("textarea, input:not([type]), input[type=text], input[type=email], input[type=password], [contenteditable=true]")
      .some(node => node.checkVisibility() && (node.isContentEditable ? node.textContent.trim() : node.value.trim()));
  }

  async function start() {
    // Bind the visible controls immediately; independent startup reads can travel together.
    const capabilitiesReady = loadCapabilities();
    installSoundPlayback();
    globalThis.LouderrHome?.init({
      api, viewer: () => state.viewer,
      openProject: openJourney,
      openDrop: (id) => { history.pushState({}, "", `/d/${encodeURIComponent(id)}`); routeLocation(); },
      startProject: (prompt) => openComposer(null, { mode: "start", prompt, allow_responses: false }),
      openUpdates: () => { history.pushState({}, "", "/notifications"); setView("notifications"); },
      openChat: () => { history.pushState({}, "", "/chat"); setView("chat"); },
    });
    bindEvents();
    restoreHomeFilter();
    bindMessagesMore();
    state.postArchive = globalThis.LouderrPostArchive?.create({
      api, viewer: () => state.viewer, scope: () => state.messages, uuid: () => crypto.randomUUID(),
      canRead: () => Boolean(state.viewer?.email_verified && !state.sessionRestricted),
      canRestore: () => Boolean(state.capabilities.community_writes),
      signIn: () => { requireVerified(); }, openDialog: dialog => openDialogWithHistory(dialog),
      renderMedia: (drop, soundContext) => {
        const host = create("div", "post-archive-media");
        host.append(mediaElement(drop, false, {privateArchive:true, soundContext}));
        if (drop.kind === "video" || drop.video_binding || drop.video_remix || state.videoRemixRemoval?.hasPending(drop.id)) {
          const controls = create("button", "secondary-button", "Remove video remix"); controls.type = "button";
          controls.addEventListener("click", () => state.videoRemixRemoval?.show(drop, controls)); host.append(controls);
        }
        return host;
      },
      releaseMedia: host => {
        for (const media of $$("audio, video", host)) {
          media.pause(); media.removeAttribute("src"); media.load();
        }
        for (const image of $$("img", host)) image.removeAttribute("src");
      },
      restoreFocus: anchor => requestAnimationFrame(() => {
        const visible = node => node?.isConnected && node.getClientRects().length && !node.closest("[hidden], dialog:not([open])");
        const target = [anchor, $("#open-post-archive"), ...$$("[data-open-settings]"), $("#main-content")].find(visible);
        target?.focus();
      }),
      changed: (id, archived) => {
        if (!archived) state.feedWindows?.invalidate();
        if (archived) { removeDrop(id); renderFeed(); if (state.view === "discover") renderDiscover(); }
        globalThis.LouderrHome?.invalidate();
        globalThis.louderrSoundPlayback?.invalidate(null, id);
      },
    });
    $("#open-post-archive")?.addEventListener("click", event => state.postArchive?.show(null, event.currentTarget));
    state.sharedCollections = globalThis.LouderrSharedCollections?.create({
      api, viewer: () => state.viewer, scope: () => state.messages, uuid: () => crypto.randomUUID(),
      canRead: () => Boolean(state.viewer?.email_verified && !state.sessionRestricted),
      canWrite: () => Boolean(state.viewer?.email_verified && state.capabilities.community_writes && !state.sessionRestricted),
      canRemove: () => Boolean(state.viewer?.email_verified && !state.sessionRestricted),
      signIn: () => { requireVerified(); }, openDialog: dialog => openDialogWithHistory(dialog),
      openPost: id => { history.pushState({}, "", `/d/${encodeURIComponent(id)}`); routeLocation(); },
      restoreFocus: id => requestAnimationFrame(() => {
        const card = id ? $$(".drop-card").find(node => node.dataset.dropId === id) : null;
        ((card ? $(".more-button", card) : null) || $("#conversation-organization summary"))?.focus();
      }),
    });
    const socialWritable = () => Boolean(state.viewer?.email_verified && state.capabilities.community_writes
      && !state.sessionRestricted);
    state.followState = globalThis.LouderrFollowState?.create({
      api, owner: () => [state.viewer?.id || null, state.messages, state.csrf], changed: receiveFollowState,
    });
    state.followRequests = globalThis.LouderrFollowRequests?.create({
      api, owner: () => [state.viewer?.id || null, state.messages, state.csrf], canWrite: socialWritable,
      styleAvatar, toast, openDialog: dialog => { if (!dialog.open) dialog.showModal(); },
      closeDialog: dialog => { if (dialog.open) dialog.close(); }, onClose: dismissFollowRequests,
      openProfile: user => {
        history.pushState({}, "", `/u/${encodeURIComponent(user.handle)}`);
        void routeLocation();
      },
    });
    $("#open-follow-requests")?.addEventListener("click", openFollowRequests);
    const socialConfirm = (message, options = {}) => confirm(message, {
      ...options, confirmLabel: options.confirmLabel || options.confirm,
    });
    state.connectionsPanel = globalThis.LouderrConnections?.create({
      elements: { dialog: el.connections, title: el.connectionsTitle, copy: el.connectionsCopy,
        list: el.connectionsList, status: el.connectionsStatus, empty: el.connectionsEmpty,
        more: el.connectionsMore, close: $("#connections-close") },
      api, owner: () => [state.viewer?.id || null, state.messages, state.csrf], canWrite: socialWritable,
      confirm: socialConfirm, followState: state.followState, paintFollowControl, runFollow: runConnectionFollow,
      styleAvatar, toast, openDialog: dialog => { if (!dialog.open) dialog.showModal(); },
      closeDialog: dialog => { if (dialog.open) dialog.close(); }, onClose: dismissConnections,
      openProfile: user => { void openCreator(user); }, relationshipChanged: refreshConnectionCounts,
    });
    const openProfileHandle = handle => {
      if (!handle) return;
      history.pushState({}, "", `/u/${encodeURIComponent(handle)}`);
      void routeLocation();
    };
    state.storiesV2 = globalThis.LouderrStoriesV2?.create({
      api, viewer: () => state.viewer, csrf: () => state.csrf, uuid: () => crypto.randomUUID(),
      capabilities: () => state.capabilities, toast, confirm: socialConfirm, canWrite: socialWritable,
      verified: () => Boolean(state.viewer?.email_verified),
      styleAvatar, signIn: () => { requireVerified(); },
      // Capture choices stay available; unsupported modes use native capture inputs.
      openCamera: globalThis.LouderrCamera ? options => globalThis.LouderrCamera.open(options) : null,
      cameraAvailable: mode => Boolean(globalThis.navigator?.mediaDevices?.getUserMedia
        && (mode !== "video" || globalThis.MediaRecorder)),
      openProfile: openProfileHandle,
      searchPeople: async query => {
        const params = new URLSearchParams({ type: "people", q: query, limit: "12" });
        const result = await api(`/api/search?${params}`);
        return Array.isArray(result?.people?.items) ? result.people.items : [];
      },
      onTrayChanged: () => { if (state.view === "all") renderRailPeople(); },
      liveTrayItems: () => state.live?.trayItems() || [],
      onHighlightsChanged: () => {
        const profile = state.profile?.data;
        if (profile) void state.storiesV2?.renderHighlights($("#profile-highlights"), profile.user,
          Boolean(profile.relationship?.is_self));
      },
    });
    state.live = globalThis.LouderrLive?.create({
      api, viewer: () => state.viewer, uuid: () => crypto.randomUUID(),
      watchOwner: () => [state.messages, state.routeRequestId, location.pathname, location.search],
      capabilities: () => state.capabilities, toast, confirm: socialConfirm, styleAvatar,
      openProfile: openProfileHandle,
      onChanged: () => { if (state.view === "all") renderRailPeople(); syncLiveEntrances(); },
    });
    state.socialExtras = globalThis.LouderrSocialExtras?.create({
      api, viewer: () => state.sessionRestricted ? null : state.viewer, toast, confirm: socialConfirm, canWrite: socialWritable,
      signIn: () => requireVerified(),
      repostOwner: () => [state.viewer?.id || null, state.messages, state.csrf, location.pathname],
      repostControlOwner: () => [state.viewer?.id || null, state.messages],
      capabilities: () => state.capabilities,
      onRepostState: applyRepostState,
      makeDropCard: drop => makeDropCard(drop), afterRender: () => observeCards(),
      repostScroll: () => ({ left: window.scrollX, top: window.scrollY }), restoreRepostScroll: restoreRouteScroll,
      openStory: id => { if (id) void state.storiesV2?.openItem(id); },
      timeZone: () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (_) { return null; } },
      onConversationChanged: id => { if (state.messages.selectedId === id) void openMessageThread(id, false); },
    });
    el.profileViewAvatar?.addEventListener("click", () => {
      const id = state.profile.data?.user?.id;
      if (id && el.profileViewAvatar.classList.contains("sv-has-story")) void state.storiesV2?.openAuthor(id);
    });
    el.profileViewAvatar?.addEventListener("keydown", event => {
      if ((event.key === "Enter" || event.key === " ") && el.profileViewAvatar.classList.contains("sv-has-story")) {
        event.preventDefault();
        el.profileViewAvatar.click();
      }
    });
    const closeAccountThen = action => () => { if (el.account?.open) el.account.close(); action(); };
    $("#open-close-friends")?.addEventListener("click", closeAccountThen(() => void state.storiesV2?.openCloseFriends()));
    $("#open-story-archive")?.addEventListener("click", closeAccountThen(() => void state.storiesV2?.openArchive()));
    $("#open-time-settings")?.addEventListener("click", closeAccountThen(() => void state.socialExtras?.openTimeSettings()));
    $("#open-supervision")?.addEventListener("click", closeAccountThen(() => void state.socialExtras?.openSupervision()));
    $("#conversation-disappearing")?.addEventListener("click", () => {
      const menu = $("#conversation-organization");
      if (menu) menu.open = false;
      state.socialExtras?.openDisappearing(state.messages.conversation);
    });
    if (!state.storiesV2) $("#composer-open-story")?.setAttribute("hidden", "");
    $("#drop-open-camera")?.addEventListener("click", () => openComposerCamera("photo"));
    $("#drop-record-video")?.addEventListener("click", () => openComposerCamera("video"));
    state.savedFolderControls = globalThis.LouderrSavedFolders?.create({
      api, viewer: () => state.viewer, scope: () => state.messages, csrf: () => state.csrf,
      canWrite: () => Boolean(state.viewer?.email_verified && state.capabilities.community_writes && !state.sessionRestricted),
      visible: () => state.view === "saved", selection: () => state.savedFolderId,
      resetSelection: () => { state.savedFolderId = null; },
      select: id => {
        state.savedFolderId = id; state.nextCursor = null;
        if (state.view === "saved") {
          const url = new URL(location.href);
          if (id) url.searchParams.set("folder", id); else url.searchParams.delete("folder");
          history.replaceState(history.state, "", `${url.pathname}${url.search}`);
          void refreshFeed();
        }
      },
      signIn: () => { if (requireVerified()) writesPausedNotice(); },
      openDialog: dialog => openDialogWithHistory(dialog),
      restoreFocus: id => requestAnimationFrame(() => {
        const card = id ? $$(".drop-card").find(node => node.dataset.dropId === id) : null;
        ((card ? $(".more-button", card) : null) || $(".saved-folder-manage"))?.focus();
      }),
      changed: () => {
        state.feedWindows?.invalidate({ view: "saved", erase: true });
        if (state.view === "saved") void refreshFeed();
      },
    });
    state.videoRemixRemoval = globalThis.LouderrVideoRemixRemoval?.create({
      api, viewer: () => state.viewer, scope: () => state.messages,
      canControl: () => Boolean(state.viewer?.email_verified && !state.sessionRestricted),
      openDialog: dialog => openDialogWithHistory(dialog),
      changed: id => {
        globalThis.LouderrHome?.invalidate();
        globalThis.louderrSoundPlayback?.invalidate(null, id);
        state.postArchive?.event(id);
        void refreshDropInPlace(id);
      },
    });
    $("#open-saved-video-changes")?.addEventListener("click", event => state.videoRemixRemoval?.showSaved(event.currentTarget));
    state.videoRemixControls = globalThis.LouderrVideoRemixControls?.create({
      api, viewer: () => state.viewer,
      canOffer: () => state.capabilities.studio_video_remix && state.capabilities.community_writes && !state.sessionRestricted,
      canControl: () => Boolean(state.viewer) && !state.sessionRestricted,
      confirm: message => confirm(message), openDialog: dialog => openDialogWithHistory(dialog),
      changed: id => { globalThis.LouderrHome?.invalidate(); void refreshDropInPlace(id); },
    });
    state.originalSounds = globalThis.LouderrOriginalSounds?.create({
      api, viewer: () => state.viewer,
      canOffer: () => state.capabilities.studio_response_audio && state.capabilities.community_writes && !state.sessionRestricted,
      canControl: () => Boolean(state.viewer) && !state.sessionRestricted,
      audioEnabled: () => state.capabilities.studio_response_audio,
      createPreparation: createSoundAudioPreparation, confirm: message => confirm(message),
      openDialog: dialog => openDialogWithHistory(dialog),
      changed: id => { globalThis.LouderrHome?.invalidate(); void refreshDropInPlace(id); },
    });
    state.coauthors = globalThis.LouderrCoauthors?.create({
      api, viewer: () => state.viewer, canWrite: () => state.capabilities.community_writes && !state.sessionRestricted,
      canDetach: () => !state.sessionRestricted,
      canRetry: () => !state.sessionRestricted,
      nameNode: accountNameNode, confirm: message => confirm(message), signIn: () => openAccountDialog(),
      inboxVisible: () => state.view === "notifications",
      restoreFocus: id => {
        const card = $$(".drop-card").find(node => node.dataset.dropId === id);
        if (card) $(".more-button", card)?.focus();
      },
      openDialog: (dialog, path) => openDialogWithHistory(dialog, path, { returnTo: "/notifications" }),
      openPost: id => { history.pushState({}, "", `/d/${encodeURIComponent(id)}`); routeLocation(); },
      changed: id => {
        globalThis.LouderrHome?.invalidate();
        if (state.view === "following") scheduleFollowingRefresh();
        else void refreshDropInPlace(id);
        if (state.view === "profile") void refreshProfile();
      },
    });
    globalThis.LouderrInstall?.connect({
      section: $("#install-settings"), status: $("#install-status"),
      button: $("#install-button"), steps: $("#install-steps"), toast,
    });
    await Promise.all([
      capabilitiesReady,
      refreshAuth({ syncRealtime: false }).then(primeInitialHomeFeed).catch(() => {
        state.viewer = null; state.csrf = null; state.sessionRestricted = false; renderAccount();
      }),
    ]);
    state.startupPending = false;
    if (state.view === "all") renderRailPeople();
    delete document.body.dataset.homeSessionPending;
    delete document.body.dataset.startupPending;
    // A guest cannot use Messages, so its seventeen modules and 206 KB are not in the first load.
    // They are wired once, when they land, and only for a signed-in viewer. Boot does not wait:
    // every reach is guarded, so the thread list app.js renders itself is unaffected until then.
    ensureMessageModules();
    handleCommerceReturn();
    handleFederatedAuthReturn();
    handleMailLinks();
    const notificationStartupReceipt = await routeStartup();
    state.initialHomeFeed?.controller?.abort();
    state.initialHomeFeed = null;
    const accountSection = new URLSearchParams(location.search).get("account");
    if (state.viewer && accountSections.has(accountSection)) {
      openAccountDialog(accountSection);
      history.replaceState({}, "", accountSection === "settings" ? "/settings" : `/settings/${accountSection}`);
    }
    const startupSupportRead = startStartupSupportReads();
    if (state.sessionRestricted) await refreshEnforcement();
    else await Promise.all([
      refreshRoom(true), startupSupportRead, refreshChatReadState(),
      refreshSocialBadges(notificationStartupReceipt), refreshEnforcement(),
    ]);
    syncRealtimeWithAuth();
    schedulePrivatePolling(true);
    scheduleEnforcementPolling();
    track("session_start");
    state.appUpdate = globalThis.LouderrAppUpdate?.connect({ document, window, safe: safeToRefreshApp });
  }

  start();
})();
